import type { Locale } from "./config";

/**
 * The message catalogs.
 *
 * Typed off the Arabic catalog rather than a hand-written interface, so English
 * is forced to stay complete: a key added to `ar` and forgotten in `en` is a
 * compile error, not a string that silently renders as its own key at runtime.
 *
 * Deliberately NOT `as const`. With it, `typeof ar` would be a type of string
 * LITERALS, and `en` would have to equal the Arabic word for word — the exact
 * opposite of what is wanted. Structural typing gives the completeness check
 * without the literal check.
 *
 * Arabic uses clear, easy Modern Standard Arabic for staff-facing copy. Preserve
 * operational meaning, interpolation keys, and the English catalog shape.
 */
export const ar = {
  /*
   * لينك مش موجود (B32). لازم الكلام يفرّق بين حاجتين الاتنين بيبانوا زي بعض
   * من بره: صفحة مش موجودة أصلاً، وصفحة موجودة بس الموظف مامعاهوش صلاحيتها —
   * دي بتقول كده بنفسها. من غير التفرقة دي، مكالمة الدعم مالهاش أول من آخر.
   */
  /*
   * Splash / page loading / offline / 404 (Claude Design "Admin System
   * Screens"). Arabic is the design's own i18n.js copy.
   */
  systemScreens: {
    realtimeUnavailable: "التحديثات المباشرة غير متاحة مؤقتاً. قد تكون البيانات المعروضة قديمة. حاول إعادة الاتصال.",
    realtimeUnconfigured: "التحديثات المباشرة غير مُعدّة على هذا الجهاز. يُعاد تحميل البيانات عند عودتك إلى التطبيق أو عند الضغط على «حاول مرة أخرى».",
    splashOpening: "جارٍ فتح ورديتك…",
    splashPowered: "بدعم من Trioty",
    loadingTitle: "جارٍ تحميل الصفحة…",
    loadingBody: "نحمّل أحدث الطلبات والطاولات والمخزون",
    offlineBadge: "غير متصل · جارٍ إعادة الاتصال",
    offlineTitle: "أنت غير متصل",
    offlineBody:
      "تحقق من الواي فاي أو كابل الشبكة على هذا الجهاز. سنعيد الاتصال تلقائياً.",
    offlineRetry: "حاول مرة أخرى",
    offlineChecking: "جارٍ التحقق من الاتصال…",
    offlineStill: "ما زلت غير متصل",
    offlineKept: "ما كتبته محفوظ على هذه الشاشة ولن يضيع.",
    offlineLastSynced: "آخر مزامنة {time}",
    offlineBackOnline: "عاد الاتصال",
    notFoundBarTitle: "الصفحة غير موجودة",
    notFoundTitle: "هذه الصفحة غير موجودة",
    notFoundBody:
      "ربما الرابط قديم أو تم نقل الصفحة. إذا كنت تعتقد أنه يجب أن تراها، اطلب من المدير مراجعة صلاحياتك.",
    notFoundToday: "الذهاب لليوم",
    notFoundBack: "رجوع",
  },
  notFound: {
    title: "هذه الصفحة غير موجودة",
    /* UX-round D61 — الجملة التانية كانت بتشرح تصنيف النظام: «ودي مش مسألة صلاحيات…».
       «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
       دي معلومة لواحد بيدعم، مش لصاحب المطعم اللي كان رايح تقريره. اتشالت،
       ومكانها بقى فيه الخطوة الجاية. */
    body: "الرابط الذي فتحته غير موجود — ربما تغيّر أو كُتب بطريقة غير صحيحة. راجع الرابط أو اسأل من أرسله إليك.",
    /* الزرار بيروح على `/{locale}` وهي بتعيد الاختيار حسب صلاحياتك — دول
       أسماء المحطة اللي هتوصلها فعلاً، عشان «ارجع لشاشتك» تقول هي فين. */
    action: "العودة إلى شاشتك",
    actionPos: "العودة إلى شاشة الكاشير",
    actionKitchen: "العودة إلى شاشة المطبخ",
    actionManage: "العودة إلى الإدارة",
    detailToggle: "تفاصيل لإرسالها إلى الدعم",
  },
  common: {
    appName: "تريوتي للمطاعم",
    /* D139 — تكلفة مش معروفة (مكوّن ما اتشراش لسه) بتتكتب «—»، مش صفر. */
    costUnknownMark: "—",
    costUnknownHint: "التكلفة غير معروفة — لم يُسجَّل لهذا المكوّن أي شراء بعد.",
    loading: "جارٍ التحميل...",
    save: "حفظ",
    cancel: "إلغاء",
    close: "إغلاق",
    add: "إضافة",
    edit: "تعديل",
    delete: "حذف",
    retry: "حاول مرة أخرى",
    back: "رجوع",
    logout: "تسجيل خروج",
    empty: "لا توجد بيانات حتى الآن",
    error: "حدث خطأ",
    confirm: "تأكيد",
    name: "الاسم",
    price: "السعر",
    quantity: "الكمية",
    total: "الإجمالي",
    language: "اللغة",
    required: "مطلوب",
    moreActions: "خيارات",
    increase: "زيادة",
    decrease: "تقليل",
    moveUp: "تحريكه إلى أعلى",
    moveDown: "تحريكه إلى أسفل",
    saved: "تم الحفظ",
    networkError:
      "تعذّر الاتصال بالخدمة. تحقق من اتصال الإنترنت وحاول مرة أخرى.",
    // Form safety. The wording is fixed by docs/design/flows-forms-orders.md
    // §4: the question, then a destructive discard and a primary "keep going".
    unsavedTitle: "لديك تعديلات لم تُحفظ بعد",
    unsavedBody: "هل تريد تجاهلها؟",
    discardChanges: "تجاهل التعديلات",
    keepEditing: "متابعة التعديل",
  },
  notifications: {
    title: "التنبيهات",
    enableSound: "تفعيل صوت التنبيهات",
    muteSound: "كتم صوت التنبيهات",
    soundUnavailable: "صوت التنبيهات غير متاح",
    markAllRead: "تحديد الكل كمقروء",
    empty: "لا توجد تنبيهات جديدة",
    permissionPromptTitle: "فعّل تنبيهات المطعم",
    permissionPromptBody:
      "سننبهك بالطلبات الجديدة وحالة المطبخ. وإذا تأخرت التنبيهات، فستظل الشاشات تُحدَّث بصورة طبيعية.",
    permissionPromptAction: "فعّل التنبيهات",
    permissionDenied:
      "التنبيهات معطلة في المتصفح. يمكنك تفعيلها من إعدادات الموقع.",
    unsupported:
      "هذا المتصفح لا يدعم التنبيهات. ستظل المتابعة داخل التطبيق متاحة.",
    registrationError:
      "تعذّر تشغيل التنبيهات الآن. ستظل المتابعة داخل التطبيق متاحة.",
    retryRegistration: "حاول تشغيلها مرة أخرى",
    feedUnavailableTitle: "سجل التنبيهات غير متاح الآن",
    bootstrapUnavailable:
      "تعذّر الوصول إلى خدمة سجل التنبيهات. ستظل شاشات المطعم والمتابعة المباشرة متاحة.",
    feedUnavailable:
      "تعذّر تحميل سجل التنبيهات. حاول مرة أخرى، وستظل شاشات المطعم تُحدَّث بصورة طبيعية.",
    storeUnavailableTitle: "التنبيه من مطعم غير متاح",
    storeUnavailableBody:
      "لا يملك هذا الحساب صلاحية فتح المطعم الذي أرسل التنبيه. لم يتغير موقعك الحالي.",
    openFailedTitle: "تعذّر فتح التنبيه",
    openFailedBody:
      "تعذّر الانتقال إلى هذا المطعم الآن. حاول فتح التنبيه مرة أخرى.",
    timeNow: "الآن",
    timeMinutesAgo: "منذ {count} د",
    timeHoursAgo: "منذ {count} س",
    timeDaysAgo: "منذ {count} ي",
    itemsRequestedTitle: "طلب جديد من الطاولة",
    itemsRequestedBodyFallback: "أرسل الضيف طلبًا جديدًا يحتاج إلى المراجعة.",
    itemsRequestedBodyOne:
      "أرسل الضيف طلبًا جديدًا يتضمن صنفًا واحدًا يحتاج إلى المراجعة.",
    itemsRequestedBodyTwo:
      "أرسل الضيف طلبًا جديدًا يتضمن صنفين يحتاجان إلى المراجعة.",
    itemsRequestedBodyFew:
      "أرسل الضيف طلبًا جديدًا يتضمن {count} أصناف تحتاج إلى المراجعة.",
    itemsRequestedBody:
      "أرسل الضيف طلبًا جديدًا يتضمن {count} صنفًا يحتاج إلى المراجعة.",
    onlineDeliveryRequestedTitle: "طلب توصيل جديد",
    onlineTakeawayRequestedTitle: "طلب تيك أواي جديد",
    onlineOrderRequestedBodyFallback:
      "أرسل العميل طلبًا عبر الإنترنت بانتظار القبول.",
    onlineOrderRequestedBodyOne:
      "أرسل العميل طلبًا عبر الإنترنت يتضمن صنفًا واحدًا بانتظار القبول.",
    onlineOrderRequestedBodyTwo:
      "أرسل العميل طلبًا عبر الإنترنت يتضمن صنفين بانتظار القبول.",
    onlineOrderRequestedBodyFew:
      "أرسل العميل طلبًا عبر الإنترنت يتضمن {count} أصناف بانتظار القبول.",
    onlineOrderRequestedBody:
      "أرسل العميل طلبًا عبر الإنترنت يتضمن {count} صنفًا بانتظار القبول.",
    paymentSubmittedTitle: "طلب دفع جديد",
    paymentSubmittedBody: "أرسل الضيف طلب دفع جديدًا للمراجعة.",
    kitchenTicketsTitle: "طلبات جديدة للمطبخ",
    kitchenTicketsBodyFallback: "وصلت طلبات جديدة إلى لوحة المطبخ.",
    kitchenTicketsBodyOne: "وصلت تذكرة مطبخ جديدة واحدة إلى اللوحة.",
    kitchenTicketsBodyTwo: "وصلت تذكرتا مطبخ جديدتان إلى اللوحة.",
    kitchenTicketsBodyFew: "وصلت {count} تذاكر مطبخ جديدة إلى اللوحة.",
    kitchenTicketsBody: "وصلت {count} تذكرة مطبخ جديدة إلى اللوحة.",
    itemsReadyTitle: "أصناف جاهزة للتقديم",
    itemsReadyBodyFallback: "أصبحت أصناف من المطبخ جاهزة للتقديم.",
    itemsReadyBodyOne: "أصبح صنف واحد جاهزًا للتقديم.",
    itemsReadyBodyTwo: "أصبح صنفان جاهزين للتقديم.",
    itemsReadyBodyFew: "أصبحت {count} أصناف جاهزة للتقديم.",
    itemsReadyBody: "أصبح {count} صنفًا جاهزًا للتقديم.",
  },
  auth: {
    loginTitle: "دخول فريق المطعم",
    phone: "رقم الهاتف",
    password: "كلمة المرور",
    submit: "دخول",
    failed: "رقم الهاتف وكلمة المرور غير متطابقين. راجعهما وحاول مرة أخرى.",
    firstPasswordTitle: "اختر كلمة مرورك",
    firstPasswordBody: "سجّلت الدخول بكلمة مرور مؤقتة. استبدلها قبل المتابعة.",
    temporaryPassword: "كلمة المرور المؤقتة",
    newPassword: "كلمة المرور الجديدة",
    confirmPassword: "تأكيد كلمة المرور الجديدة",
    saveNewPassword: "حفظ كلمة المرور",
    firstPasswordFailed:
      "تعذّر تغيير كلمة المرور. استخدم ٨ أحرف على الأقل، وتأكد من تطابق كلمتي المرور ومن صحة كلمة المرور المؤقتة.",
    // The two-store case: seller login binds the first store, which may be the
    // wrong one. This is a recoverable state, not a dead end.
    wrongStoreTitle: "هذا المتجر ليس مطعمًا",
    wrongStoreBody: "اختر المطعم الذي تريد العمل عليه.",
    noRestaurantTitle: "لا يوجد مطعم في هذا الحساب",
    noRestaurantBody:
      "هذا الحساب مرتبط بمتاجر عادية فقط. إذا سجلت الدخول بحساب آخر من بوابة البائع، فقد يكون هذا هو السبب؛ فتسجيل الدخول موحد للبوابتين. سجّل الخروج ثم سجّل الدخول بحساب المطعم، أو تواصل مع صاحب المطعم ليضيفك.",
    switching: "جارٍ التبديل...",
    // The redesigned login (claude.ai/design "Login", 2026-10-06).
    welcomeBack: "أهلاً بعودتك",
    loginSub: "سجّل الدخول برقم هاتفك لبدء ورديتك.",
    mobileNumber: "رقم الهاتف",
    loggingIn: "جاري الدخول…",
    forgotPassword: "نسيت كلمة المرور؟ اطلب من المالك أو المدير إعادة تعيينها من صفحة الفريق.",
    loggedOut: "تم تسجيل الخروج. الجهاز جاهز للشخص التالي.",
    // 403 STORE_DEACTIVATED: an admin switched the restaurant off.
    storeDeactivated: "هذا المتجر متوقف حاليًا. تواصل مع دعم تريوتي.",
    passwordSaved: "تم حفظ كلمة المرور. سجّل الدخول بكلمة المرور الجديدة.",
    showPassword: "إظهار كلمة المرور",
    hidePassword: "إخفاء كلمة المرور",
    setOwnPassword: "اختر كلمة مرور خاصة بك",
    setOwnPasswordSub: "كلمة المرور المؤقتة تعمل مرة واحدة. اختر كلمة لا يعرفها غيرك.",
    temporaryPasswordHint: "كلمة المرور التي وصلتك في رسالة أو من المدير.",
    atLeastEight: "8 أحرف على الأقل",
    atLeastEightMet: "✓ 8 أحرف على الأقل",
    moreCharactersOne: "باقي حرف واحد",
    moreCharactersTwo: "باقي حرفين",
    moreCharactersFew: "باقي {count} أحرف",
    moreCharacters: "باقي {count} حرفًا",
    notMatchingYet: "غير متطابقتين بعد",
    newPasswordTooShort: "كلمة المرور الجديدة تحتاج 8 أحرف على الأقل.",
    newPasswordsDiffer: "كلمتا المرور الجديدتان غير متطابقتين.",
    saving: "جاري الحفظ…",
    saveOwnPassword: "حفظ كلمة المرور الجديدة",
    afterSaving: "بعد الحفظ سيُسجَّل خروجك، ثم تدخل مرة أخرى بكلمة المرور الجديدة.",
    backToLogin: "العودة لتسجيل الدخول",
    poweredBy: "بدعم من Trioty",
    slideFloorTitle: "أدر المطعم كله من شاشة واحدة",
    slideFloorBody: "الصالة والمطبخ والتوصيل والخزينة متزامنة دائمًا.",
    slideKitchenTitle: "الطلبات تصل للمطبخ مباشرة",
    slideKitchenBody: "يرى الطهاة ما يحضّرونه تاليًا، الأقدم أولًا.",
    slideDeliveryTitle: "طلبات التوصيل متتبَّعة حتى الباب",
    slideDeliveryBody: "طلبات الاستلام والتوصيل والمنصات في قائمة واحدة.",
    slideCashTitle: "كل جنيه محسوب",
    slideCashBody: "تقسيم الفواتير وإنستاباي والجرد الأعمى للخزينة.",
  },
  /* «اليوم» — الصفحة الرئيسية للإدارة (D111، قرار صاحب المشروع ٢٠٢٦-١٠-٠٢).
     كل رقم هنا هو رقم الصفحة اللي التايل بيفتحها، بنفس اسمه هناك:
     «إجمالي المبيعات» هو اسم الرقم ده على تقرير إغلاق اليوم. */
  today: {
    title: "اليوم",
    subtitle: "نظرة سريعة على الخدمة والمبيعات وما يحتاج إلى توريد.",
    statSales: "إجمالي مبيعات اليوم",
    statItems: "الأصناف المتاحة",
    /* تدقيق الجولة 2 (المرحلة 3): من الأصناف المتاحة، كم صنفًا فيه مكوّن
       أعلن المطبخ نفاده — ما زال يُباع (B18)، والنادل يُنبَّه عليه. الصنف محذوف،
       فيقال 1 و2 بالكلمات. لا يُطبع السطر عند الصفر. */
    statItemsIngredientOut: "منها {count} بمكوّن نافد",
    statItemsIngredientOutOne: "منها واحد بمكوّن نافد",
    statItemsIngredientOutTwo: "منها اثنان بمكوّن نافد",
    statReorder: "مكوّنات تحتاج إلى توريد",
    // B145 — من نفس تقرير التقفيلة، من غير قراية زيادة.
    statOpenBills: "فواتير مفتوحة",
    statOpenDrawers: "خزائن مفتوحة",
    attentionTitle: "يحتاج إلى متابعة",
    // B144 — الرقمين كانوا «740 / 100» من غير اسم؛ دلوقتي كل رقم باسمه، بنفس
    // كلمة صفحة المخزون للحد («حد التنبيه»).
    levelOnHand: "الرصيد",
    levelWarnAt: "حد التنبيه",
    attentionEmpty: "كل مستويات المخزون جيدة.",
    attentionNoIngredients: "لا توجد مكوّنات مسجّلة بعد.",
    topTitle: "الأكثر مبيعًا اليوم",
    topEmpty: "لم يُبَع أي صنف اليوم بعد.",
    // «مرة» مش «طبق»: المشروب صنف برضه، و«اتباع ٣ أطباق» على بيبسي غلط.
    // ١ / ٢ / ٣–١٠ بالجمع / ١١+ بالمفرد — زي `countedKey`.
    soldOne: "بِيع مرة واحدة",
    soldTwo: "بِيع مرتين",
    soldFew: "بِيع {count} مرات",
    sold: "بِيع {count} مرة",
    loadFailed: "تعذّر تحميل بعض أرقام اليوم.",
    noAccessTitle: "لا يتاح لك شيء في هذه الصفحة",
    noAccessBody:
      "تعرض هذه الصفحة المبيعات والمخزون وقائمة الطعام. اطلب من صاحب المطعم إضافة صلاحية واحدة منها إلى دورك.",
  },
  nav: {
    expand: "إظهار القائمة",
    copyCode: "نسخ الرمز",
    codeCopied: "تم النسخ",
    copyMenuLink: "نسخ رابط القائمة",
    menuLinkCopied: "تم نسخ الرابط",
    themeLight: "فاتح",
    themeDark: "غامق",
    themeSystem: "النظام",
    collapse: "إخفاء القائمة",
    switchStore: "تبديل المتجر",
    currentStore: "الحالي",
    /* UX-round D62 — `nav.pos` («الطاولات») اتشال مع الصف التاني. كان في صفّين للصالة:
       «الطاولات» في الخدمة و«الصالة والطاولات» في المحل، بنفس المناطق ونفس
       الطاولات. بقى صف واحد اسمه `floor`. */
    // D152 — المجموعات بالشغل: الخدمة · الفلوس · المنيو · المخزن · التقارير · المحل.
    // «الخدمة» مش «الصالة»: أول صف تحتها اسمه «الصالة»، والعنوان والصف ما يتكرروش.
    groupService: "الخدمة",
    groupMoney: "المالية",
    more: "المزيد",
    customize: "ترتيب القائمة",
    customizeShow: "إظهار",
    customizeHide: "إخفاء",
    moveUp: "تحريك لأعلى",
    moveDown: "تحريك لأسفل",
    groupMenu: "إدارة القائمة",
    groupInventory: "المشتريات والمخزون",
    groupStore: "المطعم",
    groupReports: "التقارير",
    manage: "الإدارة",
    cashierWorkspace: "شغل الكاشير",
    // الصفحة الرئيسية للإدارة (D111). «اليوم» زي ما الإغلاق اسمه «إغلاق اليوم».
    today: "اليوم",
    menu: "قائمة الطعام",
    /* صف واحد للصالة (UX-round D62). الاسم بقى «الصالة» زي عنوان الشاشة نفسها، مش
       «الصالة والطاولات» اللي كانت بتتكرر جنب «الطاولات». */
    floor: "الصالة",
    ingredients: "المخزون",
    // D147 — استلام ورقة المورد بسطورها.
    receiveDelivery: "استلام البضائع",
    // D143 — عدّ الرف ومقارنته بالرصيد المسجّل، ثم ترحيل الفروق.
    stocktake: "الجرد",
    suppliers: "الموردون",
    aggregators: "منصّات التوصيل",
    addons: "الإضافات",
    tiers: "الأسعار",
    settings: "الإعدادات",
    staff: "الفريق",
    categories: "الأقسام",
    invoices: "الفواتير",
    kitchen: "المطبخ",
    reports: "التقارير",
    reportItems: "ربحية الأصناف",
    // Singular, and shorter than the page title it links to on purpose: the
    // plural form ("الإلغاءات والهدايا والمرتجعات") is 193px against a 182px
    // sidebar and rendered ellipsised. The page's own heading keeps the plural.
    reportStaff: "الإلغاء والهدية والمرتجع",
    reportDayparts: "أوقات الذروة",
    reportRemakes: "الإعادات",
    reportSpeed: "سرعة الخدمة",
    reportRefunds: "المرتجعات",
    // D134 — ما قاله الضيوف بعد الدفع من تطبيق الطاولة.
    feedback: "آراء الضيوف",
    // 2026-10-25 — every customer with a phone, from the website or the till.
    customers: "العملاء",
    // اسم قصير للقايمة الجانبية. صفحة التقرير نفسها بتستعمل الاسم الكامل.
    reportInputVat: "ضريبة المشتريات",
    shift: "الخزينة",
    shiftHistory: "حركة الخزينة",
    // The Z (D38–D41): the letter is for the accountant who asks for it by
    // name; the word an owner reads leads.
    dayClose: "تقرير نهاية اليوم",
    label: "التنقل الرئيسي",
  },
  // PAPER's own names for the rows the 2026-10 redesign names differently
  // (founder, 2026-10-02: "design names"). Only the rows that differ; every
  // other row keeps its `nav.*` label. The Arabic already says what the design
  // says, so it repeats `nav.*`.
  navPaper: {
    reports: "المبيعات والتكاليف",
    floor: "الصالة",
    shift: "الخزينة",
    categories: "الأقسام",
    reportInputVat: "ضريبة المشتريات",
    reportDayparts: "أوقات الذروة",
    reportStaff: "الإلغاء والهدية والمرتجع",
    shiftHistory: "حركة الخزينة",
    // D152 (round table 2026-10-14): «تقفيلة اليوم» — اسم التقرير في المطاعم.
    // «إغلاق اليوم» كانت بتتقري كأنها فعل بيقفل اليوم، والتقرير بيقرا بس (D38).
    dayClose: "تقرير نهاية اليوم",
  },
  /*
   * The PAPER floor (2026-10-03) — both floors, the till's `/pos` and the back
   * office's live view: the Tables / Kitchen tabs, the tile's status words,
   * money and served dots, the area count and the Takeaway section.
   */
  /*
   * P5-Q (المؤسس، ٢٠٢٦-١٠-٢٥): الحساب المقفول اللي ما اتحصّلش بيفضل على الصالة
   * يومين من قفله، وبعدها بيروح لليستة «الأقدم» دي.
   */
  olderCheques: {
    open: "حسابات أقدم",
    openHint: "الحسابات المغلقة التي لم تُحصَّل منذ أكثر من يومين",
    title: "حسابات لم تُحصَّل منذ أكثر من يومين",
    note: "يبقى الحساب المغلق على الطاولة يومين من إقفاله، ثم ينتقل إلى هنا حتى يُحصَّل. افتح الحساب لتحصيله.",
    emptyTitle: "لا توجد حسابات أقدم",
    emptyBody: "كل حساب مغلق لم يُحصَّل ما زال على الصالة.",
    closedOn: "أُقفل {date}",
    due: "المتبقي {amount}",
    // P9.6: الحسابات بتظهر ٥٠ ورا ٥٠ — الزرار ده بيجيب اللي بعدهم.
    more: "عرض المزيد",
    loadingMore: "جارٍ التحميل…",
  },
  paperFloor: {
    tabsLabel: "عرض الصالة",
    tabTables: "الطاولات",
    tabKitchen: "المطبخ",
    statusWaiting: "بانتظار المراجعة",
    statusReady: "جاهز للتقديم",
    statusPreparing: "في التحضير",
    statusEating: "يتناولون الطعام",
    due: "{amount} مستحق",
    paid: "مدفوع",
    paidSoFar: "المدفوع حتى الآن {amount}",
    served: "{served}/{total} قُدِّم",
    servedHint: "الدائرة الممتلئة = قُدِّم",
    freeTapToOpen: "متاحة · اضغط للفتح",
    areaBusy: "{busy} من {tables} مشغولة",
    newBillHere: "فاتورة جديدة",
    newBillHereHint: "ابدأ فاتورة أخرى على الطاولة نفسها",
    // The order panel head (founder, 2026-10-03): the covers, under Move. A
    // label and a figure after a colon, so no counted-noun arms.
    headGuests: "عدد الضيوف: {number}",
    kitchenEmpty: "لا توجد طلبات في المطبخ الآن",
    /* تدقيق الجولة 2 (المرحلة 3): سطر المطبخ تحت كلمة الحالة — كلمتا مسارَي
       شاشة المطبخ نفساهما («في التحضير» / «جاهز»، D174)، والطبق محذوف فيقال 1 و2
       بالكلمات كما في `summaryOpen` و`paperKitchen.readySingle`. العدد صفر لا
       يُطبع أصلًا. */
    kitchenPreparing: "{count} في التحضير",
    kitchenPreparingOne: "واحد في التحضير",
    kitchenPreparingTwo: "اثنان في التحضير",
    kitchenReady: "{count} جاهزة",
    kitchenReadyOne: "واحد جاهز",
    kitchenReadyTwo: "اثنان جاهزان",
    // يحكم به السيرفر بقاعدة شاشة المطبخ نفسها (تجاوز هدف التحضير)، وقت القراءة.
    late: "متأخر",
    /* إعادة تصميم الصالة (2026-10): سطر حالة ملوّن ومنذ متى، وكلمة الخطوة التالية.
       الوقت محايد حتى يتجاوز حدّه، ثم إطار أحمر. */
    needsYou: "تحتاجك الآن",
    needsYouLabel: "تصفية الطاولات حسب الحالة",
    groupReady: "جاهز للتقديم",
    groupBill: "الحساب",
    groupSeated: "بدون طلب",
    groupKitchen: "في المطبخ",
    groupFree: "متاحة",
    lateTables: "متأخرة: {count}",
    areaBusyLate: "{busy} من {tables} مشغولة · متأخرة: {late}",
    cardBill: "الحساب على الطاولة",
    cardStale: "الحساب تغيّر بعد الطباعة",
    cardPrebill: "طلبوا الحساب",
    cardSeated: "جلسوا بدون طلب",
    since: "منذ {count} د",
    sinceHours: "منذ {count} س",
    actServe: "قدّم",
    actCollect: "تحصيل",
    actPrint: "اطبع الحساب",
    actTakeOrder: "خذ الطلب",
    actReview: "راجع الطلب",
    actOpen: "فتح",
    servedOf: "قُدِّم {served} من {total}",
    freeLabel: "متاحة",
    legendLabel: "دليل الألوان",
    legendEating: "يتناولون الطعام: لا شيء مطلوب",
    legendLate: "إطار أحمر: تأخّر أكثر من الحد",
  },
  /* The PAPER kitchen board (founder, 2026-10-03: "keep the look AND lose no
     function"): an `N×` line asks "واحد ولا الكل؟" before it moves, and the
     floor's Kitchen tab links to the full board. MSA like the rest (D158, T14). */
  paperKitchen: {
    serveSingle: "تقديم واحد",
    serveAll: "تقديم الـ{count} جميعًا",
    serveAllTwo: "تقديم الاثنين",
    serveAllFew: "تقديم الـ{count} جميعًا",
    startSingle: "بدء واحد",
    startAll: "بدء الـ{count} جميعًا",
    startAllTwo: "بدء الاثنين",
    startAllFew: "بدء الـ{count} جميعًا",
    readySingle: "واحد جاهز",
    readyAll: "الـ{count} جاهزة",
    readyAllTwo: "الاثنان جاهزان",
    readyAllFew: "الـ{count} جاهزة",
    pickSingle: "اختيار واحد",
    pickAll: "اختيار الـ{count} جميعًا",
    pickAllTwo: "اختيار الاثنين",
    pickAllFew: "اختيار الـ{count} جميعًا",
    choiceLabel: "كم من {name}؟",
    openBoard: "فتح شاشة المطبخ كاملة",
    emptyAllBody:
      "لا توجد أطباق مُرسلة إلى المطبخ الآن. يظهر طلب الويتر هنا فور إرساله.",
  },
  /* The PAPER register (2026-10-03, founder: "a new flow for the POS"): the
     menu in two steps — the sections, then a section's dishes — with a Back
     tile leading each, and the add-on picker that asks the size first. */
  paperRegister: {
    back: "رجوع",
    backToTables: "إلى الطاولات",
    backToSections: "إلى الأقسام",
    sectionsChip: "الأقسام",
    clearSearch: "مسح البحث",
    sectionDishes: "{count} صنف",
    sectionDishesOne: "صنف واحد",
    sectionDishesTwo: "صنفين",
    sectionDishesFew: "{count} أصناف",
    fromPrice: "من {amount}",
    searchDishes: "ابحث عن طبق",
    soldOut: "نفد",
    noMatch: "لا يوجد طبق باسم «{query}»",
    sectionEmpty: "لا شيء في هذا القسم بعد",
    otherDishes: "أطباق أخرى",
    holdHint: "اضغط مطولاً لمزيد من الخيارات",
    update: "تحديث · {amount}",
    editLine: "تغيير هذا الطبق",
    dishActions: "خيارات {name}",
    basePrice: "السعر الأساسي {amount}",
    included: "مشمول",
    addToOrder: "أضف إلى الطلب · {amount}",
    quantity: "العدد",
    fewer: "أقل",
    more: "المزيد",
    optionQuantity: "عدد {name}",
    handPrice: "سعر يدوي: {amount}",
    swapSet: "يوجد تبديل في هذا الطبق",
    // Over a fire on the order panel: «من 5 د».
    ago: "من {age}",
    justNow: "الآن",
  },
  shell: {
    // The back-office shell: the ☰ bar, the drawer and the sidebar.
    openMenu: "افتح القائمة",
    closeMenu: "أغلق القائمة",
    languageArabic: "العربية",
    languageEnglish: "الإنجليزية",
  },
  /*
   * Canonical units, short enough to sit on a chip (D108).
   *
   * ITS OWN TOP-LEVEL GROUP, and that is not cosmetic: this catalogue's
   * contract is group → key → string, and `messages.spec.ts` flattens it on
   * that assumption. A nested map inside `pos` type-errors at every call site
   * in that spec and throws while the file is still being evaluated, so the
   * suite reports "no tests" rather than a failure — taking the ar/en key
   * parity, empty-string and Arabic-in-English guards down silently with it.
   * That happened here and was caught in review.
   */
  units: {
    g: "جم",
    kg: "كجم",
    ml: "مل",
    piece: "قطعة",
  },
  pos: {
    currentOrder: "الطلب الحالي",
    lineCount: "{count} صنف",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    lineCountFew: "{count} أصناف",
    // B117/A2 — «2 صنف» on the till, the board, the floor grid and the 86
    // screen. `plural.ts` exists for exactly this and names two precedents;
    // this file imported `countedKey` sixty lines from a bare `lineCount`.
    lineCountOne: "صنف واحد",
    lineCountTwo: "صنفين",
    /* D45b · ruling A1 — THE MENU'S COUNT IS NOT THE BILL'S COUNT.
       
       One screen rendered «٩ أصناف» at the top and «صنفين» at the bottom out of
       the same four keys: `MenuBoard` counting dishes the menu is SHOWING, and
       `TillSummaryBar`/`OrderPanel` counting lines on THIS BILL. Two subjects
       600px apart with one vocabulary between them, so neither could ever be
       reworded without moving the other.
       
       The menu's arms qualify themselves — «في المنيو» — because they sit in a
       search-results position, which is exactly where a qualified count belongs.
       The bill's keep `lineCount*` unchanged: they sit beside the bill total and
       the context is the bill.
       
       All four arms ship, `{count}` is in the base and `Few` arms only (the
       `One`/`Two` arms spell the number, so ar and en agree on placeholder
       parity), and `MenuBoard.tsx` switches in the same change — an unwired
       family is a dead key, which `messages.spec.ts` proves. */
    menuResultCount: "{count} صنف في قائمة الطعام",
    menuResultCountFew: "{count} أصناف في قائمة الطعام",
    menuResultCountOne: "صنف واحد في قائمة الطعام",
    menuResultCountTwo: "صنفان في قائمة الطعام",
    lineLess: "أقل",
    lineMore: "أكثر",
    lineRemove: "إزالة الصنف",
    notSentYet: "لم يُرسل بعد",
    // The PAPER order panel's two sections: plates nobody has seen yet, and
    // plates the kitchen already has.
    offPremiseEmptyClose: "الفاتورة فارغة ولا يوجد ما تُغلق عليه؛ ألغِها بدلًا من ذلك.",
    panelSectionSent: "أُرسل إلى المطبخ",
    panelSectionOnTable: "على الطاولة",
    /* الفاتورة اللي مش على طاولة: اللي اتقدّم مش «على الطاولة» — جاهز في الشنطة (جولة الورق 2026-10-07). */
    panelSectionOnTableOffPremise: "جاهز للتسليم",
    menuSearch: "ابحث عن صنف",
    menuAllCategories: "كل الأقسام",
    menuHasAddons: "إضافات",
    // D32 — الصنف اللي خلص النهاردة. بيفضل ظاهر في الشاشة عشان الجرسون يقدر
    // يرد على الزبون، بس مش بيتضاف لأوردر.
    menuSoldOut: "نفد",
    // UX-round D45 — the chip beside it already says «نفد» (`menuSoldOut`); this hint
    // was the one POS string still carrying the kitchen's «خلص» about a DISH.
    menuSoldOutHint: "نفد هذا الصنف اليوم",
    // Combos on the till (S16, D52). Nothing new to ENTER — one tap adds a
    // combo like any other item; these only say what it is.
    comboBadge: "وجبة",
    comboSoldOutHint: "نفد صنف داخل هذه الوجبة",
    // §5.8 — التعليم بإنه نفد من على الكاشير نفسه. الجملة بتقول التغيير بيوصل
    // فين، عشان زرار صغير في ركن المربع ميخبّيش إن أثره على المحل كله.
    // T14 — the kitchen's 86 words: one act, one word on the till and the board.
    menuMarkSoldOut: "إيقاف {name}",
    menuUnmarkSoldOut: "إتاحة {name}",
    menuMarkSoldOutShort: "إيقاف الصنف",
    menuUnmarkSoldOutShort: "إتاحة الصنف",
    menuMarkSoldOutBody:
      "لن يكون «{name}» متاحًا للطلب في جميع نقاط البيع أو في قائمة الطعام عبر QR حتى تضغط «إتاحة الصنف». وسيظل ظاهرًا في قائمة الطعام.",
    menuUnmarkSoldOutBody:
      "سيعود «{name}» متاحًا للطلب في جميع نقاط البيع وفي قائمة الطعام عبر QR.",
    menuMarkedSoldOutToast: "تم إيقاف الصنف",
    menuMarkedAvailableToast: "تمت إتاحة الصنف",
    menuNoResults: "لا توجد أصناف مطابقة",
    addonRequired: "*",
    // Audit round 2 (5.1) — a group that allows more than one pick says how
    // many, beside its name; the picker already stops at the cap.
    addonUpTo: "(حتى {max})",
    language: "اللغة",
    lineActionsTitle: "إجراءات الصنف",
    lineActionEdit: "تغيير الطبق",
    lineActionNote: "ملاحظة للمطبخ",
    noteClearText: "مسح الملاحظة",
    lineActionsNone: "لا يوجد ما يمكن تغييره في هذا الصنف.",
    lineCancel: "إلغاء الصنف",
    logoutConfirmTitle: "تسجيل الخروج؟",
    logoutConfirmBody: "ستحتاج رقم هاتفك وكلمة المرور لتسجيل الدخول مرة أخرى.",
    theme: "المظهر",
    sendToKitchen: "إرسال إلى المطبخ",
    sendToCashier: "إرسال إلى الكاشير",
    pickedItems: "المحدد: {number}",
    pickedClear: "إلغاء التحديد",
    pickItem: "حدد هذا الصنف",
    cashierRequested: "أُرسل إلى الكاشير الساعة {time} — بانتظار الإرسال إلى المطبخ",
    cashierRequestedToast: "أُرسل إلى الكاشير",
    cashierRequestAlert: "{place}: طلب جاهز للإرسال إلى المطبخ",
    cashierWaitingBadge: "بانتظار الكاشير",
    closeNeedsCashier: "يحتاج إلى صلاحية الكاشير",
    switchTable: "طاولة أخرى",
    tableOccupied: "مشغولة",
    // الحساب اتقفل واتطبع وراح للزبون، بس الفلوس لسه ماجتش. حالة تالتة غير
    // «فاضية» و«مشغولة»: الطاولة مش متاحة لحد تاني، والمطلوب منها تحصيل.
    // F1 (unit 4): الأكل رايح فين. مش نفس سؤال «مين جاب الزبون» — أوردر طلبات
    // هو أوردر دليفري، ولو اتخلطوا في كلمة واحدة السؤالين ميتجاوبوش صح.
    channelDineIn: "في المطعم",
    // F1 (unit 4): الحسابات اللي مش قاعدة على ترابيزة. الزرار فوق الصالة مش
    // جواها، لأن التيك أواي مش تابع لمنطقة — ولو حطيناه جوه بقى هو هو حيلة
    // «الترابيزة الوهمية» بإسم تاني.
    offPremiseTakeawayStart: "طلب جديد للاستلام",
    // D154 — وجبة الطاقم: نفس الفاتورة ونفس التقارير، بس باب واحد بدل ٣٠ ضغطة.
    // T6 / F29 (D150) — اقتراح واحد من آخر فاتورة بنفس الرقم بالظبط.
    customerSuggestionLast: "آخر طلب {date}:",
    customerSuggestionUse: "استخدام",
    staffMealStart: "وجبة الموظفين",
    staffMealBannerTitle: "وجبة الموظفين",
    staffMealBannerBody:
      "اختر الأطباق من القائمة، ثم «إرسال وجبة الموظفين»: تُرسل إلى المطبخ وتُسجَّل كلها وجبة موظفين.",
    staffMealSend: "إرسال وجبة الموظفين",
    // D154 follow-up: fired and comped already, only the close is left.
    staffMealFinish: "إغلاق وجبة الموظفين",
    staffMealSentToast: "أُرسلت وجبة الموظفين وسُجّلت وجبةَ موظفين.",
    staffMealSentOpenToast:
      "أُرسلت وجبة الموظفين وسُجّلت. أغلقها من هنا فور أن يبدأ المطبخ تحضيرها.",
    offPremiseDeliveryStart: "طلب توصيل جديد",
    offPremiseTakeawayTitle: "طلب للاستلام",
    offPremiseDeliveryTitle: "طلب توصيل",
    offPremiseNameLabel: "اسم العميل (اختياري)",
    offPremisePhoneLabel: "الهاتف (اختياري)",
    offPremiseAddressLabel: "العنوان (اختياري)",
    // بيانات الزبون على الشاشة — كانت بتتكتب وما بتظهرش في أي مكان.
    customerBlockTitle: "العميل",
    customerName: "الاسم",
    customerPhone: "الهاتف",
    customerAddress: "العنوان",
    customerNone: "لم تُسجّل بيانات العميل",
    customerEdit: "تعديل",
    customerEditTitle: "بيانات العميل",
    customerEditHint:
      "صحّح البيانات المكتوبة بطريقة غير صحيحة فقط؛ فلا تتغير رسوم التوصيل أو التطبيق من هنا.",
    customerSaved: "تم تحديث بيانات العميل",
    // T6 — تعديل الرسوم والقناة والتطبيق على فاتورة لسه مفتوحة. غير «تعديل»
    // بيانات الزبون: ده بيمس فلوس الفاتورة، فله زرار وله صلاحية الكاشير.
    chargesEdit: "الرسوم والقناة",
    chargesEditTitle: "رسوم الفاتورة وقناتها",
    chargesChannelLabel: "القناة",
    chargesHint:
      "يُعاد حساب رسوم التوصيل والخدمة والعمولة على هذه الفاتورة المفتوحة فقط؛ لا يتغير سعر أي صنف.",
    chargesFeeInvalid: "اكتب رقمًا موجبًا أو صفرًا، أو اتركه فارغًا.",
    chargesSaved: "تم تحديث رسوم الفاتورة",
    // شاشة الانتظار جوه المودال لحد ما الإضافات توصل — الويتر ضغط خلاص،
    // ولازم يشوف حاجة بدل ما يفتكر إن الضغطة ضاعت ويضغط تاني.
    addonLoading: "جارٍ تحميل اختيارات الصنف…",
    // دليفري من غير موبايل ولا عنوان = أوردر محدش يقدر يوصله.
    undeliverableWarning:
      "طلب توصيل بلا هاتف أو عنوان؛ لن يتمكن أحد من توصيله.",
    // T11 — نفس التحذير لما ناقص حاجة واحدة بس: الجملة تقول إيه الناقص بدل
    // «هاتف أو عنوان» والهاتف مكتوب قدّامه.
    undeliverableWarningNoPhone:
      "طلب توصيل بلا هاتف؛ لن يتمكن الطيار من الوصول إلى العميل.",
    undeliverableWarningNoAddress:
      "طلب توصيل بلا عنوان؛ لن يعرف الطيار إلى أين يذهب.",
    offPremiseFeeLabel: "رسوم التوصيل",
    // فاضي = سعر المحل الافتراضي. ده رد مختلف عن صفر، اللي معناه إن التوصيلة دي
    // ببلاش.
    offPremiseFeeHint: "اتركها فارغة لاستخدام سعر المطعم ({default})",
    // سعر المحل صفر: «سيبها فاضية» معناها التوصيل ببلاش، وده لازم يتقال بدل
    // ما الكاشير يفتكر إن في سعر افتراضي مستني وراها.
    offPremiseFeeHintFree:
      "سعر التوصيل في المطعم صفر؛ اتركها فارغة ليكون التوصيل مجانيًا.",
    // F2 — الأوردر جه منين. «مباشر» يعني تليفون المحل نفسه، من غير عمولة.
    // B43 — تقسيم الفاتورة على العائلات. التقسيم علامة على الأصناف، مش حسبة:
    // كل عيلة بتدفع اللي طلبته، والفلوس بتطلع من العلامة.
    // B3 — دفع جزئي: نص كاش ونص فيزا، أو عيلة بتدفع أصنافها.
    // B89 — الفاتورة اتدفع منها وبعدين اتقصّرت، فالمحل ماسك فلوس محدش طالبها.
    overpaidLabel: "مستحق للعميل",
    overpaidNotice:
      "انخفضت قيمة الفاتورة بعد الدفع؛ لدى المطعم {amount} مستحقة للعميل",
    overpaidAction: "إعادة المبلغ",
    overpaidTitle: "إعادة المبلغ الزائد",
    overpaidBody:
      "أصبح إجمالي الفاتورة {total}، والمدفوع {paid}. يُعاد الفرق إلى العميل بطريقة الدفع نفسها، ثم تُغلق الفاتورة.",
    overpaidConfirm: "إعادة {amount}",
    overpaidDone: "أُعيد المبلغ إلى العميل وأُغلقت الفاتورة",
    overpaidNeedsRefunder:
      "تتطلب إعادة النقود صلاحية المرتجع — تواصل مع المدير.",
    partialTitle: "دفع جزء من الفاتورة",
    partialPaidSoFar: "المدفوع حتى الآن",
    partialOutstanding: "الباقي على الفاتورة",
    partialWhoIsPaying: "من سيدفع",
    partialTypedAmount: "مبلغ",
    // ليه مفيش خانة مبلغ لما تختار عيلة: الرقم بييجي من أصناف العيلة نفسها.
    partialPartyNote: "يُحسب المبلغ من أصناف المجموعة ولا يُكتب يدويًا",
    partialAmountLabel: "المبلغ المستلم",
    partialTakeTheRest: "المتبقي كاملًا",
    partialOverChargeHint:
      "لا يمكن خصم مبلغ من البطاقة يتجاوز المتبقي على الفاتورة",
    partialFinishes: "ستغلق هذه الدفعة الفاتورة",
    partialLeaves: "سيظل على الفاتورة {amount}",
    partialConfirm: "تحصيل الدفعة",
    partialOpen: "دفع جزئي",
    partialTakenToast: "سُجّلت الدفعة",
    // العيلة اللي خلّصت حسابها. من غير السطر ده الشريحة بتاعتها شكلها زي
    // اللي لسه مدفعتش بالظبط — والكاشير بياخد فلوسها تاني.
    partialPartyPaid: "دفعت",
    partialPartyPaidOf: "دفعت {paid} من {amount}",
    // أخطر سطر في شاشة الدفع: أصناف محدش خدها، والترابيزة على وشك تمشي.
    partialUnclaimedOne:
      "هناك صنف لم يُحتسب على أي مجموعة — مبلغ {amount} غير محسوب على أحد",
    partialUnclaimedTwo:
      "هناك صنفان لم يُحتسبا على أي مجموعة — مبلغ {amount} غير محسوب على أحد",
    partialUnclaimed:
      "هناك {count} صنفًا لم يُحتسب على أي مجموعة — مبلغ {amount} غير محسوب على أحد",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    partialUnclaimedFew:
      "هناك {count} أصناف لم تُحتسب على أي مجموعة — مبلغ {amount} غير محسوب على أحد",
    partyTitle: "مَن طلب ماذا",
    /* B118/QA3 — «متقسمة على 1» كانت بتتكتب فعلًا: فاميليا واحدة اتعلّمت
       عليها كل الأصناف حساب مش «متقسم». */
    partyAllMarkedOne: "أُسندت كل الأصناف إلى مجموعة واحدة",
    partyAllMarkedTwo: "مقسّمة على مجموعتين، وقد أُسندت كل الأصناف",
    partyAllMarkedFew: "مقسّمة على {count} مجموعات، وقد أُسندت كل الأصناف",
    /* B118/QA4 — الصيغة العامة كانت من غير اسم، فـ١١ عيلة كانت بتقرا
       «متقسمة على 11 —» وباقي الصيغ بتقول «فاميليا». والعربي بياخد المفرد
       من ١١ وطالع. */
    partyAllMarked: "مقسّمة على {count} مجموعة، وقد أُسندت كل الأصناف",
    /* B118/QA5 — عدّادين في جملة واحدة، و`countedKey` بيختار مفتاح واحد: بيصرّف
       «صنف» صح وبيسيب عدد الفاميليات عريان، فكانت بتكتب «متقسمة على 1». الرقم
       ده بقى جملة مبنية جنبها، زي ما اتعمل في جملة الكوفرات بالظبط. */
    partySomeUnmarked: "مقسّمة على {ways} — بقي {plates} صنف بلا مجموعة",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    partySomeUnmarkedFew: "مقسّمة على {ways} — بقيت {plates} أصناف بلا مجموعة",
    // نفس السطر بالمفرد والمثنى. «لسه 1 أصناف» غلط زي «1 plates» بالظبط،
    // والسطر ده بيتقري على شاشة الطلب كل ما الفاتورة تكون متقسمة.
    partySomeUnmarkedOne: "مقسّمة على {ways} — بقي صنف واحد بلا مجموعة",
    partyWaysOne: "مجموعة واحدة",
    partyWaysTwo: "مجموعتان",
    partyWaysFew: "{count} مجموعات",
    partyWays: "{count} مجموعة",
    partySomeUnmarkedTwo: "مقسّمة على {ways} — بقي صنفان بلا مجموعة",
    partyPickPlates: "اختر الأصناف",
    partyAssignTo: "تُسند إلى",
    partyNew: "مجموعة جديدة",
    partyClear: "إزالة العلامة",
    partyAssign: "تحديد الأصناف",
    partyLabelPlaceholder: "اسم المجموعة — مثلًا «العائلة»",
    // مفتاحين مش واحد: مفيش جمع في النظام ده، و«١ أصناف» بتتقري غلط.
    partyPlateCountNone: "لا توجد أصناف",
    partyPlateCountOne: "صنف واحد",
    // المثنى. «2 أصناف» غلط في العربي زي ما «1 bills» غلط في الإنجليزي.
    partyPlateCountTwo: "صنفين",
    partyPlateCount: "{count} صنف",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    partyPlateCountFew: "{count} أصناف",
    // أهم سطر في الشاشة: اللي ما اتعلّمش ممكن يمشي من غير ما حد يدفعه.
    partyUnclaimedOne: "هناك صنف واحد بلا مجموعة",
    partyUnclaimedTwo: "هناك صنفان بلا مجموعة",
    partyUnclaimed: "هناك {count} صنفًا بلا مجموعة",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    partyUnclaimedFew: "هناك {count} أصناف بلا مجموعة",
    partyAssigned: "أُسندت الأصناف",
    offPremiseAggregatorLabel: "مصدر الطلب",
    offPremiseAggregatorDirect: "مباشر",
    // بيقول العمولة بتعمل إيه، مش بس إنها موجودة: بتتخصم من حصيلة الليلة،
    // والتطبيق بيحوّل الفلوس بعدين — يعني مش داخلة الدرج.
    offPremiseAggregatorHint:
      "تُخصم العمولة من حصيلة الليلة، ولا تدخل مبالغ التطبيق في الخزينة",
    // D163 (F39) — مرجع اختياري على دفعة البطاقة أو إنستاباي، لا يُطلب أبدًا.
    tenderReferenceLabel: "مرجع العملية (اختياري)",
    tenderReferenceHint:
      "رقم الموافقة على إيصال جهاز البطاقة، أو مرجع التحويل في إنستاباي.",
    tenderReference: "مرجع العملية",
    // T13 — رقم الطلب عند المنصة: اللي السائق بيقوله على الباب.
    platformOrderRefLabel: "رقم الطلب على المنصة (اختياري)",
    // T14 — مع منصة: الرسوم صفر على الفاتورة ومفيش رسم خدمة.
    platformFeeHint:
      "اتركها فارغة: المنصة تتحمّل رسوم التوصيل، فتكون صفرًا على الفاتورة.",
    platformNoServiceCharge: "لا رسم خدمة على فواتير المنصات.",
    // T6 — تغيير القناة أو المنصة بعد ما الأطباق راحت المطبخ.
    chargesReprintWarning:
      "أعد طباعة تذاكر المطبخ بعد هذا التعديل؛ الأطباق المرسلة تحمل البيانات القديمة.",
    // T16 — الشنطة خرجت مع مين. حقل مش دورة حياة: لا سائقين ولا تتبّع.
    handOverTitle: "إغلاق الطلب وتسليمه",
    handedToLabel: "سُلِّم إلى (اختياري)",
    handedToPlaceholder: "اسم الطيار، أو «العميل»",
    handedToRecent: "أسماء حديثة",
    handOverHint: "نسجّل فقط مع من خرجت الحقيبة؛ بلا إدارة للطيارين ولا تتبّع.",
    handOverToPlatform: "إغلاق وتسليم إلى {name}",
    handOverToPlatformHint:
      "تُغلق الفاتورة وتُسجَّل محصَّلة على {name} بالمبلغ المستحق {amount}؛ لا يدخل شيء إلى الخزينة.",
    offPremiseHandedTo: "خرج مع {name}",
    platformRefShort: "رقم المنصة {ref}",
    outWithRiders: "مع الطيارين: {count} · {amount}",
    // T15 — أساس العمولة، على شاشة الدفع وتفاصيل الفاتورة.
    aggregatorSettleHintBase: "النسبة محسوبة على {base}.",
    commissionBaseFood: "الطعام فقط",
    commissionBaseTotal: "قيمة الطلب كاملة",
    commissionRateOnBase: "عمولة {percent}% على {base}",
    // T17 — كتلة السائق على الإيصال.
    receiptToCollect: "المطلوب تحصيله",
    offPremiseStart: "بدء الطلب",
    channelTakeaway: "طلب للاستلام",
    channelDelivery: "توصيل",
    // T8 — فاتورة استلام/توصيل اتقفلت والفلوس لسه ما جتش: الشنطة خرجت،
    // والفاتورة فاضلة على الشريط لحد ما تتقبض.
    offPremiseOutStatus: "خرج ولم يُحصَّل بعد",
    offPremiseClosedSince: "أُغلقت منذ {count} د",
    offPremiseClosedSinceHours: "أُغلقت منذ {count} س",
    /* F24 (§5.12) — الزبون بيطلب شنطة بنفسه من صفحة المطعم، والطلب بيوصل
       للكاشير كبطاقة يقبلها أو يرفضها. مش فاتورة لحد ما تتقبل. بالعامية، زي
       باقي الكاشير. */
    onlineOrdersTitle: "طلبات عبر الإنترنت",
    // The counted noun — a request waiting — with its four arms.
    onlineOrdersWaiting: "{count} طلب بانتظار الرد",
    onlineOrdersWaitingOne: "طلب واحد بانتظار الرد",
    onlineOrdersWaitingTwo: "طلبان بانتظار الرد",
    onlineOrdersWaitingFew: "{count} طلبات بانتظار الرد",
    onlineOrdersEmpty: "لا توجد طلبات عبر الإنترنت بانتظار الرد الآن.",
    onlineOrdersTaking: "نستقبل الطلبات من صفحة المطعم",
    onlineOrdersPaused: "متوقف مؤقتًا — صفحة المطعم لا تستقبل طلبات جديدة",
    onlineOrdersPauseLabel: "استقبال الطلبات من صفحة المطعم",
    onlineOrdersPauseAction: "إيقاف مؤقت",
    onlineOrdersResumeAction: "استئناف الاستقبال",
    onlineOrderAccept: "قبول",
    onlineOrderReject: "رفض",
    onlineOrderReadyIn: "جاهز خلال",
    onlineOrderMap: "الخريطة",
    onlineOrderMapApproximate: "موقع تقريبي — اتصل بالعميل",
    onlineOrderVerified: "رقم مؤكَّد",
    onlineOrderMinutesChip: "{minutes} د",
    onlineOrderExpiresIn: "ينتهي خلال {minutes} د",
    onlineOrderExpiresSoon: "ينتهي خلال أقل من دقيقة",
    onlineOrderExpired: "انتهى وقته",
    onlineOrderNoteLabel: "ملاحظة",
    onlineOrderRejectTitle: "رفض الطلب",
    onlineOrderRejectReasonLabel: "السبب (اختياري) — يظهر للعميل",
    onlineOrderRejectReasonPlaceholder: "مثلًا: نفد الصنف",
    onlineOrderRejectConfirm: "رفض الطلب",
    onlineOrderAcceptedToast: "قُبل الطلب — فُتحت الفاتورة #{sequence}",
    onlineOrderRejectedToast: "رُفض الطلب",
    /* B7 — طلب توصيل لشخص آخر: البطاقة تعرض المستلم جهةَ اتصال، وتحته من طلبه
       برقم مُخفى إلا آخر أربعة أرقام. الاسم والرقم معزولان في البطاقة. */
    onlineOrderOrderedBy: "طلبه:",
    // B7 — على بطاقة طلب لشخص آخر: الكاشير يتصل بالمستلم لا بمن طلب.
    onlineOrderMapApproximateRecipient: "موقع تقريبي — اتصل بالمستلم",
    tableChequeOut: "الحساب خارج الطاولة",
    // B8: الطاولة الواحدة بقى ينفع يبقى عليها أكتر من حساب مفتوح — عيلتين على
    // ترابيزة كبيرة، أو ترابيزة اتقلبت والحساب الأول لسه برّه. التايل بيقول
    // العدد بدل رقم حساب واحد، لأن رقم واحد من اتنين نص حقيقة.
    tableBillCount: "{count} حساب",
    /* B118/QA3 — الشيبين دول مربوطين بـ `> 1`، يعني أصغر رقم ممكن يظهر هو
       الاتنين — واللي كان بيتكتب «2 حسابات». والجمع كان ثابت لكل الأرقام. */
    tableBillCountOne: "حساب واحد",
    tableBillCountTwo: "حسابين",
    tableBillCountFew: "{count} حسابات",
    tableChequeCount: "{count} حساب خارج الطاولة",
    tableChequeCountOne: "حساب واحد خارج الطاولة",
    tableChequeCountTwo: "حسابان خارج الطاولة",
    tableChequeCountFew: "{count} حسابات خارج الطاولة",
    tableChequeOutHint: "اضغط لتحصيل الحساب",
    // الطاولة اللي حسابها برّه ممكن يقعد عليها زباين جداد قبل ما الحساب
    // القديم يتقفل — دي الضغطة التانية المقصودة، مش الأساسية.
    tableNewOrder: "طلب جديد",
    tapToStart: "اضغط لبدء طلب",
    startOrderTitle: "الطاولة متاحة",
    unsentLeaveTitle: "هناك أصناف لم تُرسل بعد",
    // No count in the sentence: the panel behind the dialog already shows the
    // lines, and «صنف واحد ... ماوصلوش» would be wrong for one of them.
    unsentLeaveBody:
      "لم تصل الأصناف التي أضفتها إلى المطبخ بعد، وستُحذف إذا غادرت هذه الشاشة. أرسلها أولًا إذا اكتمل الطلب.",
    unsentLeaveConfirm: "الخروج وحذفها",
    unsentLeaveStay: "متابعة الطلب",
    startOrderHint: "سيفتح أول صنف تضيفه من قائمة الطعام فاتورة لهذه الطاولة.",
    startOrderNeedsPermission:
      "تحتاج إلى صلاحية فتح الفاتورة لبدء طلب على هذه الطاولة.",
    tableNotFound: "هذه الطاولة غير موجودة",
    paidTitle: "دُفعت",
    changeDue: "الباقي",
    newOrder: "طلب جديد",
    basePrice: "الأساسي",
    gridTitle: "الطاولات",
    allAreas: "كل الأماكن",
    backToTables: "الطاولات",
    openForMinutes: "من {count} د",
    openForHours: "من {count} س",
    // The age inside a PAPER floor tile's ring — the bare figure and its unit,
    // because the ring is 24px across. The tile's tooltip carries the sentence.
    tileAgeMinutes: "{count} د",
    tileAgeHours: "{count} س",
    tileAgeDays: "{count} ي",
    summaryOpen: "{count} مفتوحة",
    /* B118 — the floor grid's first chip was the fifth counted noun and the one
       the round-1 sweep missed: «1 مفتوحة» / «2 مفتوحة» on the app's home
       screen. Same `countedKey` shape as `lineCount` beside it. */
    summaryOpenOne: "واحدة مفتوحة",
    summaryOpenTwo: "اثنتان مفتوحتان",
    tableFree: "متاحة",
    invoiceNumber: "فاتورة #{sequence}",
    openTable: "فتح الطاولة",
    pendingCount: "{count} طلب بانتظار المراجعة",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    pendingCountFew: "{count} طلبات بانتظار المراجعة",
    // B117/A2+D11 — «2 طلب مستني» on the floor grid's pending badge.
    // B117/Q8 — a guest says they paid and nobody has looked. No plural trio:
    // a bill carries at most one claim (unique on invoice_id).
    paymentClaimBadge: "قال إنه دفع",
    pendingCountOne: "طلب واحد بانتظار المراجعة",
    pendingCountTwo: "طلبان بانتظار المراجعة",
    running: "الحساب",
    // شريط الملخّص في الشاشات الضيّقة (راوند 10). اللافتة بتقول رايح فين، مش
    // إنت فين: الزرار ده بينقل بين المنيو والحساب، والاتنين على بُعد لمسة.
    // UX-round D19 — اسم مكان، مش فعل. «افتح الحساب» كانت شبه «اقفل الفاتورة» وهُمّا
    // على بُعد ٤ ملّي من بعض، والزرار ده تبديل شاشة مش إجراء على الفاتورة.
    tillShowBill: "الحساب",
    tillShowMenu: "قائمة الطعام",
    tillUnsentCountOne: "طبق لم يُرسل",
    tillUnsentCountTwo: "طبقان لم يُرسلا",
    tillUnsentCountFew: "{count} أطباق لم تُرسل",
    tillUnsentCount: "{count} طبق لم يُرسل",
    tillBackToFloor: "رجوع إلى الصالة",
    addItems: "إضافة أصناف",
    chooseRequiredAddons: "اختر الإضافات المطلوبة أولًا.",
    // B21 — الاختيار ده بيلعب في نفس المكوّن اللي البديل غيّره، والسيرفر بيرفض
    // الاتنين مع بعض. بنقفله هنا بدل ما الطلب كله يترفض بعد ما الجرسون يكون
    // قال للزبون تمام. نفس منطق جملة الرفض في `serverErrors.swapDuplicateRemoval`.
    addonSwapCollision:
      "تغيّر هذه الاختيارات الإلزامية المكوّن نفسه الذي غيّره البديل. اتركها كما هي، أو غيّر البديل.",
    // زيادة الكمية للاختيار الواحد (S10). كلمة واحدة جوّه الشريحة والجملة
    // جنبها — الشريحة `whitespace-nowrap` من غير `overflow-hidden`.
    addonDecrease: "إنقاص",
    addonIncrease: "زيادة",
    // بدائل المكوّنات (D61). من غير `tracking` ولا `uppercase` على العربي:
    // المسافات بتفكّ الحروف المتّصلة.
    swapTitle: "تبديل مكوّن",
    // Names the INGREDIENT, not the dish: a bare "شيل" on a menu tile reads
    // as "take this dish off the menu".
    swapRemoveLabel: "إزالة مكوّن",
    swapAddLabel: "استخدام بدلًا منه",
    swapPick: "اختيار",
    swapSearch: "ابحث عن مكوّن",
    swapNoMatch: "لا يوجد مكوّن بهذا الاسم",
    // دلوقتي بقى فيه ادعاءين مختلفين على نفس الشيبة، والفرق بينهم هو نفسه
    // جواب B18: الأول حساب على رصيد محدش عدّه، والتاني قرار حد واقف قدام
    // الصينية. لو الاتنين ليهم نفس الكلمة، الصالة بتتعوّد تعدّي على الاتنين.
    swapOutOfStock: "الرصيد صفر",
    swapMarkedOut: "أبلغ المطبخ بنفاده",
    swapAddAnother: "+ بديل آخر",
    // ── مكوّن المطبخ قال إنه خلص (B18) ────────────────────────────────────
    // مش نفس «نفد» بتاعة الصنف: الصنف اللي نفد مش بيتباع خالص، أما ده الصنف
    // لسه بيتباع عادي وإحنا بنقول للجرسون قبل ما يوعد الزبون.
    menuIngredientOut: "مكوّن نافد",
    menuIngredientOutHint: "نفد في المطبخ: {items}",
    ingredientWarnTitle: "نفد أحد مكوّنات هذا الصنف",
    ingredientWarnBody:
      "يحتوي «{item}» على {items}، وقد أبلغ المطبخ بنفادها. يمكنك المتابعة، لكن يُفضّل التأكد من المطبخ أو تبديل المكوّن قبل تأكيد الطلب للعميل.",
    ingredientWarnConfirm: "متابعة وإضافة الصنف",
    // على الفاتورة: إيه اللي كان خلصان وقت ما الطبق اتحط.
    plateIngredientWarning: "بيع عند نفاد {items}",
    plateIngredientWarningSwap: "استُبدل بمكوّن من {items} وهي نافدة",
    swapChosen: "البدائل المختارة",
    swapFree: "بلا فرق",
    swapCharge: "فرق السعر",
    swapConfirm: "تبديل",
    swapNothingToRemove:
      "لا يحتوي هذا الصنف على مكوّنات مسجّلة، لذلك لا يوجد ما يمكن تبديله. اطلب من الإدارة تسجيل الوصفة أولًا.",
    // Says "one of them" rather than "this one": a dialog can now carry several
    // swaps (D67), and one unpriced pair beside a priced one made the note read
    // as if the whole plate were free while the button showed a charge.
    swapUnpricedNote:
      "هناك مكوّن بلا سعر حتى الآن، لذلك سيكون تبديله مجانيًا وسيُسجّل في مراجعة الخزينة.",
    /* حاجة تانية خالص عن اللي فوق: ده مش تبديل ببلاش، ده تبديل ماينفعش
       نحسبه أصلاً — المكوّن اللي بيتشال مابقاش في وصفة الصنف، فمفيش كمية
       نضرب فيها سعر الجرام. والسيرفر بيرفضه (NOT_ON_RECIPE)، فالزرار
       بيتقفل بدل ما يقول «من غير فرق» لحد ما الويتر يشيله. */
    swapUnpriceableNote:
      "يزيل أحد تبديلات هذا الطبق مكوّنًا لم يعد في وصفة الصنف، لذلك لا توجد كمية يمكن حساب فرق السعر عليها. افتح التبديل وأزله ثم احفظ بقية التغييرات؛ لا يمكن إرسال الطبق مع هذا التبديل.",
    // القاعدة دي مكتوبة على الشاشة عن قصد (D54.14): قبل كده كانت في تعليق
    // في الكود بس، وضيف اتحاسب غلط.
    swapCompositionRule:
      "يُضاف فرق التبديل كاملًا إلى سعر الطبق، ويُحسب خصم الفترة على سعر الصنف فقط، لا على فرق التبديل. وإذا أُدخل سعر يدوي للصنف، يُضاف الفرق فوقه أيضًا.",
    swapNeverRefunds:
      "لا يزيد التبديل السعر إلا إذا كان البديل أغلى؛ ولا يُعاد فرق إذا كان أرخص.",
    // تعديل بدائل طبق اتبعت للمطبخ (B19). الجرسون بيغيّر رأيه عند الترابيزة،
    // والطبق لسه في الطابور — فبدل ما نلغي الطبق ونعمله تاني (وده بيسجّل
    // "إلغاء" على الجرسون عشان الزبون غيّر رأيه) بنعدّل البدائل عليه.
    swapAmend: "تغيير البديل",
    swapAmendTitle: "تغيير بدائل الطبق",
    swapAmendConfirm: "حفظ التغيير",
    // الجملة اللي بتتقال في وضع الطباعة بس: الشيف ماسك ورقة، والورقة
    // مابتتحدّثش. قرار المالك 2026-09-10 — الجرسون هو اللي يبلّغ المطبخ.
    swapAmendTellKitchen:
      "يعتمد المطبخ على التذاكر الورقية، ولن تتغير التذكرة الموجودة مع الطاهي. أبلغ المطبخ بالتعديل قبل الحفظ.",
    // أقوى من اللي فوقيها عن قصد: الطبق ده الورقة بتاعته اتطبعت، والشيف ممكن
    // يكون فعلاً بدأ فيه. القرار (المالك، 2026-09-10) إن ده مسؤولية الجرسون
    // مش مسؤولية قفل في البرنامج.
    swapAmendKitchenMayHaveStarted:
      "طُبعت التذكرة وربما بدأ الطاهي إعداد هذا الطبق بالفعل. أبلغ المطبخ أولًا، ثم احفظ.",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    swapAmendOnePlateOne:
      "يحتوي هذا الصف على طبق واحد. سيُطبّق التغيير عليه ويظهر في صف مستقل.",
    swapAmendOnePlateTwo:
      "يحتوي هذا الصف على طبقين متماثلين. سيُطبّق التغيير على طبق واحد فقط، وسيظهر في صف مستقل.",
    swapAmendOnePlateFew:
      "يحتوي هذا الصف على {count} أطباق متماثلة. سيُطبّق التغيير على طبق واحد فقط، وسيظهر في صف مستقل.",
    swapAmendOnePlate:
      "يحتوي هذا الصف على {count} طبقًا متماثلًا. سيُطبّق التغيير على طبق واحد فقط، وسيظهر في صف مستقل.",
    swapAmendSaved: "حُدّثت البدائل.",
    swapAmendClear: "إزالة كل البدائل",
    swapFailed: "تعذّر تحميل المكوّنات الآن. حاول مرة أخرى.",
    remake: "إعادة تحضير",
    void: "إلغاء الصنف",
    comp: "على الحساب",
    voidReason: "السبب",
    // DEF-7: a placeholder that reads as a picker, not a text field — a
    // cashier must not be invited to type a reason into something that only
    // accepts these four codes.
    voidReasonPlaceholder: "اختر السبب",
    voidReasonRequired: "اختر سببًا من القائمة للتأكيد",
    reasonCustomerChangedMind: "غيّر العميل رأيه",
    reasonWrongOrder: "طلب خطأ",
    reasonQualityIssue: "مشكلة في الجودة",
    // D44 (B5): أسباب «على الحساب» — قايمة لوحدها، السيرفر بيرفض أي كود من
    // قايمة الإلغاء على الكومب.
    reasonStaffMeal: "وجبة الموظفين",
    reasonGoodwill: "إرضاء العميل",
    reasonKitchenError: "خطأ من المطبخ",
    reasonManagerDiscretion: "قرار المدير",
    // R10: أسباب المرتجع — قايمة تالتة لوحدها، لأن ولا سبب في قايمة الإلغاء
    // ولا الكومب بيجاوب «ليه فلوس اتاخدت رجعت تاني؟».
    reasonWrongCharge: "حُسبت بطريقة خاطئة",
    reasonQuality: "مشكلة في الطعام أو الخدمة",
    reasonDuplicateCharge: "دُفعت مرتين",
    reasonChargeback: "استردها البنك",
    // WRONG_ITEM: الزبون اتقدّم له حاجة مش اللي طلبها — غير «الحساب غلط» (السعر)
    // وغير «الجودة» (نفس الطبق بس وحش). ودي الحالة اللي بتحصل لما البديل نفسه
    // يطلع غلط.
    reasonWrongItem: "قُدّم له صنف خطأ",
    // B117/Q4 — أسباب الإعادة. قايمة رابعة لوحدها: أسباب الإلغاء كلها عن
    // الزبون، والإعادة لازم تفرّق بين غلطة الصالة وغلطة المطبخ، وده كل
    // الهدف من السؤال أصلاً. الكلام هنا وصف لللي حصل، مش اتهام لحد.
    reasonGuestChangedMind: "غيّر الضيف رأيه",
    reasonWrongItemOrdered: "سُجّل صنف غير صحيح",
    reasonCookedWrong: "أُعدّ بطريقة خاطئة",
    reasonDamagedOrCold: "وصل باردًا أو تالفًا",
    reasonOther: "سبب آخر",
    // نفس حقل السبب، بس الكلام بيتغيّر حسب القرار — «ليه على الحساب؟» سؤال
    // تاني خالص عن «ليه اتلغى؟».
    compReason: "سبب الحساب",
    compReasonPlaceholder: "على حساب من؟",
    compReasonRequired: "اختر سببًا من القائمة للتأكيد",
    // نفس حقل السبب كمان مرة — بس «ليه الطبق راجع للمطبخ؟» سؤال تالت، ولازم
    // يقرا كأنه تعليمات للطباخ مش ورقة اتهام.
    remakeReason: "سبب الإعادة",
    remakeReasonPlaceholder: "ماذا حدث؟",
    remakeReasonRequired: "اختر سببًا من القائمة للتأكيد",
    // D43 (86cbdhg4p): حقل منفصل عن قايمة الأسباب، مش بديل عنها — القايمة
    // للتصنيف، والنص ده للتفاصيل اللي حصلت فعلاً.
    voidReasonNoteLabel: "تفاصيل إضافية",
    voidReasonNoteOptionalPlaceholder: "اختياري — ماذا حدث بالتحديد؟",
    // D46 (S9): تسعير يدوي على صنف، لصاحب صلاحية RESTAURANT_INVOICE_PRICE_OVERRIDE بس.
    priceOverrideAction: "سعر مختلف",
    priceOverrideDialogTitle: "سعر مختلف للصنف",
    priceOverrideAmountLabel: "السعر",
    priceOverrideInvalid: "اكتب سعرًا صحيحًا",
    priceOverrideConfirm: "تأكيد السعر",
    // قرار المالك ٢٠٢٦-٠٩-٠٣: اللي بيغيّر السعر بإيده لازم يكتب السبب. إلغاء
    // صنف واحد بيطلب سبب من زمان، والسعر اليدوي أصعب في التتبع لأنه بيحل محل
    // سعر القايمة — فكل التقارير بتقرا الخصم صفر.
    priceOverrideNoteLabel: "السبب",
    priceOverrideNotePlaceholder: "اكتب سبب هذا السعر",
    priceOverrideNoteRequired: "اكتب السبب قبل التأكيد.",
    priceOverrideChip: "سعر يدوي",
    // خصم الشريحة بيتحسب بعد السعر اليدوي — فالرقم اللي المدير بيكتبه مش هو
    // اللي الزبون بيدفعه. بنقول الاتنين عشان محدش يتفاجئ على الطاولة.
    priceOverrideTierNote:
      "تخضع هذه الفاتورة لخصم {percent}%؛ سيدفع العميل {price} للوحدة.",
    // لما السبب "سبب تاني" الحقل بيبقى مطلوب — "سبب تاني" لوحده مبيقولش حاجة.
    voidReasonNoteRequiredPlaceholder: "مطلوب — ماذا حدث بالتحديد؟",
    voidReasonNoteRequired: "اكتب ما حدث؛ لا يكفي اختيار «سبب آخر» وحده",
    close: "إغلاق الفاتورة",
    settle: "تحصيل",
    settlementMethod: "طريقة الدفع",
    cash: "نقد",
    card: "بطاقة",
    // D31 — الفاتورة اللي مشيت من غير دفع. مش طريقة دفع تالتة بالمعنى ده:
    // دي إقرار إن الفلوس مش هتيجي، عشان كده لونها ولهجتها مختلفين عن الكاش
    // والبطاقة في كل شاشة.
    writeOff: "بلا دفع",
    writeOffTitle: "تسجيل الفاتورة بلا دفع",
    writeOffBody:
      "ستُغلق هذه الفاتورة بلا تحصيل: لن تُحتسب في الخزينة أو المتحصّل، وستظهر في مراجعة الخزينة باسمك. أُعدّ الطعام بالفعل، لذلك تبقى ضمن مبيعات اليوم في خانة «بلا دفع».",
    writeOffConfirm: "نعم، سجّلها بلا دفع",
    writeOffNeedsCashier: "يتطلب إغلاق فاتورة بلا دفع صلاحية الكاشير",
    // R1/F3 — مرتجع الفلوس. كل قاعدة بتحدد الرقم مكتوبة على الشاشة نفسها،
    // مش في تعليق في الكود: المدير لازم يقدر يقرا الورقة ويتوقّع المبلغ.
    refundAction: "إعادة المبلغ",
    refundTitle: "مرتجع نقود للعميل",
    refundConfirm: "تأكيد المرتجع",
    refundedToast: "أُعيدت النقود إلى العميل",
    refundMethodLabel: "طريقة إعادة المبلغ",
    refundMethodLockedCash:
      "دُفعت الفاتورة نقدًا، لذلك يُعاد المبلغ نقدًا من هذه الخزينة. لا يمكن اختيار طريقة أخرى هنا؛ وإلا سيظل رصيد الخزينة ناقصًا.",
    refundMethodLockedCard:
      "دُفعت الفاتورة بالبطاقة، لذلك يُعاد المبلغ إلى البطاقة نفسها. لا تتأثر الخزينة بهذا المرتجع.",
    // D44 — التحويل. الجملة لازم تقول الحاجتين: بترجع إزاي، والدرج بيتأثر
    // ولا لأ. الفلوس دخلت البنك مش الدرج، فبترجع من البنك، والكاشير اللي
    // بيعدّ الفلوس آخر الوردية ما بيتغيّرش عنده حاجة.
    refundMethodLockedInstapay:
      "دُفعت الفاتورة بتحويل إنستاباي، لذلك يُعاد المبلغ بتحويل إلى حساب العميل. لا تتأثر الخزينة أو جهاز البطاقة؛ فالمبلغ كان في الحساب البنكي ويُعاد منه.",
    // العدد جوه قوس كوحدة قياس، مش موصوف بجمع: كده الجملة تفضل سليمة مع أي
    // رقم (ساعة / ساعتين / 4 ساعات) من غير ما نبني قواعد تصريف في الواجهة.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    refundLateWarningHoursOne:
      "مرّ على دفع هذه الفاتورة أكثر من الحد المسموح (ساعة واحدة).",
    refundLateWarningHoursTwo:
      "مرّ على دفع هذه الفاتورة أكثر من الحد المسموح (ساعتان).",
    refundLateWarningHoursFew:
      "مرّ على دفع هذه الفاتورة أكثر من الحد المسموح ({hours} ساعات).",
    refundLateWarningHours:
      "مرّ على دفع هذه الفاتورة أكثر من الحد المسموح ({hours} ساعة).",
    refundLateWarningMinutes:
      "مرّ على دفع هذه الفاتورة أكثر من الحد المسموح ({minutes} دقيقة).",
    /* الذيل اللي كان متكرر حرفياً جوّه أربع صيغ الجمع + صيغة الدقايق. جملة
       واحدة سايبة جنب العدد على نفس الشاشة — مش ورا باب: دي مش شرح، دي
       اللي بتقول إن المرتجع هيعدّي بالفعل. */
    refundLateRecordedNote:
      "المرتجع مسموح وسيُنفّذ، لكنه سيُسجّل «متأخرًا» ويظهر كذلك في مراجعة الخزينة وتقرير اليوم.",
    refundAlreadyNote:
      "سبق رد مبلغ {amount} من هذه الفاتورة. لا يمكن رد الصنف نفسه مرتين.",
    // B29 — أرقام الفاتورة نفسها مش مظبوطة: رسم الخدمة والضريبة المتسجلين
    // عليها أكبر من مجموع أصنافها. الزبون بياخد فلوسه كاملة، اللي بينقص هو
    // الجزء اللي بيترد من الضريبة. بنقول ده قبل التأكيد ومابنمنعش — نفس
    // أسلوب باقي التنبيهات هنا (D42/D75).
    refundChargeSnapshotWarning:
      "أرقام هذه الفاتورة غير متطابقة: يتجاوز رسم الخدمة والضريبة المسجلان مجموع الأصناف بمبلغ {amount}. سيُنفّذ المرتجع وسيسترد العميل كامل حقه، لكن الجزء المعاد من الضريبة سينخفض بهذا المبلغ. إذا لم يكن ذلك متوقعًا، فراجع الفاتورة أولًا.",
    refundPickPlates: "اختر الأصناف التي ستُرد",
    refundSelectAll: "تحديد الكل",
    refundClearAll: "إلغاء التحديد",
    refundAllPlatesBack:
      "رُدت جميع أصناف هذه الفاتورة بالفعل. لا يوجد صنف آخر يمكن رده.",
    refundPlateAlreadyBack: "رُد سابقًا",
    // F17 — رسم التوصيل بقى يرجع، وده اختيار الكاشير في كل مرتجع لوحده.
    refundDeliveryFee: "رسم التوصيل",
    refundDeliveryFeeHint:
      "حدده إذا كانت المشكلة في التوصيل نفسه، مثل تأخره أو وصوله إلى عنوان خاطئ مع سلامة الطعام. يُعاد الرسم وضريبته معًا.",
    refundDeliveryFeeAlreadyBack: "أُعيد الرسم سابقًا",
    refundBackToCustomer: "المُعاد إلى العميل",
    refundSplitUnavailable:
      "تعذّر توزيع مبلغ المرتجع على الأصناف اعتمادًا على أرقام هذه الفاتورة. ما زال تنفيذ المرتجع متاحًا؛ إذ يحسب الخادم المبلغ النهائي المعتمد.",
    /* UX-round D22 — الست قواعد راحت ورا باب. اللي فضل على الشاشة هو الجملة اللي
       بتفسّر «غياب»: مفيش خانة مبلغ. الغياب مالوش سطر يختبي وراه. */
    refundRulePerPlateShort: "تُرجع أصنافًا، لا مبلغًا تكتبه.",
    /* UX-round D24 — الورقة نفسها. (علامة «لسه ما اتدفعتش» اتشالت — قرار
       المؤسس 2026-10-08: الورقة اللي قبل الدفع هي الفاتورة اللي الزبون بيدفع
       منها.) «طريقة الدفع مش متسجّلة» هي منطق «قول اللي ما تعرفهوش»: فاتورة
       اتدفعت ومالهاش طريقة مسجّلة ما تسكتش عن طريقة الدفع. */
    receiptMethodNotRecorded: "غير مسجّلة",
    receiptSettledAtLabel: "وقت الدفع",
    refundRulesDoorLabel: "كيف حُسب هذا المبلغ؟",
    refundRulesTitle: "طريقة حساب المرتجع",
    refundRuleTermPlates: "أصناف، لا مبلغ يدوي",
    refundRuleTermCharges: "الخدمة والضريبة",
    refundRuleTermMethod: "طريقة الرجوع",
    refundRuleTermDrawer: "الخزينة المتأثرة",
    refundRuleTermBill: "الفاتورة الأصلية",
    refundRuleTermCost: "التكلفة والهامش",
    refundRulePerPlate:
      "تُرجع أصنافًا، لا مبلغًا تكتبه: يُحسب المبلغ من الفاتورة نفسها.",
    refundRuleCharges: "تُعاد الخدمة والضريبة بنسبة كل صنف، لا بالكامل.",
    refundRuleMethod:
      "تُعاد النقود بطريقة الدفع نفسها، ولا يمكن تغييرها من هنا.",
    refundRuleDrawer:
      "يُحسب المرتجع على الخزينة المفتوحة الآن، لا على خزينة الفاتورة.",
    refundRuleBillUnchanged:
      "لا تتغيّر الفاتورة الأصلية: تبقى أرقامها كما هي، ويُسجّل المرتجع بجانبها.",
    refundRuleCost: "لا تتغيّر التكلفة إلا إذا اخترت «مرتجع من المطبخ»: فقد تم إعداد الطعام بالفعل، والذي ينخفض هو الإيراد والربح. أما «مرتجع من المطبخ» فيعيد المكونات إلى المخزون ويخصم تكلفتها من تكلفة المبيعات.",
    refundReason: "سبب المرتجع",
    refundReasonPlaceholder: "سبب إعادة المبلغ",
    refundReasonRequired: "اختر سببًا من القائمة للتأكيد",
    refundReasonNoteRequired: "يتطلب «سبب آخر» ملاحظة توضّح ما حدث",
    refundPrintReceipt: "طباعة إيصال المرتجع",
    // أكتر من مرتجع على نفس الفاتورة: كل واحد له زرار وله رقم، عشان الكاشير
    // يعرف هو بيطبع أنهي مرتجع قبل ما يدوس — مش «اطبع إيصال المرتجع» مكرر
    // مرتين بنفس الكلام.
    refundPrintReceiptOne: "طباعة المرتجع {reference} · {amount}",
    refundReceiptTitle: "إيصال مرتجع",
    refundReceiptAgainst: "على فاتورة رقم {sequence}",
    refundReceiptBillDate: "تاريخ الفاتورة: {date}",
    refundReceiptSplitUnavailable:
      "تفاصيل الأصناف غير متاحة على هذا الإيصال. المبلغ المعاد مسجّل بالكامل.",
    refundReceiptOriginalStands:
      "تبقى الفاتورة الأصلية كما هي دون تغيير. يسجّل هذا الإيصال المبلغ المعاد فقط.",
    // رقم المرتجع نفسه على الورقة: مرتجعين جزئيين من نفس الفاتورة كانوا
    // بيطلعوا ورقتين ما بينفعش تفرّق بينهم غير بالوقت.
    refundReceiptReference: "رقم المرتجع",
    // الوقت لازم يكون مسمّى: كان بيطلع رقم لوحده تحت «تاريخ الفاتورة»، والزبون
    // ما بيعرفش أنهي تاريخ إيه. وعلى الصورة بقى جنبه وقت طباعة الصورة، فلازم
    // كل واحد يقول هو إيه.
    refundReceiptMovedAt: "وقت رجوع النقود: {stamp}",
    refundReceiptCopyTitle: "صورة من إيصال المرتجع",
    // الصورة بقت صورة من مرتجع واحد بالظبط: نفس الرقم، نفس الوقت، نفس
    // الأصناف، نفس المبلغ. فالسطر ده بيقول ده بالظبط بدل ما يقول «بتغطي كل
    // اللي رجع من الفاتورة» — الكلام القديم كان بيوصف حد قديم اتشال.
    refundReceiptCopyNote:
      "هذه النسخة للمرتجع رقم {reference} فقط، وتتضمن الأصناف والمبلغ نفسيهما في الأصل. إذا تعددت مرتجعات الفاتورة، فلكل مرتجع إيصاله.",
    // ورقة رسمية من غير أي تاريخ عليها حاجة غلط، حتى لو التاريخ ده مش وقت
    // خروج الفلوس. فالصورة بتطبع وقت طباعتها هي، مسمّى كده بالظبط، عشان
    // محدش يقراه على إنه وقت المرتجع.
    refundReceiptCopyPrintedAt: "طُبعت هذه النسخة: {stamp}",
    // R9 — مفيش صلاحية، فمفيش زرار. بس الشاشة بتقول السبب: زرار غايب من غير
    // كلمة بيعلّم الكاشير إن البرنامج ما بيعملهاش، والفلوس بتخرج من الدرج
    // من غير تسجيل — وده بالظبط اللي الميزة دي اتعملت عشان تمنعه.
    refundNeedsPermission:
      "تحتاج إعادة المبلغ إلى صلاحية خاصة غير متاحة لك. اطلب من صاحب المطعم تنفيذها من هذه الشاشة.", // B118/QA5 — كانت بتقول «اطلب من المدير»، والمدير كمان معندوش الصلاحية
    // دي: `system-role.catalog.ts` مستثنيها من بريسِت المدير عن قصد. فكانت
    // بتبعت الكاشير للشخص الغلط، والمدير نفسه بيقرا نفس الجملة اللي بتقوله
    // يسأل مدير. `pos.gridDeniedBody` بتقول «صاحب المطعم» صح من الأول.
    // القرار 25 على شاشة المرتجع نفسها، مش كلام شاشة التحصيل: الحدث مختلف،
    // والكارت لازم وردية مفتوحة رغم إن الدرج ما بيتمسّش.
    refundNoShiftWarning:
      "لا توجد خزينة مفتوحة، لذلك سيُرفض المرتجع. افتح خزينة أولًا ليُسجّل خروج النقود منها.",
    refundNoShiftWarningAskManager:
      "لا توجد خزينة مفتوحة، لذلك سيُرفض المرتجع. اطلب من شخص لديه صلاحية إدارة الخزائن فتح خزينة.",
    refundNoShiftWarningCard:
      "سيُعاد المبلغ إلى البطاقة ولن تتأثر الخزينة، لكن يجب تسجيل المرتجع على خزينة مفتوحة. افتح خزينة أولًا.",
    refundNoShiftWarningCardAskManager:
      "سيُعاد المبلغ إلى البطاقة ولن تتأثر الخزينة، لكن يجب تسجيل المرتجع على خزينة مفتوحة. اطلب من شخص لديه صلاحية إدارة الخزائن فتح خزينة.",
    print: "طباعة",
    acceptPending: "قبول",
    acceptPendingAll: "قبول الكل",
    rejectPending: "رفض",
    pendingTitle: "طلبات من الضيف",
    pendingGuest: "ضيف {guestNumber}",
    pendingAskedOne: "الضيف طلب صنفًا واحدًا",
    pendingAskedMany: "طلب الضيوف {count} أصناف",
    pendingFromQr: "من قائمة QR على الطاولة",
    pendingAddsToBill: "يُضاف إلى الفاتورة",
    acceptPendingSend: "قبول وإرسال",
    acceptPendingAllSend: "قبول الكل وإرسال",
    waiterHandoffTitle: "الويتر أرسل هذا الطلب إليك",
    closeBlockedByPending:
      "اقبل طلبات الضيف أو ارفضها أولًا قبل إغلاق الفاتورة.",
    // The stale-close recovery. Deliberately not a generic error: it tells the
    // cashier what happened and what to do.
    conflictTitle: "تغيّرت الفاتورة",
    conflictBody: "عدّل شخص آخر هذه الفاتورة. حدّث الصفحة وحاول مرة أخرى.",
    // D22's other stale-close refusal: the close gate runs before the version
    // guard, so the same "your screen is behind" moment arrives as a pending
    // item instead of a version conflict.
    conflictPendingBody:
      "تتضمن الفاتورة أصنافًا لم تصل إلى المطبخ بعد، أو طلبات من الضيف تنتظر الرد. حدّث الصفحة وراجع المستجدات قبل الإغلاق.",
    // P4.1 — the tap had already gone through (its answer was lost) and the
    // bill moved after it. Say it was done, so nobody does it twice.
    conflictReplayedBody:
      "تم تنفيذ هذا الطلب من قبل، وتغيّرت الفاتورة بعده. حدّث الصفحة وراجعها قبل أن تكمل — لا تكرّر الطلب.",
    refresh: "تحديث",
    // Empty states say WHY they are empty and what to do next
    // (docs/design/components-states-motion.md), so each is a title + a body.
    itemsEmptyTitle: "الفاتورة فارغة",
    itemsEmptyBody: "أضف الأصناف التي طلبها العميل، وسيُحدّث الحساب فورًا.",
    gridEmptyTitle: "لا توجد طاولات حتى الآن",
    gridEmptyBody: "أضف المناطق والطاولات أولًا، وستظهر هنا جاهزة للخدمة.",
    gridEmptyAction: "إعداد الطاولات",
    // شاشة الطاولات محتاجة صلاحية الكاشير زي كل نداءات الـ POS. اللي مش معاه
    // بيشوف السبب ومخرج بدل شبكة كلها 403.
    gridDeniedTitle: "شاشة الطاولات غير متاحة لك",
    gridDeniedBody:
      "تحتاج هذه الشاشة إلى صلاحية الكاشير. تواصل مع صاحب المطعم لإضافتها إلى دورك إذا كان عملك يتطلب إدارة الطاولات.",
    gridDeniedToKitchen: "الانتقال إلى شاشة المطبخ",
    gridDeniedToManage: "الانتقال إلى الإدارة",
    stagedTitle: "جاهز للإضافة",
    increaseQuantity: "زيادة الكمية",
    decreaseQuantity: "تقليل الكمية",
    removeStagedItem: "إزالة الصنف",
    itemsAddedToast: "تمت إضافة الأصناف إلى الفاتورة",
    firedToast: "أُرسلت إلى المطبخ",
    serveAction: "تقديم",
    printChit: "طباعة التذكرة",
    closeBlockedHint: "هناك أصناف لم تصل إلى المطبخ بعد؛ أرسلها أو أزلها أولًا",
    // T6 / D173 — why a greyed Close is greyed (both tills), and the one tap
    // that clears it. D22 stays a block.
    closeReasonPendingOne: "تعذّر الإغلاق: طبق واحد طلبه الضيف ينتظر القبول",
    closeReasonPendingTwo: "تعذّر الإغلاق: طبقان طلبهما الضيف ينتظران القبول",
    closeReasonPendingFew: "تعذّر الإغلاق: {count} أطباق طلبها الضيف تنتظر القبول",
    closeReasonPending: "تعذّر الإغلاق: {count} طبقًا طلبه الضيف ينتظر القبول",
    closeReasonDraftsOne: "تعذّر الإغلاق: طبق واحد لم يُرسَل إلى المطبخ",
    closeReasonDraftsTwo: "تعذّر الإغلاق: طبقان لم يُرسَلا إلى المطبخ",
    closeReasonDraftsFew: "تعذّر الإغلاق: {count} أطباق لم تُرسَل إلى المطبخ",
    closeReasonDrafts: "تعذّر الإغلاق: {count} طبقًا لم يُرسَل إلى المطبخ",
    closeReasonQueuedOne: "تعذّر الإغلاق: طبق واحد لم يبدأ تحضيره",
    closeReasonQueuedTwo: "تعذّر الإغلاق: طبقان لم يبدأ تحضيرهما",
    closeReasonQueuedFew: "تعذّر الإغلاق: {count} أطباق لم يبدأ تحضيرها",
    closeReasonQueued: "تعذّر الإغلاق: {count} طبقًا لم يبدأ تحضيره",
    closeStartAndClose: "بدء التحضير والإغلاق",
    // The PAPER settle window's tap only starts the food — its own Pay is
    // the close (gate P7 review #2).
    closeStartOnly: "بدء التحضير",
    closeResendTicket: "إعادة إرسال التذكرة",
    // 2026-10-20 — «طباعة التذكرة» انتقلت إلى زر الطابعة؛ السبب يدلّ عليها.
    closePrintTicketHint: "«طباعة التذكرة» من زر الطابعة",
    // T6 / D173 — an off-premise close while food is still cooking warns,
    // never blocks (only where the workflow records READY; never on a direct
    // takeaway).
    stillCookingTitle: "ما زال الطعام في التحضير",
    stillCookingBodyOne: "طبق واحد لم يجهز بعد. هل تريد إغلاق الفاتورة الآن؟",
    stillCookingBodyTwo: "طبقان لم يجهزا بعد. هل تريد إغلاق الفاتورة الآن؟",
    stillCookingBodyFew: "{count} أطباق لم تجهز بعد. هل تريد إغلاق الفاتورة الآن؟",
    stillCookingBody: "{count} طبقًا لم يجهز بعد. هل تريد إغلاق الفاتورة الآن؟",
    stillCookingLine: "{dish} · {station}",
    stillCookingClose: "إغلاق الفاتورة",
    stillCookingWait: "انتظار",
    // T11 — an 86'd dish in the basket warns at fire; the send continues.
    outAtFireTitle: "صنف نفد",
    outAtFireBody: "نفد «{item}» بعد إضافته. هل تريد الإرسال رغم ذلك؟",
    outAtFireSend: "إرسال رغم ذلك",
    outAtFireCancel: "إلغاء",
    // T11 / D173 — the till hears an 86 (not the person who marked it; no chime).
    menuItemOutToast: "نفد «{item}» — أوقِفه على تطبيقات التوصيل أيضًا",
    closeBlockedQueuedHint:
      "توجد أصناف تنتظر في المطبخ — ابدأ تحضيرها أو اطبع التذكرة أو أزلها أولًا",
    closedToast: "أُغلقت الفاتورة",
    reopen: "فتح مرة أخرى",
    reopenedToast: "أُعيد فتح الفاتورة",
    // UX-round D17 — the two ways the close's undo can fail to happen. Both name the
    // permanent door, because the toast is the convenience and «فتح تاني» on
    // الحساب is the guarantee.
    closeUndoExpired:
      "انتهت مهلة التراجع وما زالت الفاتورة مغلقة. أعد فتحها من «فتح مرة أخرى»",
    closeUndoFailed:
      "تعذّر إعادة فتح الفاتورة وما زالت مغلقة. حاول من «فتح مرة أخرى»",
    settleTitle: "تحصيل الفاتورة",
    // Audit round 2 (5.1) — on a dine-in bill the settle title names the
    // table, as the off-premise sheet names the bag (a shared tablet).
    settleTitleTable: "تحصيل حساب طاولة {table}",
    settleTitleTables: "تحصيل حساب الطاولات {name}",
    tenderedLabel: "المبلغ المستلم",
    settleShortHint: "المبلغ أقل من الفاتورة",
    settleConfirm: "تحصيل",
    // D25: the settle refusal explained BEFORE the tap, not after — shown when
    // `shiftTrackingEnabled` is on and `shifts/current` says none is open.
    //
    // «درج» مش «وردية» (Unit C): اللي بنتابعه صندوق فلوس ليه اسم وليه صاحب،
    // والمحل يقدر يفتح أكتر من واحد في نفس الوقت (D95) — فكلمة «الوردية»
    // بقت بتوصف حاجة تانية خالص.
    noShiftWarning: "لا توجد خزينة مفتوحة؛ سيُرفض التحصيل حتى تفتح خزينة.",
    // نفس التحذير لواحد مش معاه صلاحية الأدراج (الكاشير الجاهز معاه، الويتر لأ).
    noShiftWarningAskManager:
      "لا توجد خزينة مفتوحة؛ سيُرفض التحصيل. اطلب من شخص لديه صلاحية إدارة الخزائن فتح خزينة.",
    noShiftWarningOpen: "فتح خزينة",
    // Unit C — الشريط اللي على كل شاشات الكاشير، والديالوج اللي بيفتح الدرج من
    // غير ما تسيب الفاتورة.
    drawerStripHolder:
      "لا توجد خزينة مفتوحة. يمكنك متابعة العمل، لكن لن تتمكن من تحصيل النقود حتى تفتح خزينة.",
    drawerStripAskManager:
      "لا توجد خزينة مفتوحة. يمكنك متابعة العمل، لكن سيُرفض التحصيل. اطلب من شخص لديه صلاحية إدارة الخزائن فتح خزينة.",
    openDrawerTitle: "فتح خزينة",
    openDrawerBody:
      "اكتب اسم الصندوق الموجود أمامك ورصيده الحالي. ستُحتسب النقود التي تحصّلها على هذه الخزينة باسمك.",
    openDrawerLabelLabel: "الخزينة",
    openDrawerLabelHint: "اسم الصندوق: «خزينة 1»، «الصالة»، «البار».",
    openDrawerLabelPlaceholder: "خزينة 1",
    openDrawerFloatLabel: "الرصيد الحالي في الخزينة",
    openDrawerFloatHint:
      "عُدّ المبلغ قبل البدء؛ فمنه سيُحسب الفرق في نهاية اليوم.",
    openDrawerTaken:
      "«{label}» مفتوح باسم {name}. افتح صندوقًا آخر، أو استلمه منه من شاشة الخزينة.",
    openDrawerSomeoneElse: "شخص آخر",
    openDrawerConfirm: "فتح الخزينة",
    /* UX-round D23/R8 — زرار مرمّد من غير سبب بيسيب الكاشير بيخمّن. الزرار فاضل
       مضغوط، والسبب بيتقال. */
    openDrawerNeedsLabel: "اكتب اسم الخزينة أولًا.",
    openDrawerNeedsFloat:
      "عُدّ المبلغ الموجود في الخزينة واكتب الرصيد الافتتاحي أولًا.",
    drawerOpenedToast: "فُتحت الخزينة",
    /* A diner's own claim to have paid (D44). "قال إنه دفع", never "دفع":
       the money has not arrived until a cashier says it has. */
    dinerPaymentTitle: "العميل قال إنه دفع",
    /* D133 fix — نقد أو بطاقة على الطاولة: الزبون بيطلب يدفع، لسه مادفعش. */
    dinerPaymentTitleAsk: "العميل يطلب الدفع",
    instapay: "إنستاباي",
    // F2 — التطبيق خد فلوس الزبون وهيحوّل للمطعم بعدين. مش فلوس في الدرج.
    aggregatorMethod: "التطبيق",
    aggregatorSettleHint:
      "حصّلت {name} المبلغ من العميل وستحوّله إلى المطعم لاحقًا بعد خصم {rate}%. لن يدخل هذا المبلغ في الخزينة.",
    dinerPaymentInstapay: "إنستاباي",
    dinerPaymentCash: "نقد عند الكاشير",
    dinerPaymentCard: "بطاقة عند الكاشير",
    dinerPaymentViewProof: "عرض صورة التحويل",
    dinerPaymentConfirm: "تأكيد استلام المبلغ",
    dinerPaymentReject: "رفض",
    dinerPaymentAmount: "المبلغ",
    dinerPaymentCollectNow: "تحصيل {amount}",
    dinerPaymentWontPay: "لن يدفع الآن",
    dinerPaymentVerified: "تم التأكيد",
    dinerPaymentRejected: "رُفض",
    dinerPaymentNote:
      "لا يؤدي التأكيد إلى إغلاق الفاتورة؛ يجب تسويتها بعد ذلك.",
    /* B118 round table — الرقم اللي الزبون بعته اتسجّل وقت ما بعته، والفاتورة
       بتتحرك بعده. الاتجاهين مش نفس المشكلة: الناقص ليه حل عندك، والزيادة
       معندهاش طريق رجوع في المنتج ده (تحويل ما وصلش أي درج). */
    dinerPaymentShort:
      "إجمالي الفاتورة الآن {total}، والمبلغ المرسل أقل بمقدار {gap}. أكّد الاستلام وحصّل الباقي قبل الإغلاق.",
    dinerPaymentOver:
      "إجمالي الفاتورة الآن {total}، والمبلغ المرسل يزيد بمقدار {gap}. لم يدخل التحويل إلى الخزينة، لذلك لا يمكن إعادة الفرق من هنا. تواصل مع المدير قبل التأكيد.",
    dinerPaymentNoProof: "لا توجد صورة — سيدفع العميل عند الكاشير.",
    /* D133 — نصيب من الفاتورة وبقشيش. المبلغ نصيب من الفاتورة فقط، والبقشيش منفصل
       عنه ولا يدخل الفاتورة. */
    dinerPaymentShareEvenOne: "الفاتورة على شخص واحد — هذا نصيبه",
    dinerPaymentShareEvenTwo: "الفاتورة مقسّمة بالتساوي على شخصين — هذا نصيب واحد منها",
    dinerPaymentShareEvenFew: "الفاتورة مقسّمة بالتساوي على {count} أشخاص — هذا نصيب واحد منها",
    dinerPaymentShareEven: "الفاتورة مقسّمة بالتساوي على {count} شخصًا — هذا نصيب واحد منها",
    dinerPaymentShareItemsOne: "عن طبق واحد من طلبه",
    dinerPaymentShareItemsTwo: "عن طبقين من طلبه",
    dinerPaymentShareItemsFew: "عن {count} أطباق من طلبه",
    dinerPaymentShareItems: "عن {count} طبقًا من طلبه",
    dinerPaymentTip: "+ بقشيش {percent}% بقيمة {amount} — للفريق وليس على الفاتورة",
    dinerPaymentTipAmount: "+ بقشيش بقيمة {amount} — للفريق وليس على الفاتورة",
    dinerPaymentCollect: "المطلوب تحصيله إجمالًا {amount}",
    dinerPaymentSettleShare: "تسوية هذا النصيب",
    dinerPaymentShareSettled: "تمت تسوية هذا النصيب",
    dinerPaymentAskPartSettled: "تمت تسوية جزء من الطلب — الباقي {remaining} على الفاتورة",
    dinerPaymentTipToDrawer: "تسجيل البقشيش {amount} في الخزينة",
    dinerPaymentTipInDrawer: "تم تسجيل البقشيش في الخزينة",
    dinerPaymentTipReason: "بقشيش من العميل مع دفع الفاتورة",
    fireGroup: "إرسال",
    fireGroupOnly: "إرسال هذا الطبق فقط",
    noteChip: "ملاحظة",
    noteDialogTitle: "ملاحظة للمطبخ",
    notePlaceholder: "مثال: بلا بصل",
    noteQuickPicks: "بدون بصل|حار|بدون ملح|الصوص على الجنب|مستوي جيدًا",
    noteQuickJoin: "، ",
    noteSave: "حفظ",
    noteClear: "مسح الملاحظة",
    voidCountLabel: "عدد الأطباق",
    /* D141 — إلغاء أو مرتجع صنف اتحضّر: الكاشير بيقول الصنف راح فين. نفس
       كلمتين دفتر الحركة («مرتجع من المطبخ» / «هالك»). «مرتجع من المطبخ» بيظهر
       على كارت الصنف؛ «هالك» مش بيكتب صف (التكلفة فاضلة زي ما هي). الافتراضي
       «هالك». PO-10 (phase 12): السؤال محايد — المشروب مش «طعام مُحضَّر». */
    returnToStockLabel: "أين ذهب الصنف؟",
    returnToStockWasted: "هالك",
    returnToStockReturned: "مرتجع من المطبخ",
    returnToStockHintWasted: "الهالك تبقى تكلفته محسوبة في تكلفة المبيعات، ولا يرجع شيء إلى المخزون.",
    /* P10-D10 — الكاشير ما يشوفش الوصفات: طبق من غير وصفة (مشروب) ما فيهوش
       حاجة ترجع، والسيرفر بيتخطّاه. فالنص ما يوعدش بعدد. */
    returnToStockHintReturned: "تعود المكونات إلى المخزون بتكلفتها الأصلية، وتُخصم تكلفتها من تكلفة المبيعات. الصنف الذي ليس له وصفة لا يوجد ما يُرجَع منه.",
    returnToStockAppliesToCooked: "يسري فقط على ما حُضِّر من الأصناف المختارة؛ الباقي لم يُحضَّر.",
    transferTable: "نقل إلى طاولة أخرى",
    headTransferShort: "نقل الطاولة",
    headGuestsShort: "الضيوف",
    transferTitle: "نقل الفاتورة إلى طاولة",
    transferDone: "نُقلت الفاتورة",
    transferNoFreeTables: "لا توجد طاولات فارغة الآن",
    tierPickerTitle: "التسعير",
    tierBase: "السعر الأساسي",
    settledToast: "تم التحصيل",
    statusOpen: "مفتوحة",
    statusClosed: "مغلقة",
    statusSettled: "متحصّلة",
    statusCancelled: "ملغية",
    // Degraded reads. A till that silently renders an empty list is a till
    // that lies: no addon groups makes every «اختار الحجم» rule pass by
    // accident, and an empty menu looks like a menu with nothing on it.
    addonsFailedTitle: "تعذّر تحميل الإضافات",
    addonsFailedBody:
      "لا يمكن إضافة أصناف الآن؛ فمن دون مجموعات الإضافات قد يصل إلى المطبخ طبق بلا اختياراته المطلوبة. حاول مرة أخرى.",
    menuFailedTitle: "تعذّر تحميل قائمة الطعام",
    menuFailedBody: "تعذّر تحميل قائمة الطعام. حاول مرة أخرى.",
    sectionsFailedNote: "تعذّر تحميل الأقسام، لذلك لن يظهر مرشح الأقسام الآن.",
    gridFailedNote:
      "تعذّر تحميل الطاولات، لذلك لن يعمل التنقل بينها ولن يُطبع اسم الطاولة على الفاتورة.",
    receiptTableUnknownNote:
      "اسم الطاولة غير معروف الآن؛ ستُطبع الفاتورة من دونه.",
    tiersFailedNote: "تعذّر تحميل شرائح الأسعار؛ سيُفتح الطلب بالسعر الأساسي.",
    // D47: الشريحة اللي بتتطبق لوحدها في ميعادها لو الكاشير مختارش حاجة.
    tierAutoApplied:
      "تُطبّق هذه الشريحة تلقائيًا الآن. اختر السعر الأساسي إذا لم ترد استخدامها.",
    // D45: عدد الضيوف — اختياري، وسيبه فاضي لو محدش عدّ.
    guestCountLabel: "عدد الضيوف",
    guestCountPlaceholder: "—",
    guestCountHint: "اختياري — اتركه فارغًا إذا لم تكن متأكدًا.",
    // B6: نفس السؤال، بس بعد ما الفاتورة تفتح — الجرسون بيعرف العدد لما
    // يرجع من الطاولة، مش قبل أول ضغطة على المنيو.
    guestCountUnset: "—",
    guestCountDialogTitle: "عدد الضيوف على الطاولة",
    guestCountDialogHint:
      "اتركه فارغًا إذا لم يعدّه أحد؛ فهذا أفضل من رقم تقديري.",
    guestCountInvalid: "اكتب رقمًا من 1 إلى 500",
    guestCountClear: "مسح العدد",
    // B117/covers rework Phase 1 — الدياولوج ده بيتفتح من غلق الفاتورة، مش من
    // صف الكوفرات، فالسؤال بيبقى «الترابيزة كانت كام فرد؟» مش «عدد الضيوف على
    // الطاولة». صف الكوفرات العادي فاضل زي ما هو.
    guestCountCloseDialogTitle: "كم كان عدد الأفراد على الطاولة؟",
    guestCountCloseDialogHint: "اتركه فارغًا إذا لم تعرف العدد",
    // Phase 2a — شيبس سريعة وقت فتح الطاولة؛ «أكتر» بتفتح نفس الدياولوج اللي
    // فوق.
    guestCountMoreChip: "أكثر",
    // Phase 2b — الستِبر على فاتورة مفتوحة أو مقفولة ليها عدد مسجّل. «فرد»
    // اسم معدود زي «كوفر» جمبه (`coversWordOne/Two/Few/coversWord`) — واحد
    // «فرد واحد»، اتنين «فردين»، ٣–١٠ بالجمع «{count} أفراد»، ١١+ بالمفرد
    // «{count} فرد». نفس القاعدة المكتوبة في `plural.ts`.
    guestCountWordOne: "فرد واحد",
    guestCountWordTwo: "فردين",
    guestCountWordFew: "{count} أفراد",
    guestCountWord: "{count} فرد",
    guestCountDecrement: "تقليل عدد الضيوف",
    guestCountIncrement: "زيادة عدد الضيوف",
    // Phase 2c — تنبيه خفيف، مش بلوكينج: العدد أكبر من ضعف كراسي طاولة
    // الفاتورة، والاحتمال الأكبر إن اللي اتكتب رقم الترابيزة مش عدد الناس.
    guestCountSanityHint:
      "العدد أكبر من عدد كراسي الطاولة؛ تأكد من أنك لم تُدخل رقم الطاولة",
    settleNeedsCashier: "يحتاج إلى صلاحية الكاشير لكي تحصّل",
    cancelledTitle: "الفاتورة ملغية",
    cancelledBody:
      "أُلغيت هذه الفاتورة ولا تُحتسب ضمن مبيعات اليوم. يمكنك طباعة نسخة منها للسجل.",
    // الطاولة فتحت نفسها بطلب من الـQR ولسه محدش من الفريق راح لها (D51).
    selfSeatedBadge: "طلب من الطاولة",
  },
  /*
   * The kitchen board and the chit (D18/D21).
   *
   * Register note: this block is read by cooks, not by an owner at a desk, and
   * it is read across a room. Every label here is as short as it can be and
   * still be a word rather than a code — "بدأ", "جاهز", "اتقدّم" — because a
   * button on this screen is read in the half-second before it is hit.
   */
  /**
   * D153 — «الكابتن», the waiter who owns a dine-in table (round table
   * 2026-10-14, T9). ONE word everywhere — the open step, the bill, the tile,
   * the pre-bill paper, the history filter — because it is what the pad is
   * called. Read one bill at a time; nothing here totals or ranks people.
   */
  captain: {
    label: "الويتر المسؤول",
    // A label, never an instruction: the bill has no captain yet.
    none: "بدون ويتر مسؤول",
    // «الكابتن: محمد» — the bill head, the paper and the detail.
    line: "الويتر المسؤول: {name}",
    hint: "اختياري — الويتر المسؤول عن الطاولة",
    noWaiters: "لا يوجد في الفريق من يحمل دور الويتر بعد",
    change: "تغيير الويتر المسؤول",
    sheetHint: "يُسجَّل كل تغيير على الفاتورة بالاسم والوقت.",
    saved: "سُجّل الويتر المسؤول",
    cleared: "أُزيل الويتر المسؤول",
    filterAny: "أي ويتر",
  },
  /** Call waiter (D132): a diner's ask, on the floor tile and the bill. */
  serviceRequests: {
    billHeading: "طلبات الطاولة",
    since: "من {time}",
    done: "تم",
    kindWaiter: "ويتر",
    kindWater: "ماء",
    kindCutlery: "أدوات المائدة",
    kindBill: "الحساب",
    kindCleanTable: "تنظيف الطاولة",
    kindHighChair: "كرسي أطفال",
    alertWaiter: "{table}: طلب ويتر",
    alertWater: "{table}: طلب ماء",
    alertCutlery: "{table}: طلب أدوات المائدة",
    alertBill: "{table}: طلب الحساب",
    alertCleanTable: "{table}: طلب تنظيف الطاولة",
    alertHighChair: "{table}: طلب كرسي أطفال",
  },
  /*
   * الورقة اللي بتتسلّم للضيف (2026-10-08): رأس فيه رقم الطلب والطاولة بخط كبير،
   * جدول أصناف بأعمدة، والإجمالي كبير وجنبه حالة الحساب.
   */
  paper: {
    orderNo: "رقم الطلب",
    table: "طاولة",
    date: "التاريخ",
    checkIn: "وقت الدخول",
    checkOut: "وقت الخروج",
    customer: "العميل",
    product: "الصنف",
    quantity: "الكمية",
    price: "السعر",
    lineTotal: "الإجمالي",
    total: "الإجمالي",
    statusOpen: "مفتوح",
    statusPaid: "مدفوع",
    status: "الحالة",
    captain: "الويتر المسؤول",
    guestNo: "عدد الضيوف",
    generalCustomer: "عميل عام",
    discount: "الخصم",
    poweredBy: "بواسطة تريوتي",
  },
  /*
   * T14/D172 — علامات الورقة من الكتالوج فقط، الرمز أولًا، فلا تُطبع كلمة
   * إنجليزية داخلية على تذكرة عربية. «نسخة مكررة» على النسخة الإضافية وحدها.
   */
  printArtifact: {
    receipt: "إيصال العميل",
    kitchenChit: "تذكرة المطبخ",
    remake: "↻ إعادة تحضير",
    reroute: "⇄ منقول من محطة أخرى",
    reprint: "نسخة مكررة",
    reprintNote: "سبق إرسالها؛ تأكّد قبل التجهيز",
    change: "✎ تعديل",
    void: "✕ ملغى — لا تجهّزه",
    voidRemainder: "ملغى {voided} من {total} — المتبقي {remaining}",
    addition: "إضافة {number}",
  },
  /*
   * D172 — «طباعة التذكرة» على تذكرة سبقت طباعتها: سؤال واحد. «لم تصل
   * التذكرة» تُطبع كأنها الأولى؛ «نسخة إضافية» وحدها تُطبع عليها «نسخة مكررة».
   */
  reprintQuestion: {
    title: "طباعة التذكرة مرة أخرى",
    subtitle: "سبقت طباعة هذه التذكرة. لماذا تطبعها مرة أخرى؟",
    notReceived: "لم تصل التذكرة",
    notReceivedHint: "تُطبع كأنها الأولى، دون علامة نسخة.",
    extraCopy: "نسخة إضافية",
    extraCopyHint: "يُطبع عليها «نسخة مكررة» حتى لا تُجهَّز مرتين.",
    sentToPrinter: "أُرسلت التذكرة إلى طابعة المطبخ",
    // Gate P7 rework 2 (R3) — no print computer is on, so the paper came out
    // of THIS device's printer; never «أُرسلت» when no computer took it.
    printedHere: "طُبعت التذكرة على هذا الجهاز",
  },
  /*
   * الحساب المبدئي (round 5) — ورقة الحساب اللي الجرسون يطبعها للضيوف والطاولة
   * لسه مفتوحة. مش فاتورة مقفولة ومش إيصال ضريبي ومش دفع: لو طلبوا حاجة بعدها،
   * الحساب يتعلّم إنه قديم. «حساب مبدئي» عشان يبان إنه مش «الحساب» اللي بيطلع
   * لما الفاتورة تتقفل.
   */
  /*
   * D156 — اعتماد المدير بكلمة المرور (قرار المؤسس 2026-10-15، تعديل على D27):
   * إلغاء صنف بعد إرساله، أو تسجيله على حساب المحل، أو الخصم على الفاتورة، أو
   * مرتجع يعيد الطعام إلى المخزن — إذا نفّذه أمين الصندوق — يحتاج كلمة مرور
   * مدير أو المالك لتلك العملية وحدها. الإشعار يصل إلى صاحب كلمة المرور.
   */
  managerApproval: {
    title: "اعتماد المدير",
    confirm: "اعتماد",
    intro_voidItem: "إلغاء صنف بعد إرساله إلى المطبخ يحتاج كلمة مرور مدير.",
    intro_compItem: "تسجيل صنف على حساب المطعم يحتاج كلمة مرور مدير.",
    intro_applyBillDiscount: "الخصم على الفاتورة يحتاج كلمة مرور مدير.",
    intro_refundInvoice: "إعادة الطعام إلى المخزن مع المرتجع تحتاج كلمة مرور مدير.",
    approverLabel: "المدير المعتمِد",
    passwordLabel: "كلمة مرور المدير",
    hint: "تُستخدم كلمة المرور لهذه العملية فقط ولا تُحفظ على هذا الجهاز، ويصل إشعار إلى صاحبها.",
    invalid: "لم يُقبل الاعتماد. تأكد من الاسم وكلمة المرور ثم أعد المحاولة.",
    locked:
      "توقف الاعتماد بهذا الاسم مؤقتًا بعد خمس محاولات خاطئة. أعد المحاولة بعد ربع ساعة أو اختر مديرًا آخر.",
    passwordChangeRequired:
      "يجب على المدير تغيير كلمة المرور المؤقتة أولًا: يسجّل الدخول بها مرة واحدة ويختار كلمة مرور جديدة، ثم يعتمد بها.",
    noApprovers:
      "لا يوجد مدير يمكنه الاعتماد. يمنح المالك صلاحية «اعتماد الاستثناءات» من شاشة الفريق.",
    loading: "جارٍ تحميل الأسماء…",
    // Settings → Drawer: the owner's switch.
    settingLabel: "اعتماد المدير بكلمة المرور",
    settingCheckbox: "طلب كلمة مرور المدير",
    settingHint:
      "عند التشغيل يحتاج إلغاء صنف بعد إرساله، أو تسجيله على حساب المطعم، أو الخصم على الفاتورة، أو إعادة طعام إلى المخزن مع مرتجع، كلمة مرور مدير إذا نفّذه الكاشير. المدير والمالك لا يُسألان. يُحاسَب على العملية من اعتمدها.",
    // The act, as a noun — the bill detail, the bell.
    act_voidItem: "إلغاء صنف",
    act_compItem: "صنف على حساب المطعم",
    act_applyBillDiscount: "خصم على الفاتورة",
    act_refundInvoice: "مرتجع مع إعادة إلى المخزن",
    // The bill detail (back office): who authorised which act, from whose till.
    detailTitle: "اعتمادات المدير",
    detailRow: "بواسطة {approver} · من جهاز {till}",
    unknownName: "غير معروف",
    // Staff exceptions: beside the approver's own figures.
    staffApprovals: "منها {approved} باعتماده بكلمة المرور من جهاز: {tills}",
    staffApprovalsTill: "{name} ({approved})",
    // The shift review's exception row.
    shiftTillBy: "من جهاز {name}",
    // The approver's notice (push and bell).
    notifyTitle: "اعتماد بكلمة مرورك",
    notifyBody: "كلمة مرورك اعتمدت «{act}» على فاتورة #{number} الساعة {time}",
    notifyBodyTill:
      "كلمة مرورك اعتمدت «{act}» على فاتورة #{number} الساعة {time} — من جهاز {till}",
  },
  /*
   * D146 — خصم على الفاتورة: لفتة المدير في آخر الأكلة («خليها 230»، «10%
   * للزبون الدايم»، «شكوى»)، بنسبة أو مبلغ أو إجمالي جديد. بيتخصم من صافي الأكل
   * قبل الخدمة والضريبة، فالاتنين بيقلّوا معاه، ورسم التوصيل ما بيتخصمش أبدًا.
   * الكلمة على الورق وفي التقارير اسم: «خصم على الفاتورة».
   */
  billDiscount: {
    action: "خصم",
    actionChange: "تغيير الخصم",
    lineLabel: "خصم على الفاتورة",
    sheetTitle: "خصم على الفاتورة",
    percentLabel: "نسبة من صافي الطعام",
    amountLabel: "مبلغ الخصم",
    targetLabel: "الإجمالي المطلوب",
    previewBefore: "الإجمالي الحالي",
    previewNet: "الخصم من صافي الطعام",
    previewAfter: "الإجمالي بعد الخصم",
    capHint: "الحد الأقصى للخصم {percent}% من صافي الطعام ({amount})",
    problemOverCap: "أكبر من الحد المسموح: {percent}% من صافي الطعام ({amount})",
    problemWholeBill: "هذا هو الحساب كله — تُسجَّل الأطباق «على حساب المطعم» لا خصمًا",
    problemBelowPaid: "سيصبح الإجمالي أقل من المدفوع فعلًا ({paid})",
    problemNothingOff: "لا يُخصم شيء بهذا الرقم",
    reasonLabel: "السبب",
    reasonRounding: "تقريب",
    reasonComplaint: "شكوى",
    reasonRegular: "عميل دائم",
    reasonOwnerGuest: "ضيف صاحب المطعم",
    reasonOther: "أخرى",
    noteLabel: "سبب الخصم",
    noteRequired: "يتطلب «أخرى» كتابة السبب",
    confirm: "تأكيد الخصم",
    remove: "إزالة الخصم",
    appliedToast: "سُجّل الخصم على الفاتورة",
    removedToast: "أُزيل الخصم من الفاتورة",
    blockedPriceTier: "على هذه الفاتورة فئة سعر، فلا يمكن إضافة خصم عليها",
    blockedAggregator: "لا يمكن الخصم على فاتورة منصة توصيل",
    detailWhy: "السبب: {reason} · بواسطة {name}",
    detailUnknownWho: "غير معروف",
  },
  /*
   * زر الطابعة على الفاتورة (المستخدم، 2026-10-20): خرج سطر «تقسيم الفاتورة»
   * من أسفل الفاتورة، وصار التقسيم داخل الطباعة — الحساب كاملًا، أو أصناف
   * مختارة تصبح جزءًا من الفاتورة له حسابه ودفعه.
   */
  printChoice: {
    title: "طباعة الحساب",
    whole: "الحساب كاملًا",
    wholeHint: "كل ما على الفاتورة · {amount}",
    items: "اختيار أصناف",
    itemsHint: "تصبح الأصناف المختارة جزءًا من الفاتورة، يُطبع له حساب ويُدفع وحده",
    itemsHintPrintOnly: "يُطبع حساب للأصناف المختارة وحدها",
    ticketHint: "لم تُطبع تذكرة المطبخ بعد، فلا تُغلق الفاتورة حتى تُطبع",
    partsTitle: "أجزاء الفاتورة",
    partUnpaid: "لم يُدفع",
    partSettle: "تحصيل",
    partPrint: "طباعة حساب {label}",
    editParts: "تعديل الأجزاء",
    printPickedNone: "اختر الأصناف للطباعة",
    printPickedOne: "طباعة صنف واحد · {amount}",
    printPickedTwo: "طباعة صنفين · {amount}",
    printPickedFew: "طباعة {count} أصناف · {amount}",
    printPicked: "طباعة {count} صنفًا · {amount}",
  },
  preBillCheck: {
    action: "حساب مبدئي",
    actionReprint: "إعادة طباعة الحساب المبدئي",
    actionHint: "اطبع الحساب للضيوف قبل الدفع، وتبقى الطاولة مفتوحة",
    dialogTitle: "حساب مبدئي · {name}",
    toPay: "المطلوب دفعه",
    splitLabel: "عرض التقسيم على الحساب",
    splitNone: "بدون تقسيم",
    splitGuests: "{count} فردًا",
    splitGuestsOne: "فرد واحد",
    splitGuestsTwo: "فردان",
    splitGuestsFew: "{count} أفراد",
    splitEach: "{label} · {amount} لكل فرد",
    splitUneven: "{label} · {parts}",
    stayOpenNote:
      "تبقى الطاولة مفتوحة. يمكن للضيوف إضافة أصناف، وعندها يُعلَّم هذا الحساب بأنه قديم.",
    print: "طباعة الحساب",
    recordedToast: "تم تسجيل الحساب المبدئي",
    sentToPrinterToast: "أُرسل الحساب إلى طابعة الفواتير",
    noteFresh: "طُبع الحساب المبدئي الساعة {time} · في انتظار الدفع",
    noteStale: "تغيّرت الأصناف بعد الحساب المبدئي (الساعة {time}) — أعد طباعته",
    tilePrinted: "طُبع الحساب المبدئي",
    tileStale: "الحساب المبدئي قديم",
    paperKind: "الفاتورة",
  },
  /*
   * D149 (الطاولة المستديرة 2026-10-14، T5) — قعدة الطيار «أحمد سلّم». الشنط
   * المقفولة على الشريط متجمّعة باسم اللي خرجت معاه، وزرار واحد يفتح ورقة
   * التسليم: كل فاتورة بسطر، كاش أو إنستاباي، والمصروف بتاع الطيار حركة لوحده.
   * No rider is a record in the system — a typed name only. Copy is MSA (D158):
   * «مندوب التوصيل», «تسوية حساب أحمد».
   */
  riderSitting: {
    groupsTitle: "تسوية حسابات الطيارين",
    unnamed: "بدون اسم",
    groupLine: "{name} · {count} · {amount}",
    handIn: "تسوية حساب {name}",
    handInUnnamed: "تسوية الطلبات بدون اسم",
    sheetSubOne: "طلب واحد · {amount}",
    sheetSubTwo: "طلبان · {amount}",
    sheetSubFew: "{count} طلبات · {amount}",
    sheetSub: "{count} طلبًا · {amount}",
    lineNoAddress: "بدون عنوان",
    lineMinutesOut: "بالخارج منذ {count} د",
    lineMinutesOutHours: "بالخارج منذ {count} س",
    lineTickLabel: "فاتورة {sequence} في التسليم",
    lineLeftOut: "خارج التسليم — ما زالت مستحقة",
    instapayHint:
      "إنستاباي فقط إذا وصل التحويل فعلًا. إن لم يصل بعد، أزل العلامة واتركها حتى يصل — ستبقى على الشريط.",
    expectedCash: "النقد المتوقع",
    instapayTotal: "إنستاباي",
    payoutLabel: "مصروف الطيار (اختياري)",
    payoutHint:
      "وقود أو مشاوير — يُسجَّل حركةَ «تكلفة توصيل» خارجة من الخزينة مستقلة، ولا يُخصم من الفواتير.",
    net: "صافي النقد المستحق",
    countedLabel: "النقد المُستلَم",
    short: "عجز {amount}",
    over: "زيادة {amount}",
    exact: "مطابق",
    differenceNote: "لا يوقف الفرق التسليم؛ يظهر في عدّ الخزينة.",
    payoutReasonOne: "{name} · طلب واحد",
    payoutReasonTwo: "{name} · طلبان",
    payoutReasonFew: "{name} · {count} طلبات",
    payoutReason: "{name} · {count} طلبًا",
    confirm: "تأكيد التسوية",
    nothingTicked: "لم تُحدَّد أي فاتورة.",
    lineSending: "جارٍ التحصيل…",
    lineDone: "حُصِّلت",
    lineFailed: "لم تُحصَّل: {error}",
    payoutDone: "سُجّل مصروف الطيار",
    payoutFailed: "لم يُسجَّل مصروف الطيار: {error} — ولم تُحصَّل أي فاتورة.",
    someFailedOne: "فاتورة واحدة لم تُحصَّل وبقيت على الشريط، وما زالت مستحقة.",
    someFailedTwo: "فاتورتان لم تُحصَّلا وبقيتا على الشريط، وما زالتا مستحقتين.",
    someFailedFew: "{count} فواتير لم تُحصَّل وبقيت على الشريط، وما زالت مستحقة.",
    someFailed: "{count} فاتورة لم تُحصَّل وبقيت على الشريط، وما زالت مستحقة.",
    retry: "إعادة المحاولة",
    done: "تم",
  },
  /*
   * شاشة التحصيل في PAPER (2026-10-03، المؤسس: «الدفع كامل وحسب الصنف — بدون
   * تقسيم بالتساوي — مع الإبقاء على إنستاباي»). التسميات منقولة من النسخة
   * العربية للتصميم نفسه (المتبقي، الفاتورة كاملة، حسب الصنف، تحديد الكل…)،
   * وباقي الجمل بنفس الفصحى البسيطة. «المتبقي» وليس «الباقي»: «الباقي» في هذا
   * التطبيق هو باقي النقود المُعاد للزبون (`pos.changeDue`) والشاشتان تظهران معًا.
   */
  paperSettle: {
    title: "تحصيل {name}",
    remaining: "المتبقي",
    subUnpaid: "{count} صنفًا غير مدفوع",
    subUnpaidOne: "صنف واحد غير مدفوع",
    subUnpaidTwo: "صنفان غير مدفوعين",
    subUnpaidFew: "{count} أصناف غير مدفوعة",
    subAllPaid: "تم دفع كل شيء",
    subPaidSoFar: "دُفع {amount} حتى الآن",
    modesLabel: "طريقة دفع الضيف",
    modeFull: "الفاتورة كاملة",
    modeItems: "حسب الصنف",
    fullInfo: "دفع المتبقي {amount} دفعة واحدة",
    itemsPick: "اختر ما يدفعه هذا الضيف",
    itemsSelected: "{count} صنفًا محددًا · {amount}",
    itemsSelectedOne: "صنف واحد محدد · {amount}",
    itemsSelectedTwo: "صنفان محددان · {amount}",
    itemsSelectedFew: "{count} أصناف محددة · {amount}",
    selectAll: "تحديد الكل",
    clear: "مسح",
    each: "{amount} للواحد",
    fewer: "واحد أقل من {name}",
    more: "واحد أكثر من {name}",
    partPaidNote:
      "أصناف الضيف الذي دفع جزءًا من نصيبه لا تظهر هنا — حصّل ما تبقى من «الفاتورة كاملة».",
    cashReceived: "النقد المستلم",
    amountLabel: "المبلغ المطلوب دفعه",
    takeNow: "تحصيل {amount} الآن",
    labelPart: "دفعة جزئية",
    labelRest: "ما تبقى",
    instapayLocked:
      "يُقبل إنستاباي بعد أن يؤكّد أحد الموظفين تحويل الضيف على هذه الفاتورة.",
    pay: "دفع {amount}",
    payPlain: "دفع",
    payPickFirst: "اختر أصنافًا للدفع",
    payNothing: "لا شيء للدفع",
    // An all-comped bill (the staff meal, D154): settled at 0.00 cash.
    settleAtZero: "تسوية بمبلغ صفر",
    footOpen: "الدفع يُغلق الفاتورة أمام الطلبات الجديدة، وما لم يُدفع يبقى عليها.",
    footKeepsOpen: "تبقى الفاتورة مفتوحة ويواصل المطبخ تحضير الطلبات. يُدفع الباقي لاحقًا.",
    footPaidWaiting: "دُفعت بالكامل. أغلق الفاتورة عندما ينتهي المطبخ.",
    closePaid: "إغلاق الفاتورة",
    footClosed: "ما لم يُدفع يبقى على الفاتورة.",
    lastPaid: "دُفع {amount} ({method})",
    lastLeft: "متبقٍ {amount}",
    labelItems: "{count} صنفًا",
    labelItemsOne: "صنف واحد",
    labelItemsTwo: "صنفان",
    labelItemsFew: "{count} أصناف",
    doneTitle: "تم الدفع بالكامل",
    doneBody: "فاتورة {name} مدفوعة بالكامل.",
    done: "تم",
    payments: "المدفوعات",
    guest: "ضيف {number}",
    writeOffOpen: "غادر الضيف دون أن يدفع؟",
  },
  kitchen: {
    title: "المطبخ",
    titleAtStation: "المطبخ — {station}",
    plateCount: "{count} طبق",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    plateCountFew: "{count} أطباق",
    // B117/A2 — the kitchen board's own count.
    plateCountOne: "طبق واحد",
    plateCountTwo: "طبقان",
    stationPickerLabel: "اختر المحطة",
    allStations: "كل المحطات",
    // The remembered station id no longer matches any station the board
    // returned — deleted, or this tablet was moved to another restaurant. The
    // filter is dropped rather than left pointing at nothing, and this says so:
    // a board that silently widened would read as "the kitchen is quiet".
    stationGoneNotice:
      "لم تعد المحطة المحددة لهذه الشاشة موجودة، لذلك عادت إلى عرض كل المحطات. اختر المحطة من أعلى.",
    laneQueued: "جديد",
    lanePreparing: "في التحضير",
    laneReady: "جاهز",
    laneEmpty: "لا يوجد شيء هنا الآن.",
    laneCashier: "بانتظار الكاشير",
    fromWaiter: "من الويتر · {time}",
    fromCustomer: "من الضيف · {number}",
    emptyTitle: "المطبخ فارغ",
    emptyBody:
      "لا توجد أطباق مرسلة إلى هذه المحطة الآن. سيظهر طلب الويتر هنا فور إرساله.",
    deniedTitle: "هذه الشاشة غير متاحة لك",
    deniedBody:
      "تحتاج شاشة المطبخ إلى صلاحية «عرض المطبخ». تواصل مع صاحب المطعم لإضافتها إلى دورك إذا كان عملك يتطلب استخدامها.",
    displayOffTitle: "طريقة عمل المطبخ الحالية بلا شاشة",
    displayOffBody:
      "لا يستخدم هذا المطعم شاشة المطبخ؛ تُتابع الأصناف من شاشة الكاشير أو عبر التذكرة المطبوعة. تواصل مع صاحب المطعم إذا كان ينبغي تشغيل الشاشة.",
    displayOffBodyOwner:
      "طريقة عمل المطبخ الحالية لا تستخدم شاشة؛ تُتابع الأصناف من شاشة الكاشير أو عبر التذكرة المطبوعة. اختر طريقة فيها شاشة من «طريقة عمل المطبخ» في الإعدادات ← «المطبخ»، ثم عُد إلى هنا.",
    displayOffSettingsAction: "فتح الإعدادات",
    actionStart: "بدء التحضير",
    // The till chip (`OrderPanel`): the same act, short — T14.
    actionStartShort: "بدء",
    actionReady: "جاهز",
    actionServed: "تقديم",
    printing: "جارٍ طباعة التذكرة...",
    remakeBadge: "إعادة تحضير ({count})",
    // B117/Q4 — السبب جنب البادچ، عشان الطباخ يقرا تعليمات مش عتاب. الشرط
    // اللي الشيف حطه: يعرف يصلّح إيه قبل ما يبدأ.
    remakeReasonLine: "السبب: {reason}",
    tableUnknown: "بلا طاولة",
    waitingMinutes: "{count} د",
    /* UX-round D47 — الدقايق بعد الساعة، مش الساعة لوحدها. «من 1 س» كانت بتقرا نفسها
       على طبق عدّى ساعة وطبق عدّى ساعة وخمسة وخمسين: الطباخ محتاج يفرّق. */
    waitingHours: "{count} س {minutes} د",
    /* UX-round D47 — الكلمة اللي بتقول «ده اتأخر»، جنب الخط التقيل والبرواز. اللون
       لوحده ممنوع يشيل المعنى (قاعدة الحالات في
       docs/design/components-states-motion.md). */
    lateBadge: "متأخر",
    /* الطبق اللي خلص وقاعد على البار مش «متأخر» — ده بيبرد، وده اللي بيحصل
       فعلاً: تلات أطباق قعدوا ساعة على البار في اللقطة. */
    lateBadgeReady: "يبرد",
    /* UX-round D48 — الأطباق اللي بره حدود الحارة، في العنوان اللي مش بيتحرك.
       «كمان» مش «تحت»: أول ما الطباخ ينزل في الحارة، المخفي يبقى فوق مش تحت،
       وكلمة بتحدد اتجاه بتبقى غلط في نص الحالات. أربع صيغ كاملة (`plural.ts`). */
    laneMoreOne: "واحد آخر",
    laneMoreTwo: "اثنان آخران",
    laneMoreFew: "{count} أخرى",
    laneMore: "{count} أخرى",
    chitPrintNumber: "نسخة رقم {number}",
    chitInvoice: "فاتورة #{sequence}",
    chitNoPrices: "تذكرة مطبخ — لا تتضمن أسعارًا وليست فاتورة",
    // ── شاشة «خلص النهاردة» (D50) ─────────────────────────────────────────
    // مجموعة مستقلة في آخر بلوك المطبخ عشان متتعاركش مع تعديلات موازية.
    /* UX-round D45 — «نفد» حالة الصنف في كل حتة، «خلص» حالة المكوّن في كل حتة، وكل فعل
       «علّمه …». الكلمتين كانوا واحدة على الشاشة دي — نفس اللفظ على شريحة
       الحالة وعلى الزرار، تسع مرات. */
    availabilityTitle: "النواقص",
    availabilityEntry: "النواقص",
    availabilityBack: "العودة إلى الشاشة",
    /* B118/QA3 — الاسم متشال بس الفعل بيتحرك: «٣ نفدوا» جمع، «١٢ نفد» مفرد.
       الإعفاء الأصلي («مافيش اسم بيتحرك») كان غلط هنا وصح في `summaryOpen`،
       اللي صفته مفرد مؤنث من ٣ وطالع. */
    availabilitySoldOutCount: "نفد {count} صنفًا",
    availabilitySoldOutCountFew: "نفدت {count} أصناف",
    // B117/A2 — «1 نفدوا» on the 86 screen: a plural verb about one dish.
    availabilitySoldOutCountOne: "نفد صنف واحد",
    availabilitySoldOutCountTwo: "نفد صنفان",
    availabilityOn: "موجود",
    availabilityOff: "نفد",
    // مش نفس حاجة: صنف حد قفله بنفسه، ولا وجبة اتقفلت عشان حاجة جواها ناقصة.
    // التانية مش بتتحل من الشاشة دي، فبنقول ده بدل ما نحط زرار مالوش لازمة.
    availabilityOffComponent: "نفد صنف بداخلها",
    availabilityBlockedHint: "يجب إتاحة الصنف النافد أولًا",
    // T14 — فعل لا حالة: «إيقاف الصنف» / «إتاحة الصنف» بجانب شريحة «موجود» / «نفد».
    availabilityMarkOut: "إيقاف الصنف",
    availabilityPutBack: "إتاحة الصنف",
    /* B118/QA5 — الشاشة دي بتجمّع بالمحطة، مش بالقسم. الجملة كانت بتقول
       «من غير قسم» — وده اسم حاجة تانية خالص في نفس المنتج (`menuUncategorized`
       و`statMenuUncategorised` بنفس الحروف بالظبط)، فالطباخ كان بيتقال له إن
       الأصناف مش متحطة في قسم منيو وهو في شاشة بتتكلم عن المحطات. */
    availabilityUnassigned: "بلا محطة",
    // T11 / D169 — an 86 resets at the business-day start unless it is marked
    // «حتى إتاحته يدويًا» (a longer outage).
    availabilityUntilManual: "حتى إتاحته يدويًا",
    // The ingredient pane's own twin: its act is «إتاحة المكوّن» / "Report available".
    availabilityIngredientUntilManual: "حتى إتاحته يدويًا",
    availabilityUntilManualHint: "لا يعود متاحًا تلقائيًا مع بداية يوم العمل التالي.",
    availabilitySaving: "جارٍ الحفظ...",
    availabilityConfirmOutTitle: "هل تريد إيقاف الصنف؟",
    /* UX-round D46 — جملتين، والفقرة اللي كانت فوق الليستة اتشالت.

       دي بقت المكان الوحيد اللي بيقول للطباخ إن الفعل ده بيوصل لكل نقاط البيع
       ولقايمة الـQR، فممنوع تتفضّى (E12) — بس الجملة التالتة («المنيو نفسه مش
       هيتغيّر») شايلها `pos.menuMarkSoldOutBody` على تايل الجرسون بالحرف. */
    availabilityConfirmOutBody:
      "لن يتوفر «{item}» للطلب في جميع نقاط البيع أو في قائمة QR حتى تضغط «إتاحة الصنف». وسيظل ظاهرًا على شاشة الكاشير مع علامة «نفد».",
    availabilityConfirmBackTitle: "هل تريد إتاحة الصنف؟",
    availabilityConfirmBackBody:
      "سيعود «{item}» متاحًا للطلب في جميع نقاط البيع وفي قائمة QR.",
    availabilityMarkedOut: "تم إيقاف «{item}»",
    availabilityMarkedBack: "تمت إتاحة «{item}»",
    availabilityEmptyTitle: "لا توجد أصناف في قائمة الطعام",
    availabilityEmptyBody:
      "ستظهر الأصناف هنا بعد إضافتها إلى قائمة الطعام، ويمكنك عندئذ إيقاف ما نفد منها.",
    availabilityDeniedTitle: "هذه الشاشة غير متاحة لك",
    availabilityDeniedBody:
      "تحتاج هذه الشاشة إلى صلاحية «تحديد الصنف النافد». تواصل مع صاحب المطعم لإضافتها إلى دورك إذا كان عملك يتطلب استخدامها.",
    // ── المكوّنات (B18) ───────────────────────────────────────────────────
    // نص الشاشة اتقسم لجزئين عشان الادعاءين مختلفين: الصنف اللي نفد مش
    // بيتباع، أما المكوّن اللي خلص بيفضل الصنف بيتباع وبس بننبّه الجرسون.
    // T14 — عنوان واحد للتبويبين: «النواقص» (`availabilityTitle`).
    /* UX-round D45 — فعل المكوّن، مش حالته. الحالة «خلص» والفعل «علّمه خلص»: الكلمتين
       كانوا واحدة بالعربي — شريحة الحالة والزرار بنفس اللفظ — والإنجليزي كان
       مظبوط أصلاً (`Finished` / `Mark finished`)، وده بالظبط الدليل إن العربي
       هو اللي وقع. */
    availabilityIngredientMarkOut: "الإبلاغ بنفاده",
    // T14 — the ingredient's own way back: «إتاحة الصنف» names a dish.
    availabilityIngredientPutBack: "إتاحة المكوّن",
    availabilityTabDishes: "الأصناف",
    availabilityTabIngredients: "المكوّنات",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    availabilityIngredientsCountOne: "نفد مكوّن واحد",
    availabilityIngredientsCountTwo: "نفد مكوّنان",
    availabilityIngredientsCountFew: "نفدت {count} مكوّنات",
    availabilityIngredientsCount: "نفد {count} مكوّنًا",
    availabilityIngredientOn: "موجود",
    availabilityIngredientOff: "نفد",
    // الشاشة دي مافيهاش غير القرار، فـ«خلص» واضحة لوحدها. أما في المخزن —
    // اللي بيعرض الرصيد كمان بنفس الكلمة — الكلمة لازم تقول مصدرها، وإلا
    // الجدول يقول «خلص: 0» والسطر تحته يقول «خلص»، وده بالظبط الخلط اللي
    // جواب B18 موجود عشان يمنعه.
    availabilityIngredientOffQualified: "أبلغ المطبخ بنفاده",
    availabilityIngredientEmptyTitle: "لا توجد مكوّنات مسجّلة",
    availabilityIngredientEmptyBody:
      "ستظهر المكوّنات هنا بعد تسجيلها في المخزون، ويمكنك عندئذ الإبلاغ بما نفد منها.",
    availabilityIngredientConfirmOutTitle: "هل تريد الإبلاغ بنفاد المكوّن؟",
    /* «لحد ما ترجّعه» — THE REVERSAL, WHICH THE TONE ALREADY PROMISES.
       
       Its two siblings both carry it — `pos.menuMarkSoldOutBody` and
       `kitchen.availabilityConfirmOutBody` — and this key did not, which made it
       the one `caution` confirm in the app whose body does not tell the reader
       how to undo the act. `caution` exists to say "this is reversible"; a body
       that does not name the reversal leaves the tone making a promise the words
       never keep, at the one moment the reader can still change their mind.
       
       The clause only, in the shape the siblings use. The rest of this copy was
       reviewed and stands. */
    availabilityIngredientConfirmOutBody:
      "سيُسجَّل «{item}» مكوّنًا نافدًا حتى تضغط «إتاحة المكوّن». ستظل الأصناف التي تحتوي عليه متاحة للبيع، لكن سيرى الويتر تنبيهًا قبل إضافتها ولن يتمكن من استخدامه في تبديل دون علمه.",
    availabilityIngredientConfirmBackTitle: "هل تريد إتاحة المكوّن؟",
    availabilityIngredientConfirmBackBody:
      "سيعود «{item}» متاحًا وستتوقف تنبيهاته.",
    availabilityIngredientMarkedOut: "تم الإبلاغ بنفاد «{item}»",
    availabilityIngredientMarkedBack: "تمت إتاحة «{item}»",
    // ── P5 (T12, T14, T16): the wall screen's start, its honesty, its exit ──
    // «ابدأ الشاشة» unlocks audio (a browser needs one tap per page load) and
    // keeps the screen awake. vote-ux-copy §R6 as amended by T14.
    startTitle: "شاشة المطبخ",
    startBody: "اضغط «ابدأ الشاشة» ليصدر صوت مع كل طلب جديد وتبقى الشاشة مضاءة.",
    startAction: "ابدأ الشاشة",
    startSilent: "متابعة بدون صوت",
    sizeLabel: "حجم العرض",
    sizeAuto: "تلقائي",
    sizeTouch: "شاشة لمس",
    sizeWall: "شاشة حائط",
    // The board's own disconnected band (F4): no timer, no promise to retry.
    offlineTitle: "الشاشة غير متصلة — آخر تحديث {time}",
    offlineTitleNever: "الشاشة غير متصلة",
    offlineBody: "قد تكون هناك طلبات لا تظهر.",
    offlineRefresh: "تحديث الشاشة",
    offlineNoNetwork: "الجهاز غير متصل بالشبكة — تحقق من الاتصال ثم أعد المحاولة.",
    screenOpen: "الشاشة",
    screenTitle: "إعدادات الشاشة",
    soundLabel: "صوت الطلبات الجديدة",
    soundOn: "يعمل",
    soundOff: "متوقف",
    soundLocked: "يعمل بعد أول لمسة على الشاشة",
    soundUnsupported: "غير متاح على هذا الجهاز",
    soundEnable: "تشغيل الصوت",
    soundMute: "إيقاف الصوت",
    signOutTitle: "تسجيل الخروج من هذه الشاشة؟",
    signOutBody: "اضغط مطولًا على الزر حتى يمتلئ. اللمسة السريعة لا تُخرجك، حتى لا تُغلق الشاشة بلمسة عابرة.",
    signOutHold: "اضغط مطولًا لتسجيل الخروج",
    // ── P4 (T2, T3, T5, T7, T9, T13, T14; D170): the pass screen ──
    // vote-ux-copy §R6 as amended by T14. «تسليم» on takeaway/delivery.
    actionHandOver: "تسليم",
    actionStartScoped: "بدء التحضير ({count})",
    actionReadyScoped: "جاهز ({count})",
    actionServedScoped: "تقديم ({count})",
    actionHandOverScoped: "تسليم ({count})",
    // Later rounds only: n = round − 1, nothing on the first round.
    addOnRound: "إضافة {round}",
    totalAge: "منذ الطلب {count} د",
    totalAgeHours: "منذ الطلب {count} س {minutes} د",
    unprinted: "لم تُطبع بعد",
    voidMark: "ملغى",
    paidMark: "مدفوع",
    // Gate P4 #2 — a retained card on a bill CLOSED but not settled: the
    // cheque is out, nothing is paid yet, so it says what happened.
    billClosedMark: "أُغلقت الفاتورة",
    hide: "إخفاء",
    show: "إظهار",
    viewSwitchLabel: "طريقة العرض",
    viewByStation: "حسب المحطة",
    viewByOrder: "حسب الطلب",
    orderComplete: "مكتمل",
    orderWaitingOn: "بانتظار: {stations}",
    orderServe: "تقديم الطلب ({count})",
    orderHandOver: "تسليم الطلب ({count})",
    orderEmptyTitle: "لا توجد طلبات في المطبخ",
    orderEmptyBody: "يظهر الطلب هنا فور إرساله إلى المطبخ، ببطاقة واحدة لكل طلب.",
    batchTitle: "المطلوب الآن",
    channelFilter: "استلام وتوصيل",
    channelFilterHidden: "{count} مخفية",
    recallOpen: "المُنجز مؤخرًا",
    recallServedTitle: "قُدِّم في آخر 15 دقيقة",
    recallAgedOffTitle: "جاهز وأُخفي من الشاشة",
    recallEmpty: "لم يُنجز شيء في آخر 15 دقيقة.",
    undo: "تراجع",
    undoToast: "{state} · #{number}",
    stateServed: "تم التقديم",
    stateHandedOver: "تم التسليم",
  },
  manage: {
    tableIngredient: "المكوّن",
    menuTitle: "قائمة الطعام",
    menuItemName: "اسم الصنف",
    menuItemNameEn: "الاسم بالإنجليزية",
    /* D127 — وصف الصنف، بالعربي وبالإنجليزي. اختياري. */
    menuItemDescriptionAr: "الوصف بالعربية",
    menuItemDescriptionEn: "الوصف بالإنجليزية",
    menuItemDescriptionHint:
      "اختياري، حتى {max} حرفًا. يظهر للضيوف في قائمة الطعام عبر رمز QR وللويتر في شاشة الطلب.",
    menuPriceChargesHint:
      "هذا سعر الصنف في قائمة الطعام. تحدد الإعدادات العامة ما إذا كانت الضريبة والخدمة تُضافان إليه أو تدخلان فيه.",
    addonGroups: "مجموعات الإضافات",
    /* UX-round D62 — `floorTitle` («المناطق والطاولات») اتشال: الشاشة الواحدة اسمها
       `tablesTitle` («الصالة») في العرضين. */
    areaName: "اسم المنطقة",
    tableName: "اسم الطاولة",
    tableCode: "رمز QR",
    ingredientsTitle: "المكوّنات",
    unit: "الوحدة",
    // DEF-2: the raw `IngredientUnit` code ("piece"/"g"/"ml") used to render
    // verbatim inside Arabic sentences — a Latin token flipping awkwardly
    // next to the number on an RTL line. These are the spoken words, used
    // wherever a unit is read out rather than picked from a toggle.
    unitPiece: "قطعة",
    unitGram: "جرام",
    unitKilogram: "كيلوجرام",
    unitMilliliter: "مل",
    avgCost: "متوسط التكلفة / {unit}",
    onHand: "المتاح",
    lowStock: "قارب على النفاد",
    recordPurchase: "تسجيل شراء",
    purchaseQuantity: "الكمية",
    purchaseTotal: "المبلغ المدفوع",
    // D48 (S15): كلاهما اختياري ومعلوماتي فقط — تكلفة الوحدة بتتحسب من إجمالي المدفوع زي ما هي.
    purchaseVatAmount: "منها ضريبة",
    purchaseVatAmountHint:
      "الجزء من المبلغ المدفوع الذي هو ضريبة قيمة مضافة. اختياري.",
    purchaseTaxInvoiceRef: "رقم الفاتورة الضريبية",
    // B45 (وحدة 5): المورد اختياري وهيفضل اختياري — شرا كاش من محل من غير اسم
    // ده شرا حصل فعلاً، والفورم اللي يرفضه بيطلّع التوريد من الدفتر خالص.
    purchaseSupplier: "المورد",
    purchaseSupplierHint:
      "المورّد الذي أحضر هذه التوريدة. الحقل اختياري؛ ويمكن تسجيل التوريدة من دونه، لكن لن ترتبط ضريبتها بفاتورة ضريبية.",
    purchaseSupplierNone: "بلا مورد",
    purchaseVatExceedsTotal: "لا يمكن أن تتجاوز الضريبة المبلغ المدفوع.",
    recordAdjustment: "تسوية",
    adjustmentReason: "السبب",
    /* D138 — السبب الأول، وبعده كمية موجبة والفورم هو اللي بيحط الإشارة:
       الهالك والتالف خصم دايمًا، والتصحيح باختيار صريح زيادة أو خصم. */
    adjustmentQuantity: "الكمية",
    adjustmentQuantityHint:
      "رقم موجب بوحدة {unit}. السبب هو الذي يحدد هل تُخصم الكمية من المخزون أم تُضاف إليه.",
    adjustmentQuantityRequired: "اكتب كمية أكبر من صفر.",
    adjustmentDirection: "اتجاه التصحيح",
    adjustmentDirectionUp: "إضافة إلى المخزون",
    adjustmentDirectionDown: "خصم من المخزون",
    adjustmentTakesOffNote: "الهالك والتالف يُخصمان من المخزون دائمًا.",
    adjustmentPreview: "الرصيد بعد التسوية: {quantity} {unit}",
    adjustmentNote: "ملاحظة",
    adjustmentNoteHint: "اختيارية — مثلًا: سقط على الأرض، أو انتهت صلاحيته.",
    tiersTitle: "شرائح الأسعار",
    discountPercent: "نسبة الخصم %",
    settingsTitle: "إعدادات المطعم",
    dayStartHour: "بداية يوم العمل (ساعة)",
    dayStartHint: "تُحسب الفواتير السابقة لهذه الساعة على يوم العمل السابق.",
    timezone: "المنطقة الزمنية",
    receiptHeader: "أعلى الفاتورة",
    receiptFooter: "أسفل الفاتورة (بالعربية)",
    // D82: بقى فيه سطرين، واحد لكل لغة. قبل كده كان سطر واحد بيتطبع تحت
    // الإيصالين، يعني «شكرًا لزيارتكم» كانت بتظهر تحت إيصال إنجليزي.
    receiptFooterHint:
      "يُطبع هذا السطر أسفل الإيصال العربي، سواء الفاتورة العادية أو إيصال المرتجع.",
    receiptFooterEn: "أسفل الفاتورة (بالإنجليزية)",
    receiptFooterEnHint:
      "السطر الذي يُطبع أسفل الإيصال الإنجليزي. اتركه فارغًا لطباعة السطر العربي كما يحدث الآن.",
    receiptLanguage: "لغة الإيصال",
    receiptLanguageHint:
      "اللغة التي يُطبع بها الحساب المبدئي والإيصال، أيًا كانت لغة الشاشة. تذاكر المطبخ تبقى بلغة الشاشة.",
    stationPausedSuffix: "(موقوفة)",
    ordersViaCashierEnabled: "يرسل الويتر عبر الكاشير",
    ordersViaCashierLabel: "يرسل الويتر الطلب إلى الكاشير أولًا",
    ordersViaCashierHint:
      "عند التشغيل لا يرسل الويتر إلى المطبخ مباشرة: يضغط «إرسال إلى الكاشير»، فيسمع الكاشير تنبيهًا ويرسل الطلب إلى المطبخ، ويقبل وحده طلبات الضيوف عبر رمز QR أو يرفضها. عند الإيقاف يرسل مباشرة كل من يملك صلاحية الإرسال إلى المطبخ.",
    shiftTrackingEnabled: "متابعة الخزائن",
    shiftTrackingLabel: "تتبّع الخزائن",
    shiftTrackingHint:
      "عند تشغيلها، يجب فتح خزينة لتحصيل النقود، وستظهر شاشتا «الخزينة» و«حركة الخزينة» في القائمة.",
    shiftTrackingWarning:
      "عند تشغيلها، لا يمكن لأي موظف تحصيل فاتورة حتى تُفتح خزينة بيد شخص لديه صلاحية «الخزينة». يتضمن دور «الكاشير» الجاهز هذه الصلاحية؛ أما الدور المخصّص فيجب منحه الصلاحية من شاشة الأدوار أولًا.",
    /* UX-round D53 — عنوان القسم، وبقى كمان عنوان صفحته بعد ما الإعدادات العامة
       اتقسمت لستّ شاشات. */
    taxSectionTitle: "الضرائب والرسوم",
    /* UX-round D53 — القسم اللي كان أول بلوك في الصفحة القديمة من غير أي عنوان. */
    receiptSectionTitle: "الفاتورة المطبوعة",
    shiftSectionTitle: "الخزائن",
    /* شكوى صاحب المحل رقم ٢: الرقم لوحده مش بيقول اليوم ده بيتحسب إمتى. */
    dayStartNow: "الآن أي فاتورة تُحسب على يوم {date}.",
    // T1 / T15 / D173 — «طريقة عمل المطبخ»: one owner setting in place of the
    // two kitchen switches; each workflow says who taps what, when stock is
    // deducted, what the reports cannot time, whether a bill can be held, when
    // READY leaves the screens, the hardware, and the manual refresh.
    kitchenWorkflowTitle: "طريقة عمل المطبخ",
    kitchenWorkflowApplies: "يسري على الأطباق المرسلة بعد الحفظ.",
    kitchenWorkflowPaperOnly: "الورق فقط",
    kitchenWorkflowPaperOnlyHint:
      "تُطبع التذكرة عند الإرسال فيبدأ التحضير ويُخصم المخزون، ولا يلمس الطاهي أي شاشة؛ تحتاج طابعة لكل محطة. لا تُغلق الفاتورة قبل طباعة تذكرتها، وأوقات التحضير والتسليم تظهر في التقارير «غير محسوب».",
    kitchenWorkflowPaperAndPass: "الورق وشاشة التسليم",
    kitchenWorkflowPaperAndPassHint:
      "التذكرة المطبوعة تبدأ التحضير وتخصم المخزون، ومسؤول التسليم ينقر «تسليم» أو «تقديم» مرة واحدة لكل طلب؛ تحتاج طابعة وشاشة، والفاتورة لا تُغلق قبل طباعة تذكرتها. الجاهز يختفي بعد مدة الإخفاء، ووقت التحضير «غير محسوب»، والشاشة المنقطعة تحتاج «تحديث الشاشة» يدويًا.",
    kitchenWorkflowOneTap: "شاشة بنقرة واحدة",
    kitchenWorkflowOneTapHint:
      "يبدأ التحضير ويُخصم المخزون عند الإرسال، والطاهي ينقر «جاهز» مرة واحدة؛ لا تحتاج طابعة، ولا تُمنع الفاتورة من الإغلاق، ووقت الانتظار «غير محسوب». الجاهز يختفي بعد مدة الإخفاء، والشاشة المنقطعة تحتاج «تحديث الشاشة» يدويًا.",
    kitchenWorkflowTwoTap: "شاشة بنقرتين",
    kitchenWorkflowTwoTapHint:
      "الطاهي ينقر «بدء التحضير» فيُخصم المخزون ثم «جاهز»، وهي الطريقة الوحيدة التي تقيس وقت التحضير؛ لا تحتاج طابعة، ولا تُغلق الفاتورة قبل بدء تحضير أطباقها. الجاهز يختفي بعد مدة الإخفاء، والشاشة المنقطعة تحتاج «تحديث الشاشة» يدويًا.",
    kitchenWorkflowNone: "بدون شاشة أو طابعة",
    kitchenWorkflowNoneHint:
      "يبدأ التحضير ويُخصم المخزون عند الإرسال ولا ينقر أحد في المطبخ؛ لا تحتاج أي جهاز، ولا تُمنع الفاتورة من الإغلاق. أوقات المطبخ كلها تظهر في التقارير «غير محسوب».",
    kitchenWorkflowAdvanced: "متقدّم",
    kitchenWorkflowSub: "كيف ينتقل الطلب من شاشة البيع إلى الطبق",
    kitchenWorkflowPaperOnlyShort: "تبدأ التذكرة المطبوعة التحضير وتخصم المخزون، ولا يلمس الطاهي أي شاشة.",
    kitchenWorkflowPaperAndPassShort: "تبدأ التذكرة المطبوعة التحضير، ويسلّم مسؤول التسليم الطلب من الشاشة.",
    kitchenWorkflowOneTapShort: "يبدأ التحضير عند الإرسال، ويعلن الطاهي الطبق جاهزًا من الشاشة.",
    kitchenWorkflowTwoTapShort: "يبدأ الطاهي الطبق ثم يعلنه جاهزًا؛ الطريقة الوحيدة التي تقيس وقت التحضير.",
    kitchenWorkflowNoneShort: "يبدأ التحضير ويُخصم المخزون عند الإرسال، ولا ينقر أحد في المطبخ.",
    kitchenFactPrinter: "طابعة لكل محطة",
    kitchenFactPrinterAndScreen: "طابعة وشاشة تسليم",
    kitchenFactScreen: "شاشة مطبخ بلا طابعة",
    kitchenFactNoDevice: "بلا أي جهاز",
    kitchenFactPassTap: "نقرة واحدة لكل طلب عند التسليم",
    kitchenFactOneTap: "ينقر الطاهي «جاهز» مرة واحدة",
    kitchenFactTwoTap: "«بدء التحضير» ثم «جاهز»",
    kitchenFactNoTaps: "لا ينقر أحد في المطبخ",
    kitchenFactHoldsPrint: "لا تُغلق الفاتورة قبل طباعة تذكرتها",
    kitchenFactHoldsStart: "لا تُغلق الفاتورة قبل بدء تحضير أطباقها",
    kitchenFactNoHold: "لا تُمنع الفاتورة من الإغلاق",
    kitchenWorkflowPresets: "ابدأ من",
    kitchenWorkflowPresetsHint: "يملأ الحقول فقط؛ لا يُحفظ شيء حتى تضغط «حفظ».",
    kitchenPresetGrillHall: "مشويات وصالة",
    kitchenPresetCounter: "كاونتر",
    kitchenPresetDelivery: "توصيل",
    kitchenPresetCustom: "مخصّص",
    kitchenPresetTargets:
      "يضبط أيضًا أهداف الخدمة: الانتظار {queue} د، والتحضير {prep} د، والتسليم {pass} د.",
    kitchenAgeOff: "إخفاء الأطباق الجاهزة بعد",
    kitchenAgeOffHint:
      "0 = لا تُخفى. يجب أن تكون أطول من هدف التسليم ({minutes} د). طلبات الاستلام والتوصيل لا تُخفى حتى تُسلَّم.",
    kitchenAgeOffInvalid:
      "أدخل 0 أو مدة أطول من هدف التسليم ({minutes} د) وحتى 240 دقيقة.",
    kitchenChitGrouped: "تجميع الأطباق المتطابقة في سطر واحد على التذكرة",
    kitchenChitGroupedHint:
      "مثل «3× كباب»؛ أوراق الإلغاء والتعديل وإعادة التحضير تبقى طبقًا لكل سطر.",
    kitchenWorkflowLastChange: "آخر تغيير: {name} · {date}",
    kitchenWorkflowRemovedMember: "عضو محذوف",
    kitchenWorkflowUntimed: "ما لا يسجّله أحد لا يُقدَّر: يظهر في التقارير «غير محسوب» لا صفرًا.",
    kitchenElsewhereNote: "يُحدَّد مَن يحضّر كل صنف لكل محطة على حدة.",
    kitchenStationsRow: "محطات المطبخ",
    serviceChargeOffNote:
      "رسم الخدمة معطّل الآن في أعلى هذه الصفحة، لذلك لا تؤثر هذه الاختيارات في أي فاتورة حتى تفعّله.",
    serviceChargeEnabled: "رسم الخدمة",
    serviceChargeLabel: "نحسب رسم خدمة على الفواتير",
    serviceChargeHint:
      "نسبة تُضاف إلى حساب كل طاولة تُفتح، مثل نسبة 12% المعتادة لخدمة الصالة.",
    serviceChargePercent: "نسبة رسم الخدمة %",
    vatEnabled: "ضريبة القيمة المضافة",
    vatLabel: "نحسب ضريبة القيمة المضافة",
    vatHint: "للمطاعم المسجّلة في الضرائب — النسبة الرسمية الآن 14%.",
    vatPercent: "نسبة الضريبة %",
    vatOnServiceCharge: "أساس حساب الضريبة",
    vatOnServiceChargeLabel: "الضريبة على الحساب + رسم الخدمة",
    vatOnServiceChargeHint:
      "هذه هي المعاملة المعتادة في مصر: يخضع رسم الخدمة نفسه للضريبة. إذا أزلت العلامة، فستُحسب الضريبة على الأصناف فقط.",
    pricesIncludeTax: "أسعار قائمة الطعام",
    pricesIncludeTaxLabel: "أسعار قائمة الطعام شاملة الضريبة والخدمة",
    pricesIncludeTaxHint:
      "عند تحديدها، يكون سعر قائمة الطعام هو المبلغ النهائي الذي يدفعه العميل، وتوضّح الفاتورة مكوّناته. وإذا لم تحددها، تُضاف الرسوم إلى السعر.",
    taxRegistrationNumber: "رقم التسجيل الضريبي",
    taxRegistrationHint: "يُطبع في أعلى كل فاتورة عند إدخاله.",
    // الوحدة 3 — الحقائق اللي التقارير بتشتق منها الأساس.
    // مش مفاتيح بتغيّر الحساب: الرقمين بيتحسبوا في الحالتين، وده بيقرر الرقم
    // اللي بيتقرا الأول. عشان كده الشرح بيقول «التقارير» مش «الفواتير».
    factsSectionTitle: "الوضع الضريبي للمطعم",
    vatRegistered: "المطعم مسجّل ضريبيًا",
    vatRegisteredLabel: "المطعم مسجّل في الضرائب",
    vatRegisteredHint:
      "يختلف هذا عن خيار «نحسب ضريبة» أعلاه؛ فهو يصف حالة المطعم نفسه. إذا كان مسجّلًا، يعرض التقرير تكلفة تقديرية بلا ضريبة المشتريات بجانب التكلفة الفعلية.",
    serviceChargeKeptByHouse: "لمن يذهب رسم الخدمة؟",
    serviceChargeKeptByHouseLabel: "المطعم يأخذ رسم الخدمة (لا الفريق)",
    serviceChargeKeptByHouseHint:
      "المعتاد في مصر أن تكون نسبة 12% من حق الفريق، وهذا هو الإعداد الافتراضي. إذا احتفظ بها المطعم، فتُعد دخلًا له ويحسب التقرير الهامش على هذا الأساس.",
    serviceChargeChannelsTitle: "رسم الخدمة حسب نوع الطلب",
    serviceChargeChannelsHint:
      "يُفعَّل رسم الخدمة نفسه في أعلى هذه الصفحة، وتحمله فواتير الصالة دائمًا ما دام مفعّلًا. ولطلبات الاستلام والتوصيل إعداد مستقل هنا، يُطبَّق عند فتح الفاتورة.",
    serviceChargeOnTakeaway: "طلب للاستلام",
    serviceChargeOnTakeawayLabel: "نحسب رسم خدمة على طلبات الاستلام",
    serviceChargeOnDelivery: "توصيل",
    serviceChargeOnDeliveryLabel: "نحسب رسم خدمة على التوصيل",
    deliveryFeeVatEnabled: "ضريبة على رسم التوصيل",
    deliveryFeeVatEnabledLabel: "رسم التوصيل عليه ضريبة",
    deliveryFeeVatEnabledHint:
      "يخضع الرسم للضريبة افتراضيًا. وتنفصل هذه الضريبة عن ضريبة الطعام؛ إذ تُحسب على الرسم وحده، ولا يدخل الرسم في ربح الطعام.",
    deliveryFeeDefault: "رسم التوصيل الافتراضي",
    deliveryFeeDefaultHint:
      "يُنسخ إلى كل فاتورة توصيل عند فتحها، ويمكن تغييره على الفاتورة نفسها للعنوان الأبعد. لا يؤثر تغييره هنا في الفواتير المفتوحة سابقًا.",
    // G13 — رقم غير صالح يُرفض بجانب الحقل بدل أن يُحفظ صفرًا.
    deliveryFeeDefaultInvalid: "اكتب رقمًا موجبًا أو صفرًا.",
    /* F24 (§5.12) — الطلب من صفحة المطعم: مفتاح المالك ومهلة الرد، وسطر
       للقراءة فقط عن الإيقاف المؤقت الذي يملكه الكاشير. */
    onlineOrderingTitle: "الطلب من صفحة المطعم",
    onlineOrderingEnabled: "الطلب عبر الإنترنت",
    onlineOrderingEnabledLabel:
      "يطلب العملاء استلامًا أو توصيلًا من صفحة المطعم",
    onlineOrderingEnabledHint:
      "يصل الطلب إلى الكاشير بطاقةً يقبلها أو يرفضها. لا تُفتح فاتورة ولا يُستهلك رقم يومي قبل القبول، وتُسعَّر الفاتورة عند القبول برسم التوصيل الافتراضي.",
    onlineOrderExpiryMinutes: "مهلة الرد على الطلب",
    onlineOrderExpiryMinutesHint:
      "الدقائق التي يبقى فيها الطلب بانتظار رد الكاشير قبل أن ينتهي تلقائيًا ويُبلَّغ العميل.",
    onlineOrderExpiryMinutesInvalid: "اكتب عددًا صحيحًا من 3 إلى 60 دقيقة.",
    onlineOrderExpiryMinutesUnit: "دقيقة",
    /* 2026-10-23 — مفتاح المالك لرسالة تأكيد طلب التوصيل على واتساب. */
    onlineCustomerConfirmation: "تأكيد طلبات التوصيل",
    onlineCustomerConfirmationLabel: "تأكيد الطلب مع العميل على واتساب",
    onlineCustomerConfirmationHint:
      "نرسل للعميل رسالة واتساب ليؤكد طلب التوصيل. الطلب يصل إليك في كل الأحوال.",
    /* 2026-10-23 — ما يحدث عند الإيقاف: رسالة استلام بلا طلب تأكيد. */
    onlineCustomerConfirmationOffHint:
      "عند الإيقاف نرسل للعميل رسالة استلام الطلب ورابط المتابعة بدون طلب تأكيد. الطلب يصل إليك في كل الأحوال.",
    onlineOrderingPausedNote:
      "أوقف الكاشير استقبال الطلبات مؤقتًا من شاشة الطاولات، ويُستأنف من هناك لا من هنا.",
    onlineOrderingNotPausedNote:
      "الاستقبال يعمل الآن. يكون إيقافه المؤقت — في يوم مزدحم مثلًا — من شاشة الطاولات لدى الكاشير، لا من هنا.",
    writeOffVatCountedAsOwed: "ضريبة الفواتير المشطوبة",
    writeOffVatCountedAsOwedLabel:
      "نعتبر ضريبة الفاتورة المشطوبة مستحقّة علينا",
    writeOffVatCountedAsOwedHint:
      "الإعداد الافتراضي ألا تُحتسب؛ فهي ضريبة على مبلغ لم يُحصّل. يظهر الرقمان في التقرير في الحالتين، ويحدد هذا الخيار الرقم الرئيسي فقط.",
    factsUnusedNote:
      "هذه الإعدادات مستخدمة الآن: للفاتورة نوع (داخل المطعم / طلب للاستلام / توصيل)، وتُحسب خدمة الاستلام والتوصيل من هنا، ويظهر رسم التوصيل في عمود مستقل عن سعر الطعام. تُقرأ عند فتح الفاتورة (وعند تعديل رسومها) فقط، ولا تغيّر فاتورة مفتوحة.",
    taxSnapshotNote:
      "ينطبق أي تغيير هنا على الطاولات التي تُفتح بعده فقط. وتستمر الطاولة المفتوحة وفاتورتها غير المدفوعة بالنسب التي بدأت بها.",
    // نتيجة الطاولة المستديرة رقم ٣ — الحقل اللي وحدة D شحنت العمود والجملة
    // بتاعته من غيره، فكل محل كان قاعد على أصفر من غير أي طريقة يغيّره.
    varianceSectionTitle: "فرق الخزينة",
    cashVarianceTolerance: "الفرق الذي لا يستدعي تنبيهًا (جنيه)",
    cashVarianceToleranceHint:
      "قد يكون فرق ربع جنيه في ليلة تضم 400 فاتورة فرق تقريب. اتركه صفرًا إذا أردت التنبيه على أي فرق.",
    // D146 — the most a discount at the bill may take.
    billDiscountMaxPctLabel: "أقصى خصم على الفاتورة",
    billDiscountMaxPctHint:
      "نسبة من صافي الطعام قبل الخدمة والضريبة، وتسري على الجميع بمن فيهم صاحب المطعم.",
    billDiscountMaxPctRange: "رقم صحيح من {min} إلى {max}",
    cashVarianceToleranceNote:
      "هذا إعداد للعرض فقط، وليس حدًا ماليًا أو سماحًا. يُسجّل كل فرق كما قيس، ويحدد هذا الرقم متى تعرض الشاشة تنبيهًا.",
    cashVarianceToleranceRange: "اكتب رقمًا من 0 إلى {max}.",
    refundSectionTitle: "المرتجعات",
    refundLateAfterMinutes: "بعد كم دقيقة يُعد المرتجع متأخرًا؟",
    refundLateAfterMinutesRange:
      "يجب أن يكون الرقم بالدقائق بين {min} و{max} (7 أيام).",
    refundLateAfterMinutesHint:
      "بالدقائق منذ دفع الفاتورة. الإعداد الافتراضي 240 دقيقة (4 ساعات)، والحد من 5 إلى 10080 دقيقة (7 أيام). إذا تركت الخانة فارغة، يبقى الرقم المحفوظ كما هو.",
    refundLateNote:
      "يضيف هذا الرقم علامة فقط ولا يمنع المرتجع. إذا تجاوز المرتجع هذه المدة، يرى الكاشير تحذيرًا قبل التأكيد، وتُسجّل الحركة «متأخرة» في مراجعة الخزينة والتقارير. ينطبق التعديل على المرتجعات التالية فقط ولا يغيّر ما سُجّل سابقًا.",
    addMenuItem: "إضافة صنف إلى قائمة الطعام",
    addonsListEmpty: "لا توجد مجموعات مطابقة للبحث",
    addonsPickTitle: "اختر مجموعة",
    addonsPickBody: "اضغط على مجموعة من القائمة لكي تعدّلها هنا.",
    addonGroupKind: "ما وظيفة هذه المجموعة؟",
    addonGroupKindVariant: "تغيّر الصنف نفسه",
    addonGroupKindExtra: "تضيف إلى الصنف",
    addonGroupKindVariantHint:
      "مثل «الحجم». سيفصل تقرير الأرباح الصنف وفق هذا الاختيار.",
    addonGroupKindExtraHint:
      "مثل «الصلصات». يُحسب الاختيار في سعر الطبق من دون فصل الصنف في التقرير.",
    addonGroupType: "كم اختيارًا مسموحًا؟",
    addonGroupPickOne: "اختيار واحد",
    addonGroupPickMany: "اختيار متعدد",
    addonGroupTypeHint:
      "هذه قاعدة لهذه المجموعة فقط، ولا ترتبط بما إذا كانت المجموعة تغيّر الصنف أو تضيف إليه.",
    addonGroupRequired: "إجباري",
    addonGroupRequiredSub: "يجب على الكاشير الاختيار قبل إضافة الصنف",
    addonGroupCustomBounds:
      "هذه المجموعة مضبوطة على {min}–{max}. سيغيّر هذا التبديل النطاق.",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    addonGroupUsageOne: "مربوطة بصنف واحد",
    addonGroupUsageTwo: "مربوطة بصنفين",
    addonGroupUsageFew: "مربوطة بـ {count} أصناف",
    addonGroupUsage: "مربوطة بـ {count} صنف",
    addonGroupUsageNone: "حتى الآن غير مرتبطة بأي صنف",
    addonOptions: "الاختيارات",
    // PAPER (2026-10): أعمدة جدول مجموعات الإضافات.
    addonColGroup: "المجموعة",
    addonColType: "النوع",
    addonColUsedOn: "مستخدمة في",
    addonColActive: "مفعّلة",
    // سطر المعاينة في محرر المجموعة (PAPER): المجموعة كما سيعرضها الكاشير،
    // من المسودة قبل الحفظ. {label} هي قاعدة الاختيار و{items} قائمة الاختيارات.
    addonTillPreview: "يرى الكاشير: {name} — {label}: {items}.",
    addonTillPreviewKept:
      "الفواتير السابقة تحتفظ بالاختيارات كما كانت وقت البيع.",
    addonOptionsSub: "يمكن لكل اختيار أن يزيد سعر الصنف.",
    addonOptionAdd: "+ إضافة اختيار",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    addonOptionCountOne: "اختيار واحد",
    addonOptionCountTwo: "اختيارين",
    addonOptionCountFew: "{count} اختيارات",
    addonOptionCount: "{count} اختيار",
    addonRequiredWord: "مطلوبة",
    addonOptionalWord: "اختيارية",
    addonOptionsEmptyBody: "أضف أول اختيار يراه الكاشير.",
    addonOptionName: "الاسم",
    addonOptionNameEn: "بالإنجليزية",
    addonOptionPrice: "زيادة السعر",
    addonOptionPlaceholder: "مثال: كبير",
    addonOptionsEmpty: "لا توجد اختيارات حتى الآن",
    addonOptionRemove: "إزالة الاختيار",
    addonLinked: "الأصناف المربوطة",
    addonLinkedSub: "تظهر المجموعة على هذه الأصناف لدى الكاشير.",
    // PAPER (2026-10-03): the dish form's quiet "+ Add …" under the rows —
    // the plus is drawn as an icon, so the words carry none.
    addonOptionAddQuiet: "إضافة اختيار",
    addonLinkSearch: "ربط صنف…",
    addonLinkNoMatch: "لا يوجد صنف بهذا الاسم",
    addonLinkedNone: "غير مربوطة بأي صنف بعد",
    addonUnlink: "فصل الصنف",
    addonUnsaved: "توجد تعديلات غير محفوظة",
    addonNewTitle: "مجموعة إضافات جديدة",
    addonNewSub: "الكاشير يراها وهو يأخذ الطلب",
    addonCreate: "إنشاء المجموعة",
    addonNeedName: "اكتب اسم المجموعة",
    addonNeedOption: "أضف اختيارًا واحدًا على الأقل",
    addonNeedOptionNames: "كل اختيار يحتاج إلى اسم",
    addonCreated: "أُنشئت المجموعة — اربطها بأصناف قائمة الطعام",
    addonRestoreOption: "إعادة الاختيار",
    addonOptionRestored: "أُعيد الاختيار",
    addonReactivateTitle: "هل تريد إعادة المجموعة؟",
    addonReactivateBody:
      "ستظهر مجددًا للكاشير على جميع الأصناف التي كانت مرتبطة بها.",
    addonReactivated: "أُعيدت المجموعة",
    addonDeactivate: "إيقاف المجموعة",
    addonDeactivateTitle: "هل تريد إيقاف هذه المجموعة؟",
    addonDeactivateBody:
      "ستختفي من شاشة الكاشير ومن جميع الأصناف المرتبطة بها. تبقى الفواتير القديمة كما هي، ويمكنك إعادة تفعيلها في أي وقت.",
    addonDeactivated: "أُوقفت المجموعة",
    addonReactivate: "إعادة المجموعة",
    // B117/E1 — الشاشة دي كانت الوحيدة في الباك أوفيس من غير رفض وقت التشغيل،
    // فالطباخ كان بيتودّي عليها ويقرا عنوانها وهو أصلًا مش مسموح له يشوف
    // الأصناف: الـ API بيرفض تحته مرتين على `RESTAURANT_MENU_VIEW`، والشاشة
    // نفسها ما كانتش بتقول حاجة. كل شاشة زميلة بترفض بجملة واحدة، ودي بقت زيها.
    menuNoAccessTitle: "لا تملك صلاحية عرض قائمة الأصناف",
    menuNoAccessBody:
      "تعرض هذه الشاشة أسعار الأصناف وتكلفتها، وتتطلب صلاحية «عرض قائمة الطعام». إذا أردت تحديد صنف نافد (86)، فاستخدم شاشة المطبخ.",
    menuSearchPlaceholder: "ابحث عن صنف…",
    // PAPER (2026-10): شريط أدوات جدول قائمة الطعام وعمود القسم.
    menuNewItem: "صنف جديد",
    menuColSection: "القسم",
    menuFilterButton: "تصفية",
    menuFilterClear: "مسح التصفية",
    // PAPER only (founder, 2026-10-02): the catalogue pages in the redesign's
    // own words — the top-bar sentence, the toolbar and the column names.
    paperMenuSubtitle: "كل ما في قائمة الطعام. أوقف الصنف عند نفاده.",
    paperAddonsSubtitle:
      "الإضافات والأحجام والاختيارات التي يحددها الضيف عند طلب الطبق.",
    paperCategoriesSubtitle:
      "قسّم الأطباق في قائمة الطعام وشاشة البيع. يرى الفريق هذا الترتيب على شاشة البيع.",
    paperCatalogNew: "جديد",
    paperCatalogViews: "طريقة العرض",
    paperCatalogViewTable: "جدول",
    paperCatalogViewGallery: "معرض",
    paperCatalogColCategory: "القسم",
    paperCatalogColOnPos: "في شاشة البيع",
    paperCategoriesSearch: "ابحث عن قسم",
    paperCategoriesCountOne: "قسم واحد",
    paperCategoriesCountTwo: "قسمين",
    paperCategoriesCountFew: "{count} أقسام",
    paperCategoriesCount: "{count} قسم",
    paperDishesCountOne: "طبق واحد",
    paperDishesCountTwo: "طبقين",
    paperDishesCountFew: "{count} أطباق",
    paperDishesCount: "{count} طبق",
    paperAddonsActiveOne: "واحدة مفعّلة",
    paperAddonsActiveTwo: "اثنان مفعّلان",
    paperAddonsActiveFew: "{count} مفعّلة",
    paperAddonsActive: "{count} مفعّلة",
    paperGroupsCountOne: "مجموعة واحدة",
    paperGroupsCountTwo: "مجموعتين",
    paperGroupsCountFew: "{count} مجموعات",
    paperGroupsCount: "{count} مجموعة",
    paperAddonKindVariant: "نوع",
    paperAddonKindExtra: "إضافة",
    paperAddonUnused: "غير مستخدمة بعد",
    paperAddonsTitle: "مجموعات الإضافات",
    // PAPER only (founder, 2026-10-03): the redesign's dish peek and dish form.
    paperDishEdit: "تعديل",
    paperDishAllSettings: "كل الإعدادات",
    paperDishReplacePhoto: "تغيير الصورة",
    paperDishUsePhoto: "استخدام صورة",
    paperDishUseIcon: "استخدام أيقونة",
    paperDishRowCategory: "القسم",
    paperDishRowBasePrice: "السعر الأساسي",
    paperDishRowFoodCost: "تكلفة الطعام",
    paperDishRowMargin: "هامش الربح",
    paperDishRowStatus: "الحالة",
    paperDishRowSold: "المبيع",
    paperDishRowRevenue: "إيراد اليوم",
    paperDishRowCanMake: "يمكن تحضير",
    paperDishNoRecipe: "لا توجد وصفة بعد",
    paperDishMarginLine: "{pct} · ربح {amount} في الطبق",
    paperDishSoldToday: "{quantity} اليوم",
    paperDishServingsNone: "نفدت",
    paperDishServingsOne: "طبق واحد إضافي",
    paperDishServingsTwo: "طبقان إضافيان",
    paperDishServingsFew: "{count} أطباق إضافية",
    paperDishServings: "{count} طبقًا إضافيًا",
    paperDishPlatesNone: "لا أطباق",
    paperDishPlatesOne: "طبق واحد",
    paperDishPlatesTwo: "طبقين",
    paperDishPlatesFew: "{count} أطباق",
    paperDishPlates: "{count} طبق",
    paperDishInStock: "{quantity} في المخزن",
    paperDishNotTracked: "غير متتبَّع",
    paperDishAvailable: "متوفر",
    paperDishSoldOut: "نفد",
    paperDishOffMenu: "متوقف",
    paperDishSizes: "الأحجام",
    paperDishAddons: "الإضافات",
    paperDishIngredientsPerServing: "المكونات في الطبق",
    paperDishBase: "أساسي",
    paperDishNoIngredients: "لا توجد مكونات مرتبطة. اضغط «تعديل» لإضافتها.",
    paperDishCrumbNew: "قائمة الطعام / صنف جديد",
    paperDishCrumbEdit: "قائمة الطعام / تعديل صنف",
    paperDishAddToMenu: "إضافته إلى قائمة الطعام",
    paperDishSaveChanges: "حفظ التعديلات",
    paperDishMediaHint: "اضغط لاختيار أيقونة أو رفع صورة",
    paperDishChangeIcon: "تغيير الأيقونة",
    paperDishTabBadges: "أيقونات",
    paperDishTabUpload: "رفع صورة",
    paperDishUploadTitle: "رفع صورة للطبق",
    paperDishUploadHint:
      "PNG أو JPG، ويُفضَّل المربع. تظهر على شاشة البيع وفي قائمة الطعام.",
    paperDishRemovePhoto: "إزالة الصورة",
    paperDishAllIcons: "كل الأيقونات",
    paperDishNamePlaceholder: "اسم الطبق",
    paperDishNeedName: "اكتب اسم الطبق أولًا.",
    paperDishEnglishName: "الاسم بالإنجليزية",
    paperDishPrice: "السعر",
    paperDishPrepTime: "وقت التحضير",
    paperDishPrepTimeHint: "اتركه فارغًا لاستخدام الوقت المعتاد للمطعم.",
    /* D131 — ما يراه الضيف على الطبق في قائمة الطاولة. */
    dishChefPick: "اختيار الشيف",
    dishSpicy: "حار",
    dishVegetarian: "نباتي",
    dishFlagsHint: "تظهر للضيف على الطبق في قائمة الطاولة.",
    dishCalories: "السعرات الحرارية",
    dishCaloriesUnit: "سعر حراري",
    dishCaloriesHint: "اختياري. اتركه فارغًا إن لم تكن تعرفها.",
    dishCaloriesRange: "يجب أن تكون السعرات عددًا صحيحًا من {min} إلى {max}.",
    paperDishOnMenu: "ظاهر في قائمة الطعام",
    paperDishPickCategories: "اختيار الأقسام",
    paperDishSizesHint: "الفرق المضاف إلى السعر الأساسي",
    paperDishSizeName: "اسم الحجم",
    paperDishRegular: "عادي",
    paperDishAddSize: "إضافة حجم",
    paperDishAddonsHint: "إضافات اختيارية يحددها الفريق على شاشة البيع",
    paperDishAddonName: "اسم الإضافة",
    paperDishNoAddons: "لا توجد إضافات بعد.",
    paperDishAddAddon: "إضافة جديدة",
    paperDishAddonSource: "مصدر هذه الإضافة",
    paperDishAddGroup: "إضافة مجموعة جاهزة",
    paperDishSearchGroups: "ابحث عن مجموعة أو اختيار",
    paperDishNoGroupMatch: "لا توجد مجموعة بهذا الاسم",
    paperDishLibraryGroup: "{name} · من مكتبة الإضافات",
    paperDishDetachGroup: "إزالة المجموعة",
    paperDishAddonFromStock: "من المخزن",
    paperDishAddonFreeText: "نص حر",
    paperDishAddonPerPick: "الكمية لكل اختيار",
    paperDishAddonNotStock: "ليست من المخزون — تُسجَّل على الفاتورة وفي المطبخ فقط",
    paperDishAddonUseFreeText: "أو اكتب اسمًا حرًا",
    paperDishFreeAddonPlaceholder: "مثلًا: مستوية جيدًا، بدون بصل",
    paperDishAddonSharedStock:
      "تخصم {name} — مشتركة مع أطباق أخرى، غيّرها من صفحة الإضافات",
    paperDishAddonCostedHere:
      "التكلفة محددة لهذا الطبق وحده — غيّرها من «تكلفة الاختيارات لهذا الصنف» في صفحة الطبق",
    paperDishIngredients: "المكونات",
    paperDishIngredientsHint: "الكمية في الطبق الواحد. يقل المخزون مع كل عملية بيع.",
    paperDishChooseIngredient: "اختيار مكوّن",
    paperDishNoIngs: "لا توجد مكونات مرتبطة بعد.",
    paperDishAddIngredient: "إضافة مكوّن",
    paperDishFoodCostPerServing: "تكلفة الطعام في الطبق",
    paperDishMarginPct: "هامش {pct}%",
    paperDishSharedHint: "مشتركة مع أطباق أخرى — غيّر اسمها من صفحة الإضافات",
    paperDishRemove: "إزالة",
    paperDishPartSaved: "حُفظ الطبق، لكن لم يُحفظ كل ما معه: {reason}",
    menuFilterActive: "مفعّل",
    menuFilterPaused: "موقوف",
    // D32 — «نفد النهاردة» غير «موقوف»: الأولانية قرار خدمة بتتقفل في ثانية،
    // والتانية بتشيل الصنف من المتجر الأونلاين كمان.
    menuFilterSoldOut: "نفد اليوم",
    menuItemPaused: "موقوف",
    // D32 — «نفد» غير «موقوف»: الموقوف مرفوع من المنيو خالص، والنافد خلص
    // النهاردة بس وهيرجع بكرة، ومحدش لازم يعدّل المنيو عشان يرجّعه.
    menuItemSoldOut: "نفد",
    /* نفس عيب B67: مافيش تصريف جمع في الـ i18n هنا، و«1 مجموعة إضافات» بتتقرا
       غلط. اتلقى في جولة المتصفح بتاعة B71 على شاشة مش من نفس الوحدة. */
    menuAddonGroupsCountOne: "مجموعة إضافات واحدة",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    menuAddonGroupsCountTwo: "مجموعتا إضافات",
    menuAddonGroupsCountFew: "{count} مجموعات إضافات",
    menuAddonGroupsCount: "{count} مجموعة إضافات",
    menuNoMatches: "لا يوجد صنف بهذا الاسم",
    menuNoMatchesBody: "جرّب اسمًا آخر أو أزل عبارة البحث.",
    clearSearch: "إزالة عبارة البحث",
    menuListTitle: "الأصناف",
    menuUncategorized: "بلا قسم",
    menuEmptyTitle: "قائمة الطعام فارغة حتى الآن",
    menuEmptyBody: "أضف أول صنف من الأعلى، وسيصبح جاهزًا للطلب فورًا.",
    // The second submit in the create modal: for a first-run menu entry, where
    // the job is 40 items in one sitting, not one item then a page change.
    menuDuplicate: "نسخ الصنف",
    menuDuplicated: "أُنشئت نسخة",
    menuCopySuffix: " (نسخة)",
    menuUnsaved: "توجد تعديلات غير محفوظة",
    menuRecipeCost: "التكلفة التقديرية",
    menuRecipePartial: "يوجد مكوّن غير موجود — التكلفة ناقصة",
    /* D139 — مكوّن في الوصفة ما اتشراش لسه: تكلفته مش صفر، مش معروفة. */
    menuRecipeUnknownOne:
      "مكوّن واحد لم يُشترَ بعد — تكلفته غير معروفة وغير محسوبة",
    menuRecipeUnknownTwo:
      "مكوّنان لم يُشتريا بعد — تكلفتهما غير معروفة وغير محسوبة",
    menuRecipeUnknownFew:
      "{count} مكوّنات لم تُشترَ بعد — تكلفتها غير معروفة وغير محسوبة",
    menuRecipeUnknown:
      "{count} مكوّنًا لم تُشترَ بعد — تكلفتها غير معروفة وغير محسوبة",
    menuRecipeProfit: "الربح",
    // D37 on this surface too: هامش الربح ده بيتحسب على سعر الليستة، قبل أي
    // خصم فئة سعرية — الفئة بتغيّر سعر البيع لحظة البيع نفسها فمفيش رقم واحد
    // "صح" نعوّض بيه، بس التوضيح ده لازم يبقى واضح.
    // DEF-2: "تيير" transliterates the English word instead of translating
    // it — meaningless to a native reader. "سعر الليستة" stays as-is; it is
    // ordinary Egyptian retail usage.
    menuRecipeMarginListPriceNote:
      "الهامش محسوب على سعر القائمة، قبل أي خصم من شرائح الأسعار.",
    // S2/B1 — the recipe card's link to the item's row on the realised-margin
    // report (S1), resolved by the ticket's own recommendation rather than a
    // tier selector on this card.
    menuRecipeReportLink: "عرض الربح الفعلي في التقرير",
    /* تدقيق الجولة 2 (المرحلة 3): أساس أرقام بطاقة الوصفة، نثرًا تحتها — من
       أين التقدير، وما الذي لا يدخله، وأين التكلفة الفعلية. */
    menuRecipeCostBasis:
      "تقدير من الوصفة الحالية بمتوسط التكلفة الحالي، قبل رسم الخدمة والضريبة وعمولة المنصات والتغليف. التكلفة الفعلية لكل طبق مسجّلة عند التحضير وتُقرأ في تقرير ربحية الأصناف.",
    saveAndAddAnother: "الحفظ وإضافة صنف آخر",
    menuItemAdded: "أُضيف {name}",
    menuFilterUnfiledClear: "إعادة عرض كل الأصناف",
    addArea: "إضافة منطقة",
    addTable: "إضافة طاولة",
    tablesListTitle: "الطاولات",
    floorEmptyTitle: "لا توجد طاولات حتى الآن",
    floorEmptyBody: "أضف منطقة أولًا — مثل الصالة أو الشرفة — ثم أضف طاولاتها.",
    addIngredient: "إضافة مكوّن",
    ingredientActions: "شراء أو تسوية",
    ingredientsEmptyTitle: "لا توجد مكوّنات حتى الآن",
    ingredientsEmptyBody: "أضف مكوّناتك لتسجيل المشتريات ومعرفة تكلفة الأصناف.",
    ingredientsNoAccessTitle: "لا تملك صلاحية عرض المخزون",
    ingredientsNoAccessBody:
      "تحتاج هذه الشاشة إلى صلاحية إدارة التكلفة. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    backToMenu: "العودة إلى قائمة الطعام",
    menuItemDetailTitle: "الصنف",
    menuItemDetailsSection: "بيانات الصنف",
    menuItemNotFoundTitle: "هذا الصنف غير موجود",
    menuItemNotFoundBody:
      "ربما أُزيل الصنف أو تغيّر. عُد إلى قائمة الطعام واختر صنفًا آخر.",
    menuItemStatus: "الحالة",
    menuItemActive: "مفعّل في قائمة الطعام",
    menuItemInactive: "متوقف",
    deactivateHint:
      "لا يظهر الصنف الموقوف للكاشير أو للعميل، وتبقى الفواتير القديمة كما هي.",
    // D32 — التوجيلة دي للخدمة، مش للمنيو. عشان كده لهجتها «النهاردة».
    menuItemAvailability: "التوفّر اليوم",
    menuItemAvailable: "متوفر",
    menuItemSoldOutState: "نفد اليوم",
    menuItemAvailabilityHint:
      "عند نفاد الصنف، حدّده «نافدًا» ليتوقف بيعه عند الكاشير وفي قائمة الطعام عبر QR، مع بقائه ظاهرًا للويتر بعلامة. يختلف ذلك عن إيقاف الصنف من قائمة الطعام، ولا تتغير قائمة الطعام نفسها.",
    recipeTitle: "الوصفة",
    recipeSubtitle: "الكميات التي تُخصم من المخزون كلما بيع الصنف.",
    recipeAddIngredient: "+ مكوّن",
    recipeEmptyTitle: "لا توجد وصفة لهذا الصنف حتى الآن",
    recipeEmptyBody:
      "تربط الوصفة البيع بالمخزون: عند بيع الصنف، تُخصم هذه الكميات من المكوّنات تلقائيًا، وعندئذ تعرف تكلفة الصنف وربحه. من دون وصفة، يُباع الصنف من دون خفض المخزون.",
    recipeEmptyAction: "إضافة أول مكوّن",
    recipeNoIngredientsTitle: "سجّل مكوّناتك أولًا",
    recipeNoIngredientsBody:
      "تُبنى الوصفة من مكوّنات المخزون. سجّل المكوّنات أولًا، مثل اللحم والجبن والخبز، ثم حدّد كمية كل مكوّن في الصنف.",
    recipeNoIngredientsAction: "الانتقال إلى المكوّنات",
    addRecipeLine: "إضافة مكوّن للوصفة",
    editRecipeLine: "تعديل الكمية",
    deleteRecipeLine: "إزالة المكوّن",
    deleteRecipeLineTitle: "هل تريد إزالة المكوّن من الوصفة؟",
    deleteRecipeLineBody:
      "لن يُخصم هذا المكوّن من المخزون مرة أخرى عند بيع الصنف.",
    recipeIngredient: "المكوّن",
    recipePickIngredient: "اختر مكوّنًا",
    recipeNoIngredientMatch: "لا يوجد مكوّن بهذا الاسم",
    recipeIngredientRequired: "اختر المكوّن أولًا.",
    recipeIngredientLocked:
      "لتغيير المكوّن نفسه، أزل هذا السطر ثم أضف مكوّنًا آخر.",
    recipeQuantity: "الكمية في الطلب الواحد",
    recipeQuantityHint: "تأتي الوحدة من المكوّن نفسه ولا تُكتب هنا.",
    recipeQuantityRequired: "اكتب كمية أكبر من صفر.",
    recipePerServing: "لكل طلب",
    // ---- الإضافات والأحجام (مجموعات + اختيارات + الربط بالصنف) ----
    addonsTitle: "الإضافات والأحجام",
    addonsDescription:
      "للصنف في قائمة الطعام سعر واحد فقط. يُضبط أي فرق في السعر — مثل الأحجام أو الجبن الإضافي أو الصلصة — هنا ضمن مجموعة إضافات مرتبطة بالصنف.",
    addonGroupsEmptyTitle: "لا توجد مجموعات إضافات حتى الآن",
    addonGroupsEmptyBody:
      "من هنا تنشئ الأحجام والإضافات. أنشئ مجموعة مثل «الحجم»، وأضف إليها الاختيارات وفروق الأسعار، ثم اربطها بالأصناف ذات الأحجام.",
    addonGroupsEmptyAction: "إنشاء أول مجموعة",
    addAddonGroup: "مجموعة جديدة",
    editAddonGroup: "تعديل المجموعة",
    addonGroupName: "اسم المجموعة",
    addonGroupNameEn: "الاسم بالإنجليزية",
    addonGroupNameHint:
      "الاسم الذي سيراه الكاشير فوق الاختيارات، مثل «الحجم» أو «إضافات».",
    addonGroupNameRequired: "اكتب اسم المجموعة.",
    addonGroupMin: "أقل عدد اختيارات",
    addonGroupMax: "أكبر عدد اختيارات",
    addonGroupMinHint:
      "يعني الصفر أن المجموعة اختيارية، ويعني الواحد وجوب اختيار عنصر قبل إضافة الصنف.",
    addonGroupMaxHint: "اتركها فارغة إذا لم يوجد حد أقصى.",
    addonGroupCountInvalid: "اكتب رقمًا صحيحًا لا يقل عن صفر.",
    addonGroupBoundsInvalid:
      "يجب أن يكون الحد الأقصى مساويًا للحد الأدنى أو أكبر منه.",
    addonGroupOptional: "اختياري",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    addonGroupUpToOne: "اختيار واحد فقط",
    addonGroupUpToTwo: "حتى اختيارين",
    addonGroupUpToFew: "حتى {max} اختيارات",
    addonGroupUpTo: "حتى {max} اختيار",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    addonGroupAtLeastOne: "اختيار واحد على الأقل",
    addonGroupAtLeastTwo: "اختياران على الأقل",
    addonGroupAtLeastFew: "{min} اختيارات على الأقل",
    addonGroupAtLeast: "{min} اختيار على الأقل",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    addonGroupRangeOne: "اختيار واحد بالضبط",
    addonGroupRangeTwo: "من {min} إلى اختيارين",
    addonGroupRangeFew: "من {min} إلى {max} اختيارات",
    addonGroupRange: "من {min} إلى {max} اختيار",
    addonGroupPaused: "متوقفة",
    addonGroupActiveLabel: "المجموعة مفعّلة",
    addonGroupActiveHint:
      "إذا أوقفتها، فلن تظهر للكاشير مع أي صنف. وتبقى الفواتير القديمة كما هي.",
    addonGroupNoAddons:
      "لا توجد اختيارات داخل هذه المجموعة حتى الآن — أضف اختيارًا ليظهر للكاشير.",
    addAddon: "إضافة اختيار",
    editAddon: "تعديل الاختيار",
    addonName: "اسم الاختيار",
    addonNameEn: "الاسم بالإنجليزية",
    addonNameRequired: "اكتب اسم الاختيار.",
    addonPrice: "فرق السعر",
    addonPriceHint: "يُضاف إلى سعر الصنف. يعني الصفر عدم وجود فرق.",
    addonPriceRequired: "اكتب سعرًا لا يقل عن صفر.",
    addonActiveLabel: "الاختيار مفعّل",
    addonIngredient: "المكوّن الذي يُخصم",
    addonIngredientHint:
      "إذا كان هذا الاختيار يخصم من المخزون — مثل جبن إضافي — فاختر المكوّن والكمية معًا. اتركهما فارغين إذا كان فرق سعر فقط.",
    addonPickIngredient: "اختر مكوّنًا",
    addonClearIngredient: "بلا مكوّن",
    addonNoIngredientMatch: "لا يوجد مكوّن بهذا الاسم",
    addonQuantity: "الكمية في المرة الواحدة",
    addonQuantityRequired: "اكتب كمية أكبر من صفر.",
    addonPairingInvalid:
      "أدخل المكوّن والكمية معًا أو اتركهما معًا: فالاختيار إما أن يخصم كمية من المخزون، وإما أن يكون فرق سعر فقط.",
    addonIngredientLocked:
      "هذا الاختيار مرتبط بمكوّن من المخزون. يتطلب تغييره صلاحية المخزون.",
    addonDepletes: "تخصم {quantity} {unit} من المخزون",
    addonNoDepletion: "فرق سعر فقط — لا يُخصم من المخزون",
    addonPaused: "متوقف",
    menuItemAddonsTitle: "الإضافات والأحجام",
    addonOverrideEdit: "تعديل أسعار هذا الصنف",
    addonRecipeTitle: "تكلفة الاختيارات لهذا الصنف",
    addonRecipeSubtitle:
      "يستهلك الحجم الكبير مكوّنات أكثر. إذا لم تسجّلها، فستُحسب الزيادة كاملة ضمن الربح.",
    addonRecipeEdit: "تعديل المكوّنات",
    addonRecipeAddLine: "إضافة مكوّن",
    // بدل مكوّن في الوصفة (D60) — ده إصلاح B15.
    addonReplacesLabel: "بدلًا من",
    addonReplacesNone: "يضيف فقط",
    addonReplacesHint:
      "إذا كان هذا الاختيار يحل محل مكوّن في وصفة الصنف — مثل البيني بدلًا من الفيتوتشيني — فاختر المكوّن الذي سيُزال. من دون ذلك سيُخصم المكوّنان.",
    addonRecipeUsesOwn: "يستخدم مكوّن الإضافة نفسه.",
    addonRecipeUncosted:
      "لا يستهلك مكوّنات إضافية — يُحسب فرق السعر كاملًا ضمن الربح.",
    addonRecipeDuplicate: "المكوّن مكرر.",
    addonRecipeDiscard: "توجد مكوّنات لم تُحفظ. هل تريد تجاهلها؟",
    addonRecipeQuantityInvalid: "اكتب كمية أكبر من صفر.",
    addonRecipePickIngredient: "اختر المكوّن أولًا.",
    addonRecipeNoIngredientsTitle: "سجّل مكوّناتك أولًا",
    addonRecipeNoIngredientsBody:
      "لحساب تكلفة الاختيارات، يجب أن تكون لديك مكوّنات مسجّلة.",
    addonRecipeNoIngredientsAction: "الانتقال إلى المكوّنات",
    addonRecipeNoOptionsTitle: "لا توجد اختيارات مرتبطة",
    addonRecipeNoOptionsBody:
      "اربط مجموعة إضافات بالصنف أولًا، ثم يمكنك تسجيل مكوّنات كل اختيار.",
    addonOverrideWas: "· بدلًا من {price}",
    addonOverrideDiscard: "توجد أسعار لم تُحفظ. هل تريد تجاهلها؟",
    addonOverrideTooManyDecimals: "السعر يقبل رقمين عشريين بحد أقصى.",
    addonOverrideOffersNothing:
      "جميع اختيارات هذه المجموعة معطّلة لهذا الصنف، لذلك لا يرى العملاء أيًا منها.",
    addonOverrideHint:
      "اترك الخانة فارغة ليستخدم الصنف سعر المجموعة. عطّل المفتاح إذا لم يُبع هذا الصنف بهذا الاختيار.",
    addonOverrideOfferedLabel: "{name} متاح لهذا الصنف",
    // D55 — `0` is a real decision ("free on this dish"), not an absent price,
    // so the chip has to SAY free rather than render an empty space a reader
    // cannot tell from "no opinion".
    addonOverrideFree: "مجاني",
    // Read UNDER the option's name, not inside the 104px price box: the box
    // has ~88px of usable run and clipped the trailing digits of this string,
    // cutting the one thing it exists to say.
    addonOverrideInherit: "سعر المجموعة {price}",
    // D55 — what a store-wide price will NOT reach. Phrased as a count AFTER
    // a noun so the number never has to agree: «2 صنف» is not Arabic, and the
    // dual «صنفين» cannot be produced by interpolation.
    addonOverriddenByItems: "عدد الأصناف التي لها سعر مستقل: {count}",
    addonOverrideNegative: "لا يمكن أن يكون السعر سالبًا.",
    addonOverrideNotAPrice: "اكتب السعر بالأرقام.",
    // Same agreement trap as `addonOverriddenByItems`, and it bit hardest at
    // the common `minSelect: 1`: «يتختار منها 1 اختيار» is not a sentence any
    // Egyptian speaker writes, 2 needs the dual «اختيارين» and 3-10 the plural
    // «اختيارات». The count is moved behind a colon so it agrees with nothing.
    addonOverrideUnsatisfiable:
      "هذه المجموعة إلزامية، ويجب أن يبقى فيها {count} من الاختيارات متاحًا.",
    menuItemAddonsSubtitle:
      "المجموعات المرتبطة بهذا الصنف، والتي يراها الكاشير عند تسجيل الطلب.",
    menuItemAddonsEmptyTitle: "لا توجد إضافات لهذا الصنف",
    menuItemAddonsEmptyBody:
      "للصنف سعر واحد فقط. إذا أردته بأحجام مختلفة أو بإضافات لها فرق سعر، فاربط به مجموعة إضافات.",
    menuItemAddonsEmptyAction: "ربط مجموعة",
    menuItemAddonsNoGroupsTitle: "أنشئ مجموعة إضافات أولًا",
    menuItemAddonsNoGroupsBody:
      "لا توجد مجموعات في المطعم حتى الآن. أنشئ مجموعة من صفحة الإضافات — مثل «الحجم» — ثم اربطها بأي صنف.",
    menuItemAddonsNoGroupsAction: "الانتقال إلى الإضافات",
    // Combos / meal deals (S16, D52). Appended here beside the item-detail
    // sections, deliberately far from the `reports.*` block.
    comboTitle: "الوجبة المجمّعة",
    comboSubtitle:
      "الأصناف الموجودة داخل هذه الوجبة — تُباع كطبق واحد بسعر الوجبة.",
    comboScopeLine:
      "السعر المحسوب هو سعر هذا الصنف، لا مجموع أسعار الأصناف داخله. ويُخصم المخزون لكل صنف داخل الوجبة × العدد عند التحضير.",
    // الفرق بين سعر الأصناف لوحدها وسعر الوجبة. الحالة التانية (الوجبة أغلى)
    // بتحصل لما حد يعدّل سعر صنف بعد شهور ومحدش يراجع الوجبات اللي هو جواها.
    comboSaving:
      "الأصناف منفردة {parts} · الوجبة {combo} — يوفر العميل {saving}.",
    comboPricedAbove:
      "الأصناف منفردة {parts}، لكن الوجبة {combo} — أغلى بمقدار {over}.",
    comboEmptyBody:
      "هذا الصنف ليس وجبة مجمّعة حتى الآن. أضف أول صنف أدناه ليصبح وجبة، وأزل الكل ليعود صنفًا عاديًا.",
    comboAddLabel: "أضف صنفًا إلى الوجبة",
    comboAddPlaceholder: "اختر صنفًا",
    comboAddNoMatch: "لا يوجد صنف بهذا الاسم",
    comboNestingHint: "لا يمكن أن تكون الوجبة المجمّعة داخل وجبة أخرى.",
    comboQuantity: "العدد",
    comboRemove: "إزالة",
    /* UX-round D58 — بيقول بيحفظ إيه. «احفظ الوجبة» جنب «حفظ» بتاعة الصنف نفسه كانوا
       زرارين بيقولوا نفس الكلمة على شاشة واحدة لحاجتين مختلفتين. */
    comboSave: "حفظ مكوّنات الوجبة",
    comboComponentOff: "نفد",
    comboUnknownComponent: "صنف محذوف",
    comboBlockedByComponent:
      "يوجد صنف نافد داخل هذه الوجبة، لذلك لا تتوفر الوجبة للبيع الآن. أعد إتاحة الصنف لتتوفر الوجبة.",
    comboNoCandidatesTitle: "تحتاج إلى أصناف أخرى أولًا",
    comboNoCandidatesBody:
      "لا توجد أصناف أخرى مفعّلة لإضافتها إلى الوجبة. أنشئ الأصناف أولًا من صفحة قائمة الطعام ثم عُد إلى هنا.",
    comboNoCandidatesAction: "الانتقال إلى قائمة الطعام",
    attachAddonGroup: "ربط مجموعة",
    attachAddonGroupPick: "اختر مجموعة",
    attachAddonGroupRequired: "اختر مجموعة أولًا.",
    attachAddonGroupNoneLeft: "جميع المجموعات مرتبطة بهذا الصنف بالفعل.",
    attachAddonGroupNoMatch: "لا توجد مجموعة بهذا الاسم",
    detachAddonGroup: "إزالة",
    detachAddonGroupTitle: "هل تريد إزالة المجموعة من الصنف؟",
    detachAddonGroupBody:
      "لن يتمكن الكاشير من اختيار هذه الإضافات مع هذا الصنف مرة أخرى. ستبقى المجموعة موجودة ويمكن ربطها بأصناف أخرى.",

    // Menu sections (categories) and menu-item photos.
    categoriesTitle: "أقسام قائمة الطعام",
    categoriesNoAccessTitle: "هذه الشاشة غير متاحة لك",
    categoriesNoAccessBody:
      "يضبط صاحب المطعم أو المدير أقسام قائمة الطعام. إذا احتجت إلى الوصول إليها، فتواصل مع أحدهما.",
    floorNoAccessTitle: "هذه الشاشة غير متاحة لك",
    floorNoAccessBody:
      "يضبط صاحب المطعم الصالة والطاولات. إذا احتجت إلى تعديلها، فاطلب منه إضافة صلاحية إدارة الصالة إلى دورك.",
    addonsNoAccessTitle: "الإضافات غير متاحة لك",
    addonsNoAccessBody:
      "تحتاج شاشة الإضافات إلى صلاحية عرض قائمة الطعام. إذا كان عملك يتطلب استخدامها، فتواصل مع صاحب المطعم لإضافة الصلاحية من صفحة الفريق.",
    // Bare /manage with no back-office row this session can see. Not a
    // redirect: the old fallback sent them to invoices, which then denied them.
    indexNoAccessTitle: "لا توجد شاشة إدارية متاحة لك",
    indexNoAccessBody:
      "لا يملك حسابك صلاحية الوصول إلى أي شاشة إدارية. إذا كان عملك يتطلب الوصول إلى إحدى هذه الشاشات، فتواصل مع صاحب المطعم لضبط دورك من صفحة الفريق.",
    indexNoAccessToKitchen: "الانتقال إلى شاشة المطبخ",
    indexNoAccessToPos: "الانتقال إلى شاشة الطاولات",
    categoriesEmptyTitle: "لا توجد أقسام حتى الآن",
    categoriesEmptyBody:
      "ينظّم القسم قائمة الطعام أمام الكاشير والعميل بدلًا من عرض قائمة أصناف طويلة. أنشئ قسمًا مثل «مشويات»، ثم أضف إليه الأصناف من صفحة الصنف.",
    categoriesEmptyAction: "إنشاء أول قسم",
    newCategoryTitle: "قسم جديد",
    categoryNameAr: "اسم القسم",
    categoryNameArPlaceholder: "مثال: مشويات",
    categoryNameEn: "الاسم بالإنجليزية (اختياري)",
    categoryNameEnPlaceholder: "Grills",
    categoryNameTooShort: "يجب أن يتكون اسم القسم من حرفين على الأقل.",
    categoryNameEnTooShort:
      "يجب أن يتكون الاسم بالإنجليزية من حرفين على الأقل، أو اتركه فارغًا.",
    categoryEmptyShort: "فارغ",
    // PAPER (2026-10): أعمدة جدول الأقسام، والسطر تحت العدد = كم صنف فيه
    // مفعّل ومش نافد دلوقتي.
    categoryColOrder: "الترتيب",
    categoryColDishes: "الأصناف",
    categoryColItems: "العدد",
    categoryColShown: "ظاهر",
    categoryAvailableNone: "لا يتوفر منها شيء",
    categoryAvailableOne: "متوفر منها صنف واحد",
    categoryAvailableTwo: "متوفر منها صنفان",
    categoryAvailableFew: "متوفر منها {count} أصناف",
    categoryAvailable: "متوفر منها {count} صنفًا",
    categoryItemsLabel: "أصناف القسم",
    categoryItemsHint: "يمكن أن يوجد الصنف في أكثر من قسم.",
    categoryItemsEmpty:
      "لا توجد أصناف في قائمة الطعام حتى الآن. أنشئ صنفًا أولًا من صفحة قائمة الطعام.",
    categoryItemsReadOnly:
      "لا تملك صلاحية تغيير أصناف الأقسام، فالأصناف هنا للعرض فقط.",
    categoryCreateCta: "إنشاء القسم",
    categoryHiddenBadge: "مخفي",
    categoryShowOnMenu: "إظهار القسم في قائمة الطعام",
    categoryHideFromMenu: "إخفاء القسم من قائمة الطعام",
    categoryVisibilityFailed: "تعذّر تغيير ظهور القسم.",
    categoryIconLabel: "الأيقونة",
    // أيقونة القسم من مكتبة الـ Doodles (2026-10-03). الإيموجي بقى اسمه على
    // اللي بيعمله: هو اللي بيظهر جنب اسم القسم في المنيو أونلاين.
    categoryBadgeChange: "تغيير الأيقونة",
    categoryBadgeAll: "كل الأيقونات",
    // عمودا مبيعات اليوم في جدول الأقسام (PAPER).
    categoryColSold: "المباع",
    categoryColRevenue: "الدخل",
    categorySalesTodayHint:
      "اليوم حتى الآن: الأطباق المحاسَب عليها ودخل الطعام، لكل قسم",
    stockSubtitle: "المكوّنات تُخصم بالوصفات مع البيع.",
    statStockValue: "قيمة المخزون",
    /* D139 — مكوّن ما اتشراش لسه ملوش تكلفة، فقيمته مش محسوبة — وبنقول كام واحد. */
    stockValueUnknownOne: "مكوّن واحد بلا تكلفة معروفة غير محسوب فيها",
    stockValueUnknownTwo: "مكوّنان بلا تكلفة معروفة غير محسوبين فيها",
    stockValueUnknownFew: "{count} مكوّنات بلا تكلفة معروفة غير محسوبة فيها",
    stockValueUnknown: "{count} مكوّنًا بلا تكلفة معروفة غير محسوب فيها",
    stockSearch: "ابحث عن مكوّن…",
    stockListEmpty: "لا توجد مكوّنات مطابقة لعوامل التصفية",
    tableLevel: "المستوى",
    tableValue: "القيمة",
    tableStockStatus: "الحالة",
    stockStatusOk: "جيد",
    stockStatusLow: "قارب على النفاد",
    stockStatusOut: "نفد",
    stockNoThreshold: "لا يوجد حد أدنى مسجّل",
    /* UX-round D57 — العمود كان بيرسم نفس البار المليان على كل صف: مفيش مقياس، مفيش
       علامة للحد الأدنى، ولا رقم. العلامة دي هي النص بالظبط، لأن البار
       بيتحسب على (الحد الأدنى × ٢). */
    stockLevelScale: "الحد الأدنى {min}",
    stockLevelAria: "المستوى {pct}% — الحد الأدنى {min}",
    restockTitle: "توريد",
    restockSubtitle: "يزيد على الكمية الحالية.",
    /* PAPER (2026-10) — سطر العدد فوق جدول المخزون، والتصفية في قايمة صغيرة.
       ١ / ٢ / ٣–١٠ / ١١+ على الإجمالي، زي `invoices.resultCount`. */
    stockCountOne: "{shown} من مكوّن واحد",
    stockCountTwo: "{shown} من مكوّنين",
    stockCountFew: "{shown} من {total} مكوّنات",
    stockCount: "{shown} من {total} مكوّن",
    stockFilter: "تصفية",
    stockFilterClear: "إلغاء التصفية",
    /* PAPER (المؤسس، 2026-10-02) — جملة التصميم نفسها تحت عنوان «المخزون» في
       الشريط العلوي، و«جديد» القصيرة على زرار الإضافة. */
    paperStockSubtitle:
      "مستوى كل مكوّن مقارنةً بالحد الأدنى. عدّل الكميات بعد التوريدات.",
    paperStockNew: "جديد",
    // نافذة المكوّن في PAPER: «المخزون / مكوّن جديد» وزرار الحفظ بكلام التصميم.
    paperNewIngredient: "مكوّن جديد",
    paperAddToStock: "إضافة إلى المخزون",
    /* «+» في PAPER بيسأل: توريدة (مشتريات بسعرها) ولا تصحيح عدد (من غير
       مشتريات) — عشان الجرد اللي لقى زيادة ما يتسجّلش توريدة ببلاش. */
    paperAddStock: "زيادة المخزون",
    paperAddDelivery: "وصول توريدة",
    paperAddDeliveryHint: "تُسجَّل مشتريات بسعرها ومورّدها",
    paperAddCorrection: "تصحيح العدد",
    paperAddCorrectionHint: "وجد الجرد كمية أكبر — بدون مشتريات",
    // مش «تعديل» لوحدها: دي كلمة «Edit» في كل الشاشات.
    tableAdjust: "تعديل الكمية",
    restockConfirm: "تأكيد التوريد",
    currentQuantity: "الكمية الحالية",
    quantityReceived: "الكمية الواردة",
    openingStock: "الرصيد الافتتاحي",
    openingStockHint:
      "الكمية الموجودة لديك الآن من هذا المكوّن. اتركها فارغة إن كنت ستسجّل التوريد لاحقًا.",
    openingStockPaid: "المبلغ المدفوع فيها",
    openingStockPaidHint:
      "تُحسب التكلفة من هذا المبلغ. من دونه تُحسب الأطباق بتكلفة صفر.",
    openingStockPaidRequired: "أدخل التكلفة حتى تُحسب التكلفة بدقة.",
    /* D137 — التوريدة المجانية بتنزّل المتوسط (السيرفر صح). الجملة القديمة كانت
       بتقول إن الفاضي بيسيب المتوسط زي ما هو، وده مش اللي بيحصل. */
    amountPaidHint:
      "ما دفعته في هذه التوريدة شاملًا الضريبة. اتركه فارغًا إذا كانت التوريدة مجانية.",
    newAvgCost: "متوسط سعر الوحدة الجديد: {cost}",
    projectedFreeLot:
      "توريدة مجانية: الوحدات الجديدة بلا تكلفة، لذلك ينخفض متوسط التكلفة.",
    projectedFreeSetsZero:
      "توريدة مجانية ولا يوجد رصيد بتكلفة معروفة يُحسب معه المتوسط، لذلك يصبح متوسط التكلفة صفرًا.",
    projectedSetsUnknown:
      "لا توجد تكلفة معروفة لهذا المكوّن بعد، لذلك تحدد هذه التوريدة متوسط التكلفة.",
    projectedSetsEmpty:
      "لا يوجد رصيد متاح يُحسب معه المتوسط، لذلك تحدد هذه التوريدة متوسط التكلفة.",
    /* D136 — تاريخ التوريدة: يوم عمل، النهارده افتراضيًا، لحد ٣١ يوم ورا. */
    purchaseDate: "تاريخ التوريد",
    purchaseDateHint:
      "يوم العمل الذي وصلت فيه التوريدة. يمكنك اختيار يوم سابق حتى {from}.",
    purchaseDateRequired: "اختر تاريخ التوريد.",
    purchaseDateOutOfRange: "اختر يومًا بين {from} و{to}.",
    purchaseDateBackdated:
      "ستُحتسب التوريدة على يوم {date} وتظهر في تقارير ذلك اليوم. ولا يُعاد تسعير ما استُهلك قبل تسجيلها.",
    quantityRequired: "اكتب الكمية الواردة.",
    categoryMoveUp: "تحريك القسم إلى أعلى",
    categoryMoveDown: "تحريك القسم إلى أسفل",
    categoryReorderFailed: "تعذّر حفظ الترتيب الجديد، فأُعيد الترتيب السابق.",
    categoryModalSubtitle:
      "يجمع القسم أصناف قائمة الطعام تحت اسم واحد. ويمكنك ضبط ترتيب الأقسام من قائمة الأقسام.",
    categoryEntityLabel: "قسم",
    deleteCategoryTitle: "هل تريد حذف هذا القسم؟",
    deleteCategoryBody:
      "سيُزال القسم من قائمة الطعام. لا توجد أصناف مرتبطة به الآن.",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    deleteCategoryBodyWithItemsOne:
      "لن يُحذف الصنف الموجود في هذا القسم — سيبقى في قائمة الطعام من دون قسم فقط.",
    deleteCategoryBodyWithItemsTwo:
      "لن يُحذف الصنفان الموجودان في هذا القسم — سيبقيان في قائمة الطعام من دون قسم فقط.",
    deleteCategoryBodyWithItemsFew:
      "لن تُحذف الأصناف الموجودة في هذا القسم وعددها {count} — ستبقى في قائمة الطعام من دون قسم فقط.",
    deleteCategoryBodyWithItems:
      "لن تُحذف الأصناف الموجودة في هذا القسم وعددها {count} — ستبقى في قائمة الطعام من دون قسم فقط.",
    menuItemCategories: "الأقسام",
    menuItemCategoriesHint: "يمكن أن يوجد الصنف في أكثر من قسم.",
    menuItemCategoriesNone:
      "لا توجد أقسام في المطعم حتى الآن. أنشئها أولًا من صفحة الأقسام.",
    menuItemCategoriesNoMatch: "لا يوجد قسم بهذا الاسم",
    menuItemImagesTitle: "الصور",
    menuItemImagesSubtitle: "الصور التي يراها العميل عند تصفح قائمة الطعام.",
    menuItemImagesEmptyTitle: "لا توجد صور لهذا الصنف",
    menuItemImagesEmptyBody:
      "تساعد صورة واضحة العميل على معرفة الصنف دون الحاجة إلى السؤال. ارفع صورة من الهاتف أو الكمبيوتر.",
    menuItemImagesEmptyAction: "رفع صورة",
    menuItemPhotoDrop: "الصورة — اسحبها هنا أو اضغط (اختياري)",
    addImage: "رفع صورة",
    imageAlt: "صورة الصنف رقم {index}",
    imagePrimary: "الصورة الأساسية",
    imageOrder: "صورة رقم {index}",
    imageWrongType:
      "هذا الملف ليس صورة مدعومة. الصيغ المسموحة: JPG أو PNG أو WEBP.",
    imageTooLarge: "الصورة كبيرة جدًا. الحد الأقصى 5 ميجابايت.",
    imageUploadFailed: "تعذّر رفع الصورة. حاول مرة أخرى.",
    imageEntityLabel: "صورة",
    removeImageTitle: "هل تريد إزالة هذه الصورة؟",
    removeImageBody:
      "ستُزال الصورة من الصنف. ستبقى بقية الصور كما هي وبالترتيب نفسه.",
    // ---- مركز الإعدادات (الصفحة الرئيسية + أقسامها) ----
    settingsHubDescription: "كل إعدادات المطعم مجمّعة في مكان واحد.",
    // PAPER (تصميم 2026-10) — قايمة الأقسام جنب القسم المفتوح: سطر قصير تحت
    // كل اسم، وشريط الحفظ اللي بيظهر لما يبقى فيه تعديل. السطور القصيرة هي
    // سطور التصميم نفسها (تكافؤ البكسل، 2026-10-02).
    settingsNavGeneral: "يوم العمل والمنطقة الزمنية والشعار",
    settingsNavOperations: "المطبخ والطباعة والورديات",
    settingsNavTax: "رسم الخدمة والضريبة ووضعك الضريبي",
    settingsNavTaxStatus: "التسجيل ورسم الخدمة ورسم التوصيل",
    settingsNavRefunds: "متى يُعدّ المرتجع متأخرًا",
    settingsNavDrawer: "الخزينة والخصم واعتماد المدير والمرتجعات",
    settingsNavTiers: "أسعار الطاقم والشركاء",
    settingsNavStorefront: "تصميم قائمة طعامك على الإنترنت",
    settingsNavTableOrdering: "الطلب من الطاولة عبر QR",
    settingsNavPayment: "طريقة دفع الضيوف بالتحويل",
    settingsNavStations: "أين يُجهَّز كل صنف",
    settingsNavAppearance: "يغيّر هذه الشاشة فقط",
    settingsUnsaved: "تعديلات غير محفوظة في «{name}»",
    settingsSectionSaved: "تم حفظ «{name}»",
    // PAPER — عنوان الصفحة وسطرها في الشريط العلوي، واسم كل قسم بكلمة
    // التصميم (في القايمة وفي رأس الكارت وشريط الحفظ).
    settingsPaperTitle: "الإعدادات",
    settingsPaperSubtitle: "طريقة عمل المطعم. يُحفظ كل قسم على حدة.",
    settingsPaperGeneral: "المطعم",
    settingsPaperOperations: "التشغيل",
    settingsPaperTax: "الضرائب والرسوم",
    settingsPaperTaxStatus: "الوضع الضريبي",
    settingsPaperRefunds: "المرتجعات",
    settingsPaperDrawer: "الخزينة والموافقات",
    settingsPaperTiers: "شرائح الأسعار",
    settingsPaperStorefront: "صفحة المطعم",
    settingsPaperTableOrdering: "الطلب من الطاولة",
    settingsPaperPayment: "طرق الدفع",
    settingsPaperStations: "محطات المطبخ",
    settingsPaperAppearance: "هذا الجهاز",
    // تنظيم 2026-10-29 — كل قسم موضوع واحد، تحت أربعة عناوين.
    settingsPaperKitchen: "المطبخ",
    settingsPaperFloor: "الصالة والطلب من الطاولة",
    settingsPaperPrinting: "الطباعة",
    settingsPaperDelivery: "التوصيل والتيك أواي",
    settingsPaperOnline: "الطلب عبر الإنترنت",
    settingsNavKitchen: "طريقة العمل والأوقات والمحطات",
    settingsNavFloor: "الإرسال للمطبخ والـ QR وأوقات الصالة",
    settingsNavPrinting: "الإيصال وتذكرة المطبخ والطابعات",
    settingsNavDelivery: "رسم التوصيل والطيارون والمنصّات",
    settingsNavOnline: "الطلب من صفحة المطعم وشكلها",
    settingsKitchenRowBody:
      "طريقة عمل المطبخ، والوقت المستهدف لكل مرحلة فيه، ومحطات المطبخ.",
    settingsFloorRowBody:
      "هل يرسل الويتر عبر الكاشير، والطلب من الطاولة عبر QR، وأوقات الصالة والدفع، ولون تطبيق الطاولة.",
    settingsPrintingRowBody:
      "نص الإيصال ولغته، وتذكرة المطبخ، والطابعات وجهاز الطباعة.",
    settingsDeliveryRowBody:
      "رسم التوصيل الافتراضي، ومدة قبول الطيار للطلب، والطيارون ومنصّات التوصيل.",
    settingsOnlineRowBody:
      "هل يطلب العملاء من صفحة المطعم، ومهلة الرد، والتأكيد عبر واتساب، وشكل الصفحة.",
    settingsGroupRestaurant: "المطعم",
    settingsGroupMoney: "المالية",
    settingsGroupService: "الخدمة",
    settingsGroupOffPremise: "طلبات خارج المطعم",
    settingsGroupElsewhere: "يُضبط في مكان آخر",
    settingsElsewhereTeam: "الفريق والأدوار",
    settingsElsewhereTeamHint: "الأعضاء والدعوات والصلاحيات",
    settingsElsewhereDrivers: "الطيارون",
    settingsElsewhereDriversHint: "قائمة الطيارين ومستنداتهم ودعوتهم",
    settingsElsewherePlatforms: "منصّات التوصيل",
    settingsElsewherePlatformsHint: "كل منصّة وعمولتها",
    settingsElsewhereFloor: "تخطيط الصالة ورموز QR",
    settingsElsewhereFloorHint: "المناطق والطاولات وملصقات QR",
    settingsOpen: "فتح",
    taxGroupServiceCharge: "رسم الخدمة",
    taxGroupVat: "ضريبة القيمة المضافة",
    cashGroupApprovals: "الموافقات والحدود",
    floorGroupOrders: "الإرسال إلى المطبخ",
    floorGroupTableOrdering: "الطلب من الطاولة",
    printingTicketGroup: "تذكرة المطبخ",
    printingTicketOff:
      "طريقة عمل المطبخ الحالية لا تطبع تذكرة، فلا يوجد ما يُجمَّع. تُختار الطريقة من",
    printingPrintersCard: "الطابعات وجهاز الطباعة",
    printingPrintersCardHint:
      "الطابعات، وطابعة كل محطة، وطابعة الفاتورة. يُحفظ كل منها وحده.",
    deliveryChargesSummary: "ما يُضاف على طلبات الاستلام والتوصيل",
    deliveryChargesValues:
      "رسم الخدمة على الاستلام: {takeaway} · على التوصيل: {delivery} · ضريبة على رسم التوصيل: {feeVat}",
    deliveryChargeOn: "مفعّل",
    deliveryChargeOff: "متوقف",
    // PAPER — الحقول اللي التصميم بيكتب وحدتها جوه الخانة («08 :00»، «8 %»):
    // اسم الحقل من غير الوحدة، والوحدة نفسها.
    settingsPaperDayStart: "بداية يوم العمل",
    settingsPaperServiceRate: "نسبة رسم الخدمة",
    settingsPaperVatRate: "نسبة الضريبة",
    settingsPaperRefundLate: "المرتجع يُعدّ متأخرًا بعد",
    settingsPaperBand: "الخزينة تُعدّ مضبوطة في حدود",
    settingsPaperRadius: "المسافة",
    settingsPaperSessionLength: "مدة الجلسة",
    settingsPaperUnitMinutes: "دقيقة",
    settingsPaperUnitMetres: "م",
    settingsPaperDiscard: "تجاهل",
    settingsPaperServiceTakeaway: "رسم الخدمة على طلبات الاستلام",
    settingsPaperServiceDelivery: "رسم الخدمة على التوصيل",
    // PAPER — اختيار شكل لوحة التحكم كصف واحد زي التصميم: «الشكل على هذا
    // الجهاز»، سطره، والشكل اللي شبه صفحة المطعم.
    appearancePaperLabel: "الشكل على هذا الجهاز",
    appearancePaperHint: "اختر مظهر هذه الشاشة فقط. لن يرى التغيير غيرك.",
    appearancePaperTwin: "«{name}» شبه صفحة مطعمك.",
    // D162 (F38) — حجم النص في شاشة البيع على هذا الجهاز: ثلاث درجات، والعادي هو
    // شاشة البيع كما هي. لوحة التحكم وشاشة المطبخ لا تتغيّران.
    appearanceTillTextLabel: "حجم النص في شاشة البيع",
    appearanceTillTextHint:
      "يُطبَّق على شاشة البيع في هذا الجهاز فقط، ولا يتغيّر حجم لوحة التحكم.",
    appearanceTillText_compact: "صغير",
    appearanceTillText_normal: "عادي",
    appearanceTillText_large: "كبير",
    appearanceTillTextChanged: "تغيّر حجم النص في شاشة البيع.",
    /* UX-round D53 — الصفحة العامة القديمة كانت ستّ سياسات مختلفة ورا زرار حفظ واحد.
       بقت ستّ صفوف هنا، وكل واحد بيحفظ نفسه. والوصف ده كان بيكدب: كان بيقول
       «يوم العمل وشكل الفاتورة» وهو شايل كل قرارات الضرايب كمان. */
    settingsGeneralRow: "المطعم",
    settingsGeneralRowBody:
      "بداية يوم العمل، والمنطقة الزمنية، وشعار المطعم.",
    settingsOperationsRow: "المطبخ والخزائن",
    settingsOperationsRowBody:
      "شاشة المطبخ، وتذكرة المطبخ، وما إذا كان المطعم يتابع الخزينة.",
    settingsTaxRowBody:
      "رسم الخدمة وضريبة القيمة المضافة، وما يُضاف على طلبات الاستلام والتوصيل، ووضع المطعم الضريبي.",
    settingsTaxStatusRowBody:
      "حالة التسجيل الضريبي للمطعم، ومستحق رسم الخدمة، وأنواع الطلبات التي يُحسب عليها.",
    settingsRefundsRowBody:
      "المدة التي يُعد بعدها المرتجع «متأخرًا» ويظهر في تقرير الاستثناءات.",
    settingsDrawerRowBody:
      "متابعة الخزينة وفرق العدّ المسموح، وأقصى خصم، واعتماد المدير، ومتى يُعدّ المرتجع متأخرًا، والجرد دون عرض الأرصدة.",
    settingsTiersRowBody:
      "نِسَب خصم جاهزة للاختيار عند التحصيل، من دون حسابها كل مرة.",
    settingsStorefrontRow: "شكل صفحة المطعم",
    settingsStorefrontRowBody:
      "اختر تصميمًا جاهزًا للصفحة التي يراها العملاء، وتحكّم في ترتيب أقسامها.",
    settingsTableOrderingRow: "طلب الطاولة والـ QR",
    settingsTableOrderingRowBody:
      "ثبّت موقع المطعم والمسافة المسموحة، ثم اطبع رمز QR دائمًا لكل طاولة.",
    tableOrderingTitle: "طلب الطاولة والموقع",
    tableOrderingHowItWorksTitle: "رمز QR دائم",
    tableOrderingHowItWorksBody:
      "يبقى الملصق على الطاولة. بعد المسح، يسمح الضيف بالوصول إلى موقعه ويحصل على جلسة مدتها ساعة، ثم يُتحقق من الموقع مرة أخرى عند إرسال الطلب.",
    tableOrderingUseCurrentLocation: "استخدام موقع هذا الجهاز",
    tableOrderingAccuracy:
      "دقة القراءة الحالية: نحو {meters} متر. الدقة للعرض فقط ولا تُحفظ.",
    tableOrderingLocationDenied:
      "المتصفح منع الوصول للموقع. اسمح بالموقع من إعدادات المتصفح أو اكتب الإحداثيات يدويًا.",
    tableOrderingLocationTimeout:
      "تأخر تحديد الموقع ولم يكتمل في الوقت المحدد. اقترب من نافذة أو حاول مرة أخرى.",
    tableOrderingLocationUnavailable:
      "الموقع غير متاح على هذا الجهاز. اكتب الإحداثيات يدويًا.",
    tableOrderingLatitude: "خط العرض",
    tableOrderingLongitude: "خط الطول",
    tableOrderingCoordinatesInvalid:
      "اكتب خط عرض من -90 إلى 90 وخط طول من -180 إلى 180، أو اترك الحقلين فارغين.",
    tableOrderingRadiusHint: "من 25 إلى 1000 متر حول الموقع المحدد.",
    tableOrderingRadiusInvalid:
      "يجب أن تكون المسافة عددًا صحيحًا من {min} إلى {max} متر.",
    tableOrderingSessionHint:
      "المدة التي يبقى فيها الطلب من الطاولة مفتوحًا للضيف بعد مسح الرمز، من 5 إلى 120 دقيقة.",
    tableOrderingSessionInvalid:
      "يجب أن تكون المدة عددًا صحيحًا من {min} إلى {max} دقيقة.",
    // B125/D115 — أوقات الخدمة: متى يُعدّ الطبق أو الطاولة متأخرًا.
    settingsPaperServiceTiming: "أوقات الخدمة",
    settingsNavServiceTiming: "متى يُعدّ الطبق متأخرًا",
    settingsServiceTimingRow: "أوقات الخدمة",
    settingsServiceTimingRowBody:
      "الوقت المستهدف لكل مرحلة، في شاشة المطبخ وتقرير سرعة الخدمة.",
    serviceTimingTitle: "أوقات الخدمة",
    serviceTimingKitchenGroup: "المطبخ",
    serviceTimingFloorGroup: "الصالة والدفع",
    serviceTimingQueue: "انتظار بدء التحضير",
    serviceTimingQueueHint: "من إرسال الطبق إلى المطبخ حتى يبدأ تحضيره.",
    serviceTimingPrep: "التحضير",
    serviceTimingPrepHint:
      "الوقت المعتاد لكل صنف. يمكن تحديد وقت مختلف لأي صنف من صفحته في قائمة الطعام.",
    serviceTimingPass: "من الجاهزية إلى الطاولة",
    serviceTimingPassHint: "من تجهيز الطبق حتى تقديمه.",
    serviceTimingFirstOrder: "من الجلوس إلى أول طلب",
    serviceTimingFirstOrderHint:
      "لفواتير الصالة: من فتح الفاتورة حتى أول إرسال للمطبخ.",
    serviceTimingQrAccept: "قبول طلبات الضيوف",
    serviceTimingQrAcceptHint: "من إرسال الضيف طلبه حتى يقبله أحد العاملين.",
    serviceTimingPayment: "من الحساب إلى الدفع",
    serviceTimingPaymentHint: "من آخر طباعة للحساب حتى التحصيل.",
    serviceTimingProofReview: "مراجعة إثبات الدفع",
    serviceTimingProofReviewHint: "من إرسال الضيف إثبات الدفع حتى يُراجَع.",
    // T2 / D173 — the pass target must stay under the READY age-off.
    serviceTimingPassOverAgeOff:
      "يجب أن يكون هدف التسليم أقل من مدة إخفاء الأطباق الجاهزة ({minutes} د). غيّر المدة من «طريقة عمل المطبخ» أولًا.",
    // The server refused with no figure this page can trust: no number.
    serviceTimingPassOverAgeOffUnknown:
      "يجب أن يكون هدف التسليم أقل من مدة إخفاء الأطباق الجاهزة. غيّر المدة من «طريقة عمل المطبخ» أولًا.",
    serviceTimingRange:
      "يجب أن يكون الوقت عددًا صحيحًا من {min} إلى {max} دقيقة.",
    serviceTimingFrozenNote:
      "كل فاتورة تحتفظ بالأوقات التي فُتحت بها، فلا يغيّر التعديل هنا حكم الفواتير السابقة.",
    tableOrderingEnableTitle: "تشغيل التحقق بالموقع",
    tableOrderingGuestTitle: "ما يراه الضيف على الطاولة",
    tableOrderingAccountsTitle: "حساب تريوتي على الطاولة",
    tableOrderingAccountsLabel: "اعرض «حسابي» للضيف واحفظ الزيارة في حسابه",
    tableOrderingAccountsHint: "اختياري للضيف ولا يُطلب للطلب. كل من يدخل حسابه على الطاولة يجد الزيارة في طلباته السابقة، ولا يُضاف إلى عملائك.",
    tableOrderingBillHiddenTitle: "إخفاء إجمالي الحساب أثناء الطلب",
    tableOrderingBillHiddenLabel: "لا تعرض إجمالي الحساب حتى تطلب الطاولة الحساب",
    tableOrderingBillHiddenHint: "يرى الضيف الأطباق التي طلبها وسعر كل طبق كالمعتاد، لكن الإجمالي وطرق الدفع تظهر فقط بعد طلب الحساب أو تقديمه.",
    tableOrderingEnableLabel: "السماح بطلبات الطاولة بعد التحقق من الموقع",
    tableOrderingEnableNeedsLocation: "حدد خط العرض وخط الطول قبل التشغيل.",
    tableOrderingPermissionNote:
      "إذا رفض الضيف إذن الموقع أو كان بعيدًا، فيمكنه تصفح قائمة الطعام فقط، ولن يتمكن من فتح حساب الطاولة أو إرسال طلب.",
    /* D130 — شكل قائمة الطعام التي يفتحها الضيف بعد مسح كود الطاولة. */
    tableMenuStyleTitle: "شكل القائمة على الطاولة",
    tableMenuStyleHint: "ما يراه الضيف أولًا بعد مسح الرمز. الأسعار والطلب والحساب واحدة في الأشكال الثلاثة.",
    tableMenuStyleCalmList: "قائمة هادئة",
    tableMenuStyleCalmListHint: "أقسام بعناوين ثابتة في الأعلى، وصفوف بصورة صغيرة لكل طبق.",
    tableMenuStylePhotoGallery: "معرض صور",
    tableMenuStylePhotoGalleryHint: "اختيار الشيف في الأعلى، وفلاتر، وشبكة صور من عمودين.",
    tableMenuStyleCategoryTiles: "مربعات الأقسام",
    tableMenuStyleCategoryTilesHint: "مربعات كبيرة للأقسام، ثم قسم واحد في كل مرة ببطاقات كبيرة.",
    tableMenuStyleGalleryHint: "ما يراه الضيف بعد مسح رمز الطاولة. يُطبَّق فورًا، بدون نشر.",
    tableMenuStyleApplied_toast: "قائمة الطاولة أصبحت «{name}».",
    tableAppAccentTitle: "لون تطبيق الطاولة",
    tableAppAccentHint: "لون الأزرار والاختيارات في تطبيق الطاولة. يُطبَّق فورًا.",
    tableAppAccentDefault: "الافتراضي",
    tableAppAccentGreen: "أخضر",
    tableAppAccentRed: "أحمر",
    tableAppAccentOrange: "برتقالي",
    tableAppAccentPurple: "بنفسجي",
    tableAppAccentPink: "وردي",
    tableAppAccentBrown: "بني",
    tableAppAccentInk: "داكن",
    tableAppAccentCustom: "لون آخر",
    tableAppAccentCode: "رمز اللون",
    tableAppAccentInvalid: "اكتب لونًا مثل #2383E2.",
    tableAppAccentApplied_toast: "تم تغيير لون تطبيق الطاولة.",
    tablesTitle: "الصالة",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    tablesSeatsOne: "كرسي واحد",
    tablesSeatsTwo: "كرسيين",
    tablesSeatsFew: "{count} كراسي",
    tablesSeats: "{count} كرسي",
    settingsAppearanceRow: "شكل لوحة التحكم",
    settingsAppearanceRowBody:
      "اختر الشكل الذي تستخدمه أنت وفريقك هنا. لن تتأثر صفحة العملاء.",
    appearanceChanged: "تغيّر شكل لوحة التحكم.",
    appearanceTheme_trioty: "تريوتي",
    appearanceTheme_homegrill: "هوم جريل",
    appearanceTheme_stack: "ستاك",
    appearanceTheme_verde: "فيردي",
    appearanceTheme_paper: "بيبر",
    storefrontTitle: "شكل صفحة المطعم",
    storefrontThemesTitle: "التصميمات",
    storefrontThemesBody:
      "اختر تصميمًا ليُطبّق على المسودة أولًا، ثم انشره عندما يصبح جاهزًا.",
    storefrontApplied: "مُطبَّق",
    storefrontApply: "استخدام هذا",
    storefrontApplying: "جارٍ التطبيق...",
    storefrontApplied_toast: "طُبّق التصميم على المسودة. راجعه ثم انشره.",
    storefrontTabTheme: "التصميم",
    storefrontTabContent: "المحتوى",
    storefrontTabSections: "الأقسام",
    storefrontEditorSubtitle: "محرر المطعم",
    storefrontSaved: "محفوظ",
    storefrontSaving: "جارٍ الحفظ…",
    storefrontGroupTheme: "التصميمات",
    storefrontGroupColors: "الألوان",
    storefrontGroupSections: "أقسام الصفحة",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    storefrontSectionsVisibleOne: "قسم واحد ظاهر من {total}",
    storefrontSectionsVisibleTwo: "قسمان ظاهران من {total}",
    storefrontSectionsVisibleFew: "{visible} أقسام ظاهرة من {total}",
    storefrontSectionsVisible: "{visible} قسم ظاهر من {total}",
    storefrontMenuNote:
      "تأتي الأصناف والأسعار من صفحة قائمة الطعام — وهنا تتحكم فيما يراه العميل.",
    storefrontPublishedToast: "نُشر على {url}",
    storefrontReset: "استعادة التصميم الأصلي",
    storefrontResetTitle: "هل تريد استعادة التصميم الأصلي؟",
    storefrontResetBody:
      "سيعيد الألوان والنصوص والأقسام إلى حالة التصميم الأصلية — وستُحذف تعديلات المسودة. لن تتغير الصفحة المنشورة حتى تنشر.",
    storefrontResetConfirm: "استعادة",
    storefrontResetDone: "أُعيد التصميم إلى حالته الأصلية.",
    storefrontShowSection: "إظهار هذا القسم",
    storefrontUndo: "تراجع",
    storefrontNoCopy: "لا يحتوي هذا القسم على نص قابل للتعديل.",
    storefrontEditor: "تعديل الصفحة",
    storefrontEditorTitle: "محرر الصفحة",
    storefrontPreviewTitle: "معاينة صفحة المطعم",
    storefrontPreviewLoading: "جارٍ تجهيز المعاينة…",
    storefrontPreviewUnavailable: "تعذّر تجهيز المعاينة الآن.",
    storefrontPreviewDesktop: "كمبيوتر",
    storefrontPreviewPhone: "هاتف",
    storefrontEditorBack: "العودة إلى الإعدادات",
    storefrontEditorDraft: "لديك تعديلات غير منشورة",
    storefrontEditorLive: "الصفحة منشورة كما هي",
    storefrontColorsTitle: "الألوان",
    storefrontColorsBody:
      "غيّر ألوان التصميم من دون تغيير شكله. يُحفظ في المسودة مثل أي تعديل آخر.",
    /* UX-round D60 — «أنا مش عارف الأكواد بتاعتي. أنا عارف إن اللافتة بتاعتي أخضر
       غامق.» فالاختيار بقى بالأسامي، والكود اتحط ورا «اكتب الكود بالظبط». */
    storefrontColorExact: "اكتب الرمز بالضبط",
    swatchNearBlack: "أسود",
    swatchGrey: "رمادي",
    swatchWhite: "أبيض",
    swatchCream: "بيج",
    swatchDarkGreen: "أخضر داكن",
    swatchGreen: "أخضر",
    swatchRed: "أحمر",
    swatchOrange: "برتقالي",
    swatchYellow: "أصفر",
    swatchBlue: "أزرق",
    swatchPurple: "بنفسجي",
    swatchBrown: "بني",
    storefrontColorPrimary: "اللون الأساسي",
    storefrontColorPrimaryHint: "الأزرار والأسعار والعناصر القابلة للضغط.",
    storefrontColorAccent: "اللون المساعد",
    storefrontColorAccentHint: "الشارات والتفاصيل الصغيرة.",
    storefrontColorSurface: "لون الخلفية",
    storefrontColorSurfaceHint: "خلفية الصفحة والبطاقات.",
    storefrontColorInk: "لون النص",
    storefrontColorInkHint: "العناوين والنص.",
    storefrontColorsSave: "حفظ الألوان",
    storefrontColorsSaving: "جارٍ الحفظ…",
    storefrontColorsSaved: "حُفظت الألوان في المسودة.",
    storefrontColorsReset: "استعادة الألوان السابقة",
    storefrontColorsInvalid: "يجب أن يكون اللون رمزًا من ست خانات.",
    storefrontColorsContrast:
      "يصعب قراءة النص على هذا اللون. جرّب لونًا أغمق أو أفتح.",
    storefrontSectionsTitle: "أقسام الصفحة",
    storefrontSectionsBody:
      "أخفِ أي قسم أو غيّر ترتيبه ونصه. يأتي التصميم جاهزًا.",
    storefrontEditCopy: "تغيير النص",
    storefrontCloseCopy: "إغلاق",
    storefrontCopyHint:
      "النص التالي مرفق مع التصميم كنموذج — غيّره ليناسب مطعمك.",
    storefrontCopySave: "حفظ النص",
    storefrontCopySaving: "جارٍ الحفظ…",
    storefrontCopySaved: "حُفظ النص",
    storefrontCopyArabic: "عربي",
    storefrontCopyEnglish: "إنجليزي",
    storefrontCopyTooLong: "أطول من اللازم",
    storefrontShow: "ظاهر",
    storefrontHide: "مخفي",
    storefrontMoveUp: "إلى أعلى",
    storefrontMoveDown: "إلى أسفل",
    storefrontLocked: "ثابت",
    storefrontPreviewOnly: "غير مفعّل حتى الآن",
    storefrontPreviewOnlyBody: "يظهر في المعاينة فقط — لم تُنفّذ الميزة بعد.",
    storefrontDraftPending: "لديك تغييرات غير منشورة.",
    storefrontPublish: "نشر",
    storefrontPublishing: "جارٍ النشر...",
    storefrontPublished: "نُشرت الصفحة وحُدّثت.",
    storefrontDiscard: "العودة إلى المنشور",
    storefrontDiscarded: "أُعيدت آخر نسخة منشورة.",
    storefrontOpen: "فتح الصفحة",
    storefrontNoThemes: "لا توجد تصميمات متاحة الآن.",
    storefrontLiveBadge: "منشور",
    storefrontDraftBadge: "تغييرات غير منشورة",
    storefrontCurrentLive: "هذا ما يراه عملاؤك الآن.",
    storefrontCurrentDraft: "مطبّق على المسودة — انشره ليظهر للعملاء.",
    storefrontCustomize: "تخصيص",
    storefrontSwitchHint:
      "يحافظ تغيير التصميم على قائمة الطعام والأسعار والصور — ويتغير الشكل فقط. خصصه من المحرر.",
    // راوند 5 — «عرض الأسعار على القائمة أونلاين». إعداد للمطعم مش جزء من مسودة
    // التصميم، فبيتطبّق على الصفحة المنشورة على طول — والوصف لازم يقول كده.
    storefrontPricesLabel: "عرض الأسعار في قائمة الطعام على الإنترنت",
    storefrontPricesHint:
      "عند إيقافه، يرى من يتصفح قائمتك الأطباق بلا أسعار. أما الضيف الذي يطلب من رمز الطاولة فيرى دائمًا سعر كل طبق وإجمالي فاتورته. يُطبَّق فورًا على صفحتك المنشورة، ولا ينتظر نشر التصميم.",
    storefrontPricesShown_toast: "الأسعار ظاهرة الآن في قائمتك على الإنترنت",
    storefrontPricesHidden_toast: "الأسعار مخفية الآن من قائمتك على الإنترنت",
    storefrontNothingToPublish: "لا يوجد ما يمكن نشره.",
    /* UX-round D60 — الحالة التالتة اللي الكارت ده ماكانش عنده: محل لسه ماختارش تصميم.
       كان بيتكتب عليه «منشور» جنب «مفيش تصميمات متاحة دلوقتي». */
    storefrontUnsetTitle: "لم تختر تصميمًا حتى الآن",
    storefrontUnsetBadge: "غير مضبوطة",
    storefrontUnsetBody:
      "اختر تصميمًا أدناه، ثم انشره ليصبح التصميم الذي يراه العملاء.",
    storefrontPickFirst: "اختر تصميمًا أولًا من التصميمات أدناه.",
    storefrontOpenLive: "فتح الصفحة كما يراها العميل",
    settingsNoAccessTitle: "لا توجد إعدادات متاحة لحسابك",
    settingsNoAccessBody:
      "تحتاج هذه الأقسام إلى صلاحية الإعدادات أو صلاحية شرائح الأسعار، ولا يملك حسابك أيًا منهما. اطلب من صاحب المطعم إضافة الصلاحية من صفحة الفريق.",
    backToSettings: "العودة إلى الإعدادات",
    restaurantLogoTitle: "شعار المطعم",
    restaurantLogoHint:
      "يظهر في منتصف رمز QR على ملصق الطاولة. صورة مربعة، JPG أو PNG أو WEBP.",
    restaurantLogoEmpty: "لا يوجد شعار بعد",
    restaurantLogoChoose: "اختيار الشعار",
    restaurantLogoReplace: "تغيير الشعار",
    restaurantLogoRemove: "حذف الشعار",
    restaurantLogoUpload: "رفع الشعار",
    restaurantLogoUpdate: "تحديث الشعار",
    restaurantLogoSaved: "حُفظ الشعار",
    restaurantLogoRemoved: "أُزيل الشعار",
    restaurantLogoUnreadable: "تعذّر قراءة الصورة. جرّب ملفًا آخر.",
    generalNoAccessTitle: "لا تملك صلاحية تعديل الإعدادات",
    generalNoAccessBody:
      "تعديل إعدادات المطعم يحتاج إلى صلاحية الإعدادات. اطلبها من صاحب المطعم من صفحة الفريق.",
    tiersNoAccessTitle: "لا تملك صلاحية شرائح الأسعار",
    tiersNoAccessBody:
      "شرائح الأسعار تحتاج إلى صلاحية الشرائح. اطلبها من صاحب المطعم من صفحة الفريق.",
    tiersEmptyTitle: "لا توجد شرائح أسعار حتى الآن",
    tiersEmptyBody:
      "الشريحة نسبة خصم تحفظها مرة وتستخدمها عند الحاجة — مثل خصم الموظفين أو أسعار الشركات. أضف أول شريحة وحدّد نسبتها.",
    tiersEmptyAction: "إضافة أول شريحة",
    addTier: "إضافة شريحة",
    newTierTitle: "شريحة سعر جديدة",
    editTierTitle: "تعديل الشريحة",
    tierNameRequired: "اكتب اسمًا للشريحة.",
    tierPercentRange: "يجب أن تكون النسبة رقمًا بين 0 و100.",
    tierInactive: "متوقفة",
    // PAPER (تكافؤ البكسل) — صف الشريحة زي التصميم: الاسم والخصم في سطر واحد.
    tierPaperName: "{name} · خصم {percent}%",
    // D47 (B4): مواعيد الشريحة — أيام الأسبوع وساعة البداية والنهاية.
    weekdayMon: "الاثنين",
    weekdayTue: "الثلاثاء",
    weekdayWed: "الأربعاء",
    weekdayThu: "الخميس",
    weekdayFri: "الجمعة",
    weekdaySat: "السبت",
    weekdaySun: "الأحد",
    tierWindowEveryDay: "كل يوم",
    tierWindowNoDay: "لم يُحدّد يوم",
    tierWindowOvernight: "تمتد بعد منتصف الليل",
    /* UX-round D54 — الصف بقى بيقول الميعاد اللي طالع منه فعلاً، حتى لو الشريحة
       شغّالة طول الوقت. ده بالظبط الصف اللي كان بيخبّي خصم ٢٠% طول اليوم. */
    tierWindowAllDay: "طوال اليوم",
    tierScheduleAutomatic: "تُطبّق بمفردها",
    tierScheduleManual: "يختارها الكاشير",
    tierWindowSectionTitle: "مواعيد الشريحة",
    tierWindowDaysLabel: "الأيام",
    tierWindowDaysHint:
      "اترك جميع الأيام محددة إذا كانت الشريحة مفعّلة طوال الأسبوع.",
    tierWindowFromLabel: "من الساعة",
    tierWindowToLabel: "حتى الساعة",
    /* UX-round D54 — كان ٢٦٧ حرف بيشرح قواعد نص الليل، وهي أطول جملة في الإعدادات.
       النتيجة نفسها بقت مكتوبة فوق في سطر «الميعاد اللي هيطلع»، فالجملة دي
       رجعت تقول الحاجة الوحيدة اللي السطر ده مش بيقولها: إن الأيام هي ليالي
       البداية. */
    tierWindowHint:
      "بتوقيت المطعم. اترك الوقتين فارغين إذا كانت الشريحة مفعّلة طوال اليوم. الأيام المحددة هي ليالي البداية؛ فشريحة الجمعة 22:00–02:00 تعمل من ليلة الجمعة حتى صباح السبت.",
    // B11 — الميعاد بيتشيّك وقت فتح الفاتورة بس، مش على كل صنف يتضاف بعدها.
    tierWindowLockInHint:
      "يُتحقق من الموعد عند فتح الفاتورة — وتحتفظ الفاتورة التي فُتحت ضمن الموعد بسعرها حتى تُغلق، ولو استمرت بعد نهاية الموعد.",
    tierWindowPairRequired:
      "أدخل وقتَي البداية والنهاية معًا، أو اتركهما فارغين.",
    tierWindowTimeInvalid: "اكتب الوقت بصيغة 24 ساعة — مثل 17:00.",
    tierWindowNoDaysSelected: "اختر يومًا واحدًا على الأقل.",
    tierAutoApplyLabel: "تطبيقها تلقائيًا في موعدها",
    /* UX-round D54 — كانت ٢٢٦ حرف. «بتتطبق لوحدها» بقت مكتوبة على الصف نفسه، فالجملة
       دي بقت بتقول الحاجة اللي مش باينة: إنها بتمسك ترابيزات الـ QR كمان. */
    tierAutoApplyHint:
      "من دون اختيار الكاشير — وعلى طاولات QR أيضًا، ليبقى السعر المعلن موحدًا في الصالة كلها.",
    tierWindowColumn: "الموعد",
    tierActiveLabel: "مفعّلة",
    tierActiveHint:
      "لا تظهر الشريحة الموقوفة للكاشير عند فتح فاتورة، وتبقى الفواتير القديمة التي حُسبت بها كما هي. لا يمكن حذف الشريحة؛ ويكون الإيقاف هو البديل.",
    // --- kitchen stations (D21): where a plate gets cooked ---
    stationsTitle: "المحطات والطابعات",
    stationsNoAccessTitle: "لا تملك صلاحية عرض المحطات",
    stationsNoAccessBody:
      "المحطات والطابعات تحتاج إلى صلاحية إدارة الإعدادات. اطلبها من صاحب المطعم من صفحة الفريق.",
    stationsReadOnlyNote:
      "يمكنك عرض المحطات فقط ولا يمكنك تعديلها. يتطلب التعديل صلاحية الإعدادات.",
    stationsEmptyTitle: "لا توجد محطات حتى الآن",
    stationsEmptyBody:
      "المحطة هي المكان الذي يُحضّر فيه الصنف، مثل الشواية أو السلطات أو البار. عند تسجيل الطلب تصدر تذكرة مستقلة لكل محطة، فلا تعرض محطة الشواية مشروبات البار.",
    stationsEmptyAction: "إضافة أول محطة",
    addStation: "إضافة محطة",
    newStationTitle: "محطة جديدة",
    editStationTitle: "تعديل المحطة",
    stationNameArLabel: "الاسم بالعربية",
    stationNameEnLabel: "الاسم بالإنجليزية",
    stationNameEnHint: "اختياري. يظهر للطاقم الذي يستخدم الإنجليزية.",
    stationNameRequired: "اكتب اسمًا للمحطة.",
    stationSortOrder: "الترتيب",
    stationSortOrderHint: "يظهر الرقم الأصغر أولًا في شاشة المطبخ.",
    stationSortOrderValue: "الترتيب {order}",
    stationActiveLabel: "المحطة مفعّلة",
    stationActiveHint:
      "لن تظهر المحطة الموقوفة عند تحديد محطة الصنف، وستعود الأصناف المرتبطة بها إلى المحطة الأساسية.",
    stationActiveHintDefault:
      "هذه هي المحطة الاحتياطية ولا يمكن إيقافها. اختر أولًا محطة أخرى مفعّلة لتكون احتياطية.",
    stationDefaultBadge: "المحطة الأساسية",
    // T13 / D173 — the «المطلوب الآن» strip, per station (on by default).
    stationBatchStrip: "«المطلوب الآن» على شاشة المحطة",
    stationBatchStripHint: "عدد كل طبق في الطلبات الجديدة وفي التحضير، أعلى الشاشة.",
    stationDefaultExplainer:
      "تُوجّه إلى هنا الأصناف التي لم تُحدد لها محطة. لذلك لا يمكن حذفها؛ وإذا لم تعد بحاجة إليها، فأوقفها من شاشة التعديل.",
    stationPaused: "موقوفة",
    stationEntityLabel: "محطة",
    deleteStationTitle: "حذف المحطة",
    deleteStationBody:
      "ستعود الأصناف المرتبطة بهذه المحطة تلقائيًا إلى المحطة الأساسية، ولن يضيع أي صنف. لا يمكن التراجع عن الحذف.",
    stationDeleteRefused:
      "لم تُحذف هذه المحطة. ربما حذفها شخص آخر، أو لعلها المحطة الأساسية التي لا يمكن حذفها.",
    stationBuiltInBadge: "أساسية في النظام",
    activateStation: "تشغيل المحطة",
    deactivateStation: "إيقاف المحطة",
    makeFallbackStation: "تعيينها محطة احتياطية",
    settingsStationsRowBody: "قسّم المطبخ إلى محطات، وحدد الطابعات ومسار كل صنف.",
    printersTitle: "الطابعات",
    printersDescription:
      "اكتشف طابعات الجهاز واختبر الطابعة قبل التشغيل التلقائي.",
    configureDetectedPrinterTitle: "إعداد طابعة مكتشفة",
    editPrinterTitle: "تعديل الطابعة",
    printerLabel: "اسم الطابعة داخل المطعم",
    printerQueue: "الطابعة المكتشفة",
    printerQueueHint: "اختر طابعة من الطابعات المكتشفة.",
    printerPaperWidth: "عرض الورق",
    printerProfile: "ملف الجهاز المختبر",
    printerProfileHint:
      "اختياري. فعّل القص أو فتح الخزينة فقط إذا اختُبر هذا الملف على الجهاز وبرنامج التشغيل نفسيهما.",
    printerCutter: "القص التلقائي مختبر ومسموح",
    printerCashDrawer: "فتح الخزينة مختبر ومسموح",
    printerCapabilityWarning:
      "معطّل افتراضيًا؛ فعّله فقط بعد اختبار الجهاز الفعلي.",
    printerActive: "الطابعة مفعّلة",
    printerRequiredFields: "اختر طابعة مكتشفة واكتب اسمها داخل المطعم.",
    printerDiscoveryTransportNote:
      "اكتشف طابعات USB وBluetooth وWi-Fi وLAN المتصلة بالكمبيوتر الحالي، أو أضف طابعة شبكة بعنوان IP الخاص بها دون برنامج تشغيل. لا تحتاج أجهزة تسجيل الطلبات، مثل جهاز الويتر اللوحي، إلى إعداد للطباعة.",
    discoverPrinters: "اكتشاف الطابعات",
    addNetworkPrinter: "إضافة طابعة شبكة",
    searchNetwork: "البحث في هذه الشبكة",
    networkScanHint: "يفحص المنفذ 9100 في شبكة هذا الكمبيوتر. يستغرق نحو نصف دقيقة.",
    networkScanProgress: "جارٍ البحث… {done} من {total}",
    networkScanFound: "الطابعات التي استجابت:",
    networkScanNone:
      "لم تستجب أي طابعة في هذه الشبكة. تأكد أن الكمبيوتر على نفس شبكة الطابعة، بكابل أو على Wi-Fi بدون عزل للأجهزة.",
    networkScanUse: "استخدام",
    networkScanAdded: "مضافة بالفعل",
    qzSetupTitle: "تجهيز هذا الكمبيوتر للطباعة",
    qzSetupSubtitle: "مرة واحدة لكل كمبيوتر طباعة. لا يحتاجها جهاز الويتر اللوحي.",
    qzSetupStepInstall: "ثبّت QZ Tray من qz.io/download وافتحه.",
    qzSetupStepCommand:
      "انسخ الأمر الخاص بنظام هذا الكمبيوتر، والصقه، واضغط Enter. سيطلب كلمة مرور مدير الجهاز مرة واحدة.",
    qzSetupMacWhere: "على Mac: الصقه في تطبيق Terminal.",
    qzSetupWindowsWhere: "على Windows: الصقه في PowerShell بعد فتحه كمسؤول.",
    qzSetupCopyMac: "نسخ أمر Mac",
    qzSetupCopyWindows: "نسخ أمر Windows",
    qzSetupStepMacNetwork:
      "على Mac فقط: فعّل «java» من إعدادات النظام ← الخصوصية والأمان ← الشبكة المحلية.",
    qzSetupRerun: "إذا ظهرت رسالة ثقة بعد تحديث QZ Tray، شغّل الأمر مرة أخرى.",
    qzSetupCopied: "تم نسخ الأمر. الصقه واضغط Enter.",
    networkPrinterTitle: "طابعة شبكة",
    printerAddress: "عنوان IP للطابعة",
    printerAddressHint:
      "مطبوع على صفحة الاختبار الذاتي للطابعة (اضغط زر FEED أثناء تشغيلها)، مثل 192.168.1.236.",
    networkPrinterSetupNote:
      "يطبع هذا الكمبيوتر عليها مباشرة دون برنامج تشغيل. على جهاز Mac، فعّل «java» من إعدادات النظام ← الخصوصية والأمان ← الشبكة المحلية.",
    networkPrinterRequiredFields: "اكتب عنوان IP للطابعة واسمها داخل المطعم.",
    discoveredPrinterQueues: "طوابير الطباعة المكتشفة",
    printerDiscoveryComplete: "تم اكتشاف الطابعات. اختر طابعة لإعدادها.",
    printerDiscoveryUnavailable:
      "تعذّر الوصول إلى الطابعات على هذا الكمبيوتر. تأكد من تشغيل خدمة الطباعة، ثم حاول مرة أخرى.",
    printerDiscoveryEmpty:
      "لا توجد طابعات متاحة على هذا الكمبيوتر. أضف الطابعة من إعدادات الجهاز، ثم حاول مرة أخرى.",
    printerDiscoveryNoNew: "جميع الطابعات المكتشفة مُعدّة بالفعل.",
    printOutcomeUnknown:
      "نتيجة إرسال الطباعة غير مؤكدة. راجع الطابعة قبل إعادة المحاولة.",
    printJobFailed: "تعذر إرسال مهمة الطباعة، وستظل ظاهرة لإعادة المحاولة.",
    printKitchenAdvancePending:
      "تم إرسال الورقة لكن تحديث تذكرة المطبخ ما زال معلقًا.",
    printBrowserFallbackUsed: "استُخدمت نافذة طباعة المتصفح كبديل.",
    printerOnlineConfigured: "مفعّلة",
    printerInactive: "موقوفة",
    noPrintersConfigured: "لا توجد طابعات مكتشفة ومُعدّة حتى الآن.",
    testPrinter: "طباعة اختبار",
    testPrintQueued: "أُضيف اختبار الطباعة إلى قائمة الانتظار.",
    testPrintNoInvoice: "لا يفتح اختبار الطباعة أي فاتورة ولا يعدّلها.",
    deactivatePrinter: "إيقاف الطابعة",
    stationPrinterBindings: "طابعة كل محطة",
    stationPrinterBindingsHint:
      "لكل محطة طابعة أساسية وعدد نسخ. ويمكن لأكثر من محطة استخدام الطابعة نفسها.",
    stationPrinter: "الطابعة",
    printCopies: "عدد النسخ",
    noPrinter: "بلا طابعة",
    receiptPrinterTitle: "طابعة فاتورة العميل",
    receiptPrinterHint:
      "هذا الاختيار مستقل عن طابعات المطبخ. تبدأ الطباعة التلقائية بعد تأكيد الفاتورة.",
    receiptPrinter: "الطابعة الافتراضية للفاتورة",
    autoReceiptPrint: "طباعة فاتورة العميل تلقائيًا",
    autoReceiptPrintHint:
      "يجب اختيار طابعة مفعّلة. لا يمنع فشل الطباعة تأكيد الفاتورة.",
    automaticPrintDeviceLabel: "كمبيوتر الطباعة الحالي",
    printDeviceSessionMissing: "لم يُنشئ الخادم جلسة جهاز الطباعة.",
    // --- how a diner pays by transfer (D44, B35) ---
    settingsPaymentRow: "التحويل وإنستاباي",
    settingsPaymentRowBody:
      "العنوان الذي يحوّل إليه الضيف من هاتفه. من دونه لا يظهر زر التحويل.",
    paymentTitle: "التحويل وإنستاباي",
    paymentSubtitle:
      "يظهر هذا النص للضيف على هاتفه عند اختيار التحويل. سيظهر كما تكتبه، لذا راجع الحروف والأرقام.",
    paymentEmptyTitle: "لا يوجد عنوان تحويل حتى الآن",
    paymentEmptyBody:
      "يرى الضيف الآن خيار «الدفع عند الكاشير» فقط. أضف عنوان إنستاباي ليتمكن من التحويل من مكانه.",
    paymentAdd: "إضافة عنوان",
    paymentEdit: "تعديل",
    paymentDelete: "حذف",
    paymentDeleteTitle: "هل تريد حذف عنوان التحويل؟",
    paymentDeleteBody:
      "لن يرى الضيف زر التحويل مرة أخرى. لن تتأثر الفواتير المدفوعة سابقًا.",
    paymentFieldLabelAr: "الاسم الذي يظهر بالعربية",
    paymentFieldLabelEn: "الاسم الذي يظهر بالإنجليزية",
    paymentFieldValue: "العنوان نفسه",
    paymentFieldAdd: "إضافة سطر",
    paymentFieldRemove: "إزالة السطر",
    paymentActive: "مفعّل",
    paymentInactive: "متوقف",
    paymentSaved: "تم التسجيل",
    paymentDeleted: "حُذف",
    paymentNeedsOneField: "يجب إضافة سطر واحد على الأقل يتضمن العنوان.",
    // --- menu item: which station cooks it ---
    menuItemStationTitle: "محطة التحضير",
    menuItemStationSubtitle:
      "المحطة التي يُحضّر فيها هذا الصنف، ومنها تُطبع تذكرة المطبخ الخاصة به.",
    menuItemStationPick: "اختر المحطة",
    stationPickerDefaultOption: "المحطة الأساسية (بلا تحديد)",
    menuItemStationNoneTitle: "لا توجد محطات مضبوطة",
    menuItemStationNoneBody: "يجب إنشاء محطة أولًا لتوجيه الصنف إليها.",
    menuItemStationNoneAction: "فتح المحطات",
    // --- floor: areas & tables (edit / move / pause / delete) ---
    floorDescription: "مناطق المطعم والطاولات الموجودة داخل كل منطقة.",
    floorViewLayout: "المخطط",
    floorViewQr: "رموز QR",
    tableQrNeedsLocationTitle: "اضبط موقع المطعم أولًا",
    tableQrNeedsLocationAction: "فتح إعدادات طلب الطاولة",
    tableQrNeedsLocationBody:
      "الطباعة تُفتح بعد حفظ المكان وتشغيل التحقق بالموقع من إعدادات طلب الطاولة.",
    tableQrNoActiveTitle: "لا توجد طاولات مفعّلة للطباعة",
    tableQrNoActiveBody:
      "لا تدخل الطاولات المتوقفة ورقة الطباعة. فعّل طاولة من المخطط أو أضف طاولة جديدة.",
    tableQrPermanentNote:
      "هذه روابط دائمة للملصقات. أما جلسة الضيف التي تبدأ بعد المسح فتنتهي بعد ساعة.",
    tableQrPreview: "معاينة",
    tableQrDownload: "تنزيل PNG",
    tableQrPrintOne: "طباعة هذا",
    tableQrPrintAll: "طباعة كل الطاولات المفعّلة",
    tableQrStickerScan: "امسح الرمز لتفتح قائمة الطعام وتطلب من الطاولة",
    /* The sticker's own lines (2026-10-29 board): the label above the table
       number, the headline, and what scanning gives the guest. */
    tableQrStickerTable: "طاولة",
    tableQrStickerTitle: "امسح الرمز بكاميرا الهاتف",
    tableQrStickerBody:
      "شاهد القائمة واطلب من طاولتك، ونادِ الويتر أو اطلب الحساب. بدون تطبيق.",
    tableQrImageAlt: "رمز QR للطاولة {name}",
    tableQrInvalidCode:
      "هذا الرمز محجوز ولا يمكن إنشاء رمز QR منه. غيّر رمز الطاولة من المخطط.",
    /* عنوان فرعي لفورم الطاولة في اللوحة الجانبية. */
    floorTableFormHint: "تظهر الطاولة في هذه المنطقة، ولها رمز QR خاص بها.",
    areasEmptyAction: "إضافة أول منطقة",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    areaTableCountOne: "طاولة واحدة",
    areaTableCountTwo: "طاولتين",
    areaTableCountFew: "{count} طاولات",
    areaTableCount: "{count} طاولة",
    areaTableCountNone: "لا توجد طاولات",
    areaNoTablesBody:
      "لا توجد طاولات في هذه المنطقة حتى الآن. أضف طاولة لتظهر لطاقم الصالة.",
    /* Layout redesign (2026-10-29 board). */
    addTables: "إضافة طاولات",
    addTableShort: "طاولة",
    addAreaLong: "منطقة جديدة (صالة، تراس، غرف…)",
    floorReorder: "ترتيب",
    floorReorderDone: "تم",
    floorReorderTitle: "وضع الترتيب.",
    floorReorderBody:
      "اسحب الطاولة أو المنطقة إلى موضعها، أو استخدم الأسهم. هذا الترتيب هو ما يراه الكاشير والويتر على الصالة.",
    floorDragHandle: "اسحب لتغيير الترتيب",
    floorSumAreasOne: "منطقة",
    floorSumAreasTwo: "منطقتان",
    floorSumAreasFew: "مناطق",
    floorSumAreas: "منطقة",
    floorSumTablesOne: "طاولة",
    floorSumTablesTwo: "طاولتان",
    floorSumTablesFew: "طاولات",
    floorSumTables: "طاولة",
    floorSumSeatsOne: "مقعد",
    floorSumSeatsTwo: "مقعدان",
    floorSumSeatsFew: "مقاعد",
    floorSumSeats: "مقعداً",
    floorSumPaused: "موقوفة",
    floorSumHint: "اضغط أي طاولة لتعديلها. الحذف والإيقاف داخلها.",
    areaPausedCountOne: "طاولة موقوفة",
    areaPausedCountTwo: "طاولتان موقوفتان",
    areaPausedCountFew: "{count} طاولات موقوفة",
    areaPausedCount: "{count} طاولة موقوفة",
    areaMenuLabel: "خيارات المنطقة",
    areaMenuDetails: "نوع المنطقة والتفاصيل",
    areaMenuReorder: "ترتيب المناطق",
    editTableNamed: "طاولة {name}",
    tableInService: "في الخدمة",
    tablePausedSub: "موقوفة · لا تظهر على الصالة",
    newTablesIn: "طاولات جديدة في {area}",
    newTablesSub: "تظهر على الصالة فور الحفظ",
    newAreaSub: "جزء من المكان تجلس فيه طاولات",
    tableSeatsLabel: "المقاعد",
    tablesHowMany: "كم طاولة؟",
    tablesNamesPreview: "ستُضاف بهذه الأسماء",
    tablesNamesHint:
      "بعد آخر طاولة في المنطقة. يمكنك تغيير أي اسم لاحقاً، ولكل طاولة رمز QR خاص.",
    tableQrBlock: "رمز QR",
    tableQrCodeLine: "رمز الطاولة: {code} · تغييره يوقف الملصق المطبوع",
    tableQrChangeCode: "تغيير",
    tableQrPrintShort: "اطبع",
    tableDeleteSection: "حذف الطاولة",
    tableDeleteNote:
      "تختفي من الصالة ويتوقف ملصق QR الخاص بها. إن كانت عليها فواتير سابقة سنقترح إيقافها بدلاً من الحذف.",
    tableDeleteNamed: "حذف {name}",
    addTablesCtaOne: "أضف طاولة",
    addTablesCtaTwo: "أضف طاولتين",
    addTablesCtaFew: "أضف {count} طاولات",
    addTablesCta: "أضف {count} طاولة",
    addTableToArea: "إضافة طاولة هنا",
    openTableSettings: "فتح إعدادات الطاولة",
    deleteAreaAction: "حذف المنطقة",
    deleteTableAction: "حذف الطاولة",
    newAreaTitle: "منطقة جديدة",
    editAreaTitle: "تعديل المنطقة",
    areaNameRequired: "اكتب اسمًا للمنطقة.",
    areaEntityLabel: "منطقة",
    deleteAreaTitle: "حذف المنطقة",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    deleteAreaBodyOne:
      "تحتوي هذه المنطقة على طاولة واحدة. إذا حذفت المنطقة، فستُحذف الطاولة ويتوقف رمز QR الخاص بها نهائيًا. لا يمكن التراجع عن هذا الإجراء. انقل الطاولة إلى منطقة أخرى أولًا إذا أردت الاحتفاظ بها.",
    deleteAreaBodyTwo:
      "تحتوي هذه المنطقة على طاولتين. إذا حذفت المنطقة، فستُحذف الطاولتان وتتوقف رموز QR الخاصة بهما نهائيًا. لا يمكن التراجع عن هذا الإجراء. انقل الطاولتين إلى منطقة أخرى أولًا إذا أردت الاحتفاظ بهما.",
    deleteAreaBodyFew:
      "تحتوي هذه المنطقة على {count} طاولات. إذا حذفت المنطقة، فستُحذف جميع هذه الطاولات وتتوقف رموز QR الخاصة بها نهائيًا. لا يمكن التراجع عن هذا الإجراء. انقل الطاولات إلى منطقة أخرى أولًا إذا أردت الاحتفاظ بها.",
    deleteAreaBody:
      "تحتوي هذه المنطقة على {count} طاولة. إذا حذفت المنطقة، فستُحذف جميع هذه الطاولات وتتوقف رموز QR الخاصة بها نهائيًا. لا يمكن التراجع عن هذا الإجراء. انقل الطاولات إلى منطقة أخرى أولًا إذا أردت الاحتفاظ بها.",
    deleteAreaBodyNoTables:
      "لا تحتوي هذه المنطقة على طاولات، لذلك لن يؤثر حذفها في شيء آخر. لا يمكن التراجع عن الحذف.",
    areaBlockedTitle: "لا يمكن حذف هذه المنطقة",
    areaBlockedBody:
      "ترتبط بعض طاولات هذه المنطقة بفواتير سابقة، ويجب أن تبقى الفواتير مرتبطة بطاولاتها لتظل التقارير صحيحة. يمكنك تغيير اسم المنطقة، أو إيقاف الطاولة غير المستخدمة بدلًا من حذفها.",
    areaBlockedUnderstood: "فهمت",
    newTableTitle: "طاولة جديدة",
    editTableTitle: "تعديل الطاولة",
    tableAreaLabel: "المنطقة",
    areaPickerPlaceholder: "ابحث عن منطقة",
    areaPickerNoResults: "لا توجد منطقة بهذا الاسم",
    tableAreaRequired: "اختر المنطقة التي توجد فيها الطاولة.",
    tableNameRequired: "اكتب اسمًا للطاولة.",
    tableCodeRequired: "لا يمكن ترك الرمز فارغًا.",
    tableCodeInvalid:
      "استخدم حروفًا إنجليزية وأرقامًا والشرطة (-) والشرطة السفلية (_) فقط، بحد أقصى 40 حرفًا، وابدأ بحرف أو رقم. لا تُستخدم النقطة ولا المسافة ولا الحروف العربية.",
    tableCodeTaken:
      "هذا الرمز مستخدم لطاولة أخرى. يجب أن يكون لكل طاولة رمز مستقل.",
    tableCodeChangeWarning:
      "هذا الرمز جزء من رابط QR الخاص بالطاولة. إذا غيّرته، ستتوقف ملصقات QR المطبوعة لهذه الطاولة ولن يصل الضيف إلى الصفحة عند مسحها. لا تغيّره إلا إذا كنت ستطبع الملصق من جديد.",
    tableActiveLabel: "الطاولة مفعّلة",
    tableActiveHint:
      "إذا أوقفتها، فستختفي من شاشة الصالة ولن تُفتح عليها فواتير جديدة، بينما تبقى فواتيرها القديمة كما هي.",
    tableInactive: "موقوفة",
    tableEntityLabel: "طاولة",
    deleteTableTitle: "حذف الطاولة",
    deleteTableBody:
      "ستُحذف هذه الطاولة من شاشة الصالة، وسيتوقف ملصق QR الخاص بها نهائيًا. لا يمكن التراجع عن هذا الإجراء.",
    tableBlockedTitle: "لا يمكن حذف هذه الطاولة",
    tableBlockedBody:
      "ترتبط هذه الطاولة بفواتير سابقة، وسيؤدي حذفها إلى فقدان هذا الارتباط. أوقف الطاولة بدلًا من حذفها؛ فستختفي من شاشة الصالة ولن تُفتح عليها فواتير جديدة، بينما يبقى سجلها محفوظًا.",
    tableBlockedBodyInactive:
      "ترتبط هذه الطاولة بفواتير سابقة وهي موقوفة بالفعل، لذلك لا تظهر على شاشة الصالة. يجب أن تبقى الفواتير مرتبطة بها، ولهذا لا يمكن حذفها.",
    tableBlockedDeactivate: "إيقاف الطاولة",
    tableBlockedKeep: "إبقاؤها كما هي",
    /* UX-round D62 — `floorLayoutBanner` اتشال. كان بيقول إن دي مش الشاشة التانية؛
       بقى فيه شاشة واحدة بعرضين، فالجملة مبقاش ليها موضوع. */
    floorOrderFailed: "تعذّر حفظ الترتيب الجديد، فأُعيد الترتيب السابق.",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    seatCountOne: "مقعد واحد",
    seatCountTwo: "مقعدين",
    seatCountFew: "{count} مقاعد",
    seatCount: "{count} مقعد",
    seatsUnset: "المقاعد غير مسجّلة",
    seatsPerTable: "المقاعد لكل طاولة",
    tableBatchCount: "عدد الطاولات",
    tableBatchHint: "يضيف عدة طاولات بعدد المقاعد نفسه دفعة واحدة.",
    tableAutoName: "تُحدّد الأسماء تلقائيًا من اسم المنطقة.",
    newTablesTitle: "إضافة طاولات",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    tablesAddedOne: "أُضيفت طاولة واحدة",
    tablesAddedTwo: "أُضيفت طاولتان",
    tablesAddedFew: "أُضيفت {count} طاولات",
    tablesAdded: "أُضيفت {count} طاولة",
    renameArea: "تغيير اسم المنطقة",
    areaKindLabel: "النوع",
    areaKindHint: "للأيقونة فقط — السلوك واحد في كل مكان.",
    areaKindIndoor: "داخلي",
    areaKindOutdoor: "خارجي",
    areaKindRooms: "غرف",
    tableNameLabel: "اسم الطاولة",
  },
  /**
   * The staff & roles screen — the delegation mechanism.
   *
   * The restaurant system roles are WAITER and CASHIER, and neither holds a
   * single back-office permission, so an owner who wants a manager has exactly
   * two options: hand over their own login, or mint a role here. This screen is
   * the second option, which is why its copy talks about handing over work
   * rather than about "RBAC".
   */
  staff: {
    title: "الفريق والصلاحيات",
    description: "مَن يمكنه الدخول وما الصلاحيات المتاحة له.",
    noAccessTitle: "هذه الشاشة لصاحب المطعم",
    noAccessBody:
      "إدارة الفريق والأدوار متاحة لصاحب المطعم فقط. إذا احتجت إلى الوصول إليها، فتواصل معه.",

    membersTitle: "الفريق",
    membersEmptyTitle: "أنت العضو الوحيد حتى الآن",
    membersEmptyBody:
      "أضف من سيعمل معك بالاسم ورقم الهاتف، وحدد الدور الذي سيحصل عليه.",
    memberRoles: "الأدوار",
    memberNoRoles: "بلا دور",
    memberRolesRequired: "يجب اختيار دور واحد على الأقل.",
    removeMember: "إزالته من الفريق",
    removeMemberTitle: "هل تريد إزالة هذا الشخص من الفريق؟",
    removeMemberBody:
      "لن يتمكن من دخول المطعم مرة أخرى. ستبقى الفواتير والحركات التي نفّذها كما هي.",
    memberEntityLabel: "عضو",

    invitesTitle: "دعوات معلّقة",
    invitesEmptyTitle: "لا توجد دعوات معلّقة",
    invitesEmptyBody:
      "تبقى الدعوة التي لم تُقبل هنا حتى يقبلها صاحبها أو تلغيها أنت.",
    inviteAction: "إضافة عضو",
    inviteTitle: "إضافة عضو جديد",
    inviteEmail: "البريد الإلكتروني (اختياري)",
    inviteEmailRequired: "اكتب بريدًا إلكترونيًا صحيحًا.",
    memberPhone: "رقم الهاتف",
    memberDetailsRequired: "اكتب الاسم ورقم الهاتف.",
    temporaryPassword: "كلمة المرور المؤقتة",
    temporaryCredentialsHint:
      "انسخ بيانات الدخول الآن وشاركها بأمان. يجب على المستخدم تغيير كلمة المرور بعد تسجيل الدخول.",
    copyCredentials: "نسخ بيانات الدخول",
    credentialsCopied: "تم نسخ بيانات الدخول",
    inviteRoles: "الدور",
    inviteRolesRequired: "اختر دورًا واحدًا على الأقل.",
    inviteSend: "إضافة العضو",
    copyInviteLink: "نسخ رابط الدعوة",
    copyInviteLinkDone: "تم نسخ الرابط؛ أرسله إلى الموظف",
    inviteSent: "تمت إضافة العضو",
    inviteExpires: "تنتهي",
    invitedBy: "بدعوة من",
    revokeInvite: "إلغاء الدعوة",
    revokeInviteTitle: "هل تريد إلغاء هذه الدعوة؟",
    revokeInviteBody:
      "سيتوقف رابط الدعوة المرسل عن العمل. يمكنك إرسال دعوة أخرى في أي وقت.",
    inviteEntityLabel: "دعوة",

    rolesTitle: "الأدوار",
    // B117/E2 — الرابط ده كان بيرد بقايمة الأعضاء بدل الأدوار من غير ما يقول
    // حاجة: مافيش تسريب (السيرفر بيرفض `GET /seller/roles` صح)، بس الشاشة كانت
    // بترد على سؤال تاني غير اللي اتسأل. دي الشاشة الوحيدة في التطبيق اللي
    // كانت بترد على رابط مباشر لا بالحاجة ولا بالرفض.
    rolesNoAccessTitle: "لا تملك صلاحية عرض الأدوار",
    rolesNoAccessBody:
      "تعديل الأدوار وصلاحياتها متاح لصاحب المطعم. لعرض أعضاء الفريق، افتح صفحة الفريق.",
    rolesSubtitle:
      "الدور مجموعة صلاحيات تمنحها للموظف. أنشئ دور «مدير» وسلّمه الإدارة من دون مشاركة حسابك.",
    rolesEmptyTitle: "لا توجد أدوار حتى الآن",
    rolesEmptyBody: "أنشئ أول دور وحدد ما يمكنه الوصول إليه بدقة.",
    addRole: "دور جديد",
    systemRole: "دور جاهز",
    systemRoleHint:
      "لا يمكن تعديل الأدوار الجاهزة أو حذفها. إذا احتجت إلى صلاحيات مختلفة، فأنشئ دورًا جديدًا.",
    viewRole: "عرض صلاحياته",
    editRole: "تعديل الدور",
    deleteRole: "حذف الدور",
    duplicateRole: "نسخ الدور",
    roleCopyName: "{name} (نسخة)",
    /* D128 — قصر الدور على محطات مطبخ بعينها. */
    roleStationsTitle: "محطات المطبخ",
    roleStationsHint:
      "المحطات التي يراها هذا الدور على شاشة المطبخ ويعمل عليها. اختر محطة واحدة ليبقى الطباخ على محطته فقط.",
    roleStationsAll: "كل المحطات",
    roleStationsSome: "محطات محددة فقط",
    roleStationsPickOne: "اختر محطة واحدة على الأقل.",
    roleStationsLoadFailed: "تعذّر تحميل محطات المطبخ.",
    roleStationsNone: "لا توجد محطات مطبخ بعد.",
    deleteRoleTitle: "هل تريد حذف هذا الدور؟",
    deleteRoleBody:
      "سيُزال الدور نهائيًا. إذا كان لا يزال مسندًا إلى أحد، فأزله منه أولًا ثم احذفه.",
    roleEntityLabel: "دور",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    rolePermissionsCountOne: "صلاحية واحدة",
    rolePermissionsCountTwo: "صلاحيتين",
    rolePermissionsCountFew: "{count} صلاحيات",
    rolePermissionsCount: "{count} صلاحية",
    rolePermissionsFailed:
      "تعذّر تحميل قائمة الصلاحيات، لذلك لا يمكن فتح دور أو إنشاء دور جديد الآن.",
    rolePermissionsLoading:
      "انتظر قليلًا؛ ما زال تحميل قائمة الصلاحيات جاريًا.",

    // أسماء الأدوار الجاهزة. السيرفر بيخلقها بأسماء إنجليزية وهي مش قابلة
    // للتعديل، فالمكان الوحيد اللي تتقال فيه بالعربي هو هنا.
    presetOwner: "صاحب المطعم",
    presetManager: "مدير",
    presetFulfillment: "تجهيز الطلبات",
    presetSupport: "خدمة العملاء",
    presetWaiter: "ويتر",
    presetCashier: "الكاشير",
    presetKitchen: "مطبخ",
    presetExpediter: "مسؤول التسليم",
    /* تدقيق الجولة 2 (المرحلة 3): أوصاف الأدوار الجاهزة. السيرفر يكتبها
       بالإنجليزية (`system-role.catalog.ts`) ولا تُعدَّل، فتُقال هنا بالعربية
       بمعناها نفسه. وصف الدور المخصص يبقى كما كتبه صاحب المطعم. */
    presetDescription_OWNER: "صلاحيات كاملة على المطعم",
    presetDescription_MANAGER:
      "يدير الصالة: شاشة البيع والمطبخ والخزينة، والقرارات التي يحيلها إليه الكاشير",
    presetDescription_FULFILLMENT: "يطّلع على الطلبات ويجهّزها",
    presetDescription_SUPPORT: "اطّلاع فقط، لخدمة العملاء",
    presetDescription_WAITER: "يفتح الطاولات ويضيف الأصناف ويطلب إعادة التحضير",
    presetDescription_CASHIER:
      "كل ما يفعله الويتر، إضافةً إلى الإلغاء و«على الحساب» وإغلاق الفاتورة وتحصيلها",
    presetDescription_KITCHEN: "يعمل على شاشة المطبخ: يبدأ تحضير الطعام وينهيه",
    presetDescription_KITCHEN_EXPEDITOR:
      "يعمل على التسليم: يسلّم الطعام الجاهز ويعيد طباعة التذكرة ويوقف الصنف",
    // Driver backend B1: the DRIVER preset (server name "Driver").
    presetDriver: "طيار",
    // Read by `presetDescription` in role-names.ts (master's audit round 2 switch).
    presetDescription_DRIVER: "يوصّل طلبات التوصيل الخاصة بالمطعم من تطبيق تريوتي للطيار",
    newRoleTitle: "دور جديد",
    editRoleTitle: "تعديل الدور",
    roleName: "اسم الدور",
    roleNamePlaceholder: "مثلاً: مدير الفرع",
    roleNameRequired: "اكتب اسمًا للدور.",
    /* UX-round D55 — «الكود» وأربع جمله اتشالوا مع الخانة نفسها. صاحب المطعم مش
       المفروض يخترع كود إنجليزي لحاجة «للنظام بس ومحدش هيشوفه غير هنا»،
       فالكود بقى بيتولّد وقت الحفظ (`generateRoleKey`). */
    roleDescription: "وصف مختصر",
    rolePermissionsTitle: "الصلاحيات",
    roleQuickLevel: "تفعيل سريع",
    roleLevelNone: "لا شيء",
    roleLevelBasic: "الأساسي",
    roleLevelAll: "الكل",
    roleTagSensitive: "حساس",
    roleTagPartial: "جزئي",
    roleTagCeiling: "أعلى من صلاحياتك",
    roleGroupCount: "{done} من {total}",
    roleSummaryTitle: "ماذا يستطيع هذا الشخص؟",
    roleSummaryCan: "يستطيع",
    roleSummaryCannot: "لا يستطيع",
    roleSummaryEmpty: "لا شيء بعد. فعّل ما يحتاجه من القائمة.",
    roleSettleNeedsDrawer: "إن كان تتبّع الخزينة مفعّلاً في مطعمك، لن يستطيع تحصيل الدفع قبل أن يفتح خزينة. فعّل «فتح خزينته وإغلاقها وعدّ النقدية».",
    roleMembersBanner: "أعضاء على هذا الدور الآن: {count} · أي تغيير يسري عليهم كلهم",
    roleStartFrom: "ابدأ من",
    roleStartBlank: "دور فارغ",
    rolePermissionsHint:
      "لكل جزء مستوى واحد، ويشمل المستوى الأعلى ما تحته — فلا يمكن لأحد حذف شيء لا يملك صلاحية عرضه.",
    showPermissions: "عرض الصلاحيات بالتفصيل",
    teamMembersTab: "الفريق",
    teamRolesTab: "الأدوار",
    // PAPER (تكافؤ البكسل، 2026-10-02) — كلمات التصميم: العنوان وسطره في الشريط
    // العلوي، والتبويبان، وخانة البحث.
    paperTitle: "الفريق",
    paperSubtitle: "من يعمل هنا، وما يستطيع كل دور فعله.",
    paperMembersTab: "الأعضاء",
    paperRolesTab: "الأدوار والصلاحيات",
    paperSearch: "ابحث بالاسم أو رقم الهاتف",
    paperFieldName: "الاسم بالكامل",
    paperFieldPhone: "رقم الهاتف",
    paperNamePlaceholder: "مثلاً: نور حسن",
    paperPhonePlaceholder: "010 0000 0000",
    paperEmailPlaceholder: "name@example.com",
    teamRoleFilterAll: "الكل",
    teamListEmpty: "لا يوجد أحد مطابق لعوامل التصفية",
    teamPending: "لم يقبل بعد",
    teamHeaderMember: "العضو",
    teamRoleMemberCountOne: "عضو واحد",
    teamRoleMemberCountNone: "لا أحد حتى الآن",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    teamRoleMemberCountTwo: "عضوين",
    teamRoleMemberCountFew: "{count} أعضاء",
    teamRoleMemberCount: "{count} عضو",
    teamHeaderRole: "الدور",
    teamRoleMembers: "أعضاء هذا الدور",
    teamNoRoleMembers: "لا يوجد أحد في هذا الدور حتى الآن",
    teamRolePermsTitle: "الصلاحيات",
    teamRoleNote:
      "تُضبط الصلاحيات من تعديل الدور — ولا يمكن تعديل الأدوار الأساسية.",
    teamMemberCount: "{count} في الفريق",
    // PAPER (تصميم 2026-10) — جدول الفريق ونافذة العضو.
    teamHeaderStatus: "الحالة",
    statusActive: "نشط",
    // راوند 5 — عمود «آخر نشاط»: آخر حاجة سجّلها العضو، وآخر 7 أيام عمل (حسب
    // بداية يوم المطعم) كمربعات. اللون مش لوحده: كل مربع ليه نص بيقول اليوم
    // والحالة.
    teamHeaderLastActive: "آخر نشاط",
    lastActiveNone: "لا نشاط بعد",
    activityWeek: "آخر 7 أيام عمل",
    activityWorked: "في الخدمة",
    activityException: "إلغاء أو هدية أو مرتجع أو فاتورة بلا دفع أو تصحيح دفع",
    activityOff: "لا نشاط",
    activityExceptionDetail:
      "في الخدمة، ومعه إلغاء أو هدية أو مرتجع أو فاتورة بلا دفع أو تصحيح طريقة دفع",
    activityDay: "{day}: {state}",
    activityLoadFailed: "تعذّر تحميل آخر نشاط للفريق.",
    customRole: "دور مخصّص",
    editMemberTitle: "تعديل {name}",
    memberEmail: "البريد الإلكتروني",
    // الاسم والرقم والإيميل بتوع حساب الشخص نفسه، والمحل مش بيعدّلهم.
    memberIdentityNote:
      "الاسم والرقم والبريد تخص حساب الشخص نفسه، وهو وحده من يغيّرها.",
    inviteNote:
      "إذا كان لهذا الرقم حساب بالفعل فسيُربط كما هو، وإلا فستظهر لك كلمة مرور مؤقتة تسلّمها له، ويختار كلمة مروره بنفسه عند أول دخول.",
    hidePermissions: "إخفاء التفاصيل",
    /* UX-round D55 — في عرض الدور الجاهز، الصلاحية بتتقال بكلمة مش بمفتاح: المفتاح
       اللي محدش يقدر يلمسه صورة زرار مش زرار. */
    permHeld: "متاحة",
    permNotHeld: "غير متاحة",
    // المجموعة المطويّة بتاعة المتجر الأونلاين (راوند 10، QA §7). مطويّة، مش
    // مخفيّة: المحرّر بيبعت الصلاحيات كلها مع بعض، فأي جزء مش مرسوم كان هيتشال
    // من الدور وقت الحفظ. العدّاد بيظهر لما يكون جوّه صلاحيات فعلاً مدّية.
    storefrontAreasTitle: "صلاحيات المتجر الإلكتروني",
    storefrontAreasHint:
      "تخص هذه الصلاحيات متجر المطعم الإلكتروني — المنتجات والطلبات الإلكترونية والكوبونات والتقييمات والشحن والدفع وإعدادات المتجر — وليس لها شاشة في تطبيق المطعم.",
    storefrontAreasGranted: "{count} ممنوحة",
    // الكاشير بيقرا المنيو والأقسام الاتنين عشان يبني الطلب، وكل واحدة فيهم
    // في جزء تاني — فالجزئين لازم يتدّوا مع بعض، ومحدش يخمّن التاني.
    posNeedsMenuView:
      "ليعمل الكاشير بصورة صحيحة، امنح أيضًا «قائمة الطعام: مشاهدة فقط» و«الأقسام: مشاهدة فقط» — فمن دونهما تُفتح الشاشة بلا أصناف أو مرشحات.",
    ceilingTitle: "لا يمكنك منح صلاحية لا تملكها",
    ceilingBody:
      "الصلاحيات المعطّلة أدناه غير متاحة لحسابك، لذلك لا يمكنك منحها. يستطيع صاحب المطعم منحها.",
    ceilingRejected:
      "رفض الخادم الحفظ: يحتوي هذا الدور على صلاحيات لا تملكها، لذلك لا يمكنك منحها.",
    noPermissionsSelected:
      "لا يملك هذا الدور أي صلاحية حتى الآن. اختر صلاحية أولًا.",
  },
  /**
   * Permission and area labels.
   *
   * Every code the server can return has a line here, because the alternative —
   * showing `RESTAURANT_INVOICE_VOID` to a restaurant owner — is not a
   * translation, it is a leak of the enum. A code with no line falls back to
   * its raw value rather than disappearing.
   */
  /**
   * Typed server refusals, in the words a cashier acts on.
   *
   * The backend message beside each code is English and for logs; these are
   * the same facts said to the person holding the till (docs/design: errors
   * must be human-readable). Only codes a screen does NOT branch on inline
   * live here — a stale close, for instance, becomes a refresh prompt long
   * before it could become a toast.
   */
  serverErrors: {
    /* Inventory item card change (D136–D143) — every new typed refusal in
       words, per the error-localization contract. */
    purchaseDateOutOfRange:
      "لا يمكن تسجيل التوريدة على هذا اليوم. اختر يومًا بين {from} و{to}.",
    purchaseDateOutOfRangeUnbounded:
      "لا يمكن تسجيل التوريدة على هذا اليوم. اختر اليوم أو يومًا قريبًا قبله.",
    adjustmentSignInvalid:
      "اتجاه الكمية لا يتوافق مع السبب: الهالك والتالف يُخصمان من المخزون، والتصحيح لا يكون صفرًا. راجع الكمية وحاول مرة أخرى.",
    supplierReturnExceedsPurchase:
      "يمكن إرجاع {quantity} فقط من هذه التوريدة، وهو ما لم يُرجَع منها بعد. عدّل الكمية وحاول مرة أخرى.",
    supplierReturnExceedsPurchaseUnnamed:
      "الكمية أكبر مما بقي من هذه التوريدة دون إرجاع. عدّل الكمية وحاول مرة أخرى.",
    // D163 — a reference rides a card or InstaPay tender only.
    tenderReferenceNotAllowed:
      "يُسجَّل مرجع العملية مع الدفع بالبطاقة أو إنستاباي فقط. احذف المرجع أو غيّر طريقة الدفع.",
    returnToStockNotAllowed:
      "لا يُرجَع طبق «على الحساب» إلى المخزن؛ فهو يُحتسب هالكًا دائمًا.",
    itemNotReturnableNotCooked:
      "لم يُحضَّر هذا الطبق، فلا يوجد ما يُرجَع إلى المخزن. أكمل دون إرجاعه.",
    itemNotReturnableAlreadyReturned:
      "أُرجع هذا الطبق إلى المخزن من قبل. أكمل دون إرجاعه مرة ثانية.",
    itemNotReturnable:
      "لا يمكن إرجاع هذا الطبق إلى المخزن. أكمل دون إرجاعه.",
    stocktakeAlreadyOpen:
      "يوجد جرد مفتوح بالفعل. أكمله أو ألغِه قبل بدء جرد جديد.",
    stocktakeNotOpenPosted:
      "رُحِّل هذا الجرد بالفعل ولا يمكن تعديله. حدّث الصفحة لترى ما سُجِّل.",
    stocktakeNotOpenCancelled:
      "أُلغي هذا الجرد ولا يمكن تعديله. ابدأ جردًا جديدًا إذا احتجت.",
    stocktakeNotOpen: "هذا الجرد لم يعد مفتوحًا. حدّث الصفحة.",
    stocktakeNotFound:
      "لم نجد هذا الجرد. ربما تغيّر من جهاز آخر — حدّث الصفحة.",
    itemCardTooLarge:
      "في هذه الفترة حركات أكثر مما تعرضه البطاقة مرة واحدة. اختر فترة أقصر.",
    ledgerIdConflict:
      "رقم هذه الحركة مستخدم لسجل آخر. أغلق النافذة وأعد المحاولة.",
    ingredientNotFound: "لم نجد هذا المكوّن. ربما حُذف — حدّث الصفحة.",
    // The dish has a REQUIRED option group and nothing was chosen. Names the
    // control the waiter has to tap, because a refusal that does not say what
    // to do next just stops service.
    addonSelectionTooFew:
      "يجب اختيار {count} على الأقل من إضافات هذا الصنف. اضغط «+ إضافات» أولًا.",
    // A stale tab or a tampered payload: the picker does not let a waiter
    // break maxSelect, and a group the dish does not carry cannot be tapped.
    addonSelectionNotValid:
      "الإضافات المختارة غير صالحة لهذا الصنف. أغلق الصنف وافتحه مرة أخرى، ثم اختر من البداية.",
    invoiceHasUnreachedItems:
      "توجد أصناف لم تصل إلى المطبخ بعد. أرسلها أو اقبلها أو أزلها أولًا.",
    kitchenStationMissing:
      "لا توجد محطة مطبخ تستقبل هذا الصنف. راجع إعدادات المحطات.",
    stationHasHistory:
      "ترتبط بهذه المحطة تذاكر مطبخ سابقة، لذلك لا يمكن حذفها. أوقفها بدلًا من حذفها.",
    kitchenStationNotAllowed:
      "هذه المحطة ليست ضمن محطات دورك. اطلب من المالك إضافتها إلى دورك.",
    stationLockedByRole:
      "أحد أدوار الفريق مقصور على هذه المحطة. احذفها من محطات ذلك الدور أولًا، أو أوقفها بدلًا من حذفها.",
    fallbackStationInactive:
      "يجب أن تكون المحطة الاحتياطية مفعّلة. فعّل المحطة ثم اخترها.",
    fallbackStationProtected:
      "لا يمكن إيقاف المحطة الاحتياطية أو حذفها. اختر أولًا محطة أخرى مفعّلة.",
    builtInStationProtected:
      "لا يمكن حذف محطات المطبخ والطعام والمشروبات الأساسية، ولكن يمكن إيقافها.",
    printingNotFound:
      "الطابعة أو جهاز الطباعة المطلوب غير موجود أو موقوف. حدّث الصفحة وراجع الإعدادات.",
    printingRequestInvalid:
      "إعداد الطباعة غير صالح. راجع الطابعة وعرض الورق والخصائص المختبرة.",
    printingStateConflict:
      "تغيرت حالة مهمة الطباعة على جهاز آخر. حدّث الصفحة قبل إعادة المحاولة.",
    printDeviceUnauthorized:
      "انتهت جلسة جهاز الطباعة أو أُلغيت. أعد ربط الجهاز.",
    invoiceNotCancellable:
      "تحتوي هذه الفاتورة على أصناف أُبلغ بها المطبخ، لذلك لا يمكن إلغاؤها. أغلقها بدلًا من ذلك.",
    invoiceNotReopenable:
      "حُصّلت هذه الفاتورة أو أُلغيت بالفعل، لذلك لا يمكن فتحها مرة أخرى.",
    settlementShort: "المبلغ المستلم أقل من إجمالي الفاتورة.",
    partyNotFound:
      "هذه المجموعة غير موجودة على الفاتورة؛ ربما أزال شخص آخر العلامة. حدّث الشاشة وحاول مرة أخرى.",
    partyLabelRequired: "اكتب اسمًا للمجموعة أو اختر مجموعة موجودة.",
    // العيلة دي خلّصت حسابها — غالباً كاشير تاني أخد منها على جهاز تاني.
    partyAlreadySettled:
      "دفعت هذه المجموعة حصتها بالفعل. حدّث الفاتورة وراجع المبلغ المتبقي.",
    customerNotEditable:
      "هذه الفاتورة مرتبطة بطاولة ولا تحتوي على بيانات عميل قابلة للتعديل. حدّث الشاشة.",
    // T6 — `editCharges` على فاتورة داخل المحل، أو نحو قناة داخل المحل.
    invoiceEmpty: "الفاتورة فارغة — ألغِها بدلًا من إغلاقها.",
    chargesNotEditable:
      "هذه الفاتورة مرتبطة بطاولة، فلا رسوم توصيل ولا قناة تُعدَّل عليها. حدّث الشاشة.",
    // D153 — كابتن على فاتورة تيك أواي أو دليفري: الشاشة قديمة.
    captainNotDineIn: "يُسجَّل الويتر المسؤول على فواتير الصالة فقط. حدّث الشاشة.",
    // D153 — اللي اتختار مش ويتر في المحل ده (أو اتشال من الفريق).
    captainNotEligible:
      "هذا الشخص ليس من فريق الصالة — يجب أن يحمل الويتر المسؤول دور الويتر. حدّث الشاشة.",
    // C2 — تطبيق توصيل على فاتورة داخل المحل.
    aggregatorChannelInvalid: "لا يمكن ربط فاتورة داخل المطعم بتطبيق توصيل.",
    partialWriteOff:
      "يُطبّق الشطب على الفاتورة كاملة، لا على جزء منها. أغلق الفاتورة كاملة بطريقة الشطب من شاشة الدفع.",
    invoiceAlreadyTendered:
      "دُفع جزء من هذه الفاتورة بالفعل. حصّل المتبقي من «دفع جزئي»؛ حتى لا يُحسب الدفع الكامل مرتين.",
    // D133 fix — نصيب الزبون اتسوّى قبل كده من طلبه (أو الطلب اترفض).
    paymentShareSettled:
      "تمت تسوية نصيب هذا الضيف بالفعل. حدّث الفاتورة وراجع المبلغ المتبقي.",
    // R1/F3 — رفض المرتجع. كل واحد فيهم ممكن يحصل قدام الكاشير وسط الشغل،
    // فكل واحد ليه جملة بالعربي بدل رسالة السيرفر الإنجليزي.
    refundNotSettled: "لا يمكن إعادة مبلغ من فاتورة لم تُدفع بعد.",
    // الرفض ده ليه سببين مختلفين تماماً، فليه جملتين. الأولى للفاتورة اللي
    // اتقفلت من غير دفع (WRITE_OFF): فعلاً مفيش فلوس دخلت. والتانية لطريقة
    // دفع السيستم لسه ما قررش يرجّع بيها — جملة الأولى لو اتقالت على دي
    // هتبقى كذب: الفاتورة دي اتدفعت.
    refundMethodNotRefundable:
      "أُغلقت هذه الفاتورة بلا دفع، لذلك لا يوجد مبلغ لإعادته.",
    refundMethodNotRefundableOther:
      "دُفعت هذه الفاتورة بطريقة لا يدعم النظام رد الأموال إليها بعد. تواصل مع الإدارة قبل إعادة أي مبلغ بطريقة أخرى.",
    refundNothingToRefund: "قيمة هذه الفاتورة صفر، لذلك لا يوجد مبلغ لإعادته.",
    refundItemNotChargeable:
      "أحد الأصناف المختارة غير محسوب على الفاتورة، لأنه أُلغي أو قُدّم مجانًا، ولذلك لا يوجد مبلغ لإعادته.",
    refundItemAlreadyRefunded:
      "سبق رد أحد الأصناف المختارة. حدّث الشاشة واختر الأصناف التي لم تُرد بعد.",
    refundDuplicateItem:
      "لا يمكن تحديد الصنف نفسه مرتين في عملية الاسترجاع نفسها.",
    // قايمة أسباب المرتجع (والإلغاء والهدية) مقفولة على السيرفر، والشاشة
    // بتمنع ده أصلاً — فالرفض ده بييجي من تبويب قديم أو نسخة قديمة من
    // البرنامج، ولازم يوصل بالعربي زي أي رفض تاني.
    reasonCodeInvalid:
      "السبب المرسل غير مسموح لهذه الحركة. حدّث الشاشة واختر سببًا من الأسباب الظاهرة أمامك.",
    // نفس الحالة: الشاشة بتقفل زرار التأكيد لو «سبب تاني» من غير ملاحظة،
    // فالرفض ده معناه إن اللي بعت الطلب شاشة قديمة.
    voidReasonNoteRequired:
      "عند اختيار «سبب آخر»، يجب كتابة ملاحظة توضح ما حدث.",
    // مسارات المرتجع بتلمس فاتورة وأصناف بالاسم، فأي واحدة منهم ممكن تكون
    // اتغيرت من جهاز تاني بين آخر تحديث للشاشة ودوسة الكاشير.
    invoiceNotFound:
      "هذه الفاتورة غير موجودة. حدّث الشاشة وافتحها من قائمة الفواتير مرة أخرى.",
    invoiceItemNotFound:
      "لم يعد أحد الأصناف المختارة موجودًا على الفاتورة. حدّث الشاشة واختر مرة أخرى.",
    // B117/D5 — الرفض ده كان بيوصل بالجملة اللي فوق، فالجرسون كان بيتقال له إن
    // الصنف مابقاش على الفاتورة ويحدّث الشاشة — والصنف موجود، والمشكلة في
    // حالته مش في وجوده، فالتحديث ما كانش هيفرق. «إعادة التحضير» بتسجّل هدر
    // لمكوّنات اتصرفت فعلًا، فطبق لسه في الطابور مافيش فيه حاجة تتعاد.
    itemNotStarted:
      "لم يبدأ المطبخ تحضير هذا الطبق بعد، لذلك لا يمكن طلب إعادته. انتظر بدء التحضير أو أزله من الفاتورة.",
    // B117/RT-D — يوم الشغل بتاع الدرج ده خلص. الـ Z بيتحسب من الأول كل مرة
    // بيتقرا فيها، فتعديل عدّة درج بعد ما اليوم قفل بيغيّر ورقة اتطبعت
    // واتوقّعت وراحت للبنك.
    recountWindowClosed:
      "انتهى يوم العمل الخاص بهذه الخزينة، لذلك لا يمكن تعديل عدّها. إذا بقي فرق يحتاج إلى تفسير، فتواصل مع صاحب المطعم.",
    // B117/C7+C8 — الرفض ده كان بيتكتب بالإنجليزي على شاشة عربية، وكان بيسمّي
    // الاسم اللي المستخدم لسه كاتبه هو، مش المورد اللي موجود فعلًا. التضارب على
    // الاسم بعد التطبيع (§5.10)، يعني «الجمله» و«الجملة» مورد واحد — فلازم
    // الجملة تقول الاسم اللي هيلاقيه في القايمة.
    supplierNameTaken:
      "يوجد مورد مسجّل بهذا الاسم: «{name}». افتحه وعدّله بدلًا من إنشاء مورد آخر.",
    supplierNameTakenUnnamed:
      "يوجد مورد مسجّل بالاسم نفسه. ابحث عنه في القائمة وعدّله بدلًا من إنشاء مورد آخر.",
    // رقم الأمر بيتولد على الجهاز، فالتضارب ده عمليًا مستحيل — بس لو حصل
    // يبقى فيه حاجة غلط في الجهاز، والكاشير المفروض يعرف إن الحركة ما تمّتش.
    commandOutcomeUncertain:
      "تعذّر التأكد من تنفيذ المحاولة السابقة. راجع الفاتورة قبل إضافة الطلب أو التحصيل مرة أخرى.",
    commandIdConflict:
      "لم تُنفّذ الحركة لأن رقمها مسجّل لحركة أخرى. حدّث الشاشة وحاول مرة أخرى، وأبلغ الدعم إذا تكرر ذلك.",
    // P9: حركة رقمها أقدم من ٧ أيام — ما اتنفذتش دلوقتي، والشاشة لازم تتحدّث.
    commandExpired:
      "هذه الحركة أقدم من ٧ أيام ولم تُنفَّذ مرة أخرى. حدّث الشاشة لترى الوضع الحالي.",
    // P9.3: ساعة الجهاز متأخرة أكتر من ٧ أيام — التحديث مش هيحلّها.
    deviceClockWrong:
      "تاريخ ووقت هذا الجهاز غير مضبوطين. اضبطهما على التوقيت الصحيح من إعدادات الجهاز، ثم حاول مرة أخرى.",
    moneyLedgerRejected:
      "لم تُنفّذ عملية الاسترجاع ولم يتحرك أي مبلغ. حاول مرة أخرى، وأبلغ الدعم إذا تكرر ذلك.",
    // نفس نوع العطل اللي فوق: حسبة توزيع المبلغ على الأصناف رفضت نفسها على
    // السيرفر. مش حاجة الكاشير عملها، والمهم اللي محتاج يعرفه إن الفلوس
    // ما خرجتش.
    refundAllocationBasisInvalid:
      "لم تُنفّذ عملية الاسترجاع ولم يتحرك أي مبلغ؛ فأرقام هذه الفاتورة غير متسقة. أبلغ الدعم برقم الفاتورة.",
    refundAllocationBasisMismatch:
      "لم تُنفّذ عملية الاسترجاع ولم يتحرك أي مبلغ بسبب اختلاف في توزيع المبلغ على الأصناف. أبلغ الدعم برقم الفاتورة.",
    // D30 متعدّلة بـ S14/D49 — حدّ يوم الشغل اتشال، فما بقاش فيه غير سبب
    // واحد للرفض: الفاتورة مش متحصّلة أصلاً، ومفيش طريقة دفع تتصحّح.
    settlementNotCorrectable:
      "لا يمكن تعديل طريقة الدفع إلا لفاتورة مُحصّلة، وهذه الفاتورة لم تُحصّل بعد.",
    // B3 — رفض خاص بـ«مشيت من غير دفع» كتصحيح: مسموح بس في نفس يوم العمل
    // بتاع الفاتورة، عشان الوردية اللي اتقفلت تفضل ثابتة بأرقامها.
    writeOffOutsideBusinessDay:
      "لا يمكن التحويل إلى «غادر بلا دفع» إلا في يوم الفاتورة نفسه. أُغلق هذا اليوم بالفعل.",
    // نفس الكود، بس الحالة معكوسة: تحصيل اترفض عشان الفاتورة متحصّلة خلاص
    // بطريقة تانية. الجملة بتوّدي الكاشير على «تعديل طريقة الدفع» بدل ما
    // يعيد التحصيل ويسيب أثر غلط في مراجعة الوردية.
    settlementAlreadySettledDifferently:
      "حُصّلت هذه الفاتورة بالفعل بطريقة أخرى. لتغيير طريقة الدفع، استخدم «تعديل طريقة الدفع».",
    // نفس الطريقة بس بمبلغ مختلف: المبلغ المسجّل مش بيتغيّر، ومفيش أمر
    // بيغيّره — فالرسالة ماتوجّهش لحاجة مش هتنفع.
    settlementAlreadySettledSame:
      "حُصّلت هذه الفاتورة بالفعل بالطريقة نفسها، ولن يتغير المبلغ المسجّل.",
    tableOccupied: "توجد فاتورة مفتوحة بالفعل على هذه الطاولة.",
    invoiceNotOpen: "هذه الفاتورة غير مفتوحة. حدّث الشاشة.",
    // D172 — «طباعة التذكرة» على تذكرة أُلغيت كل أصنافها: لم يُرسَل شيء.
    ticketNothingToPrint: "لا توجد أصناف في هذه التذكرة لإعادة طباعتها.",
    // D167 — «تراجع» بعد أن تغيّرت حالة الطبق أو انتهت المهلة، أو تراجع عنه جهاز آخر.
    undoUnavailable: "تعذّر التراجع: تغيّرت حالة الطبق أو انتهت مهلة التراجع.",
    // D173 — مدة الإخفاء أمام هدف التسليم، خارج صفحة الإعدادات التي تقولها بجانب الحقل.
    readyAgeOffInvalid:
      "يجب أن تكون مدة إخفاء الأطباق الجاهزة 0 أو أطول من هدف التسليم ({minutes} د)، وبحد أقصى 240 دقيقة.",
    readyAgeOffInvalidUnnamed:
      "يجب أن تكون مدة إخفاء الأطباق الجاهزة 0 أو أطول من هدف التسليم، وبحد أقصى 240 دقيقة.",
    // P7 R2 — ورقة المطبخ تغيّرت بعد قراءتها؛ لم يُطبع شيء.
    ticketPaperChanged: "تغيّرت تذكرة المطبخ منذ قراءتها. حدّث الشاشة ثم أعد المحاولة.",
    // D146 — the discount at the bill's refusals, each in words.
    billDiscountAggregator: "فاتورة منصة توصيل، ولا يمكن الخصم عليها.",
    billDiscountPriceTier: "على هذه الفاتورة فئة سعر، فلا يمكن إضافة خصم عليها.",
    billDiscountOverCap:
      "الخصم أكبر من الحد المسموح: {percent}% من صافي الطعام ({amount}). قلّل الرقم وحاول مرة أخرى.",
    billDiscountOverCapUnnamed:
      "الخصم أكبر من الحد المسموح للمطعم. قلّل الرقم وحاول مرة أخرى.",
    billDiscountBelowPaid:
      "سيصبح الإجمالي بعد الخصم أقل من المدفوع ({paid}). يُعاد المبلغ المدفوع بمرتجع لا بخصم.",
    billDiscountBelowPaidUnnamed:
      "سيصبح الإجمالي بعد الخصم أقل من المدفوع. يُعاد المبلغ المدفوع بمرتجع لا بخصم.",
    billDiscountWholeBill:
      "لا يمكن الخصم على الحساب كله — تُسجَّل الأطباق «على حساب المطعم».",
    billDiscountNothingOff: "لن يخفض هذا الخصم شيئًا من الفاتورة بوضعها الحالي.",
    billDiscountReasonInvalid: "السبب ليس من القائمة. اختر أحد الأسباب المعروضة.",
    billDiscountNoteRequired: "يتطلب «أخرى» كتابة السبب.",
    // D156 — the approval refusals, if one ever reaches a toast.
    approvalRequired: "هذه العملية تحتاج كلمة مرور مدير.",
    approvalInvalid: "لم يُقبل الاعتماد. تأكد من الاسم وكلمة المرور.",
    approvalLocked: "توقف الاعتماد بهذا الاسم مؤقتًا بعد عدة محاولات خاطئة.",
    approvalPasswordChangeRequired: "يجب على المدير تغيير كلمة المرور المؤقتة أولًا.",
    shiftRequired: "لا توجد خزينة مفتوحة. افتح خزينة أولًا لإتمام التحصيل.",
    // نفس الرفض، بس لواحد مش معاه صلاحية فتح الوردية: الجملة اللي فوق دي
    // تبقى تعليمة ميّتة بالنسباله، فبتتقاله يكلّم مين بدل ما يدوّر على شاشة
    // مش هيقدر يفتحها.
    shiftRequiredAskManager:
      "لا توجد خزينة مفتوحة. اطلب من شخص لديه صلاحية إدارة الخزائن أن يفتح خزينة أولًا.",
    shiftAlreadyOpen: "توجد خزينة مفتوحة بالفعل. حدّث الشاشة.",
    shiftNotOpen: "لا توجد خزينة مفتوحة لإغلاقها أو لتسجيل حركة عليها.",
    shiftTrackingDisabled:
      "نظام الخزائن معطّل في الإعدادات. فعّله قبل فتح خزينة.",
    /*
     * B118 — رفوض الأدراج الجديدة كلها كانت بتوصل بالإنجليزي على شاشة عربية:
     * الأكواد دي مالهاش سطر في الـ switch، فكانت بتقع على رسالة السيرفر، وهي
     * مكتوبة للّوجات مش للكاشير. نفس قاعدة الملف اللي فوق — أي رفض بيقابل حد
     * وهو واقف على الكاشير في نص الشغل لازم يوصله بلغته.
     */
    shiftAlreadyRecounted:
      "أُعيد عدّ هذه الخزينة مرة واحدة بالفعل، ولا يُسمح بعدّ ثالث. إذا بقي فرق يحتاج إلى تفسير، فأضف ملاحظة إلى الخزينة أو تواصل مع المدير.",
    /*
     * الرفض ده ليه سببين على السيرفر (`recountShift`): درج اتقفل قبل ما العدّ
     * المغلق يبقى موجود من الأساس، أو حد بيحاول يعدّ درج مش هو اللي قفله.
     * رسالة السيرفر بتقول السبب الأول في الحالتين، فمدير بيعدّ درج اتقفل من
     * خمس دقايق كان بيتقال له إن الدرج أقدم من العدّ المغلق — يعني بلاغ عن
     * عطل مش موجود. لحد ما السيرفر ينشر `reason` جنب الكود (زي
     * `RESTAURANT_SUBSTITUTION_INVALID`)، الجملة بتقول الشرطين الاتنين بدل ما
     * تسمّي واحد فيهم غلط.
     */
    recountUnavailable:
      "لا يمكن إعادة عدّ هذه الخزينة: إما أنها أُغلقت بلا عدّ نهائي، أو أنك لست من أغلقها؛ فإعادة العد مخصصة لمن أغلق الخزينة. ويمكن للمدير تسجيل عدّ مستقل بجانب عدّ الكاشير.",
    /*
     * والسيرفر بقى بينشر `reason` جنب الكود، فالجملتين دول بيقولوا السبب
     * الحقيقي بدل الشرطين. الجملة اللي فوق فضلت زي ما هي عشان ردّ قديم من
     * بيلد أقدم من `reason` ما يوصلش التيل بالإنجليزي الخام.
     */
    recountNoFirstCount:
      "أُغلقت هذه الخزينة قبل تسجيل العدّ النهائي، لذلك لا توجد قراءة أولى للمقارنة ولا يمكن إعادة العد. إذا بقي فرق يحتاج إلى تفسير، فأضف ملاحظة إلى الخزينة.",
    recountNotYourDrawer:
      "لم تُغلق هذه الخزينة، وإعادة العد مخصصة لمن أغلقها. إذا أردت تسجيل الفرق بنفسك، فيمكن للمدير تسجيل عدّ مستقل بجانب عدّ الكاشير.",
    shiftAlreadyManagerCounted:
      "سجّل مدير عدًّا لهذه الخزينة بالفعل، ولا يمكن تسجيل عدّ المدير إلا مرة واحدة.",
    // نفس شكل الرفض اللي فوق: كود واحد لحالتين، والسيرفر نفسه بيقول الاتنين في
    // رسالته — فالجملة العربي بتمشي وراه بدل ما تختار واحدة.
    managerCountUnavailable:
      "لا يمكن عدّ هذه الخزينة بصفة مدير: إما أنها ما زالت مفتوحة، أو أنك أغلقتها بنفسك؛ ومن أغلق الخزينة يستخدم «إعادة العد» بدلًا من ذلك.",
    shiftNoteEmpty: "اكتب الملاحظة أولًا؛ فلا يمكن تسجيل ملاحظة فارغة.",
    /* B118/QA4 — الرفض ده كان بيقول «ناقصك صلاحية»، وهي مش ناقصها حاجة:
       نفس الشخص بيكتب على درجه هو بنفس الصلاحيات. اللي اتكسر هنا قاعدة
       «الدرج ده بتاع مين»، فالجملة بتقولها وبتدّي المخرجين الاتنين. */
    shiftNoteNotYourDrawer:
      "لم تُغلق هذه الخزينة، لذلك لا يمكنك إضافة ملاحظات إليها. اكتب الملاحظة على خزينة أغلقتها أنت، أو اطلب من مراجع الخزائن كتابتها.",
    /*
     * أدراج D95/Unit B. الأربع رفوض دي بتيجي ومعاها اسم الصندوق، والسيرفر
     * بيبعته بالذات عشان الشاشة تقدر تقوله: تيل عليه تلات أدراج مايتعملش فيه
     * حاجة بجملة «مافيش درج مفتوح». ولو الاسم ما وصلش لأي سبب، الجملة بتقول
     * «الدرج ده» بدل ما تخترع اسم.
     */
    drawerAlreadyOpen:
      "«{label}» مفتوح بالفعل مع شخص آخر. تسلّمه منه أو افتح خزينة باسم آخر.",
    drawerAlreadyOpenUnnamed:
      "الخزينة مفتوحة بالفعل مع شخص آخر. تسلّمها منه أو افتح خزينة باسم آخر.",
    drawerIsYours:
      "«{label}» هي خزينتك. أغلقها بعدّ النقد؛ فالاستلام مخصص لشخص آخر.",
    drawerIsYoursUnnamed:
      "هذه خزينتك. أغلقها بعدّ النقد؛ فالاستلام مخصص لشخص آخر.",
    drawerNotOpen:
      "لا توجد خزينة مفتوحة باسم «{label}» الآن؛ ربما أُغلقت قبل قليل. حدّث الشاشة واقرأ القائمة مرة أخرى.",
    drawerNotOpenUnnamed:
      "لا توجد خزينة مفتوحة على هذا الصندوق الآن؛ ربما أُغلقت قبل قليل. حدّث الشاشة واقرأ القائمة مرة أخرى.",
    drawerAlreadyTakenOver:
      "تسلّم شخص آخر «{label}» قبلك. حدّث الشاشة واقرأ قائمة الخزائن مرة أخرى.",
    drawerAlreadyTakenOverUnnamed:
      "تسلّم شخص آخر هذه الخزينة قبلك. حدّث الشاشة واقرأ قائمة الخزائن مرة أخرى.",
    lastDrawerHasOpenInvoices:
      "لا يمكن إغلاق آخر خزينة مفتوحة وما زالت هناك فواتير مفتوحة. حصّلها أو ألغِها أولًا، ثم أغلق الخزينة.",
    // B117/Q6 — بعد ما الفاتورة تتقفل، عدد الضيوف بيتصحّح من رتبة الإلغاء زي
    // ما السيرفر بيقول بالظبط. الجملة بتقول لمين يروح، مش بس إن الحركة اترفضت.
    guestCountLocked:
      "بعد إغلاق الفاتورة، لا يعدّل عدد الضيوف إلا شخص لديه صلاحية إلغاء الأصناف، مثل الكاشير أو المدير. اطلب منه تعديل العدد.",
    // رسالة الجارد نفسها («Missing required permissions») بتوصل الشاشة زي ما
    // هي، وده رفض عادي بيقابل كاشير مش معاه رتبة الأدراج.
    permissionDenied:
      "تحتاج هذه الحركة إلى صلاحية غير متاحة لك. اطلب من صاحب المطعم منحك الصلاحية، أو اطلب من شخص يملكها تنفيذ الحركة.",
    /*
     * حركات الكاش اللي بتخرج وتدخل الدرج (Unit E). الشاشة بتمنع الخمس حالات
     * دي قبل ما تبعت — القايمة بتعرض التصنيفات المسموحة بس، وبتشيل اسمك انت من
     * قايمة الأسماء — فالرفض ده معناه تبويب قديم أو حد ساب المحل بين ما القايمة
     * اتقرت وما الكاشير دوس. برضه بيقع على شاشة عربية، وبالقاعدة اللي فوق
     * «عمليًا مش هيحصل» مش سبب يخليه بالإنجليزي.
     */
    movementCategoryInvalid:
      "لا يتوافق هذا التصنيف مع نوع الحركة. حدّث الشاشة واختر تصنيفًا من التصنيفات الظاهرة أمامك.",
    movementCounterpartyRequired:
      "يجب تحديد من تسلّم المبلغ في هذه الحركة. اختر الاسم أولًا.",
    movementCounterpartyNotAllowed:
      "لا تسمح هذه الحركة بتحديد شخص آخر من الفريق. أزل الاسم وحاول مرة أخرى.",
    movementCounterpartyIsYou:
      "اختر شخصًا آخر؛ فلا يمكنك تسجيل تسليم المبلغ لنفسك.",
    movementCounterpartyUnknown:
      "هذا الاسم ليس ضمن فريق المطعم. حدّث الشاشة واختر من القائمة مرة أخرى.",
    // D40 — the export's row ceiling is a REFUSAL, not a silent truncation.
    exportTooLarge:
      "سيكون الملف كبيرًا جدًا. قلّص نطاق التاريخ في عوامل التصفية وحاول مرة أخرى.",
    // D47/B4 — the till's picker mirrors `isActiveNow` and disables an
    // out-of-window tier, but the open command is the real authority (clock
    // skew, a stale read), so a refusal still needs a sentence a waiter reads.
    priceTierNotFound: "فئة السعر غير موجودة. حدّث الشاشة وحاول مرة أخرى.",
    priceTierInactive: "فئة السعر معطّلة الآن.",
    priceTierOutsideWindow: "فئة السعر خارج وقت تفعيلها الآن.",
    // D55/D56 — `RESTAURANT_ADDON_OVERRIDE_INVALID` covers five refusals under
    // one code, and the server's own prose is English. Without a case here the
    // mapper fell through to `failure.message` and put that English in front of
    // an Arabic owner. `addonOverrideInvalid` is the catch-all: a reason this
    // build has never heard of still arrives translated.
    addonOverrideGroupNotAttached:
      "هذه المجموعة غير مرتبطة بهذا الصنف. حدّث الشاشة وحاول مرة أخرى.",
    addonOverrideAddonNotInGroup:
      "هذا الاختيار لا ينتمي إلى هذه المجموعة. حدّث الشاشة وحاول مرة أخرى.",
    // Raised for a repeated OPTION (the override set) and for a repeated
    // INGREDIENT (the D56 costing save), so the sentence names neither.
    addonOverrideDuplicateRow:
      "يوجد سطر مكرر. أزل السطر الزائد وحاول مرة أخرى.",
    // D61–D63 — the swap refusals, in the waiter's language. The server's
    // `message` is English prose, and this refusal lands at a TABLE with a
    // guest waiting: an English sentence there is the same defect the addon
    // block above exists to fix. The two collision reasons say what to DO,
    // because the waiter can fix both from the same screen.
    swapSameIngredient: "لا يمكن استبدال المكوّن بنفسه. اختر مكوّنًا آخر.",
    swapDuplicateRemoval:
      "أُزيل هذا المكوّن بالفعل، إما من تبديل آخر أو من اختيار مضاف إلى الصنف. أزله من موضع واحد فقط.",
    swapChainedSwap:
      "يوجد تبديل يضيف مكوّنًا يزيله تبديل آخر. اجعله تبديلًا واحدًا من المكوّن الأصلي إلى الأخير.",
    swapIngredientNotFound:
      "هذا المكوّن غير موجود ضمن مكوّنات المطعم. حدّث الشاشة وحاول مرة أخرى.",
    swapNotOnRecipe:
      "هذا المكوّن غير موجود في وصفة الصنف، لذلك لا يوجد شيء لإزالته.",
    swapUnitMismatch:
      "لا يستخدم المكوّنان وحدة القياس نفسها، ولا يوجد تحويل بينهما. اختر بديلًا بالوحدة نفسها.",
    swapNotSupportedOnCombo: "لا يمكن تبديل مكوّنات الوجبات المركّبة.",
    // B19: الحد اللي المالك رسمه — الطبق يتعدّل قبل ما الشيف يبدأ فيه بس،
    // عشان بعد كده المكوّنات بتكون اتصرفت فعلاً والأكل هيترمي من غير ما
    // حاجة تسجّله. الجملة بتقول يعمل إيه، مش بس إن مينفعش.
    swapPlateAlreadyStarted:
      "بدأ الطاهي تحضير هذا الطبق، لذلك لا يمكن تعديله. إذا لزم تغييره، فألغِ الطبق واطلبه من البداية.",
    swapPlateNotAmendable:
      "لا يمكن تعديل هذا الطبق من هنا؛ فإما أنه طلب من الضيف لم يُقبل بعد، أو أنه أُلغي.",
    // The catch-all, on the same rule as `addonOverrideInvalid`: a reason
    // added server-side after this build ships still arrives translated.
    swapInvalid: "هذا التبديل غير صالح. حدّث الشاشة وحاول مرة أخرى.",
    // «الإعداد» is software-configuration MSA and «من غير اختيارات كفاية» is
    // an English calque; neither belongs on a menu screen. The named half is
    // used wherever the call site can turn the server's `subjectId` into a
    // dish — a group-level edit strands a dish that is NOT on screen, and
    // «الصنف» left the owner a menu to search by hand.
    addonOverrideRequiredUnsatisfiable:
      "هذه المجموعة إلزامية، وسيترك هذا التعديل الصنف بخيارات أقل مما تتطلبه المجموعة.",
    addonOverrideRequiredUnsatisfiableNamed:
      "هذه المجموعة إلزامية، وسيترك هذا التعديل «{name}» بخيارات أقل مما تتطلبه المجموعة.",
    addonOverrideIngredientNotFound:
      "هذا المكوّن غير موجود ضمن مكوّنات المطعم. حدّث الشاشة وحاول مرة أخرى.",
    addonOverrideInvalid:
      "هذا التعديل غير مقبول. راجع الأسعار والاختيارات وحاول مرة أخرى.",
    // F24 (§5.12) — the customer's request for a bag. Both reachable from
    // the till's strip, so both in words rather than the backend's English.
    onlineOrderNotRequested:
      "هذا الطلب لم يعد بانتظار الرد — قُبل من جهاز آخر، أو رُفض، أو انتهى وقته، أو ألغاه العميل. حدّث الشاشة.",
    onlineOrderingOff:
      "الطلب من صفحة المطعم متوقف لهذا المطعم. يُشغَّل من الإعدادات ← الطلب عبر الإنترنت.",
    menuItemUnavailable:
      "صنف في هذا الطلب لم يعد متاحًا في القائمة (نفد أو حُذف). ارفض الطلب أو اتصل بالعميل — لم تُفتح فاتورة.",
  },
  perm: {
    area_POS: "شاشة البيع",
    area_INVOICE_HISTORY: "الفواتير السابقة",
    area_BILL_ADJUST: "تعديل الفاتورة",
    area_MENU: "قائمة الطعام",
    area_FLOOR: "الطاولات",
    area_COSTING: "المخزون والتكلفة",
    // D147: استلام البضاعة من المورد — من غير ما يشوف التكلفة.
    area_DELIVERY: "استلام البضائع",
    area_PRICE_TIER: "أسعار المناسبات",
    area_RESTAURANT_SETTINGS: "إعدادات المطعم",
    area_KITCHEN: "المطبخ",
    area_SHIFT: "الخزائن",
    area_PRICE_OVERRIDE: "تعديل السعر يدويًا",
    // R9: رد فلوس فاتورة اتحصّلت. صف لوحده لأنه مش بيتوهب لأي دور جاهز.
    area_REFUND: "استرجاع النقود",
    // D156: كلمة مرور المدير على إلغاء أمين الصندوق وخصمه.
    area_EXCEPTION_APPROVE: "اعتماد الاستثناءات",
    area_SUBSTITUTE: "تبديل المكوّنات",
    // Driver backend B1: four separate rows, not one ladder.
    area_DRIVER: "عمل الطيار",
    area_DISPATCH: "توزيع التوصيلات",
    area_DRIVER_DOCUMENTS: "مستندات الطيارين",
    area_DRIVER_INVITE: "دعوة الطيارين",
    area_CUSTOMERS: "العملاء",
    area_REPORTS: "التقارير",
    // شكل المتجر أونلاين — بيتظبط من بوابة البائع مش من هنا، عشان كده بيتجمّع
    // جوه قسم "المتجر أونلاين".
    area_DESIGN: "مظهر المتجر على الإنترنت",
    // Defensive: any future RESTAURANT_* code with no spec of its own lands in
    // the prefix-derived "RESTAURANT" bucket. Labelling it keeps the row
    // readable instead of showing a raw key.
    area_RESTAURANT: "المطعم — صلاحيات أخرى",
    area_PRODUCT: "المنتجات",
    area_CATEGORY: "الأقسام",
    area_ORDER: "الطلبات الإلكترونية",
    area_MEMBER: "الفريق",
    area_ROLE: "الأدوار",
    area_STORE: "بيانات المتجر",
    area_COUPON: "الكوبونات",
    area_REVIEW: "التقييمات",
    area_SHIPPING: "الشحن",
    area_PAYMENT: "الدفع",

    tierNone: "لا يوجد",
    tierView: "مشاهدة فقط",
    tierManage: "إدارة",
    tierFull: "كامل",
    tierCustom: "مخصص",
    tierNoneHelp: "لن يرى هذا الجزء مطلقًا.",
    tierViewHelp: "يمكنه العرض فقط، من دون تغيير شيء.",
    tierManageHelp: "يمكنه العرض والإضافة والتعديل، من دون حذف.",
    tierFullHelp: "كل شيء في هذا الجزء، بما في ذلك الحذف.",
    tierCustomHelp:
      "تشكيلة مخصصة يدويًا. اختر مستوى من الأعلى لإعادتها إلى إعداد بسيط.",

    p_RESTAURANT_POS_USE: "فتح شاشة البيع",
    p_RESTAURANT_KITCHEN_VIEW: "شاشة المطبخ",
    p_RESTAURANT_ITEM_FIRE: "إرسال الأصناف للمطبخ",
    p_RESTAURANT_ITEM_PREPARE: "بدء تحضير الطعام وتجهيزه",
    p_RESTAURANT_ITEM_SERVE: "تقديم الأصناف",
    p_RESTAURANT_KITCHEN_PRINT: "طباعة تذكرة المطبخ",
    p_RESTAURANT_REPORT_VIEW: "تقرير المصاريف والدخل",
    p_RESTAURANT_REPORT_OPERATIONS_VIEW: "تقارير الوردية والموظفين",
    p_RESTAURANT_INVOICE_OPEN: "فتح فاتورة لطاولة",
    p_RESTAURANT_INVOICE_APPEND: "إضافة أصناف للفاتورة",
    p_RESTAURANT_INVOICE_REMAKE: "طلب إعادة تحضير صنف",
    p_RESTAURANT_INVOICE_VOID: "إلغاء صنف من الفاتورة",
    p_RESTAURANT_INVOICE_CLOSE: "إغلاق الفاتورة",
    p_RESTAURANT_INVOICE_SETTLE: "تحصيل الفاتورة",
    p_RESTAURANT_MENU_VIEW: "مشاهدة قائمة الطعام",
    p_RESTAURANT_MENU_MANAGE: "تعديل قائمة الطعام",
    p_RESTAURANT_FLOOR_MANAGE: "إدارة الصالات والطاولات",
    p_RESTAURANT_COSTING_MANAGE: "إدارة المخزون والتكلفة",
    // D147: يسجّل ورقة المورد بسطورها ويدفعها من درجه — من غير متوسطات ولا وصفات.
    p_RESTAURANT_DELIVERY_RECORD: "استلام البضائع من المورد",
    p_RESTAURANT_TIER_MANAGE: "إدارة أسعار المناسبات",
    p_RESTAURANT_SETTINGS_MANAGE: "تعديل إعدادات المطعم",
    p_RESTAURANT_SHIFT_MANAGE: "فتح الخزائن وإغلاقها",
    // D46/S9: صلاحية كتابة سعر مختلف على الصنف وقت البيع.
    p_RESTAURANT_INVOICE_PRICE_OVERRIDE:
      "كتابة سعر مختلف للصنف في شاشة البيع",
    // D61/D63: الجرسون بيبدّل مكوّن بمكوّن، والسيرفر هو اللي بيحسب الفرق —
    // مفيش رقم بيتكتب بالإيد، وده اللي بيخلّيها صلاحية تانية غير اللي فوقها.
    p_RESTAURANT_INVOICE_SUBSTITUTE: "تبديل مكوّنات الصنف في شاشة البيع",
    // 86'ing (D32): قرار خدمة بيتاخد على الكاشير، مش تعديل في المنيو.
    p_RESTAURANT_MENU_AVAILABILITY: "تحديد الصنف النافد (86)",
    // R9: أخطر صلاحية في المحل — الفلوس بتخرج والدرج بيفضل مظبوط.
    p_RESTAURANT_INVOICE_REFUND: "رد نقود فاتورة مُحصّلة",
    p_RESTAURANT_EXCEPTION_APPROVE: "اعتماد إلغاء الكاشير وخصمه بكلمة المرور",
    p_RESTAURANT_DRIVER_WORK: "تنفيذ توصيلاته من تطبيق الطيار",
    p_RESTAURANT_DISPATCH_MANAGE: "إسناد التوصيلات إلى الطيارين ومتابعتها",
    p_RESTAURANT_DISPATCH_CASH_CONFIRM: "تأكيد استلام النقود من الطيار",
    p_RESTAURANT_DRIVER_DOCUMENTS_VIEW: "الاطلاع على مستندات هوية الطيارين",
    p_RESTAURANT_DRIVER_INVITE: "دعوة طيار جديد",
    p_RESTAURANT_CUSTOMER_VIEW: "عرض قائمة العملاء وسجل طلباتهم",
    p_RESTAURANT_CUSTOMER_BLOCK: "حظر عميل من الطلب عبر صفحة المطعم",
    p_STORE_VIEW: "مشاهدة بيانات المتجر",
    p_STORE_EDIT: "تعديل بيانات المتجر",
    p_DESIGN_VIEW: "عرض مظهر المتجر على الإنترنت",
    p_DESIGN_EDIT: "تعديل مسودة شكل المتجر",
    p_DESIGN_PUBLISH: "نشر الشكل للمشترين",
    p_PRODUCT_VIEW: "مشاهدة المنتجات",
    p_PRODUCT_CREATE: "إضافة منتج",
    p_PRODUCT_EDIT: "تعديل منتج",
    p_PRODUCT_DELETE: "حذف منتج",
    p_CATEGORY_VIEW: "مشاهدة الأقسام",
    p_CATEGORY_CREATE: "إضافة قسم",
    p_CATEGORY_EDIT: "تعديل قسم",
    p_CATEGORY_DELETE: "حذف قسم",
    p_ORDER_VIEW: "مشاهدة الطلبات",
    p_ORDER_EDIT: "تعديل الطلبات",
    p_ORDER_FULFILL: "تجهيز الطلبات وتسليمها",
    p_COUPON_VIEW: "مشاهدة الكوبونات",
    p_COUPON_CREATE: "إضافة كوبون",
    p_COUPON_EDIT: "تعديل كوبون",
    p_COUPON_DELETE: "حذف كوبون",
    p_REVIEW_VIEW: "مشاهدة التقييمات",
    p_REVIEW_MODERATE: "مراجعة التقييمات",
    p_SHIPPING_VIEW: "مشاهدة إعدادات الشحن",
    p_SHIPPING_EDIT: "تعديل إعدادات الشحن",
    p_PAYMENT_VIEW: "مشاهدة إعدادات الدفع",
    p_PAYMENT_EDIT: "تعديل إعدادات الدفع",
    p_MEMBER_VIEW: "مشاهدة الفريق",
    p_MEMBER_INVITE: "دعوة عضو إلى الفريق",
    p_MEMBER_REMOVE: "إزالة عضو من الفريق",
    p_MEMBER_EDIT_ROLES: "تغيير أدوار الفريق",
    p_ROLE_VIEW: "مشاهدة الأدوار",
    p_ROLE_CREATE: "إنشاء دور",
    p_ROLE_EDIT: "تعديل دور",
    p_ROLE_DELETE: "حذف دور",
    p_RESTAURANT_INVOICE_HISTORY_VIEW: "رؤية فواتير الأيام السابقة",
    p_RESTAURANT_INVOICE_PAST_EDIT: "العمل على فاتورة من يوم سابق",
    p_RESTAURANT_INVOICE_CANCEL: "إلغاء الفاتورة كلها",
    p_RESTAURANT_INVOICE_REOPEN: "إعادة فتح فاتورة لم تُدفع",
    p_RESTAURANT_INVOICE_CHARGES_EDIT: "تعديل الخدمة ورسوم التوصيل",
    p_RESTAURANT_INVOICE_COMP: "ضيافة صنف",
    p_RESTAURANT_INVOICE_DISCOUNT: "خصم على الفاتورة",
    p_RESTAURANT_INVOICE_PAYMENT_CORRECT: "تصحيح طريقة الدفع",
    p_RESTAURANT_INVOICE_WRITE_OFF: "شطب فاتورة لم تُدفع",
    // The role editor's rows, groups and sections (2026-11-02, `role-acts.ts`).
    act_pos: "فتح شاشة البيع ورؤية القائمة",
    actHint_pos: "وفواتير اليوم، مفتوحة ومغلقة",
    act_open: "فتح فاتورة لطاولة أو تيك أواي",
    actHint_open: "ويشمل اليوم قبول طلبات الأونلاين وإيقاف استقبالها",
    act_append: "إضافة أصناف على الفاتورة المفتوحة",
    actHint_append: "وعدد الضيوف والعميل ونقلها لطاولة أخرى",
    act_fire: "إرسال الأصناف للمطبخ",
    act_serve: "تسجيل تقديم الطبق للطاولة",
    act_remake: "طلب إعادة عمل طبق",
    act_subst: "تبديل مكوّن في طبق",
    actHint_subst: "السعر يحسبه النظام، والمخزون يتغير",
    act_avail: "إيقاف صنف نفد اليوم",
    actHint_avail: "يختفي من شاشة البيع حتى نهاية اليوم",
    act_void: "إلغاء صنف من الفاتورة",
    actHint_void: "بدون «اعتماد زميل» يطلب كلمة مرور مدير",
    act_comp: "ضيافة صنف على حساب المطعم",
    actHint_comp: "بدون «اعتماد زميل» يطلب كلمة مرور مدير",
    act_discount: "خصم على الفاتورة كلها",
    actHint_discount: "بدون «اعتماد زميل» يطلب كلمة مرور مدير",
    act_chargesEdit: "تعديل الخدمة ورسوم التوصيل",
    actHint_chargesEdit: "وتحويل الطلب بين تيك أواي وتوصيل",
    act_cancel: "إلغاء الفاتورة كلها",
    actHint_cancel: "فاتورة لم يُحسب عليها شيء، فُتحت بالخطأ",
    act_reopen: "إعادة فتح فاتورة طُبع حسابها ولم تُدفع",
    actHint_reopen: "العميل طلب حاجة زيادة · الفاتورة المدفوعة لا تُفتح",
    act_payFix: "تصحيح طريقة دفع فاتورة مدفوعة",
    actHint_payFix: "مثلاً من نقدي إلى بطاقة",
    act_writeOff: "شطب فاتورة لم تُدفع",
    actHint_writeOff: "الضيف خرج دون دفع",
    act_override: "كتابة سعر صنف يدوياً",
    act_approve: "اعتماد إلغاء أو ضيافة أو خصم لزميل",
    actHint_approve: "يكتب كلمة مروره على جهاز الزميل، ولا يحتاج هو موافقة",
    act_close: "طباعة الحساب وإغلاق الفاتورة",
    actHint_close: "وتحديد كابتن الطاولة",
    act_settle: "تحصيل الدفع (نقدي، بطاقة، إنستاباي)",
    actHint_settle: "كامل أو مقسوم · النقدي يدخل خزينته المفتوحة",
    act_refund: "استرجاع مبلغ فاتورة مدفوعة",
    actHint_refund: "أخطر صلاحية في المطعم: لا تُعطى إلا لشخص بعينه",
    act_historyView: "رؤية فواتير الأيام السابقة",
    actHint_historyView: "الفواتير المدفوعة والملغاة قبل يوم العمل الحالي · فواتير اليوم والفواتير التي لم تُدفع تظهر دائماً",
    act_pastEdit: "العمل على فاتورة من يوم سابق",
    actHint_pastEdit: "إغلاقها أو تحصيلها أو إلغاء صنف منها أو استرجاعها",
    act_shift: "فتح خزينته وإغلاقها وعدّ النقدية",
    actHint_shift: "ويشمل اليوم إدخال وإخراج نقدية، واستلام خزينة زميل، ورؤية خزائنه السابقة · خزائن الآخرين مع «تقارير التشغيل»",
    act_dispatch: "توزيع الطلبات على الطيارين",
    act_driverCash: "استلام نقدية الطيار",
    actHint_driverCash: "تُحسب في خزينته",
    act_driverInvite: "دعوة طيار جديد",
    act_driverDocs: "رؤية مستندات الطيارين",
    actHint_driverDocs: "البطاقة والرخصة",
    act_driverWork: "العمل كطيار من تطبيق الطيار",
    act_kitchenView: "فتح شاشة المطبخ",
    actHint_kitchenView: "يمكن قصرها على محطات معينة",
    act_prepare: "تحريك الأطباق: قيد التجهيز ← جاهز",
    act_kitchenPrint: "طباعة تذكرة المطبخ",
    act_menuView: "رؤية قائمة الطعام",
    actHint_menuView: "تحتاجها شاشة البيع",
    act_menuManage: "تعديل القائمة والأصناف والإضافات",
    act_categories: "إضافة أقسام القائمة وتعديلها",
    act_tiers: "قوائم الأسعار والمناسبات",
    act_floor: "تعديل مخطط الصالة والطاولات",
    act_receive: "استلام بضاعة من مورد",
    actHint_receive: "بدون رؤية التكاليف والوصفات",
    act_costing: "الخامات والوصفات والجرد والمشتريات",
    actHint_costing: "يرى متوسط التكلفة · ويشمل تسجيل الدفع للموردين",
    act_operations: "تقارير التشغيل",
    actHint_operations: "الإلغاءات حسب الموظف، أوقات الذروة، وكل الخزائن وحركة الخزينة",
    act_profit: "الأرباح والتكاليف والمحاسب",
    actHint_profit: "هوامش الأصناف ومدفوعات الموردين",
    act_customers: "قائمة العملاء",
    act_blockCustomer: "حظر عميل",
    act_settings: "إعدادات المطعم",
    actHint_settings: "الضرائب، الخدمة، المنصات، الطابعات",
    act_teamView: "رؤية الفريق",
    act_teamManage: "دعوة الأعضاء وإزالتهم وتغيير أدوارهم",
    act_rolesView: "رؤية الأدوار",
    act_rolesManage: "إنشاء الأدوار وتعديلها وحذفها",
    actHint_rolesManage: "لا يمنح أكثر مما يملكه هو",
    grp_order: "الطلبات",
    grpSub_order: "شاشة البيع، الفاتورة المفتوحة، المطبخ والتقديم",
    grp_edit: "تغيير مبلغ الفاتورة",
    grpSub_edit: "قبل الدفع: كل ما يقلل المبلغ أو يغيّر السعر",
    grp_pay: "الحساب والدفع",
    grpSub_pay: "طباعة الحساب، التحصيل، والاسترجاع",
    grp_history: "الفواتير السابقة",
    grpSub_history: "ما قبل يوم العمل الحالي",
    grp_drawer: "الخزينة",
    grpSub_drawer: "خزينته هو فقط",
    grp_delivery: "التوصيل والطيارون",
    grpSub_delivery: "توزيع الطلبات، الطيارون ونقديتهم",
    grp_kitchen: "شاشة المطبخ",
    grpSub_kitchen: "تجهيز الأطباق وطباعة التذاكر",
    grp_menu: "القائمة والصالة",
    grpSub_menu: "الأصناف والأسعار ومخطط الطاولات",
    grp_stock: "المخزون والموردون",
    grpSub_stock: "الاستلام والخامات والجرد",
    grp_reports: "التقارير والعملاء",
    grpSub_reports: "التشغيل، الأرباح، قائمة العملاء",
    grp_admin: "الإعدادات والفريق",
    grpSub_admin: "الإعدادات والفريق والأدوار",
    sec_bill: "الفاتورة: من أول طلب لآخر دفعة",
    sec_shift: "الوردية",
    sec_admin: "إدارة المطعم",
  },
  /**
   * Invoice history — the first surface in this app that can look at a day
   * that is already over.
   *
   * Its own group rather than an addition to `manage`, because unlike every
   * other back-office screen it is NOT owner-only: the endpoint behind it is
   * `RESTAURANT_POS_USE`, which waiter and cashier both hold. The status words
   * are NOT redefined here — `pos.status*` already carries them, written once
   * for the till, and a second Arabic word for "settled" that drifted from the
   * first would be worse than no translation.
   */
  invoices: {
    pickTitle: "اختر فاتورة",
    pickBody: "اضغط على فاتورة في القائمة لعرض تفاصيلها هنا.",
    statInvoicesSub: "{count} متحصّلة",
    statSales: "المبيعات",
    statSalesSub:
      "المُحصّل فقط، من دون الفواتير المغلقة بلا دفع، وشامل أي ضريبة أو خدمة مُحصّلة",
    statAverage: "متوسط الفاتورة",
    statCancelledNone: "لا يوجد إلغاء",
    /*
     * PAPER (round 3) — خانات الإجابة في شاشة الفواتير، زرار التصفية، عمود
     * الوقت، وسطر الدفع تحت الإيصال في الدرج الجانبي. كل الأرقام دي للصفحة
     * المعروضة بس، زي باقي الخانات.
     */
    paperStatOpen: "لم تُحصَّل بعد",
    paperStatCancelledOne: "فاتورة واحدة ملغية · {amount}",
    paperStatCancelledTwo: "فاتورتين ملغيتين · {amount}",
    paperStatCancelledFew: "{count} فواتير ملغية · {amount}",
    paperStatCancelled: "{count} فاتورة ملغية · {amount}",
    paperFilter: "تصفية",
    tableTime: "الوقت",
    paperPaidBy: "دُفعت: {method}",
    paperDue: "المطلوب {amount}",
    basisTermOpen: "لم تُحصَّل بعد",
    basisOpenNote:
      "الفواتير المفتوحة أو المنتظرة للدفع في هذه الصفحة فقط، بإجمالياتها الحالية. هذه مبالغ لم تُحصَّل بعد، وليست مبيعات.",

    /*
     * ── الشاشة دي بقت صفحة تقرير (UX-round D41 / ruling F20 / Slice 9) ───────────────
     *
     * كان فيها التصدير ومافيهاش باب — وعشان كده تحذير النطاق كان عايم على
     * الشاشة: مكانش فيه مكان تاني يروح له. دلوقتي فيه باب وورقة أساس.
     *
     * وتحت كل تايل حقيقة واحدة بس: جملة «المتحصّل بس، من غير اللي مشي بدون
     * دفع، وشامل أي ضريبة أو خدمة اتحصّلت» جملة أساس، ومكانها ورا الباب مش
     * تحت الرقم.
     */
    cardTitle: "الفواتير",
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذه الأرقام",
    basisChipPage: "هذه الصفحة",
    basisTermScope: "نطاق الأرقام",
    basisTermSales: "المبيعات",
    basisTermAverage: "متوسط الفاتورة",
    basisAverageNote:
      "المتوسط = المبيعات ÷ عدد الفواتير المحصّلة في هذه الصفحة، مقرّبًا إلى أقرب جنيه.",
    // المرتجع بقى تايل بنفسه بدل سطر تاني تحت المبيعات — التايل بيقول حقيقة
    // واحدة، والفلوس اللي رجعت للزبون رقم لوحده مش حاشية على رقم تاني.
    statRefunded: "أُعيد إلى العميل",
    statRefundedFooter: "مخصوم من المبيعات أعلاه",
    // D31 — بيظهر تحت خانة المبيعات لما يكون في فواتير مشيت بدون دفع، عشان
    // الفرق بين اللي اتباع واللي اتاكل يبقى مكتوب مش مستنتج.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    statWriteOffsOne: "فاتورة واحدة بلا دفع · {amount}",
    statWriteOffsTwo: "فاتورتان بلا دفع · {amount}",
    statWriteOffsFew: "{count} فواتير بلا دفع · {amount}",
    statWriteOffs: "{count} فاتورة بلا دفع · {amount}",
    // R12 — المرتجع فلوس وصلت للدرج ورجعت للزبون. الفاتورة بتفضل «متحصّلة»
    // بكاش أو بطاقة (مافيش حالة جديدة)، فمن غير السطر ده رقم المبيعات كان
    // هيعدّ فاتورة رجعت بالكامل كأنها دخل ومحدش ياخد باله.
    statRefunds: "بعد خصم {amount} أُعيدت إلى العميل",
    // R12 — علامة على السطر نفسه في قايمة الفواتير. الشريط فوق بيقول كام
    // رجع في اليوم كله، بس مفيش سطر واحد كان بيقول إن الفاتورة دي هي اللي
    // رجع منها — فسؤال «أنهي فواتير رجعت النهاردة؟» مكانش ليه إجابة من
    // القايمة. الفاتورة نفسها بتفضل بقيمتها فوقه زي ما اتقفلت (R5).
    rowRefunded: "أُعيد منها {amount}",
    refundTotal: "أُعيد إلى العميل",
    // المرتجع جنب أرقام الفاتورة مش جواها: الفاتورة بتفضل بتقول اللي اتطبع
    // عليها بالظبط، والمرتجع حقيقة تانية عنها.
    refundNote:
      "تبقى أرقام الفاتورة أعلاه كما كانت عند الإغلاق — يظهر المرتجع بجانبها ولا يغيّرها.",
    refundMethodDiverged:
      "أُعيدت النقود بطريقة تختلف عن طريقة الدفع الحالية في الفاتورة، لأن طريقة الدفع صُححت بعد المرتجع. هذا تنبيه وليس خطأ: فقد خرج هذا النقد من الخزينة بالفعل.",
    // B29 — الفاتورة نفسها أرقامها مش متطابقة. بيظهر هنا وعلى شاشة المرتجع في
    // الكاشير، عشان ده المكان اللي بيتفتح بعد أسابيع لما حد يسأل عن رقم.
    chargeSnapshotExcess:
      "أرقام هذه الفاتورة غير متطابقة؛ إذ يزيد رسم الخدمة والضريبة المسجّلان عليها على مجموع أصنافها بمقدار {amount}. سيعيد أي مرتجع منها نقود العميل كاملة، لكن الجزء المعاد من الضريبة سينخفض بهذا المبلغ.",
    /*
     * B2 — أرقام الفلوس فوق بتتحسب من صفوف الصفحة المحمّلة بس، فالنطاق مكتوب.
     *
     * Slice 9 / ruling F20 — الجملة دي كانت عايمة تحت صف التايلات، على بعد
     * كارت كامل من الأرقام اللي بتوصفها: «تحذير بعيد برقم عن رقمه ما بيوصفش
     * حاجة، واللي بينسخ الرقم عمره ما بيوصله». بقت ورا الباب وفي ورقة الأساس،
     * ومكانها على الشاشة بقى توكن واحد (`basisChipPage`) قاعد جنب الرقم نفسه.
     */
    statScopeNote: "تعرض أرقام النقود الفواتير المحصّلة في هذه الصفحة فقط.",
    tableStatus: "الحالة",
    // F1 (unit 4): العمود ده بقى بيرد على «الحساب ده حصل فين» مش «على أنهي
    // ترابيزة» — لأن حساب الدليفري والتيك أواي مش قاعد على ترابيزة خالص، وكتابة
    // «دليفري» تحت عمود اسمه «الطاولة» بتخلي الكلمة نفسها غلط.
    tableTable: "المكان",
    itemPreviewMore: "+{count}",
    searchPlaceholder: "رقم الفاتورة…",
    cancelInvoice: "إلغاء الفاتورة",
    cancelInvoiceTitle: "هل تريد إلغاء هذه الفاتورة؟",
    cancelInvoiceBody:
      "ستُزال الفاتورة من مبيعات اليوم. ستبقى أصنافها وسجلها كما هما.",
    /* تدقيق الجولة 2 (المرحلة 3): الجملة الثانية تقول ما يضمنه السيرفر —
       الإلغاء يُرفض متى كان على الفاتورة طبق عند المطبخ — فلا يخرج شيء من
       المخزون ولا يُسجَّل هالك. («عند المطبخ» لا «وصل إلى المطبخ»: صنف أُرسل
       ثم أُلغي سُجّل مصيره عند إلغائه، والإلغاء هنا لا يمسّه.) */
    cancelInvoicePosBody:
      "ستُزال الفاتورة من مبيعات اليوم، وستُزال معها أي أصناف لم تُرسل إلى المطبخ بعد. لا شيء على هذه الفاتورة عند المطبخ، فلن يخرج شيء من المخزون ولن يُسجَّل شيء هالكًا.",
    cancelInvoiceDone: "أُلغيت الفاتورة",
    tableInvoice: "الفاتورة",
    tableDate: "التاريخ",
    tableItems: "الأصناف",
    tableTotal: "الإجمالي",
    title: "الفواتير",
    description: "فواتير خدمة اليوم والأيام السابقة.",
    // PAPER only (founder, 2026-10-02): the redesign's own top-bar sentence,
    // with "invoice" where the artboard says "order" — they are not one thing.
    paperDescription:
      "كل فواتير اليوم، المدفوعة منها والمفتوحة. افتح أي فاتورة لعرض إيصالها.",
    filtersLabel: "تصفية الفواتير",
    statusFilterLabel: "الحالة",
    statusAll: "الكل",
    tableFilterLabel: "المكان",
    // F1: «كل الأماكن» مش «كل الطاولات» — الفلتر ده بقى بيغطي كمان حسابات
    // الدليفري والتيك أواي اللي مش قاعدة على ترابيزة أصلاً.
    tableAll: "كل الأماكن",
    // D31 — الفلتر اللي بيخلي الفاتورة اللي مشيت من غير دفع ممكن توصلها.
    methodFilterLabel: "طريقة الدفع",
    methodAll: "كل طرق الدفع",
    // B26 — "did money come back off this bill?". «رجّعنا منها» rather than a
    // literal "refunded": it is how a cashier says it, and the marker already
    // on the row reads «رجع منها … جنيه».
    refundedFilterLabel: "المرتجعات",
    refundedAll: "كل الفواتير",
    refundedWith: "الفواتير التي أُعيدت منها نقود",
    refundedWithout: "الفواتير التي لم تُعد منها نقود",
    // جولة التدقيق ٢، P5 — الفواتير اللي حصل عليها حاجة: صنف وصل المطبخ واتلغى،
    // أو صنف على الحساب، أو خصم على الفاتورة، أو مرتجع. طلب QR اترفض أو الزبون
    // لغاه مش استثناء — تقارير الاستثناءات مش بتعدّه.
    exceptionFilterLabel: "الاستثناءات",
    exceptionWith: "الفواتير التي بها استثناء",
    rowException: "استثناء",
    basisTermException: "استثناء",
    basisExceptionNote:
      "فاتورة فيها صنف أُلغي بعد وصوله إلى المطبخ، أو صنف على الحساب، أو خصم على الفاتورة، أو مرتجع. المرتجع يُحسب مهما كان يومه.",
    // T10 — القناة والمنصة والزبون: «فاتورة منى» و«فواتير طلبات» بلا فتح كل
    // فاتورة. البحث بالاسم أو الهاتف، من حرفين فأكثر.
    channelFilterLabel: "القناة",
    channelAll: "كل القنوات",
    platformFilterLabel: "المنصة",
    platformAll: "كل المنصات",
    customerSearchLabel: "العميل",
    customerSearchPlaceholder: "اسم العميل أو هاتفه أو رقم طلب المنصة",
    customerSearchHint: "حرفان على الأقل",
    // T9 — سطر ضريبة رسوم التوصيل على الإيصال المطبوع، ليجمع الإيصال.
    deliveryFeeVat: "ضريبة رسوم التوصيل",
    // T20 — سطر الضريبة يقول نسبته وأساسه: «ضريبة 14% على 130.00».
    vatWithRateBase: "ضريبة القيمة المضافة {percent}% على {base}",
    vatIncludedWithRateBase: "منها ضريبة القيمة المضافة {percent}% على {base}",
    deliveryFeeVatIncluded: "منها ضريبة على رسوم التوصيل",
    // The fallback when the table is not in the grid at all (deleted, or the
    // grid has not loaded). A shortened id is honest; an invented name is not.
    tableFallback: "طاولة {id}",
    dateRangeToday: "خدمة اليوم",
    dateRangeValue: "من {from} إلى {to}",
    dateRangeApply: "تطبيق",
    dateRangeClear: "مسح",
    dateRangeInvalid:
      "تاريخ البداية بعد تاريخ النهاية. عدّل المدة وحاول مرة أخرى.",
    // P5-Q3 (2026-10-27) — the history and its export read at most 92 days,
    // the reports' own limit: the picker's footer hint, and its refusal.
    dateRangeHint: "اختر يومًا واحدًا، أو فترة حتى {span}.",
    dateRangeTooLong: "يعرض السجل {span} كحد أقصى؛ اختر فترة أقصر.",
    clearFilters: "إزالة المرشحات",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    resultCountOne: "ترى {shown} من فاتورة واحدة",
    resultCountTwo: "ترى {shown} من فاتورتين",
    resultCountFew: "ترى {shown} من {total} فواتير",
    resultCount: "ترى {shown} من {total} فاتورة",
    resultCountLoading: "جارٍ تحميل الفواتير...",
    // "محسوب" on purpose: `itemCount` is ACTIVE rows only, the same
    // basis as the total next to it. It is not a count of lines.
    itemCount: "{count} صنف محسوب",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    itemCountFew: "{count} أصناف محسوبة",
    // B117/B4 — «2 صنف محسوب» on every history row, beside money that is now
    // «بطاقة + كاش». Same family as the till's own counted nouns.
    itemCountNone: "لا توجد أصناف محسوبة",
    itemCountOne: "صنف واحد محسوب",
    itemCountTwo: "صنفين محسوبين",
    emptyTodayTitle: "لا توجد فواتير في خدمة اليوم حتى الآن",
    emptyTodayBody:
      "ستظهر هنا أول فاتورة تُغلق على أي طاولة. ولعرض يوم سابق، اختر التاريخ من أعلى.",
    emptyFilteredTitle: "لا توجد فاتورة مطابقة",
    emptyFilteredBody:
      "لا توجد فاتورة تطابق المرشحات المحددة. وسّع المدة أو امسح المرشحات للعودة إلى خدمة اليوم.",
    paginationLabel: "صفحات الفواتير",
    prevPage: "السابق",
    nextPage: "التالي",
    pageOf: "صفحة {page} من {pages}",
    noAccessTitle: "لا تملك صلاحية عرض الفواتير",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية الكاشير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    detailFallbackTitle: "الفاتورة",
    backToList: "العودة إلى الفواتير",
    // شاشة التحصيل مش موجودة غير في الكاشير، فالفاتورة المفتوحة أو المقفولة
    // محتاجة لينك يوصّل لها من السجل بدل ما تفضل مقروءة وبس.
    openInTill: "فتحها في شاشة البيع",
    // GAP-1 — الفاتورة المتحصّلة كان مالهاش طريق ترجع منها فلوس غير لينك
    // بيتكتب بالإيد. اللينك ده هو الطريق: من سجل الفواتير للفاتورة للكاشير.
    // اسم مختلف عن «افتحها في الكاشير» عشان يقول ليه بنفتحها أصلاً.
    openInTillToRefund: "فتحها في شاشة البيع لإعادة النقود",
    refundNeedsPermission:
      "يتطلب رد النقود صلاحية خاصة غير متاحة لك. اطلب من صاحب المطعم فتح هذه الفاتورة عند الكاشير وتنفيذ المرتجع.",
    // نسخة تانية من إيصال المرتجع، من الفاتورة نفسها — الطابعة بتزنق،
    // والزبون بيرجع تاني يوم، والورقة دي مستند ضريبي.
    printRefundReceipt: "طباعة نسخة من إيصال المرتجع",
    // فاتورة رجع منها أكتر من مرة: صورة لكل مرتجع، كل واحدة بترقمها ومبلغها.
    printRefundReceiptOne: "طباعة نسخة من المرتجع {reference} · {amount}",
    // D75 — labels ONE refund's row of markers on a bill that has more than
    // one. Same reference and same amount as that refund's reprint button, so
    // the flag and the paper it belongs to are named identically.
    refundMarkerRowLabel: "مرتجع {reference} · {amount}",
    businessDate: "يوم الخدمة",
    openedAt: "فُتحت",
    closedAt: "أُغلقت",
    settledAt: "حُصّلت",
    settlementMethod: "طريقة الدفع",
    // NOT «المدفوع». The column holds what came ACROSS THE COUNTER, which on a
    // cash bill is the note handed over — 500 against a 31.92 cheque — and a
    // manager reconciling that row read it as a 468 overpayment. The till
    // already calls this «المبلغ المستلم»; the back office now agrees with it.
    settlementAmount: "المبلغ المستلم",
    // B117/B1 — على فاتورة اتدفعت بأكتر من طريقة، «المبلغ المستلم» رقم واحد
    // جامع دفعتين مختلفتين، والكلمة نفسها بتوحي بالكاش. السطر ده بيفكّه:
    // «٢ دفعات: بطاقة ١٠٠ · كاش ١٢٣٫٤٤».
    settlementTenderBreakdown: "{count} دفعة: {parts}",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    settlementTenderBreakdownFew: "{count} دفعات: {parts}",
    // B117/B4 — الحالة الغالبة بالتحديد: دفعتين. المفرد مستحيل هنا (السطر ده
    // مابيتطبعش غير لما يكون فيه أكتر من دفعة)، فالمثنى بس هو اللي ناقص.
    settlementTenderBreakdownTwo: "دفعتين: {parts}",
    settlementChange: "الباقي المعاد",
    notYet: "حتى الآن",
    // D45: عدد الضيوف زي ما اتسجّل وقت فتح الترابيزة — بيظهر بس لو اتسجّل.
    guestCount: "عدد الضيوف",
    // D46: سعر اتكتب بالإيد على السطر ده بدل سعر المنيو.
    priceOverrideChip: "سعر يدوي {price}",
    // R12/§10: الصنف ده رجع فلوسه للزبون. متحسوب من دفتر الفلوس، مش متخزّن
    // على السطر — الفاتورة نفسها ما اتغيّرتش.
    itemRefundedChip: "أُعيد مبلغه",
    // D30، متعدّلة بـ S14/D49 — تصحيح ضغطة غلط: الكاشير ضغط كاش والزبون دفع
    // بالبطاقة. مابقاش محدود بيوم الشغل: أرقام الوردية بتتثبّت وقت القفل،
    // فالتصحيح مابيغيّرش وردية اتقفلت. لسه محتاج صلاحية الكاشير.
    correctSettlement: "تصحيح طريقة الدفع",
    correctSettlementTitle: "طريقة الدفع الصحيحة",
    correctSettlementBody:
      "اختر الطريقة التي دفع بها العميل بالفعل. سيُضبط المبلغ تلقائيًا. ويمكن التصحيح أيضًا لفاتورة من يوم سابق؛ إذ تبقى الخزينة المغلقة بأرقامها ويظهر التعديل في مراجعة خزينة اليوم.",
    correctSettlementDone: "عُدّلت طريقة الدفع",
    // القرار 3 — قبل التأكيد. تصحيح طريقة الدفع بعد المرتجع مسموح وما بيتمنعش،
    // بس الفلوس اللي رجعت بتفضل مسجّلة بالطريقة اللي خرجت بيها فعلاً، فالمدير
    // لازم يعرف إنه بيعمل الفرق ده بنفسه بدل ما يكتشفه بعدين.
    correctSettlementRefundWarning:
      "أُعيد مبلغ {amount} من هذه الفاتورة بطريقة «{method}». يمكن تغيير طريقة الدفع، لكن سيبقى المبلغ المعاد مسجّلًا بالطريقة التي خرج بها؛ لذلك قد تعرض الفاتورة طريقة مختلفة عن الخزينة. هذا تنبيه وليس خطأ.",
    correctSettlementRefundDiverged:
      "أُعيد مبلغ {amount} من هذه الفاتورة بطريقة تختلف عن طريقة الدفع الحالية بعد تصحيح سابق. ما زال التصحيح مسموحًا، وسيبقى المبلغ المعاد مسجّلًا بالطريقة التي خرج بها. هذا تنبيه وليس خطأ.",
    // B3 — تحويل فاتورة متحصّلة لـ«مشيت من غير دفع» بيمسح دخل فعلي، فلازم
    // سبب مسجّل زي أي إلغاء بالظبط.
    writeOffReasonIntro:
      "ستتحول هذه الفاتورة إلى «غادر بلا دفع» — وهذا يزيل دخلها بالكامل من جميع الأرقام. سجّل السبب.",
    itemsEmptyHistoryBody: "أُغلقت هذه الفاتورة من دون أي صنف.",
    addonSeparator: "، ",
    remadeTimes: "أُعيد تحضيره {count} مرة",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    remadeTimesOne: "أُعيد تحضيره مرة واحدة",
    remadeTimesTwo: "أُعيد تحضيره مرتين",
    remadeTimesFew: "أُعيد تحضيره {count} مرات",
    // The three states the history row's `itemCount` deliberately excludes.
    itemActive: "محسوب",
    itemPending: "بانتظار الموافقة",
    itemDraft: "لم يُضف بعد",
    itemQueued: "في قائمة الانتظار",
    itemPreparing: "في التحضير",
    itemReady: "جاهز",
    itemServed: "قُدّم",
    itemVoided: "ملغى",
    itemComped: "على الحساب",
    itemNotCharged: "غير محسوب",
    totalsTitle: "الحساب",
    subtotal: "المجموع",
    discountTotal: "الخصم",
    // D33: the two charge lines. The "منها" pair is the inclusive-mode
    // wording — the amount is already inside the prices, so the line names a
    // part of the total rather than an addition to it.
    serviceCharge: "رسم الخدمة",
    serviceChargeIncluded: "منها رسم الخدمة",
    vat: "ضريبة القيمة المضافة",
    /** F17: بند رسم التوصيل، لما نحتاج نسمّيه جوه سطر مرتجع. */
    deliveryFee: "رسم التوصيل",
    vatIncluded: "منها ضريبة القيمة المضافة",
    // The receipt variants carry the snapshotted rate: the paper has to let a
    // customer (or an inspector) redo the arithmetic without asking the till.
    serviceChargeWithRate: "رسم الخدمة {percent}%",
    serviceChargeIncludedWithRate: "منها رسم الخدمة {percent}%",
    vatWithRate: "ضريبة القيمة المضافة {percent}%",
    vatIncludedWithRate: "منها ضريبة القيمة المضافة {percent}%",
    // DEF-6: the inclusive-mode "منها" group's opening line — the goods
    // value before service/VAT were folded into the printed prices. Sits
    // above the "منها" pair so net + service + VAT visibly sum to the total.
    netBeforeCharges: "الصافي قبل الرسوم",
    taxRegistrationNumber: "الرقم الضريبي",
    totalsStillRunning:
      "الفاتورة ما زالت مفتوحة — تتغير هذه الأرقام حتى تُغلق الفاتورة، ثم تُسجّل نهائيًا.",
    // D40 — the CSV export, rendered only for `REPORT_VIEW`. Its label says
    // it exports the filters currently on screen, since it carries them.
    exportCsv: "تنزيل CSV",
    exportCsvHint: "ينزّل الفواتير بالمرشحات الظاهرة الآن في ملف CSV.",
    exporting: "جارٍ تنزيل الملف...",
    exportDone: "تم تنزيل الملف",
  },
  /**
   * Stock reconciliation. Read-only, and the copy says so twice — once as a
   * standing line and once inside the explanation of a difference, because an
   * owner who reads a drift as "it fixed itself" will never record the
   * adjustment that actually fixes it.
   */
  reconcile: {
    title: "مراجعة الرصيد",
    description:
      "تقارن المراجعة الرصيد المسجّل للصنف بالرصيد المحسوب من المشتريات والتسويات والمبيعات.",
    run: "مراجعة الرصيد",
    running: "جارٍ حساب الرصيد...",
    cached: "الرصيد المسجّل",
    derived: "الرصيد المحسوب",
    drift: "الفرق",
    matchChip: "متطابق",
    driftChip: "يوجد فرق",
    matchBody:
      "الرقمان متطابقان. يساوي الرصيد المعروض في الشاشة الرصيد الناتج من الحركات، ولا يلزم اتخاذ أي إجراء.",
    driftBody:
      "يزيد الرصيد المسجّل على الرصيد المحسوب من الحركات بمقدار {drift} {unit}، ويعني الرقم السالب أن المسجّل أقل. لا يدل ذلك وحده على نقص فعلي في المخزون، بل قد تكون حركة سُجّلت خطأ أو تغيّر الرصيد بلا حركة مقابلة. راجع مشتريات الصنف وتسوياته، وإذا كان الرصيد الحقيقي مختلفًا فسجّل تسوية بالفرق.",
    readOnly: "المراجعة للقراءة فقط؛ لا تغيّر الرصيد ولا تسجّل تسوية.",
  },
  /**
   * Editing an ingredient, and reading back its two append-only ledgers.
   *
   * The unit-lock copy is the load-bearing part: it has to explain that stored
   * quantities carry no unit of their own, so a unit change would reinterpret
   * history rather than convert it — and that the remedy for a genuine mistake
   * is a new ingredient, not a rewrite of what happened.
   */
  ingredient: {
    editTitle: "تعديل المكوّن",
    nameRequired: "اكتب اسم المكوّن.",
    unitChangeableHint:
      "لا توجد مشتريات أو مبيعات مسجّلة على هذا المكوّن حتى الآن، لذلك يمكنك تغيير وحدته. ستُقفل الوحدة بعد تسجيل أول شراء أو بيع.",
    unitLockedTitle: "الوحدة مغلقة",
    unitLockedBody:
      "لهذا المكوّن حركات مسجّلة بالفعل، ولا تحمل الكميات المخزّنة وحدة داخلها؛ فالرقم 500 مثلًا محفوظ كما هو. إذا تغيّرت الوحدة من {unit} إلى وحدة أخرى، فستُقرأ كل المشتريات القديمة بوحدة جديدة من دون تحويل قيمها، وسيتغير متوسط التكلفة. لذلك لا يُسمح بتغيير الوحدة.",
    unitLockedRemedy:
      "إذا سُجّلت الوحدة خطأ منذ البداية، فأنشئ مكوّنًا جديدًا بالوحدة الصحيحة واستخدمه من الآن بدلًا من تغيير السجل السابق.",
    unitLockedAfterAttempt:
      "رفض الخادم التغيير لأن هذا المكوّن بيع سابقًا، حتى إن لم تكن له مشتريات مسجّلة. ما زالت بقية تعديلاتك موجودة؛ اضغط حفظ.",
    // D148 — العبوة: تلميح للاستلام بس (الكيس = 5000 جم). مش تحويل وحدات.
    packHint: "العبوة",
    packHintHelp:
      "إذا كان الصنف يأتي في عبوة ثابتة (كيس أرز 5 كيلو)، فاكتب اسمها وحجمها بوحدة {unit}. تحسب شاشة استلام البضائع الكمية منها. اتركها فارغة للأصناف الموزونة.",
    packLabel: "اسم العبوة",
    packSize: "حجم العبوة",
    packSuggestions: "أسماء شائعة",
    packPairRequired: "اكتب اسم العبوة وحجمها معًا، أو اتركهما فارغين.",
    packSizeInvalid: "اكتب حجمًا أكبر من صفر.",
    packLabelTooLong: "اسم العبوة حتى 20 حرفًا.",
    packNotConversion:
      "هذا تلميح للاستلام فقط — لا يوجد تحويل وحدات في الوصفات أو الجرد أو التقارير.",
    lowStockThreshold: "حد التنبيه",
    lowStockThresholdHint:
      "تظهر علامة «قارب على النفاد» بجانب المكوّن عندما تصل الكمية المتاحة إلى هذا الرقم أو أقل، بوحدة {unit}. اتركه فارغًا إذا لم ترد تنبيهًا.",
    thresholdInvalid: "اكتب رقمًا موجبًا، أو اتركه فارغًا.",
    // سعر بيع المكوّن (D62). سعر واحد للمكوّن على مستوى المحل — مش لكل صنف.
    sellPrice: "سعر البيع",
    sellPriceHint:
      "يُستخدم لحساب فرق التبديل فقط: إذا استبدل الويتر مكوّنًا بآخر أغلى، يُضاف الفرق إلى الطبق. لا يرتبط بسعر الصنف في قائمة الطعام. اتركه فارغًا ليكون التبديل مجانيًا، وسيُسجّل عندئذ في مراجعة الخزينة لتسعيره لاحقًا.",
    sellPriceInvalid: "اكتب رقمًا موجبًا أو صفرًا، أو اتركه فارغًا.",
    // D108: السعر فوق ده — هل هو مبلغ ثابت للتبديل، ولا سعر للوحدة بيتضرب في
    // كمية الوصفة؟ الافتراضي «ثابت»، وهو اللي كل المكوّنات القديمة عليه.
    sellPriceBasis: "نوع السعر",
    sellPriceBasisFlat: "مبلغ ثابت للتبديل",
    sellPriceBasisPerUnit: "سعر لكل {unit}",
    sellPriceBasisFlatHint:
      "يُطبّق الفرق نفسه على أي طبق مهما كانت كمية المكوّن في وصفة الصنف. هذا هو الإعداد الحالي.",
    sellPriceBasisPerUnitHint:
      "يُضرب الفرق في كمية الوصفة، لذلك يكون تبديل اللحم في طبق كبير أغلى من التبديل نفسه في شطيرة، كما تزيد تكلفة المطبخ. تُؤخذ الكمية من وصفة الصنف وقت التبديل.",
    sellPriceBasisChanged:
      "غيّرت نوع السعر، فأصبحت القيمة المكتوبة تحمل معنى مختلفًا. أدخل السعر من جديد وفق النوع الجديد قبل الحفظ.",
    // أيقونة المكوّن (2026-10-03): شارة من مكتبة الرسومات يتعرض بيها المكوّن.
    icon: "الأيقونة",
    iconAll: "كل الأيقونات",
    iconChange: "تغيير الأيقونة",
    iconHint: "اضغط على الأيقونة لتغييرها",
    historyAction: "السجل",
    /* UX-round D56 — تلات شاشات سجل كانت بتتفتح من نفس الصف بقت درج واحد بفلتر.
       العنوان بقى واحد، والوصف اللي تحته هو اللي بيتغيّر — عشان كل سؤال من
       التلاتة يفضل سؤال قايم بذاته مش سطر في لستة واحدة مخلوطة. */
    recordTitle: "سجل {name}",
    recordViewLabel: "اختر السجل المطلوب",
    recordViewLedger: "الوارد والتصحيحات",
    recordViewDepletions: "ما استُهلك في التحضير",
    recordViewAvailability: "ما أبلغ المطبخ بنفاده",
    historyTitle: "سجل {name}",
    historySubtitle: "كل عمليات الشراء والتسويات المسجّلة على هذا المكوّن",
    appendOnlyNote:
      "يُضاف إلى هذا السجل فقط — فلا يمكن تعديل أي سطر أو حذفه. إذا سُجّلت حركة خطأ، فتُصحح بتسوية جديدة للفرق، ويظل السطران ظاهرين.",
    purchasesTitle: "المشتريات",
    /* P5.3 — السجل بيتعرض لحد ٢٠٠ سطر، الأحدث الأول، زي سجل الاستهلاك. */
    purchasesTruncatedNote:
      "مشتريات هذا المكوّن أكثر مما نعرضه — والقائمة أدناه هي أحدث ٢٠٠ عملية شراء، لا السجل كاملًا.",
    purchasesEmptyTitle: "لا توجد مشتريات مسجّلة حتى الآن",
    purchasesEmptyBody:
      "هذا طبيعي للمكوّن الجديد. سجّل أول عملية شراء من «تسجيل شراء» في صفحة المكوّنات، وستظهر هنا الكمية والمبلغ وتكلفة الوحدة.",
    unitCostAtPurchase: "تكلفة الوحدة في عملية الشراء هذه",
    /* D136 — اليوم اللي التوريدة محسوبة عليه (ممكن يكون قبل يوم تسجيلها). */
    purchaseBusinessDate: "محسوبة على يوم",
    avgCostAfter: "متوسط التكلفة بعدها",
    adjustmentsTitle: "التسويات",
    adjustmentsTruncatedNote:
      "تسويات هذا المكوّن أكثر مما نعرضه — والقائمة أدناه هي أحدث ٢٠٠ تسوية، لا السجل كاملًا.",
    adjustmentsEmptyTitle: "لا توجد تسويات حتى الآن",
    adjustmentsEmptyBody:
      "تُسجّل التسوية عند هدر شيء أو تلفه، أو لتصحيح رقم سُجّل خطأ. لا توجد تسويات على هذا المكوّن، وهذا وضع طبيعي.",
    deltaUp: "زيادة",
    deltaDown: "نقص",
    reasonUnknown: "سبب غير معروف ({code})",
  },
  /**
   * The 86 log on one dish (B12).
   *
   * Register: the switch itself says «نفد النهاردة», so every label in here
   * uses «نفد» / «متوفر» too. A second Arabic word for the same state would
   * read as a second feature.
   */
  depletionEvents: {
    action: "الأطباق التي استهلكت هذا المكوّن",
    title: "استهلاك {name}",
    subtitle: "الأطباق التي خُصمت من هذا المكوّن، وكل طبق بكميته وتكلفته.",
    // المجاميع مكانها ما اتغيّرش؛ دي التفصيلة اللي تحتها لما رقم يبان غريب.
    note: "تبقى الأرقام المجمّعة في التقارير كما هي — وهذه تفاصيلها لتحديد الطبق المعني بدقة.",
    emptyTitle: "لم يُخصم شيء من هذا المكوّن حتى الآن",
    emptyBody:
      "يعني ذلك أنه لا يوجد طبق استهلك هذا المكوّن في السجل. هذا وضع طبيعي وليس خطأ.",
    truncatedNote:
      "حركات هذا المكوّن أكثر مما نعرضه — والقائمة أدناه هي أحدث جزء من السجل، أي حد أدنى لا السجل كاملًا.",
    // بنقول «في الصفحة دي» عشان الليستة محدودة، وجمع على ليستة محدودة لو
    // اتقرا كإجمالي بيبقى بالظبط الغلط اللي الحد الأقصى موجود عشان يمنعه.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    /* D140/D141 — في الليستة دي حركات داخلة كمان (عكس تحضير، مرتجع من المطبخ)،
       فالإجمالي صافي: الخارج ناقص الراجع. */
    pageTotalOne: "صافي هذه الصفحة: {cost} — لحركة واحدة",
    pageTotalTwo: "صافي هذه الصفحة: {cost} — لحركتين",
    pageTotalFew: "صافي هذه الصفحة: {cost} — لعدد {count} حركات",
    pageTotal: "صافي هذه الصفحة: {cost} — لعدد {count} حركة",
    netNote: "المرتجع من المطبخ وعكس التحضير يُطرحان من الصافي.",
    /* D139 — حركة بتكلفة مش معروفة بتتعدّ ومش بتتجمع. */
    unknownCostOne: "حركة واحدة بتكلفة غير معروفة غير محسوبة في الصافي",
    unknownCostTwo: "حركتان بتكلفة غير معروفة غير محسوبتين في الصافي",
    unknownCostFew: "{count} حركات بتكلفة غير معروفة غير محسوبة في الصافي",
    unknownCost: "{count} حركة بتكلفة غير معروفة غير محسوبة في الصافي",
    billRef: "فاتورة #{sequence}",
  },
  /**
   * One signed movement vocabulary (plan §2.1, D135). The adjustment form,
   * the ledger list, the record drawer and the item card all read their
   * movement names from here — one thing, one word.
   */
  stockMovement: {
    purchase: "وارد مشتريات",
    supplierReturn: "مرتجع مشتريات",
    kitchenIssue: "منصرف للمطبخ",
    remakeWaste: "هالك إعادة تحضير",
    cookReversal: "عكس تحضير (تعديل)",
    returnedToStock: "مرتجع من المطبخ",
    waste: "هالك",
    spoilage: "تالف",
    correction: "تصحيح",
    stocktake: "تسوية جرد",
    directionIn: "وارد",
    directionOut: "منصرف",
  },
  /**
   * The count screen — الجرد (D143, phase 9). «تسوية جرد» is the MOVEMENT a
   * posted count writes (`stockMovement.stocktake`); «جرد» is the session.
   */
  stocktake: {
    title: "الجرد",
    subtitle:
      "عُدّ ما على الرف وقارنه بالرصيد المسجّل. لا يتغير المخزون إلا عند ترحيل الجرد.",
    startTitle: "لا يوجد جرد مفتوح",
    startBody:
      "ابدأ جردًا لتسجّل ما تجده على الرف لكل مكوّن. يمكنك المتابعة من جهاز آخر، ويبقى الجرد مفتوحًا حتى ترحّله أو تلغيه.",
    start: "بدء جرد",
    openedBy: "جرد رقم {number} — بدأه {name}، {date}",
    openedAt: "جرد رقم {number} — بدأ {date}",
    startedBy: "بدأه {name}، {date}",
    startedAt: "بدأ {date}",
    resumed: "كان هناك جرد مفتوح بالفعل، فهذا هو. أكمل العدّ فيه.",
    // M-1 (phase 12): no timer — the count is re-read on opening the screen,
    // on coming back to it, before post/cancel, and when the reader asks.
    refresh: "تحديث",
    refreshAria: "حدّث الجرد لترى ما عدّه جهاز آخر",
    refreshNote: "يُقرأ الجرد من جديد عند فتح الشاشة وعند العودة إليها وقبل الترحيل أو الإلغاء. اضغط «تحديث» لترى ما عدّه جهاز آخر الآن.",
    reference: "جرد رقم {number}",
    unitCostLine: "تكلفة الوحدة: {cost}",
    historyOpen: "عرض الجرد",
    historyOpenAria: "عرض الجرد رقم {number}",
    viewSubtitle: "ما عُدّ في هذا الجرد، للقراءة فقط.",
    viewNoLines: "لم يُعَدّ أي مكوّن في هذا الجرد.",
    viewCounted: "المعدود",
    searchLabel: "ابحث عن مكوّن",
    searchPlaceholder: "مثال: طماطم",
    filterLabel: "عرض",
    filterAll: "الكل",
    filterUncounted: "لم يُعَدّ",
    filterCounted: "تم عدّه",
    filterVariance: "به فرق",
    sheetLabel: "قائمة الجرد",
    expectedNow: "الرصيد المسجّل الآن",
    expectedAtCount: "الرصيد المسجّل وقت العدّ",
    countedLabel: "المعدود ({unit})",
    countedAria: "الكمية المعدودة من {name}",
    variance: "الفرق",
    varianceValue: "قيمة الفرق",
    colIngredient: "المكوّن",
    colBooks: "الرصيد المسجّل",
    colCounted: "المعدود",
    colValue: "القيمة",
    booksWhenCounted: "وقت العدّ",
    booksNow: "الآن",
    tileOver: "زيادة على الرف",
    tileShort: "عجز على الرف",
    tileLinesNone: "لا يوجد",
    tileLinesOne: "مكوّن واحد",
    tileLinesTwo: "مكوّنان",
    tileLinesFew: "{count} مكوّنات",
    tileLines: "{count} مكوّن",
    matches: "مطابق",
    notCounted: "لم يُعَدّ بعد",
    saving: "جارٍ الحفظ…",
    saved: "محفوظ",
    unsaved: "لم يُحفظ بعد — اضغط خارج الخانة أو Enter",
    invalidCount: "اكتب كمية صفرًا أو أكثر، بأربعة أرقام عشرية على الأكثر.",
    clear: "مسح العدّ",
    clearAria: "مسح عدّ {name}",
    inactive: "غير مستخدم",
    emptyIngredients: "لا توجد مكوّنات بعد. أضف المكوّنات من شاشة المخزون أولًا.",
    noMatch: "لا يوجد مكوّن مطابق للبحث أو للعرض المختار.",
    summaryTitle: "ملخص الجرد",
    summaryCounted: "المكوّنات المعدودة",
    summaryVarianceLines: "مكوّنات بها فرق",
    summaryValue: "صافي قيمة الفروق",
    summaryUnknownOne: "مكوّن واحد فرقه بتكلفة غير معروفة، فهو غير محسوب في القيمة.",
    summaryUnknownTwo: "مكوّنان فرقهما بتكلفة غير معروفة، فهما غير محسوبين في القيمة.",
    summaryUnknownFew: "{count} مكوّنات فروقها بتكلفة غير معروفة، فهي غير محسوبة في القيمة.",
    summaryUnknown: "{count} مكوّن فروقها بتكلفة غير معروفة، فهي غير محسوبة في القيمة.",
    summaryPreviewNote:
      "القيم محسوبة بمتوسط التكلفة الحالي، وتُثبَّت عند الترحيل.",
    post: "ترحيل الجرد",
    postNeedsCount: "عُدّ مكوّنًا واحدًا على الأقل قبل الترحيل.",
    postTitle: "ترحيل الجرد؟",
    postBodyNone:
      "لا يوجد فرق في أي مكوّن معدود، فلن يتغير المخزون؛ سيُغلق الجرد فقط. لا يمكن التراجع عن الترحيل.",
    postBodyOne:
      "سيُسجَّل فرق مكوّن واحد في المخزون كـ«تسوية جرد» بتاريخ اليوم، ويتحرك رصيده بمقدار الفرق بالضبط. لا يمكن التراجع عن الترحيل؛ أي خطأ يُصحَّح بجرد جديد أو بتسوية.",
    postBodyTwo:
      "سيُسجَّل فرق مكوّنين في المخزون كـ«تسوية جرد» بتاريخ اليوم، ويتحرك رصيد كل منهما بمقدار فرقه بالضبط. لا يمكن التراجع عن الترحيل؛ أي خطأ يُصحَّح بجرد جديد أو بتسوية.",
    postBodyFew:
      "سيُسجَّل فرق {count} مكوّنات في المخزون كـ«تسوية جرد» بتاريخ اليوم، ويتحرك رصيد كل منها بمقدار فرقه بالضبط. لا يمكن التراجع عن الترحيل؛ أي خطأ يُصحَّح بجرد جديد أو بتسوية.",
    postBody:
      "سيُسجَّل فرق {count} مكوّن في المخزون كـ«تسوية جرد» بتاريخ اليوم، ويتحرك رصيد كل منها بمقدار فرقه بالضبط. لا يمكن التراجع عن الترحيل؛ أي خطأ يُصحَّح بجرد جديد أو بتسوية.",
    cancel: "إلغاء الجرد",
    cancelTitle: "إلغاء الجرد؟",
    cancelBody:
      "لن يُسجَّل شيء في المخزون. يبقى ما عددته في السجل، لكن لا يمكن فتح هذا الجرد مرة أخرى.",
    keepCounting: "متابعة الجرد",
    postedTitle: "رُحِّل الجرد",
    postedBody:
      "سُجِّلت الفروق بتاريخ {date} كـ«تسوية جرد». افتح بطاقة الصنف لترى الحركة والرصيد بعدها.",
    postedNoVariance: "لم يكن هناك فرق، فلم يتغير المخزون.",
    cancelledTitle: "أُلغي الجرد",
    cancelledBody: "لم يتغير شيء في المخزون.",
    newCount: "بدء جرد جديد",
    historyTitle: "الجرد في آخر 30 يومًا",
    historyEmpty: "لا يوجد جرد في هذه الفترة.",
    statusOpen: "مفتوح",
    statusPosted: "مُرحَّل",
    statusCancelled: "ملغى",
    historyLinesOne: "مكوّن واحد معدود",
    historyLinesTwo: "مكوّنان معدودان",
    historyLinesFew: "{count} مكوّنات معدودة",
    historyLines: "{count} مكوّن معدود",
    // D160 (F36) — the blind count. The server withholds the book figures
    // while the count is not posted; these say so, and say when they appear.
    blindNote:
      "جرد دون عرض الأرصدة: لا يظهر الرصيد المسجّل ولا الفرق أثناء العدّ. يظهر الفرق بعد ترحيل الجرد.",
    blindFilterReason: "عرض «به فرق» متاح بعد ترحيل الجرد.",
    blindSummary: "يظهر الفرق بعد ترحيل الجرد.",
    postBodyBlind:
      "سيُسجَّل فرق كل مكوّن معدود في المخزون كـ«تسوية جرد» بتاريخ اليوم، ويتحرك رصيده بمقدار الفرق بالضبط، ثم تظهر الفروق. لا يمكن التراجع عن الترحيل؛ أي خطأ يُصحَّح بجرد جديد أو بتسوية.",
    viewBlind: "لم يُرحَّل هذا الجرد، فلا يظهر فيه الرصيد المسجّل ولا الفرق.",
    settingLabel: "الجرد دون عرض الأرصدة",
    settingCheckbox: "إخفاء الرصيد المسجّل والفرق أثناء الجرد",
    settingHint:
      "لا تُظهر ورقة الجرد أي رصيد مسجّل ولا أي فرق حتى ترحيل الجرد، فتُسجَّل فيها الكمية الموجودة على الرف لا الكمية في الدفاتر. يسري على الجرد الذي يبدأ بعد الحفظ.",
  },
  /**
   * Return to supplier — مرتجع مشتريات (D142, phase 9; renamed 2026-10-14). The movement's name is
   * `stockMovement.supplierReturn`; this block is the form.
   */
  supplierReturn: {
    title: "مرتجع مشتريات",
    action: "إرجاع إلى المورد",
    actionAria: "إرجاع جزء من توريدة {name} إلى المورد",
    subtitle: "{name} — توريدة {date}",
    factDelivered: "الكمية المورَّدة",
    factPrice: "السعر المدفوع",
    factReturned: "أُرجع منها",
    factLeft: "يمكن إرجاع حتى",
    quantityLabel: "الكمية المرتجعة ({unit})",
    quantityHint: "حتى {quantity} {unit}",
    quantityRequired:
      "اكتب كمية أكبر من صفر، بأربعة أرقام عشرية على الأكثر.",
    quantityTooMuch: "يمكن إرجاع {quantity} {unit} فقط من هذه التوريدة.",
    refLabel: "رقم إشعار المورد (اختياري)",
    refHint: "كما هو مكتوب على إشعار المرتجع، حتى 64 حرفًا.",
    noteLabel: "ملاحظة (اختياري)",
    noteHint: "سبب الإرجاع، مثلًا: تالفة عند الاستلام.",
    previewTitle: "أثر المرتجع",
    previewValue: "قيمة المرتجع",
    previewVat: "ضريبتها",
    previewStock: "الرصيد بعد المرتجع",
    owedLowers: "ينقص ما عليك لـ{name} بمقدار {amount}.",
    owedPaid:
      "هذه التوريدة مدفوعة بالفعل، فلا يتغير ما عليك. استرد المبلغ من المورد مباشرة.",
    owedUntracked:
      "هذه التوريدة ليست في حساب مورد، فلا يتغير ما عليك.",
    submit: "تسجيل المرتجع",
    done: "تم تسجيل المرتجع: {quantity} {unit} بقيمة {amount}.",
    notFound: "لم نجد هذه التوريدة في سجل المكوّن. حدّث الصفحة.",
    nothingLeft: "أُرجعت هذه التوريدة بالكامل.",
    returned: "أُرجع منها {quantity} {unit}",
  },
  /**
   * The inventory item card — كارت الصنف (D135). Labels defined in phase 8A so
   * the card screen (8B) only wires them. Counted strings (plates per row) are
   * added with their siblings by the screen that renders them.
   */
  itemCard: {
    title: "بطاقة الصنف",
    titleNamed: "بطاقة الصنف — {name}",
    openAction: "بطاقة الصنف",
    subtitle:
      "كل حركة على هذا المكوّن خلال الفترة، بكميتها وقيمتها، والرصيد بعد كل حركة.",
    opening: "رصيد أول المدة",
    closing: "رصيد آخر المدة",
    totals: "إجمالي الفترة",
    colDate: "التاريخ",
    colMovement: "الحركة",
    colDocument: "المستند",
    colQtyIn: "كمية وارد",
    colQtyOut: "كمية منصرف",
    colUnitCost: "تكلفة الوحدة",
    colValueIn: "قيمة وارد",
    colValueOut: "قيمة منصرف",
    colBalanceQty: "الرصيد (كمية)",
    colBalanceValue: "الرصيد (قيمة)",
    colNote: "ملاحظة",
    valueUnknown: "تكلفة غير معروفة",
    unknownRowsNote: "الحركات ذات التكلفة غير المعروفة (مكوّن لم يُشترَ وقتها) داخلة في الكميات وغير داخلة في القيم.",
    liveTitle: "المسجَّل في النظام الآن",
    liveQtyDifference: "فرق الكمية عن رصيد آخر المدة",
    liveValueDifference: "فرق القيمة عن رصيد آخر المدة (تقدير بمتوسط التكلفة الحالي)",
    emptyRange:
      "لا توجد حركات في هذه الفترة — رصيد آخر المدة يساوي رصيد أول المدة.",
    howTitle: "كيف تُحسب هذه الأرقام",
    howBody: "القيم بالتكلفة كما سُجِّلت، أي شاملة ضريبة المدخلات المسجّلة على التوريدة (وهي نفسها تُخصم في تقرير ضريبة المشتريات). متوسط التكلفة مرجّح ويُعاد حسابه بترتيب تسجيل الحركات، وتُقرَّب تكلفة الوحدة إلى أربعة أرقام عشرية وقيمة كل حركة إلى أقرب قرش. التوريدة المسجّلة بتاريخ سابق تُحتسب على يومها ولا تغيّر أي قيمة سابقة: ما استُهلك قبل تسجيلها لا يُعاد تسعيره. داخل اليوم الواحد تتبع الحركات ترتيب تسجيلها، لذلك قد يظهر رصيد وسيط بالسالب ثم يعتدل. سطر «منصرف للمطبخ» يجمع أطباق اليوم، فإذا سُجِّلت حركة أخرى بين أطباقه (توريدة أو جرد أو تسوية) انقسم السطر حولها، فيبقى الرصيد بعد الجرد هو المعدود.",
    drillTitle: "الأطباق التي يتكوّن منها هذا السطر",
    drillTruncated: "تُعرض هنا أول ألف حركة فقط من حركات هذا السطر (والطبق الذي أُعيد تحضيره له حركتان)؛ أما إجمالي السطر فيشمل الحركات كلها.",
    billOfDay: "فاتورة #{sequence} — {date}",
    stocktakeRef: "جرد رقم {number}",
    rangeInvalid:
      "اختر فترة صحيحة: تاريخ البداية قبل تاريخ النهاية، والفترة لا تزيد على 366 يومًا.",
    printRange: "من {from} إلى {to}",
    // ── Pass B (8.2–8.4): the screen, the paper and the file ──────────────
    backToStock: "العودة إلى المخزون",
    unitLine: "الوحدة: {unit}",
    avgNow: "متوسط التكلفة الآن / {unit}: {cost}",
    notFoundTitle: "لم نجد هذا المكوّن",
    notFoundBody:
      "ربما حُذف، أو أن الرابط يشير إلى مكوّن في مطعم آخر. ارجع إلى المخزون واختر المكوّن من القائمة.",
    colQty: "الكمية",
    colValue: "القيمة",
    colBalance: "الرصيد",
    colDirection: "الاتجاه",
    colBillDate: "تاريخ الفاتورة",
    colSupplier: "المورد",
    colPlates: "الأطباق",
    colUnknownRows: "حركات بتكلفة غير معروفة",
    // Counted LEDGER ROWS (phase-7 gate INFO 3): a cook and its reversal at
    // unknown cost are two entries that cancel in quantity. Worded «حركة»
    // since phase 12 (Khalifa): «قيد» is a journal entry to an accountant.
    unknownRowsOne: "حركة واحدة بتكلفة غير معروفة",
    unknownRowsTwo: "حركتان بتكلفة غير معروفة",
    unknownRowsFew: "{count} حركات بتكلفة غير معروفة",
    unknownRows: "{count} حركة بتكلفة غير معروفة",
    platesOne: "طبق واحد",
    platesTwo: "طبقان",
    platesFew: "{count} أطباق",
    plates: "{count} طبقًا",
    liveOnHand: "الكمية المسجَّلة الآن",
    liveValue: "قيمتها بمتوسط التكلفة الحالي",
    liveMatches: "مطابق لرصيد آخر المدة",
    // R8-4: the value could not be compared (unknown average) — say only
    // what WAS compared.
    liveMatchesQty: "الكمية مطابقة لرصيد آخر المدة؛ القيمة غير معروفة",
    liveNone:
      "تنتهي الفترة قبل اليوم، فلا مقارنة مع الرصيد المسجَّل الآن.",
    liveNote: "تظهر عندما تمتد الفترة حتى اليوم: الكمية المسجَّلة في النظام الآن بجانب رصيد آخر المدة، والفرق بينهما في الكمية والقيمة. ما يصنع فرقًا في القيمة: تقريب كل حركة إلى القرش؛ والحركات ذات التكلفة غير المعروفة (لا تدخل في القيم)؛ والرصيد السالب، إذ لا يدخل الرصيد السالب في حساب المتوسط فيُحسب المتوسط من التوريدة التالية وحدها، وقد يصنع ذلك فرقًا ظاهرًا لا قرشًا فقط. أما التوريدة المسجّلة بتاريخ سابق فلا تصنع وحدها فرقًا هنا.",
    // PA-7 (phase 12): «إجمالي الفترة» is GROSS — say so.
    totalsNote: "«إجمالي الفترة» إجمالي لا صافٍ: الوارد يشمل المشتريات والمرتجع من المطبخ، والمنصرف يشمل مرتجع المشتريات؛ فصافي المشتريات هو المشتريات ناقص مرتجع المشتريات.",
    // PA-5 / PO-2 / PA-11 (phase 12) — what a row is, in its own words.
    supplierReturnOf: "عن فاتورة {ref} — {date}",
    supplierReturnOfDelivery: "عن توريدة {date}",
    returnSourceVoid: "إلغاء",
    returnSourceRefund: "مرتجع نقود",
    freeLot: "مجانية",
    colOriginalInvoice: "الفاتورة الأصلية",
    colOriginalDate: "تاريخ الفاتورة الأصلية",
    colDish: "الطبق",
    colReturnSource: "سبب المرتجع",
    colQtyInUnit: "كمية وارد ({unit})",
    colQtyOutUnit: "كمية منصرف ({unit})",
    colBalanceQtyUnit: "الرصيد ({unit})",
    // PA-8 (phase 12): the file says whose it is, what, when and by whom.
    csvStore: "المطعم",
    csvIngredient: "المكوّن",
    csvRange: "الفترة",
    csvExportedAt: "وقت التصدير",
    csvExportedBy: "صدّره",
    csvZone: "بتوقيت {zone}",
    csvZoneDevice: "بتوقيت الجهاز",
    // PA-13 (phase 12): the paper's sign-off lines.
    printPreparedBy: "أعدّه",
    printReviewedBy: "راجعه",
    printedBy: "طبعه: {name}",
    // PA-4 (phase 12): the drill-down's outcome column.
    drillColOutcome: "ما حدث للطبق",
    outcomeSold: "مباع",
    outcomeOpenBill: "فاتورة لم تُسدَّد بعد",
    outcomeCancelledBill: "فاتورة ملغاة",
    outcomeStaffMeal: "وجبة الموظفين",
    outcomeComped: "على الحساب",
    outcomeWrittenOff: "بلا دفع",
    outcomeVoided: "ملغى",
    outcomeRefunded: "أُعيد مبلغه",
    drillColBill: "الفاتورة",
    drillColProduct: "الطبق",
    drillColQty: "الكمية",
    drillTotal: "الإجمالي",
    drillEmpty: "لا توجد أطباق مسجَّلة على هذا السطر.",
    printButton: "طباعة البطاقة",
    csvItem: "البند",
    csvFigure: "الرقم",
  },
  addonOverrideHistory: {
    action: "سجل أسعار الإضافات",
    title: "سجل أسعار إضافات {name}",
    subtitle:
      "مَن غيّر سعر إضافة على هذا الصنف، والقيمة السابقة والجديدة، ووقت التغيير.",
    // اللبس الوحيد المتوقع: الحفظ بيكتب المجموعة كلها، فالاختيارات اللي محدش
    // لمسها بتتسجّل برضه. بنقول كده قبل الليستة عشان محدش يقراها كتكرار.
    note: "يسجّل كل حفظ جميع اختيارات المجموعة، حتى ما لم يتغير منها — لذلك لا يعني ظهور السعر نفسه وجود تكرار خاطئ.",
    emptyTitle: "لم تتغير أسعار إضافات هذا الصنف",
    emptyBody:
      "يعني ذلك أنه يستخدم أسعار المجموعات مثل أي صنف آخر. هذا وضع طبيعي وليس خطأ.",
    truncatedNote:
      "تغييرات هذا الصنف أكثر مما نعرضه — والقائمة أدناه هي أحدث جزء من السجل، أي حد أدنى لا السجل كاملًا.",
    unknownActor: "عضو أُزيل من الفريق",
    deletedOption: "اختيار أُزيل",
    // الحالات الأربعة. «سعر المجموعة» و«مجاني» لازم يفضلوا مختلفين: الأول يعني
    // الصنف مالوش رأي، والتاني قرار إن الإضافة ببلاش عليه هو بالذات.
    stateInherits: "سعر المجموعة",
    stateFree: "مجاني",
    stateWithdrawn: "غير متاح لهذا الصنف",
    // «إلى» مش «لـ»: الـ«لـ» سابقة بتلزق بالكلمة اللي بعدها («لسعر المجموعة»)
    // لكن بتتكتب منفصلة قدام رقم («لـ 45 جنيه»)، و{to} ممكن تكون الاتنين —
    // فقالب واحد بـ«لـ» بيطلع غلط في نص الحالات.
    changed: "من {from} إلى {to}",
    // مش «أول مرة»: الصفوف اللي اتكتبت قبل ما نسجّل القيمة السابقة مالهاش
    // قيمة قبلها متسجّلة، وده مش معناه إنها أول تسعير.
    noRecordedPrevious: "لا توجد قيمة سابقة مسجّلة — حُفظت: {to}",
    loadingOption: "جارٍ تحميل الاختيارات…",
    // نداء الاختيارات وقع. لو فضلنا نقول «بنحمّل» يبقى بنوعد باسم مش جاي.
    unresolvedOption: "اسم الاختيار غير ظاهر",
    restated: "حُفظت كما هي: {to}",
  },
  availabilityHistory: {
    action: "سجل التوفّر",
    title: "سجل توفّر {name}",
    subtitle: "مَن حدّد هذا الصنف نافدًا، ومَن أعاده، ومتى.",
    // اللبس الوحيد المتوقع في الشاشة دي: سطر بيأكّد نفس الحالة مش تكرار غلط.
    note: "تُسجّل هنا كل ضغطة على مفتاح التوفر، حتى عند تأكيد أن صنفًا متوفر بالفعل. هذا ليس تكرارًا؛ فالتأكيد نفسه قرار مسجّل.",
    emptyTitle: "لا توجد قرارات تخص هذا الصنف",
    emptyBody:
      "يعني ذلك أن أحدًا لم يحدده نافدًا ولم يُعد إتاحته منذ إنشائه. هذه نتيجة كاملة وليست مشكلة.",
    truncatedNote:
      "قرارات هذا الصنف أكثر مما نعرضه — والقائمة أدناه هي أحدث جزء من السجل، أي حد أدنى لا السجل كاملًا.",
    // العضو اتشال من الفريق، والقرار فضل. بنقول كده صريح بدل خانة فاضية.
    unknownActor: "عضو أُزيل من الفريق",
    wentOff: "حدّده نافدًا",
    cameBack: "أعاده متوفرًا",
    stillOff: "أكّد أنه نافد",
    stillOn: "أكّد أنه متوفر",
    setOff: "سجّله نافدًا",
    setOn: "سجّله متوفرًا",
    // ── نفس السجل، بس للمكوّن (B18) ───────────────────────────────────────
    // عنوان وشرح مستقلين عشان الادعاء مختلف: الصنف اللي نفد مش بيتباع، أما
    // المكوّن اللي خلص الأصناف بتفضل تتباع وبس الجرسون بيتنبّه.
    ingredientAction: "سجل «نفد»",
    ingredientTitle: "سجل «نفد» — {name}",
    ingredientSubtitle:
      "مَن حدّد هذا المكوّن نافدًا، ومَن أعاده، ومتى. ظلت الأصناف التي تحتوي عليه متاحة للبيع؛ فهذا سجل تنبيه لا سجل منع.",
    ingredientEmptyTitle: "لا توجد قرارات تخص هذا المكوّن",
    ingredientEmptyBody:
      "يعني ذلك أن أحدًا لم يحدده نافدًا ولم يُعد إتاحته منذ تسجيله. هذه نتيجة كاملة وليست مشكلة.",
    // نفس القرارات الستة، بس بكلام المكوّن مش كلام الصنف. لما السجل كان
    // بيستعير كلمات الصنف كان بيقول «علّمه نفد» تحت عنوان بيقول «خلص» —
    // كلمتين لحالة واحدة على شاشة واحدة.
    ingredientWentOff: "أبلغ بنفاده",
    ingredientCameBack: "أعاده",
    ingredientStillOff: "أكّد نفاده",
    ingredientStillOn: "أكّد توفّره",
    ingredientSetOff: "سجّله نافدًا",
    ingredientSetOn: "سجّله متوفرًا",
  },
  /**
   * The spent-vs-gained report (phase 2, handoff §8). Copy mirrors the money
   * rules pinned in `restaurant-report.types.ts`:
   *
   * - Revenue is CLOSED + SETTLED only; an all-comped invoice closes at zero
   *   and is excluded from the average ticket on purpose (`allCompedNote`).
   * - A day can show cost with zero revenue (open tables, prep days) — the
   *   per-day table copy says that plainly rather than let it look broken.
   */
  reports: {
    // زرار التنزيل مشترك بين تقارير الأصناف والموظفين والأوقات — نفس الكلمة
    // اللي في شاشة الفواتير، عشان التنزيل يبقى حاجة واحدة في البرنامج كله.
    exportCsv: "تنزيل CSV",
    title: "المصروف والدخل",
    // الاسم زي ما هو — ده التقرير اللي العميل طلبه بالاسم ده. الوصف بقى بيقول
    // المقارنة اللي فوق فعلاً: الدخل ناقص تكلفة اللي اتباع.
    description: "الدخل وتكلفة الطعام المباع والفرق بينهما — ضمن أي نطاق زمني.",
    noAccessTitle: "لا تملك صلاحية عرض التقرير",
    noAccessBody:
      "تحتاج هذه الشاشة إلى صلاحية عرض التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك — فهذه الصلاحية لا تُمنح تلقائيًا حتى للمدير.",
    rangeLabel: "من {from} إلى {to}",
    dateRangeApply: "تطبيق",
    dateRangeClear: "آخر 7 أيام",
    dateRangePrevious: "الشهر السابق",
    dateRangeNext: "الشهر التالي",
    // PO-7 (phase 12): a PAPER table wider than its card says it scrolls.
    tableScrollCue: "اسحب الجدول جانبًا لترى باقي الأعمدة",
    // The PAPER report toolbar's period presets (2026-10 redesign). The custom
    // range picker stays beside them for any other window.
    paperPeriod: "الفترة",
    paperPresetToday: "اليوم",
    paperPresetYesterday: "أمس",
    paperPreset7d: "7 أيام",
    paperPreset30d: "30 يومًا",
    // P6-Q4 (2026-10-27): من أول شهر العمل الحالي لليوم.
    paperPresetMonth: "هذا الشهر",
    // The toolbar's own "any other window" choice beside the presets. It
    // opens the range picker; once a window is picked it shows its dates.
    paperPresetCustom: "فترة مخصصة",
    paperCustomRangeLabel: "{from} – {to}",
    paperCustomDialog: "اختر الفترة",
    paperCustomHint: "اختر اليوم الأول ثم الأخير، أو يومًا واحدًا فقط.",
    paperCustomTooLong:
      "يغطي التقرير {span} كحد أقصى؛ اختر فترة أقصر.",
    paperCustomFuture: "لا يمكن اختيار يوم بعد يوم العمل الحالي.",
    dateRangeInvalid:
      "هذه المدة غير مسموحة؛ أقصى مدى 92 يومًا. قصّر المدة وحاول مرة أخرى.",
    loading: "جارٍ تجهيز التقرير...",
    emptyTitle: "لا توجد بيانات في هذه المدة حتى الآن",
    emptyBody:
      "يمتلئ التقرير عند وجود فواتير أو مشتريات في المدة التي اخترتها. جرّب نطاقًا زمنيًا آخر.",
    statCostOfSalesSub: "تكلفة الطعام الذي بيع بالفعل",
    statMargin: "هامش الربح",
    basesTitle: "أساس حساب الهامش",
    basesEstimateNote:
      "هذا الرقم تقديري وليس قياسًا نهائيًا: تُحسب تكلفة المبيعات بمتوسط شراء شامل للضريبة، وتُستمد النسبة المستبعدة من مشتريات هذه المدة، بينما قد يكون المخزون المستخدم مشتَرى سابقًا بنسبة أخرى. اعتبره مؤشرًا لا رقمًا نهائيًا.",
    statCostOfSalesNet: "≈ {amount} دون ضريبة المشتريات (تقديري)",
    statAvgTicket: "متوسط الفاتورة",
    // B2 — سطر النطاق: التقرير على اليوم التجاري، والتقسيم حسب طريقة الدفع
    // مش ممكن يغطي غير المتحصّل، فالباقي مكتوب بالاسم مش مسكوت عنه.
    scopeNote:
      "تشمل الأرقام يوم العمل كاملًا، ويغطي التقسيم حسب طريقة الدفع الفواتير المُحصّلة فقط — ويظهر الباقي ضمن «لم يُحصّل بعد».",
    methodCash: "نقد",
    methodCard: "بطاقة",
    methodInstapay: "إنستاباي",
    methodUnsettled: "لم يُحصّل بعد",
    // The base is IN the sentence, not left to be read off the bucket above —
    // that bucket counts bills the platform SETTLED and this counts bills the
    // platform BROUGHT, and reading one against the other says 65%.
    commissionNote:
      "جلبت التطبيقات {orders} في هذه المدة، وأخذت منها {amount}. لا تُخصم العمولة من الدخل أعلاه؛ فالدخل هو ما دفعه العملاء. محسوبة بالنسبة والأساس المسجّلين على كل فاتورة؛ كشف المنصة هو المرجع.",
    commissionEmpty: "لا توجد مبيعات واردة من تطبيق في هذه الفترة.",
    // T4 — ضريبة المدخلات التي تحملها العمولة غالبًا: رقم مشتق يُراجع مع
    // الفاتورة الضريبية للمنصة، لا رقم مطابقة ولا رقم إقرار.
    commissionVatEstimate: "ضريبة مدخلات تقديرية على العمولة",
    // D93 — رسوم التوصيل بتنزل من السلم زي رسم الخدمة: دي فلوس الطيار، ودي
    // فلوس الصالة، و«صافي مبيعات الأكل» تحت مش أي واحدة منهم. لازم السطور توصل
    // للرقم اللي تحتيها.
    grossToNetLessDeliveryFee: "ناقص رسوم التوصيل",
    grossToNetNet: "صافي المبيعات",
    grossToNetNote:
      "صافي المبيعات هو دخل المطعم الفعلي — فالضريبة تُحصّل لحساب مصلحة الضرائب، ورسم الخدمة ليس ثمن الطعام. وعلى هذا الرقم يُحسب هامش الربح.",
    // §12 DECISION 2 and R13, on the screen the figures are read on — a filing
    // figure that moves has to say why where it moves (trap 1).
    refundsNote: "تُخصم النقود المعادة إلى العميل من الدخل ورسم الخدمة والضريبة، وتُحسب في يوم إعادتها لا يوم الفاتورة؛ لذلك ينخفض معها الموقف الضريبي. تبقى تكلفة الطعام كما هي لأنها سُجّلت عند التحضير، ولذلك ينخفض الهامش — إلا إذا اختار الكاشير «مرتجع من المطبخ»: عندها تعود المكونات إلى المخزون وتُخصم تكلفتها من تكلفة المبيعات في يوم الإرجاع.",
    refundsMethodNote:
      "يُخصم المرتجع من خانة طريقة الدفع التي أُعيد بها: النقد من النقد، والبطاقة من البطاقة، وإنستاباي من إنستاباي. ولا يمر تحويل إنستاباي بأي خزينة.",
    consumptionSold: "تكلفة المبيعات",
    // B117/C1 — الصف ده كان ناقص خالص من السلّم، والإجمالي اللي فوقه كان شامله:
    // ٦ صفوف بتجمع ٨٤٨٫٣٠ تحت إجمالي مطبوع ١٬٣٠٨٫٣٠. الـ Z نفسه بيسمّي نفس الرقم
    // «تسوية المخزون»، فشاشتين فلوس كانوا بيختلفوا على إن تكلفة المحل اتكوّنت من إيه.
    // نفس الاسم المستخدم على الـ Z بالظبط، عشان اللي بيقرا الاتنين يلاقي كلمة واحدة.
    consumptionAdjustment: "تسوية المخزون",
    // B117/C1 — ورقة «محسوب إزاي؟» بتاعة كارت التكاليف ما كانتش بتذكر التسويات
    // خالص، فحتى لما كان الرقم داخل في الإجمالي مافيش حاجة بتقول ده إيه.
    basisDefAdjustmentCost: "مخزون خرج أو ظهر من دون بيع — حركة هالك أو تالف أو تصحيح أو تسوية جرد. تُسعّر كل حركة بتكلفتها وقت تسجيلها. لا تدخل في تكلفة الطعام المباع أو هامش الربح، لكنها تدخل في إجمالي التكلفة لأنها خسارة فعلية للمطعم. وتظهر تسوية الجرد التي تجد زيادة في المخزون بقيمة سالبة.",
    // C-r2-5 — اسم نفس الرقم في ملف الـCSV. «الإجمالي» لوحدها كافية تحت سلّم
    // التكاليف على الشاشة، لكن في بلوك إجماليات المدة وسط الضريبة والهامش
    // مابتقولش إجمالي إيه. ونفس اللفظ اللي مكتوب في جملة التسوية نفسها
    // («داخل في إجمالي التكلفة»)، عشان الجملة والصف يسمّوا حاجة واحدة.
    consumptionTotalCost: "إجمالي التكلفة",
    tiersTitle: "الدخل حسب الشريحة",
    tierBase: "الأسعار الأساسية",
    tiersEmpty: "لا توجد فواتير في هذه المدة حتى الآن.",
    // B94 — أي تطبيق بيكلّف كام. الرقم الواحد بيقول التطبيقات خدت كام، ومش
    // بيقول أنهي واحد فيهم يستاهل.
    platformsTitle: "التطبيقات: ما جلبته وما أخذته",
    daysTitle: "الأداء اليومي",
    dayDate: "اليوم",
    dayRevenue: "الدخل",
    dayInvoices: "الفواتير",
    daySoldCost: "تكلفة المبيعات",
    dayUnpaidCost: "هالك وعلى الحساب",
    dayPurchases: "المشتريات",
    dayCostNoRevenueHint:
      "وجود تكلفة بلا دخل طبيعي في يوم لا تزال طاولاته مفتوحة أو في يوم تحضير.",
    ingredientsTitle: "استهلاك المكوّنات",
    ingredientName: "المكوّن",
    comparePrevious: "مقارنة بالسابقة",
    comparisonRangeNote: "المقارنة مع {from} إلى {to}",
    deltaUp: "▲ {pct}%",
    deltaDown: "▼ {pct}%",
    deltaFlat: "بدون تغيير",
    // S7/S15 — سطر الضريبة القابل للنسخ.
    // وحدة ٢ — الفلوس اللي مش أكل (F5، B2، ونص B46 بتاع تكلفة الدليفري).
    // كل زوج: اللي اتحصّل جنب اللي اتسلّم. حركات بيتتبّعها، مش مديونية
    // بتتراكم: مفيش رصيد جاري ولا رصيد لكل واحد من الفريق، والبطاقة بتقول كده
    // بصوت عالي — الحد ده لو محدش قاله، القارئ هيكتشفه وهو بيثق في رقم
    // ماوعدش بالحاجة دي أصلاً.
    nonFoodTitle: "إيرادات غير غذائية",
    // T12 — هل يدفع التوصيل ثمن نفسه: الرسوم المحصّلة مقابل ما دُفع للسائقين.
    deliveryTitle: "التوصيل: الرسوم مقابل تكلفة الطيارين",
    // الزوج بيبان من التسجيل: اللي اتحصّل من الفواتير، واللي اتسلّم من حركات
    // الدرج. لو المحل بياخد الخدمة لنفسه مفيش حاجة تتسلّم أصلاً، فالصفر هنا
    // جواب كامل مش التزام ماتمّش.
    nonFoodServiceChargeKeptNote:
      "إعداد المطعم ينص على احتفاظه برسم الخدمة، لذلك لا يوجد مبلغ يُسلّم إلى الفريق، ويكون الصفر هنا صحيحًا.",
    nonFoodServiceChargeNote:
      "هذا الفرق خاص بالمدة المعروضة فقط، وليس رصيدًا مستحقًا للفريق؛ فتوزيع خدمة الشهر السابق يُسجّل في شهره، وعدم وجود توزيع في مدة ما لا يعني أن رسم الخدمة كله لا يزال مستحقًا.",
    // نص البقشيش اللي البرنامج مش شايفه، مكتوب عشان الرقم مايتقرأش على إنه
    // كل بقشيش المدة.
    nonFoodTipsNote:
      "يشمل الرقم البقشيش النقدي الذي دخل الخزينة فقط. أما البقشيش المضاف على البطاقة فلا يصل إلى الخزينة أو الفاتورة، لذلك لا يراه النظام. وقد يظهر الفرق سالبًا إذا سُلّم بقشيش نقدًا وكان قد حُصّل أصلًا عبر البطاقة.",
    // نص التكلفة بس. الإيراد (رسم الدليفري على الفاتورة، D93) بقى موجود وبيتشال
    // من الصافي في سلّم «من إجمالي المحصّل لصافي المبيعات» — النص كان لسه بيقول
    // إنه مش موجود لحد 2026-09-18.
    nonFoodDeliveryNote:
      "هذا هو المبلغ المدفوع للطيارين خلال المدة، ويمثل جانب التكلفة فقط. تظهر رسوم التوصيل المحصّلة على الفواتير أعلاه في «ناقص رسوم التوصيل»؛ قارن الرقمين لمعرفة ما إذا كانت الرسوم تغطي التكلفة.",
    vatTitle: "ضريبة القيمة المضافة",
    vatOutput: "ضريبة على المبيعات",
    vatInput: "ضريبة على المشتريات",
    // F8 (وحدة 5) — الأساس التاني لضريبة الشرا، جنب الأول مش بدله. الكلمة هي
    // «ورا فاتورة ضريبية» مش «قابلة للخصم»: ده وصف لحقايق موجودة في الدفتر،
    // والقرار الضريبي نفسه مش قرار الشاشة.
    vatInputSupported: "منها خلف فاتورة ضريبية",
    vatPositionOnSupported: "الموقف عند الحساب على المدعوم فقط",
    vatPosition: "الموقف الضريبي (مبيعات ناقص مشتريات)",
    vatNote:
      "هذا احتساب تقريبي من بيانات المطعم فقط، وليس إقرارًا ضريبيًا؛ إذ لا يشمل مصروفات أخرى مثل الإيجار والفواتير.",
    vatRefunded: "ضريبة مرتجعة",
    // F6 — الرقم اللي بره الحساب لازم يكون مكتوب عليه في العنوان نفسه إنه
    // مش رقم إقرار، مش في تلميح. الخطر بتاع القرار ده حقيقي بس لما الرقم
    // التاني يبقى مخبي — وهو مش مخبي، يبقى ماينفعش يبقى صعب اللقيان كمان.
    vatWriteOffExcluded: "ضريبة فواتير أُغلقت بلا دفع (ليست رقم إقرار)",
    vatPositionIfCounted: "الموقف عند احتسابها (ليس رقم إقرار)",
    vatWriteOffNote:
      "لا تُحتسب ضريبة الفاتورة المغلقة بلا دفع ضمن الضريبة المستحقة في الرقم أعلاه. ويعرض الرقم الآخر ما كان سيصبح عليه الموقف إذا احتُسبت. ترتبط الضريبة في مصر بالفاتورة لا بالتحصيل، وعادة ما يتطلب إلغاء ضريبة فاتورة غير مدفوعة إشعار خصم، وهو غير متاح في النظام. استشر محاسبك قبل تقديم أي رقم من هنا.",
    // الوحدة 3 — نفس البطاقة لما المحل يقلب الإعداد. اللي بيتصدّر بيتغيّر،
    // وعلامة «مش رقم إقرار» بتمشي مع الرقم اللي مش متصدّر — هي علامة على
    // التاني مش على رقم بعينه.
    vatPositionCounted: "الموقف الضريبي (شامل الفواتير المشطوبة)",
    // مش «فوق» ولا «تحت»: السطر ده بيتحرك حسب الأساس المتصدّر — لما يتحسب
    // بيبقى جوّه السلّم نفسه فوق الموقف، ولما ما يتحسبش بيبقى تحت الخط.
    vatWriteOffIncluded: "ضريبة فواتير أُغلقت بلا دفع (محتسبة في الموقف)",
    vatPositionExcludedNotFiling: "الموقف من دونها (ليس رقم إقرار)",
    vatWriteOffNoteCounted:
      "أُعدّ هذا المطعم لاحتساب ضريبة الفاتورة المشطوبة ضمن الضريبة المستحقة، وهو الأساس المستخدم في الرقم أعلاه. ويعرض الرقم الآخر الموقف من دونها. ترتبط الضريبة في مصر بالفاتورة لا بالتحصيل، لكن إلغاء ضريبة فاتورة غير مدفوعة يتطلب عادة إشعار خصم، وهو غير متاح في النظام. استشر محاسبك قبل تقديم أي رقم من هنا.",
    // B30 — فلوس رجعت فعلاً بس مش داخلة في أي رقم فوق، عشان فاتورتها أصلاً
    // خارج الإيرادات على إنها مشيت من غير دفع. لو خصمناها تاني نكون شلنا
    // نفس الفلوس مرتين؛ ولو سكتنا عنها تكون اختفت.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    refundsNotNettedNoteOne:
      "أُعيد إلى العميل مبلغ {amount} عن صنف واحد من فواتير أُغلقت بلا دفع.",
    refundsNotNettedNoteTwo:
      "أُعيد إلى العميل مبلغ {amount} عن صنفين من فواتير أُغلقت بلا دفع.",
    refundsNotNettedNoteFew:
      "أُعيد إلى العميل مبلغ {amount} عن {count} أصناف من فواتير أُغلقت بلا دفع.",
    refundsNotNettedNote:
      "أُعيد إلى العميل مبلغ {amount} عن {count} صنفًا من فواتير أُغلقت بلا دفع.",
    /* الذيل اللي كان متكرر حرفياً جوّه أربع الصيغ. جملة واحدة جنب الرقم على
       نفس الكارت: دي مش تفسير زيادة، دي السبب اللي بيمنع طرح نفس الفلوس
       مرتين — ولازم تتقري مع الرقم. */
    refundsNotNettedBasis:
      "لا تُخصم من الأرقام أعلاه لأن الفاتورة نفسها مستبعدة أصلًا من الإيرادات؛ وإلا خُصمت النقود نفسها مرتين. وقد خرجت هذه النقود بالفعل من الخزينة.",
    vatCopied: "تم نسخ السطر",
    vatCopyFailed: "تعذّر النسخ؛ انسخ السطر يدويًا.",
    purchasesInputVatNote:
      "يتضمن ذلك {amount} ضريبة مسجّلة على المشتريات، إن وُجدت.",
    // S5 — الكوفرات. Covers contract (2026-09-18): دلوقتي على فواتير الصالة بس
    // (الفاتورة الوحيدة اللي بيتسأل فيها أصلاً)، والـ«مش متاح» بقى ليه شرط
    // واضح (٧٠٪ / ١٠ فواتير) مش مجرد «محدش سجّل».
    statCovers: "عدد الضيوف",
    statAvgSpendPerCover: "متوسط الإنفاق لكل ضيف",
    // كانت `statAvgSpendPerCoverUnknown` بتفترض إن محدش سجّل ولا كوفر خالص.
    // دلوقتي القيمة ممكن تبقى null لأسباب تانية (أقل من ٧٠٪ أو أقل من ١٠
    // فواتير) حتى لو فيه كوفرات متسجّلة، فالنص بقى بيقول السبب الحقيقي.
    statAvgSpendPerCoverUnknown:
      "غير متاح — لم يُسجّل عدد الضيوف في أغلب فواتير الصالة",
    // الذيل القصير اللي الشاشة بتوريه مرة واحدة تحت بلوك الكوفرات، بدل ما
    // يتفرّق على أكتر من جملة فرعية (founder: التقرير يفضل قصير).
    statCoversFootnote:
      "هذا هو عدد الضيوف الذي سجّله الويتر. يُحسب على فواتير الصالة فقط قبل الضريبة والخدمة والتوصيل، ويجب تسجيله في 70% منها على الأقل وبحد أدنى 10 فواتير.",
    // NEW — البلوك اللي بيوري الدخل مقسّم حسب القناة (صالة / تيك أواي /
    // دليفري)، صفوف مكدّسة مش جدول كثيف عشان يبان كويس على 390px.
    byChannelTitle: "حسب القناة",
    byChannelColChannel: "القناة",
    byChannelColInvoiceCount: "عدد الفواتير",
    byChannelItemsPerBillLabel: "متوسط الأصناف في الفاتورة",
    csvCoveredOfDineIn: "عدد الضيوف المسجّل في فواتير الصالة",
    // B117/Q5 — what the kitchen cooked twice, and what it cost. The MANAGER's
    // report: remake cost otherwise lives only on the owner's rung.
    remakesTitle: "الإعادات وتكلفتها",
    remakesCountLabel: "عدد الإعادات",
    remakesCostLabel: "تكلفة الإعادات",
    remakesByDish: "الإعادات حسب الصنف",
    remakesDish: "الصنف",
    remakesEmptyTitle: "لا توجد إعادات في هذه الفترة",
    remakesEmptyBody: "لم يُعد المطبخ أي طبق، وهذه نتيجة جيدة.",
    /*
     * ITS OWN REFUSAL, not the spend-gain one it borrowed. That copy ends with
     * «مش بتتديها تلقائي حتى للمدير» — true of the owner's margins, false here:
     * the remakes report is on the SUPERVISION rung and the manager opens it.
     * It also pointed the reader at the wrong grant, which is how an owner hands
     * over the P&L to fix a supervision screen.
     */
    remakesNoAccessTitle: "لا تملك صلاحية عرض هذا التقرير",
    remakesNoAccessBody:
      "تتطلب هذه الشاشة صلاحية متابعة التشغيل، وهي متاحة للمدير وصاحب المطعم. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    /*
     * C-r2-4 — الأساس. التقرير ده نزل من غير باب «محسوب إزاي؟» ومن غير ملف،
     * والأربع جُمل دي مالهاش مكان تانى على الشاشة. المدير بيتقاس عليها.
     * الشاشة والورقة وملف الـCSV بيقروا من نفس المصفوفة (`basisNotes`).
     */
    remakesBasisCount:
      "تُحسب عودة الطبق إلى المطبخ مرة واحدة كإعادة واحدة، حتى إن احتوى على عشرة مكوّنات أو لم تُسجّل له وصفة. يكون العد حسب الطبق والمحاولة، لا حسب صفوف المخزون المصروفة.",
    remakesBasisCost:
      "هي تكلفة المكوّنات المسجّلة في الإعادة نفسها بسعرها وقت الصرف. لا نعيد تسعير الماضي بمتوسط اليوم. الإعادة بلا وصفة تزيد العدد، لكن تكلفتها المسجّلة صفر؛ هذا لا يعني أن الطعام مجاني.",
    remakesBasisWindowTerm: "المدة",
    remakesBasisWindow:
      "تُحسب وفق يوم العمل مثل بقية أرقام المطعم. وتُنسب الإعادة بعد منتصف الليل إلى يوم العمل الذي فُتح فيه الطلب.",
    remakesBasisOpenTerm: "الفواتير المفتوحة",
    remakesBasisOpen:
      "تدخل في هذه الأرقام؛ فقد خرجت المكوّنات من المخزون عند بدء الطاهي التحضير، فتحققت التكلفة ولو ظلت الفاتورة مفتوحة أو أُلغيت لاحقًا.",
    /* B118 — بلوك الأسباب بيقرا من سجل الأوامر، مش من عمود السبب على الصنف
       (العمود ده بيتكتب فوق نفسه، فتلات إعادات على طبق واحد كانوا هيبانوا
       إعادة واحدة). و«سبب قديم» بقعد في الجدول عشان مجموع الأسباب يطلع نفس
       الرقم اللي فوق بالظبط. */
    remakesBasisReasonTerm: "الأسباب",
    remakesBasisReason:
      "من سجل الأوامر نفسه: كل إعادة وسببها المسجّل وقتها، لا آخر سبب مسجّل على الطبق. وتعني «سبب قديم» إعادة نُفّذت قبل أن يصبح السبب مطلوبًا، وتُحتسب لكي يساوي مجموع الأسباب إجمالي الإعادات أعلاه.",
    remakesByReason: "الإعادات بالسبب",
    remakesReason: "السبب",
    // PAPER (2026-10) — تقارير المبيعات: ملاحظة الفترة في شريط التقرير، وكام
    // يوم عمل بتغطيه المدة.
    salesPaperDaysOne: "يوم عمل واحد",
    salesPaperDaysTwo: "يوما عمل",
    salesPaperDaysFew: "{count} أيام عمل",
    salesPaperDays: "{count} يوم عمل",
    // PAPER parity (2026-10-02) — شريط التقرير زي التصميم بالظبط: سطر الفترة
    // هو نفسه اللي بيفتح اختيار التاريخ، و«طريقة الحساب» و«تصدير».
    paperNoteToday: "اليوم، من {hour} (بداية يوم العمل)",
    paperNoteTodayPlain: "اليوم، من بداية يوم العمل",
    paperNoteYesterday: "أمس، من {hour} إلى {hour}",
    paperNoteYesterdayPlain: "يوم عمل أمس",
    paperNoteLastOne: "آخر يوم عمل",
    paperNoteLastTwo: "آخر يومَي عمل",
    paperNoteLastFew: "آخر {count} أيام عمل",
    paperNoteLast: "آخر {count} يوم عمل",
    paperNoteMonth: "{month} حتى اليوم",
    paperBasisDoor: "طريقة الحساب",
    paperExport: "تصدير",
    // PAPER — مربعات تقرير الإعادات.
    remakesPaperDishesOne: "على صنف واحد",
    remakesPaperDishesTwo: "على صنفين",
    remakesPaperDishesFew: "على {count} أصناف",
    remakesPaperDishes: "على {count} صنف",
    remakesPaperPerRemake: "{amount} في المتوسط لكل إعادة",
    remakesPaperTopDish: "أغلى صنف في الإعادات",
    remakesPaperTopReason: "أغلى سبب",
    remakesPaperTopSubOne: "{amount} · إعادة واحدة",
    remakesPaperTopSubTwo: "{amount} · إعادتان",
    remakesPaperTopSubFew: "{amount} · {count} إعادات",
    remakesPaperTopSub: "{amount} · {count} إعادة",
    remakesPaperNone: "لا يوجد",
    reasonLegacy: "سبب قديم (قبل تصنيف الأسباب)",
    // S7 — أعمدة يومية إضافية.
    dayService: "رسم الخدمة",
    dayVat: "الضريبة",
    dayNet: "الصافي",
    // ── الجولة التانية من إعادة تصميم التقارير (2026-09-11) ──────────────
    // تسع كروت بقوا تلاتة، و~2400 حرف من الكلام بخط 12px طلعوا من الشاشة
    // للباب الواحد وللملاحظات في ملف الـCSV. دي المفاتيح اللي الشكل ده
    // محتاجها.
    /** التلات كروت. كل عنوان هو كمان عنوان قسم في ورقة الشرح وسطر في
     *  ملاحظات الـCSV، فمستحيل التلاتة يختلفوا. */
    cardSales: "المبيعات والتحصيل",
    cardCosts: "التكاليف",
    // الخط الفاصل بين القارئين، مسمّى بمحتواه — مش بالقارئ بتاعه. عنوان
    // بيخاطب المستخدم («لمحاسبك») بيخلّي التقرير كأنه بيكلّمه، مش عنوان قسم
    // في تقرير (مراجعة المؤسس، 2026-09-11).
    cardTaxBases: "الضريبة والأوعية الضريبية",
    // نفس كلمات باب أي تقرير تاني، عشان المستخدم يتعلّم حاجة واحدة في كل
    // التقارير مش أربعة.
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذا التقرير",
    basisTermRefunds: "المرتجعات",
    basisTermScope: "ما يدخل في الحساب",
    basisDefSoldCost: "تكلفة الطعام المباع بسعره وقت التحضير، لا بمتوسط اليوم.",
    basisTermWriteOffVat: "ضريبة الفواتير المغلقة بلا دفع",
    purchasesTerm: "المشتريات",
    purchasesNote:
      "تُحسب وقت الشراء لا وقت الطهي — فيوم التوريد ليس خسارة.",
    csvPeriodTotals: "إجماليات المدة",
    /* D139 (R8-3) — the file says how many movements its costs leave out. */
    csvUnknownCostRows: "حركات بتكلفة غير معروفة (غير محسوبة في التكاليف)",
    // D142 — تُضاف في آخر الملف (ملف يُلحق به فقط).
    daySupplierReturns: "مرتجعات مشتريات",
    csvSupplierReturnsTotal: "قيمة مرتجعات المشتريات",
    csvSupplierReturnsCount: "عدد مرتجعات المشتريات",
    csvSupplierReturnsVat: "ضريبة المدخلات المخصومة على المرتجعات",
    basisTermSupplierReturns: "مرتجعات مشتريات",
    basisDefSupplierReturns:
      "قيمة ما أُرجع للموردين في الفترة، على يوم الإرجاع. لا تُخصم من المشتريات، فالمشتريات هي كل توريدة استلمتها كما سُجِّلت. أما ضريبتها فتُخصم من ضريبة المدخلات، فضريبة المدخلات هنا صافية بعد المرتجعات.",
    purchasesWithoutVatCount: "مشتريات بلا رقم ضريبي",
    purchasesWithoutTaxInvoiceCount: "مشتريات بلا فاتورة ضريبية",
    /** قاموس الحالة المقفول. أربع كلمات، مش قابلة للضغط، على الرقم نفسه —
     *  لا جملة ولا لون. الرقم اللي مالوش أي واحدة منهم يبقى مقيس، وغيابها
     *  هو الترميز. */
    markEstimate: "تقديري",
    markFloor: "حد أدنى",
    /** التنقل جوه الصفحة. التنقل بالعناوين بيخدم قارئ الشاشة أصلاً؛ ده
     *  موجود للي بيستخدم الكيبورد وللي مكبّر 400%. */
    jumpTo: "في هذه الصفحة",
    /** علامة الحد الأدنى، على صف ضريبة الشرا نفسه. مش «ناقص» ومش جملة:
     *  العدد هو اللي بيخليه حد أدنى، فالعدد جوه العلامة. */
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    markFloorWithCountOne: "حد أدنى — عملية شراء واحدة بلا ضريبة",
    markFloorWithCountTwo: "حد أدنى — عمليتا شراء بلا ضريبة",
    markFloorWithCountFew: "حد أدنى — {count} عمليات شراء بلا ضريبة",
    markFloorWithCount: "حد أدنى — {count} عملية شراء بلا ضريبة",
    /** علامة F8، على صف «منها ورا فاتورة ضريبية». العدد هو اللي بيفسّر الفرق
     *  بين الرقمين، فالعدد جوه العلامة زي أختها فوق. */
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    markUndocumentedWithCountOne: "عملية شراء واحدة بلا فاتورة ضريبية",
    markUndocumentedWithCountTwo: "عمليتا شراء بلا فاتورة ضريبية",
    markUndocumentedWithCountFew: "{count} عمليات شراء بلا فاتورة ضريبية",
    markUndocumentedWithCount: "{count} عملية شراء بلا فاتورة ضريبية",
  },
  /** Per-item profitability (S1) — `manage/reports/items`, `RESTAURANT_REPORT_VIEW`. */
  /* PAPER's money colour key (`components/paperTones.tsx`) — see English. */
  paperTones: {
    legend: "دليل الألوان",
    neg: "مخصوم",
    info: "مُضاف فوق السعر (الضريبة، الخدمة، البقشيش)",
    pos: "مكسب / يُخفّض ما عليك",
    warn: "مستحق أو مصروف",
  },
  /*
   * PAPER «المبيعات والتكاليف» (2026-10-03) — see the English block.
   */
  reportSales: {
    title: "المبيعات والتكاليف",
    subtitle: "المتحصلات وتكلفة الطعام والهامش والضريبة للأيام التي تختارها.",
    compare: "مقارنة",
    compareThis: "هذه الفترة",
    comparePrevious: "مقابل الفترة السابقة",
    tileTakings: "المتحصلات",
    tileTakingsSubOne: "فاتورة واحدة · متوسط {avg}",
    tileTakingsSubTwo: "فاتورتان · متوسط {avg}",
    tileTakingsSubFew: "{count} فواتير · متوسط {avg}",
    tileTakingsSub: "{count} فاتورة · متوسط {avg}",
    tileCost: "تكلفة المبيعات",
    // {pct} carries its own "%" inside an LTR isolate — a period whose
    // returns outweigh its cooks prints "-7%", and a bare minus beside Arabic
    // paints on the wrong side (R8-1).
    tileCostSub: "{pct} من صافي المبيعات",
    // T1/B139 — نسبة تانية جنب الأولى، مش بدلها: من غير أكل الفواتير المفتوحة
    // (لسه مش مبيعات)، فتقابل تقرير ربحية الأصناف.
    tileCostSubOpen: "{pct} من صافي المبيعات · {pctOpen} دون الفواتير المفتوحة",
    tileCostSubOnlyOpen: "منها {amount} على فواتير ما زالت مفتوحة",
    // تدقيق الجولة 2 (2.3): السطر تحت مربع التكلفة إلى ربحية الأصناف — أرقامه
    // من `bridge` في تقرير ربحية الأصناف للمدة نفسها، لا تُحسب هنا.
    // إعادة العمل (R-1، Q3): كل رقم في الفرق أثرٌ عليه بإشارته كما في السلّم
    // («+255.66»، «−8.04»، والصفر بلا إشارة)، ولا «و» ملتصقة برقم أو بكلمة العملة.
    costBridgeLine:
      "التكلفة في تقرير ربحية الأصناف: {cost}. الفرق: {open} أثر طعام طُهي لفواتير ما زالت مفتوحة، {earlier} أثر مرتجع من المطبخ لأطباق بيعت قبل المدة.",
    costBridgeLink: "ربحية الأصناف للمدة نفسها",
    costBridgeTerm: "تكلفة الأصناف في ربحية الأصناف",
    // مراجعة Codex لـ PR #240: في الملف فقط — قراءتا التقريرين من لحظتين
    // مختلفتين حتى بعد إعادة القراءة، فلا يُكتب سطر بأرقام لا تبلغ تكلفة المبيعات.
    costBridgeUnreached:
      "تعذّر ربط تكلفة الأصناف بتكلفة المبيعات في هذا الملف لاختلاف لحظة قراءة التقريرين.",
    // مراجعة PR #240 (2026-10-16): قراءة تقرير ربحية الأصناف نفسها فشلت عند
    // التصدير، فلا سطر ولا صمت — الملف يقول ذلك.
    costBridgeUnread:
      "تعذّرت قراءة تقرير ربحية الأصناف، فلم تُربط تكلفة الأصناف بتكلفة المبيعات في هذا الملف.",
    tileMargin: "الهامش",
    tileMarginSubHouse:
      "صافي المبيعات والخدمة المحتفظ بها، ناقص تكلفة الطعام المباع",
    tileMarginSub: "صافي المبيعات ناقص تكلفة الطعام المباع",
    tileAvg: "متوسط الفاتورة",
    tileAvgSub: "صافٍ، لكل فاتورة · {items} صنف لكل فاتورة",
    tileAvgSubPlain: "صافٍ، لكل فاتورة",
    deltaUp: "↑ {pct}% عن الفترة السابقة",
    deltaDown: "↓ {pct}% عن الفترة السابقة",
    deltaFlat: "بلا تغيير عن الفترة السابقة",
    deltaNone: "— لا مقارنة",
    chartTitle: "المتحصلات وتكلفة الطعام يومياً",
    chartSub: "الفرق بين العمودين هو ما كسبه الطعام.",
    chartOneDay: "اختر 7 أو 30 يوماً لرؤية الشكل عبر الوقت.",
    chartTakings: "المتحصلات",
    chartCost: "تكلفة الطعام المباع",
    chartTip: "{date} · المتحصلات {revenue} · تكلفة الطعام {cost}",
    chartTipEmpty: "{date} · لا فواتير · تكلفة الطعام {cost}",
    salesTitle: "المبيعات",
    salesSub: "من صافي المبيعات إلى ما دفعه الضيوف",
    lineNet: "صافي المبيعات",
    lineService: "رسم الخدمة",
    lineVat: "ضريبة القيمة المضافة",
    lineDelivery: "رسوم التوصيل",
    lineDeliveryNote: "تُحصَّل من الضيوف على فواتير التوصيل؛ ليست طعاماً",
    // D146 — disclosed above the ladder; already out of net sales below.
    lineBillDiscount: "خصم على الفاتورة ({count})",
    lineBillDiscountNote: "مخصوم أصلًا من صافي المبيعات أدناه — لا يُخصم مرة أخرى",
    lineTakings: "المتحصلات",
    lineRefunds: "الإعادات",
    lineRefundsNoteOne: "طبق واحد · مخصومة من المتحصلات بالفعل",
    lineRefundsNoteTwo: "طبقان · مخصومة من المتحصلات بالفعل",
    lineRefundsNoteFew: "{count} أطباق · مخصومة من المتحصلات بالفعل",
    lineRefundsNote: "{count} طبق · مخصومة من المتحصلات بالفعل",
    paidTitle: "كيف دفع الضيوف",
    methodCash: "نقدي",
    methodCard: "بطاقة",
    methodInstapay: "إنستاباي",
    methodPlatforms: "منصات التوصيل",
    methodUnsettled: "لم تُسدَّد بعد",
    methodUnsettledNote: "فواتير مغلقة لم تُدفع بعد",
    commission: "عمولة المنصات",
    commissionNote:
      "على {amount} من طلبات المنصات — بالنسبة والأساس على كل فاتورة؛ كشف المنصة هو المرجع",
    lineCommissionVat: "ضريبة مدخلات تقديرية على العمولة",
    lineCommissionVatNote:
      "تقدير — ليس رقمًا للإقرار؛ راجعها مع الفاتورة الضريبية للمنصة",
    walkedOutOne: "فاتورة واحدة خرجت دون دفع · شُطب {amount}",
    walkedOutTwo: "فاتورتان خرجتا دون دفع · شُطب {amount}",
    walkedOutFew: "{count} فواتير خرجت دون دفع · شُطب {amount}",
    walkedOut: "{count} فاتورة خرجت دون دفع · شُطب {amount}",
    seeThem: "← عرضها",
    costsTitle: "التكاليف",
    costsSub: "كل ما استخدمه المطبخ، حسب سبب خروجه",
    consSold: "مباع",
    // T1/B139 — كانت «طعام على فواتير مدفوعة»، وده مش صحيح: الخانة فيها أكل
    // ترابيزات لسه مفتوحة وفواتير اتقفلت ولسه ما اتدفعتش.
    consSoldNote: "طعام طُهي لفواتير محاسَبة",
    consSoldOpenNote: "منها {amount} على فواتير ما زالت مفتوحة",
    consComped: "ضيافة",
    consStaff: "وجبات الموظفين",
    consVoided: "أُلغي بعد الطهي",
    consVoidedNote: "هالك",
    consRemake: "إعادة تحضير",
    consRemakeNote: "طُهي مرتين",
    consWalked: "خرج دون دفع",
    consWalkedNote: "فواتير مشطوبة",
    consAdjust: "تسويات المخزون",
    consAdjustNote: "الجرد الذي يجد مخزوناً زائداً يظهر بالسالب",
    totalCost: "إجمالي التكلفة",
    compReasons: "الضيافة حسب السبب:",
    purchasesSection: "المشتريات",
    linePurchases: "المشتريات",
    linePurchasesNoteOne: "توريد واحد · ضريبة مدخلات {vat}",
    linePurchasesNoteTwo: "توريدان · ضريبة مدخلات {vat}",
    linePurchasesNoteFew: "{count} توريدات · ضريبة مدخلات {vat}",
    linePurchasesNote: "{count} توريد · ضريبة مدخلات {vat}",
    lineNoVat: "بدون ضريبة مسجّلة",
    lineNoTaxInvoice: "لا توجد فاتورة ضريبية من المورد",
    marginTitle: "الهامش",
    marginSubThree: "نفس الهامش على ثلاثة أسس",
    marginSubTwo: "نفس الهامش على أساسين",
    lineMargin: "الهامش",
    lineMarginHouse: "الخدمة تحتفظ بها المنشأة",
    lineMarginNetOnly: "على صافي المبيعات فقط",
    lineMarginNetted: "التكلفة بدون ضريبة المدخلات",
    lineMarginAfterCommission: "بعد عمولة المنصات",
    marginSubFour: "نفس الهامش على أربعة أسس",
    markEstimate: "تقديري",
    vatTitle: "موقف الضريبة",
    copy: "نسخ",
    copied: "تم النسخ",
    lineOutputVat: "ضريبة المخرجات",
    lineOutputVatNote: "على المبيعات، بعد خصم {amount} مُعادة",
    lineOutputVatNotePlain: "على المبيعات",
    lineInputVat: "ضريبة المدخلات",
    lineInputVatNote: "على فواتير الموردين المسجّلة",
    // D142 — مرتجع المشتريات بجانب المشتريات، لا يُخصم منها؛ ضريبته تُخصم من المدخلات.
    lineSupplierReturns: "مرتجعات مشتريات",
    lineSupplierReturnsNoteOne: "مرتجع واحد · ضريبة مدخلات مخصومة {vat}",
    lineSupplierReturnsNoteTwo: "مرتجعان · ضريبة مدخلات مخصومة {vat}",
    lineSupplierReturnsNoteFew: "{count} مرتجعات · ضريبة مدخلات مخصومة {vat}",
    lineSupplierReturnsNote: "{count} مرتجع · ضريبة مدخلات مخصومة {vat}",
    lineInputVatNetNote: "على فواتير الموردين المسجّلة، بعد خصم {amount} على المرتجعات",
    // PA-2 (phase 12): a NEGATIVE net input says so, and why it adds.
    lineInputVatNegative: "ضريبة المدخلات (صافي سالب: {amount})",
    lineInputVatNegativeNote: "المرتجعات ({amount}) أكبر من ضريبة مشتريات الفترة ({vat})، فالصافي سالب ويُضاف إلى ما عليك بدل أن يُخصم منه.",
    colSupplierReturns: "مرتجعات",
    linePosition: "موقف الضريبة",
    linePositionCounted: "موقف الضريبة، مع الفواتير غير المدفوعة",
    linePositionNote: "ما عليك لمصلحة الضرائب عن هذه الفترة",
    lineSupported: "ضريبة مدخلات بفاتورة ضريبية كاملة",
    lineSupportedPosition: "الموقف على هذا الأساس",
    markNotFiling: "ليس رقماً للإقرار",
    vatFoot:
      "خصم ضريبة المدخلات قرار محاسبك. هذه البطاقة تعرض ما تقوله سجلاتك.",
    nonFoodTitle: "أموال ليست للطعام",
    nonFoodSub: "ما حُصّل للموظفين أو للتوصيل، وما خرج منه",
    lineSvcCollected: "رسم الخدمة المحصّل",
    lineSvcPaid: "رسم الخدمة المدفوع للموظفين",
    lineSvcKept: "هذا المطعم يحتفظ برسم الخدمة",
    lineNotPaidYet: "{amount} حُصّلت ولم تُصرف بعد",
    lineOverPaid: "صُرف {amount} أكثر مما حُصّل",
    lineTipsCollected: "البقشيش المحصّل",
    lineTipsPaid: "البقشيش المصروف",
    lineTipsNote: "البقشيش النقدي في الخزينة فقط",
    lineDeliveryCost: "تكلفة التوصيل",
    lineDeliveryCostNote: "مدفوعة للطيارين من الخزينة",
    // T12 — بطاقة التوصيل: الرسوم المحصّلة مقابل ما دُفع للسائقين.
    deliveryTitle: "التوصيل",
    deliverySub: "رسوم التوصيل المحصّلة مقابل ما دُفع للطيارين",
    lineDeliveryFees: "رسوم التوصيل المحصّلة",
    lineDeliveryFeesNote: "بعد خصم المرتجع، وبلا ضريبتها",
    lineDeliveryCouriers: "المدفوع للطيارين",
    lineDeliveryDifference: "الفرق",
    guestsTitle: "الضيوف",
    guestsSub:
      "داخل المطعم فقط. الفواتير التي لم يُسأل فيها عن العدد تُستبعد ولا تُخمَّن.",
    guestsLine: "الضيوف",
    guestsBills: "فواتير بعدد ضيوف",
    guestsBillsValueOne: "{covered} من فاتورة واحدة داخل المطعم",
    guestsBillsValueTwo: "{covered} من فاتورتين داخل المطعم",
    guestsBillsValueFew: "{covered} من {bills} فواتير داخل المطعم",
    guestsBillsValue: "{covered} من {bills} فاتورة داخل المطعم",
    guestsSpend: "الإنفاق لكل ضيف",
    guestsSpendNote: "صافي مبيعات داخل المطعم على الفواتير المحسوبة ÷ الضيوف",
    guestsNone: "لا فواتير داخل المطعم في هذه الفترة",
    tiersTitle: "فئات الأسعار",
    tiersSub: "صافي المبيعات لكل قائمة أسعار",
    tierBillsOne: "فاتورة واحدة",
    tierBillsTwo: "فاتورتان",
    tierBillsFew: "{count} فواتير",
    tierBills: "{count} فاتورة",
    colTier: "فئة السعر",
    tierTableTitle: "المتحصلات وصافي المبيعات حسب فئة السعر",
    platformsTitle: "منصات التوصيل",
    platformsSub: "ما فوترته كل منصة وما احتفظت به",
    platformSubOne: "طلب واحد · عمولة {pct}%",
    platformSubTwo: "طلبان · عمولة {pct}%",
    platformSubFew: "{count} طلبات · عمولة {pct}%",
    platformSub: "{count} طلب · عمولة {pct}%",
    leftTitle: "خارج المبيعات",
    leftSub: "فواتير أُغلقت دون أن تكسب شيئاً",
    leftWalkedOne: "فاتورة واحدة خرجت دون دفع",
    leftWalkedTwo: "فاتورتان خرجتا دون دفع",
    leftWalkedFew: "{count} فواتير خرجت دون دفع",
    leftWalked: "{count} فاتورة خرجت دون دفع",
    leftWalkedNote: "سُوّيت كشطب. تُحتسب تكلفة طعامها ولا تُحتسب مبيعاتها.",
    leftCompedOne: "فاتورة واحدة ضيافة بالكامل",
    leftCompedTwo: "فاتورتان ضيافة بالكامل",
    leftCompedFew: "{count} فواتير ضيافة بالكامل",
    leftComped: "{count} فاتورة ضيافة بالكامل",
    leftCompedNote: "أُغلقت على صفر. خارج عدد الفواتير ومتوسط الفاتورة.",
    channelTitle: "حسب القناة",
    channelSub: "متوسط الفاتورة صافٍ، لكل فاتورة",
    colChannel: "القناة",
    colBills: "الفواتير",
    colNet: "صافي المبيعات",
    colAvg: "متوسط الفاتورة",
    colItems: "أصناف / فاتورة",
    colCommission: "العمولة",
    colNetAfterCommission: "بعد العمولة",
    colGuests: "الضيوف",
    guestsCellOne: "ضيف واحد على {covered} من {bills} فاتورة",
    guestsCellTwo: "ضيفان على {covered} من {bills} فاتورة",
    guestsCellFew: "{count} ضيوف على {covered} من {bills} فاتورة",
    guestsCell: "{count} ضيف على {covered} من {bills} فاتورة",
    total: "الإجمالي",
    daysTitle: "يوماً بيوم",
    daysSub: "كل صف يُجمع: الصافي + الخدمة + الضريبة = المتحصلات.",
    daysSubDelivery:
      "كل صف يُجمع: الصافي + الخدمة + الضريبة + رسوم التوصيل = المتحصلات.",
    colDay: "اليوم",
    colTakings: "المتحصلات",
    colService: "الخدمة",
    colVat: "الضريبة",
    colNetShort: "الصافي",
    colFoodCost: "تكلفة الطعام",
    colGivenAway: "ضيافة وهالك",
    colPurchases: "المشتريات",
    colDelivery: "رسوم التوصيل",
    ingTitle: "المكوّنات المستخدمة",
    ingSub: "الأعلى تكلفة أولاً، بتكلفة يوم الاستخدام",
    colIngredient: "المكوّن",
    colUsed: "المستخدم",
    colBought: "المشترى",
    colCost: "التكلفة",
    colShare: "الحصة من تكلفة الطعام",
    moreTitle: "المزيد من هذا التقرير",
    moreSub: "المتحصلات حسب فئة السعر والمخزون المتاح وسعر شراء كل مكوّن",
    stockTitle: "المخزون وتكلفة الوحدة",
    colBoughtCost: "قيمة المشتريات",
    colOnHand: "المتاح",
    colAvgCost: "متوسط تكلفة الوحدة",
    /* D139 — حركات بتكلفة مش معروفة: برّه المجاميع، والعدد مكتوب. */
    unknownCostRowsOne:
      "حركة واحدة بتكلفة غير معروفة (مكوّن لم يُشترَ وقتها) غير محسوبة في هذه التكاليف، فهي أقل من الحقيقة.",
    unknownCostRowsTwo:
      "حركتان بتكلفة غير معروفة (مكوّن لم يُشترَ وقتها) غير محسوبتين في هذه التكاليف، فهي أقل من الحقيقة.",
    unknownCostRowsFew:
      "{count} حركات بتكلفة غير معروفة (مكوّن لم يُشترَ وقتها) غير محسوبة في هذه التكاليف، فهي أقل من الحقيقة.",
    unknownCostRows:
      "{count} حركة بتكلفة غير معروفة (مكوّن لم يُشترَ وقتها) غير محسوبة في هذه التكاليف، فهي أقل من الحقيقة.",
    /* D140/D141 — المرتجع من المطبخ بيتطرح من التكلفة في يومه، فممكن تبقى بالسالب. */
    negativeCostNote: "قد تظهر تكلفة بالسالب: ما رجع من المطبخ إلى المخزون أو عُكس تحضيره يُطرح من التكلفة في يومه، فإذا زاد في فترة على ما حُضِّر فيها صار الصافي سالبًا.",
    pagerLabel: "صفحات {name}",
    pagerRange: "{first}–{last} من {total}",
    pagerRows: "الصفوف",
    pagerPrevious: "الصفحة السابقة",
    pagerNext: "الصفحة التالية",
  },
  reportItems: {
    title: "ربحية الأصناف",
    description:
      "الكمية المباعة، الدخل الصافي، والتكلفة الفعلية من دفتر الاستهلاك — لكل صنف، على أي مدى تاريخ.",
    noAccessTitle: "لا تملك صلاحية عرض هذا التقرير",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية عرض التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    loading: "جارٍ تجهيز التقرير...",
    emptyTitle: "لا توجد بيانات في هذه المدة حتى الآن",
    emptyBody:
      "يُملأ التقرير عند وجود فواتير محصّلة في المدة المختارة. جرّب نطاقًا زمنيًا آخر.",
    // The scope/basis line every money screen states (§5.5).
    basisLine:
      "الدخل صافٍ من رسم الخدمة والضريبة، بالطريقة نفسها المستخدمة في التقرير الرئيسي. وتأتي التكلفة من سجل الاستهلاك الفعلي وقت التحضير، لا من الوصفة الحالية، وتشمل ضريبة المشتريات؛ لذلك يكون الهامش هنا أقل قليلًا من الهامش الفعلي. وفي تفاصيل الاختيارات، يتضمن كل سطر الطبق كاملًا؛ فسطر «كبير» يشمل أيضًا تكلفة الإضافات المبيعة مع هذه الأطباق، لا تكلفة الحجم وحده.",
    // B10 — الخطين اللي التصنيف مبني عليهم، وأقل عدد مبيعات يدخل التصنيف
    // أصلاً — صنف اتباع مرة أو اتنين مش عيّنة كفاية يتحكم عليه.
    thresholdsLine:
      "يعتمد التصنيف على ما يلي: مبيعات تتجاوز {units} تعني «رائج»، وهامش وحدة يتجاوز {contribution} يعني «هامش مرتفع». لا يدخل في التصنيف صنف بيع أقل من {minUnits} خلال هذه المدة.",
    quadrantBelowThreshold:
      "مبيعات أقل من {minUnits}؛ العينة صغيرة جدًا للحكم عليها",
    // R13, stated always and not only when a refund happened: it is a standing
    // rule of this report, and it is the figure most likely to be reported as
    // a bug (trap 1 — the rule lives on screen, never in a comment).
    refundNote: "إذا أُعيد مبلغ طبق وكان طعامه هالكًا، يُخصم إيراده من الصنف وتبقى تكلفته ويبقى ضمن الكمية المباعة؛ فالتكلفة هي ما استهلكه الطبق عند التحضير، ولذلك ينخفض هامش الصنف. أما إذا رجع الطبق إلى المخزون («مرتجع من المطبخ») فهو مردود مبيعات: عاد المال وعادت المكونات، فلا يُحسب بيعًا ويخرج من الكمية المباعة، ويظهر عدده بجانبها («رُدّ للمخزن»). يُحسب المرتجع في المدة التي أُعيدت فيها النقود.",
    colItem: "الصنف",
    colUnits: "الكمية المباعة",
    // PO-1 (phase 12): sales returns taken out of the units, counted beside them.
    returnedToStockLabel: "رُدّ للمخزن",
    colRevenue: "الدخل الصافي",
    colCost: "التكلفة الفعلية",
    colContributionUnit: "هامش الوحدة",
    colContributionTotal: "إجمالي الهامش",
    colMarginPct: "نسبة الهامش",
    colQuadrant: "التصنيف",
    costUnknown: "غير معروفة",
    /* D139 — أطباق اتحضّرت بمكوّن ما اتشراش لسه: تكلفتها مش في الرقم. */
    costUnknownPlatesOne: "طبق واحد بتكلفة غير معروفة",
    costUnknownPlatesTwo: "طبقان بتكلفة غير معروفة",
    costUnknownPlatesFew: "{count} أطباق بتكلفة غير معروفة",
    costUnknownPlates: "{count} طبقًا بتكلفة غير معروفة",
    costUnknownPlatesNote:
      "التكلفة المعروضة لا تشمل الأطباق ذات التكلفة غير المعروفة (مكوّن لم يُشترَ وقت تحضيرها)، فهي أقل من الحقيقة لهذه الأصناف.",
    // MINOR 12 — «نجم» فوق هامش سالب. التصنيف نسبي بطبعه (فوق وسيط الأصناف
    // التانية)، والمدة اللي فيها مرتجعات كتير بتنزّل الوسيط لتحت الصفر، فطبق
    // خسران يطلع «نجم» — رقم صح بيتقرا على إنه باج. التصنيف ما اتغيّرش؛ الجملة
    // هي اللي كانت ناقصة.
    quadrantRelativeNote:
      "يعرض التصنيف مقارنة ببقية الأصناف في هذه المدة، ولا يحكم وحده على ربح الطبق؛ فقد يتجاوز الطبق المتوسط مع بقاء هامشه سالبًا. وإذا كثرت المرتجعات في المدة، فقد يصبح المتوسط نفسه سالبًا.",
    quadrantStar: "نجم",
    quadrantPlowhorse: "رائج منخفض الهامش",
    quadrantPuzzle: "لغز",
    quadrantDog: "ضعيف",
    quadrantStarHint:
      "يُباع كثيرًا وهامشه مرتفع؛ وهو أفضل أطباقك أداءً. حافظ على جودته وسعره.",
    quadrantPlowhorseHint:
      "يُباع كثيرًا لكن هامشه منخفض. حاول رفع سعره قليلًا أو خفض تكلفته.",
    quadrantPuzzleHint:
      "هامشه مرتفع لكن الطلب عليه منخفض. روّج له من خلال ترشيح الويتر أو عرضه في موضع بارز بقائمة الطعام.",
    quadrantDogHint:
      "لا يُباع كثيرًا وهامشه منخفض. فكّر في إزالته من قائمة الطعام أو إعادة تصميمه.",
    highlighted: "الصنف المحدد",
    breakdownTitle: "تفصيل حسب الاختيار",
    // The CSV mixes dish rows and option rows in one grid. The row-type
    // column SAYS which is which; the separate option columns are what makes
    // a plain SUM down any single column come out right (§6.06).
    colLevel: "نوع السطر",
    levelItem: "صنف",
    levelOption: "اختيار",
    colOption: "الاختيار",
    colOptionGroup: "مجموعة الاختيار",
    colOptionUnits: "الكمية للاختيار",
    colOptionRevenue: "الدخل الصافي للاختيار",
    colOptionCost: "التكلفة الفعلية للاختيار",
    colOptionContributionUnit: "هامش الوحدة للاختيار",
    colOptionContributionTotal: "إجمالي الهامش للاختيار",
    colOptionMarginPct: "نسبة الهامش للاختيار",
    /* D139 (R8-3) — plates whose cost is left out of the dish's cost. */
    colCostUnknownPlates: "أطباق بتكلفة غير معروفة (خارج التكلفة)",
    variantUnattributed: "بلا اختيار",
    // What this row ACTUALLY holds. It used to say the row carried the cost of
    // plates cooked on written-off bills — a case that cannot reach it any
    // more (a bill written off inside the period leaves the report entirely,
    // D54.15), so an accountant was reading ordinary, profitable trade as
    // walk-out loss. The row is units, revenue AND cost, and the reasons a
    // plate names no option are all ordinary ones.
    breakdownNote:
      "تُجمع هذه الأرقام لتساوي سطر الصنف أعلاه. يحتوي سطر «من دون اختيار» الأطباق التي بيعت من دون اختيار من هذه المجموعة؛ ربما رُبطت المجموعة بالصنف خلال المدة، أو تغيّر نوعها، أو كان الاختيار اختياريًا للعميل. ولهذه الأطباق عدد ودخل وتكلفة مثل بقية الأطباق.",
    // ── نمط التقارير، مُطبّق (2026-09-11) ─────────────────────────────
    tableTitle: "الأصناف مرتبة حسب المساهمة",
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذا التقرير",
    statTopContributor: "أكبر مساهمة في الربح",
    statTopContributorSub: "{amount} من {units} مبيع",
    statLosing: "خاسر",
    statCostedAll: "كل الأصناف لها تكلفة وصفة",
    statNoCosted: "لا يوجد صنف له تكلفة وصفة حتى الآن",
    statNoSample: "لا يوجد صنف بمبيعات كافية حتى الآن",
    /** علامة الحد الأدنى: الصنف اللي مالوش تكلفة مش داخل في أي رقم فلوس في
     *  الصفحة دي، فكلهم حد أدنى لحد ما المنيو كله يتكلّف. */
    markFloor: "حد أدنى — {count} بلا تكلفة",
    // ── PAPER (2026-10): مربعات الإجابة، وهندسة قائمة الطعام، والجدول ──────
    paperFoodCost: "تكلفة الطعام من المبيعات",
    paperFoodCostSub: "{cost} من دخل صافٍ {revenue}",
    paperThinnest: "أقل هامش",
    paperThinnestSub: "هامش {pct} · تكلفة الطبق {cost}",
    paperUncosted: "بلا تكلفة وصفة",
    paperQuadTitle: "هندسة قائمة الطعام",
    paperQuadSub:
      "كل صنف مقارنةً ببقية الأصناف: كم يُباع، وكم يربح الطبق الواحد.",
    paperRuleStar: "رائج وهامشه مرتفع",
    paperRulePlowhorse: "رائج وهامشه منخفض",
    paperRulePuzzle: "هامشه مرتفع وطلبه قليل",
    paperRuleDog: "مبيعاته قليلة وهامشه منخفض",
    paperAdviceStar: "اجعله ظاهرًا وثابت الجودة، ولا تخفّض سعره.",
    paperAdvicePlowhorse: "خفّض تكلفة الوصفة، أو ارفع السعر قليلًا.",
    paperAdvicePuzzle:
      "ضعه في مكان أبرز بقائمة الطعام، أو اطلب من الطاقم ترشيحه.",
    paperAdviceDog: "أعد تصميمه أو تسعيره، أو ارفعه من قائمة الطعام.",
    paperQuadEmpty: "لا يوجد صنف في هذه المجموعة",
    paperMoreOne: "صنف آخر",
    paperMoreTwo: "صنفان آخران",
    paperMoreFew: "{count} أصناف أخرى",
    paperMore: "{count} صنف آخر",
    paperFilterAll: "الكل",
    paperFilterAria: "عرض الأصناف حسب المجموعة",
    paperCostPerPlate: "تكلفة الطبق {cost}",
    paperCostPlateTerm: "تكلفة الطبق",
    paperBasisFoodCost:
      "التكلفة الفعلية مقسومة على الدخل الصافي، للأصناف التي لها تكلفة فقط. الصنف الذي بلا تكلفة وصفة لا يدخل في الرقمين، حتى لا تنخفض النسبة بطعام لم تُحسب تكلفته.",
    paperBasisThinnest:
      "أقل هامش بين الأصناف التي بيعت بما يكفي لتصنيفها؛ الصنف الذي بيع مرة أو مرتين لا يُحتسب هنا.",
    paperBasisPerPlate:
      "التكلفة الفعلية مقسومة على الكمية المباعة، على الأساس نفسه الذي يُحسب به هامش الوحدة.",
    paperBasisUncosted:
      "الصنف الذي بلا تكلفة وصفة ليس له هامش، فلا يدخل في أي من المجموعات الأربع ولا في أرقام الربح.",
    // PAPER (2026-10-02) — جملة الشريط العلوي زي التصميم.
    paperSubtitle: "أي الأصناف تكسبك المال، وماذا تفعل بالباقي.",
    // الصنف الخسران، لما يكون فيه — نفس إجابة الأشكال التانية.
    paperLosingSub: "{amount} من {units} مبيع",
    // ── تدقيق الجولة 2 (2.2): التسوية مع «المبيعات والتكاليف» ──────────
    // كل درجة رقم من الخادم (`bridge`)؛ الصفحة لا تجمع شيئًا.
    bridgeTitle: "تسوية مع تقرير المبيعات والتكاليف",
    bridgeSub:
      "من أرقام هذا التقرير إلى أرقام تقرير المبيعات والتكاليف للمدة نفسها.",
    bridgeRevenueTitle: "صافي المبيعات",
    bridgeCostTitle: "تكلفة المبيعات",
    bridgeItemsRevenue: "الدخل الصافي للأصناف في هذا التقرير",
    bridgePriorRefunds: "مرتجعات أطباق بيعت قبل هذه المدة",
    bridgeFeeRefunds: "مرتجعات رسوم التوصيل",
    bridgeDisclosedMark: "إفصاح",
    bridgeFeeRefundsNoteNone: "لا مرتجعات لرسوم التوصيل في هذه المدة.",
    bridgeFeeRefundsNoteOne:
      "رُدّ {gross} في مرتجع واحد (الرسوم وضريبتها)؛ مبلغ خارج صافي المبيعات، يُذكر ولا يُطرح.",
    bridgeFeeRefundsNoteTwo:
      "رُدّ {gross} في مرتجعين (الرسوم وضريبتها)؛ مبلغ خارج صافي المبيعات، يُذكر ولا يُطرح.",
    bridgeFeeRefundsNoteFew:
      "رُدّ {gross} في {count} مرتجعات (الرسوم وضريبتها)؛ مبلغ خارج صافي المبيعات، يُذكر ولا يُطرح.",
    bridgeFeeRefundsNote:
      "رُدّ {gross} في {count} مرتجعًا (الرسوم وضريبتها)؛ مبلغ خارج صافي المبيعات، يُذكر ولا يُطرح.",
    bridgeSpendGainNet: "صافي المبيعات في تقرير المبيعات والتكاليف",
    bridgeItemsCost: "التكلفة الفعلية للأصناف في هذا التقرير",
    bridgeOpenBills: "طعام طُهي لفواتير ما زالت مفتوحة",
    bridgeGiveBacks: "مرتجع من المطبخ لأطباق بيعت قبل هذه المدة",
    bridgeSpendGainSoldCost: "تكلفة المبيعات في تقرير المبيعات والتكاليف",
    bridgeDisclosures: "إفصاحات",
    bridgeUncosted: "أصناف بلا تكلفة وصفة",
    bridgeUncostedNote: "تكلفتها غير معروفة، فهي خارج الرقمين أعلاه.",
    bridgeReturnedNoteNone: "لم يُردّ أي طبق إلى المخزون في هذه المدة.",
    bridgeReturnedNoteOne:
      "طبق واحد؛ قيمته محسوبة في الرقمين أعلاه معًا، فلا تغيّر الفرق.",
    bridgeReturnedNoteTwo:
      "طبقان؛ قيمتهما محسوبة في الرقمين أعلاه معًا، فلا تغيّر الفرق.",
    bridgeReturnedNoteFew:
      "{count} أطباق؛ قيمتها محسوبة في الرقمين أعلاه معًا، فلا تغيّر الفرق.",
    bridgeReturnedNote:
      "{count} طبقًا؛ قيمتها محسوبة في الرقمين أعلاه معًا، فلا تغيّر الفرق.",
    // خلف باب «طريقة الحساب»: تعريف كل درجة، والرقم قبله.
    bridgeDefItemsRevenue:
      "مجموع عمود «الدخل الصافي» في الجدول: ما كسبته أطباق الفواتير المحصّلة في المدة بعد الخدمة والضريبة والخصم على الفاتورة، ناقص ما رُدّ منها.",
    bridgeDefPriorRefunds:
      "مبالغ رُدّت في هذه المدة عن أطباق بيعت قبلها. يطرحها تقرير المبيعات والتكاليف في يوم ردّ النقود، ولا يحملها أي صنف هنا لأن بيعها خارج المدة.",
    bridgeDefFeeRefunds:
      "أثر مرتجعات رسوم التوصيل على صافي المبيعات. صافي المبيعات لا يشمل رسوم التوصيل ولا ضريبتها، فلا ينقص بردّها؛ والمبلغ المردود ({gross}) يُذكر ولا يُطرح.",
    bridgeDefSpendGainNet:
      "صافي المبيعات في تقرير المبيعات والتكاليف للمدة نفسها: الدخل الصافي للأصناف، ناقص مرتجعات الأطباق المبيعة قبل المدة، ناقص أثر مرتجعات رسوم التوصيل.",
    bridgeDefItemsCost:
      "مجموع عمود «التكلفة الفعلية» للأصناف التي لها تكلفة. تكلفة كل طبق هي حصته من تكلفة المبيعات نفسها، مقرّبة بالطريقة نفسها، فيلتقي الرقمان بالقرش.",
    bridgeDefOpenBills:
      "طعام طُهي لفواتير ما زالت مفتوحة: يدخل تكلفة المبيعات منذ طهيه، ولا يدخل أي صنف هنا حتى تُحاسَب فاتورته. وهو الرقم نفسه الوارد في تقرير المبيعات والتكاليف «على فواتير ما زالت مفتوحة».",
    bridgeDefGiveBacks:
      "مكوّنات عادت إلى المخزون في هذه المدة من أطباق بيعت قبلها؛ تُنقص تكلفة المبيعات في يومها، ولا تمس أي صنف هنا.",
    bridgeDefSpendGainSoldCost:
      "تكلفة المبيعات في تقرير المبيعات والتكاليف للمدة نفسها: التكلفة الفعلية للأصناف، زائد طعام الفواتير المفتوحة، زائد مرتجع المطبخ لأطباق بيعت قبل المدة.",
    bridgeDefUncosted:
      "عدد الأصناف التي بلا تكلفة وصفة. تكلفتها غير معروفة (لا وصفة لها، أو مكوّن لم يُشترَ)، فهي خارج الرقمين معًا، ولا مبلغ يمكن ذكره لها.",
    bridgeDefReturned:
      "أطباق رُدّت مبالغها ورجع طعامها إلى المخزون في المدة، وقيمة ما رجع. القيمة مخصومة من التكلفة الفعلية للأصناف ومن تكلفة المبيعات معًا، فلا تغيّر الفرق بينهما.",
  },
  /** The refunds report (founder decision 5, 2026-10-02) — `manage/reports/refunds`,
      on the operations rung (`REPORT_OPERATIONS_VIEW` or `REPORT_VIEW`). */
  reportRefunds: {
    title: "المرتجعات",
    description:
      "كل مبلغ رُدّ إلى عميل في المدة: متى، وما الذي رُدّ ولماذا، وبأي طريقة، وما يحتاج مراجعة.",
    noAccessTitle: "لا تملك صلاحية عرض هذا التقرير",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية تقارير التشغيل أو صلاحية التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    emptyTitle: "لا توجد مرتجعات في هذه المدة",
    emptyBody: "لم يُردّ أي مبلغ إلى عميل في الأيام المختارة.",
    tileRefunded: "المُرتجَع",
    tileRefundedSubOne: "مرتجع واحد",
    tileRefundedSubTwo: "مرتجعان",
    tileRefundedSubFew: "{count} مرتجعات",
    tileRefundedSub: "{count} مرتجع",
    tileShare: "النسبة من المبيعات",
    sharePct: "{pct}%",
    tileShareSub: "من {amount} صافي مبيعات",
    tileShareNone: "لا مبيعات في المدة",
    tileLate: "مرتجعات متأخرة",
    tileLateSub: "الحد الحالي: {time} بعد الدفع",
    tileOtherWay: "رُدّت بطريقة أخرى",
    tileOtherWaySub: "صُحّحت طريقة دفع الفاتورة بعد المرتجع",
    moneyTitle: "أموال المرتجعات",
    methodsTitle: "المرتجعات حسب طريقة الرد",
    methodsEmpty: "لم تُردّ أي نقود في هذه المدة.",
    insideTitle: "تفصيل المبلغ المُرتجَع",
    insideFood: "الطعام والمشروبات",
    insideVat: "ضريبة القيمة المضافة المردودة",
    insideService: "خدمة مردودة",
    insideFee: "رسوم توصيل مردودة",
    tableTitle: "قائمة المرتجعات",
    filterAria: "تصفية المرتجعات حسب التنبيه",
    filterAll: "الكل",
    flagLate: "متأخر",
    flagMethodDiverged: "طريقة مختلفة",
    flagClosedShift: "خزينة مغلقة",
    flagOtherActor: "موظف آخر",
    colInvoice: "الفاتورة",
    colAmount: "المبلغ",
    invoiceRef: "#{sequence}",
    afterPaying: "بعد الدفع بـ {time}",
    paidTimeUnknown: "وقت الدفع غير مسجّل",
    plateQuantity: "{quantity}× {name}",
    deliveryFee: "رسوم التوصيل",
    methodsLine: "{paid} ← {method}",
    refundedBy: "بواسطة {name}",
    unknownActor: "غير معروف",
    noFlags: "بلا تنبيه",
    filterEmpty: "لا توجد مرتجعات بهذا التنبيه.",
    afterPayingTerm: "بعد الدفع",
    basisRefunded:
      "المبلغ الذي رُدّ إلى العملاء شاملًا الضريبة والخدمة ورسوم التوصيل، محسوبًا في يوم العمل الذي خرجت فيه النقود، لا يوم الفاتورة. هو نفسه المبلغ الذي يطرحه تقرير المصروف والدخل.",
    basisShare:
      "الجزء الخاص بالطعام والمشروبات من المرتجعات، مقسومًا على صافي مبيعات المدة قبل المرتجعات؛ لا ضريبة ولا خدمة ولا رسوم توصيل في أي من الطرفين. مرتجع فاتورة من مدة سابقة يُحسب هنا مقابل مبيعات هذه المدة.",
    basisMethods:
      "الطريقة التي خرجت بها النقود فعلًا: النقد يخرج من الخزينة المفتوحة وقتها، والبطاقة لا تحرّك نقدًا، والتحويل يُرد من الحساب البنكي.",
    basisInside:
      "الطعام والضريبة والخدمة ورسوم التوصيل المردودة مجموعها يساوي إجمالي المُرتجَع بالضبط؛ كل مرتجع يحمل نصيبه المحسوب وقت الرد.",
    basisLate:
      "يُسجَّل وقت المرتجع نفسه مقابل الحد المضبوط وقتها (الإعدادات ← الخزينة والموافقات). تغيير الحد بعد ذلك لا يغيّر المرتجعات السابقة.",
    basisMethodDiverged:
      "صُحّحت طريقة دفع الفاتورة بعد المرتجع، فأصبح المرتجع على طريقة لم تعد الفاتورة تذكرها. يظل المرتجع على الطريقة التي خرجت بها النقود فعلًا. تنبيه وليس خطأً.",
    basisClosedShift:
      "الخزينة التي دُفعت فيها الفاتورة كانت قد أُغلقت حين رُدّت النقود. المرتجع نفسه يخرج من الخزينة المفتوحة وقتها، وعدّ الخزينة المغلقة لا يتغير.",
    basisOtherActor: "من ردّ النقود ليس من حصّلها. يُذكر اسم من ردّ فقط.",
    basisAfterPaying:
      "الدقائق من دفع الفاتورة إلى المرتجع، مقرّبة لأسفل. الشريط يمتد إلى ثلاثة أضعاف الحد الحالي، والجزء المظلّل بعد الحد.",
    csvBusinessDate: "يوم العمل",
    csvRefundedAt: "وقت المرتجع",
    csvInvoice: "رقم الفاتورة",
    csvInvoiceDate: "يوم الفاتورة",
    csvPlace: "المكان",
    csvItems: "الأصناف",
    csvFeeRefunded: "رسوم التوصيل مردودة",
    csvReason: "السبب",
    csvNote: "ملاحظة",
    csvPaidBy: "طريقة الدفع",
    csvRefundMethod: "طريقة الرد",
    csvRefundedBy: "من ردّها",
    csvMinutesAfter: "دقائق بعد الدفع",
    csvPeriodTotal: "إجمالي المدة",
    // PAPER (founder, 2026-10-02): the redesign's own words for the top bar's
    // subtitle, the two cards, the list and its columns. Only under PAPER.
    paperSubtitle: "المبالغ التي رُدّت بعد الدفع، وما يستحق منها نظرة ثانية.",
    paperMethodsTitle: "رُدّت عن طريق",
    // The figure follows in its own run, 6px after the dot.
    paperMethodLegend: "{method} ·",
    paperInsideTitle: "داخل المبلغ المُرتجَع",
    paperListTitle: "المرتجعات",
    paperColWhen: "متى",
    paperColWhat: "ماذا ولماذا",
    paperColMethods: "الدفع ← الرد",
    paperColFlags: "يحتاج مراجعة",
  },
  /** Voids & comps by staff (S3) — `manage/reports/staff`, `RESTAURANT_REPORT_VIEW`. */
  reportStaff: {
    title: "الإلغاءات والهدايا والمرتجعات حسب الموظف",
    description:
      "عدد ومبلغ كل إلغاء وهدية ومرتجع لكل موظف، مقسّم حسب السبب، وحصته من المبيعات — على أي مدى تاريخ.",
    noAccessTitle: "لا تملك صلاحية عرض هذا التقرير",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية عرض التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    loading: "جارٍ تجهيز التقرير...",
    emptyTitle: "لا توجد إلغاءات أو هدايا أو مرتجعات في هذه المدة حتى الآن",
    emptyBody:
      "يُملأ التقرير عند إلغاء صنف أو تقديمه هدية أو إعادة نقود خلال المدة المختارة.",
    // B8 — النسبة بتقارن الإلغاء والهدايا (صافية أصلاً) بـ صافي مبيعات
    // الموظف مش إجماليها — عشان النسبة متبقاش مقارنة صافي بإجمالي شامل
    // الضريبة ورسم الخدمة، اللي بتقلل النسبة عن الحقيقة بربع تقريبًا.
    basisLine:
      "المبيعات المحسوبة لكل موظف هي صافي الأصناف التي أضافها بنفسه بعد خصم رسم الخدمة والضريبة. وإذا لم يكن للصنف موظف مسجّل، لأنه أُضيف قبل بدء التسجيل أو جاء من طلب QR، فيُنسب إلى من فتح الفاتورة.",
    // كل أرقام الفلوس في الجدول ده صافية بنفس الطريقة — الإلغاء والهدية
    // والفاتورة اللي مشيت من غير دفع — عشان تتقارن ببعض وبالمقام نفسه.
    // والفواتير اللي مشيت من غير دفع مش محسوبة ضمن مبيعاته، فممكن النسبة
    // تعدّي ١٠٠٪ لو معظم اللي عمله في المدة دي كان استثناءات.
    basisLineMoney:
      "جميع المبالغ هنا صافية من رسم الخدمة والضريبة، ولا تُحتسب الفاتورة المغلقة بلا دفع ضمن مبيعات الموظف؛ لذلك قد تتجاوز النسبة 100%.",
    truncatedNote:
      "تتضمن الفترة عمليات إلغاء وهدايا أكثر مما هو معروض؛ لذلك تمثل هذه الأرقام حدًا أدنى وليست حصرًا كاملًا.",
    colActor: "الموظف",
    colVoids: "الإلغاءات",
    colComps: "الهدايا",
    // D154 — وجبات الطاقم بتتسجّل ضيافة على الكاشير اللي سجّلها؛ بنسمّيها جنبها.
    compsOfWhichStaffMeals: "منها {amount} وجبات موظفين",
    // B7 — عمودين جداد: الفواتير اللي مشيت من غير دفع، وعدد تصحيحات طريقة
    // الدفع — الاتنين بيحركوا فلوس حقيقية ومكانوش ظاهرين قبل كده.
    colWriteOffs: "خروج بدون دفع",
    // فاتورة اتكتشف إنها مشيت من غير دفع بعد يومها — بتتقفل يوم ما اتكتشفت،
    // فالخسارة بتنزل على اليوم ده. لازم نقول إنها قديمة عشان محدش يقراها
    // على إنها ترابيزة مشيت النهاردة.
    lateWriteOffs: "منها {count} من أيام سابقة",
    // بدائل المكوّنات (D64): بتتعرض جنب الإلغاء والضيافة، وعمرها ما بتتجمع
    // معاهم — البديل فلوس المحل خدها، مش فلوس ضاعت.
    colSwaps: "بدائل",
    flaggedSwapCostHeader: "تكلفة البدائل المجانية",
    // The MONEY question: food the guest did not pay for, on every swap —
    // priced or not. Not "free swaps": the charge is clamped at zero, so a
    // priced swap can charge nothing and still cost the kitchen more.
    swapGiveAwayHeader: "قيمة الطعام المجاني",
    swapGiveAwayLabel: "صُرف بلا مقابل",
    // The AUTHORING question, signed: what the unpriced swaps cost the kitchen.
    flaggedSwapCostNetHeader: "صافي تكلفة البدائل المجانية",
    swapsFreeSuffix: "{count} مجانًا",
    // D109: مصدر الأكل المجاني — الزوج نفسه، مش الشخص.
    /* «جه منين» على التبديلات بالتحديد، مش على الإلغاءات والهدايا: الكارت
       اللي فوق اسمه «اللي راح» كمان وبيعدّ حاجة تانية خالص (إلغاءات + هدايا +
       فواتير محدش دفعها). رقمين بنفس الاسم على شاشة واحدة هو بالظبط العيب
       اللي التقرير ده اتصلّح منه قبل كده. */
    giveAwaySourceTitle: "مصدر الطعام المجاني في التبديلات",
    giveAwayColPair: "التبديل",
    giveAwayColSpread: "عدد المرات",
    giveAwayColCharged: "المحصّل",
    giveAwayColGiveAway: "غير المغطى",
    /* D105's four arms. Arabic counts 1, the dual, 3–10 (plural noun) and 11+
       (singular noun), and this figure is READ as the answer to "is this the
       price list or a person" — «١ من الموظفين» would undercut the one column
       whose job is to be unambiguous. */
    giveAwayActors: "{count} موظف",
    giveAwayActorsOne: "موظف واحد",
    giveAwayActorsTwo: "موظفان",
    giveAwayActorsFew: "{count} موظفين",
    giveAwayOfCost: "من تكلفة {cost}",
    /* B23: اترجّع للعميل. جنب «اتحصّل» مش مطروح منه. الأربع حالات (D105):
       الواحد، المثنى، من ٣ لـ ١٠ بالجمع، ومن ١١ وطالع بالمفرد. */
    giveAwayRefunded: "أُعيد منها {amount} في {count} طبق",
    giveAwayRefundedOne: "أُعيد منها {amount} في طبق واحد",
    giveAwayRefundedTwo: "أُعيد منها {amount} في طبقين",
    giveAwayRefundedFew: "أُعيد منها {amount} في {count} أطباق",
    giveAwaySourceBasis:
      "هذه هي قيمة «ما صُرف» نفسها أعلاه، لكنها مجمّعة حسب التبديل بدلًا من الموظف. ويفصل ذلك بين حالتين قد تبدوان متشابهتين: أن يكون سعر التبديل أقل من تكلفته، فيظهر مع موظفين مختلفين باستمرار، أو أن يستغل موظف بعينه هذا الفرق. يوضّح عمود «عدد الموظفين» أي الحالتين تنطبق.",
    /* نفس البلوك في الملف المصدَّر (D109). العناوين الأربعة اللي فوق
       بتتعاد هنا عشان الملف والشاشة يقولوا نفس الكلام؛ والخمسة دول هم
       الأرقام اللي الشاشة بتكتبها كسطر صغير تحت الرقم، وكل واحد فيهم
       لازم يبقى عمود لوحده: الخانة اللي بتعرض رقمين ما ينفعش تتصدّر رقم واحد. */
    csvGiveAwayStaff: "عدد الموظفين",
    csvGiveAwayCost: "تكلفة هذه التبديلات",
    csvGiveAwayUnpriced: "تبديلات بلا سعر",
    csvGiveAwayRefundedCount: "أطباق أُعيدت",
    csvGiveAwayRefundedValue: "الفرق المعاد",

    // A HEADER, so it carries no count — the cell's own suffix does.
    colSwapsFree: "بدائل مجانًا",
    // The attribution caveat for the swaps column specifically: since B17 the
    // swap follows the plate's own author, with the opener as the fallback for
    // a plate that carries none.
    swapsBasisLine:
      "يُنسب عمود البدائل إلى الموظف الذي أضاف الصنف. وإذا لم يكن للصنف موظف مسجّل، لأنه أُضيف قبل بدء التسجيل أو جاء من طلب QR، فيُنسب إلى من فتح الفاتورة.",
    // B23 — الصنف اللي اترجع للزبون بيفضل «متحاسب عليه» في النظام عن قصد
    // (عشان مجموع الأصناف يفضل مساوي للفاتورة)، فالبديل اللي عليه فرق سعر
    // كان بيتقرا على إنه فلوس المحل خدها حتى لو رجعت. الرقم ده جنبه دلوقتي.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    swapRefundedLabelOne: "أُعيد واحد منها:",
    swapRefundedLabelTwo: "أُعيد اثنان منها:",
    swapRefundedLabelFew: "أُعيد {count} منها:",
    swapRefundedLabel: "أُعيد {count} منها:",
    swapsRefundBasisLine:
      "تعني «أُعيدت» في عمود البدائل أن الصنف أُعيد إلى العميل حتى الآن، ولو أُعيدت النقود في شهر آخر لا في المدة المعروضة فقط.",
    csvRatioWithRefunds: "النسبة + المرتجع",
    csvSwapsRefundedCount: "بدائل على أصناف أُعيدت",
    csvSwapsRefundedValue: "قيمة البدائل المعادة",
    colCorrections: "تصحيحات الدفع",
    // F19 (قرار المالك 2026-09-13) — مين اللي طبّق سعر خاص، وكام سِعّب.
    //
    // آخر تخفيض في المنتج ما كانش عليه اسم. تطبيق الفئة بيحصل في `openInvoice`
    // وبتصريح `RESTAURANT_INVOICE_OPEN` — أقل درجة موجودة — فالويتر يقدر ياخد
    // من العميل السعر الكامل كاش، ويعمل الفاتورة على خصم المول، ويحتفظ بالفرق.
    // كل تخفيض تاني في التقرير بيقول مين عمله؛ دا كان مش بيقول.
    //
    // منسوب للي فتح الفاتورة، وده دقيق مش تقريبي: الفئة بتتحدد عند الفتح وما
    // بتتغيرش بعد كده.
    colDiscounts: "خصومات الفئات",
    // D146 — per the person who APPLIED it, beside the comps.
    colBillDiscounts: "خصم على الفاتورة",
    billDiscountAfterCheck: "بعد طباعة الحساب المبدئي ({count})",
    // بدل UUID لما الفئة تكون اتمسحت ومعاها اسمها.
    unnamedTier: "فئة محذوفة",
    csvDiscountValue: "قيمة خصم الفئة",
    csvDiscountCount: "فواتير على فئة",
    csvDiscountTiers: "الفئات",
    // R12 — المرتجعات نوع لوحده جنب الإلغاء والهدية، مش جواهم ومش جوه
    // النسبة: النسبة بتقيس اللي الموظف بيوزّعه من غير مقابل، والمرتجع
    // بيرجّع بيعة العميل دفعها فعلاً — سؤال مفتوح لصاحب المطعم (B22).
    colRefunds: "المرتجعات",
    // فلوس المرتجع بتتقرا من دفتر الفلوس زي ما خرجت بالظبط: بالخدمة
    // والضريبة. باقي أرقام الجدول صافية، فالفرق ده لازم يتقال مش يتخبى.
    refundsBasis:
      "تشمل أرقام المرتجعات رسم الخدمة والضريبة كما أُعيدت إلى العميل، وليست صافية كبقية الأعمدة، ولا تدخل في النسبة. وتُحتسب كاملة حتى إذا كانت بقية قائمة التقرير مقتطعة.",
    // «مرة» = عدد المرات اللي ضغط فيها مرتجع، و«صنف» = عدد الأصناف اللي
    // رجعت — الرقمين مختلفين ومحدش منهم يغني عن التاني.
    /* B118/QA3 — كان سطر واحد فيه عدّادين مستقلين («1 مرة · 1 صنف»), و
       `countedKey` بيختار مفتاح واحد. اتقسم لجملتين، كل واحدة بتعدّ اسمها. */
    refundTimesNone: "لا توجد مرتجعات",
    refundTimesOne: "مرة واحدة",
    refundTimesTwo: "مرتين",
    refundTimesFew: "{count} مرات",
    refundTimes: "{count} مرة",
    refundPlatesOne: "صنف واحد",
    refundPlatesTwo: "صنفين",
    refundPlatesFew: "{plates} أصناف",
    refundPlates: "{plates} صنف",
    lateRefunds: "منها {count} بعد الوقت المسموح",
    // D75 — علامتين مشتقّتين جنب الحد اللي في الإعدادات، كل واحدة بتجاوب على
    // سؤال لوحدها: مين بيرجّع فلوس من ورديات اتقفلت واتوقّعت، ومين بيرجّع فلوس
    // حصّلها حد تاني. مش رفض ومش نسبة — عدد مرات، بيتقال جنب الفلوس.
    closedShiftRefunds: "منها {count} على خزينة مغلقة",
    otherActorRefunds: "منها {count} على تحصيل موظف آخر",
    csvRefundClosedShift: "مرتجعات على خزينة مغلقة",
    csvRefundOtherActor: "مرتجعات لتحصيل موظف آخر",
    // أعمدة الملف: مفيش سطر أساس في ملف CSV، فاسم العمود نفسه بيقول إن
    // الفلوس دي بالخدمة والضريبة.
    csvRefundValueGross: "المرتجعات (بالخدمة والضريبة)",
    csvRefundCount: "عدد مرات المرتجع",
    csvRefundPlates: "أصناف أُعيدت",
    csvRefundLate: "مرتجعات بعد الوقت المسموح",
    csvRefundReasons: "أسباب المرتجع",
    countSuffix: "{count} فاتورة",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    countSuffixNone: "لا توجد فواتير",
    countSuffixOne: "فاتورة واحدة",
    countSuffixTwo: "فاتورتين",
    countSuffixFew: "{count} فواتير",
    colOwnSales: "صافي مبيعاته",
    colRatio: "النسبة",
    // اللي بيتكتب تحت النسبة في نفس الخانة، ومابيظهرش غير لو الرقمين مختلفين.
    ratioWithRefundsLabel: "+ المرتجع {pct}%",
    // البسط في العمود التاني بيخلط أساسين: فلوس الإلغاء والهدية صافية،
    // وفلوس المرتجع بالخدمة والضريبة زي ما رجعت. فالنسبة دي بتطلع أعلى
    // شوية في محل بياخد خدمة وضريبة — بنقولها بدل ما نخبّيها.
    ratioBasisLine:
      "«النسبة» = الإلغاء والهدية والفواتير التي أُغلقت بلا دفع ÷ صافي المبيعات. و«النسبة + المرتجع» تضيف مبلغ المرتجع إلى البسط. يشمل مبلغ المرتجع الخدمة والضريبة، بينما تبقى القيم الأخرى صافية؛ لذلك قد تكون هذه النسبة أعلى قليلًا في مطعم يضيف خدمة وضريبة.",
    ratioUnknown: "—",
    unknownActor: "موظف غير معروف",
    drillLink: "عرض الفواتير",
    // ── نمط التقارير، مُطبّق (2026-09-11) ─────────────────────────────
    tableTitle: "الاستثناءات حسب الموظف",
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذا التقرير",
    basisTermAttribution: "على من يُحسب الصنف",
    basisTermMoney: "أرقام النقود",
    basisTermSwapRefunds: "الاستبدالات المرتجعة",
    statGivenAway: "ما صُرف بلا مقابل",
    statGivenAwaySub: "على {count} موظف",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    statGivenAwaySubOne: "على موظف واحد",
    statGivenAwaySubTwo: "على موظفين",
    statGivenAwaySubFew: "على {count} موظفين",
    statPlatesSub: "{plates} صنف",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    statPlatesSubOne: "صنف واحد",
    statPlatesSubTwo: "صنفين",
    statPlatesSubFew: "{plates} أصناف",
    /** مفيش اسم في المربع ده بقصد — الشرح عند مكان الاستخدام. */
    statHighestRatio: "أعلى نسبة توزيع",
    statHighestRatioValue: "{pct}% من مبيعاته",
    statHighestRatioSub: "التفاصيل في الجدول",
    markFloor: "حد أدنى — القائمة مقطوعة",
    // ── PAPER (2026-10): المربعات، وجدول الموظفين، والأسباب ─────────────────
    paperTopReason: "السبب الأكثر تكرارًا",
    paperTopReasonNone: "لا يوجد سبب مسجّل",
    paperRemakesSub: "بتكلفة {amount}",
    paperShareNote:
      "تُحسب النسبة من صافي مبيعات كل موظف نفسه، وما يتجاوز {pct}% يُميَّز.",
    paperShareHigh: "مرتفعة",
    paperReasonsTitle: "الإلغاءات والهدايا حسب السبب",
    paperReasonsAll: "الكل",
    paperReasonsAria: "عرض الأسباب حسب النوع",
    paperReasonsEmpty: "لا يوجد إلغاء أو هدية في هذا الاختيار.",
    paperReasonsTotal: "الإجمالي",
    paperClearStaff: "عرض كل الموظفين",
    paperBasisShareFlag:
      "تُميَّز نسبة الموظف عندما تتجاوز {pct}% من مبيعاته. هذا خط للقراءة فقط، لا قاعدة يطبقها النظام.",
    paperBasisReasons:
      "أسباب الإلغاءات والهدايا مجمّعة من الجدول نفسه: عدد الأطباق وقيمتها الصافية لكل سبب، مرتبة حسب القيمة. «السبب الأكثر تكرارًا» هو السبب الذي يغطي أكبر عدد من الأطباق، لذلك قد يختلف عن أول سطر في القائمة. اختيار موظف من الجدول يعرض أسبابه وحده.",
    // PAPER (2026-10-02) — جملة الشريط العلوي زي التصميم.
    paperSubtitle:
      "الأموال التي تُعطى بلا مقابل على شاشة البيع، حسب الموظف وحسب السبب.",
    paperReasonsSub: "عدد الأطباق وقيمتها لكل سبب في هذه الفترة.",
    // جولة الإصلاح (2026-10-03) — عدد الإلغاءات والهدايا على مربع «ما صُرف».
    paperVoidCount: "{count} إلغاء",
    paperVoidCountOne: "إلغاء واحد",
    paperVoidCountTwo: "إلغاءان",
    paperVoidCountFew: "{count} إلغاءات",
    paperCompCount: "{count} هدية",
    paperCompCountOne: "هدية واحدة",
    paperCompCountTwo: "هديتان",
    paperCompCountFew: "{count} هدايا",
    csvVoidCount: "عدد مرات الإلغاء",
    csvVoidPlates: "أصناف أُلغيت",
    csvVoidReasons: "أسباب الإلغاء",
    csvCompCount: "عدد مرات الهدية",
    csvCompPlates: "أصناف أُهديت",
    csvCompReasons: "أسباب الهدية",
    csvBillDiscountValue: "خصم على الفاتورة (صافي)",
    csvBillDiscountCount: "عدد الخصومات",
    csvBillDiscountAfterCheck: "خصومات بعد الحساب المبدئي",
    csvBillDiscountReasons: "أسباب الخصم",
  },
  // B124/D114 — سرعة الخدمة: الوقت بين كل مرحلة والتي تليها. لا أموال ولا أسماء.
  reportSpeed: {
    title: "سرعة الخدمة",
    description: "أين يذهب الوقت من طلب الطبق حتى وصوله إلى الطاولة.",
    noAccessTitle: "لا تملك صلاحية عرض هذا التقرير",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية تقارير التشغيل. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    emptyTitle: "لا توجد أطباق في هذه الفترة",
    emptyBody: "يظهر التقرير بعد إغلاق أول فاتورة وصلت أطباقها إلى المطبخ.",
    tileWait: "انتظار الضيف للطبق",
    tileWaitSub: "واحد من كل عشرة انتظر أكثر من {duration}",
    tileLate: "أطباق تجاوزت الهدف",
    tileLateSub: "{late} من {judged}",
    tileLateSubStation: "{late} من {judged} · أكثرها في {station}",
    tileHour: "أبطأ ساعة",
    tileMedianSub: "الوسيط {duration}",
    tileTimed: "الأطباق المحسوب وقتها",
    tileTimedSub: "{measured} من {eligible}",
    notEnough: "عدد الأطباق المحسوبة غير كافٍ",
    notTimed: "غير محسوب",
    // خلية صنف تحت الحد الأدنى من الأطباق المحسوبة: لا رقم يُبنى عليه حكم.
    belowMinimum: "أقل من الحد الأدنى",
    // مرحلة لم تحدث في الفترة أصلًا (لا طلبات ضيوف مثلًا): لا شيء يُحسب، وليست «٠ من ٠».
    noneInPeriod: "لم يحدث في هذه الفترة",
    noStation: "بدون محطة",
    stagesTitle: "مراحل الخدمة",
    plateStagesBlock: "من المطبخ إلى الطاولة",
    floorStagesBlock: "الصالة والدفع",
    stageCol: "المرحلة",
    medianCol: "الوسيط",
    p90Col: "المئين التسعون",
    timedCol: "المحسوب",
    timedOf: "{measured} من {eligible}",
    lateCol: "تجاوز الهدف",
    lateOf: "{late} من {judged}",
    // مرحلة حُسب وقتها لكن كل فواتيرها أقدم من الأهداف: لا هدف يُحكم به.
    notJudged: "بلا هدف",
    stationLateCol: "تجاوز الهدف حتى الطاولة",
    dishCookLateCol: "تجاوز وقت التحضير",
    stageToTable: "من الطلب إلى الطاولة",
    stageKitchen: "من الطلب إلى الجاهزية",
    stageQueue: "انتظار بدء التحضير",
    stageCook: "التحضير",
    stagePass: "من الجاهزية إلى الطاولة",
    stageFirstOrder: "من الجلوس إلى أول طلب",
    stageQrAccept: "قبول طلبات الضيوف",
    stagePayment: "من الحساب إلى الدفع",
    stageProofReview: "مراجعة إثبات الدفع",
    excludedRemade: "أطباق أُعيدت، خارج الحساب",
    excludedClosed: "أطباق أُنهيت بإغلاق الفاتورة",
    hoursTitle: "حسب الساعة",
    hourCol: "الساعة",
    platesCol: "الأطباق",
    hourMedianCol: "الوسيط حتى الطاولة",
    hoursEmpty: "لم يُحسب وصول أي طبق إلى الطاولة في هذه الفترة.",
    stationsDishesTitle: "المحطات والأصناف",
    stationsBlock: "المحطات",
    dishesBlock: "الأصناف",
    stationCol: "المحطة",
    dishCol: "الصنف",
    toTableCol: "حتى الطاولة",
    kitchenCol: "حتى الجاهزية",
    dishesBelow: "أصناف تحت الحد الأدنى للأطباق المحسوبة",
    dishesEmpty: "لا يوجد صنف بلغ الحد الأدنى للأطباق المحسوبة في هذه الفترة.",
    durSeconds: "{seconds} ث",
    durMinutesSeconds: "{minutes} د {seconds} ث",
    durMinutes: "{minutes} د",
    durHoursMinutes: "{hours} س {minutes} د",
    csvMedianSeconds: "الوسيط (ثانية)",
    csvP90Seconds: "المئين التسعون (ثانية)",
    csvTimed: "المحسوب",
    csvEligible: "المؤهَّل",
    csvKitchenSeconds: "حتى الجاهزية، الوسيط (ثانية)",
    csvToTableSeconds: "حتى الطاولة، الوسيط (ثانية)",
    csvQueueSeconds: "انتظار بدء التحضير، الوسيط (ثانية)",
    csvCookSeconds: "التحضير، الوسيط (ثانية)",
    csvJudged: "المقارَن بهدف",
    csvLate: "تجاوز الهدف",
    csvToTableJudged: "حتى الطاولة، المقارَن بهدف",
    csvToTableLate: "حتى الطاولة، تجاوز الهدف",
    csvCookJudged: "التحضير، المقارَن بهدف",
    csvCookLate: "التحضير، تجاوز الهدف",
    basisMedianTerm: "الوسيط",
    basisMedian:
      "نصف الأطباق أخذت هذا الوقت أو أقل. لا يُستعمل المتوسط لأن طبقًا واحدًا نُسي لساعات يرفع المتوسط ولا يغيّر الوسيط.",
    basisP90Term: "المئين التسعون",
    basisP90:
      "تسعة أطباق من كل عشرة أخذت هذا الوقت أو أقل، والطبق العاشر انتظر أكثر. هذه هي الانتظارات الطويلة التي يتذكرها الضيف.",
    basisTimedTerm: "المحسوب",
    basisTimed:
      "يُحسب الوقت فقط حين تُسجَّل اللحظتان، وبفارق موجب. لا يُفترض أي وقت ناقص، ولذلك يظهر عدد المحسوب من المؤهَّل بجانب كل مرحلة.",
    basisStartTerm: "بدء التحضير",
    basisStart:
      "يُحسب انتظار بدء التحضير والتحضير فقط حين يضغط أحد العاملين «بدء التحضير» على شاشة المطبخ أو «بدء» على شاشة البيع. في المطبخ الذي يطبع التذكرة، أو بدون مطبخ مُعدّ، يبدأ النظام الطبق بنفسه فلا يكون ذلك انتظارًا.",
    basisCloseTerm: "إغلاق الفاتورة",
    basisClose:
      "الطبق الذي أنهاه إغلاق الفاتورة دون أن يُسلَّم لا يُحسب له وقت الوصول إلى الطاولة، لأن لحظة الإغلاق ليست لحظة التسليم.",
    basisRemakeTerm: "الإعادات",
    basisRemake:
      "الطبق المُعاد خارج كل المراحل: الإعادة تُرجع الطبق إلى المطبخ وتمسح أوقاته، فلا يبقى إلا وقت الطهي الثاني. العدد هنا عدد الأطباق لا عدد الإعادات، فالطبق المُعاد مرتين يُعدّ مرة واحدة؛ تقرير الإعادات يعدّ كل إعادة.",
    basisTargetTerm: "الأهداف",
    basisTarget:
      "يتجاوز الطبق الهدف حين تأخذ مرحلته وقتًا أطول من الوقت المحدد في إعدادات أوقات الخدمة، أو من وقت تحضير الصنف نفسه إن كان له وقت خاص. تحتفظ كل فاتورة بالأهداف التي فُتحت بها، وكل طبق بوقت تحضيره منذ إرساله إلى المطبخ، فتغيير الهدف لا يعيد الحكم على الفواتير السابقة، والفواتير الأقدم من الأهداف يُحسب وقتها ولا يُحكم عليها. تُقارن مرحلة «من الطلب إلى الجاهزية» بمجموع الانتظار والتحضير، ومرحلة «من الطلب إلى الطاولة» بمجموع المراحل الثلاث. وتلوّن شاشة المطبخ الطبق المتأخر بالقاعدة نفسها.",
    basisWindowTerm: "المدة",
    basisWindow:
      "فواتير مغلقة أو محصّلة في أيام العمل المختارة، ومنها المشطوبة، لأن التقرير يصف التشغيل لا الإيراد.",
    basisFloorTerm: "مراحل الصالة",
    basisFloor:
      "من الجلوس إلى أول طلب لفواتير الصالة فقط، من فتح الفاتورة إلى أول إرسال للمطبخ. من الحساب إلى الدفع من آخر طباعة للحساب إلى التحصيل.",
    basisHourTerm: "الساعة",
    basisHour:
      "ساعة إرسال الطبق إلى المطبخ، على ساعة المطعم لا على ساعة الجهاز.",
    basisMinimumTerm: "الحد الأدنى",
    basisMinimum:
      "لا يُدرج صنف، ولا تُسمّى محطة أو ساعة بأنها الأبطأ، قبل أن تبلغ أطباقه المحسوبة الحد الأدنى، وهو {min}، لأن طبقين بطيئين لا يكفيان للحكم.",
  },
  /** Hour × weekday revenue and table turn time (S4) — `manage/reports/dayparts`. */
  // D134 — آراء الضيوف: ما قالوه بعد الدفع من تطبيق الطاولة.
  feedback: {
    title: "آراء الضيوف",
    description: "التقييم والملاحظات التي تركها الضيوف بعد الدفع من تطبيق الطاولة.",
    noAccessTitle: "لا تملك صلاحية عرض هذه الصفحة",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية تقارير التشغيل. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    emptyTitle: "لا توجد آراء في هذه الفترة",
    emptyBody:
      "تظهر هنا آراء الضيوف بعد أن يقيّموا زيارتهم من تطبيق الطاولة عند الدفع.",
    tileAverage: "متوسط التقييم",
    tileAverageValue: "{value} من 5",
    tileCount: "عدد التقييمات",
    tileFromOne: "من تقييم واحد",
    tileFromTwo: "من تقييمين",
    tileFromFew: "من {count} تقييمات",
    tileFrom: "من {count} تقييم",
    tileLow: "تقييمات منخفضة",
    tileLowSub: "تقييم 1 أو 2 من 5",
    tileTopChip: "أكثر ما أعجب الضيوف",
    tileNone: "لا شيء بعد",
    tileChipTimesOne: "مرة واحدة",
    tileChipTimesTwo: "مرتان",
    tileChipTimesFew: "{count} مرات",
    tileChipTimes: "{count} مرة",
    chipsTitle: "ما أعجب الضيوف",
    ratingsTitle: "توزيع التقييمات",
    ratingStars: "{rating} من 5",
    listTitle: "كل الآراء",
    listSub: "الأحدث أولًا",
    colDate: "التاريخ",
    colTable: "الطاولة",
    colBill: "الفاتورة",
    colRating: "التقييم",
    colChips: "ما أعجبهم",
    colNote: "الملاحظة",
    noTable: "بدون طاولة",
    noNote: "بدون ملاحظة",
    billNumber: "#{sequence}",
    chipTastyFood: "طعام لذيذ",
    chipFastService: "خدمة سريعة",
    chipFriendlyStaff: "طاقم ودود",
    chipGreatValue: "قيمة ممتازة",
    chipCleanTable: "طاولة نظيفة",
  },
  reportDayparts: {
    title: "أوقات الذروة",
    // خمس كلمات. الجملة القديمة كانت بتسمّي أول قسم بالحرف، وبتوصف مُنتقي
    // التاريخ اللي واقف جمبها.
    description: "ساعات الذروة وزمن دوران الطاولات.",
    // PAPER (2026-10-02) — جملة الشريط العلوي زي التصميم.
    paperSubtitle: "متى يكون المطعم مزدحمًا، لتجهّز الفريق والتحضير له.",
    noAccessTitle: "لا تملك صلاحية عرض هذا التقرير",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية عرض التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    loading: "جارٍ تجهيز التقرير...",
    emptyTitle: "لا توجد فواتير محصّلة في هذه المدة حتى الآن",
    emptyBody: "جرّب نطاقًا زمنيًا آخر، أو راجع التقرير بعد انتهاء الخدمة.",
    /*
     * الأساس، ورا باب واحد.
     *
     * ده كان `basisLine` — أربع جمل في فقرة بخط 10.5px تحت اسم الشاشة
     * على طول، وهو أغلى شريط في الصفحة وأقل حاجة بتتقرا فيها. الحقايق نفسها
     * مش اختيارية (الدخل هنا إجمالي، وباقي التقارير بتوري الصافي؛ واليوم هو
     * يوم العمل) — فهي ماتشالتش، بقت ضغطة واحدة، ونفس الكلام بيتكتب في ملف
     * الـCSV عشان الملف يقول أساسه بنفسه. باب واحد لكل تقرير، بنفس الاسم في
     * التقارير الأربعة، عشان المستخدم يتعلم حاجة واحدة مش أربعة.
     */
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذا التقرير",
    basisTermRevenue: "الدخل",
    basisDefRevenue:
      "إجمالي يشمل رسم الخدمة والضريبة. تعرض بقية التقارير الصافي، لذلك قد يظهر الأسبوع نفسه بقيمة أعلى هنا.",
    basisTermHour: "الساعة واليوم",
    basisDefHour:
      "وقت فتح الفاتورة بتوقيت المطعم. تُحسب الفاتورة التي تتجاوز منتصف الليل على يوم العمل الذي فُتحت فيه.",
    basisTermTurn: "زمن الدوران",
    basisDefTurn:
      "زمن الدوران هو وقت الإغلاق ناقص وقت الفتح. تُحسب الفواتير التي لها الوقتان فقط، ويظهر عددها بجانب كل متوسط.",
    // الكلمة الوحيدة اللي بتفضل ظاهرة. دي الحقيقة اللي بتخلي الشاشة دي
    // تختلف عن تقرير المصروف والمكسوب، ورقم بيختلف عن شاشة تانية من غير
    // تفسير أسوأ من أي زحمة كلام.
    basisGross: "إجمالي",
    statPeakHour: "أعلى ساعة",
    statTotalSub: "{count} فاتورة",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    statTotalSubOne: "فاتورة واحدة",
    statTotalSubTwo: "فاتورتين",
    statTotalSubFew: "{count} فواتير",
    // مش «والساعة واليوم»: السبع صفوف بأسمائها بتقول اليوم أصلاً، والعنوان
    // الأطول كان بيتقطع بالنقط على شاشة الموبايل.
    gridTitle: "الدخل حسب الساعة",
    // إزاي الرسم بيعرّف نفسه — جملة واحدة قبل السبع صفوف، عشان قارئ الشاشة
    // ماينفعش يستنتج الشكل من ١٦٨ رقم.
    chartSummary:
      "صف لكل يوم في الأسبوع، والساعات من {from} إلى {to} بتوقيت المطعم. يمثل كل عمود إجمالي دخل الساعة. أعلى وقت: {peak}.",
    // لكل صف، لأن اسم واحد لرسم فيه سبع صفوف مايسيبش للقارئ طريقة يوصل
    // ليوم واحد. `{peak}` أصلاً جملة فيها اليوم والساعة.
    chartRowLabel:
      "{weekday}: أعلى ساعة {hour}، {revenue}. إجمالي اليوم {total}.",
    chartRowEmpty: "{weekday}: لا توجد فواتير.",
    peaksTitle: "أكثر الساعات ازدحامًا",
    peakRow: "{revenue} · {covers}",
    dayTotal: "إجمالي اليوم",
    tableCaption:
      "الدخل الإجمالي وعدد الفواتير لكل ساعة في كل يوم عمل، بتوقيت المطعم.",
    colHour: "الساعة",
    gridHourLabel: "الساعة {hour}",
    // عدد الفواتير اتشال من هنا لأن جملة الكوفرات بقت بتقوله — «8 كوفرات على 2
    // فاتورة من 9» — فمافيش رقم اتضاع، بس بقى له مقامه جمبه.
    gridCellLabel: "{revenue}، {covers}",
    gridCellEmpty: "لا توجد فواتير",
    /*
     * B117/Q6 — الكوفرات وعدد الفواتير اللي هي محسوبة عليها.
     *
     * السيرفر بيستبعد الفاتورة اللي محدش عدّ ناسها بدل ما يحطها صفر، يعني
     * الرقم على جزء من الفواتير. من غير الجملة دي «١٬٩١٧٫١٦ · ٨ كوفرات» فوق ٩
     * فواتير بتتقسم لـ٢٤٠ للفرد، والحقيقة ٧٨٫٣٦.
     */
    /* B118/QA3 — تلات أرقام في جملة واحدة، و`countedKey` بيختار مفتاح واحد.
       الاسم اتنقل لآخر رقم («على ٢ من ٩ فواتير») عشان الاتفاق يبقى على رقم
       واحد بس، وده اللي بيتصرّف هنا. النسخة الإنجليزية عاملة كده أصلًا. */
    /* B118/QA4 — و«كوفر» كمان اسم معدود، وكان سايب في الأربع صيغ. القايمة نفسها
       بتثبت إنه بيتحرك: `coversNone` مكتوبة «مفيش كوفرات». الجملة دي فيها تلات
       أرقام، و`countedKey` بيختار مفتاح واحد — فالعدد اللي بيتصرّف هو عدد
       الفواتير (آخر اسم)، و«كوفر» بقى جملة مبنية جنبه زي ما `{covers}` عاملة في
       خلية الجدول. */
    coversOverOne: "{covers} على {covered} من فاتورة صالة واحدة",
    coversOverTwo: "{covers} على {covered} من فاتورتي صالة",
    coversOverFew: "{covers} على {covered} من {bills} فواتير صالة",
    coversOver: "{covers} على {covered} من {bills} فاتورة صالة",
    coversWordOne: "ضيف واحد",
    coversWordTwo: "ضيفان",
    coversWordFew: "{covers} ضيوف",
    coversWord: "{covers} ضيفًا",
    // «٠ كوفرات» معناها «محدش جه»؛ دي معناها «محدش عدّ».
    coversNoneOne: "لم يُسجّل عدد الضيوف في فاتورة صالة واحدة",
    coversNoneTwo: "لم يُسجّل عدد الضيوف في فاتورتي صالة",
    coversNoneFew: "لم يُسجّل عدد الضيوف في {bills} فواتير صالة",
    coversNone: "لم يُسجّل عدد الضيوف في {bills} فاتورة صالة",
    daypartsTitle: "حسب الفترة",
    daypartBreakfast: "الإفطار",
    daypartLunch: "الغداء",
    daypartDinner: "العشاء",
    daypartLate: "بعد منتصف الليل",
    colShare: "النسبة",
    // ليه ليها عمود دخل خاص بيها مش `reportItems.colRevenue`: دي مكتوب فيها
    // «الدخل الصافي»، وكل رقم في الصفحة دي إجمالي. استعارتها كانت بتحط عكس
    // الحقيقة في عنوان العمود وفي ملف الـCSV.
    colRevenue: "الدخل",
    turnTitle: "زمن دوران الطاولة",
    colTable: "الطاولة",
    colAvgTurn: "متوسط الدوران",
    // كانت «عدد الفواتير المحسوبة» — التفسير بقى في الباب، حيث ينفع يبقى
    // جملة كاملة.
    colTurnCount: "فواتير مقيسة",
    minutesShort: "{minutes} د",
    noTurnData: "—",
    // «—» مابتتقرأش بأي صوت، فالشرطة بقى جمبها كلمة مايسمعهاش غير قارئ
    // الشاشة.
    noTurnDataLabel: "لا توجد فواتير مغلقة",
    colHours: "الساعات",
    // PAPER (2026-10) — شكل التقرير الجديد. مفيش «إنفاق الضيف»: الدخل على كل
    // الفواتير والضيوف على اللي اتعدّ منها بس، والقسمة بينهم غلط.
    paperPeriodNote: "يبدأ يوم العمل الساعة {time}",
    paperPeakHourSub: "{revenue} إجمالي · الأهدأ {time}",
    paperGrossAmount: "{revenue} إجمالي",
    paperStatBusiestDay: "أكثر الأيام ازدحامًا",
    paperTurnLongest: "الأطول في {name}: {minutes}",
    paperStatCovers: "الضيوف",
    paperCoverageSub: "مسجّل على {covered} من {bills} فاتورة صالة ({pct}%)",
    paperCoverageSubOne: "مسجّل على {covered} من فاتورة صالة واحدة ({pct}%)",
    paperCoverageSubTwo: "مسجّل على {covered} من فاتورتي صالة ({pct}%)",
    paperCoverageSubFew: "مسجّل على {covered} من {bills} فواتير صالة ({pct}%)",
    paperNoDineIn: "لا توجد فواتير صالة في هذه المدة",
    paperShareGross: "{pct}% من المدة، إجمالي",
    paperViewChart: "رسم",
    paperViewTable: "جدول",
    paperTurnAverage: "المتوسط لكل الطاولات: {minutes}",
    paperColVsAverage: "مقارنة بالمتوسط",
    paperTurnSlower: "أبطأ بـ {minutes}",
    paperTurnFaster: "أسرع بـ {minutes}",
    paperTurnAbout: "قريب من المتوسط",
    // جولة الإصلاح (2026-10-03) — اللي الأشكال التانية بتقوله ومكانش ظاهر.
    paperPeriodTotal: "{revenue} المتحصّل في المدة",
    paperTurnMedian: "الوسيط {minutes}",
    paperTurnSlowest: "الأبطأ: طاولة {name}، {minutes}",
  },
  /**
   * الموردين (B45، وحدة 5) — مين جاب التوريدة.
   *
   * مش أوامر شرا: مافيش هنا حاجة بتتطلب ولا حاجة مستحقة لحد. الصف ده اسم،
   * ومعاه رقم تسجيل ضريبي لو موجود — وده تالت حقيقة بتقوم عليها ضريبة
   * المشتريات، وماكانش ليها مكان في الدفتر قبل كده.
   */
  aggregators: {
    title: "منصّات التوصيل",
    description:
      "طلبات وإلمنس وغيرهما، مع النسبة التي تقتطعها كل منصّة من الطلب قبل وصول النقود إليك.",
    noAccessTitle: "لا تملك صلاحية عرض منصّات التوصيل",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية إدارة الإعدادات لتعديل النسب، أو صلاحية التقارير لعرض إجمالياتها. تواصل مع صاحب المطعم لإضافة إحداهما إلى دورك.",
    // P6-Q5، الخيار A: مدير الصالة يشوف الشاشة والإجماليات من غير ما يعدّل.
    readOnlyNote:
      "يمكنك رؤية المنصّات وإجمالياتها. إضافة منصّة أو تغيير نسبتها أو إيقافها يتطلب صلاحية إدارة الإعدادات.",
    add: "منصّة جديدة",
    editTitle: "تعديل المنصّة",
    emptyTitle: "لا توجد منصّات حتى الآن",
    emptyBody:
      "أضف طلبات أو إلمنس أو أي منصّة تتعامل معها، وحدّد نسبتها؛ ويمكنك بعدها اختيارها عند فتح فاتورة توصيل.",
    fieldName: "اسم المنصّة",
    fieldCommission: "نسبة العمولة %",
    invoiceCountNone: "لا توجد فواتير عليها حتى الآن",
    invoiceCountOne: "فاتورة واحدة عليها",
    // الاتنين في العربي مثنى، مش رقم + جمع: «فاتورتين»، مش «2 فاتورة».
    invoiceCountTwo: "فاتورتين عليها",
    invoiceCount: "{count} فاتورة عليها",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    invoiceCountFew: "{count} فواتير عليها",
    retiredChip: "موقوفة",
    retire: "إيقاف المنصّة",
    restore: "إعادة المنصّة",
    retired: "موقوفة — لن تظهر في الاختيارات",
    restored: "أُعيدت المنصّة",
    created: "أُضيفت المنصّة",
    saved: "تم الحفظ",
    /* B118/QA5 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    countSummaryOne: "منصّة واحدة نشطة من {total}",
    countSummaryTwo: "منصّتان نشطتان من {total}",
    countSummaryFew: "{active} منصّات نشطة من {total}",
    countSummary: "{active} منصّة نشطة من {total}",
    // PAPER (تصميم 2026-10) — شاشة المنصّات: ٣ حقائق فوق الكروت، وكارت لكل منصّة.
    factCommissionTitle: "العمولة تُخصم أولًا",
    factCommissionBody:
      "طلب بقيمة {amount} بعمولة {percent}% يصلك منه {kept}. والتقارير تعرض ما يتبقى لك.",
    factPayoutTitle: "التحويلات لا تمر بالخزينة",
    factPayoutBody:
      "تدفع المنصّات بعد أيام بتحويل، لذلك لا تغيّر طلباتها عدّ النقدية في الخزينة.",
    factRateTitle: "النسبة الجديدة لا تمس ما قبلها",
    factRateBody:
      "تغيير النسبة يسري على الفواتير الجديدة، وتحتفظ كل فاتورة سابقة بالنسبة التي فُتحت بها.",
    listTitle: "المنصّات",
    rateShort: "عمولة {percent}%",
    // T15 — على إيه النسبة بتتحسب. جملة واحدة لكل أساس.
    fieldBase: "تُحسب العمولة على",
    baseFood: "الطعام فقط",
    baseFoodHint:
      "النسبة على الأطباق وضريبتها فقط؛ رسوم التوصيل للمنصة ولا تدخل في العمولة.",
    baseTotal: "قيمة الطلب كاملة",
    baseTotalHint: "النسبة على كل ما دفعه العميل، رسوم التوصيل وضريبتها معًا.",
    baseLine: "على {base}",
    statusActive: "نشطة",
    // P6b (قرار P6-Q2): الأرقام دي بقت لفترة يختارها المدير، وبتتحمّل لما يطلبها
    // بس — مش مع كل فتح للشاشة. نفس أساس «المبيعات والتكاليف»، فبتطابقه لنفس الأيام.
    statBilled: "إجمالي الفواتير",
    statCommission: "العمولة",
    statEffective: "{rate}% فعليًا",
    keepShare: "يتبقى لك {pct}%",
    keptToYou: "{amount} لك",
    totalsShow: "عرض الإجماليات",
    totalsHide: "إخفاء الإجماليات",
    totalsRegion: "إجماليات المنصّات",
    allTimeChip: "على مدى الوقت",
    // طريقة الحساب ورا باب «كيف تم الاحتساب؟»، زي التقارير (report-pages.md).
    basisBilled:
      "قيمة كل فاتورة مغلقة أو مسدّدة جاءت من المنصّة في أيام العمل المختارة، كما حُفظت على الفاتورة — نفس أرقام «المبيعات والتكاليف» للأيام نفسها.",
    basisCommission:
      "العمولة المحفوظة على كل فاتورة بالنسبة التي فُتحت بها؛ تغيير النسبة لاحقًا لا يغيّرها.",
    basisWriteOffTerm: "الفواتير المسجّلة بلا دفع",
    basisWriteOff:
      "فاتورة سُجّلت بلا دفع داخل هذه الأيام تخرج من الرقمين، وأخرى سُجّلت بعدها تبقى فيهما، كقاعدة التقارير.",
    basisTitle: "كيفية احتساب هذه الأرقام",
    basisCountTerm: "عدد الفواتير على المنصّة",
    basisCount:
      "كل فاتورة تحمل اسم المنصّة بأي حالة، على مدى الوقت وليس للفترة المختارة؛ ولهذا لا تُحذف المنصّة بل تُوقف.",
    totalsLoading: "جارٍ جمع الفواتير...",
    totalsAll: "كل المنصّات: إجمالي الفواتير {billed} · العمولة {commission}",
    totalsNone: "لا توجد فواتير منصّات في هذه الأيام.",
    totalsCardNone: "لا توجد فواتير عليها في هذه الأيام",
    totalsPeriod: "في هذه الفترة",
    totalsTooLong: "تغطي الإجماليات {span} كحد أقصى؛ اختر فترة أقصر.",
    // مراجعة #310: من غير يوم العمل بتاع المطعم الإجماليات مش بتتطلب.
    totalsDayUnknown:
      "تعذّر قراءة يوم العمل الخاص بالمطعم، لذلك لم تُحسب الإجماليات بعد.",
    editRate: "تعديل النسبة",
    previewKeep: "من طلب بقيمة {amount} يتبقى لك {kept}.",
    previewForward:
      "تسري النسبة الجديدة من الفاتورة التالية، وتحتفظ الفواتير السابقة بنسبتها القديمة.",
    // PAPER (تكافؤ البكسل، 2026-10-02) — كلمات التصميم نفسها: سطر الشريط العلوي،
    // زرار الإضافة، العدّ، وأزرار الكارت والنافذة.
    paperSubtitle: "طلبات وإلمنس وغيرهما: العمولة وما يتبقى لك.",
    paperAdd: "إضافة منصّة",
    /* العدّ بنصّين بينهما « · »، ولكل نص أشكال العدد الأربعة (١ / ٢ / ٣–١٠ /
       ١١+). انظر `plural.ts`. */
    paperCountActiveOne: "منصّة نشطة",
    paperCountActiveTwo: "منصّتان نشطتان",
    paperCountActiveFew: "{count} منصّات نشطة",
    paperCountActive: "{count} منصّة نشطة",
    paperCountRetiredOne: "منصّة موقوفة",
    paperCountRetiredTwo: "منصّتان موقوفتان",
    paperCountRetiredFew: "{count} منصّات موقوفة",
    paperCountRetired: "{count} منصّة موقوفة",
    paperRetire: "إيقاف",
    paperBringBack: "إعادتها",
    paperAddTitle: "إضافة منصّة توصيل",
    paperEditTitle: "تعديل {name}",
    paperNamePlaceholder: "مثلاً: طلبات",
    paperFieldCommission: "العمولة",
  },
  suppliers: {
    title: "الموردون",
    description: "الموردون الذين تشتري منهم وأرقام تسجيلهم الضريبي، إن وجدت.",
    noAccessTitle: "لا تملك صلاحية عرض الموردين",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية إدارة التكاليف. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    add: "مورد جديد",
    editTitle: "تعديل المورد",
    searchPlaceholder: "ابحث باسم المورد أو رقم التسجيل",
    filterLabel: "تصفية الموردين",
    filterAll: "الكل",
    filterActive: "نشطون",
    filterRetired: "موقوفون",
    emptyTitle: "لم يُسجّل أي مورد حتى الآن",
    emptyBody:
      "أضف المورد، ثم يمكنك اختياره عند تسجيل توريدة. وبذلك يوضّح تقرير ضريبة المشتريات ما تدعمه فاتورة ضريبية وما لا تدعمه.",
    noMatchTitle: "لا يوجد مورد بهذا الاسم",
    noMatchBody: "جرّب اسمًا آخر أو أزل التصفية.",
    // «موقوف» مش «متمسوح»: الصف ده هو السجل الوحيد اللي بيقول فاتورة ضريبية
    // جات من مين، فمش بيتشال من الداتابيز — بيتشال من قايمة الاختيار بس.
    retire: "إيقاف المورد",
    restore: "إعادة المورد",
    retiredChip: "موقوف",
    retired: "أُوقف المورد — لن يظهر في اختيار التوريدة.",
    restored: "أُعيد المورد.",
    created: "سُجّل المورد.",
    saved: "تم الحفظ.",
    taxRegistrationLabel: "تسجيل ضريبي",
    // غياب الرقم إجابة مش فراغ: أغلب مشتريات أي مطعم مصري من موردين مالهمش
    // تسجيل، والشرا منهم مالوش فاتورة ضريبية تسنده.
    noTaxRegistration: "بلا تسجيل ضريبي",
    /* الجمع. مافيش آلية تصريف في الـ i18n هنا، والمفتاحين دول أرخص من واحدة:
       «1 توريدة مسجّلة» تقرا غلط، و«توريدة واحدة مسجّلة» هي اللي بتتقال. */
    purchaseCountOne: "توريدة واحدة مسجّلة",
    purchaseCountNone: "لا توجد توريدات مسجّلة حتى الآن",
    purchaseCountTwo: "توريدتان مسجّلتان",
    purchaseCountFew: "{count} توريدات مسجّلة",
    purchaseCount: "{count} توريدة مسجّلة",
    /* B118/QA5 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    countSummaryOne: "مورد واحد نشط من إجمالي {total}",
    countSummaryTwo: "موردان نشطان من إجمالي {total}",
    countSummaryFew: "{active} موردين نشطين من إجمالي {total}",
    countSummary: "{active} مورد نشط من إجمالي {total}",
    fieldName: "اسم المورد",
    fieldTaxRegistration: "رقم التسجيل الضريبي",
    fieldTaxRegistrationHint:
      "أدخل رقم تسجيل المورد، لا رقم المطعم. الحقل اختياري، لكن من دونه لن تُعد مشترياته مدعومة بفاتورة ضريبية.",
    fieldPhone: "الهاتف",
    fieldNotes: "ملاحظات",
    // PAPER (2026-10) — سطرين تحت بلاطتين الموردين.
    paperRetireNote: "يُوقف المورد ولا يُحذف",
    paperAllRegistered: "كل الموردين لهم تسجيل ضريبي",
    // PAPER (المؤسس، 2026-10-02) — جملة التصميم تحت العنوان، وزرار الإضافة بكلمته.
    paperSubtitle: "الموردون الذين تشتري منهم، وما يوردونه، والمستحق لهم.",
    paperAdd: "إضافة مورد",
    paperAddTitle: "إضافة مورد جديد",
    paperAllPaidUp: "كل المستحقات مدفوعة",
  },
  /**
   * الشراء بأبسط شكل (قرار صاحب المنتج، 2026-10-02): الطلبية، والمستحق
   * للموردين، وصورة فاتورة المورد.
   *
   * «طلبية» = اللي اتطلب من المورد ولسه ما وصلش. «توريدة» = اللي وصل واتسجّل.
   * الطلبية ما بتحرّكش المخزون ولا التكلفة — التوريدة بس هي اللي بتحرّكهم،
   * والكلام لازم يقول كده في كل مكان الاتنين بيظهروا فيه.
   */
  /*
   * استلام بضاعة (D147, D148 — 2026-10-14). ورقة المورد بسطورها في فعل واحد،
   * مدفوعة من الدرج أو على الحساب، من اللي وزن البضاعة. الشاشة مافيهاش ولا رقم
   * تكلفة: لا متوسط ولا قيمة مخزون — الكاشير بيشوفها.
   */
  deliveries: {
    title: "استلام البضائع",
    description: "فاتورة المورد بسطورها دفعة واحدة — والدفع من الخزينة أو على الحساب.",
    noAccessTitle: "استلام البضائع غير متاح لك",
    noAccessBody:
      "تحتاج هذه الشاشة صلاحية «استلام البضائع من المورد». اطلبها من صاحب المطعم.",
    loadFailed: "تعذّر تحميل الأصناف والموردين. حدّث الصفحة.",
    noIngredientsTitle: "لا توجد أصناف مخزون بعد",
    noIngredientsBody:
      "يضيف صاحب المطعم أصناف المخزون أولًا، ثم يمكن الاستلام عليها.",
    headerTitle: "فاتورة المورد",
    paperCardSub: "ما سلّمه المورد، كما هو مكتوب",
    supplierHint: "اختياري.",
    colNumber: "#",
    colVat: "الضريبة",
    photoAdd: "إضافة صورة",
    photoRemove: "إزالة الصورة",
    noPaperSwitch: "لم يسلّم المورد فاتورة",
    savedDismiss: "إخفاء",
    date: "يوم الاستلام",
    dateHint: "اليوم، أو حتى {from} سابقًا.",
    dateOutOfRange: "يجب أن يكون اليوم بين {from} واليوم الحالي.",
    supplier: "المورد",
    supplierNone: "بدون مورد",
    supplierPlaceholder: "اختر المورد",
    supplierNoResults: "لا يوجد مورد بهذا الاسم",
    supplierTaxRegistered: "هذا المورد مسجّل ضريبيًا.",
    paperRef: "رقم الفاتورة",
    paperRefHint: "الرقم المكتوب على فاتورة المورد، إن وُجد.",
    paperTotal: "إجمالي الفاتورة",
    paperTotalHint: "المبلغ المكتوب في آخر الفاتورة كما هو.",
    paperTotalInvalid: "أدخل إجمالي الفاتورة (صفر أو أكثر).",
    taxInvoice: "نوع الفاتورة",
    taxInvoiceYes: "فاتورة ضريبية",
    taxInvoiceNo: "لا توجد فاتورة ضريبية من المورد",
    taxInvoiceYesHint:
      "أدخل الضريبة المكتوبة على كل سطر؛ مجموعها هو ضريبة الفاتورة.",
    taxInvoiceNoHint: "تُسجَّل الضريبة صفرًا على كل السطور.",
    linesTitle: "السطور",
    linesHint: "سطر لكل صنف في الفاتورة، ويمكن أن يرد الصنف نفسه في سطرين.",
    lineNumber: "سطر {number}",
    lineIngredient: "الصنف",
    lineIngredientPlaceholder: "ابحث عن الصنف",
    lineNoResults: "لا يوجد صنف بهذا الاسم",
    lineQuantity: "الكمية",
    lineQuantityMode: "الكمية بوحدة",
    linePackFill: "{packs} {noun} × {size} = {quantity} {unit}",
    lineTotal: "إجمالي السطر",
    lineVat: "ضريبة السطر",
    lineVatHint: "ضمن إجمالي السطر.",
    lineRemove: "حذف",
    lineRemoveAria: "حذف السطر {number}",
    addLine: "سطر جديد",
    tooManyLines: "الفاتورة الواحدة حتى {max} سطرًا.",
    lineIngredientRequired: "اختر الصنف.",
    lineQuantityInvalid: "أدخل كمية أكبر من صفر.",
    lineTotalInvalid: "أدخل إجمالي السطر (صفر أو أكثر).",
    lineVatInvalid: "لا يمكن أن تتجاوز الضريبة إجمالي السطر.",
    noLines: "أضف سطرًا واحدًا على الأقل.",
    summaryTitle: "الإجماليات",
    summaryLines: "مجموع السطور",
    summaryPaper: "الفاتورة",
    summaryNonStock: "غير مخزون",
    summaryNonStockHint:
      "الفرق بين الفاتورة والسطور — بنود مثل الغاز والصابون والثلج. يُسجَّل بجانب الفاتورة ولا يوقف الحفظ.",
    summaryOverPaper:
      "تزيد السطور على الفاتورة بمقدار {amount}. راجع الأرقام — لن يتوقف الحفظ.",
    summaryVat: "ضريبة الفاتورة",
    paymentTitle: "الدفع",
    paymentDrawer: "من الخزينة",
    paymentAccount: "على الحساب",
    paymentDrawerHint:
      "يُسجَّل الآن مصروف بإجمالي الفاتورة ({amount}) من الخزينة المفتوحة، وتصبح السطور مدفوعة.",
    paymentAccountHint: "تبقى السطور مستحقة للمورد حتى تسجيل الدفع، مثل أي توريد.",
    paperTitle: "مستند الفاتورة",
    paperPhoto: "صورة الفاتورة",
    paperNone: "بدون فاتورة",
    paperPick: "اختيار صورة",
    paperChange: "تغيير الصورة",
    paperPicked: "الصورة: {name}",
    paperPhotoHint: "صورة واحدة للفاتورة كلها، تُرفع فور الحفظ.",
    paperNoneHint: "لا توجد فاتورة إطلاقًا — مثل مشتريات السوق.",
    paperAnswerRequired: "صوّر الفاتورة، أو فعّل «لم يسلّم المورد فاتورة».",
    paperOwnerOptional:
      "الصورة ليست إلزامية لك، لكنها تزيل علامة «بدون صورة» عن كل السطور في تقرير ضريبة المشتريات.",
    save: "تسجيل الاستلام",
    saving: "جارٍ التسجيل…",
    savedTitle: "سُجّل الاستلام",
    savedBody: "سُجّلت الفاتورة بمجموع سطور {amount}.",
    savedDrawer: "سُجّل مصروف {amount} من خزينتك.",
    savedAccount: "السطور مستحقة على حساب المورد.",
    savedNonStock: "بنود غير مخزنية في الفاتورة: {amount}.",
    photoUploading: "جارٍ رفع الصورة…",
    photoUploaded: "رُفعت صورة الفاتورة.",
    photoFailed:
      "سُجّلت الفاتورة، لكن لم تُرفع الصورة. حاول مرة أخرى من هنا.",
    photoRetry: "إعادة رفع الصورة",
    photoMissing: "صورة ناقصة",
    photoMissingHint:
      "تحتاج هذه الفاتورة إلى صورة، وستبقى عليها علامة «صورة ناقصة» حتى تُرفع.",
    receiveAnother: "استلام آخر",
    openStock: "المخزون",
    openSupplier: "صفحة المورد",
    openDrawer: "الخزينة",
    stockEntry: "استلام البضائع",
    // الرفض من السيرفر، بالكلام.
    errorDrawerNotOpen:
      "لا توجد خزينة مفتوحة باسمك. افتح خزينتك، أو اختر «على الحساب».",
    errorDuplicatePaper:
      "سُجّلت فاتورة هذا المورد بهذا الرقم من قبل يوم {date}.",
    errorDuplicatePaperUndated:
      "سُجّلت فاتورة هذا المورد بهذا الرقم من قبل.",
    errorVatWithoutTaxInvoice:
      "يوجد سطر عليه ضريبة، والفاتورة ليست فاتورة ضريبية.",
    errorPackInvalid:
      "الكمية لا تطابق عدد العبوات × حجمها. أدخلها مرة أخرى.",
    errorDrawerTotalZero:
      "يتطلب الدفع من الخزينة إجمالي فاتورة أكبر من صفر.",
    errorPhotoAlreadyAttached:
      "صورة الفاتورة مسجّلة بالفعل، ويغيّرها صاحب المطعم فقط.",
    errorNotFound: "هذه الفاتورة غير موجودة.",
    // صفحة المورد: السطور متجمّعة تحت ورقتها.
    paperGroupRef: "فاتورة رقم {ref}",
    paperGroupNoRef: "فاتورة بدون رقم",
    paperGroupBy: "استلمها {name}",
    paperGroupTotal: "الفاتورة",
    paperGroupNonStock: "غير مخزون",
    paperGroupNoPaper: "بدون فاتورة",
    paperGroupHasPhoto: "عليها صورة",
    paperGroupFromDrawer: "دُفعت من الخزينة",
    paperGroupOnAccount: "على الحساب",
    // صف الدرج: المصروف اللي دفع ورقة.
    drawerMovementPaper: "فاتورة {supplier} رقم {ref}",
    drawerMovementPaperNoRef: "فاتورة {supplier}",
  },
  purchasing: {
    // ── الطلب (شاشة المخزون) ──
    reorder: "طلب",
    reorderAria: "طلب {item} من مورد",
    reorderAllLow: "طلب كل الناقص",
    reorderTitle: "طلبية من مورد",
    reorderTitleMany: "طلبية لكل ما قارب على النفاد",
    reorderSubtitle:
      "الطلبية لا تحرّك المخزون ولا التكلفة. يزيد المخزون عند استلام التوريدة.",
    fieldSupplier: "المورد",
    fieldQuantity: "الكمية",
    fieldExpectedOn: "موعد التوريد المتوقع",
    fieldExpectedOnHint: "اختياري — اتركه فارغًا إن لم يحدد المورد يومًا.",
    suggestionHint:
      "المقترح {quantity} {unit}: ما يعيد المخزون إلى ضعف الحد الأدنى، بعد خصم الموجود وما طُلب بالفعل.",
    suggestionNoMinimum:
      "لا يوجد حد أدنى لهذا المكوّن، فلا يوجد اقتراح — اكتب الكمية.",
    supplierRequired: "اختر المورد الذي ستطلب منه.",
    supplierPick: "اختر موردًا",
    noActiveSuppliers:
      "أضف موردًا أولًا من شاشة الموردين — الطلبية تُسجَّل مع مورد.",
    removeLine: "إزالة {item} من الطلبية",
    placeOrder: "تسجيل الطلبية",
    ordered: "سُجّلت الطلبية. يتحرك المخزون عند الاستلام.",
    reorderNothing: "لا يوجد مكوّن ناقص إلا وهو مطلوب بالفعل.",
    alreadyCovered: "مطلوب بالفعل بما يكفي: {items}",
    // ── الطلبيات اللي لسه ما وصلتش ──
    onOrderLine: "مطلوب {quantity} {unit}",
    onOrderDue: "متوقع {date}",
    openOrdersTitle: "طلبيات لم تصل",
    openOrdersSubtitle:
      "عند وصول الطلبية اضغط «استلام» — يسجّل ذلك التوريدة، وهي وحدها ما يحرّك المخزون والتكلفة.",
    openOrdersFromSupplier: "طلبيات لم تصل من هذا المورد",
    receive: "استلام",
    receiveAria: "استلام {item} من {name}",
    cancelOrder: "إلغاء الطلبية",
    cancelOrderAria: "إلغاء طلبية {item}",
    cancelConfirmTitle: "إلغاء هذه الطلبية؟",
    cancelConfirmBody:
      "{item} من {name}. لم يتحرك شيء عند الطلب، فلا يُلغى إلا الطلبية نفسها. اطلبها مرة أخرى إن احتجت إليها.",
    cancelConfirmKeep: "إبقاء الطلبية",
    cancelled: "أُلغيت الطلبية.",
    expectedOn: "متوقع {date}",
    noExpectedDate: "بلا موعد",
    orderedOn: "طُلبت {date}",
    receivingNote:
      "استلام طلبية {date} من {name}. المورد هو مورد الطلبية؛ إن جاءت التوريدة من مورد آخر فسجّلها بدون الطلبية.",
    // ── المستحق للموردين ──
    youOwe: "عليك",
    paidUp: "لا شيء مستحق",
    owedSuppliersOne: "لمورد واحد",
    owedSuppliersTwo: "لموردين",
    owedSuppliersFew: "لـ{count} موردين",
    owedSuppliers: "لـ{count} مورد",
    nextDelivery: "التوريد القادم",
    nextDeliveryNone: "لا توجد طلبيات",
    nextDeliveryUndated: "مطلوب بلا موعد",
    lastDelivery: "آخر توريد",
    noneYet: "لا يوجد بعد",
    colNext: "القادم",
    markPaid: "تسجيل {amount} كمدفوع",
    markPaidTitle: "تسجيل {amount} لـ{name} كمدفوع؟",
    markPaidBody:
      "يسجّل هذا أنك سدّدت كل توريدات هذا المورد غير المدفوعة. لا يسحب أي مبلغ من الخزينة — إن دفعت من الخزينة فسجّل المصروف على الخزينة أيضًا.",
    markPaidConfirm: "تسجيل كمدفوع",
    markPaidDone: "سُجّل الدفع.",
    markPaidNeedsReports:
      "تسجيل الدفع يحتاج صلاحية عرض التقارير أيضًا، لأنه يغيّر ما تقوله الأرقام عن المستحق.",
    owedChanged:
      "تغيّر المستحق لهذا المورد إلى {amount}. راجعه وأكّد مرة أخرى.",
    // ── سجل المورد ──
    details: "التفاصيل",
    latestDelivery: "آخر توريدة · {date}",
    stepOrdered: "طُلبت",
    stepNotOrdered: "بدون طلبية",
    stepDelivered: "وصلت",
    stepTaxIn: "الفاتورة الضريبية وصلت",
    stepTaxMissing: "الفاتورة الضريبية ناقصة",
    stepTaxMissingHint: "اطلبها من المورد",
    stepReceiptOnly: "إيصال فقط",
    stepReceiptOnlyHint: "المورد غير مسجّل ضريبيًا",
    stepPaid: "مدفوعة",
    stepUnpaid: "لم تُدفع بعد",
    stepUntracked: "سُجّلت قبل متابعة الدفع",
    deliveriesTitle: "التوريدات",
    deliveriesTruncated: "تُعرض أحدث التوريدات فقط.",
    noDeliveries: "لم تُسجَّل توريدات من هذا المورد بعد.",
    colDate: "التاريخ",
    colInvoice: "الفاتورة",
    colItem: "الصنف",
    colAmount: "المبلغ",
    colStatus: "الحالة",
    receipt: "إيصال",
    statusPaid: "مدفوعة",
    statusUnpaid: "غير مدفوعة",
    statusUntracked: "غير متابَعة",
    // D142 (phase 9): أُرجعت كلها قبل أن تُدفع — لا شيء مستحق عليها.
    statusReturned: "مُرتجعة بالكامل",
    stepReturned: "مُرتجعة بالكامل",
    stepReturnedHint: "لا شيء مستحق عليها",
    // لم تُدفع وأُرجع جزء منها: ما زال مستحقًا عليها بعد المرتجع.
    stepUnpaidNet: "المستحق {amount}",
    netOwed: "الصافي {amount}",
    // ── صورة فاتورة المورد ──
    photo: "صورة الفاتورة",
    // عمود الصورة في ملف التصدير.
    photoOnFile: "مرفقة",
    photoAttach: "إرفاق صورة",
    photoAttachAria: "إرفاق صورة فاتورة {item}",
    photoReplace: "استبدال الصورة",
    photoRemove: "حذف الصورة",
    photoView: "عرض الصورة",
    photoAlt: "فاتورة المورد لـ{item}",
    photoPrivateNote: "تُحفظ بشكل خاص، ولا تُفتح إلا برابط مؤقت.",
    photoRemoveTitle: "حذف صورة الفاتورة؟",
    photoRemoveBody:
      "تبقى التوريدة كما سُجّلت وتُحذف الصورة فقط. يمكنك إرفاق صورة أخرى.",
    photoAttached: "أُرفقت الصورة.",
    photoRemoved: "حُذفت الصورة.",
    photoWrongType: "استخدم صورة بصيغة JPEG أو PNG أو WebP.",
    photoTooLarge: "الصورة كبيرة جدًا.",
    photoUploadFailed: "لم تُرفع الصورة. تحقّق من الاتصال وحاول مرة أخرى.",
    photoNotYours: "هذه الصورة لا تخص هذه التوريدة. أرفقها مرة أخرى.",
    photoLoadFailed: "تعذّر فتح الصورة. حاول مرة أخرى.",
    // ── ضريبة المشتريات ──
    issueNoPhoto: "بلا صورة",
    noPhotoCountOne: "توريدة واحدة عليها ضريبة بلا صورة فاتورة",
    noPhotoCountTwo: "توريدتان عليهما ضريبة بلا صورة فاتورة",
    noPhotoCountFew: "{count} توريدات عليها ضريبة بلا صورة فاتورة",
    noPhotoCount: "{count} توريدة عليها ضريبة بلا صورة فاتورة",
    noPhotoNote:
      "غياب الصورة لا يجعل التوريدة غير مدعومة؛ الصورة نسخة الفاتورة المحفوظة هنا لمن يراجعها.",
    // ── رسائل الخادم ──
    errorOrderNotOpen: "هذه الطلبية استُلمت أو أُلغيت بالفعل. حدّث القائمة.",
    errorOrderNotFound: "هذه الطلبية لم تعد موجودة. حدّث القائمة.",
    errorOrderMismatch:
      "هذه التوريدة لا تطابق الطلبية (مكوّن أو مورد مختلف). سجّلها بدون الطلبية.",
    errorSupplierInactive: "هذا المورد موقوف. أعِده قبل أن تطلب منه.",
    errorPurchaseNotFound: "هذه التوريدة لم تعد موجودة. حدّث الصفحة.",
    errorOwedChanged: "تغيّر المستحق لهذا المورد. راجع المبلغ وأكّد مرة أخرى.",
  },
  /**
   * ضريبة المشتريات — الطرف التاني من الإقرار (F8، وحدة 5).
   *
   * الشاشة دي ما بتقولش إيه اللي «ينفع يتخصم» — ده قرار ضريبي، وهو قرار صاحب
   * المطعم ومحاسبه مش قرار الشاشة. اللي بتقيسه هو الاكتمال: هل الحقايق التلاتة
   * اللي بتتكوّن منها الفاتورة الضريبية موجودة على التوريدة ولا لأ.
   */
  reportInputVat: {
    // D142 — مرتجعات المشتريات في الفترة: ضريبتها تُخصم من كل أرقام الضريبة.
    returnsTitle: "مرتجعات مشتريات في هذه الفترة",
    returnsBodyOne: "مرتجع واحد بقيمة {amount} خُصم منه {vat} من ضريبة المدخلات. كل أرقام الضريبة في هذه الصفحة صافية بعد هذا الخصم، أما المشتريات وعددها فكما استُلمت.",
    returnsBodyTwo: "مرتجعان بقيمة {amount} خُصم منهما {vat} من ضريبة المدخلات. كل أرقام الضريبة في هذه الصفحة صافية بعد هذا الخصم، أما المشتريات وعددها فكما استُلمت.",
    returnsBodyFew: "{count} مرتجعات بقيمة {amount} خُصم منها {vat} من ضريبة المدخلات. كل أرقام الضريبة في هذه الصفحة صافية بعد هذا الخصم، أما المشتريات وعددها فكما استُلمت.",
    returnsBody: "{count} مرتجع بقيمة {amount} خُصم منها {vat} من ضريبة المدخلات. كل أرقام الضريبة في هذه الصفحة صافية بعد هذا الخصم، أما المشتريات وعددها فكما استُلمت.",
    returnsNet: "صافي ضريبة المدخلات المسجّلة",
    returnsNegativeNote: "المرتجعات في هذه الفترة أكبر من مشترياتها، لذلك صافي ضريبة المدخلات بالسالب.",
    basisTermReturns: "مرتجعات مشتريات",
    basisDefReturns: "ضريبة المرتجع تُخصم في فترة الإرجاع، من نفس الأساس الذي تقع عليه توريدته (بفاتورة كاملة أو لا) ومن صف مورده. تظهر المرتجعات سطورًا في قائمة إشعارات المرتجع، لا في قائمة التوريدات.",
    csvReturnsTitle: "مرتجعات مشتريات",
    csvReturnsCount: "العدد",
    csvReturnsValue: "القيمة",
    csvReturnsVat: "الضريبة المخصومة",
    csvReturnsSupportedVat: "منها على توريدات بفاتورة كاملة",
    csvNetVat: "صافي ضريبة المدخلات المسجّلة",
    // PA-9 (phase 12, Khalifa): the credit notes one by one.
    creditNotesTitle: "إشعارات المرتجع",
    colCreditNote: "رقم الإشعار",
    colOriginalInvoice: "عن فاتورة",
    colOriginalDate: "تاريخ الفاتورة الأصلية",
    colQuantity: "الكمية",
    creditNotesTruncated: "الإشعارات أكثر مما نعرضه؛ هذه أحدثها فقط، أما الإجماليات أعلاه فتشمل الفترة كلها.",
    title: "ضريبة المشتريات",
    description: "الطرف الآخر من الإقرار: ضريبة دُفعت للموردين.",
    noAccessTitle: "لا تملك صلاحية عرض هذا التقرير",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية عرض التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    loading: "جارٍ تجهيز التقرير...",
    emptyTitle: "لا توجد مشتريات مسجّلة في هذه المدة",
    emptyBody: "جرّب نطاقًا زمنيًا آخر، أو سجّل التوريدات من شاشة المخزون.",
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذا التقرير",
    basisTermSupported: "«خلف فاتورة ضريبية»",
    basisDefSupported:
      "التوريدة المدعومة هي التي سُجّلت عليها ضريبة، وربطت بمورّد له رقم تسجيل ضريبي، وكُتب رقم فاتورتها. يوضّح التقرير اكتمال هذه البيانات فقط، ولا يقرر قابلية خصم الضريبة؛ فهذا قرار بينك وبين محاسبك.",
    basisTermGross: "المبالغ",
    basisDefGross:
      "كل مبلغ هنا معروض بالإجمالي كما دُفع؛ فمتوسط التكلفة محسوب حاليًا على الإجمالي ولم يُعدّل.",
    basisTermDay: "اليوم",
    basisDefDay:
      "تُحسب التوريدة على يوم العمل الذي سُجّلت فيه، مثل تقرير المصروف والدخل تمامًا — ولذلك يتطابق إجمالي المشتريات في التقريرين.",
    basisTermScope: "ما الذي تغيّر هنا؟",
    basisDefScope:
      "يشمل التقرير مشتريات المكوّنات فقط، ولا يشمل الإيجار والكهرباء أو أي فواتير أخرى. لذلك فهو ليس إقرارًا ضريبيًا، بل يعرض البيانات المتاحة لدى المطعم.",
    basisGross: "إجمالي",
    statPurchases: "المشتريات",
    statPurchasesSub: "{count} توريدة في المدة",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    statPurchasesSubOne: "توريدة واحدة في المدة",
    statPurchasesSubTwo: "توريدتين في المدة",
    statPurchasesSubFew: "{count} توريدات في المدة",
    statRecorded: "الضريبة المسجّلة",
    statSupportedSub: "{pct}% من الضريبة المسجّلة",
    statSupportedNone: "لا توجد ضريبة مسجّلة في هذه المدة",
    splitNoVat: "بلا ضريبة مسجّلة",
    splitTotal: "الإجمالي",
    purchaseCountOne: "(توريدة واحدة)",
    purchaseCountTwo: "(توريدتين)",
    purchaseCountFew: "({count} توريدات)",
    purchaseCount: "({count} توريدة)",
    gapsTitle: "ما البيانات الناقصة؟",
    gapNoVat: "لا توجد ضريبة مسجّلة",
    gapNoSupplier: "لا يوجد مورد",
    gapNoRegistration: "المورد بلا تسجيل ضريبي",
    gapNoRef: "لا يوجد رقم فاتورة ضريبية",
    // الأعداد التلاتة مش بتتجمع على عدد التوريدات: التوريدة الواحدة ممكن
    // ينقصها حاجتين. اللي يجمعهم ويطلع رقم أكبر هيشك في الكارت كله وهو محق.
    gapsOverlapNote:
      "قد ينقص التوريدة الواحدة أكثر من بيان، لذلك لا يساوي مجموع هذه الأعداد عدد التوريدات.",
    lineSupported: "كاملة",
    suppliersTitle: "حسب المورد",
    linesTitle: "التوريدات بالتفصيل",
    unattributed: "بلا مورد",
    noRegistration: "بلا تسجيل ضريبي",
    colSupplier: "المورد",
    colRegistration: "التسجيل الضريبي",
    colPurchases: "توريدات",
    colTotal: "الإجمالي",
    colVat: "الضريبة",
    colSupportedVat: "منها خلف فاتورة",
    colDate: "اليوم",
    colRef: "رقم الفاتورة",
    colItem: "الصنف",
    colStatus: "الحالة",
    truncatedNote:
      "بلغت القائمة الحد الأقصى للصفوف المعروضة، بينما حُسبت الإجماليات أعلاه على المدة كاملة.",
    /* PAPER (2026-10) — شاشة ضريبة المشتريات بالتصميم الجديد. الكلمة هنا
       «الاكتمال» مش «قابلية الخصم»: الخصم قرار المحاسب. */
    paperMonthAria: "الشهر",
    paperExport: "تصدير للمحاسب",
    paperVatBacked: "{amount} منها خلف فاتورة ضريبية",
    paperCompleteOf: "{count} من {total}",
    paperNeedsFixing: "تحتاج استكمال",
    paperFixSub: "{amount} ضريبة بلا فاتورة ضريبية مكتملة",
    paperFixNone: "لا يوجد ما يحتاج استكمال",
    paperRingTitle: "مدى اكتمال مستنداتك",
    // تحت النسبة في الحلقة: نسبة الضريبة المسجّلة اللي وراها فاتورة ضريبية.
    paperRingCaption: "من الضريبة موثّقة",
    paperChipNotRegistered: "مورد غير مسجّل",
    paperDeductNote:
      "تعرض هذه الصفحة ما تسجّله فواتير الموردين. أما خصم الضريبة من عدمه فيؤكده المحاسب.",
    paperRefMissing: "ناقص",
    paperAddTaxNumber: "إضافة الرقم الضريبي",
    // PAPER (المؤسس، 2026-10-02) — جملة التصميم تحت «ضريبة المشتريات».
    paperSubtitle:
      "ضريبة القيمة المضافة على فواتير الموردين، والفواتير التي ما زالت تحتاج إلى استكمال.",
  },
  /**
   * The cash-drawer shift feature (D23-D29). `manage/shift` (open shift, live
   * summary, end-shift flow — `SHIFT_MANAGE`) and `manage/shifts` (closed
   * history — `REPORT_VIEW`). Both hidden from the nav unless
   * `shiftTrackingEnabled` is on (D25) — see `manage.shiftTracking*` for the
   * settings toggle that flips it.
   */
  shift: {
    title: "الخزينة",
    description:
      "افتح خزينة، وسجّل حركات النقد، ثم أغلقها في نهاية اليوم وفق المبلغ الموجود فعليًا فيها.",
    noAccessTitle: "لا تملك صلاحية إدارة الخزينة",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية إدارة الخزينة. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    loading: "جارٍ تجهيز الخزينة...",
    labelPlaceholder: "خزينة ١",
    labelTakenChip: "مشغول",
    labelTakenWarning:
      "«{label}» مفتوح باسم {name}. لا يمكنك فتح الصندوق نفسه مرة أخرى؛ استلمه منه من القائمة أدناه، أو افتح صندوقًا آخر.",
    someoneElse: "شخص آخر",
    otherDrawersEmpty: "لا توجد خزينة أخرى مفتوحة في هذا المطعم الآن.",
    takeoverButton: "استلام الخزينة",
    takeoverTitle: "استلام «{label}»",
    takeoverBody:
      "عُدّ النقد الموجود في الصندوق مع {name} الآن، ثم اكتب المبلغ المتفق عليه. تُغلق هذه العملية خزينته وتفتح لك خزينة جديدة على الصندوق نفسه في اللحظة ذاتها.",
    takeoverCountHint: "المبلغ الذي عددتماه معًا، وليس المبلغ المتوقع.",
    takeoverDoneToast: "استلمت الخزينة",
    // The two reads that must never degrade to a guess. A failed
    // `shifts/current` used to render the open-a-shift form, which opens a
    // second drawer over a running one; a failed summary used to render a
    // skeleton for ever.
    currentErrorBody:
      "تعذّر التحقق من وجود خزينة مفتوحة، لذلك لن نعرض شاشة فتح خزينة حتى لا تفتح خزينة ثانية فوق خزينة تعمل. حاول مرة أخرى.",
    summaryErrorBody:
      "تعذّر تحميل ملخص الخزينة، لذلك لا تتوفر الأرقام أدناه الآن. حاول مرة أخرى قبل إغلاق الخزينة.",
    // No shift open yet.
    openTitle: "لا توجد خزينة مفتوحة",
    openBody:
      "افتح خزينة لتحصيل النقد ومتابعته. اكتب المبلغ الموجود في الخزينة الآن بوصفه رصيدًا افتتاحيًا.",
    openingFloatLabel: "الرصيد الافتتاحي",
    openingFloatHint: "النقد الموجود في الخزينة قبل بدء الخدمة.",
    openedToast: "فُتحت الخزينة",
    // Unit D — العد الأعمى: بتعدّ الأول، وبعدين تشوف الفرق.
    hiddenUntilCounted: "—",
    blindCountPrintNote:
      "لا يُطبع المبلغ المتوقع في هذه الورقة ما دامت الخزينة مفتوحة، حتى يظل العد مستقلًا. سيظهر بعد الإغلاق.",
    /* B118/QA4 — جملة الشاشة، مش جملة الورقة. لما قاعدة التركيب اتخبّت
       والدرج مفتوح، الشاشة كانت بتستعمل جملة التشيك حرفيًا — «مش بيتطبع
       على الورقة دي» على صفحة مافيهاش ورقة أصلًا. نفس المعنى، بس بتتكلم عن
       المكان اللي الكاشير واقف فيه. */
    blindCountScreenNote:
      "لا تظهر طريقة حساب المبلغ المتوقع ما دامت الخزينة مفتوحة، حتى يظل العد مستقلًا. ستظهر هي والمبلغ المتوقع بعد العد والإغلاق.",
    blindCountHint:
      "عُدّ المبلغ الموجود في الخزينة واكتبه من دون الاطلاع على أي رقم. سيظهر المبلغ المتوقع والفرق بعد الإغلاق، حتى يكون الإدخال نتيجة عد فعلي.",
    blindConfirmHint:
      "أُخفي المبلغ المتوقع والفرق هنا عمدًا. سيظهران فور الإغلاق، ويمكنك عندئذ إعادة العد مرة واحدة إذا اكتشفت خطأً.",
    firstCountNote:
      "كانت نتيجة العد الأول {amount}؛ وهذا المبلغ مسجّل ولا يتغير.",
    // وحدة E — اللي كان برّه على الأرضية لحظة العدّة، متثبّت مع الدرج.
    // فرق صفر مش معناه ليلة نظيفة: ممكن الدرج يقفل مظبوط و٩٠٠ ماشية مع
    // الجرسونات، والنقص بيبان الصبح كأنه مشكلة الصبح.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    closedAgedChequesOne:
      "عند العد كان هناك حساب واحد خارج الخزينة بقيمة {amount}؛ ولم تصل هذه النقود إلى هذه الخزينة.",
    closedAgedChequesTwo:
      "عند العد كان هناك حسابان خارج الخزينة بقيمة {amount}؛ ولم تصل هذه النقود إلى هذه الخزينة.",
    closedAgedChequesFew:
      "عند العد كان هناك {count} حسابات خارج الخزينة بقيمة {amount}؛ ولم تصل هذه النقود إلى هذه الخزينة.",
    closedAgedCheques:
      "عند العد كان هناك {count} حساب خارج الخزينة بقيمة {amount}؛ ولم تصل هذه النقود إلى هذه الخزينة.",
    // نتيجة الطاولة المستديرة رقم ٦ — سلسلة العهدة، بتتقال للكاشير وهو واقف
    // مش الصبح في التقرير. الرقم مش بيتعرض قبل ما يعدّ: لو شاف "قفل بـ ٦٤٠"
    // هيكتب ٦٤٠ — ده بالظبط اللي العدّ الأعمى اتعمل عشانه. يعدّ، وبعدين يبان
    // الفرق وهو لسه قادر يروح يجيب الفلوس.
    // نتيجة الطاولة المستديرة رقم ٤ — التسليم على شاشة المالك، مش بس على شاشة
    // الكاشير اللي استلم. الفرق اللي متسجّل على درج ما عدّهوش صاحبه لازم يقول
    // كده: المالك بيقرا فرق بعد أسبوع، وماعندوش طريقة تانية يعرف بيها.
    filedCountedByOther:
      "عدّ {closer} هذه الخزينة وأغلقها، وليس {opener} الذي فتحها؛ لذلك احتُسب الفرق أدناه وفق عدّ شخص آخر.",
    filedTookOver:
      "استُلِمت هذه الخزينة من {name}؛ والرصيد الافتتاحي {amount} هو المبلغ الذي عُدّ وقت التسليم، وليس رصيدًا أُدخل بصورة مستقلة.",
    filedTookOverChip: "تسليم",
    floatChainTitle: "أُغلقت هذه الخزينة آخر مرة بمبلغ {amount}",
    floatChainHigher:
      "وفتحتها بمبلغ {opened}؛ أي ظهرت زيادة قدرها {gap} في الصندوق من دون تسجيل. راجعها الآن قبل بدء العمل.",
    floatChainLower:
      "وفتحتها بمبلغ {opened}؛ أي خرج منها مبلغ قدره {gap}. قد يكون ذلك طبيعيًا إذا نُقلت النقود إلى الخزنة، لكن يجب تسجيله.",

    varianceWithinTolerance:
      "هذا الفرق ضمن الحد المسموح الذي حدده المطعم ({amount}).",
    // B117/B3 — لافتة على شاشة الدرج المفتوح، للدرج اللي قفلته ولسه ينفع
    // تعدّه تاني. الزرار ده كان عايش في حالة الشاشة بس، فأي reload كان
    // بيوديه — مع إن السيرفر لسه سامح بالعدّة.
    recountPendingTitle: "ما زال بإمكانك عدّ «{label}» مرة أخرى",
    recountPendingBody:
      "أغلقته بعد عدّ {counted}. يُسمح بإعادة العد مرة واحدة فقط، وتظل نتيجة العد الأولى مسجّلة.",
    recountBody:
      "مرة واحدة فقط. ستظل نتيجة العد الأولى مسجّلة كما هي، وسيُحتسب الفرق من نتيجة العد الجديدة وفق المبلغ المتوقع نفسه.",
    recountHint: "المبلغ الذي عددته الآن.",
    recountedToast: "تم تسجيل نتيجة العد الجديدة",
    trackingOffTitle: "نظام الخزائن معطّل في الإعدادات",
    trackingOffBody:
      "لا يستخدم المطعم نظام الخزائن حاليًا. إذا كان ينبغي تفعيله، فتواصل مع صاحب المطعم.",
    trackingOffBodyOwner:
      "نظام الخزائن معطّل حاليًا. فعّل «تتبع الخزائن» من الإعدادات، ثم عُد إلى هنا لفتح خزينة.",
    trackingOffAction: "فتح الإعدادات",
    /*
     * B118 — THE WARNING WITHOUT THE FIGURE. It used to interpolate
     * `{expected}`, which is the one number D99 withholds while the drawer is
     * open: a cashier typed any large amount into "paid out" and the form
     * printed the exact expectation back at her, before the count, with nothing
     * submitted. The blind count was handed over by its own soft check. The
     * warning still earns its place — paying out more than the box holds is
     * usually a typo — it just no longer says how much that is.
     */
    payoutOverDrawerWarning:
      "هذا المبلغ أكبر من المبلغ المتوقع في الخزينة؛ تحقّق منه قبل تسجيله.",

    // The open shift's live summary.
    statExpectedCash: "النقد المتوقع",
    statExpectedCard: "مدفوعات البطاقات المتوقعة",
    statSettledCount: "فواتير متحصّلة",
    statFloat: "الرصيد الافتتاحي",
    // اللي الفواتير نفسها وصلت له بكل طريقة — بيتعرض في مراجعة وردية مقفولة،
    // عشان الفرق يبقى ليه أجزاء تتقري مش رقم واحد من غير تفسير.
    statCashTotal: "نقد الفواتير",
    statCardTotal: "مدفوعات بطاقات الفواتير",
    // D31 — بيتعرض جنب فلوس الدرج مش جواها: الفاتورة دي مدخلتش أي فلوس،
    // وإخفاءها هو اللي بيخلي عدد الفواتير ميطابقش المبلغ.
    statWriteOff: "بلا دفع",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    statWriteOffSubOne: "فاتورة واحدة",
    statWriteOffSubTwo: "فاتورتان",
    statWriteOffSubFew: "{count} فواتير",
    statWriteOffSub: "{count} فاتورة",
    // R6/R12 (BLOCKER 1) — المرتجعات باسمها على شاشة الدرج نفسها. نفس ألفاظ
    // الـ Z بالظبط («مرتجع الدرج» / «شبكة الدرج») عشان الورقة والشاشة
    // يتكلموا بنفس اللغة، مش لغتين لنفس الرقم.
    statRefundCash: "مرتجع الخزينة",
    statRefundCard: "مرتجع بطاقات الخزينة",
    statRefundSub: "{count} صنف أُعيد",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    statRefundSubOne: "صنف واحد أُعيد",
    statRefundSubTwo: "صنفان أُعيدا",
    statRefundSubFew: "{count} أصناف أُعيدت",
    // D44 — العدد بيعدّ كل صنف رجع بأي طريقة، والفلوس اللي جنبه هي بس اللي
    // خرجت من الدرج. المرتجع بتحويل بنكي مش بيحرّك ولا رقم من الاتنين، فلازم
    // الجملة دي تكون على الشاشة عشان العدد ما يبانش غلط.
    refundTransferNote:
      "يشمل عدد الأصناف أعلاه كل ما أُعيد بأي طريقة. أما المبالغ المعروضة فهي التي خرجت من الخزينة أو ماكينة البطاقات فقط؛ إذ يُعاد مرتجع إنستاباي من البنك، فلا يغيّر النقد المتوقع ولا قيمة البطاقات المتوقعة.",
    statInstapay: "تحويلات إنستاباي",
    statInstapaySub: "ذهبت إلى البنك لا إلى الخزينة",
    // Trap 1 — قاعدة التركيب على الشاشة نفسها، مش في تعليق في الكود: من غير
    // السطر ده الورقة بتطبع عهدة وكاش فواتير وكاش متوقع مايجمعوش على بعض،
    // ومحدش يعرف ليه. التحويلات مش في المعادلة أصلًا — لا في الدرج ولا في
    // الشبكة.
    expectedCashFormula:
      "النقد المتوقع = الرصيد الافتتاحي + نقد الفواتير + الداخل − الخارج − مرتجع الخزينة. ومدفوعات البطاقات المتوقعة = مدفوعات بطاقات الفواتير − مرتجع بطاقات الخزينة.",
    // B2 — سطر النطاق: الوردية مش يوم تجاري، والأرقام دي للي اتحصّل عليها هي.
    scopeNote:
      "هذه الأرقام للفواتير التي حُصّلت على هذه الخزينة فقط؛ فالخزينة لا تمثل اليوم التجاري.",
    // S14/D49 — سطر الأساس: الدرج المفتوح أرقامه حيّة، بتتحسب دلوقتي من
    // الفواتير وبتتغيّر مع كل تحصيل، وبتتثبّت ساعة ما تقفل الوردية وتعدّ الدرج.
    basisLive:
      "أرقام الخزينة المفتوحة مباشرة؛ تُحسب الآن وتتغير مع كل فاتورة، وتُثبّت عند إغلاق الخزينة وعدّها.",
    // The expected cash/card figures carry collected VAT and service charge,
    // which the store owes onward — said plainly so a drawer count is never
    // read as profit.
    chargesInCashNote:
      "تشمل المبالغ المتوقعة أي ضريبة قيمة مضافة أو رسوم خدمة حُصّلت مع الفواتير؛ فهي مبالغ موجودة في الخزينة وليست كلها إيرادًا للمطعم.",

    /*
     * ── الشاشة دي بقت صفحة تقرير، في مكانها (UX-round D41 / ruling F15 / Slice 9) ────
     *
     * قرار المالك: الدرج ما اتقسمش على راوتين. الشاشة فضلت لوحة تشغيل حيّة
     * وتقرير درج على نفس الراوت، والخلط ده مسجّل كتنازل مقبول مش كسهو.
     *
     * اللي اتغيّر: ١٣ تايل بقوا صفّ واحد من اتنين (المتوقع كاش والمتوقع فيزا،
     * بالشرطة بتاعتهم زي ما هي — دول العدّ الأعمى شغال)، والباقي بقى سطور جوه
     * تلات كروت، وأربع فقرات أساس كانت مركونة تحت التايلات على طول بقوا ورا
     * باب واحد وفي ملف الـCSV. اللافتات دي هي اللي الشكل ده محتاجها.
     *
     * `basisTrigger` و`basisTitle` و`jumpTo` متكرّرين حرف بحرف من عيلة
     * التقارير عشان الباب يبقى باب واحد في التطبيق كله.
     */
    cardDrawerTitle: "الخزينة الآن",
    /* عنوان الكارت التالت. مش «الإلغاءات والخصومات» — دي بقت كتلة جوّاه جنب
       المرتجعات والبدائل، والكارت لازم يكون اسمه أوسع من أول كتلة فيه. */
    cardExceptionsTitle: "استثناءات الوردية",
    jumpTo: "في هذه الصفحة",
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذه الشاشة",
    exportCsv: "تنزيل CSV",
    /* التوكن الوحيد اللي فاضل على الشاشة بدل سطر `scopeNote`: كلمة واحدة
       بتقول ليه الأرقام دي مش أرقام تقفيلة اليوم. الجملة ورا الباب. */
    basisChipDrawer: "هذه الخزينة",
    /*
     * PAPER (round 3) — رأس الدرج، خانات الإجابة، سجل الدرج، ونافذتين: توريد
     * أو صرف كاش، وعدّ الدرج وقفله. العدّ أعمى زي ما هو: لا رقم متوقع ولا
     * رصيد جاري بيظهر قبل القفل.
     */
    paperOpenedSub: "فُتح {time} برصيد افتتاحي {amount}",
    // الجملة اللي تحت اسم الصفحة في الشريط العلوي (المؤسس، 2026-10-02).
    paperDescription:
      "الخزينة المفتوحة الآن. سجّل المبالغ الداخلة والخارجة، ثم عُدّها لإغلاقها.",
    paperClosedSub: "فُتحت {opened} · أُغلقت {closed}",
    paperStatusOpen: "مفتوحة",
    paperStatusBalanced: "مُغلقة · مطابقة",
    paperStatusOff: "مُغلقة · فرق {amount}",
    paperPayIn: "توريد",
    paperPayOut: "صرف",
    paperCountAndClose: "العدّ والإغلاق",
    paperHiddenSub: "يظهر بعد العدّ والإغلاق — ليبقى العدّ عدًّا حقيقيًا.",
    paperSettledSubNone: "لم تُحصَّل أي فاتورة على هذه الخزينة بعد",
    paperSettledSubOne: "فاتورة واحدة حُصّلت على هذه الخزينة، بأي طريقة دفع",
    paperSettledSubTwo: "فاتورتان حُصّلتا على هذه الخزينة، بأي طريقة دفع",
    paperSettledSubFew: "{count} فواتير حُصّلت على هذه الخزينة، بأي طريقة دفع",
    paperSettledSub: "{count} فاتورة حُصّلت على هذه الخزينة، بأي طريقة دفع",
    paperTilePaidInOut: "توريد / صرف",
    paperCardSub: "ليست في الخزينة — تذهب إلى البنك",
    paperHistoryTitle: "سجل الخزينة",
    paperHistorySub:
      "الأحدث أولًا: الرصيد الافتتاحي والنقد المورَّد أو المصروف يدويًا. المبيعات محسوبة في الخانات أعلاه.",
    paperPayInTitle: "توريد نقدية إلى الخزينة",
    paperPayOutTitle: "صرف نقدية من الخزينة",
    paperPayInSub: "نقدية داخلة إلى الخزينة ليست من المبيعات.",
    paperPayOutSub: "نقدية خارجة من الخزينة، وليست مرتجعًا لعميل.",
    paperCountTitle: "عدّ الخزينة",
    paperCountSub:
      "عُدّ كل ورقة نقدية وعملة، أو أدخل الإجمالي مباشرة. يظهر المبلغ المتوقع بعد الإغلاق.",
    paperCountNote: "ورقة / عملة",
    paperCountHowMany: "العدد",
    paperCountValue: "القيمة",
    /*
     * PAPER update 2 (2026-10-03) — شبكة كروت الدرج، نافذة فتح/استلام الدرج،
     * والعدّ اللي بيشتغل كمان لإعادة العدّ. الكلام من i18n.js بتاع التصميم.
     */
    pdPeriodOpen: "هذه الخزينة منذ فتحها {time}",
    pdPeriodClosed: "هذه الخزينة، من {opened} إلى {closed}",
    pdPeriodNone: "ليست لديك خزينة مفتوحة الآن",
    pdBasisDoor: "طريقة الحساب",
    pdBasisTitle: "كيف تُحسب هذه الخزينة",
    pdExport: "تصدير",
    pdPrintX: "طباعة X",
    pdPrintXTitle: "تقرير X: مطبوعة أثناء الوردية، والخزينة تبقى مفتوحة",
    pdStatusNone: "لا توجد خزينة",
    pdOpenDrawer: "فتح خزينة",
    pdRecountOnce: "إعادة العدّ مرة واحدة",
    pdCountTitle: "العدّ",
    pdCountSub: "المتوقع يظهر الآن فقط لأن الخزينة تم عدّها",
    pdSeeInLog: "عرضه في حركة الخزينة",
    pdExpectedCash: "النقدية المتوقعة",
    pdExpectedCard: "البطاقة المتوقعة",
    pdHidden: "مخفية حتى العدّ",
    pdBlindTag: "عدّ أعمى",
    pdCounted: "ما قمت بعدّه",
    pdRecountTag: "إعادة عد",
    pdManagerTag: "عدّ المدير",
    pdDifference: "الفرق",
    pdOver: "زيادة",
    pdShort: "عجز",
    pdBalanced: "متوازن",
    pdTakingsTitle: "متحصلات هذه الوردية",
    pdTakingsSub: "النقد وحده يدخل هذه الخزينة",
    pdCash: "نقد",
    pdCard: "بطاقة",
    pdInstapay: "إنستاباي",
    // D163 — مدفوعات البطاقة وإنستاباي التي كُتب لها مرجع، بلا مجموع.
    pdRefsTitle: "مراجع العمليات",
    pdRefsSub: "مدفوعات البطاقة وإنستاباي التي سُجّل لها مرجع",
    pdBillsSettled: "الفواتير المسددة",
    pdBillsSettledNote: "بأي طريقة دفع",
    pdInNoDrawer: "خارج الخزائن",
    pdFloorTitle: "ما زالت في الصالة",
    pdFloorSub: "أموال لم تحصّلها هذه الوردية بعد",
    pdOpenTables: "طاولات مفتوحة",
    pdOpenTablesNote: "ما زال الضيوف يتناولون الطعام — لا شيء مطلوب",
    pdChecksOut: "حسابات مطبوعة",
    pdPrintedTag: "مطبوع",
    pdChecksOutNote: "مع الويتر — حصّلها قبل العدّ",
    pdCarriedIn: "منقولة من قبل",
    pdCarriedInNote: "من قبل بداية هذه الخدمة — ورثتها",
    pdSeeBills: "عرض هذه الفواتير ←",
    pdFloorEmpty: "لا شيء في الصالة — كل الفواتير مسددة.",
    pdRefundsTitle: "المرتجعات",
    pdRefundsSub: "أموال رُدّت بعد الدفع",
    pdRefundsNone: "لا مرتجعات على هذه الخزينة.",
    pdRefundsTransfer:
      "الاسترداد المدفوع بتحويل بنكي لا يخرج من أي خزينة؛ ويظهر ضمن الاستثناءات.",
    pdMovesTitle: "الإيداع والسحب",
    pdMovesSub: "أين ذهب النقد الذي خرج",
    pdLeftBusiness: "خرجت من المطعم",
    pdLeftBusinessNote: "مصروفات وتوصيل وسحب المالك",
    pdMovedElsewhere: "نُقلت لمكان آخر",
    pdMovedElsewhereNote: "إيداع في الخزنة أو البنك، وما سُجّل كـ «أخرى»",
    pdPassedThrough: "مرّت للموظفين",
    pdPassedThroughNote: "بقشيش وخدمة صُرفت للفريق",
    pdExceptionsTitle: "استثناءات هذه الوردية",
    pdExceptionsSub: "الإلغاءات والضيافة والمرتجعات والشطب، ومن وافق عليها",
    pdExceptionsEmpty: "لا شيء للمراجعة على هذه الخزينة.",
    pdSwaps: "تبديلات مجانية",
    pdSwapsNote:
      "التكلفة هي تكلفة الطعام وليست السعر. التبديلات المجانية لا تغيّر أي مبلغ في الخزينة.",
    pdOthersTitle: "خزائن مفتوحة أخرى",
    pdOthersSub: "استلم خزينة إذا غادرها الكاشير دون إغلاقها",
    pdOthersSubHolding:
      "من يعمل على خزينة أخرى الآن. أغلق خزينتك قبل أن تستلم غيرها.",
    pdOthersSince: "{name} · منذ {time}",
    pdNotesTitle: "ملاحظات على خزائنك",
    pdNotesSub:
      "خزائنك المغلقة التي كان بها فرق أو كتب عنها أحد. أضف روايتك؛ لا يمكن تعديل الملاحظات أو حذفها.",
    pdNotesFoot:
      "تظهر الملاحظات فقط بعد عدّ الخزينة وإغلاقها، حتى لا تكشف المبلغ المتوقع لخزينة مفتوحة.",
    pdNoNotes: "لا توجد ملاحظات بعد.",
    pdNotePlaceholder: "اكتب ملاحظة عن هذه الخزينة",
    pdAddNote: "إضافة ملاحظة",
    pdManagersBasis: "(عدّ المدير)",
    pdHistorySub: "الأحدث أولاً. النقد فقط؛ مدفوعات البطاقة لا تمر بالخزينة.",
    pdOdTitle: "فتح خزينة",
    pdOdDrawer: "الخزينة",
    pdOdDrawerHint:
      "الاسم المطبوع على تقريري X وZ. لا يمكن فتح خزينة مفتوحة بالفعل مع شخص آخر مرتين.",
    pdOdSave: "فتح الخزينة",
    pdRuleVariance: "أي فرق يُسجَّل على خزينة {name}، وليس على خزينتك.",
    pdRuleFloat: "عدّك يصبح رصيد البداية لخزينتك.",
    pdSaveRecount: "حفظ إعادة العدّ",
    pdCcCounted: "المعدود",
    pdCcExpected: "المتوقع",
    pdFloatPlain: "فُتح برصيد بداية {amount}.",
    pdFloatMatches: "فُتحت بـ {amount}، وهو نفس آخر عدّ لهذه الخزينة.",
    pdFloatTookOver: "استُلم بـ {amount} من {name} — عدّك أصبح رصيد البداية.",
    pdClosedMore:
      "باقي تفاصيل هذه الخزينة — المرتجعات والإيداع والسحب والاستثناءات — في حركة الخزينة.",
    // مصطلحات ورقة الأساس اللي مافيش لافتة على الشاشة تنفع اسم ليها.
    basisTermScope: "نطاق الأرقام",
    basisTermTiming: "وقت تسجيل هذه الأرقام",
    basisTermCharges: "الضريبة ورسم الخدمة",
    basisMovementsNote:
      "ينقسم الصرف من الخزينة إلى ثلاثة أنواع: نقود خرجت من المطعم تمامًا، ونقود نُقلت إلى مكان آخر داخل المطعم مثل الخزنة، ونقود مرّت بالخزينة ولا تخص المطعم أصلًا مثل البقشيش. تُخصم الأنواع الثلاثة من المبلغ المتوقع بالطريقة نفسها، ويختلف تفسيرها لا طريقة احتسابها.",

    openedAtLabel: "بدأت {time}",
    /** Unit B: الدرج ده مستلَم، مش مفتوح من الأول. */
    tookOverLabel: "استلمته من {name} بـ {amount} الساعة {time}",
    openedByLabel: "بدأها {name}",
    unknownStaff: "موظف غير معروف",
    // Column header for the drawer-movements/exceptions/after-close tables —
    // deliberately NOT `unknownStaff`, which is the missing-actor fallback
    // shown below it (DEF-4): a header must name the column, not the one
    // value a row can take.
    staffColumn: "الموظف",
    movementsTitle: "حركات النقد",
    movementsEmpty: "لا توجد حركات نقد في هذه الخزينة حتى الآن.",
    movementKindOut: "صرف من الخزينة",
    // FIX 1: لازم فئة لكل حركة جديدة — دي أصل الميزة. الاختيارات بتتغيّر حسب
    // الاتجاه المختار (توريد/صرف)، والسيرفر برضه بيرفض أي فئة متطابقتش.
    movementCategoryLabel: "فئة الحركة",
    movementCategoryRequired: "اختر فئة للحركة.",
    // وحدة E — الفلوس اللي بتتنقل من إيد لإيد لازم تسمّي الاتنين. «صرف ٥٤٠٠ —
    // ترحيل للخزنة» بتسمّي درج ومبلغ وفئة، وماحدش تاني خالص: الكاش خرج من درج
    // كاشير باسمه ودخل عند مفيش حد. ده شكل أي خلاف بيحصل في مطعم — الكاشير
    // بيقول سلّمتها للمدير، والمدير بيقول ماستلمتش، والبرنامج عنده سطر واحد
    // بيوافق الاتنين. الاسم التاني هو اللي بيخلّي السطر تسليم بالفعل.
    movementCounterpartyLabel: "سُلّمت إلى مَن؟",
    movementCounterpartyPlaceholder: "اختر الشخص",
    movementCounterpartyHint:
      "تتضمن هذه الفئة نقل النقود بين شخصين؛ حدّد الشخص الذي استلمها أو سلّمها.",
    movementCounterpartyRow: "سُلّمت إلى {name}",
    movementCounterpartyOwedBy: "على {name}",
    movementCounterpartyHeldFor: "لصالح {name}",
    categoryExpense: "مصروف (صُرف من المطعم)",
    categoryDropToSafe: "ترحيل إلى الخزنة",
    categoryBankDeposit: "إيداع بنكي",
    categoryOwnerDraw: "سحب صاحب المطعم",
    // وحدة ٢ — الفلوس اللي مش أكل. الخدمة والبقشيش فلوس الفريق بتتسلّم،
    // والدليفري مصروف على المطعم نفسه.
    categoryServiceChargePayout: "توزيع الخدمة على الفريق",
    categoryTipPayout: "توزيع البقشيش على الفريق",
    categoryDeliveryCost: "تكلفة توصيل (أجرة الدراجة النارية)",
    categoryDriverShortage: "عجز طيار",
    categoryDriverOverage: "زيادة طيار",
    categoryTipIn: "بقشيش نقد دخل الخزينة",
    categoryChangeFund: "فكة / رصيد افتتاحي",
    categoryOwnerCapital: "رأس مال من صاحب المطعم",
    categoryCorrection: "تصحيح تسوية",
    // D155 — السلفة فلوس المطعم لسه، عند واحد من الفريق، وهترجع.
    categoryStaffAdvance: "سلفة لأحد أفراد الفريق",
    categoryStaffAdvanceRepaid: "سداد سلفة",
    categoryOther: "أخرى",
    // مش «أخرى»: دي حركة قبل ما الفئات تتضاف أصلاً، ومحدش سُئل عن فئتها.
    categoryUncategorised: "غير مصنّف",
    movementAmountLabel: "المبلغ",
    movementReasonLabel: "السبب",
    // عنوان عمود الوقت في ملف الـCSV. عمود، فاسم كمية مش جملة.
    movementAtColumn: "الوقت",
    movementReasonPlaceholder: "مثلاً: شراء ثلج",
    movementAdd: "تسجيل الحركة",
    movementReasonRequired: "اكتب سبب الحركة.",
    movementAddedToast: "سُجّلت الحركة",
    // D157 — صورة الإيصال اختيارية: لا تحذير عند غيابها، ولا شيء ينتظرها؛
    // تُسجَّل الحركة أولًا، ثم تُرفع الصورة.
    movementPhotoLabel: "صورة الإيصال",
    movementPhotoOptionalHint: "اختياري — إن كان الإيصال معك فصوّره.",
    movementPhotoPick: "اختيار صورة",
    movementPhotoChange: "تغيير الصورة",
    movementPhotoUnpick: "إزالة الصورة",
    movementPhotoAttach: "إرفاق صورة",
    movementPhotoAttachAria: "إرفاق صورة إيصال: {reason}",
    movementPhotoReplace: "استبدال الصورة",
    movementPhotoView: "عرض صورة الإيصال",
    movementPhotoViewAria: "عرض صورة الإيصال: {reason}",
    movementPhotoTitle: "صورة الإيصال",
    movementPhotoAlt: "إيصال الحركة: {reason}",
    movementPhotoUploading: "جارٍ رفع الصورة…",
    movementPhotoUploaded: "رُفعت الصورة.",
    movementPhotoFailed:
      "سُجّلت الحركة، لكن الصورة لم تُرفع. أعد المحاولة، أو أرفقها لاحقًا من السجل.",
    movementPhotoRetry: "إعادة المحاولة",
    movementPhotoDismiss: "إغلاق",
    movementPhotoErrorAlreadyAttached:
      "لهذه الحركة صورة مرفقة بالفعل، ولا يستبدلها إلا صاحب المطعم.",
    movementPhotoErrorNotAllowed:
      "لم تسجّل هذه الحركة، وليست لديك صلاحية إدارة الخزينة.",
    movementPhotoErrorViewNotAllowed: "صورة هذا الإيصال غير متاحة لك.",
    movementPhotoErrorNotFound: "لم يُعثر على هذه الحركة.",
    paidInTotal: "إجمالي الداخل",
    paidOutTotal: "إجمالي الخارج",
    // FIX 1: التقسيم اللي الميزة دي كلها من أجله — «طلع من المطعم» فعلاً
    // (مصروف أو سحب صاحب المحل) مقابل «اتنقل بس لسه فلوس المطعم» (خزنة،
    // بنك، تصحيح، أو حركة من غير فئة).
    paidOutLeftBusinessTotal: "منها ما خرج من المطعم",
    paidOutMovedElsewhereTotal: "ومنها ما لا يزال من نقود المطعم (نُقل فقط)",
    // وحدة ٢: الذراع التالت. الكاش طلع فعلاً، بس أصلاً ماكانش فلوس المطعم —
    // خدمة أو بقشيش بتتسلّم للفريق. مش مصروف على المطعم ومش نقل داخلي.
    paidOutPassedThroughTotal: "ومنها تسليم للفريق (خدمة/بقشيش)",
    exceptionsTitle: "الإلغاءات والخصومات",
    // B117/RT-A — القايمة دي على مستوى المحل، مش على مستوى الدرج، والكلام
    // اللي كان مكتوب هنا كان بيقول العكس.
    //
    // B117/B2 خلّى صفوف الفلوس (المرتجع، اللي مشي من غير دفع، تصحيح طريقة
    // الدفع) كل واحد على درجه. الإلغاء والضيافة مابيحركوش فلوس ومالهمش درج
    // أصلًا — صنف اتشال من على ترابيزة ده شغل الصالة — فسابناهم على مستوى
    // المحل عن قصد. بس النص فضل بيقول «في الدرج ده»، يعني كاشيرين بيقروا نفس
    // الإلغاء وكل واحد فاكره بتاعه، واللي بيجمع الاتنين بيطلعله الرقم مرتين.
    exceptionsIntro:
      "هذه جميع الإلغاءات والضيافة في المطعم خلال مدة هذه الخزينة، وليست الخاصة بهذه الخزينة وحدها. لا تحرّك نقودًا ولا تؤثر في المبلغ المتوقع، ويظهر بجانب كل سطر الموظف الذي نفّذه.",
    exceptionsEmpty: "لا توجد إلغاءات أو خصومات خلال هذه المدة حتى الآن.",
    // R12 (MAJOR 7) — المرتجعات ليها عنوانها هي. كانت بتتحط تحت عنوان
    // «الإلغاءات والخصومات»، وده بالظبط الخلط اللي R12 مانعه: الكومب أكل
    // اتدّى ببلاش، والفاتورة من غير دفع فلوس ماوصلتش، والمرتجع فلوس وصلت
    // ورجعت.
    refundsTitle: "المرتجعات",
    refundsIntro:
      "المرتجع مبلغ حُصّل ثم أُعيد إلى العميل — وهو نوع مستقل، لا إلغاء ولا صنف على الحساب. مرتجع النقد فقط هو الذي ينقص النقد المتوقع، ومرتجع البطاقة ينقص مدفوعات البطاقات المتوقعة.",
    refundsEmpty: "لا توجد مرتجعات في هذه الخزينة.",
    exceptionsTruncated:
      "تُعرض أحدث {count} حركة فقط؛ توجد حركات أقدم خلال هذه المدة لا تظهر هنا. الأرقام أعلاه محسوبة على الخزينة كاملةً، لا على هذه القائمة.",
    // بدائل مشيت ببلاش (D64): مش قرار زي الإلغاء والضيافة — دي ثغرة تسعير
    // الصالة وقعت فيها، عشان كده ليها قايمة لوحدها جنبهم.
    flaggedSwapsTitle: "بدائل صُرفت مجانًا",
    flaggedSwapsEmpty: "لا توجد بدائل صُرفت مجانًا في هذه الخزينة.",
    flaggedSwapsBasisNote:
      "تُنسب هذه البدائل إلى من أضاف الصنف، وإذا لم يُسجّل موظف عليه فتُنسب إلى من فتح الحساب. الرقم بجانب كل بديل هو تكلفة الطعام المصروف، وليس مبلغًا دفعه العميل.",
    exceptionVoid: "إلغاء صنف",
    exceptionComp: "صنف على الحساب",
    exceptionCancel: "إلغاء فاتورة",
    exceptionCorrectSettlement: "تعديل طريقة الدفع",
    exceptionWriteOff: "فاتورة بلا دفع",
    // R12: نوعه لوحده. لا «إلغاء» ولا «من غير دفع» — الكومب أكل اتدّى ببلاش،
    // والفاتورة من غير دفع فلوس أصلاً ماوصلتش، والمرتجع فلوس وصلت ورجعت.
    exceptionRefund: "مرتجع للعميل",
    exceptionRefundAmount: "أُعيد إلى العميل {amount}",
    // DECISION 3 على الشاشة: الفلوس رجعت بطريقة، والفاتورة اتصححت لطريقة
    // تانية بعدها. تنبيه مش خطأ — التصحيح مسموح، وسطر المرتجع بيفضل بالطريقة
    // اللي الفلوس خرجت بيها فعلاً عشان الدرج يفضل صادق مع اللي فيه.
    exceptionRefundDiverged:
      "أُعيدت النقود بطريقة تختلف عن طريقة الدفع المسجّلة حاليًا؛ فقد صُححت الفاتورة بعد المرتجع. هذا تنبيه وليس خطأً، لأن هذا المبلغ خرج بالفعل من الخزينة.",
    exceptionPlateCount: "{count} صنف",
    /* B118 round table — الحالة التالتة. من ٣ لـ ١٠ العدد بياخد الجمع، ومن ١١
       وطالع بياخد المفرد. انظر `plural.ts`. */
    exceptionPlateCountFew: "{count} أصناف",
    // B117/B4 — «2 صنف رجع» and its family on every money screen.
    exceptionPlateCountOne: "صنف واحد",
    exceptionPlateCountTwo: "صنفين",
    // R12 (MAJOR 7) — الطريقة اللي الفلوس خرجت بيها فعلاً، عشان الكاش المتوقع
    // يتحسب بالإيد من الصفحة: تلات مرتجعات بنفس المبلغ من غير طريقة
    // مايتجمعوش على أي رقم.
    exceptionRefundMethod: "أُعيدت عبر {method}",
    // R8 (MAJOR 3) — الشيلة نفسها كلمة واحدة، والسطر تحتها هو اللي بيشرح.
    exceptionRefundLate: "متأخر",
    // D75 — علامتين مشتقّتين جنب الحد اللي في الإعدادات. مش رفض، مجرد تنبيه.
    // شِب بكلمة واحدة (قاعدة التصميم للعربي)؛ الجملة اللي تحت هي اللي بتوضّح.
    exceptionRefundClosedShift: "خزينة مغلقة",
    exceptionRefundClosedShiftNote:
      "أُعيدت هذه النقود بعد إغلاق الخزينة التي حُصّلت فيها واعتماد توقعاتها؛ لذلك لم تعد الحصيلة التي اعتمدها الكاشير مطابقة لما في الخزينة. العملية مسموحة وسُجّلت للمراجعة.",
    exceptionRefundOtherActor: "موظف آخر",
    exceptionRefundOtherActorNote:
      "أعاد النقود موظف غير الذي حصّلها. قد يحدث ذلك بين ورديتين، ويُسجّل في كل مرة.",
    exceptionRefundLateNote:
      "نُفّذ هذا المرتجع بعد المهلة المحددة في الإعدادات. العملية مسموحة، وسُجّلت بوصفها «متأخرة» للمراجعة.",
    // الفاتورة دي من يوم تاني — القرار اتاخد النهاردة والخسارة نزلت على الدرج
    // ده، بس الفاتورة نفسها من {date}.
    exceptionBillDate: "فاتورة يوم {date}",
    // Issued between two shifts, while no drawer was open. Shown so nobody
    // misses it, labelled so nobody reads it as this cashier's.
    exceptionOutsideShift: "خارج الخزينة",
    // The D24 warning — never a block, and split three ways so "still eating"
    // is never mistaken for "the cash is walking the floor".
    outstandingTitle: "نقود لم تصل إلى الخزينة بعد",
    // نتيجة الطاولة المستديرة رقم ٥ — رقم واحد الأول، وتحته الثلاثة اللي
    // بيتكوّن منهم. التلاتة دول مالهمش تقاطع بالبناء: طاولة لسه بتاكل، شيك
    // مطبوع في الخدمة دي، وفاتورة من قبل الخدمة. الكاشير كان بيقرا ٣ أرقام عن
    // نفس الكومة من غير ما حد يقوله علاقتهم ببعض.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    outstandingTotalOne: "فاتورة واحدة لم تصل نقودها إلى الخزينة بعد",
    outstandingTotalTwo: "فاتورتان لم تصل نقودهما إلى الخزينة بعد",
    outstandingTotalFew: "{count} فواتير لم تصل نقودها إلى الخزينة بعد",
    outstandingTotal: "{count} فاتورة لم تصل نقودها إلى الخزينة بعد",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    outstandingOpenTablesOne:
      "طاولة واحدة لا تزال قيد الخدمة؛ وهذا طبيعي وليس مشكلة.",
    outstandingOpenTablesTwo:
      "طاولتان لا تزالان قيد الخدمة؛ وهذا طبيعي وليس مشكلة.",
    outstandingOpenTablesFew:
      "{count} طاولات لا تزال قيد الخدمة؛ وهذا طبيعي وليس مشكلة.",
    outstandingOpenTables:
      "{count} طاولة لا تزال قيد الخدمة؛ وهذا طبيعي وليس مشكلة.",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    outstandingChequesOutOne:
      "حساب واحد مطبوع مع ويتر خارج الخزينة؛ لم يصل هذا النقد إلى الخزينة بعد. حصّله قبل العد.",
    outstandingChequesOutTwo:
      "حسابان مطبوعان مع ويتر خارج الخزينة؛ لم يصل هذا النقد إلى الخزينة بعد. حصّله قبل العد.",
    outstandingChequesOutFew:
      "{count} حسابات مطبوعة مع ويتر خارج الخزينة؛ لم يصل هذا النقد إلى الخزينة بعد. حصّله قبل العد.",
    outstandingChequesOut:
      "{count} حساب مطبوع مع ويتر خارج الخزينة؛ لم يصل هذا النقد إلى الخزينة بعد. حصّله قبل العد.",
    // مش كلها «مفتوحة»: العدّة دي بتشمل الفواتير اللي حسابها اتقفل واتطبع
    // وبرّه مع الزبون كمان — نفس حالة «الحساب برّه» على شاشة الطاولات. لما
    // كانت بتتقال «مفتوحة» بس، الكاشير كان بيدوّر على طاولات لسه بتاكل
    // ويلاقي حسابات مستنية تحصيل.
    // وحدة E — الشيكات اللي بقالها كتير برّه. العدّة اللي فوق عادية الساعة
    // ١٠ بالليل؛ اللي بقاله ساعة ونص برّه بقى مش طاولة لسه بتاكل، بقى فلوس
    // محدش فسّرها. والمبلغ هو اللي بيخلّيها سؤال: عدد لوحده مابيقولش لصاحب
    // المحل الأرضية شايلة ٩٠ ولا ٩٠٠٠.
    // «منها» — عشان الرقم ده جزء من اللي فوق، مش رقم رابع جنبه.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    outstandingAgedOne:
      "منها حساب واحد ظل خارج الخزينة مدة طويلة بقيمة {amount}؛ أصبحت هذه نقودًا غير مفسّرة، فتحقّق منها.",
    outstandingAgedTwo:
      "منها حسابان ظلا خارج الخزينة مدة طويلة بقيمة {amount}؛ أصبحت هذه نقودًا غير مفسّرة، فتحقّق منها.",
    outstandingAgedFew:
      "منها {count} حسابات ظلت خارج الخزينة مدة طويلة بقيمة {amount}؛ أصبحت هذه نقودًا غير مفسّرة، فتحقّق منها.",
    outstandingAged:
      "منها {count} حساب ظل خارج الخزينة مدة طويلة بقيمة {amount}؛ أصبحت هذه نقودًا غير مفسّرة، فتحقّق منها.",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    outstandingCarriedInOne:
      "فاتورة واحدة من خزينة سابقة، إما مفتوحة أو حسابها خارج الخزينة، وقد استلمتها بهذه الحالة.",
    outstandingCarriedInTwo:
      "فاتورتان من خزينة سابقة، إما مفتوحتان أو حساباهما خارج الخزينة، وقد استلمتهما بهذه الحالة.",
    outstandingCarriedInFew:
      "{count} فواتير من خزينة سابقة، إما مفتوحة أو حساباتها خارج الخزينة، وقد استلمتها بهذه الحالة.",
    outstandingCarriedIn:
      "{count} فاتورة من خزينة سابقة، إما مفتوحة أو حسابها خارج الخزينة، وقد استلمتها بهذه الحالة.",
    outstandingTrace: "عرض الفواتير",
    outstandingTraceNoAccess:
      "توجد هذه الفواتير في سجل الفواتير، ويتطلب فتحه صلاحية الكاشير.",
    // End-shift flow.
    endShiftButton: "إغلاق الخزينة",
    endShiftTitle: "إغلاق الخزينة",
    endShiftBody:
      "أدخل المبلغ الذي عددته فعليًا في الخزينة. سيظهر المبلغ المتوقع والفرق بعد الإغلاق، لا قبله.",
    // قرار 2026-10-03: آخر درج مفتوح ما يتقفلش وفيه فواتير لسه مفتوحة، لأن
    // التحصيل بعدها مالوش درج يستقبله.
    lastDrawerOpenInvoicesOne:
      "هذه آخر خزينة مفتوحة، وما زالت هناك فاتورة مفتوحة. حصّلها أو ألغِها أولًا، ثم أغلق الخزينة؛ فبعد إغلاقها لن يجد التحصيل خزينة تستقبله.",
    lastDrawerOpenInvoicesTwo:
      "هذه آخر خزينة مفتوحة، وما زالت هناك فاتورتان مفتوحتان. حصّلهما أو ألغِهما أولًا، ثم أغلق الخزينة؛ فبعد إغلاقها لن يجد التحصيل خزينة تستقبله.",
    lastDrawerOpenInvoicesFew:
      "هذه آخر خزينة مفتوحة، وما زالت هناك {count} فواتير مفتوحة. حصّلها أو ألغِها أولًا، ثم أغلق الخزينة؛ فبعد إغلاقها لن يجد التحصيل خزينة تستقبله.",
    lastDrawerOpenInvoices:
      "هذه آخر خزينة مفتوحة، وما زالت هناك {count} فاتورة مفتوحة. حصّلها أو ألغِها أولًا، ثم أغلق الخزينة؛ فبعد إغلاقها لن يجد التحصيل خزينة تستقبله.",
    countedCashLabel: "النقد الذي في الخزينة",
    varianceLabel: "الفرق",
    varianceShortHint: "الخزينة ناقصة عن المتوقع.",
    varianceOverHint: "الخزينة زيادة عن المتوقع.",
    varianceMatchHint: "الخزينة متطابقة.",
    confirmEndShift: "تأكيد إغلاق الخزينة",
    closedBody: "سُجّل الفرق، ويمكنك العثور على هذه الخزينة في حركة الخزينة.",
    ownNotesCounted: "المبلغ الذي عددته: {counted}",
    startNewShift: "فتح خزينة جديدة",
    closedToast: "أُغلقت الخزينة",
    printDocTitle: "ملخص الخزينة (X)",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    printExceptionCountOne: "استثناء واحد (إلغاء أو خصم)",
    printExceptionCountTwo: "استثناءان (إلغاء أو خصم)",
    printExceptionCountFew: "{count} استثناءات (إلغاء أو خصم)",
    printExceptionCount: "{count} استثناء (إلغاء أو خصم)",
    // نفس السطر لما القايمة تبقى مقصوصة: العدد ده اتعدّ من القايمة نفسها،
    // فهو حد أدنى مش عدّ كامل — والورقة اللي بتتوقّع مايصحّش تقول رقم أقل من
    // الحقيقة من غير ما تقول إنه أقل.
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    printExceptionCountFloorOne: "استثناء واحد (إلغاء أو خصم) على الأقل",
    printExceptionCountFloorTwo: "استثناءان (إلغاء أو خصم) على الأقل",
    printExceptionCountFloorFew: "{count} استثناءات (إلغاء أو خصم) على الأقل",
    printExceptionCountFloor: "{count} استثناء (إلغاء أو خصم) على الأقل",
    // BLOCKER 2 — الورقة اللي بتتوقّع مايصحّش تقول رقم أقل من الحقيقة: العدد
    // اللي فوق محسوب على الوردية كلها، والقايمة نفسها مقصوصة عند حد السيرفر،
    // والسطر ده بيقول كده على الورق زي ما الشاشة بتقوله.
    printExceptionsTruncated:
      "تقتصر القائمة على أحدث {count} حركة؛ توجد حركات أقدم غير مطبوعة هنا.",
    // R12 — المرتجعات على الورق برضه ليها عنوانها.
    printRefundsTitle: "المرتجعات",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    printRefundCountOne: "صنف واحد أُعيد",
    printRefundCountTwo: "صنفان أُعيدا",
    printRefundCountFew: "{count} أصناف أُعيدت",
    printRefundCount: "{count} صنف أُعيد",
  },
  /**
   * The owner's review of past shifts (D27's exceptions, D26's frozen
   * snapshots). `REPORT_VIEW`, not `SHIFT_MANAGE` — variance is margin-grade
   * sensitive, the same reasoning `reports` already carries.
   */
  shiftHistory: {
    title: "حركة الخزينة",
    description: "الخزائن المغلقة، والفرق في كل خزينة، وتفاصيلها الكاملة.",
    noAccessTitle: "لا تملك صلاحية عرض حركة الخزينة",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية عرض التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    loading: "جارٍ تحميل حركة الخزينة...",
    emptyTitle: "لا توجد ورديات مغلقة حتى الآن",
    emptyBody: "ستظهر أول خزينة تُغلق هنا بكل تفاصيلها.",
    tableOpened: "الفتح",
    tableClosed: "الإغلاق",
    tableOpenedBy: "فتحها",
    tableClosedBy: "أغلقها",
    tableExpected: "المتوقع",
    tableCounted: "المعدود",
    tableVariance: "الفرق",
    unknownStaff: "غير معروف",
    paginationLabel: "صفحات حركة الخزينة",
    prevPage: "السابق",
    nextPage: "التالي",
    pageOf: "صفحة {page} من {pages}",
    // Unit D — العدّة الأولى، على الشاشة اللي المالك بيقرأ منها.
    // B117 round table 2 — a manager's own count, the choice he makes when it
    // differs, and the words beside a variance.
    managerCountTitle: "عدّ الخزينة بنفسك",
    managerCountLabel: "النقد الذي وجدته في الخزينة",
    managerCountHint:
      "اكتب الرقم الذي عددته بنفسك. سنعرض بعد ذلك عدّ الكاشير والمبلغ المتوقع.",
    managerCountRequired: "أدخل المبلغ الذي عددته.",
    managerCountInvalid: "رقم غير صالح — منزلتين عشريتين بحد أقصى.",
    managerCountContinue: "متابعة",
    managerCountCashierLine: "نتيجة عدّ {name}",
    managerCountCashierUnnamed: "الكاشير",
    managerCountYours: "عدّك أنت",
    managerCountExpected: "المتوقع",
    managerCountDiffers:
      "يوجد فرق قدره {amount} بين العدّتين؛ حدّد المبلغ الذي سيُعتمد.",
    /* B118/QA4 — درج اتقفل من غير عدّة مسجّلة أصلًا. مافيش «العدّتين»،
       فمافيش فرق: عدّتك هي الرقم الوحيد اللي الدرج ده عنده. */
    managerCountOnlyCount:
      "لا توجد نتيجة عد سابقة مسجّلة لهذه الخزينة، لذلك ستكون نتيجة عدّك هي المبلغ الوحيد المعتمد.",
    managerCountChoiceHint:
      "ستظل نتيجتا العد مسجّلتين باسمي صاحبيهما مهما كان اختيارك. أنت تحدد هنا المبلغ الذي سيُعتمد للخزينة.",
    managerCountUseMine: "اعتماد عدّي — {amount}",
    managerCountUseTheirs: "استخدام عدّ الكاشير — {amount}",
    managerCountAction: "عدّ الخزينة",
    managerCountedNote:
      "{name} عدّ الخزينة بنفسه: {amount} (المسجّل قبلها {cashier}).",
    managerCountSupersededNote: "حُسم الفرق باعتماد عدّ المدير.",
    managerCountDeferredNote:
      "راجع المدير الفرق وأبقى عدّ الكاشير معتمدًا.",
    recountedByNote: "نفّذ {name} العد الثاني.",
    notesTitle: "ملاحظات على الخزينة",
    notesEmpty: "لا توجد ملاحظات على هذه الخزينة.",
    noteLabel: "اكتب ملاحظة",
    notePlaceholder: "مثال: دُفع ٥٠ جنيهًا لمورد الثلج ولم يُسجّل المبلغ.",
    noteAdd: "إضافة الملاحظة",
    noteAppendOnly:
      "تُضاف الملاحظات ولا يمكن حذفها أو تعديلها؛ ويُسجّل التصحيح في ملاحظة جديدة.",
    noteEmpty: "أدخل الملاحظة أولًا.",
    managerCountedToast: "سُجّل عدّك على الخزينة.",
    noteAddedToast: "أُضيفت الملاحظة.",
    detailErrorBody: "تعذّر تحميل تفاصيل هذه الخزينة.",
    // S14/D49 — كل شاشة فلوس بتقول أرقامها جاية منين في سطر واحد. الوردية
    // المقفولة بعد S14 أرقامها متثبّتة ساعة العدّ، فأي تصحيح دفع بعد كده
    // بيظهر في وردية النهاردة مش هنا. الورديات القديمة مالهاش أرقام متثبّتة،
    // فبتتحسب دلوقتي — والسطر ده بيقول كده بدل ما المستخدم يفترض.
    basisSnapshot:
      "ثُبّتت أرقام هذه الخزينة عند عدّها. ويظهر أي تصحيح لاحق لطريقة الدفع في خزينة اليوم، لا هنا.",
    basisLive:
      "أُغلقت هذه الخزينة قبل بدء تثبيت الأرقام، لذلك تُحسب الأرقام المعروضة الآن من الفواتير.",

    /*
     * ── الشاشة دي بقت صفحة تقرير (UX-round D41 / ruling F14 / Slice 9) ───────────────
     *
     * كارت واحد كبير فيه الجدول، وفوقه تلات تايلات، وباب واحد بيفتح ورقة
     * الأساس، وملف CSV. اللافتات دي هي اللي الشكل ده محتاجها: عنوان الكارت،
     * لافتة الباب، وزرار التنزيل — متكرّرين حرف بحرف من عيلة التقارير عشان
     * صاحب المطعم يتعلّم باب واحد مش خمسة.
     */
    cardTitle: "الخزائن المغلقة",
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذا التقرير",
    exportCsv: "تنزيل CSV",
    /* التوكن الوحيد اللي فاضل على الشاشة: ليه أرقام الصفحة دي مش زي أرقام
       تقفيلة اليوم. الجملة الكاملة ورا الباب. */
    basisChipPage: "هذه الصفحة",

    /* الرقم فوق مجموع الأدراج اللي اتعدّت بس — والجملة دي بتقول كده لما يكون
       فيه درج محدش عدّه. قاعدة «الجملة اللي بتتكلم عن جزء بتقول إنه جزء». */
    statVarianceFooterAllCounted: "عُدّت كل الخزائن في هذه الصفحة",
    statVarianceFooterSomeUncounted:
      "لم تُعدّ بعض الخزائن، ولذلك لا تدخل في هذا الرقم",
    statUncountedFooter: "تظهر في الجدول بشرطة بدلًا من صفر",

    // مصطلحات ورقة الأساس اللي مافيش لافتة على الشاشة تنفع اسم ليها.
    basisTermTiming: "وقت تسجيل هذه الأرقام",
    basisTermScope: "نطاق الأرقام",
    basisScopeNote:
      "تُحسب البطاقات أعلاه من الخزائن الظاهرة في هذه الصفحة فقط، لا من السجل كاملًا؛ لذلك لا يلزم أن تطابق إغلاق يوم.",
    basisCountedNote:
      "«المعدود» هو المبلغ المعتمد للخزينة. وإذا عدّ المدير الخزينة بنفسه وقرر اعتماد نتيجته، أصبحت هي المبلغ المعتمد، مع بقاء نتيجتي العد مسجّلتين باسمي صاحبيهما.",
    basisVarianceNote:
      "الفرق = المعدود ناقص المتوقع. تظهر الخزينة التي لم تُعد بشرطة ولا تدخل في أي مجموع.",

    /* علامة العدّ التانية / عدّة المدير في الجدول (UX-round D44 / ruling F23).
       بتتكتب من الفلاج المتسجّل `managerCountSupersedes`، مش من مقارنة رقمين —
       المدير اللي عدّ نفس الرقم واللي عدّ رقم تاني وساب عدّة الكاشير الاتنين
       قرّروا حاجة، والفلاج لوحده هو اللي بيفرّق بينهم. والرقمين الاتنين
       بيركبوا السطر: علامة لوحدها بتقول إن رقم اتغيّر من غير ما تقول من إيه. */
    markManagerStands: "عدّ المدير · النتيجة المسجّلة سابقًا {amount}",
    /* درج اتقفل قبل ما العدّ الأعمى ينزل: مافيش عدّة كاشير أصلًا، فالعلامة
       مالهاش رقم تاني تقوله — ومابنحطّش صفر مكانه. */
    markManagerOnly: "عدّ المدير · لا توجد نتيجة عدّ مسجّلة قبله",
    markManagerDeferred:
      "عدّ الكاشير · عدّ المدير {amount} وأبقى النتيجة الأولى",
    markRecounted: "أُعيد عدّه · نتيجة العد الأولى {amount}",

    // PAPER (2026-10) — سجل الأدراج بالشكل الجديد. كل رقم هنا من اللي
    // السيرفر بعته: المتوقع والعدّات والفرق اللي الدرج واقف عليه.
    paperCountedSub: "عُدّت منها {count} خزينة",
    paperCountedSubOne: "عُدّت منها خزينة واحدة",
    paperCountedSubTwo: "عُدّت منها خزينتان",
    paperCountedSubFew: "عُدّت منها {count} خزائن",
    paperOutsideBand: "{count} خزينة بفرق أكبر من {amount}",
    paperOutsideBandOne: "خزينة واحدة بفرق أكبر من {amount}",
    paperOutsideBandTwo: "خزينتان بفرق أكبر من {amount}",
    paperOutsideBandFew: "{count} خزائن بفرق أكبر من {amount}",
    paperAllWithinBand: "كل الخزائن المعدودة في حدود {amount}",
    paperBandNote: "الفرق في حدود ±{amount} يُعتبر متوازنًا.",
    paperBandExact:
      "لا تُعتبر الخزينة متوازنة إلا إذا طابق العدّ المتوقع تمامًا.",
    paperChecksLabel: "أُعيد عدّها أو راجعها مدير",
    paperChecksSub: "العدّ الأعمى الأول يبقى مسجّلًا دائمًا",
    paperAllBoxes: "كل الخزائن",
    paperColSpan: "فتح ← إغلاق",
    paperColChecks: "المراجعات",
    paperDrawerName: "{label} · {name}",
    paperDrawerMeta: "{date} · رصيد افتتاحي {amount}",
    paperSpan: "{opened} ← {closed}",
    paperDrawerSub: "{date} · {opened} ← {closed}",
    paperHandoverFrom: "استلام من {name}",
    paperFloatCountedIn: "رصيد افتتاحي معدود",
    paperNotCounted: "لم يُعدّ",
    paperNotCountedNote: "أُغلق دون عدّ",
    paperTagRecounted: "أُعيد العدّ",
    paperTagManager: "راجعه مدير",
    paperTagNotes: "ملاحظات",
    paperTagCustody: "عهدة",
    paperTabsAria: "تفاصيل الخزينة",
    paperTabSummary: "الملخص",
    paperTabHistory: "السجل",
    paperTabExceptions: "الاستثناءات",
    paperOtherTakings: "البطاقات وباقي التحصيل",
    paperCountsTitle: "العدّ",
    paperCountFirst: "العدّ الأعمى بواسطة {name}",
    paperCountFirstNote: "مُثبَّت قبل رؤية المبلغ المتوقع",
    paperCountFirstInline: "مُثبَّت قبل رؤية المبلغ المتوقع",
    paperCountPlain: "العدّ بواسطة {name}",
    paperCountRecount: "إعادة العدّ بواسطة {name}",
    paperCountRecountUnnamed: "إعادة العد",
    paperCountManager: "عدّ المدير بواسطة {name}",
    paperCountAt: "الساعة {time}",
    paperManagerCountHint:
      "يُضاف عدّك بجانب عدّ الكاشير، وتبقى أرقامه مسجّلة كما هي.",
    paperHistOpened: "فتحها {name}",
    paperHistClosed: "أغلقها {name}",
    paperHistNote: "ملاحظة من {name}",
    paperHistExpected: "المتوقع {amount}",
    // PAPER (founder, 2026-10-02): the redesign's own words. Only under PAPER.
    paperSubtitle: "كل خزينة: ما يجب أن يكون فيها، وما عُدّ فعلًا.",
    paperStatDrawers: "الخزائن",
    paperStatVariance: "إجمالي الفرق",
    paperStatUncounted: "لم تُعدّ بعد",
    paperCardTitle: "الخزائن",
    paperColVariance: "الفرق",
    paperBalanced: "متوازن",
    paperTagHandover: "تسليم",
    // Who closed the drawer, and the day when it is not the opening day.
    paperClosedBy: "أغلقه {name}",
    paperClosedOnBy: "أغلقه {name} يوم {date}",
  },
  /**
   * The Z (day-close) report (D38–D41) — a pure READ over one business day.
   * Three blocks: A (sales) always renders; B (drawers) and C (the bridge)
   * render together, only when `shiftTrackingEnabled`.
   */
  dayClose: {
    title: "تقرير نهاية اليوم",
    description:
      "قراءة ليوم عمل واحد: مبيعاته وخزائنه والتسوية التي تربط بينهما.",
    noAccessTitle: "لا تملك صلاحية عرض تقرير نهاية اليوم",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية عرض التقارير. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    loading: "جارٍ تجهيز تقرير نهاية اليوم...",
    errorBody: "تعذّر تحميل تقرير إغلاق هذا اليوم.",
    // The server's own typed 400 for a day that has not started yet
    // (`RESTAURANT_DAY_CLOSE_FUTURE_DATE`) — the picker already blocks future
    // days, so this is a defensive sentence, not the common path.
    futureDateInvalid: "لم يأت هذا التاريخ بعد. اختر يوم عمل سابقًا أو اليوم.",
    dateApply: "تطبيق",
    // Resets the picker to the server's own default — the CURRENT business
    // day — rather than to nothing: there is no "everything" reading for a
    // single-day report the way there is for a date range.
    dateClear: "اليوم",

    /*
     * ── الشاشة دي بقت صفحة تقرير (UX-round D41 / Slice 5) ────────────────────────────
     *
     * تلات كروت كبار بس، وصف تايلات على رأس الصفحة أول واحدة فيهم الفرق، وباب
     * واحد لكل كارت بيفتح على نفس الورقة، وملف CSV لليوم. الكلمات اللي تحت دي
     * هي اللي الشكل ده محتاجها ومكانتش موجودة: عناوين الكروت الكبار، ولافتة
     * الباب، وزرار التنزيل.
     *
     * `basisTrigger` و`basisTitle` و`jumpTo` متكرّرين حرف بحرف من عيلة التقارير
     * (`reports`، `reportDayparts`، `reportItems`، `reportStaff`): المفروض
     * صاحب المطعم يتعلّم باب واحد مش أربعة.
     */
    cardDrawersTitle: "الخزائن والتسوية",
    cardAuditTitle: "استثناءات اليوم وتعديلاته",
    jumpTo: "في هذه الصفحة",
    basisTrigger: "كيف تم الاحتساب؟",
    basisTitle: "كيفية احتساب هذا التقرير",
    exportCsv: "تنزيل CSV",

    /*
     * صف التايلات — أول واحدة الفرق (F14-i).
     *
     * الفرق ماينفعش يقعد ورا الباب ولا جوه كارت لازم تنزل ليه: ده الرقم اللي
     * اليوم بيتحاسب عليه. وتحت كل تايل حقيقة واحدة بس — مش جملة أساس.
     */
    statVarianceFooterAllCounted: "عُدّت كل الخزائن",
    statVarianceFooterSomeUncounted: "توجد خزينة لم تُعدّ",
    statVarianceFooterNoDrawers: "لا توجد خزائن في هذا اليوم",
    statTakingsLabel: "صافي المتحصّل",

    // مصطلحات الباب اللي مافيش لافتة على الشاشة تنفع اسم ليها.
    basisTermTiming: "وقت تسجيل هذه الأرقام",
    basisTermExpected: "المتوقع في الخزينة",

    // عناوين ملف الـCSV — صف واحد ليوم الشغل، وبعده كتلة الأدراج وكتلة الجسر.
    csvDayTotals: "إجماليات اليوم",
    csvDrawers: "الخزائن",
    csvBillDiscountTotal: "خصم على الفاتورة (صافي)",
    csvBillDiscountCount: "عدد فواتير الخصم",

    // Block A — the day's sales.
    salesTitle: "مبيعات اليوم",
    grossLabel: "إجمالي المبيعات",
    ofWhichServiceCharge: "منها رسم الخدمة",
    ofWhichVat: "منها ضريبة القيمة المضافة",
    // D93 — منها إيه جاي من ضريبة رسوم التوصيل، ورسوم التوصيل نفسها.
    //
    // سطر «منها ضريبة» فوق كان ضريبة الأكل لوحدها، لأن ضريبة رسم التوصيل ليها
    // عمود منفصل على الفاتورة (الرسم لازم يقعد بره `subtotal` وإلا حساب
    // المرتجع بيرفض). يعني الرقم اللي المحاسب بياخده من الشاشة دي للإقرار كان
    // ناقص ضريبة كل توصيلة. بقى كامل، والسطر ده بيقول الزيادة جاية منين: رقم
    // ضريبي بيتغير من غير ما يقول ليه أسوأ من رقم غلط.
    //
    // ورسم التوصيل نفسه بينزل من «صافي مبيعات الأكل» تحت لنفس سبب رسم الخدمة:
    // دا بيروح للطيار ودا بيروح للصالة، ومفيش واحد فيهم فلوس المطبخ.
    ofWhichDeliveryFeeVat: "منها ضريبة رسوم التوصيل",
    // D44 — الجزء من المرتجع اللي ماخرجش لا من الدرج ولا من ماكينة الفيزا.
    // مكتوب عشان «صافي اللي وصل الدرج» تحت ما يبانش ناقص من غير سبب على الورقة.
    //
    // الجملة بتقول اللي الحسبة نفسها بتعرفه: «لا كاش ولا فيزا». الحسبة هي
    // refundTotal ناقص الكاش ناقص الفيزا، يعني أي طريقة تانية بتقع هنا — دي
    // التحويلات اليوم، ولو اتضافت طريقة جديدة للمرتجع بكرة هتقع هنا كمان
    // والجملة تفضل صح. عشان كده التحويل مذكور كمثال، مش كتعريف.
    ofWhichOffDrawer: "منها ما أُعيد خارج الخزينة والبطاقة (تحويل)",
    // Made visually the settled figure on the screen — this is the house's
    // actual food money, after the government's two cuts.
    netLabel: "صافي مبيعات الطعام",
    // D93 — الجملة بقت بتقول التلاتة اللي بتتشال فعلاً. كانت بتقول رسم الخدمة
    // والضريبة بس، وبعد ما رسم التوصيل بقى بينزل كمان بقيت بتوصف طرح مش هو دا،
    // والسطور فوقها بتورّي العكس.
    netHint:
      "يمثل هذا الرقم قيمة الطعام فقط بعد خصم رسم الخدمة والضريبة ورسوم التوصيل.",
    bucketsTitle: "طريقة التحصيل",
    // The SIX buckets partition `grossLabel` EXACTLY — same "the block must
    // sum" rule the till totals block and the drawer follow.
    bucketCash: "نقد",
    bucketCard: "بطاقة",
    bucketInstapay: "إنستاباي",
    // F2 — البيع اللي جه من تطبيق توصيل: فلوس التطبيق مدين بيها للمحل، مش في
    // الدرج ولا على الماكينة.
    bucketAggregator: "على التطبيقات",
    // T3 — المجموعة مذكورة في العنوان: فواتير جلبتها منصة، بلا المشطوبة.
    commissionLabel: "عمولة التطبيقات (بلا الفواتير المشطوبة)",
    // T15 — محسوبة بما سُجّل على كل فاتورة؛ كشف المنصة هو المرجع.
    commissionHint:
      "حققت التطبيقات {orders} هذه الليلة واقتطعت منها {amount}. لا تُخصم العمولة من الإجمالي أعلاه، لأنه يمثل ما دفعه العميل. محسوبة بالنسبة والأساس المسجّلين على كل فاتورة؛ كشف المنصة هو المرجع.",
    bucketWriteOff: "بلا دفع",
    bucketUnsettled: "لم يُحصّل بعد",
    bucketsTotalLabel: "= الإجمالي",
    invoiceCountLabel: "عدد الفواتير المحسوبة",
    sequenceLabel: "أرقام الفواتير",
    /* B118/QA3 — «#» حرف محايد الاتجاه، فـ«#1» في فقرة عربي بتتقلب لـ«1#»،
       والرقم كان بيطلع مقلوب على الشاشة وعلى الورق. الـ«#» اتنقلت جوه
       المتغيّر عشان تتعزل LTR مع رقمها — نفس الحيلة اللي على الرقم الضريبي
       في ورقة الـZ. */
    sequenceRangeLabel: "من رقم {first} إلى رقم {last}",
    sequenceRangeNone: "لا توجد فواتير في هذا اليوم",
    // The legibility note D39 asks for: `invoiceCount` counts BILLED
    // invoices while the range spans EVERY number the day used — a cancelled
    // or still-open ticket took a number too. Explaining the gap, not
    // narrowing the range, is the audit signal.
    sequenceGapHint:
      "قد يقل عدد الفواتير المحسوبة عن مدى الأرقام إذا أُلغيت فاتورة أو بقيت مفتوحة؛ إذ تأخذ كل فاتورة رقمًا عند فتحها ولو أُلغيت لاحقًا. ظهور هذا الفرق مقصود وليس خطأً في العد.",
    givenAwayTitle: "ما صُرف بلا مقابل",
    discountTotalLabel: "الخصم",
    // D146 — its own line beside the tier's, never inside it.
    billDiscountLabel: "خصم على الفاتورة ({count})",
    compedTotalLabel: "على الحساب",
    voidedTotalLabel: "الملغى",
    // Named for what it is — stock adjusted, not food comped: a تسوية مخزون
    // (waste/spoilage/correction) is a cost that never touched an invoice.
    stockAdjustmentCostLabel: "تسوية المخزون",
    givenAwayNetTag: "صافي الطعام",
    givenAwayBasisNote:
      "يشمل الرقم الأكبر رسم الخدمة والضريبة، وهو المبلغ الذي لم يصل إلى الخزينة. أما الرقم الأصغر تحته فهو صافي قيمة الطعام بعد خصمهما. يجيب الرقمان عن سؤالين مختلفين، وكل منهما صحيح في سياقه.",

    // R12 — المرتجعات: نوع لوحده، جنب التقسيمة مش جواها.
    refundsTitle: "المرتجعات",
    /* UX-round D37/D38 · ruling F3 — SPLIT, not moved whole, and gated on
       `refundCount > 0`.
       
       Two of the three clauses qualify the «= الإجمالي» row directly: the
       partition clause is what tells a reader checking the bucket column by pen
       WHY it still sums to gross, and the cut-off clause is what tells them why
       a refund on yesterday's bill is in today's figure. `report-pages.md`:
       "a qualifier lives in the row of the figure it qualifies… a caveat a
       card-height from its number qualifies nothing." So they ride with the
       buckets, on the days refunds exist.
       
       The third clause — what a refund IS, as against a comp or a write-off —
       is a taxonomy that is true on every day including the quiet ones, which
       is exactly what the method block is for. It is `refundsBasisNote` below,
       and it reaches the door, the CSV and the printed block through
       `dayCloseBasis.ts`. */
    refundsIntro:
      "تظهر المرتجعات بجانب تقسيم التحصيل ولا تدخل فيه، لذلك يظل مجموع التقسيم مساويًا لإجمالي المبيعات. وتُحتسب في اليوم الذي خرجت فيه النقود، ولو كانت الفاتورة من يوم سابق.",
    refundsBasisNote:
      "المرتجع مبلغ حُصّل ثم أُعيد إلى العميل — وهو نوع مستقل، وليس «على الحساب» ولا «بلا دفع».",
    refundTotalLabel: "أُعيد إلى العميل",
    refundCountLabel: "{count} صنف أُعيد",
    /* B118 round table — الحالة التالتة (١ / ٢ / ٣–١٠ / ١١+). انظر `plural.ts`. */
    refundCountLabelOne: "صنف واحد أُعيد",
    refundCountLabelTwo: "صنفان أُعيدا",
    refundCountLabelFew: "{count} أصناف أُعيدت",
    refundCountFeeOnly: "لا أصناف — رسم توصيل فقط",
    refundsNone: "لا توجد مرتجعات في هذا اليوم.",
    /* G3/T20 — رسم توصيل أُعيد للزبون وضريبته: جزء من «أُعيد إلى الزبون» أعلاه، مذكور بجانبه. */
    ofWhichDeliveryFeeRefunded: "منها رسم توصيل أُعيد",
    ofWhichDeliveryFeeVatRefunded: "منها ضريبة رسم التوصيل المُعاد",
    netTakingsLabel: "= صافي المتحصّل (نقد + بطاقة − المرتجع)",
    /* UX-round D37/D38 · ruling F3 — the same split, and gated on
       `netTakings !== gross` (the divergence gate). On the evening that opened
       this round the figure this qualifies EQUALLED gross to the piastre, so
       236 characters explained a difference that was not there.
       
       What survives is the distinction itself — the one thing a reader can act
       on wrongly in the next five seconds by reading this figure as "sales
       minus refunds". The three exceptions that make the distinction true are
       `netTakingsBasisNote`, in the method block. */
    netTakingsHint:
      "هذا ما تبقى من النقود التي وصلت إلى الخزينة بالفعل، وليس إجمالي المبيعات ناقص المرتجع.",
    netTakingsBasisNote:
      "لا توجد نقود واردة أصلًا من الفاتورة المغلقة بلا دفع أو غير المحصّلة، كما أن المرتجع المحوّل يخرج من البنك لا من الخزينة.",
    /* B118/QA4 — ١ / ٢ / ٣–١٠ / ١١+. انظر `plural.ts`. */
    /* UX-round D38 · ruling A3 — EVERY COUNTED LABEL CARRIES THE COUNT AND NOTHING ELSE.
       
       All four arms carried a byte-identical 145-character tail, so the sheet
       printed the same explanation four times over in the catalogue and once
       in whichever arm the night selected. The tail is now ONE sibling,
       `refundDivergedNote`, rendered directly beside the count — A3's condition
       is that it lands "on the same sheet of paper as the count it explains",
       and it keeps both of its ideas: flag-not-fault, and the drawer stays
       honest about what is in it.
       
       `shift.exceptionRefundDiverged` is a DIFFERENT key and is not touched:
       `shiftExceptions.spec.ts:154-165` asserts its English contains "not an
       error" and "drawer". */
    refundDivergedLabelOne:
      "مرتجع واحد أُعيد بطريقة تختلف عن المسجّلة حاليًا في الفاتورة",
    refundDivergedLabelTwo:
      "مرتجعان أُعيدا بطريقة تختلف عن المسجّلة حاليًا في الفاتورة",
    refundDivergedLabelFew:
      "{count} مرتجعات أُعيدت بطريقة تختلف عن المسجّلة حاليًا في الفاتورة",
    refundDivergedLabel:
      "{count} مرتجع أُعيد بطريقة تختلف عن المسجّلة حاليًا في الفاتورة",
    refundDivergedNote:
      "يبقى سطر المرتجع على الطريقة التي خرجت بها النقود فعليًا، حتى تطابق الخزينة ما تحتويه. هذا تنبيه، وليس خطأ.",
    // B47 — قرار المالك ١٠/٩/٢٠٢٦: الـ Z يسجّل اللي حصل فعلاً، الداخل والخارج
    // الاتنين في الصافي.
    writtenOffInflowLabel: "+ مقبوض على فواتير أُزيلت لاحقًا",
    writtenOffInflowHint:
      "هذه فاتورة دُفعت ثم سُجّلت «بلا دفع». دخلت نقودها إلى الخزينة فعلًا، ولذلك تُحتسب هنا. وإذا أُعيدت إلى العميل، يخصمها سطر المرتجع أعلاه فتظهر الفاتورة بصفر في صافي المتحصّل. وإذا لم تُعد، تظل النقود في الخزينة ويعرضها هذا السطر.",
    netTakingsLabelWithWriteOff:
      "= صافي المتحصّل (نقد + بطاقة + المقبوض على فواتير أُزيلت − المرتجع)",

    // Block B — the day's drawers.
    drawersTitle: "خزينة اليوم",
    // D149 — تسليم الطيارين في قسم الدرج: فواتير التوصيل بتاعة طيارين المحل
    // لليوم، بالاسم اللي اتكتب عند التسليم. للقراءة بس، ومفيش رصيد بيترحّل.
    ridersTitle: "تسوية حسابات الطيارين",
    ridersIntro:
      "فواتير توصيل اليوم التي خرجت مع طياري المطعم، مجمّعة بالاسم المكتوب عند التسليم (أحمد واحمد اسم واحد). للاطلاع فقط: لا يُرحَّل رصيد من يوم إلى آخر، ومصروف الطيار ضمن حركات الخزينة.",
    ridersUnnamed: "بدون اسم",
    ridersNameColumn: "الاسم",
    ridersBillsColumn: "الفواتير",
    ridersCashColumn: "نقد",
    ridersInstapayColumn: "إنستاباي",
    ridersOtherColumn: "طرق أخرى",
    ridersWriteOffColumn: "بلا دفع",
    ridersStillOutColumn: "ما زالت بالخارج",
    ridersDifferenceColumn: "فرق التسوية",
    ridersCountAmount: "{count} · {amount}",
    // S14/D49 — سطر الأساس للـ Z: القسم (أ) بيتحسب دلوقتي من فواتير اليوم،
    // والقسم (ب) بياخد أرقام كل وردية زي ما اتثبّتت ساعة العدّ. عشان كده
    // تصحيح طريقة دفع بعد القفل مابيحرّكش الدرج القديم، وبيبان في
    // "بعد القفل" تحت.
    basisNote:
      "تُحسب أرقام المبيعات الآن من فواتير اليوم، بينما ثُبّتت أرقام كل خزينة عند عدّها. يظهر أي تصحيح بعد الإغلاق في قسم تعديلات ما بعد إقفال اليوم، ولا يغيّر الخزينة المغلقة.",
    varianceTotalLabel: "إجمالي الفرق",

    // Block C — the bridge. Rendered as a labelled arithmetic chain:
    // daySettled − carriedOut + carriedIn = shiftsTakings.
    // العنوان كان «ليه الرقمين مختلفين» — بيسأل سؤال قبل ما يعرف لو فيه فرق
    // أصلاً، وفي ليلة عادية بيرد على نفسه بـ«الرقمين متطابقين». العنوان
    // بيسمّي اللي بيتقارن، والنتيجة بتيجي تحت.
    bridgeTitle: "مبيعات اليوم في مقابل خزائن اليوم",
    bridgeIntro:
      "قد لا تتطابق مبيعات اليوم مع خزائن اليوم إذا بدأت خزينة قبل منتصف الليل؛ وتوضّح هذه التسوية السبب.",
    daySettledLabel: "المتحصّل من فواتير اليوم",
    carriedInLabel: "زائد: نقود أيام سابقة حُصّلت في ورديات اليوم",
    // NOT "another shift". `carriedOut` excludes every shift that is not one
    // of THIS day-close's closed shifts — which mid-service includes the
    // store's own still-open shift. Read at 9pm the old wording asserted the
    // money had gone somewhere else, about takings sitting in the drawer the
    // reader was standing at. The arithmetic was right; the sentence was not.
    carriedOutLabel:
      "ناقص: فواتير اليوم التي حُصّلت في خزائن غير داخلة في هذا الإقفال",
    stillUnsettledLabel:
      "ناقص: فواتير أُغلقت ولم تُحصّل بعد (حسابات لا تزال خارج الخزينة)",
    // B91 — فلوس فعلاً في الدرج على فاتورة لسه محدش خلّص دفعها. غير
    // «stillUnsettledLabel» فوق: دي إجمالي الفاتورة، وده اللي اتدفع منها.
    takenOnUnsettledLabel: "زائد: دفعات مقدّمة على فواتير لم تُغلق بعد",
    shiftsTakingsLabel: "= ما دخل خزينة اليوم بالفعل",
    // The bridge's OTHER side: summed independently من فواتير الورديات نفسها،
    // مش من نفس أرقام مبيعات اليوم اللي بنيت عليها shiftsTakings.
    shiftsActualTakingsLabel: "المتحصّل الفعلي بحسب الخزينة (محسوب بصورة مستقلة)",
    // «الجسر متوازن» كانت بتتقري كأنها حكم على اليوم كله، وهي مش كده: الجسر
    // بيقارن فواتير بفواتير ومابيشوفش الدرج. في ليلة الدرج فيها ناقص ٣٤٢.١٨
    // كانت الورقة بتقول «متوازن» على بُعد قسمين من النقص نفسه. الجملة دلوقتي
    // بتسمّي اللي اتطابق بالظبط، والدرج له جملته اللي تحت.
    bridgeMatchesMessage: "تتطابق فواتير اليوم مع ما دخل الخزائن.",
    bridgeMismatchWarning:
      "لا تتطابق فواتير اليوم مع ما دخل الخزائن؛ والفرق {amount}.",
    /*
     * الجملة اللي كانت ناقصة (UX-round D32) — ودي بتزوّد كلام مش بتشيل.
     *
     * الشكوى اللي فتحت الجولة كانت إن الورقة بتشرح كتير. المحاسب لقى العكس في
     * أهم سطر: الورقة بتصرف ١١ سطر على حاجات ماحصلتش، وماتقولش ولا كلمة إن
     * الدرج ناقص ٣٤٢.١٨ — وهو كل اللي المطعم حصّله. `bridgeRefundNote` هي
     * الجملة الوحيدة اللي بتوصل بين «الجسر متوازن» و«الدرج ناقص»، وهي متعلّقة
     * على وجود مرتجع، فبتسكت بالظبط في اليوم اللي محتاجينها فيه.
     */
    varianceUnexplainedNote:
      "تقارن التسوية الفواتير بالفواتير ولا تفحص الخزينة؛ لذلك قد تتوازن مع وجود نقص في الخزينة. ما زال فرق الخزائن البالغ {amount} بلا تفسير.",
    /*
     * الصيغة الجزئية، وهي مش تحسين — هي شرط (V1).
     *
     * `varianceTotal` بيتجمع في `day-close.policy.ts` بـ
     * `operativeCashVariance ?? 0`، يعني درج محدش عدّه بيتحسب صفر. فلو اتقالت
     * الجملة اللي فوق على يوم فيه درج ماتعدّش، تبقى الورقة الموقّعة بتدّعي رقم
     * على أدراج مش كلها اتعدّت — وده نفس «الصفر المفبرك» اللي المبدأ الأول
     * بيمنعه، بس على مستوى الجملة مش الخانة.
     */
    varianceUnexplainedPartialNote:
      "تقارن التسوية الفواتير بالفواتير ولا تفحص الخزينة. يوجد في الخزائن المعدودة فرق قدره {amount} بلا تفسير، وما زالت هناك خزينة لم تُعدّ؛ لذلك لا يمثل هذا الرقم فرق اليوم كاملًا.",
    bridgeRefundedInDayLabel: "ما أُعيد إلى العميل في هذا اليوم",
    bridgeRefundedCashLabel: "منه نقد خرج من الخزينة",
    // المرتجع مش جزء من الحسبة اللي فوق وده مقصود: الجسر بيقارن فواتير
    // بفواتير، والمرتجع مش فاتورة.
    bridgeRefundNote:
      "لا يدخل المرتجع في الحساب أعلاه، لأن التسوية تقارن الفواتير بالفواتير والمرتجع ليس فاتورة. يوضّح السطران سبب احتمال وجود نقص في الخزينة رغم توازن التسوية.",

    /* GAP 1 / UX-round D39 · ruling F8 — naming every number `sequenceGapHint` only
       counted, and since 2026-09-17 as a ROW per kind rather than nine chips.
       
       `report-pages.md`: "An exclusion with a count is a ROW, not a sentence —
       label · count · amount — inside the ladder it qualifies." Nine repetitions
       of «لسه مفتوحة» wrapped across two printed rows and said the least
       informative thing the app knows about those tickets; three rows say the
       same numbers and add the count each kind carries.
       
       These are the ROW LABELS now, so «الرقم ده مش موجود خالص» — deictic,
       written for a single chip — reads as a group. The numbers themselves are
       still printed one by one in every row: the accountant's insist-list #4
       allows the chips to become a grouped line and forbids the numbers being
       summarised away into "9 open". */
    sequenceExceptionCancelled: "أُلغيت",
    sequenceExceptionOpen: "ما زالت مفتوحة",
    // MISSING: no invoice row anywhere claims this number — the one kind that
    // means investigate, and the one F8 forbids summarising.
    sequenceExceptionMissing: "غير موجودة تمامًا",

    // S14/D49: the drawer's own frozen cash/card takings, block B (rework
    // finding 10) — a legacy null renders as "—", never 0.
    drawerCashColumn: "نقد الخزينة",
    drawerCardColumn: "بطاقات الخزينة",
    // R6/R12 — مرتجع الدرج، عشان حساب السطر يبان كامل على سطر واحد.
    drawerRefundColumn: "مرتجع الخزينة",
    drawerRefundFormula:
      "المتوقع = العهدة + نقد الخزينة + الداخل − الخارج − مرتجع النقد.",

    // GAP 2: paid-in/paid-out per drawer, and the day's combined movements.
    // B118/QA3 — عمود اسم الدرج، اللي جمل القصص تحت الجدول بتسمّي بيه.
    drawerLabelColumn: "الخزينة",
    /* B118/QA4 — الاسم بيتكرر في نفس اليوم (الفهرس الفريد على المفتوح بس)،
       فليلة فيها تسليم بتحطّ صفّين اسمهم «تيل ١». الوقت بيفصل بينهم، وبيتزاد
       بس لما يتكرر. */
    drawerLabelAt: "{label} (فُتح {at})",
    drawerMovementsColumn: "داخل / خارج",
    /* UX-round D40 · ruling B5 — WHERE THE FOUR LABELS' MEANING WENT.
       
       The cell stacked four nil money figures under four labels on every
       ordinary night: «+0 / −0», «طلعت من المطعم 0», «لسه فلوس المطعم 0» —
       six lines of row height for a drawer nobody paid into or out of. It now
       collapses to a single `0`, and NEVER to «—»: these are counted zeros, and
       a dash there would assert "nobody counted this" about money that WAS
       counted at nil — invariant 1 running backwards.
       
       Charter invariant 6 is why this clause exists: removing the four labels
       from an all-nil cell may not remove what they mean, so their meaning
       rides the method block, named, and the last sentence says where the cell
       went. */
    drawerMovementsBasisNote:
      "«داخل» هو نقد دخل الخزينة من دون فاتورة، و«خارج» هو نقد خرج منها. وينقسم الخارج إلى «خرج من المطعم»، أي مصروف دُفع فعلًا، و«لا يزال من نقود المطعم»، أي نُقل إلى خزنة أو خزينة أخرى. وعندما تكون القيم الأربع صفرًا تعرض الخانة صفرًا واحدًا.",
    dayMovementsTitle: "حركات نقد اليوم",
    // FIX 1: نفس تقسيم `paidOutLeftBusinessTotal` بس جنب رقم كل وردية في
    // جدول الدرج، عشان القارئ ميحتاجش يفتح كل وردية لوحدها عشان يعرف.
    drawerLeftBusinessLabel: "خرجت من المطعم",
    drawerMovedElsewhereLabel: "ما زالت من نقود المطعم",
    // وحدة ٢: الذراع التالت — تسليم خدمة أو بقشيش للفريق.
    drawerPassedThroughLabel: "تسليم للفريق",

    // GAP 3: the day's voids/comps/write-offs/cancellations, accountability.
    dayExceptionsTitle: "إلغاءات وخصومات اليوم",
    // The list stopped at the server's cap while the counts above it did not.
    dayExceptionsTruncated:
      "تُعرض أحدث {count} حركة فقط؛ توجد حركات أقدم في هذا اليوم لا تظهر هنا. حُسبت الأرقام أعلاه وعدد المرتجعات على اليوم كاملًا، لا على هذه القائمة.",
    platesColumn: "الأصناف",

    // GAP 4: float continuity — a drawer opened with less/more than what the
    // previous one closed with (FIX 2, corrected 2026-09).
    //
    // The old single flag fired on `openingFloat !== previousCountedCash`,
    // which is EVERY normal night: close counting 5,000 → the takings are
    // removed → the next shift opens with a 500 float. That is money LEAVING
    // between drawers (banking, a drop), and it is normal — so it is now
    // informational, no warning tone. Money APPEARING between drawers is the
    // opposite: nothing legitimate puts cash IN a drawer between shifts, so
    // that direction keeps the warning tone.
    floatContinuityLower:
      "أُغلقت الخزينة السابقة بمبلغ {amount}؛ وقد يكون الفرق طبيعيًا بسبب التحصيل أو النقل بين الخزائن.",
    floatContinuityHigher:
      "أُغلقت الخزينة السابقة بمبلغ {amount} فقط؛ وقد ظهرت نقود زائدة في الخزينة من دون تسجيل.",

    openShiftPendingWarning:
      "ما زالت خزينة مفتوحة؛ لذلك لم يصبح تقرير نهاية اليوم نهائيًا بعد.",

    // D42: what اتعمل في فواتير اليوم ده بعد ما اليوم نفسه اتقفل. ظاهر في كل
    // تقفيلة حتى لو مفيهاش حاجة — "اتعدّل بعد القفل: لأ" هي نفسها الإجابة
    // اللي المحاسب محتاجها.
    afterCloseTitle: "تعديلات بعد إقفال اليوم",
    /* UX-round D37 — gated on `afterClose.rows.length > 0`, and cut to the one
       distinction a reader acts on: a late table is not an amendment.
       
       This was the Z's longest string at 291 characters and it printed four
       lines directly above «اتعدّل بعد القفل: لأ، مفيش» — four lines qualifying
       rows that are not there. `afterCloseNone` below is the ASSERTION and
       stays unconditional; only the preamble is gated. The full text is
       `afterCloseBasisNote`, in the door, the CSV and the printed method
       block. */
    afterCloseIntro:
      "لا توجد مهلة زمنية لإعادة فتح فاتورة بعد الإغلاق؛ أما الفاتورة الممتدة حتى الفجر ثم أُغلقت فليست تعديلًا، بل وصلت متأخرة فحسب.",
    afterCloseBasisNote:
      "لا توجد مهلة زمنية لإعادة فتح فاتورة مغلقة؛ فقد يحتاج الموظف إلى تصحيح إغلاق خاطئ من الخزينة نفسها بعد تغيّر يوم العمل. لذلك يعرض تقرير نهاية اليوم كل ما طرأ على فواتيره بعد الإغلاق. أما فاتورة طاولة استمرت حتى الفجر ثم أُغلقت فليست تعديلًا، بل فاتورة وصلت متأخرة وتظل قيمتها ضمن إجمالي اليوم.",
    afterCloseNone: "تعديلات بعد الإقفال: لا يوجد",
    afterCloseAmendmentCountLabel: "تعديلات (قد تغيّر الإجمالي)",
    afterCloseLateTicketCountLabel:
      "فواتير ممتدة (أُغلقت بعد بدء اليوم الجديد)",
    afterCloseLateSettlementCountLabel: "تحصيل متأخر (حساب كان خارج الخزينة)",
    // Finding 2 (follow-up review): the counts above become UNDER-counts
    // once the backend's list hit its own cap — say so plainly rather than
    // let "100" read as exact when it might not be.
    afterCloseTruncatedWarning:
      "بلغ العدد الحد الأقصى المعروض؛ وقد يكون العدد الفعلي أكبر.",
    // The three kinds a row can be — a chip on each row so no two look
    // alike (the float-continuity mistake, not repeated here). LATE_TICKET
    // (finding 1) is a table that ran past الفجر ولسه ما اتقفلتش — مش تعديل
    // على تقرير اتقفل، الفاتورة نفسها وصلت متأخرة.
    afterCloseKindAmendment: "تعديل",
    afterCloseKindLateSettlement: "تحصيل متأخر",
    afterCloseKindLateTicket: "فاتورة ممتدة إلى اليوم التالي",
    afterCloseTicketLabel: "فاتورة #{sequence}",
    afterCloseTicketUnknown: "فاتورة حُذفت",
    // Command labels — an allow-list of two decides "late settlement";
    // everything else, including a نوع أمر المستقبل مش عارفينه هنا، بيتوصف
    // بالنص العام بتاع afterCloseCommandOther.
    afterCloseCommandReopenInvoice: "فتح الفاتورة مرة أخرى",
    afterCloseCommandSettleInvoice: "تحصيل الفاتورة",
    afterCloseCommandCorrectSettlement: "تعديل طريقة الدفع",
    afterCloseCommandAddItems: "إضافة أصناف",
    afterCloseCommandVoidItem: "إلغاء صنف",
    afterCloseCommandCompItem: "صنف على الحساب",
    afterCloseCommandCloseInvoice: "إغلاق الفاتورة",
    afterCloseCommandCancelInvoice: "إلغاء الفاتورة",
    afterCloseCommandTransferInvoice: "نقل الفاتورة إلى طاولة أخرى",
    // الفلوس الأول: دي اللي محاسب بيدوّر عليها في الورقة المطبوعة، وكانت
    // بتطلع بالاسم البرمجي الخام جوه «تعديل تاني».
    afterCloseCommandSettlePartial: "تحصيل دفعة من الفاتورة",
    afterCloseCommandRefundInvoice: "إعادة مبلغ صنف",
    afterCloseCommandRefundOverpayment: "إعادة المبلغ الزائد",
    // بعدها اللي بيغيّر شكل الفاتورة من غير ما يمس فلوس.
    afterCloseCommandOpenInvoice: "فتح فاتورة",
    afterCloseCommandUpdateDraftItems: "تعديل الأصناف قبل إرسالها",
    afterCloseCommandSetGuestCount: "تسجيل عدد الأفراد",
    afterCloseCommandAssignParty: "إسناد أصناف إلى مجموعة",
    afterCloseCommandEditCustomer: "تصحيح بيانات العميل",
    afterCloseCommandEditCharges: "تعديل رسوم الفاتورة أو قناتها",
    afterCloseCommandApplyBillDiscount: "خصم على الفاتورة",
    afterCloseCommandRemoveBillDiscount: "إزالة الخصم من الفاتورة",
    afterCloseCommandPrintCheck: "طباعة حساب مبدئي",
    afterCloseCommandRequestCashier: "إرسال إلى الكاشير",
    afterCloseCommandSetCaptain: "تغيير الويتر المسؤول",
    afterCloseCommandSubstituteItem: "تعديل تبديلات صنف",
    afterCloseCommandRemakeItem: "إعادة تحضير صنف",
    // المطبخ.
    afterCloseCommandFireItems: "إرسال أصناف إلى المطبخ",
    afterCloseCommandPrintTicket: "إعادة طبع تذكرة المطبخ",
    afterCloseCommandStartPreparing: "بدء تحضير صنف",
    afterCloseCommandMarkReady: "صنف جاهز",
    afterCloseCommandMarkServed: "صنف قُدّم",
    afterCloseCommandUndoReady: "التراجع عن «جاهز»",
    afterCloseCommandUndoServed: "التراجع عن تقديم صنف",
    // اللي بييجي من الـ QR — التطبيق ده ما بيبعتهاش، بس بتحصل على الفواتير
    // وبالتالي بتوصل للتقرير ده.
    afterCloseCommandRequestItems: "طلب من قائمة الطعام عبر QR",
    afterCloseCommandAcceptPendingItems: "قبول طلب الضيف",
    afterCloseCommandRejectPendingItems: "رفض طلب الضيف",
    afterCloseCommandCancelRequestedItems: "ألغى الضيف طلبه",
    afterCloseCommandOther: "تعديل آخر ({type})",

    // The printed A4 document.
    printDocTitle: "تقرير نهاية اليوم (Z)",
    printBusinessDate: "يوم الخدمة",
    // B117/RT-E — الورقة دي بتتوقّع وبتتقدّم للمحاسب، ولازم تقول بتوقيت مين
    // اتكتبت وامتى اتطبعت. من غير كده الشاشة والـ CSV والورقة تلات مستندات
    // مافيش حاجة تثبت إنهم نفس اليوم.
    printTimezoneNote: "كل الأوقات بتوقيت {zone} · طُبعت {at}",
    // لما المحل مالوش توقيت متسجّل: بنقول إن الأوقات بتوقيت الجهاز بدل ما
    // نطبع توقيت مانقدرش نضمنه.
    printTimezoneDevice: "الجهاز",
    // B117 round table 2 (Q1/Q3) — what the filed sheet could not say.
    drawerRecountedLine:
      "«{label}»: كانت نتيجة العد الأولى {first}، ثم أعاد {by} العد فكانت النتيجة {final} الساعة {at}.",
    drawerManagerCountStands:
      "«{label}»: عدّه {manager} بنفسه فكانت النتيجة {amount} الساعة {at}، بعد نتيجة مسجّلة قدرها {cashier}، وقرر اعتماد نتيجته.",
    drawerManagerCountDeferred:
      "«{label}»: عدّه {manager} بنفسه فكانت النتيجة {amount} الساعة {at}، مقابل نتيجة مسجّلة قدرها {cashier}، وقرر إبقاء عدّ الكاشير معتمدًا.",
    drawerCountedByUnknown: "شخص غير مسجّل",
    /*
     * The four sentences below QUOTE the label and never prepend «درج» to it:
     * the label IS the box's name and a store that called one «درج ١» read
     * «درج درج ١» on its own Z. Quoting also keeps a one-word English label
     * («Bar») from running into the Arabic around it.
     *
     * B118 — THE NOTE, ON THE PAPER. Q2 approved a drawer note "append-only,
     * never editable, author and timestamp stamped, and it PRINTS ON THE Z",
     * on the reasoning that a note which can change after the sheet is signed
     * is worse than no note. It shipped on the screens and on no paper.
     */
    drawerNoteLine: "«{label}» — {author} كتب الساعة {at}: «{body}»",

    /*
     * ── قدم الورقة: التوقيع، وإن الورقة اتحسبت وقت الطباعة، والطريقة (UX-round D35/D36)
     *
     * المحاسب قال السبب بنفسه: الـZ قراية صافية من السجل، مافيش موديل محفوظ
     * لقفلة اليوم — يعني ورقة اتطبعت الضهر ممكن ورقة تانية بنفس العنوان تطلع
     * بأرقام غيرها بعد عدّ تاني نفس اليوم. مكتوب على الورقة نفسها، من غير شرط،
     * جنب ساعة الطباعة، عشان اللي ماسك الورقة يعرف ده من غير ما يسأل حد.
     *
     * وكتلة الطريقة تحت التوقيع بتقرا نفس مصفوفة `basisNotes()` اللي الباب على
     * الشاشة و ملف الـCSV بيقروا منها. مصفوفة واحدة، تلات أسطح.
     */
    printSignatureName: "الاسم والتوقيع",
    printSignatureDate: "التاريخ",
    printRecomputedNote:
      "تُحسب هذه الورقة من السجل وقت الطباعة، ولا تُثبّت وقت الإغلاق.",
    printMethodTitle: "كيف حُسبت هذه الأرقام",
    /*
     * ترقيم الصفحات — `{page}` و`{pages}` بالظبط، مش `{n}` و`{total}`.
     *
     * `{n}` مش مصنّف في `messages.spec.ts` و`{total}` محسوب على إنه عدّ
     * (`COUNT_LIKE`)، والاتنين بيكسروا الحارس ده — اتجرّبوا وسقطوا. والأرقام
     * لاتينية (0-9) زي أي رقم تاني في التطبيق، مش «صفحة ١ من ٢»: قرار المؤسس
     * في الجولة ١٠، ومكتوب في `apps/restaurant/AGENTS.md:143`.
     */
    printPageLabel: "صفحة {page} من {pages}",

    /*
     * ── شكل PAPER (إعادة التصميم 2026-10، الجولة ٤) ─────────────────────────
     *
     * مفيش أمر «قفل اليوم» في المنتج (D38: الـ Z قراءة بس)، فالقايمة اللي فوق
     * مش بوابة قدام فعل — دي البنود اللي مخلّية أرقام اليوم مش نهائية، وكل بند
     * معاه المكان اللي بيتقفل فيه. والسلّم «من الإجمالي إلى صافي المتحصّل»
     * مبني من هويّتين الـ payload نفسه بيقولهم، مش من خطوات التصميم.
     */
    paperStatusDrawerOpen: "ما زالت خزينة مفتوحة",
    paperStatusAllClosed: "أُغلقت كل الخزائن",
    paperChecksTitle: "قبل تقرير نهاية اليوم",
    paperChecksLeftOne: "بند واحد يجب إنهاؤه أولًا",
    paperChecksLeftTwo: "بندان يجب إنهاؤهما أولًا",
    paperChecksLeftFew: "{count} بنود يجب إنهاؤها أولًا",
    paperChecksLeft: "{count} بندًا يجب إنهاؤها أولًا",
    paperChecksNone: "لا يوجد بند مفتوح في هذا اليوم.",
    paperCheckOpenTicketsOne: "فاتورة واحدة ما زالت مفتوحة",
    paperCheckOpenTicketsTwo: "فاتورتان ما زالتا مفتوحتين",
    paperCheckOpenTicketsFew: "{count} فواتير ما زالت مفتوحة",
    paperCheckOpenTickets: "{count} فاتورة ما زالت مفتوحة",
    paperCheckUnsettled: "فواتير بقيمة {amount} لم تُحصّل بعد",
    paperCheckUnsettledSub: "فواتير أُغلقت وما زالت تنتظر الدفع.",
    paperCheckDrawerOpenSub:
      "عُدّ الخزينة ثم أغلقها؛ وقبل ذلك لا تصبح أرقام هذا اليوم نهائية.",
    paperCheckUncountedOne: "خزينة واحدة أُغلقت دون عدّ",
    paperCheckUncountedTwo: "خزينتان أُغلقتا دون عدّ",
    paperCheckUncountedFew: "{count} خزائن أُغلقت دون عدّ",
    paperCheckUncounted: "{count} خزينة أُغلقت دون عدّ",
    paperCheckGoFloor: "الذهاب إلى الصالة",
    paperCheckGoInvoices: "عرض هذه الفواتير",
    paperCheckGoDrawer: "الذهاب إلى الخزينة",
    paperCheckGoDrawerHistory: "فتح حركة الخزينة",
    paperFlowTitle: "من الإجمالي إلى المتحصّل",
    paperFlowSubtitle: "كل خطوة تحرّك المجموع، والأشرطة الداكنة هي المجاميع.",
    paperFlowInstapay: "إنستاباي (خارج الخزائن)",
    paperFlowDrawerTakings: "نقد وبطاقة",
    paperFlowWrittenOffInflow: "مقبوض على فواتير أُزيلت لاحقًا",
    paperBlockSales: "المبيعات والرسوم",
    paperServiceCharge: "رسم الخدمة",
    paperVat: "ضريبة القيمة المضافة",
    paperDeliveryFee: "رسوم التوصيل (للطيار)",
    paperBlockInvoices: "الفواتير",
    paperInvoicesCancelled: "أُلغيت",
    paperInvoicesOpen: "ما زالت مفتوحة",
    paperInvoicesMissing: "غير موجودة تمامًا",
    paperInvoicesWrittenOff: "بلا دفع — ليست من المتحصّل",
    paperAfterTime: "الوقت",
    paperAfterKind: "ماذا",
    paperAfterTicket: "الفاتورة",
    paperAfterDetail: "التفاصيل",
    paperAfterBy: "بواسطة",
    // PAPER (founder, 2026-10-02): the redesign's own words. Only under PAPER.
    paperSubtitle: "راجع اليوم وأغلقه واطبعه.",
    paperPrintButton: "طباعة تقرير Z",
    // D151 (2026-10-14 round table, T7) — the Z as a WhatsApp message, sent
    // from the reader's own phone (a share link; no API, no template). The
    // same lines as the owner's nightly push, every figure off this Z.
    shareWhatsapp: "مشاركة على واتساب",
    shareTitle: "تقرير نهاية اليوم {date}",
    shareSales: "الإجمالي (شاملًا الخدمة والضريبة) {gross} · الصافي {net}",
    shareDrawer: "{label}: المعدود {counted}",
    shareDrawerWithVariance: "{label}: المعدود {counted} · الفرق {variance}",
    shareDrawersTotal: "عدد الخزائن {count} · فرق الخزائن {variance}",
    shareOpenBills: "فواتير مفتوحة: {count}",
    shareUnsettled: "مُغلقة بانتظار الدفع {amount}",
    shareStillOut: "ما زالت مع الطيارين: {count} · {amount}",
    shareAsOf: "حتى الساعة {time} — قد يتغير الرقم إذا أُعيد فتح فاتورة",
    // D161 (F37, founder 2026-10-15) — the lead block under the Z's header.
    // Only the title and two labels are new; every other word in the block
    // is the label the same figure already carries further down the page
    // (the drawers table's columns, the two buckets, the riders' cell), and
    // an uncounted drawer reads `shiftHistory.paperNotCountedNote` — one
    // thing, one word.
    leadTitle: "خلاصة اليوم",
    leadOpenBills: "فواتير ما زالت مفتوحة",
    leadRidersOut: "ما زالت مع الطيارين",
    // P6.1 (founder, P6-Q1, 2026-10-25) — THE SAVED Z. A closed day's figures
    // are the ones saved when its last drawer closed, and they do not change;
    // what moved since is its own block. The figure names are the labels the
    // same figures carry on the page, without the ladder's «+»/«=» signs.
    paperStatusSaved: "أُقفل اليوم {time} · الأرقام محفوظة",
    // Review of #307: a copy saved in an older shape of the Z is not served.
    paperStatusSavedOutdated: "أُقفل اليوم {time} · الأرقام الحالية",
    closedDayOutdated:
      "النسخة المحفوظة عند القفل بصيغة أقدم من هذا التقرير ولا يمكن عرضها، فالأرقام أعلاه هي الأرقام الحالية.",
    closedDayTitle: "تغييرات منذ قفل اليوم ({time})",
    closedDayIntro:
      "الأرقام أعلاه هي المحفوظة عند إقفال آخر خزينة، ولا تتغير. كل سطر هنا رقم تحرّك بعد ذلك: المحفوظ، والآن، والفرق.",
    closedDayNone: "تغييرات منذ القفل: لا يوجد",
    closedDayFigureColumn: "الرقم",
    closedDaySavedColumn: "المحفوظ",
    closedDayNowColumn: "الآن",
    closedDayDifferenceColumn: "الفرق",
    closedDayFigureOther: "رقم آخر في التقرير",
    shareSaved: "محفوظ عند القفل {time} — أي تعديل بعده يظهر وحده في التقرير",
    shareChangedSince: "تغيّر بعد القفل: {count}",
    closedDayFigureGross: "إجمالي المبيعات",
    closedDayFigureServiceChargeTotal: "رسم الخدمة",
    closedDayFigureVatTotal: "ضريبة القيمة المضافة",
    closedDayFigureDeliveryFeeTotal: "رسوم التوصيل",
    closedDayFigureDeliveryFeeVatTotal: "ضريبة رسوم التوصيل",
    closedDayFigureNet: "صافي مبيعات الطعام",
    closedDayFigureCashTotal: "نقد",
    closedDayFigureCardTotal: "بطاقة",
    closedDayFigureInstapayTotal: "إنستاباي",
    closedDayFigureWriteOffTotal: "بلا دفع",
    closedDayFigureAggregatorTotal: "على التطبيقات",
    closedDayFigureCommissionTotal: "عمولة التطبيقات",
    closedDayFigureAggregatorOrderTotal: "قيمة طلبات التطبيقات",
    closedDayFigureUnsettledTotal: "لم يُحصّل بعد",
    closedDayFigureInvoiceCount: "عدد الفواتير المحسوبة",
    closedDayFigureDiscountTotal: "الخصم",
    closedDayFigureBillDiscountTotal: "خصم على الفاتورة",
    closedDayFigureBillDiscountCount: "فواتير عليها خصم",
    closedDayFigureCompedTotal: "على الحساب",
    closedDayFigureVoidedTotal: "الملغى",
    closedDayFigureCompedNetTotal: "على الحساب (صافي الطعام)",
    closedDayFigureVoidedNetTotal: "الملغى (صافي الطعام)",
    closedDayFigureStockAdjustmentCost: "تسوية المخزون",
    closedDayFigureRefundTotal: "أُعيد إلى العميل",
    closedDayFigureRefundCount: "أصناف أُعيدت",
    closedDayFigureRefundServiceChargeTotal: "رسم خدمة أُعيد",
    closedDayFigureRefundVatTotal: "ضريبة أُعيدت",
    closedDayFigureRefundDeliveryFeeTotal: "رسم توصيل أُعيد",
    closedDayFigureRefundDeliveryFeeVatTotal: "ضريبة رسم توصيل أُعيدت",
    closedDayFigureRefundCashTotal: "مرتجع نقدي",
    closedDayFigureRefundCardTotal: "مرتجع على البطاقة",
    closedDayFigureRefundMethodDivergedCount: "مرتجعات بغير طريقة الدفع",
    closedDayFigureRefundLateCount: "مرتجعات متأخرة",
    closedDayFigureWrittenOffInflowCashTotal: "نقد مقبوض على فواتير أُزيلت لاحقًا",
    closedDayFigureWrittenOffInflowCardTotal: "بطاقة مقبوضة على فواتير أُزيلت لاحقًا",
    closedDayFigureNetTakings: "صافي المتحصّل",
    closedDayFigureDaySettled: "المتحصّل من فواتير اليوم",
    closedDayFigureCarriedIn: "نقود أيام سابقة حُصّلت في ورديات اليوم",
    closedDayFigureCarriedOut: "حُصّل في وردية لم تُقفل هنا",
    closedDayFigureStillUnsettled: "فواتير لم تُحصّل بعد",
    closedDayFigureTakenOnUnsettled: "دفعات مقدّمة على فواتير لم تُغلق",
    closedDayFigureShiftsTakings: "ما دخل خزينة اليوم",
    closedDayFigureShiftsActualTakings: "المتحصّل الفعلي بحسب الخزينة",
    closedDayFigureRefundedInDay: "ما أُعيد إلى العميل في هذا اليوم",
    closedDayFigureRefundedCashInDay: "منه نقد خرج من الخزينة",
    closedDayFigureVarianceTotal: "إجمالي الفرق",
    closedDayFigureOpenBillCount: "فواتير ما زالت مفتوحة",
  },
  // Driver backend B1 (2026-10-17): a driver-only account on the web, the
  // public invite page a driver opens from WhatsApp, and the team screen's
  // "Invite a driver". MSA throughout.
  driverAccess: {
    landingTitle: "هذا حساب طيار",
    landingBody:
      "تستلم الطلبات وتوصّلها من تطبيق تريوتي للطيار على هاتفك، لا من هذه الشاشة.",
    landingStore: "المطعم: {store}",
    landingHint:
      "سجّل الدخول في التطبيق برقم الهاتف وكلمة المرور اللذين تستخدمهما هنا.",
    landingSwitchTitle: "لديك عمل في مطعم آخر؟",
    landingSwitchBody: "اختر المطعم للانتقال إليه.",
    inviteTitle: "دعوة للعمل طيارًا",
    inviteBody:
      "افتح الدعوة في تطبيق تريوتي للطيار لتفعيل حسابك والانضمام إلى المطعم.",
    inviteOpenApp: "افتح التطبيق",
    inviteInstallHint:
      "إذا لم يُفتح التطبيق، فثبّت تطبيق تريوتي للطيار أولًا، ثم افتح هذا الرابط من جديد.",
    inviteKeepPrivate: "هذا الرابط خاص بك، فلا تشاركه مع أحد.",
    inviteNoAppTitle: "تطبيق تريوتي للطيار غير مثبّت",
    inviteNoAppBody:
      "لم يُفتح التطبيق لأنه غير مثبّت على هذا الهاتف. للانضمام إلى المطعم اتبع هذه الخطوات:",
    inviteNoAppStepAsk:
      "اطلب من المطعم الذي دعاك تطبيق تريوتي للطيار أو رابط تنزيله.",
    inviteNoAppStepInstall: "ثبّت التطبيق على هاتفك.",
    inviteNoAppStepReopen:
      "ارجع إلى رسالة الدعوة على واتساب وافتح الرابط نفسه من جديد.",
    inviteNoAppRetry: "افتح التطبيق بعد تثبيته",
    composerAction: "دعوة طيار",
    composerTitle: "دعوة طيار",
    composerIntro:
      "اكتب اسم الطيار ورقم هاتفه، وستحصل على رابط ترسله إليه من واتساب على هاتفك.",
    composerName: "اسم الطيار",
    composerNamePlaceholder: "مثلًا: محمود علي",
    composerPhone: "رقم الهاتف",
    composerCreate: "إنشاء الرابط",
    composerDetailsRequired: "اكتب اسم الطيار (حرفان على الأقل) ورقم هاتفه.",
    composerReady:
      "الرابط جاهز. أرسله إلى {name} من واتساب على هاتفك أو انسخه، وهو صالح سبعة أيام.",
    composerShareWhatsapp: "إرسال على واتساب",
    composerCopy: "نسخ الرابط",
    composerCopied: "نُسخ الرابط",
    composerShareMessage:
      "مرحبًا {name}، دعاك {store} للعمل طيارًا على تريوتي. افتح هذا الرابط من هاتفك لتفعيل حسابك في تطبيق تريوتي للطيار:\n{url}",
    errorAlreadyOpen:
      "أُنشئت دعوة لهذا الرقم في اللحظة نفسها. أعد المحاولة لتحصل على رابطها.",
    errorAlreadyMember: "صاحب هذا الرقم عضو في فريق هذا المطعم بالفعل.",
    errorInvalid:
      "تحقّق من الاسم ورقم الهاتف. اكتب الرقم هكذا: 01012345678، أو بالصيغة الدولية.",
    errorPermission: "لا تملك صلاحية دعوة الطيارين. اطلبها من صاحب المطعم.",
  },
  // Driver backend B3 (2026-10-18): the till's dispatch — the delivery tile's
  // driver status, assigning / reassigning / taking an offer back, and the
  // owner's accept timeout. MSA throughout.
  dispatch: {
    chipOffered: "عُرض على {name} · {time}",
    chipAccepted: "قبِل {name} الطلب",
    chipPickedUp: "في الطريق مع {name}",
    chipArrived: "وصل {name} إلى العميل",
    chipDelivered: "سلّمه {name}",
    chipFailed: "لم يُسلَّم · يعود مع {name}",
    chipProblem: "أبلغ {name} عن مشكلة",
    chipNotAnswered: "لم يردّ {name}",
    chipDeclined: "اعتذر {name}",
    actionAssign: "إسناد إلى طيار",
    actionChange: "تغيير الطيار",
    sheetTitle: "طيار الطلب رقم {sequence}",
    sheetCurrent: "الطلب الآن مع {name}",
    sheetPick: "اختر طيارًا",
    sheetOnShift: "متاح",
    sheetOffShift: "غير متاح",
    sheetActiveOne: "معه طلب واحد",
    sheetActiveTwo: "معه طلبان",
    sheetActiveFew: "معه {count} طلبات",
    sheetActive: "معه {count} طلبًا",
    sheetNoDrivers: "لا يوجد طيارون بعد. ادعُ طيارًا من شاشة الفريق.",
    sheetNoneOnShift:
      "لا يوجد طيار متاح الآن. يفعّل الطيار «متاح» في التطبيق ليستقبل الطلبات.",
    sheetTimeout: "أمام الطيار {minutes} ليقبل الطلب، ويحدّد صاحب المطعم هذه المدة.",
    sheetWithdraw: "سحب الطلب من الطيار",
    sheetLoadFailed: "تعذّر تحميل قائمة الطيارين.",
    minutesOne: "دقيقة واحدة",
    minutesTwo: "دقيقتان",
    minutesFew: "{count} دقائق",
    minutes: "{count} دقيقة",
    toastAssigned: "عُرض الطلب على {name}",
    toastWithdrawn: "سُحب الطلب من {name}",
    errorNotAvailable: "هذا الطيار غير متاح الآن. اختر طيارًا متاحًا.",
    errorAlreadyAssigned:
      "أُسند هذا الطلب إلى طيار آخر في اللحظة نفسها، وقد حدّثنا القائمة.",
    errorNotDeliverable:
      "لا يمكن إسناد هذا الطلب إلى طيار: فهو ليس طلب توصيل للمطعم، أو أُلغي، أو سُلّم بالفعل.",
    errorInvalidTransition:
      "تغيّرت حالة الطلب، فربما استلمه الطيار أو انتهى العرض. وقد حدّثنا القائمة.",
    errorNotFound: "لم نجد هذا الإسناد، وقد حدّثنا القائمة.",
    errorConflict: "تعذّر تنفيذ العملية. أعد المحاولة.",
    settingsTitle: "الطيارون",
    settingsTimeoutLabel: "مدة قبول الطيار للطلب",
    settingsTimeoutHint:
      "من 1 إلى 15 دقيقة. إذا لم يقبل الطيار الطلب خلال هذه المدة عاد الطلب إلى الكاشير ليُسنده من جديد.",
    settingsMinutesUnit: "دقيقة",
    settingsSave: "حفظ المدة",
    settingsSaved: "حُفظت مدة القبول",
    settingsInvalid: "اكتب عددًا صحيحًا من 1 إلى 15.",
    settingsReadOnly: "يغيّر صاحب المطعم هذه المدة فقط.",
    settingsLoadFailed: "تعذّر تحميل مدة القبول.",
    // B3 · P14 — problems, ending a delivery from the till, and the driver's
    // cash handover inside the rider's sitting.
    actionProblem: "عرض بلاغ الطيار",
    actionFollowUp: "متابعة التوصيل",
    problemSheetTitle: "توصيل الطلب رقم {sequence}",
    problemFrom: "بلاغ من {name} · {time}",
    reasonNoAnswer: "العميل لا يرد على الهاتف",
    reasonWrongAddress: "العنوان خطأ أو لم يجده الطيار",
    reasonRefused: "العميل رفض استلام الطلب",
    reasonMissingOrDamaged: "يوجد صنف ناقص أو تالف",
    reasonOther: "سبب آخر",
    problemDriverNote: "ملاحظة الطيار: {body}",
    problemReplyLabel: "رسالة إلى الطيار (اختيارية)",
    problemReplyHint: "تظهر للطيار مع قرارك.",
    resolveRetry: "يحاول مرة أخرى",
    resolveRetryHint: "يبقى الطلب مع الطيار ويكمل التوصيل.",
    resolveReturn: "يعيد الطلب إلى المطعم",
    resolveReturnHint: "لا يحصّل الطيار أي مبلغ، ويعيد الطلب كاملًا إلى الكاونتر.",
    resolveCancel: "إلغاء التوصيل",
    resolveCancelHint:
      "ينتهي التوصيل هنا ولا تتغيّر الفاتورة: ألغِها أو استرد قيمتها من شاشة الفاتورة إن لزم.",
    resolveConfirmCancel: "تأكيد إلغاء التوصيل",
    resolveDoneRetry: "أُبلغ {name} أن يحاول مرة أخرى",
    resolveDoneReturn: "أُبلغ {name} أن يعيد الطلب إلى المطعم",
    resolveDoneCancel: "أُلغي توصيل الطلب رقم {sequence}",
    endTitle: "إنهاء التوصيل من المطعم",
    endHint:
      "للطلب الذي خرج ولا يستطيع طياره إكماله، كأن يُفصل من الفريق أو يُغلق هاتفه. لا يمكن التراجع عن الإنهاء، ولا تتغيّر الفاتورة.",
    endReturned: "عاد الطلب إلى المطعم",
    endCancelled: "أُلغي التوصيل",
    endConfirmReturned: "تأكيد: عاد الطلب إلى المطعم",
    endConfirmCancelled: "تأكيد: أُلغي التوصيل",
    endDelivered: "تأكّدنا أن العميل استلمه",
    endConfirmDelivered: "تأكيد: استلم العميل الطلب",
    endNoteLabel: "سبب الإنهاء (يُحفظ مع بلاغ الطيار)",
    endDone: "انتهى توصيل الطلب رقم {sequence}",
    errorProblemNotOpen: "حُسم هذا البلاغ بالفعل، وقد حدّثنا القائمة.",
    groupDriverBadge: "طيار",
    groupPending: "تسليم بانتظارك",
    handoverPanelTitle: "حساب الطيار {name}",
    handoverPending: "سلّم {name} نقدية بقيمة {amount} · {time}",
    handoverNone: "لا يوجد تسليم نقدية بانتظارك من {name}.",
    handoverLoading: "جارٍ تحميل التسليم…",
    handoverLoadFailed: "تعذّر تحميل التسليم. أعد المحاولة.",
    handoverOrder: "الطلب رقم {number}",
    handoverOrderUnknown: "طلب",
    handoverBagSettle: "يُسدَّد عند التأكيد",
    handoverBagSkip: "سُدّد في الجلسة، ولن يُسدَّد مرة أخرى",
    handoverBagChanged: "تغيّرت الفاتورة بعد التسليم",
    handoverSettleTotal: "يدخل خزينتك عند التأكيد",
    handoverSkipTotal: "سُدّد من قبل في الجلسة",
    handoverDrawerNote: "تدخل النقدية خزينتك المفتوحة، وتُسدَّد كل فاتورة باسمك.",
    handoverChangedNote:
      "تغيّرت فاتورة على الأقل بعد التسليم، فلن يُقبل التأكيد. راجع الفاتورة، أو اضغط «غير صحيح» ليعيد الطيار التسليم.",
    handoverConfirm: "تأكيد الاستلام",
    handoverReject: "غير صحيح",
    handoverRejectNoteLabel: "ما المشكلة؟ (اختيارية، لا يراها الطيار)",
    handoverRejectSend: "رفض التسليم",
    handoverRejectHint: "يعود التسليم إلى الطيار ليعيد إرساله، ولا تُسدَّد أي فاتورة.",
    handoverConfirmed: "أُكّد الاستلام · سُدّد {amount}",
    handoverResultSettled: "سُدّدت: {items}",
    handoverResultSkipped: "تُركت لأنها سُدّدت في الجلسة: {items}",
    handoverRejected: "رُفض التسليم، وسيعيد الطيار إرساله.",
    handoverReplay: "سُجّلت هذه العملية من قبل، ولم يُسدَّد شيء مرة أخرى.",
    errorHandoverBillChanged:
      "تغيّرت الفاتورة رقم {number} بعد التسليم، فلم يُسدَّد أي طلب. راجع الفاتورة، أو اضغط «غير صحيح» ليعيد الطيار التسليم.",
    errorHandoverBillChangedUnknown:
      "تغيّرت إحدى الفواتير بعد التسليم، فلم يُسدَّد أي طلب. راجع الفواتير، أو اضغط «غير صحيح» ليعيد الطيار التسليم.",
    errorHandoverNotPending: "حُسم هذا التسليم بالفعل، وقد حدّثنا القائمة.",
    errorHandoverNotFound: "لم نجد هذا التسليم، وقد حدّثنا القائمة.",
    errorBusy: "المطعم مشغول بعمليات أخرى الآن، ولم يُسدَّد شيء. أعد المحاولة.",
    stillOutTitle: "ما زالت مع الطيار",
    perBagTitle: "سلّمها ولم تدخل تسليمًا",
    perBagOpen: "تسوية هذه الطلبات واحدًا واحدًا",
    perBagHint:
      "الطلب الذي دُفع جزء منه بعد خروجه لا يقبله تسليم الطيار، فيُسدَّد هنا في الجلسة فقط.",
    handOverDriver: "يخرج الطلب مع الطيار {name}",
    handOverDriverOffered: "عُرض الطلب على {name} ولم يقبله بعد. يمكنك إغلاقه الآن، ويستلمه بعد أن يقبل.",
    handOverDriverHint: "يُكتب اسم الطيار على الفاتورة تلقائيًا.",
    handOverDriversTitle: "أو أسنده إلى طيار متاح",
    handOverAssignClose: "إسناد وإغلاق",
    handOverAssignFailed: "تعذّر إسناد الطلب إلى الطيار، فلم يُغلق: {error}",
    // B3 · P14 rework.
    chipReturned: "عاد إلى المطعم · {name}",
    chipDeliveryCancelled: "أُلغي التوصيل · {name}",
    sittingEndedReturned: "عاد إلى المطعم: لا يُحصَّل هنا، وتُعالَج فاتورته من شاشتها.",
    sittingEndedCancelled: "أُلغي التوصيل: لا يُحصَّل هنا، وتُعالَج فاتورته من شاشتها.",
    groupEnded: "عادت أو أُلغيت: {items}",
    resolveConfirmReturn: "تأكيد: يعيد الطلب إلى المطعم",
    sheetEndShift: "إنهاء الوردية",
    sheetEndShiftConfirm: "تأكيد إنهاء الوردية",
    toastShiftEnded: "انتهت وردية {name}، ولن تصله طلبات جديدة.",
    errorDriverNotFound: "لم نجد هذا الطيار في فريق المطعم، وقد حدّثنا القائمة.",
    // B4 · P16: the customer's public tracking page (no login).
    trackPageTitle: "تتبّع طلبك",
    trackOrderNumber: "طلب رقم {number}",
    trackStagePreparing: "يجهّز المطعم طلبك",
    trackStageOnTheWay: "طلبك في الطريق إليك",
    trackStageArrived: "وصل الطيار إلى موقع التوصيل",
    trackStageDelivered: "سُلِّم طلبك",
    trackStageNotDelivered: "لم يكتمل توصيل طلبك",
    trackDeliveredBody: "نتمنى لك وجبة هنيئة.",
    trackNotDeliveredBody: "للاستفسار عن طلبك تواصل مع المطعم.",
    // The ETA's noun agrees with the range's upper bound (`countedKey` on max).
    trackEta: "يصل خلال {min}–{max} دقيقة تقريبًا",
    trackEtaOne: "يصل خلال دقيقة تقريبًا",
    trackEtaTwo: "يصل خلال دقيقتين تقريبًا",
    trackEtaFew: "يصل خلال {min}–{max} دقائق تقريبًا",
    trackStepPreparing: "يُجهَّز الطلب",
    trackStepOnTheWay: "في الطريق",
    trackStepArrived: "وصل الطيار",
    trackStepDelivered: "سُلِّم الطلب",
    trackDriverTitle: "الطيار",
    trackDriverCall: "اتصل بـ{name}",
    trackNoPosition: "لا يتوفّر موقع حديث للطيار الآن، وسيظهر على الخريطة حين يتوفّر.",
    trackLastUpdated: "آخر تحديث {time}",
    trackMapLabel: "خريطة التوصيل",
    trackMapRestaurant: "المطعم",
    trackMapDestination: "موقع التوصيل",
    trackMapDriver: "الطيار",
    trackLoading: "جارٍ تحميل حالة طلبك…",
    trackRetrying: "تعذّر تحديث حالة الطلب، وسنحاول مرة أخرى بعد قليل.",
    trackNotFoundTitle: "رابط التتبّع غير متاح",
    trackNotFoundBody:
      "الرابط غير صحيح أو انتهت صلاحيته. إذا كان طلبك ما زال في الطريق فتواصل مع المطعم.",
    // B4 · P16: sharing the tracking link from the till.
    shareAction: "مشاركة رابط التتبّع",
    shareActionShared: "شُورك رابط التتبّع",
    shareSheetTitle: "رابط تتبّع الطلب #{sequence}",
    shareIntro: "أرسل الرابط إلى العميل ليتابع طلبه على الخريطة حتى يصل إليه.",
    shareLoading: "جارٍ تجهيز الرابط…",
    shareWhatsapp: "إرسال إلى العميل على واتساب",
    shareCopy: "نسخ الرابط",
    shareCopied: "نُسخ الرابط",
    shareNoPhone: "لا يوجد على الفاتورة رقم هاتف صالح للعميل، فانسخ الرابط وأرسله بنفسك.",
    shareStatusSent: "شُورك مع العميل الساعة {time}",
    shareStatusNotSent: "لم يُشارَك مع العميل بعد",
    shareExpires: "يعمل الرابط حتى الساعة {time}",
    shareExpiresDay: "يعمل الرابط حتى يوم {day} الساعة {time}",
    shareMessage:
      "مرحبًا، يمكنك متابعة طلبك رقم {sequence} من {store} حتى يصل إليك من هذا الرابط:\n{url}",
    shareErrorNotDeliverable: "لا يمكن إنشاء رابط تتبّع لهذا الطلب الآن.",
    shareCopyFailed: "تعذّر النسخ تلقائيًا. حدّد الرابط وانسخه بنفسك.",
    shareMarkFailed: "تعذّر تسجيل مشاركة الرابط: {error}",
    // B5 · P18: the drivers roster, a driver's details and the review of
    // their documents (manager / owner), with the access log.
    driversTab: "الطيارون",
    driversTitle: "الطيارون",
    driversSubtitle: "الطيارون في هذا المطعم: من في الوردية الآن، ومركباتهم، وحالة مستنداتهم.",
    driversSubtitleNoDocs: "الطيارون في هذا المطعم: من في الوردية الآن، ومركباتهم.",
    driversNoAccessTitle: "لا يمكنك عرض الطيارين",
    driversNoAccessBody: "تحتاج صلاحية إدارة التوصيل أو مراجعة مستندات الطيارين. اطلبها من صاحب المطعم.",
    driversEmptyTitle: "لا يوجد طيارون بعد",
    driversEmptyBody: "ادعُ طيارًا، وسيظهر هنا بعد أن يفعّل حسابه.",
    driversLoadFailed: "تعذّر تحميل الطيارين: {error}",
    driversHeaderDriver: "الطيار",
    driversHeaderShift: "الوردية",
    driversHeaderVehicle: "المركبة",
    driversHeaderDocuments: "المستندات",
    driverOnShift: "في الوردية",
    driverOffShift: "خارج الوردية",
    driverDetailsAction: "التفاصيل",
    driverDetailsFor: "تفاصيل الطيار {name}",
    docsSummary: "مقبولة {accepted} · بانتظار {waiting} · للإعادة {resubmit} · ناقصة {missing}",
    vehicleTypeMOTORBIKE: "دراجة نارية",
    vehicleTypeCAR: "سيارة",
    vehicleTypeBICYCLE: "دراجة",
    vehicleTypeFOOT: "مشيًا",
    vehicleNone: "لم تُحدَّد",
    vehiclePlate: "اللوحة: {letters} {numbers}",
    driverBack: "كل الطيارين",
    driverLoadFailed: "تعذّر تحميل بيانات الطيار: {error}",
    driverNotFound: "لم يعد هذا الطيار في فريق المطعم.",
    driverNotFoundBody: "ارجع إلى قائمة الطيارين لترى من في الفريق الآن.",
    driverProfileTitle: "بيانات الطيار",
    driverPhone: "رقم الهاتف",
    driverNationalId: "الرقم القومي",
    driverNationalIdLast4: "ينتهي بالأرقام {last4}",
    driverNationalIdNone: "لم يُضَف",
    driverVehicle: "المركبة",
    driverConsent: "الموافقة على مراجعة المستندات",
    driverConsentAt: "وافق في {date}",
    driverConsentNone: "لم يوافق بعد",
    docsTitle: "المستندات",
    docsIntro: "يُسجَّل كل فتح لمستند باسمك ووقته، ويعمل رابط المستند دقيقة واحدة فقط.",
    docKindPROFILE_PHOTO: "الصورة الشخصية",
    docKindID_FRONT: "وجه البطاقة",
    docKindID_BACK: "ظهر البطاقة",
    docKindSELFIE_WITH_ID: "صورة الطيار ممسكًا بالبطاقة",
    docKindCRIMINAL_RECORD: "صحيفة الحالة الجنائية",
    docKindDRIVING_LICENCE: "رخصة القيادة",
    docKindVEHICLE_LICENCE: "رخصة المركبة",
    docKindVEHICLE_PHOTO: "صورة المركبة واللوحة",
    docStatusMISSING: "لم يُرفَع",
    docStatusSUBMITTED: "بانتظار المراجعة",
    docStatusACCEPTED: "مقبول",
    docStatusRESUBMIT: "مطلوب إعادته",
    docUploadedAt: "رُفع في {date}",
    docNote: "ملاحظتك: {note}",
    docOpen: "فتح",
    docDownload: "تنزيل",
    docOpenFailed: "تعذّر فتح المستند: {error}",
    docPopupBlocked: "منع المتصفح فتح نافذة جديدة. اسمح بالنوافذ المنبثقة لهذا الموقع ثم أعد المحاولة.",
    docAccept: "قبول",
    docAccepted: "قُبل المستند",
    docResubmit: "طلب إعادة الرفع",
    docResubmitTitle: "طلب إعادة رفع: {kind}",
    docResubmitIntro: "سيرى الطيار ملاحظتك في التطبيق ليرفع ملفًا جديدًا.",
    docResubmitNote: "ملاحظة للطيار",
    docResubmitPlaceholder: "مثلًا: الصورة غير واضحة، صوّر البطاقة في إضاءة أفضل.",
    docResubmitNoteRequired: "اكتب ملاحظة للطيار لا تزيد على ٥٠٠ حرف.",
    docResubmitSend: "إرسال الطلب",
    docResubmitSent: "أُرسل طلب إعادة الرفع إلى الطيار",
    docCancel: "إلغاء",
    docGone: "تغيّر هذا المستند أو حُذف، فحُدّثت القائمة.",
    docReviewFailed: "تعذّر حفظ المراجعة: {error}",
    accessLogTitle: "سجل فتح المستندات",
    accessLogIntro: "من فتح كل مستند ومتى.",
    accessLogEmpty: "لم يفتح أحد أي مستند لهذا الطيار بعد.",
    accessLogRow: "فتح {viewer}: {kind}",
    accessLogUnknownViewer: "عضو لم يعد في الفريق",
    accessLogFailed: "تعذّر تحميل السجل: {error}",
    /** P19 (G18 RV18-L5): a review button's accessible name, «قبول: وجه البطاقة». */
    docActionAria: "{label}: {kind}",
    /** B6 D-39: 409 RESTAURANT_DELIVERY_CONTACT_REQUIRED, by what is missing. */
    contactRequiredBoth: "أضف رقم هاتف العميل وعنوانه قبل إرسال طلب التوصيل إلى المطبخ أو إغلاقه.",
    contactRequiredPhone: "أضف رقم هاتف العميل قبل إرسال طلب التوصيل إلى المطبخ أو إغلاقه.",
    contactRequiredAddress: "أضف عنوان العميل قبل إرسال طلب التوصيل إلى المطبخ أو إغلاقه.",
    // B6 · P24: the Delivery & takeaway tab, its Waiting-for-a-driver table
    // and the panel that offers one or many bags to a driver.
    tabDelivery: "التوصيل والتيك أواي",
    newDeliveryOrder: "طلب توصيل جديد",
    newPickupOrder: "طلب استلام جديد",
    waitingTitle: "بانتظار طيار",
    waitingAssignSelectedOne: "إسناد الطلب المحدد",
    waitingAssignSelectedTwo: "إسناد الطلبين المحددين",
    waitingAssignSelectedFew: "إسناد {count} طلبات محددة",
    waitingAssignSelected: "إسناد {count} طلبًا محددًا",
    waitingSelect: "تحديد الطلب رقم {sequence}",
    waitingEmpty: "كل طلبات التوصيل لها طيار",
    waitingNoName: "عميل بلا اسم",
    waitingNoAddress: "لا يوجد عنوان",
    kitchenReadyNow: "جاهز الآن",
    kitchenReadyFor: "جاهز منذ {count} د",
    kitchenReadyForHours: "جاهز منذ {count} س",
    kitchenPreparing: "قيد التحضير",
    kitchenNotSent: "لم يُرسل بعد",
    payCashOnDelivery: "الدفع عند الاستلام",
    payPaid: "مدفوع",
    assignTitle: "إسناد إلى طيار",
    assignMetaOne: "الطلب رقم {sequence} · {name}",
    assignMetaOneNoName: "الطلب رقم {sequence}",
    assignMetaTwo: "طلبان · {amount}",
    assignMetaFew: "{count} طلبات · {amount}",
    assignMeta: "{count} طلبًا · {amount}",
    assignStaffDrivers: "طيارو المطعم",
    assignOnRunOne: "في مشوار · طلب واحد",
    assignOnRunTwo: "في مشوار · طلبان",
    assignOnRunFew: "في مشوار · {count} طلبات",
    assignOnRun: "في مشوار · {count} طلبًا",
    assignHasOffer: "معه العرض الآن",
    assignPlatformNote:
      "تُسند منصة التوصيل طلباتها بنفسها، ويظهر طياروها في التسويات بعد أن يستلموا الطلبات.",
    assignConfirm: "إسناد إلى {name}",
    assignResultOffered: "عُرض على {name}",
    assignResultSkipped: "لم يعد ممكنًا إسناده، فقد تغيّرت حالته.",
    assignPartial: "لم يُعرض بعض الطلبات. السبب مكتوب بجانب كل طلب، والطلبات المتبقية ما زالت محددة.",
    toastAssignedBagsOne: "عُرض الطلب على {name}",
    toastAssignedBagsTwo: "عُرض الطلبان على {name}",
    toastAssignedBagsFew: "عُرضت {count} طلبات على {name}",
    toastAssignedBags: "عُرض {count} طلبًا على {name}",
    // P24 rework (G24): the map-pin mark for a pasted location, the short row button.
    waitingMapPin: "موقع على الخريطة",
    // P24b + P26: the Pickup orders table, the new-order flow straight into the
    // register, and the order panel's Delivery / Pickup head.
    pickupTitle: "طلبات الاستلام",
    pickupEmpty: "لا توجد طلبات استلام جارية",
    pickupOpenBill: "فتح الفاتورة",
    pickupOpenBillAria: "فتح فاتورة الطلب رقم {sequence}",
    deliveryOrdersTitle: "طلبات التوصيل",
    backToDelivery: "إلى التوصيل والتيك أواي",
    crumbNewDelivery: "طلب توصيل جديد",
    crumbNewPickup: "طلب استلام جديد",
    panelDelivery: "توصيل",
    panelPickup: "استلام",
    panelDeliveryInfo: "يُسلَّم إلى طيار بعد أن يجهّزه المطبخ",
    panelPickupInfo: "يستلمه العميل من الكاشير",
    panelPlatformInfo: "طلب {platform}",
    makePickup: "تحويله إلى استلام",
    makeDelivery: "تحويله إلى توصيل",
    contactName: "اسم العميل",
    contactPhone: "الهاتف *",
    contactAddress: "عنوان التوصيل *",
    contactMissing: "يلزم رقم الهاتف والعنوان قبل الإرسال",
    contactMissingToast: "أضف رقم الهاتف والعنوان أولًا",
    /** 2026-10-20 — the bill's own «إسناد إلى مندوب», before anything was sent (G24b M3's rule). */
    billAssignNotSent: "أرسل الطلب إلى المطبخ أولًا، ثم أسنده إلى طيار.",
    /** A hand-over's greyed close says «تعذّر التسليم: …» (the close sentence's lead swapped). */
    closeReasonLead: "تعذّر الإغلاق:",
    handOverReasonLead: "تعذّر التسليم:",
    /** 2026-10-20 — the close of a bag its driver ACCEPTED: handing it over (T16). The name stays on the state line. */
    handOverToDriver: "تسليم إلى الطيار",
    handOverToDriverAria: "تسليم الطلب رقم {sequence} إلى الطيار",
    /** 2026-10-20 — a waiting / with-driver row's number opens its bill. */
    openBillAria: "فتح الفاتورة #{sequence}",
    /** 2026-10-20 — the bill head's short dispatch chip (the name is in its title). */
    billChipOffered: "بانتظار قبول الطيار · {time}",
    billChipAccepted: "تم القبول من الطيار",
    billChipPickedUp: "مع الطيار",
    billChipArrived: "الطيار عند العميل",
    billChipDelivered: "تم التسليم",
    billChipFailed: "تعذّر التوصيل",
    billChipProblem: "بلاغ من الطيار",
    billChipNotAnswered: "لم يردّ الطيار",
    billChipDeclined: "رفض الطيار",
    billChipReturned: "أُعيد الطلب",
    billChipDeliveryCancelled: "أُلغي التوصيل",
    /** 2026-10-20 — the cashier's toast when an own driver marks a bag DELIVERED. */
    deliveredToastCash: "سلّم {name} الطلب رقم {sequence} · حصّل {amount}",
    deliveredToastInstapay: "سلّم {name} الطلب رقم {sequence} · تحويل إنستاباي {amount}",
    deliveredToast: "سلّم {name} الطلب رقم {sequence}",
    switchedToPickup: "صار الطلب استلامًا من الكاشير",
    switchedToDelivery: "صار الطلب توصيلًا",
    // P24b + P26 rework (G24b): the With-a-driver and Not-sent-yet tables, the sent crumb.
    withDriverTitle: "مع الطيار",
    /* إعادة تصميم التوصيل (2026-10-26): ثلاثة أعمدة لما يحدث للطلب — بانتظار
       مندوب، مع المندوب، بانتظار التحصيل — ثم الاستلام من المطعم. */
    laneWaitingHint: "حدّد عدة طلبات لإسنادها معًا",
    waitingItemsOne: "صنف واحد",
    waitingItemsTwo: "صنفان",
    waitingItemsFew: "{count} أصناف",
    waitingItems: "{count} صنفًا",
    waitingShowItems: "عرض أصناف الطلب رقم {sequence}",
    waitingItemsLoading: "جارٍ تحميل الأصناف…",
    waitingItemsFailed: "تعذّر تحميل الأصناف: {error}",
    waitingItemsNone: "لا توجد أصناف مُرسلة إلى المطبخ بعد.",
    laneWithHint: "قبِل · في الطريق · وصل",
    laneWithEmpty: "لا طلبات مع الطيارين الآن",
    laneCollectTitle: "بانتظار التحصيل",
    laneCollectHint: "النقد مع كل طيار حتى تسويته",
    laneCollectEmpty: "لا يوجد نقد مع الطيارين",
    laneCollectLabel: "للتحصيل",
    laneAge: "منذ {count} د",
    laneAgeHours: "منذ {count} س",
    laneUnsentChip: "لم يُرسل إلى المطبخ بعد · أرسله أولًا ثم أسنده",
    laneUnsentTip: "أرسل الطلب إلى المطبخ أولًا، ثم أسنده إلى طيار.",
    laneBagDeliveredCash: "سُلِّم · نقد",
    laneBagDeliveredPaid: "سُلِّم · مدفوع",
    laneBagReturned: "مرتجع",
    laneBagVoided: "ملغى",
    laneOutOne: "طلب واحد لا يزال معه · التسوية بعد انتهاء المشوار",
    laneOutTwo: "طلبان لا يزالان معه · التسوية بعد انتهاء المشوار",
    laneOutFew: "{count} طلبات لا تزال معه · التسوية بعد انتهاء المشوار",
    laneOut: "{count} طلبًا لا تزال معه · التسوية بعد انتهاء المشوار",
    laneSettleNamed: "تسوية {name}",
    pickupSectionTitle: "الاستلام من المطعم",
    pickupSectionHint: "يستلمها العميل أو طيار المنصة من الكاشير، بلا طيار من المطعم",
    pickupSourceDirect: "استلام",
    pickupSourceDelivery: "توصيل",
    pickupStateClosedUnpaid: "استلم · لم يُدفع",
    pickupStateEarlierDay: "من يوم سابق · سلّمه أو ألغِه",
    pickupStateCourier: "بانتظار طيار {platform}",
    pickupPayOnPlatform: "مدفوع على {platform}",
    pickupPayNotRecorded: "لم يُسجَّل الدفع",
    pickupPayOnCollect: "يُدفع عند الاستلام",
    pickupActCollect: "تحصيل {amount}",
    pickupActCourier: "سلّم لطيار {platform}",
    pickupActHandCollect: "تسليم والتحصيل",
    pickupRefOn: "رقم الطلب على {platform}",
    unsentTitle: "لم تُرسل إلى المطبخ بعد",
    crumbDeliveryNumber: "توصيل #{sequence}",
    crumbPickupNumber: "استلام #{sequence}",
    // B6 · P25 — the drivers' settlement cards and the settle panel (plan D-41 … D-44). MSA.
    settleTitle: "تسويات الطيارين",
    settleFiltersAria: "تصفية التسويات",
    settleFilterToSettle: "للتسوية",
    settleFilterReturns: "بها مرتجعات",
    settleFilterAll: "الكل",
    settleSearch: "ابحث عن طيار",
    settleChipStaff: "موظف",
    settleChipRider: "طيار",
    settleChipPlatform: "منصة",
    settleCashToCollect: "النقد المطلوب تحصيله",
    settleFlagReturned: "#{number} مرتجع",
    settleFlagVoided: "#{number} ملغى",
    settleFlagLeftOwed: "#{number} مؤجَّل",
    settleNoFlags: "لا مرتجعات ولا إلغاءات",
    settleButton: "تسوية",
    settleButtonAria: "تسوية حساب {name}",
    settleViewRun: "عرض المشوار",
    settleSettledAt: "سُوّي الحساب {time}",
    settleEmpty: "لا توجد تسويات مفتوحة",
    settleEmptyFiltered: "لا يطابق البحث أي طيار",
    settleLoadFailed: "تعذّر تحميل التسويات.",
    settleKindStaff: "طيار موظف",
    settleKindRider: "طيار خاص",
    settleKindPlatform: "طيار {platform}",
    settleOrders: "{count} طلبًا",
    settleOrdersNone: "لا طلبات",
    settleOrdersOne: "طلب واحد",
    settleOrdersTwo: "طلبان",
    settleOrdersFew: "{count} طلبات",
    settleRunTitle: "طلبات هذا المشوار",
    settleStateOnTheWay: "في الطريق",
    settleStateDelivered: "سُلِّم",
    settleStateReturned: "مرتجع",
    settleStateVoided: "ملغى",
    settleStateWaiting: "بانتظار الاستلام",
    settleStateWithRider: "مع الطيار",
    settleMethodCash: "نقدًا",
    settleMethodInstapay: "إنستاباي",
    settleMethodCard: "بطاقة",
    settleMethodPlatform: "مدفوع للمنصة",
    settleMethodPaid: "مدفوع",
    settleMethodSettledEarlier: "سُوّي سابقًا",
    settleMethodLeaveOwed: "يؤجَّل",
    settleMethodNotReceived: "لم يصل التحويل",
    settleMethodAria: "طريقة تسوية الطلب #{number}",
    settleViewProof: "عرض الإثبات",
    settleProofFailed: "تعذّر فتح إثبات التحويل: {error}",
    settleProofGone: "لم يعد إثبات التحويل متاحًا.",
    settleChangedAfterPickup: "تغيّرت الفاتورة بعد الاستلام: طُلب تحصيل {amount}، والمستحق الآن {expected}",
    settleLeftOwedBefore: "مؤجَّل من تسوية سابقة",
    settleDeliversOnSettle: "لم يسجّله السائق كمُسلَّم بعد — استلام نقده يسجّله مُسلَّمًا",
    settleSumAlreadyPaid: "سُلِّم ومدفوع بالبطاقة أو قبل خروجه",
    settleSumLeftOwed: "مؤجَّل",
    settleSumReturned: "مرتجع أو ملغى (لم يُحصَّل)",
    settleSumPayout: "صرف للطيار",
    settleSumCashExpected: "النقد المتوقَّع",
    settleCashHanded: "النقد المُسلَّم",
    settleDiffMatch: "يطابق النقد المتوقَّع",
    settleDiffShort: "ناقص {amount}",
    settleDiffOver: "زائد {amount}",
    settleReturnsTick: "استلمتُ الطعام الراجع",
    settlePayoutAdd: "إضافة صرف للطيار",
    settlePayoutAmount: "مبلغ الصرف",
    settlePayoutReason: "سبب الصرف",
    settlePayoutHint: "يُسجَّل حركةً «تكلفة توصيل» على الخزينة ويُخصم من النقد المتوقَّع.",
    settleConfirm: "تأكيد التسوية",
    settleWithDifference: "تسوية مع الفرق",
    settleWithDifferenceArmed: "اضغط مجددًا للتسوية مع الفرق",
    settleStillOut: "{count} طلبًا لا تزال في الطريق",
    settleStillOutOne: "طلب واحد لا يزال في الطريق",
    settleStillOutTwo: "طلبان لا يزالان في الطريق",
    settleStillOutFew: "{count} طلبات لا تزال في الطريق",
    settleGateTick: "أكِّد استلام الطعام الراجع",
    settleGateType: "اكتب النقد المُسلَّم",
    settleGatePayout: "أكمل مبلغ الصرف وسببه",
    settleGatePayoutTooLarge: "الصرف أكبر من النقد المستحق",
    settleGateNothing: "لا شيء للتسوية في هذا المشوار",
    settleGone: "لم يبقَ لدى هذا الطيار ما يُسوّى؛ حُدِّثت البطاقات.",
    settleToast: "سُوّي حساب {name}",
    settleStaleLead: "تغيّرت هذه التسوية منذ فتحها. راجع الأرقام المحدّثة ثم أكِّد:",
    settleChangeCash: "صار النقد المتوقَّع {expected} بدلًا من {amount}",
    settleChangeAdded: "أُضيف الطلب #{number} إلى المشوار",
    settleChangeRemoved: "خرج الطلب #{number} من المشوار",
    settleChangeDue: "الطلب #{number}: صار المستحق {expected} بدلًا من {amount}",
    settleChangeState: "تغيّرت حالة الطلب #{number}",
    settleErrorBagInvalid: "أحد طلبات هذا المشوار لم يعد ضمنه أو سُوّي من قبل. حُدِّثت البطاقة؛ راجعها ثم أكِّد.",
    settleErrorOutcome: "لم يعد هذا الاختيار متاحًا للطلب #{number}. حُدِّثت البطاقة؛ راجعها ثم أكِّد.",
    settleErrorOutcomeUnknown: "لم يعد أحد الاختيارات متاحًا. حُدِّثت البطاقة؛ راجعها ثم أكِّد.",
    settleErrorReturns: "أكِّد أن الطعام الراجع وصل قبل التسوية.",
    settleErrorPayout: "الصرف ({amount}) أكبر من النقد المستحق على الطيار ({expected}). سجّله حركةً يدويةً «تكلفة توصيل» على الخزينة، ثم سوِّ الحساب بلا صرف.",
  },
  // 2026-10-25 — the customer list (`manage/customers`) and a customer's drawer.
  customers: {
    title: "العملاء",
    description:
      "كل عميل له رقم هاتف، سواء طلب من صفحة المطعم أو سُجّل رقمه على فاتورة استلام أو توصيل.",
    noAccessTitle: "لا تملك صلاحية عرض هذه الصفحة",
    noAccessBody:
      "تتطلب هذه الشاشة صلاحية عرض العملاء. تواصل مع صاحب المطعم لإضافتها إلى دورك.",
    searchLabel: "ابحث بالاسم أو رقم الهاتف",
    searchPlaceholder: "الاسم أو رقم الهاتف",
    listLabel: "قائمة العملاء",
    loadFailed: "تعذّر تحميل العملاء: {error}",
    emptyTitle: "لا يوجد عملاء بعد",
    emptyBody:
      "يظهر هنا كل عميل يطلب من صفحة المطعم، أو يُكتب رقمه على فاتورة استلام أو توصيل.",
    noMatchTitle: "لا يوجد عميل يطابق البحث",
    noMatchBody: "جرّب جزءًا من رقم الهاتف أو بداية الاسم.",
    maskedNote:
      "بيانات العملاء مخفية لأن اشتراك المتجر مقيّد. جدّد الاشتراك لعرض الأسماء وأرقام الهواتف والعناوين.",
    colCustomer: "العميل",
    colPhone: "الهاتف",
    colBills: "الفواتير",
    colSpent: "إجمالي المدفوع",
    colLast: "آخر نشاط",
    colStatus: "الحالة",
    noName: "بلا اسم",
    hidden: "مخفي",
    sharesProfile: "الاسم من ملفه الشخصي",
    blocked: "محظور",
    noActivity: "لا يوجد",
    channelTakeaway: "استلام",
    channelDelivery: "توصيل",
    sourceTill: "شاشة البيع",
    sourceWebsite: "صفحة المطعم",
    openDetails: "تفاصيل {name}",
    detailsLoadFailed: "تعذّر تحميل بيانات العميل: {error}",
    notFoundTitle: "لم يُعثر على هذا العميل",
    notFoundBody: "ربما لم يعد ضمن عملاء هذا المتجر. ارجع إلى القائمة وحدّثها.",
    profileTitle: "البيانات",
    factName: "الاسم",
    factPhone: "الهاتف",
    call: "اتصال",
    factFirstSeen: "أول ظهور",
    factTags: "الوسوم",
    factStatus: "الحالة",
    statusActive: "نشط",
    statusBlockedSince: "محظور منذ {date}",
    factBlockReason: "سبب الحظر",
    addressesTitle: "العناوين",
    addressesEmpty: "لا توجد عناوين مسجّلة لهذا العميل.",
    addressNoText: "بلا عنوان مكتوب",
    addressRecipient: "المستلم: {name}",
    addressLastUsed: "آخر استخدام: {date}",
    openMap: "فتح في الخريطة",
    historyTitle: "سجل الطلبات",
    historyEmpty: "لا يوجد سجل لهذا العميل بعد.",
    historyBill: "فاتورة #{number}",
    historyBillPlain: "فاتورة",
    historyRequest: "طلب",
    historyOpenBill: "فتح الفاتورة #{number}",
    historyTruncated: "تُعرض أحدث السجلات فقط",
    billOpen: "مفتوحة",
    billClosed: "مغلقة",
    billSettled: "محصَّلة",
    billCancelled: "ملغاة",
    requestRequested: "بانتظار الرد",
    requestRejected: "مرفوض",
    requestExpired: "انتهى وقته",
    requestCancelled: "ملغى",
    blockAction: "حظر العميل",
    unblockAction: "إلغاء الحظر",
    blockHint:
      "الحظر يمنع طلبات هذا الرقم من صفحة المطعم فقط، ولا يمنع تسجيله على شاشة البيع.",
    blockTitle: "حظر {name}",
    blockIntro:
      "لن يتمكن هذا الرقم من إرسال طلبات جديدة من صفحة المطعم. لا يتأثر البيع على شاشة البيع، ويمكنك إلغاء الحظر في أي وقت.",
    blockReasonLabel: "سبب الحظر",
    blockReasonPlaceholder: "مثلًا: طلبات متكررة لم تُستلم",
    blockReasonRequired: "اكتب سببًا للحظر.",
    blockConfirm: "حظر",
    blockedToast: "حُظر العميل",
    unblockedToast: "أُلغي حظر العميل",
    blockFailed: "تعذّر تغيير الحظر: {error}",
  },
};

export const en: typeof ar = {
  systemScreens: {
    realtimeUnavailable: "Live updates are temporarily unavailable. Displayed information may be out of date. Try reconnecting.",
    realtimeUnconfigured: "Live updates are not set up on this device. Information refreshes when you return to the app or press Try again.",
    splashOpening: "Opening your shift…",
    splashPowered: "Powered by Trioty",
    loadingTitle: "Loading the page…",
    loadingBody: "Getting the latest orders, tables and stock",
    offlineBadge: "Offline · reconnecting",
    offlineTitle: "You're offline",
    offlineBody:
      "Check the Wi-Fi or network cable on this device. We'll reconnect automatically.",
    offlineRetry: "Try again",
    offlineChecking: "Checking connection…",
    offlineStill: "Still offline",
    offlineKept: "Anything you typed is kept on this screen and won't be lost.",
    offlineLastSynced: "Last synced {time}",
    offlineBackOnline: "Back online",
    notFoundBarTitle: "Page not found",
    notFoundTitle: "This page doesn't exist",
    notFoundBody:
      "The link may be old or the page was moved. If you think you should see it, ask a manager to check your access.",
    notFoundToday: "Go to Today",
    notFoundBack: "Go back",
  },
  notFound: {
    title: "This page doesn't exist",
    // UX-round D61 — the second clause used to explain the product's own taxonomy
    // ("this is not a permissions problem…"). That is a sentence for whoever
    // is being phoned, not for the owner who wanted their report. It is
    // replaced by the next step.
    body: "The link you opened isn't one of ours — it may have changed, or been mistyped. Check the link, or ask whoever sent it to you.",
    // The button goes to `/{locale}`, which re-decides by permission — these
    // name the stop it actually reaches, so "back to your screen" says where.
    action: "Back to your screen",
    actionPos: "Back to the till",
    actionKitchen: "Back to the kitchen screen",
    actionManage: "Back to the back office",
    detailToggle: "Details to send to support",
  },
  common: {
    appName: "Trioty Restaurant",
    /* D139 — an unknown cost (never purchased) is written "—", never 0. */
    costUnknownMark: "—",
    costUnknownHint: "Cost unknown — this ingredient has never been purchased.",
    loading: "Loading…",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    retry: "Try again",
    back: "Back",
    logout: "Log out",
    empty: "Nothing here yet",
    error: "Something went wrong",
    confirm: "Confirm",
    name: "Name",
    price: "Price",
    quantity: "Quantity",
    total: "Total",
    language: "Language",
    required: "Required",
    moreActions: "More actions",
    increase: "Increase",
    decrease: "Decrease",
    moveUp: "Move up",
    moveDown: "Move down",
    saved: "Saved",
    networkError:
      "Can't reach the server. Check your connection and try again.",
    unsavedTitle: "You have unsaved changes",
    unsavedBody: "Do you want to discard them?",
    discardChanges: "Discard changes",
    keepEditing: "Keep editing",
  },
  notifications: {
    title: "Notifications",
    enableSound: "Enable notification sound",
    muteSound: "Mute notification sound",
    soundUnavailable: "Notification sound unavailable",
    markAllRead: "Mark all as read",
    empty: "No new notifications",
    permissionPromptTitle: "Turn on restaurant notifications",
    permissionPromptBody:
      "We'll alert you about new requests and kitchen activity. The live screens still refresh normally if push is delayed.",
    permissionPromptAction: "Turn on notifications",
    permissionDenied:
      "Notifications are blocked in this browser. You can enable them in site settings.",
    unsupported:
      "This browser does not support notifications. In-app updates still work.",
    registrationError:
      "Notifications could not be enabled right now. In-app updates still work.",
    retryRegistration: "Try again",
    feedUnavailableTitle: "Notification history is unavailable",
    bootstrapUnavailable:
      "We could not connect to notification history. Restaurant screens and live updates still work normally.",
    feedUnavailable:
      "We could not load notification history. Try again; restaurant screens still refresh normally.",
    storeUnavailableTitle: "Restaurant unavailable",
    storeUnavailableBody:
      "This account cannot open the restaurant that sent the notification. Your current restaurant was not changed.",
    openFailedTitle: "Notification could not open",
    openFailedBody:
      "We could not switch to that restaurant right now. Try opening the notification again.",
    timeNow: "Just now",
    timeMinutesAgo: "{count}m ago",
    timeHoursAgo: "{count}h ago",
    timeDaysAgo: "{count}d ago",
    itemsRequestedTitle: "New table request",
    itemsRequestedBodyFallback: "A guest request is waiting for review.",
    itemsRequestedBodyOne: "A guest request with 1 item is waiting for review.",
    itemsRequestedBodyTwo:
      "A guest request with 2 items is waiting for review.",
    itemsRequestedBodyFew:
      "A guest request with {count} items is waiting for review.",
    itemsRequestedBody:
      "A guest request with {count} items is waiting for review.",
    onlineDeliveryRequestedTitle: "New delivery order",
    onlineTakeawayRequestedTitle: "New takeaway order",
    onlineOrderRequestedBodyFallback:
      "An online order is waiting to be accepted.",
    onlineOrderRequestedBodyOne:
      "An online order with 1 item is waiting to be accepted.",
    onlineOrderRequestedBodyTwo:
      "An online order with 2 items is waiting to be accepted.",
    onlineOrderRequestedBodyFew:
      "An online order with {count} items is waiting to be accepted.",
    onlineOrderRequestedBody:
      "An online order with {count} items is waiting to be accepted.",
    paymentSubmittedTitle: "New payment request",
    paymentSubmittedBody: "A guest submitted a payment for review.",
    kitchenTicketsTitle: "New kitchen work",
    kitchenTicketsBodyFallback: "New tickets arrived on the kitchen board.",
    kitchenTicketsBodyOne: "1 new ticket arrived on the kitchen board.",
    kitchenTicketsBodyTwo: "2 new tickets arrived on the kitchen board.",
    kitchenTicketsBodyFew: "{count} new tickets arrived on the kitchen board.",
    kitchenTicketsBody: "{count} new tickets arrived on the kitchen board.",
    itemsReadyTitle: "Items ready to serve",
    itemsReadyBodyFallback: "Kitchen items are ready to be served.",
    itemsReadyBodyOne: "1 item is ready to be served.",
    itemsReadyBodyTwo: "2 items are ready to be served.",
    itemsReadyBodyFew: "{count} items are ready to be served.",
    itemsReadyBody: "{count} items are ready to be served.",
  },
  auth: {
    loginTitle: "Restaurant staff login",
    phone: "Phone number",
    password: "Password",
    submit: "Log in",
    failed: "That phone number and password don’t match. Check both and try again.",
    firstPasswordTitle: "Choose your password",
    firstPasswordBody:
      "You signed in with a temporary password. Replace it before continuing.",
    temporaryPassword: "Temporary password",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    saveNewPassword: "Save password",
    firstPasswordFailed:
      "We couldn't change the password. Use at least 8 characters, make sure the new passwords match, and check the temporary password.",
    wrongStoreTitle: "That store is not a restaurant",
    wrongStoreBody: "Pick the restaurant you want to work on.",
    noRestaurantTitle: "This account has no restaurant",
    noRestaurantBody:
      "This account is only linked to regular stores. If you signed into a different account from the seller portal, that is why — both apps share one sign-in. Log out and sign in with the restaurant account, or ask the restaurant owner to add you.",
    switching: "Switching…",
    // The redesigned login (claude.ai/design "Login", 2026-10-06).
    welcomeBack: "Welcome back",
    loginSub: "Log in with your mobile number to open your shift.",
    mobileNumber: "Mobile number",
    loggingIn: "Logging in…",
    forgotPassword: "Forgot your password? Ask the owner or a manager to reset it from Team.",
    loggedOut: "You’ve logged out. This device is ready for the next person.",
    // 403 STORE_DEACTIVATED: an admin switched the restaurant off.
    storeDeactivated: "This store is currently deactivated. Contact Trioty support.",
    passwordSaved: "Password saved. Log in with your new password.",
    showPassword: "Show password",
    hidePassword: "Hide password",
    setOwnPassword: "Set your own password",
    setOwnPasswordSub: "Your temporary password works once. Choose one only you know.",
    temporaryPasswordHint: "The one you got by SMS or from your manager.",
    atLeastEight: "At least 8 characters",
    atLeastEightMet: "✓ At least 8 characters",
    moreCharactersOne: "1 more character",
    moreCharactersTwo: "2 more characters",
    moreCharactersFew: "{count} more characters",
    moreCharacters: "{count} more characters",
    notMatchingYet: "Doesn’t match yet",
    newPasswordTooShort: "The new password needs at least 8 characters.",
    newPasswordsDiffer: "The two new passwords don’t match.",
    saving: "Saving…",
    saveOwnPassword: "Save new password",
    afterSaving: "After saving, you’ll be logged out and asked to log in with the new password.",
    backToLogin: "Back to log in",
    poweredBy: "Powered by Trioty",
    slideFloorTitle: "Run the whole restaurant from one screen",
    slideFloorBody: "Floor, kitchen, delivery and the cash drawer stay in sync.",
    slideKitchenTitle: "Tickets go straight to the kitchen",
    slideKitchenBody: "Cooks see what to make next, oldest first.",
    slideDeliveryTitle: "Delivery orders, tracked to the door",
    slideDeliveryBody: "Takeaway, delivery and platform orders in one list.",
    slideCashTitle: "Every pound accounted for",
    slideCashBody: "Split bills, InstaPay and blind drawer counts.",
  },
  /* "Today" — the back office's home (D111, founder 2026-10-02). Each figure
     is the figure of the page its tile opens, under that page's own name:
     "Gross sales" is what the Z calls this number. */
  today: {
    title: "Today",
    subtitle: "A quick look at service, sales and what needs restocking.",
    statSales: "Gross sales today",
    statItems: "Items available",
    // Audit round 2, Phase 3 — of those available, how many carry an
    // ingredient the kitchen marked finished (still sellable, B18). Not
    // printed at zero.
    statItemsIngredientOut: "{count} of them with an ingredient out",
    statItemsIngredientOutOne: "1 of them with an ingredient out",
    statItemsIngredientOutTwo: "2 of them with an ingredient out",
    statReorder: "Ingredients to reorder",
    statOpenBills: "Bills still open",
    statOpenDrawers: "Drawers still open",
    attentionTitle: "Needs attention",
    levelOnHand: "On hand",
    levelWarnAt: "Warn at",
    attentionEmpty: "All stock levels look good.",
    attentionNoIngredients: "No ingredients recorded yet.",
    topTitle: "Top sellers today",
    topEmpty: "Nothing sold yet today.",
    soldOne: "1 sold",
    soldTwo: "2 sold",
    soldFew: "{count} sold",
    sold: "{count} sold",
    loadFailed: "Some of today's figures could not be loaded.",
    noAccessTitle: "Nothing on this page is open to you",
    noAccessBody:
      "Today shows sales, stock and the menu. Ask the restaurant owner to add one of those to your role.",
  },
  nav: {
    expand: "Show menu",
    copyCode: "Copy code",
    codeCopied: "Copied",
    copyMenuLink: "Copy menu link",
    menuLinkCopied: "Link copied",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    collapse: "Hide menu",
    switchStore: "Switch store",
    currentStore: "Current",
    /* UX-round D62 — `nav.pos` ("Tables") went with the second row. The floor had two
       rows: "Tables" under Service and "Floor & tables" under Store, on the
       same areas and the same tables. One row now, keyed `floor`. */
    groupService: "Service",
    groupMoney: "Money",
    more: "More",
    customize: "Customize menu",
    customizeShow: "Show",
    customizeHide: "Hide",
    moveUp: "Move up",
    moveDown: "Move down",
    groupMenu: "Menu",
    groupInventory: "Stock",
    groupStore: "Store",
    groupReports: "Reports",
    manage: "Manage",
    cashierWorkspace: "Cashier workspace",
    // The back office's home (D111).
    today: "Today",
    menu: "Menu",
    /* One floor row (UX-round D62), named like the screen itself — not "Floor & tables"
       sitting a group away from "Tables". */
    floor: "The floor",
    ingredients: "Stock",
    receiveDelivery: "Receive a delivery",
    stocktake: "Stocktake",
    suppliers: "Suppliers",
    aggregators: "Delivery platforms",
    addons: "Add-ons",
    tiers: "Pricing",
    settings: "Settings",
    staff: "Team",
    categories: "Sections",
    invoices: "Invoices",
    kitchen: "Kitchen",
    reports: "Reports",
    reportItems: "Item profitability",
    reportStaff: "Voids, comps & refunds",
    reportDayparts: "Dayparts",
    reportRemakes: "Remakes",
    reportSpeed: "Service speed",
    reportRefunds: "Refunds",
    feedback: "Guest feedback",
    customers: "Customers",
    // Short for the sidebar; the page's own heading carries the full name.
    reportInputVat: "Input VAT",
    shift: "Drawer",
    shiftHistory: "Drawer history",
    // The Z (D38–D41): named by letter because accountants ask for it that
    // way, but the word an owner reads leads.
    dayClose: "Day close-out (Z)",
    label: "Main navigation",
  },
  navPaper: {
    reports: "Sales & costs",
    floor: "Floor",
    shift: "Cash drawer",
    categories: "Categories",
    reportInputVat: "Purchase tax",
    reportDayparts: "Peak hours",
    reportStaff: "Voids, comps & returns",
    shiftHistory: "Drawer log",
    dayClose: "End of day",
  },
  olderCheques: {
    open: "Older cheques",
    openHint: "Closed cheques not settled for more than two days",
    title: "Cheques not settled for over two days",
    note: "A closed cheque stays on its table for two days after it is closed, then moves here until it is settled. Open one to settle it.",
    emptyTitle: "No older cheques",
    emptyBody: "Every closed cheque not yet settled is still on the floor.",
    closedOn: "Closed {date}",
    due: "{amount} due",
    // P9.6: the cheques come 50 at a time; this loads the next 50.
    more: "Show more",
    loadingMore: "Loading…",
  },
  paperFloor: {
    tabsLabel: "Floor view",
    tabTables: "Tables",
    tabKitchen: "Kitchen",
    statusWaiting: "Waiting",
    statusReady: "Ready to serve",
    statusPreparing: "Preparing",
    statusEating: "Eating",
    due: "{amount} due",
    paid: "Paid",
    paidSoFar: "{amount} paid so far",
    served: "{served}/{total} served",
    servedHint: "Filled = served",
    freeTapToOpen: "Free · tap to open",
    areaBusy: "{busy} of {tables} busy",
    newBillHere: "New bill",
    newBillHereHint: "Start a second bill at this table",
    headGuests: "Guests: {number}",
    kitchenEmpty: "Nothing in the kitchen right now",
    // Audit round 2, Phase 3 — the kitchen line under the status word, in the
    // board's own lane words; a zero is not printed at all.
    kitchenPreparing: "{count} preparing",
    kitchenPreparingOne: "1 preparing",
    kitchenPreparingTwo: "2 preparing",
    kitchenReady: "{count} ready",
    kitchenReadyOne: "1 ready",
    kitchenReadyTwo: "2 ready",
    // Decided by the server with the kitchen board's rule, at the read.
    late: "Late",
    // 2026-10 floor redesign: one coloured status line with its time, and the
    // next step. The time stays neutral until it passes its limit.
    needsYou: "Needs you now",
    needsYouLabel: "Filter tables by status",
    groupReady: "Ready to serve",
    groupBill: "Bill",
    groupSeated: "No order",
    groupKitchen: "In the kitchen",
    groupFree: "Free",
    lateTables: "Late: {count}",
    areaBusyLate: "{busy} of {tables} busy · late: {late}",
    cardBill: "Bill on the table",
    cardStale: "Bill changed after printing",
    cardPrebill: "Asked for the bill",
    cardSeated: "Seated, no order",
    since: "{count} min",
    sinceHours: "{count} h",
    actServe: "Serve",
    actCollect: "Collect",
    actPrint: "Print bill",
    actTakeOrder: "Take order",
    actReview: "Review order",
    actOpen: "Open",
    servedOf: "{served} of {total} served",
    freeLabel: "Free",
    legendLabel: "Colour key",
    legendEating: "Eating: nothing needed",
    legendLate: "Red ring: past its limit",
  },
  paperKitchen: {
    serveSingle: "Serve 1",
    serveAll: "Serve all {count}",
    serveAllTwo: "Serve both",
    serveAllFew: "Serve all {count}",
    startSingle: "Start 1",
    startAll: "Start all {count}",
    startAllTwo: "Start both",
    startAllFew: "Start all {count}",
    readySingle: "1 is ready",
    readyAll: "All {count} ready",
    readyAllTwo: "Both ready",
    readyAllFew: "All {count} ready",
    pickSingle: "Select 1",
    pickAll: "Select all {count}",
    pickAllTwo: "Select both",
    pickAllFew: "Select all {count}",
    choiceLabel: "How many {name}?",
    openBoard: "Open full board",
    emptyAllBody:
      "Nothing has been fired to the kitchen yet. A waiter's order shows up here the moment it is sent.",
  },
  paperRegister: {
    back: "Back",
    backToTables: "to Tables",
    backToSections: "to Categories",
    sectionsChip: "Sections",
    clearSearch: "Clear search",
    sectionDishes: "{count} items",
    sectionDishesOne: "1 item",
    sectionDishesTwo: "2 items",
    sectionDishesFew: "{count} items",
    fromPrice: "from {amount}",
    searchDishes: "Search dishes",
    soldOut: "Sold out",
    noMatch: 'No dish matches "{query}"',
    sectionEmpty: "Nothing in this category yet",
    otherDishes: "Other dishes",
    holdHint: "Press and hold for more options",
    update: "Update · {amount}",
    editLine: "Change this dish",
    dishActions: "Options for {name}",
    basePrice: "Base price {amount}",
    included: "Included",
    addToOrder: "Add to order · {amount}",
    quantity: "Quantity",
    fewer: "Fewer",
    more: "More",
    optionQuantity: "How many {name}",
    handPrice: "Price set by hand: {amount}",
    swapSet: "A swap is set on this dish",
    ago: "{age} ago",
    justNow: "just now",
  },
  shell: {
    openMenu: "Open the menu",
    closeMenu: "Close the menu",
    languageArabic: "Arabic",
    languageEnglish: "English",
  },
  units: {
    g: "g",
    kg: "kg",
    ml: "ml",
    piece: "pc",
  },
  pos: {
    currentOrder: "Current order",
    lineCount: "{count} items",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    lineCountFew: "{count} items",
    // B117/A2 — and «1 items» in English, which is the B67 defect again.
    lineCountOne: "1 item",
    lineCountTwo: "2 items",
    // D45b · ruling A1 — the menu's count, qualified by where it sits. See the
    // Arabic for why the family had to split.
    menuResultCount: "{count} on the menu",
    menuResultCountFew: "{count} on the menu",
    menuResultCountOne: "1 on the menu",
    menuResultCountTwo: "2 on the menu",
    lineLess: "Less",
    lineMore: "More",
    lineRemove: "Remove item",
    notSentYet: "Not sent yet",
    offPremiseEmptyClose:
      "Nothing is on this bill to close it on — cancel it instead.",
    panelSectionSent: "Sent to the kitchen",
    panelSectionOnTable: "On the table",
    panelSectionOnTableOffPremise: "Ready to hand over",
    menuSearch: "Search the menu",
    menuAllCategories: "All sections",
    menuHasAddons: "Addons",
    menuSoldOut: "Out",
    // UX-round D45 — see the Arabic: the chip beside it already reads "Out".
    menuSoldOutHint: "This item is out today",
    // Combos on the till (S16, D52).
    comboBadge: "Combo",
    comboSoldOutHint: "A dish inside this combo has run out",
    menuMarkSoldOut: "Turn {name} off",
    menuUnmarkSoldOut: "Turn {name} on",
    menuMarkSoldOutShort: "Turn off",
    menuUnmarkSoldOutShort: "Turn on",
    menuMarkSoldOutBody:
      '"{name}" stops being orderable on every till and on the diners\' QR menu until you turn it on again. It is not removed from the menu.',
    menuUnmarkSoldOutBody:
      '"{name}" becomes orderable again on every till and on the QR menu.',
    menuMarkedSoldOutToast: "Turned off",
    menuMarkedAvailableToast: "Turned on",
    menuNoResults: "No matching items",
    addonRequired: "*",
    addonUpTo: "(up to {max})",
    language: "Language",
    lineActionsTitle: "Line actions",
    lineActionEdit: "Change dish",
    lineActionNote: "Kitchen note",
    noteClearText: "Clear note",
    lineActionsNone: "Nothing to change on this line.",
    lineCancel: "Cancel item",
    logoutConfirmTitle: "Log out?",
    logoutConfirmBody: "You'll need your phone number and password to sign back in.",
    theme: "Theme",
    sendToKitchen: "Send to kitchen",
    sendToCashier: "Send to cashier",
    pickedItems: "Selected: {number}",
    pickedClear: "Clear selection",
    pickItem: "Select this item",
    cashierRequested: "Sent to the cashier at {time} — waiting to go to the kitchen",
    cashierRequestedToast: "Sent to the cashier",
    cashierRequestAlert: "{place}: an order is ready to send to the kitchen",
    cashierWaitingBadge: "Waiting for cashier",
    closeNeedsCashier: "Needs cashier permission",
    switchTable: "Switch table",
    tableOccupied: "Occupied",
    // F1 (unit 4): where the food goes. NOT the same question as who brought
    // the customer — an aggregator order is a delivery order, and collapsing
    // the two makes both unanswerable.
    channelDineIn: "Dine-in",
    // F1 (unit 4): the bills that sit at no table. The buttons live ABOVE the
    // floor rather than inside it — a takeaway order does not belong to an
    // area, and putting it in one would be the dummy table again.
    offPremiseTakeawayStart: "New takeaway",
    customerSuggestionLast: "Last order {date}:",
    customerSuggestionUse: "Use it",
    staffMealStart: "Staff meal",
    staffMealBannerTitle: "Staff meal",
    staffMealBannerBody:
      "Pick the dishes from the menu, then “Send staff meal”: they go to the kitchen and are all recorded as staff meals.",
    staffMealSend: "Send staff meal",
    staffMealFinish: "Close the staff meal",
    staffMealSentToast: "Staff meal sent and recorded as staff meals.",
    staffMealSentOpenToast:
      "Staff meal sent and recorded as staff meals. Close it from here once the kitchen starts it.",
    offPremiseDeliveryStart: "New delivery",
    offPremiseTakeawayTitle: "Takeaway order",
    offPremiseDeliveryTitle: "Delivery order",
    offPremiseNameLabel: "Customer name (optional)",
    offPremisePhoneLabel: "Phone (optional)",
    offPremiseAddressLabel: "Address (optional)",
    // The customer, on screen. These three were typed and rendered nowhere.
    customerBlockTitle: "Customer",
    customerName: "Name",
    customerPhone: "Phone",
    customerAddress: "Address",
    customerNone: "Nobody recorded who this is for",
    customerEdit: "Edit",
    customerEditTitle: "Customer details",
    customerEditHint:
      "Corrects what was typed wrong — the delivery fee and the platform are not changed from here.",
    customerSaved: "Customer details updated",
    // T6 — the fee, the channel and the platform, corrected while the bill is
    // still OPEN. Not "Edit" on the customer block: this one reprices the
    // bill, so it has its own control and the cashier's rung.
    chargesEdit: "Charges",
    chargesEditTitle: "Charges and channel",
    chargesChannelLabel: "Channel",
    chargesHint:
      "Reprices the delivery fee, service charge and commission on this open bill only — no dish price changes.",
    chargesFeeInvalid: "Enter zero or a positive number, or leave it blank.",
    chargesSaved: "Charges updated",
    // The waiting state inside the modal, while the add-on groups arrive. The
    // waiter has already tapped; something has to hold their place or the tap
    // reads as dead and gets repeated.
    addonLoading: "Loading this dish's options…",
    // A delivery with neither is an order nobody can deliver.
    undeliverableWarning:
      "A delivery with no phone and no address — nobody can deliver it.",
    // T11 — the same warning when only ONE is missing: it names the gap
    // rather than saying "phone or address" beside a phone that is there.
    undeliverableWarningNoPhone:
      "A delivery with no phone — the rider can't reach the customer.",
    undeliverableWarningNoAddress:
      "A delivery with no address — the rider has nowhere to go.",
    offPremiseFeeLabel: "Delivery fee",
    // Blank = the store's default. A different answer from 0, which means this
    // delivery is free.
    offPremiseFeeHint: "Leave blank to use the store's default ({default})",
    // The store's default is ZERO, so "leave blank" means free. Say so, rather
    // than let a cashier believe a number is waiting behind an empty box.
    offPremiseFeeHintFree:
      "This store's delivery fee is zero — leave it blank and delivery is free.",
    // F2 — where the order came from. "Direct" is the store's own phone, with
    // no commission on it.
    // B43 — splitting a bill between families. The split is a MARKING on the
    // plates, not an arithmetic: a family pays for what it ordered, and the
    // money falls out of the marking.
    // B3 — a partial payment: half cash half card, or a family paying for its
    // own plates.
    // B89 — the bill was part-paid and then shortened, so the restaurant is
    // holding money nobody owes it.
    overpaidLabel: "Owed back",
    overpaidNotice:
      "The bill was shortened after payment — you are holding {amount} for the customer",
    overpaidAction: "Give it back",
    overpaidTitle: "Give the extra back",
    overpaidBody:
      "The bill is now {total} and {paid} has been paid. The difference goes back the way it came, and settles the bill.",
    overpaidConfirm: "Give back {amount}",
    overpaidDone: "The money went back and the bill is settled",
    overpaidNeedsRefunder:
      "Handing money back needs the refund permission — ask a manager.",
    partialTitle: "Take part of the bill",
    partialPaidSoFar: "Paid so far",
    partialOutstanding: "Still owed",
    partialWhoIsPaying: "Who is paying",
    partialTypedAmount: "An amount",
    // Why there is no field once a party is picked: the figure comes from the
    // family's own plates, and a till that could type it could type it wrong.
    partialPartyNote:
      "The amount comes from the party's plates — it is not typed",
    partialAmountLabel: "Amount received",
    partialTakeTheRest: "All of it",
    partialOverChargeHint:
      "A card cannot be charged more than the bill still owes",
    partialFinishes: "This payment finishes the bill",
    partialLeaves: "{amount} will still be owed",
    partialConfirm: "Take the payment",
    partialOpen: "Part payment",
    partialTakenToast: "The payment is recorded",
    // A family that is square. Without this line its chip looked exactly like
    // a family that had not paid — and the cashier took its money again.
    partialPartyPaid: "Paid",
    partialPartyPaidOf: "Paid {paid} of {amount}",
    // The most dangerous line on a screen that takes money: plates nobody has
    // claimed, on a table that is about to leave.
    partialUnclaimedOne: "1 plate is nobody's — {amount} is on no family",
    partialUnclaimedTwo: "2 plates are nobody's — {amount} is on no family",
    partialUnclaimed: "{count} plates are nobody's — {amount} is on no family",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    partialUnclaimedFew:
      "{count} plates are nobody's — {amount} is on no family",
    partyTitle: "Who ordered what",
    // QA3 — "Split 1 ways" was reachable: one family with every plate on it.
    partyAllMarkedOne: "Every plate is marked to one family",
    partyAllMarkedTwo: "Split 2 ways — every plate is marked",
    partyAllMarkedFew: "Split {count} ways — every plate is marked",
    partyAllMarked: "Split {count} ways — every plate is marked",
    // QA5 — see the Arabic note: two counts, one key, and the families went bare.
    partySomeUnmarked: "Split across {ways} — {plates} plates still nobody's",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    partySomeUnmarkedFew:
      "Split across {ways} — {plates} plates still nobody's",
    // The same line for one and for two. "1 plates" reads as a bug on a screen
    // about money, and this one renders on the order panel of every split bill.
    partySomeUnmarkedOne: "Split across {ways} — 1 plate is still nobody's",
    partyWaysOne: "1 family",
    partyWaysTwo: "2 families",
    partyWaysFew: "{count} families",
    partyWays: "{count} families",
    partySomeUnmarkedTwo: "Split across {ways} — 2 plates are still nobody's",
    partyPickPlates: "Pick the plates",
    partyAssignTo: "Assign to",
    partyNew: "New party",
    partyClear: "Clear the mark",
    partyAssign: "Mark plates",
    partyLabelPlaceholder: "Party name — e.g. \u0022the ladies\u0022",
    // Two keys, not one: there is no plural machinery in this runtime, and
    // "1 plates" reads as a bug on a screen about money.
    partyPlateCountNone: "No plates",
    partyPlateCountOne: "1 plate",
    partyPlateCountTwo: "2 plates",
    partyPlateCount: "{count} plates",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    partyPlateCountFew: "{count} plates",
    // The most important line here: an unmarked plate can walk out unpaid.
    partyUnclaimedOne: "1 plate is still nobody's",
    partyUnclaimedTwo: "2 plates are still nobody's",
    partyUnclaimed: "{count} plates are still nobody's",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    partyUnclaimedFew: "{count} plates are still nobody's",
    partyAssigned: "The plates are marked",
    offPremiseAggregatorLabel: "Order came from",
    offPremiseAggregatorDirect: "Direct",
    // Says what the commission DOES rather than that it exists: it comes off
    // the night's takings, and the platform settles later — so it never reaches
    // the drawer.
    offPremiseAggregatorHint:
      "Commission comes off the night's takings, and the platform's money never reaches the drawer",
    // D163 (F39) — an optional reference on a card or InstaPay tender; never required.
    tenderReferenceLabel: "Transaction reference (optional)",
    tenderReferenceHint:
      "The approval number on the card terminal's slip, or the InstaPay transfer's reference.",
    tenderReference: "Transaction reference",
    // T13 — the platform's order number: what the rider at the door says.
    platformOrderRefLabel: "Platform order no. (optional)",
    // T14 — with a platform the fee is 0 on the bill and the service charge is off.
    platformFeeHint:
      "Leave blank — the platform carries the delivery fee, so it is 0 on this bill.",
    platformNoServiceCharge: "No service charge on platform bills.",
    // T6 — the channel or the platform changed after plates reached the kitchen.
    chargesReprintWarning:
      "Reprint the kitchen chits after this change — the plates already sent carry the old details.",
    // T16 — who the bag left with. A field, not a lifecycle: no riders, no tracking.
    handOverTitle: "Close and hand over",
    handedToLabel: "Handed to (optional)",
    handedToPlaceholder: "Rider's name, or “customer”",
    handedToRecent: "Recent",
    handOverHint:
      "Only who the bag left with is recorded — no riders, no tracking.",
    handOverToPlatform: "Close & hand to {name}",
    handOverToPlatformHint:
      "Closes the bill and records it as settled by {name} for the {amount} due — nothing enters the drawer.",
    offPremiseHandedTo: "out with {name}",
    platformRefShort: "Platform ref {ref}",
    outWithRiders: "Out with riders: {count} · {amount}",
    // T15 — the commission base, on the settle sheet and the bill detail.
    aggregatorSettleHintBase: "The rate is taken on {base}.",
    commissionBaseFood: "the food only",
    commissionBaseTotal: "the whole order value",
    commissionRateOnBase: "{percent}% commission on {base}",
    // T17 — the rider block on the receipt.
    receiptToCollect: "TO COLLECT",
    offPremiseStart: "Start order",
    channelTakeaway: "Takeaway",
    channelDelivery: "Delivery",
    // T8 — a takeaway or delivery bill that is CLOSED and not yet paid: the
    // bag has left and the bill stays on the strip until the money is in.
    offPremiseOutStatus: "Out · unpaid",
    offPremiseClosedSince: "closed {count} min ago",
    offPremiseClosedSinceHours: "closed {count} h ago",
    // F24 (§5.12) — the customer's own request for a bag, on the till.
    onlineOrdersTitle: "Online orders",
    onlineOrdersWaiting: "{count} waiting for an answer",
    onlineOrdersWaitingOne: "1 waiting for an answer",
    // Spelled, as the Arabic dual is — the parity spec pins the placeholders.
    onlineOrdersWaitingTwo: "2 waiting for an answer",
    onlineOrdersWaitingFew: "{count} waiting for an answer",
    onlineOrdersEmpty: "No online orders waiting right now.",
    onlineOrdersTaking: "Taking orders from the storefront",
    onlineOrdersPaused: "Paused — the storefront is not taking new orders",
    onlineOrdersPauseLabel: "Take orders from the storefront",
    onlineOrdersPauseAction: "Pause",
    onlineOrdersResumeAction: "Resume",
    onlineOrderAccept: "Accept",
    onlineOrderReject: "Reject",
    onlineOrderReadyIn: "Ready in",
    onlineOrderMap: "Map",
    onlineOrderMapApproximate: "Approximate spot — call the customer",
    onlineOrderVerified: "Verified number",
    onlineOrderMinutesChip: "{minutes} min",
    onlineOrderExpiresIn: "expires in {minutes} min",
    onlineOrderExpiresSoon: "expires in under a minute",
    onlineOrderExpired: "expired",
    onlineOrderNoteLabel: "Note",
    onlineOrderRejectTitle: "Reject the order",
    onlineOrderRejectReasonLabel:
      "Reason (optional) — the customer will see it",
    onlineOrderRejectReasonPlaceholder: "e.g. the dish ran out",
    onlineOrderRejectConfirm: "Reject order",
    onlineOrderAcceptedToast: "Accepted — invoice #{sequence} is open",
    onlineOrderRejectedToast: "Order rejected",
    // B7 — a delivery sent for someone else: the card's contact is the
    // recipient; this line names who ordered it, phone masked to 4 digits.
    onlineOrderOrderedBy: "Ordered by:",
    // B7 — on a card sent for someone else: ring the person at the door.
    onlineOrderMapApproximateRecipient: "Approximate spot — call the recipient",
    tableChequeOut: "Cheque out",
    // B8: a table can carry several open bills now — two families on one big
    // table, or a table that turned while the first cheque was still out. The
    // tile reports the COUNT rather than one bill's number, because one number
    // out of two is a half-truth about the table.
    tableBillCount: "{count} bills",
    // QA3 — both chips are gated on `> 1`, so the dual is the smallest
    // value either can ever show and it was reading as a plural.
    tableBillCountOne: "1 bill",
    tableBillCountTwo: "2 bills",
    tableBillCountFew: "{count} bills",
    tableChequeCount: "{count} cheques out",
    tableChequeCountOne: "1 cheque out",
    tableChequeCountTwo: "2 cheques out",
    tableChequeCountFew: "{count} cheques out",
    tableChequeOutHint: "Tap to take payment",
    // A cheque-out table still gets seated; this is the deliberate second tap.
    tableNewOrder: "New order",
    tapToStart: "Tap to start an order",
    startOrderTitle: "This table is still free",
    unsentLeaveTitle: "Some items were never sent",
    unsentLeaveBody:
      "The items you added haven't reached the kitchen, and leaving this screen discards them. Send them first if the order is done.",
    unsentLeaveConfirm: "Leave and discard",
    unsentLeaveStay: "Keep the order",
    startOrderHint: "The first item you add opens an invoice for this table.",
    startOrderNeedsPermission:
      "You need permission to open an invoice to start an order here.",
    tableNotFound: "That table doesn't exist",
    paidTitle: "Paid",
    changeDue: "Change due",
    newOrder: "New order",
    basePrice: "Base",
    gridTitle: "Tables",
    allAreas: "All areas",
    backToTables: "Tables",
    openForMinutes: "{count}m",
    openForHours: "{count}h",
    tileAgeMinutes: "{count}m",
    tileAgeHours: "{count}h",
    tileAgeDays: "{count}d",
    summaryOpen: "{count} open",
    summaryOpenOne: "1 open",
    summaryOpenTwo: "2 open",
    tableFree: "Free",
    invoiceNumber: "Invoice #{sequence}",
    openTable: "Open table",
    pendingCount: "{count} waiting",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    pendingCountFew: "{count} waiting",
    // B117/Q8 — a guest says they paid and nobody has looked.
    paymentClaimBadge: "Says they paid",
    pendingCountOne: "1 waiting",
    pendingCountTwo: "2 waiting",
    running: "Running total",
    tillShowBill: "The bill",
    tillShowMenu: "The menu",
    tillUnsentCountOne: "1 not sent",
    tillUnsentCountTwo: "2 not sent",
    tillUnsentCountFew: "{count} not sent",
    tillUnsentCount: "{count} not sent",
    tillBackToFloor: "Back to the floor",
    addItems: "Add items",
    chooseRequiredAddons: "Choose the required options first.",
    addonSwapCollision:
      "The greyed-out options change the same ingredient as the swap on this plate. Leave them, or go back and change the swap.",
    addonDecrease: "Less",
    addonIncrease: "More",
    swapTitle: "Swap an ingredient",
    swapRemoveLabel: "Swap out",
    swapAddLabel: "Use instead",
    swapPick: "Choose",
    swapSearch: "Search ingredients",
    swapNoMatch: "No ingredient by that name",
    // Two different claims on one chip row, and the difference IS B18's
    // answer: the first is arithmetic over a balance nobody may have counted,
    // the second is a cook standing in front of an empty tray. One word for
    // both teaches the floor to skip past both.
    swapOutOfStock: "Stock at zero",
    swapMarkedOut: "Kitchen says finished",
    swapAddAnother: "+ Another swap",
    // ── An ingredient the kitchen has called finished (B18) ───────────────
    // Not the dish's "sold out": an 86'd dish cannot be sold at all, while
    // this dish sells normally and the waiter is simply told first.
    menuIngredientOut: "Ingredient out",
    menuIngredientOutHint: "Finished in the kitchen: {items}",
    ingredientWarnTitle: "An ingredient in this dish is finished",
    ingredientWarnBody:
      '"{item}" contains {items}, which the kitchen has called finished. You can still add it — but check with the kitchen or swap the ingredient before you promise it at the table.',
    ingredientWarnConfirm: "Add it anyway",
    // On the bill: what was finished when the plate was rung up.
    plateIngredientWarning: "Sold while {items} was finished",
    plateIngredientWarningSwap: "Swapped onto {items} while it was finished",
    swapChosen: "Swaps on this plate",
    swapFree: "No difference",
    swapCharge: "Price difference",
    swapConfirm: "Swap",
    swapNothingToRemove:
      "This dish has no recorded ingredients, so there is nothing to swap. Ask the office to record its recipe first.",
    swapUnpricedNote:
      "One of these ingredients has no price yet, so that swap goes through free and is recorded in the drawer review.",
    /* A DIFFERENT THING from the note above, and the difference is the point:
       that one is a swap with no price, which is free and flagged (D64); this
       one is a swap that cannot be priced AT ALL — the ingredient it removes
       has left the dish's recipe, so a per-unit rate has no quantity to run
       over. The server refuses it (`NOT_ON_RECIPE`), so the button is closed
       rather than quoting "No difference" on a change that cannot be sent. */
    swapUnpriceableNote:
      "A swap on this plate removes an ingredient the dish's recipe no longer has, so there is no quantity to price the difference over. Tap that swap to take it off and save the rest — it cannot be sent while it is on.",
    swapCompositionRule:
      "A swap is added to the plate at full price. The period discount applies to the dish only, never to the difference — and if a price was typed by hand for the dish, the difference is still added on top of it.",
    swapNeverRefunds:
      "A swap can only add. If the replacement is cheaper, nothing is refunded.",
    swapAmend: "Change swap",
    swapAmendTitle: "Change this plate's swaps",
    swapAmendConfirm: "Save the change",
    swapAmendTellKitchen:
      "This kitchen works on paper, and the chit in the cook's hand will not change. Tell the kitchen yourself before you save.",
    swapAmendKitchenMayHaveStarted:
      "The chit is printed and a cook may already have started this plate. Speak to the kitchen first, then save.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    swapAmendOnePlateOne:
      "This row holds 1 plate. The change applies to it, and it will split onto a row of its own.",
    swapAmendOnePlateTwo:
      "This row holds 2 identical plates. The change applies to ONE of them, and it will split onto a row of its own.",
    swapAmendOnePlateFew:
      "This row holds {count} identical plates. The change applies to ONE of them, and it will split onto a row of its own.",
    swapAmendOnePlate:
      "This row holds {count} identical plates. The change applies to ONE of them, and it will split onto a row of its own.",
    swapAmendSaved: "The swaps were changed.",
    swapAmendClear: "Remove every swap",
    swapFailed: "Could not load the ingredients right now. Try again.",
    remake: "Remake",
    void: "Void",
    comp: "Comp",
    voidReason: "Reason",
    voidReasonPlaceholder: "Choose a reason",
    voidReasonRequired: "Choose a reason from the list to confirm",
    reasonCustomerChangedMind: "Customer changed mind",
    reasonWrongOrder: "Wrong order",
    reasonQualityIssue: "Quality issue",
    // D44 (B5): the comp vocabulary — its own list, and the server refuses a
    // void code filed on a comp.
    reasonStaffMeal: "Staff meal",
    reasonGoodwill: "Goodwill",
    reasonKitchenError: "Kitchen error",
    reasonManagerDiscretion: "Manager's discretion",
    // R10: the refund vocabulary — its own list, because nothing on the void
    // or comp lists answers why money already taken went back.
    reasonWrongCharge: "Charged in error",
    reasonQuality: "Food or service not acceptable",
    reasonDuplicateCharge: "Charged twice",
    reasonChargeback: "Bank chargeback",
    reasonWrongItem: "Served the wrong item",
    // B117/Q4 — the remake vocabulary. Its own fourth list: the void codes are
    // all about the customer, and a remake has to tell the floor's mistake
    // from the kitchen's, which is the entire point of asking. The words
    // describe what happened; none of them names a person.
    reasonGuestChangedMind: "Guest changed their mind",
    reasonWrongItemOrdered: "Wrong item ordered",
    reasonCookedWrong: "Cooked wrong",
    reasonDamagedOrCold: "Damaged or cold",
    reasonOther: "Other",
    // Same field, different question: "who is the house paying for?" is not
    // "why did this come off the bill?".
    compReason: "Comp reason",
    compReasonPlaceholder: "Who is the house paying for?",
    compReasonRequired: "Choose a reason from the list to confirm",
    // The same field once more — "why is this plate going back?" is a third
    // question, and it has to read as an instruction to the cook rather than
    // a blame form.
    remakeReason: "Remake reason",
    remakeReasonPlaceholder: "What happened?",
    remakeReasonRequired: "Choose a reason from the list to confirm",
    // D43 (86cbdhg4p): a separate field from the code picker, never a
    // replacement for it — the code is what reports group by, the note is
    // what a human reads.
    voidReasonNoteLabel: "Note",
    voidReasonNoteOptionalPlaceholder: "Optional — what actually happened?",
    // D46 (S9): manual pricing on an item, for holders of
    // RESTAURANT_INVOICE_PRICE_OVERRIDE only.
    priceOverrideAction: "Different price",
    priceOverrideDialogTitle: "A different price for this item",
    priceOverrideAmountLabel: "Price",
    priceOverrideInvalid: "Enter a valid price",
    priceOverrideConfirm: "Confirm price",
    // Founder decision 2026-09-03: whoever changes a price by hand says why.
    // Voiding one item has demanded a reason for far longer, and a manual
    // price is the harder of the two to see afterwards — it replaces the list
    // price, so every roll-up reads a discount of zero.
    priceOverrideNoteLabel: "Reason",
    priceOverrideNotePlaceholder: "Why this price?",
    priceOverrideNoteRequired: "Write the reason before confirming.",
    priceOverrideChip: "Manual price",
    // The tier comes off the manual price, so the number typed is not the
    // number paid. Both are stated, so nobody is surprised at the table.
    priceOverrideTierNote:
      "This bill carries a {percent}% tier — the guest pays {price} each.",
    // Required once "Other" is picked — "Other" alone says nothing.
    voidReasonNoteRequiredPlaceholder: "Required — what actually happened?",
    voidReasonNoteRequired: 'Say what happened — "Other" alone is not enough',
    close: "Close invoice",
    settle: "Settle",
    settlementMethod: "Payment method",
    cash: "Cash",
    card: "Card",
    writeOff: "Write-off",
    writeOffTitle: "Record this bill as unpaid",
    writeOffBody:
      "This bill closes with no money at all: it counts into neither the drawer nor the money collected, and it appears in the drawer review under your name. The food really was made, so the bill still stands in the day's sales — under “unpaid”.",
    writeOffConfirm: "Yes, record it unpaid",
    writeOffNeedsCashier: "Closing a bill unpaid needs cashier permission",
    // R1/F3 — giving money back. Every rule that decides the figure is on the
    // sheet itself, never in a code comment: a manager must be able to read it
    // and predict the number.
    refundAction: "Give money back",
    // QA10-2 (phase 12): the sheet's title is the act that opened it — the
    // longer "…to the customer" was cut to "Give money back t…" at 390 beside
    // the header's door, in both themes (the shared title truncates).
    refundTitle: "Give money back",
    refundConfirm: "Confirm refund",
    refundedToast: "The money went back to the customer",
    refundMethodLabel: "Goes back by",
    refundMethodLockedCash:
      "This bill was paid in cash, so the money goes back in cash out of this drawer. There is no other choice here: sending it back another way would leave the drawer permanently short.",
    refundMethodLockedCard:
      "This bill was paid by card, so the money goes back to the same card. The drawer is not touched by this refund at all.",
    // D44 — the transfer. The sentence carries BOTH halves, like the two
    // above: how the money goes back, and what it does to the drawer. It
    // reached a bank account, so it leaves from one — and the cashier counting
    // notes at close, and the card terminal's own Z, are both unaffected.
    refundMethodLockedInstapay:
      "This bill was paid by InstaPay transfer, so the money goes back as a transfer to the customer's account. Neither the drawer nor the card terminal is touched by this refund: the money was in the bank and that is where it goes back from.",
    // B118/QA5 — Arabic declines at 1, 2 and 3–10. See `plural.ts`.
    refundLateWarningHoursOne:
      "More than an hour has passed since this bill was paid.",
    refundLateWarningHoursTwo:
      "More than 2 hours have passed since this bill was paid.",
    refundLateWarningHoursFew:
      "More than {hours} hours have passed since this bill was paid.",
    refundLateWarningHours:
      "More than {hours} hours have passed since this bill was paid.",
    refundLateWarningMinutes:
      "More than {minutes} minutes have passed since this bill was paid.",
    /* The tail that used to be repeated verbatim inside all four plural arms
       AND the minutes arm. One sibling sentence beside the count, on the same
       sheet — NOT behind a door: it is not an explanation, it is the clause
       that says the refund will go through. */
    refundLateRecordedNote:
      "The refund is allowed and will go through, but it is recorded as LATE and shows that way in the drawer review and the day's report.",
    refundAlreadyNote:
      "{amount} has already gone back off this bill. A plate that has come back once cannot come back again.",
    refundChargeSnapshotWarning:
      "This bill's own figures don't add up: the service charge and VAT recorded on it exceed the total of its items by {amount}. The refund will go through and the customer gets all of their money, but the VAT returned will be short by that amount. If that isn't expected, check the bill first.",
    refundPickPlates: "Pick the plates coming back",
    refundSelectAll: "Select all",
    refundClearAll: "Clear",
    refundAllPlatesBack:
      "Every plate on this bill has already come back. There is nothing left to refund.",
    refundPlateAlreadyBack: "Already came back",
    // F17 — the fee can come back now, and it is the cashier's choice per refund.
    refundDeliveryFee: "Delivery fee",
    refundDeliveryFeeHint:
      "Tick this when the DELIVERY is what went wrong — the food arrived and was eaten, but it was late or went to the wrong address. The fee and the VAT on it go back together.",
    refundDeliveryFeeAlreadyBack: "The fee already came back",
    refundBackToCustomer: "Back to the customer",
    refundSplitUnavailable:
      "The till cannot split this amount across the plates from this bill's own figures. The refund still works — the server does the arithmetic and it is the authority.",
    /* UX-round D22 — the six rules went behind a door. What stays on the canvas is the
       clause that explains an ABSENCE: there is no amount field. An absence
       has no row to hide behind. */
    refundRulePerPlateShort: "You pick plates, never an amount.",
    /* UX-round D24 — the paper itself. (The "Not paid yet" mark is gone —
       founder, 2026-10-08: the pre-payment print is the bill the customer
       pays from.) "Not recorded" is invariant 1 on paper: a settled bill with
       no stored method says so instead of going silent about how it was paid. */
    receiptMethodNotRecorded: "not recorded",
    receiptSettledAtLabel: "Paid at",
    refundRulesDoorLabel: "Why this number?",
    refundRulesTitle: "How the refund is worked out",
    refundRuleTermPlates: "Plates, not an amount",
    refundRuleTermCharges: "Service charge and VAT",
    refundRuleTermMethod: "How the money goes back",
    refundRuleTermDrawer: "Which drawer it hits",
    refundRuleTermBill: "The original bill",
    refundRuleTermCost: "Cost and margin",
    refundRulePerPlate:
      "You pick plates, never an amount: the figure comes from the bill itself.",
    refundRuleCharges:
      "The service charge and VAT come back in proportion with each plate, not in full.",
    refundRuleMethod:
      "The money goes back by the method the bill was paid with, and that cannot be changed here.",
    refundRuleDrawer:
      "It counts in the drawer that is open now, not the one the bill was settled in.",
    refundRuleBillUnchanged:
      "The original bill does not change: its figures stay as they were and the refund is recorded beside them.",
    refundRuleCost: "The cost stays unless you choose \"Returned from kitchen\": the food was still cooked, so what drops is the revenue and the margin. Returned from kitchen puts the ingredients back into stock and takes their cost off the cost of sales.",
    refundReason: "Refund reason",
    refundReasonPlaceholder: "Why is the money going back?",
    refundReasonRequired: "Choose a reason from the list to confirm",
    refundReasonNoteRequired: '"Other" needs a note saying what happened',
    refundPrintReceipt: "Print refund receipt",
    refundPrintReceiptOne: "Print refund {reference} · {amount}",
    refundReceiptTitle: "Refund receipt",
    refundReceiptAgainst: "Against bill #{sequence}",
    refundReceiptBillDate: "Bill date: {date}",
    refundReceiptSplitUnavailable:
      "The per-plate detail is not available on this slip. The amount refunded is recorded in full.",
    refundReceiptOriginalStands:
      "The original bill stands unchanged. This slip records only the money that went back.",
    refundReceiptReference: "Refund reference",
    refundReceiptMovedAt: "Money went back: {stamp}",
    refundReceiptCopyTitle: "Copy of the refund receipt",
    refundReceiptCopyNote:
      "This copy is of refund {reference} only — the same plates and the same amount as the original. If this bill was refunded more than once, each refund has its own slip.",
    // A tax document with no date anywhere on it is wrong even when the date
    // it cannot carry is the refund's. So the copy prints ITS OWN print time,
    // named as exactly that, and nobody can read it as the moment the money
    // moved.
    refundReceiptCopyPrintedAt: "This copy printed: {stamp}",
    refundNeedsPermission:
      "Giving money back needs its own permission, and you do not hold it. Ask the restaurant owner to do it from this same screen.", // QA5 — it said "ask a manager", and a manager does not hold
    // `INVOICE_REFUND` either: the role catalogue excludes it from that preset
    // deliberately, so the manager read a notice telling them to ask a manager.
    refundNoShiftWarning:
      "No drawer is open — the refund will be refused. Open a drawer first so the money leaves a box that is on record.",
    refundNoShiftWarningAskManager:
      "No drawer is open — the refund will be refused. Ask someone with the drawer permission to open one.",
    refundNoShiftWarningCard:
      "The money goes back to the card and the drawer is not touched, but the refund still has to be recorded against an open drawer — without one it is refused. Open a drawer first.",
    refundNoShiftWarningCardAskManager:
      "The money goes back to the card and the drawer is not touched, but the refund still has to be recorded against an open drawer — without one it is refused. Ask someone with the drawer permission to open one.",
    print: "Print",
    acceptPending: "Accept",
    acceptPendingAll: "Accept all",
    rejectPending: "Reject",
    pendingTitle: "Customer requests",
    pendingGuest: "Guest {guestNumber}",
    pendingAskedOne: "A guest asked for 1 item",
    pendingAskedMany: "Guests asked for {count} items",
    pendingFromQr: "From the QR menu at the table",
    pendingAddsToBill: "Adds to the bill",
    acceptPendingSend: "Accept and send",
    acceptPendingAllSend: "Accept all and send",
    waiterHandoffTitle: "A waiter sent this order to you",
    closeBlockedByPending:
      "Accept or reject the customer's requests before closing the invoice.",
    conflictTitle: "This invoice changed",
    conflictBody: "Someone else edited this invoice. Refresh and try again.",
    conflictPendingBody:
      "This invoice has items that never reached the kitchen, or customer requests still waiting for an answer. Refresh and read what changed before closing.",
    conflictReplayedBody:
      "This was already done, and the invoice changed after it. Refresh and read it before you go on — don't do it again.",
    refresh: "Refresh",
    itemsEmptyTitle: "This invoice is empty",
    itemsEmptyBody:
      "Add what the customer ordered and the total updates as you go.",
    gridEmptyTitle: "No tables yet",
    gridEmptyBody:
      "Add your areas and tables first and they'll show up here, ready for service.",
    gridEmptyAction: "Set up tables",
    gridDeniedTitle: "The tables screen is not yours",
    gridDeniedBody:
      "This screen needs the till permission. Ask the restaurant owner to add it to your role if you are meant to work the floor.",
    gridDeniedToKitchen: "Go to the kitchen board",
    gridDeniedToManage: "Go to the back office",
    stagedTitle: "Ready to add",
    increaseQuantity: "Increase quantity",
    decreaseQuantity: "Decrease quantity",
    removeStagedItem: "Remove item",
    itemsAddedToast: "Items added to the invoice",
    firedToast: "Sent to the kitchen",
    serveAction: "Serve",
    printChit: "Print ticket",
    closeBlockedHint:
      "Some items never reached the kitchen — fire them or remove them first",
    closeReasonPendingOne: "Can't close: 1 dish the guest ordered is waiting to be accepted",
    closeReasonPendingTwo: "Can't close: 2 dishes the guest ordered are waiting to be accepted",
    closeReasonPendingFew: "Can't close: {count} dishes the guest ordered are waiting to be accepted",
    closeReasonPending: "Can't close: {count} dishes the guest ordered are waiting to be accepted",
    closeReasonDraftsOne: "Can't close: 1 dish hasn't been sent to the kitchen",
    closeReasonDraftsTwo: "Can't close: 2 dishes haven't been sent to the kitchen",
    closeReasonDraftsFew: "Can't close: {count} dishes haven't been sent to the kitchen",
    closeReasonDrafts: "Can't close: {count} dishes haven't been sent to the kitchen",
    closeReasonQueuedOne: "Can't close: 1 dish hasn't been started",
    closeReasonQueuedTwo: "Can't close: 2 dishes haven't been started",
    closeReasonQueuedFew: "Can't close: {count} dishes haven't been started",
    closeReasonQueued: "Can't close: {count} dishes haven't been started",
    closeStartAndClose: "Start and close",
    closeStartOnly: "Start preparing",
    closeResendTicket: "Resend the ticket",
    closePrintTicketHint: "“Print ticket” is on the printer button",
    stillCookingTitle: "Food is still being prepared",
    stillCookingBodyOne: "1 dish isn't ready yet. Close the bill now?",
    stillCookingBodyTwo: "2 dishes aren't ready yet. Close the bill now?",
    stillCookingBodyFew: "{count} dishes aren't ready yet. Close the bill now?",
    stillCookingBody: "{count} dishes aren't ready yet. Close the bill now?",
    stillCookingLine: "{dish} · {station}",
    stillCookingClose: "Close the bill",
    stillCookingWait: "Wait",
    outAtFireTitle: "Out of stock",
    outAtFireBody: "\"{item}\" ran out after it was added. Send anyway?",
    outAtFireSend: "Send anyway",
    outAtFireCancel: "Cancel",
    menuItemOutToast: "\"{item}\" is out — stop it on the delivery apps too",
    closeBlockedQueuedHint:
      "Some plates are waiting at the kitchen — start them, print the chit, or remove them first",
    closedToast: "Invoice closed",
    reopen: "Reopen",
    reopenedToast: "Invoice reopened",
    closeUndoExpired:
      "That window has passed — the invoice is still closed. Reopen it from the bill.",
    closeUndoFailed:
      "It did not reopen — the invoice is still closed. Try Reopen on the bill.",
    settleTitle: "Settle invoice",
    settleTitleTable: "Settle the bill for table {table}",
    settleTitleTables: "Settle the bill for tables {name}",
    tenderedLabel: "Amount received",
    settleShortHint: "Less than the bill",
    settleConfirm: "Settle",
    /*
     * "Drawer", never "shift" (Unit C). What this feature tracks is a CASH BOX
     * with a name and a holder, and since D95 a store can have several open at
     * once — so "the shift" names a working period the product does not model
     * and cannot answer questions about.
     */
    noShiftWarning:
      "No drawer is open — settling will be refused until you open one.",
    noShiftWarningAskManager:
      "No drawer is open — settling will be refused. Ask someone with the drawer permission to open one.",
    noShiftWarningOpen: "Open a drawer",
    // Unit C — the strip that rides every till screen, and the dialog that opens a
    // drawer without leaving the bill.
    drawerStripHolder:
      "No drawer is open. You can keep working, but you cannot take money until you open one.",
    drawerStripAskManager:
      "No drawer is open. You can keep working, but settling will be refused — ask someone with the drawer permission to open one.",
    openDrawerTitle: "Open a drawer",
    openDrawerBody:
      "Name the box in front of you and count what is in it. The money you take goes into this drawer, under your name.",
    openDrawerLabelLabel: "Drawer",
    openDrawerLabelHint: 'The box\'s name: "Till 1", "Counter", "Bar".',
    openDrawerLabelPlaceholder: "Till 1",
    openDrawerFloatLabel: "What is in it now",
    openDrawerFloatHint:
      "Count it before you start — this is the figure tonight's variance is measured from.",
    openDrawerTaken:
      '"{label}" is open with {name}. Open another box, or take it over from the drawer screen.',
    openDrawerSomeoneElse: "someone else",
    openDrawerConfirm: "Open the drawer",
    /* UX-round D23/R8 — a grey button with no reason leaves the cashier guessing. The
       button stays pressable and says why instead. */
    openDrawerNeedsLabel: "Name the drawer first.",
    openDrawerNeedsFloat:
      "Count what is in the drawer and type the figure first.",
    drawerOpenedToast: "The drawer is open",
    /* A diner's own claim to have paid (D44). "says they paid", never "paid":
       nothing has arrived until a cashier says it has. */
    dinerPaymentTitle: "The customer says they paid",
    /* D133 fix — cash or card at the table: they are ASKING to pay, nothing
       has changed hands yet. "Says they paid" stays for a transfer. */
    dinerPaymentTitleAsk: "The customer asks to pay",
    instapay: "InstaPay",
    // F2 — the platform took the diner's money and pays the restaurant later.
    // It is real revenue and it is in no drawer.
    aggregatorMethod: "Platform",
    aggregatorSettleHint:
      "{name} took the customer's money and pays the restaurant later, less {rate}% — none of it passes through the drawer.",
    dinerPaymentInstapay: "InstaPay",
    dinerPaymentCash: "Cash at the till",
    dinerPaymentCard: "Card at the till",
    dinerPaymentViewProof: "View the screenshot",
    dinerPaymentConfirm: "Confirm the money arrived",
    dinerPaymentReject: "Refuse",
    dinerPaymentAmount: "Amount",
    dinerPaymentCollectNow: "Collect {amount}",
    dinerPaymentWontPay: "Not paying now",
    dinerPaymentVerified: "Confirmed",
    dinerPaymentRejected: "Refused",
    dinerPaymentNote: "Confirming does not settle the bill — settle it after.",
    // See the Arabic note: the claim's figure was frozen at submission and the
    // bill has moved. Short has a remedy at the till; over has none in this
    // product, and the card says so before the tap rather than after.
    dinerPaymentShort:
      "The bill is now {total} — they sent {gap} less. Confirm, then take the rest before closing.",
    dinerPaymentOver:
      "The bill is now {total} — they sent {gap} more. The transfer reached no drawer, so the difference cannot be given back from here — check with your manager before confirming.",
    dinerPaymentNoProof: "No screenshot — they will pay at the till.",
    // D133 — a share of the bill and a tip. The amount is the bill share only;
    // the tip is apart from it and never on the bill.
    dinerPaymentShareEvenOne: "Bill for one person — this is their share",
    dinerPaymentShareEvenTwo: "Bill split evenly between 2 people — this is one share",
    dinerPaymentShareEvenFew: "Bill split evenly between {count} people — this is one share",
    dinerPaymentShareEven: "Bill split evenly between {count} people — this is one share",
    dinerPaymentShareItemsOne: "For one of their own dishes",
    dinerPaymentShareItemsTwo: "For 2 of their own dishes",
    dinerPaymentShareItemsFew: "For {count} of their own dishes",
    dinerPaymentShareItems: "For {count} of their own dishes",
    dinerPaymentTip: "+ {percent}% tip, {amount} — for the team, not on the bill",
    dinerPaymentTipAmount: "+ tip, {amount} — for the team, not on the bill",
    dinerPaymentCollect: "Collect {amount} in all",
    dinerPaymentSettleShare: "Settle this share",
    dinerPaymentShareSettled: "This share is settled",
    dinerPaymentAskPartSettled: "Part of the request is settled — {remaining} is still on the bill",
    dinerPaymentTipToDrawer: "Put the {amount} tip in the drawer",
    dinerPaymentTipInDrawer: "Tip recorded in the drawer",
    dinerPaymentTipReason: "Diner tip paid with the bill",
    fireGroup: "Send",
    fireGroupOnly: "Send just this dish",
    noteChip: "Note",
    noteDialogTitle: "Note for the kitchen",
    notePlaceholder: "e.g. no onions",
    noteQuickPicks: "No onion|Spicy|No salt|Sauce on the side|Well done",
    noteQuickJoin: ", ",
    noteSave: "Save",
    noteClear: "Clear note",
    voidCountLabel: "How many plates",
    returnToStockLabel: "Where did this item go?",
    returnToStockWasted: "Wasted",
    returnToStockReturned: "Returned from kitchen",
    returnToStockHintWasted: "Wasted keeps its cost in the cost of sales; nothing goes back into stock.",
    returnToStockHintReturned: "The ingredients go back into stock at their original cost, and that cost comes off the cost of sales. An item with no recipe has nothing to return.",
    returnToStockAppliesToCooked: "Applies only to the items in your selection that were cooked; the rest never were.",
    transferTable: "Move to another table",
    headTransferShort: "Move table",
    headGuestsShort: "Guests",
    transferTitle: "Move the invoice to a table",
    transferDone: "Invoice moved",
    transferNoFreeTables: "No free tables right now",
    tierPickerTitle: "Pricing",
    tierBase: "Base prices",
    settledToast: "Payment recorded",
    statusOpen: "Open",
    statusClosed: "Closed",
    statusSettled: "Settled",
    statusCancelled: "Cancelled",
    addonsFailedTitle: "Addons didn't load",
    addonsFailedBody:
      "Items can't be added right now: without the addon groups a plate could reach the kitchen missing the choices it requires. Try again.",
    menuFailedTitle: "The menu didn't load",
    menuFailedBody: "The menu could not be loaded. Try again.",
    sectionsFailedNote: "Sections didn't load — the section filter is hidden.",
    gridFailedNote:
      "The tables didn't load — switching tables is unavailable and the table name will be missing from the cheque.",
    receiptTableUnknownNote:
      "The table name is unknown right now — the cheque will print without it.",
    tiersFailedNote:
      "Pricing didn't load — the order will open at base prices.",
    // D47: the tier that applies on its own when the cashier picks none.
    tierAutoApplied:
      "This tier applies on its own right now — pick base prices to skip it.",
    // D45: covers — optional, and blank is a real answer.
    guestCountLabel: "Guests",
    guestCountPlaceholder: "—",
    guestCountHint: "Optional — leave blank if nobody counted.",
    // B6: the same question, after the bill is open — a waiter knows the count
    // coming back from the table, not before the first menu tap.
    guestCountUnset: "—",
    guestCountDialogTitle: "Guests at this table",
    guestCountDialogHint:
      "Leave it blank if nobody counted — better than a guess.",
    guestCountInvalid: "Enter a number from 1 to 500",
    guestCountClear: "Clear count",
    // Covers rework Phase 1 — opened from the CLOSE path rather than the
    // covers row, so it asks a slightly different question.
    guestCountCloseDialogTitle: "How many were at the table?",
    guestCountCloseDialogHint: "Leave it if you don't know.",
    // Phase 2a — quick chips at table-open; "more" opens the same dialog.
    guestCountMoreChip: "More",
    // Phase 2b — the stepper on a bill with a recorded count. Same
    // one/two/few/many shape as `coversWordOne/Two/Few/coversWord` beside it.
    guestCountWordOne: "1 guest",
    guestCountWordTwo: "2 guests",
    guestCountWordFew: "{count} guests",
    guestCountWord: "{count} guests",
    guestCountDecrement: "Decrease guest count",
    guestCountIncrement: "Increase guest count",
    // Phase 2c — inline, non-blocking: the count is more than double the
    // bill's own tables' seats, most likely the table number typed by habit.
    guestCountSanityHint:
      "That's more than the table's seats — make sure it isn't the table number.",
    settleNeedsCashier: "Needs cashier permission to settle",
    cancelledTitle: "This invoice was cancelled",
    cancelledBody:
      "It was cancelled and does not count towards today's sales. You can still print a copy for the record.",
    // The table opened itself from a QR request and no one on staff has been
    // to it yet (D51).
    selfSeatedBadge: "Self-seated",
  },
  captain: {
    label: "Captain",
    none: "No captain",
    line: "Captain: {name}",
    hint: "Optional — who has this table",
    noWaiters: "Nobody on the team holds the Waiter role yet",
    change: "Change captain",
    sheetHint: "Every change is logged on this bill with who and when.",
    saved: "Captain recorded",
    cleared: "Captain cleared",
    filterAny: "Any captain",
  },
  serviceRequests: {
    billHeading: "The table asks for",
    since: "since {time}",
    done: "Done",
    kindWaiter: "Waiter",
    kindWater: "Water",
    kindCutlery: "Cutlery",
    kindBill: "Bill",
    kindCleanTable: "Clean table",
    kindHighChair: "High chair",
    alertWaiter: "{table} is calling a waiter",
    alertWater: "{table} asks for water",
    alertCutlery: "{table} asks for cutlery",
    alertBill: "{table} asks for the bill",
    alertCleanTable: "{table} asks for the table to be cleaned",
    alertHighChair: "{table} asks for a high chair",
  },
  paper: {
    orderNo: "Order No",
    table: "Table",
    date: "Date",
    checkIn: "Check in",
    checkOut: "Check out",
    customer: "Customer",
    product: "Product",
    quantity: "Qty",
    price: "Price",
    lineTotal: "Total",
    total: "Total",
    statusOpen: "Open",
    statusPaid: "Paid",
    status: "Status",
    captain: "Captain",
    guestNo: "Guest No",
    generalCustomer: "General Customer",
    discount: "Discount",
    poweredBy: "Powered by Trioty",
  },
  /*
   * T14/D172 — every paper marker comes from the catalogue, symbol first, so
   * no internal enum token is printed. «Duplicate copy» on an extra copy only.
   */
  printArtifact: {
    receipt: "Customer receipt",
    kitchenChit: "Kitchen ticket",
    remake: "↻ Remake",
    reroute: "⇄ Moved from another station",
    reprint: "Duplicate copy",
    reprintNote: "Sent before; check before making it",
    change: "✎ Changed",
    void: "✕ Cancelled — do not make",
    voidRemainder: "Cancelled {voided} of {total} — {remaining} left",
    addition: "Addition {number}",
  },
  /*
   * D172 — «Print ticket» on a ticket that already printed asks once. «Not
   * received» prints as the first paper; only «Extra copy» says «Duplicate copy».
   */
  reprintQuestion: {
    title: "Print the ticket again",
    subtitle: "This ticket has printed before. Why print it again?",
    notReceived: "Ticket did not arrive",
    notReceivedHint: "Prints as the first paper, with no copy mark.",
    extraCopy: "Extra copy",
    extraCopyHint: "Marked «Duplicate copy» so it is not made twice.",
    sentToPrinter: "Ticket sent to the kitchen printer",
    printedHere: "Printed on this device",
  },
  /*
   * D156 — the manager's password on the exception acts (founder 2026-10-15,
   * amending D27): a void after the kitchen ticket, a comp, a discount at the
   * bill or a refund returning stock, sent by a cashier, needs a manager's or
   * the owner's own password for that one act. The password's owner is told.
   */
  managerApproval: {
    title: "Manager approval",
    confirm: "Approve",
    intro_voidItem: "Voiding an item already sent to the kitchen needs a manager's password.",
    intro_compItem: "Putting an item on the house needs a manager's password.",
    intro_applyBillDiscount: "A discount on the bill needs a manager's password.",
    intro_refundInvoice: "Returning food to stock with a refund needs a manager's password.",
    approverLabel: "Approving manager",
    passwordLabel: "Manager's password",
    hint: "The password is used for this one action, is not kept on this device, and its owner is notified.",
    invalid: "The approval was not accepted. Check the name and the password, then try again.",
    locked:
      "Approval by this name is paused after five wrong attempts. Try again in fifteen minutes, or choose another manager.",
    passwordChangeRequired:
      "The manager must change their temporary password first: sign in with it once and choose a new one, then approve with that.",
    noApprovers:
      "Nobody here can approve. The owner grants \"Approving exceptions\" on the team screen.",
    loading: "Loading names…",
    settingLabel: "Manager approval by password",
    settingCheckbox: "Ask for a manager's password",
    settingHint:
      "When on, a void after the kitchen ticket, a comp, a discount on the bill or returning food to stock with a refund needs a manager's password if a cashier does it. Managers and the owner are not asked. Whoever approves is accountable for the act.",
    act_voidItem: "Item voided",
    act_compItem: "Item on the house",
    act_applyBillDiscount: "Bill discount",
    act_refundInvoice: "Refund returned to stock",
    detailTitle: "Manager approvals",
    detailRow: "By {approver} · from {till}'s device",
    unknownName: "Unknown",
    staffApprovals: "{approved} of them approved by password from: {tills}",
    staffApprovalsTill: "{name} ({approved})",
    shiftTillBy: "from {name}'s device",
    notifyTitle: "Your password was used",
    notifyBody: "Your password approved \"{act}\" on bill #{number} at {time}",
    notifyBodyTill:
      "Your password approved \"{act}\" on bill #{number} at {time} — from {till}'s device",
  },
  /*
   * D146 — the discount at the bill: the manager's gesture at the end of a
   * meal, as a percent, an amount or a new total. Taken off the net food
   * before service and VAT; the delivery fee is never discounted.
   */
  billDiscount: {
    action: "Discount",
    actionChange: "Change discount",
    lineLabel: "Bill discount",
    sheetTitle: "Bill discount",
    percentLabel: "Percent of the net food",
    amountLabel: "Amount off",
    targetLabel: "New total",
    previewBefore: "Total now",
    previewNet: "Off the net food",
    previewAfter: "Total after discount",
    capHint: "At most {percent}% of the net food ({amount})",
    problemOverCap: "Above the limit: {percent}% of the net food ({amount})",
    problemWholeBill: "That is the whole bill — comp the plates instead",
    problemBelowPaid: "The total would fall below what is already paid ({paid})",
    problemNothingOff: "That takes nothing off this bill",
    reasonLabel: "Reason",
    reasonRounding: "Rounding",
    reasonComplaint: "Complaint",
    reasonRegular: "Regular guest",
    reasonOwnerGuest: "Owner's guest",
    reasonOther: "Other",
    noteLabel: "Reason in words",
    noteRequired: "\"Other\" needs a written reason",
    confirm: "Apply discount",
    remove: "Remove",
    appliedToast: "Discount applied to the bill",
    removedToast: "Discount removed from the bill",
    blockedPriceTier: "This bill has a price tier, so it cannot take a discount too",
    blockedAggregator: "A delivery platform's bill cannot take a discount",
    detailWhy: "Reason: {reason} · by {name}",
    detailUnknownWho: "unknown",
  },
  printChoice: {
    title: "Print the check",
    whole: "Whole check",
    wholeHint: "Everything on the bill · {amount}",
    items: "Choose items",
    itemsHint: "The chosen items become a part of the bill, with its own check and its own payment",
    itemsHintPrintOnly: "Prints a check for the chosen items only",
    ticketHint: "The kitchen ticket hasn't printed, so the bill can't close until it does",
    partsTitle: "Parts of the bill",
    partUnpaid: "Not paid",
    partSettle: "Settle",
    partPrint: "Print {label}'s check",
    editParts: "Edit the parts",
    printPickedNone: "Choose items to print",
    printPickedOne: "Print 1 item · {amount}",
    printPickedTwo: "Print 2 items · {amount}",
    printPickedFew: "Print {count} items · {amount}",
    printPicked: "Print {count} items · {amount}",
  },
  preBillCheck: {
    action: "Check",
    actionReprint: "Reprint check",
    actionHint:
      "Print the check for the guests before paying — the table stays open",
    dialogTitle: "Check · {name}",
    toPay: "To pay",
    splitLabel: "Show a split on the check",
    splitNone: "No split",
    splitGuests: "{count} guests",
    splitGuestsOne: "1 guest",
    splitGuestsTwo: "2 guests",
    splitGuestsFew: "{count} guests",
    splitEach: "{label} · {amount} each",
    splitUneven: "{label} · {parts}",
    stayOpenNote:
      "The table stays open. Guests can still add items; the check is marked out of date if they do.",
    print: "Print check",
    recordedToast: "Check recorded",
    sentToPrinterToast: "Check sent to the receipt printer",
    noteFresh: "Check printed at {time} · waiting for payment",
    noteStale: "Items changed since the check at {time} — reprint it",
    tilePrinted: "Check printed",
    tileStale: "Check out of date",
    paperKind: "Receipt",
  },
  riderSitting: {
    groupsTitle: "Riders handing in",
    unnamed: "No name",
    groupLine: "{name} · {count} · {amount}",
    handIn: "{name} hands in",
    handInUnnamed: "Hand in the unnamed bags",
    sheetSubOne: "1 bag · {amount}",
    sheetSubTwo: "2 bags · {amount}",
    sheetSubFew: "{count} bags · {amount}",
    sheetSub: "{count} bags · {amount}",
    lineNoAddress: "No address",
    lineMinutesOut: "out {count}m",
    lineMinutesOutHours: "out {count}h",
    lineTickLabel: "Bill {sequence} in this hand-in",
    lineLeftOut: "Left out — still owed",
    instapayHint:
      "InstaPay only once the transfer has actually arrived. If it has not, untick the bill and leave it until it does — it stays on the strip.",
    expectedCash: "Cash expected",
    instapayTotal: "InstaPay",
    payoutLabel: "Rider's pay-out (optional)",
    payoutHint:
      "Petrol or drops — recorded as its own “Delivery cost” paid out of the drawer, never taken off the bills.",
    net: "Net on the counter",
    countedLabel: "Cash handed in",
    short: "Short {amount}",
    over: "Over {amount}",
    exact: "Exact",
    differenceNote:
      "A difference does not stop the hand-in; it shows in the drawer count.",
    payoutReasonOne: "{name} · 1 drop",
    payoutReasonTwo: "{name} · 2 drops",
    payoutReasonFew: "{name} · {count} drops",
    payoutReason: "{name} · {count} drops",
    confirm: "Confirm hand-in",
    nothingTicked: "No bill is ticked.",
    lineSending: "Settling…",
    lineDone: "Settled",
    lineFailed: "Not settled: {error}",
    payoutDone: "Rider's pay-out recorded",
    payoutFailed: "The rider's pay-out was not recorded: {error} — no bill was settled.",
    someFailedOne: "1 bill was not settled and stays on the strip, still owed.",
    someFailedTwo: "2 bills were not settled and stay on the strip, still owed.",
    someFailedFew: "{count} bills were not settled and stay on the strip, still owed.",
    someFailed: "{count} bills were not settled and stay on the strip, still owed.",
    retry: "Try again",
    done: "Done",
  },
  paperSettle: {
    title: "Settle {name}",
    remaining: "Remaining",
    subUnpaid: "{count} unpaid items",
    subUnpaidOne: "1 unpaid item",
    subUnpaidTwo: "2 unpaid items",
    subUnpaidFew: "{count} unpaid items",
    subAllPaid: "Everything is paid",
    subPaidSoFar: "{amount} paid so far",
    modesLabel: "How the guest pays",
    modeFull: "Full bill",
    modeItems: "By item",
    fullInfo: "Pay the remaining {amount} in one go",
    itemsPick: "Pick what this guest is paying for",
    itemsSelected: "{count} selected · {amount}",
    itemsSelectedOne: "1 selected · {amount}",
    itemsSelectedTwo: "2 selected · {amount}",
    itemsSelectedFew: "{count} selected · {amount}",
    selectAll: "Select all",
    clear: "Clear",
    each: "{amount} each",
    fewer: "One fewer {name}",
    more: "One more {name}",
    partPaidNote:
      "Items of a guest who paid part of their share are not listed — take the rest with Full bill.",
    cashReceived: "Cash received",
    amountLabel: "Amount to pay",
    takeNow: "Take {amount} now",
    labelPart: "Part payment",
    labelRest: "The rest",
    instapayLocked:
      "InstaPay is taken once staff confirm the guest's transfer on this bill.",
    pay: "Pay {amount}",
    payPlain: "Pay",
    payPickFirst: "Select items to pay",
    payNothing: "Nothing to pay",
    settleAtZero: "Settle at 0.00",
    footOpen: "Paying closes the bill to new orders. Anything not paid stays on it.",
    footKeepsOpen: "The bill stays open and the kitchen keeps cooking. The rest is paid later.",
    footPaidWaiting: "Paid in full. Close the bill once the kitchen is done.",
    closePaid: "Close the bill",
    footClosed: "Anything not paid stays on the bill.",
    lastPaid: "{amount} paid by {method}",
    lastLeft: "{amount} left",
    labelItems: "{count} items",
    labelItemsOne: "1 item",
    labelItemsTwo: "2 items",
    labelItemsFew: "{count} items",
    doneTitle: "All paid",
    doneBody: "{name} is fully paid.",
    done: "Done",
    payments: "Payments",
    guest: "Guest {number}",
    writeOffOpen: "Guest walked out without paying?",
  },
  kitchen: {
    title: "Kitchen",
    titleAtStation: "Kitchen — {station}",
    plateCount: "{count} plates",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    plateCountFew: "{count} plates",
    // B117/A2 — «1 plates» in English.
    plateCountOne: "1 plate",
    plateCountTwo: "2 plates",
    stationPickerLabel: "Pick a station",
    allStations: "All stations",
    stationGoneNotice:
      "The station this screen was set to no longer exists, so it is showing every station. Pick a station above.",
    laneQueued: "New",
    lanePreparing: "Preparing",
    laneReady: "Ready",
    laneEmpty: "Nothing here right now.",
    laneCashier: "Waiting for cashier",
    fromWaiter: "From the waiter · {time}",
    fromCustomer: "From the customer · {number}",
    emptyTitle: "The kitchen is clear",
    emptyBody:
      "Nothing has been fired to this station. The moment a waiter sends an order it shows up here.",
    deniedTitle: "This screen is not yours",
    deniedBody:
      'The kitchen board needs the "kitchen view" permission. If you are meant to work on it, ask the owner to add it to your role from the team screen.',
    displayOffTitle: "The current kitchen workflow has no screen",
    displayOffBody:
      "This restaurant doesn't run a kitchen display — plates move from the till or by the printed chit. If the board should be on, ask the owner.",
    displayOffBodyOwner:
      'The current kitchen workflow uses no screen — plates move from the till or by the printed chit. Pick a workflow with a screen under "Kitchen workflow" in Settings → Kitchen, then come back.',
    displayOffSettingsAction: "Open settings",
    actionStart: "Start preparing",
    // The till chip (`OrderPanel`): the same act, short — T14.
    actionStartShort: "Start",
    actionReady: "Ready",
    actionServed: "Serve",
    printing: "Printing the chit...",
    remakeBadge: "Remake ({count})",
    // B117/Q4 — the reason beside the badge, so the cook reads an instruction
    // rather than a reproach: what to fix before he starts.
    remakeReasonLine: "Reason: {reason}",
    tableUnknown: "No table",
    waitingMinutes: "{count}m",
    // UX-round D47 — the minutes past the hour. "1h" read the same on a plate 61
    // minutes old and one 115 minutes old; the cook needs the difference.
    waitingHours: "{count}h {minutes}m",
    // UX-round D47 — the WORD beside the weight and the border. Colour never carries a
    // state on its own (docs/design/components-states-motion.md).
    lateBadge: "LATE",
    // A finished plate sitting on the pass is not "late", it is going cold —
    // which is what the capture showed: three READY plates at 1h.
    lateBadgeReady: "GOING COLD",
    // UX-round D48 — what the lane is hiding, in the heading that never scrolls. "more"
    // and not "below": scroll the lane and the hidden cards are ABOVE, so a
    // word that names a direction is wrong half the time.
    laneMoreOne: "1 more",
    laneMoreTwo: "2 more",
    laneMoreFew: "{count} more",
    laneMore: "{count} more",
    chitPrintNumber: "Copy #{number}",
    chitInvoice: "Invoice #{sequence}",
    chitNoPrices: "Kitchen chit — no prices, not a bill",
    // ── The 86 screen (D50) ───────────────────────────────────────────────
    /* UX-round D45 — "Out" is the dish state everywhere, "Finished" the ingredient
       state everywhere, and every act is an imperative. The chip and the
       button read `Sold out` / `Sold out` — one word for a fact and for a
       command, nine times on one screen. */
    availabilityTitle: "Out today",
    availabilityEntry: "Out today",
    availabilityBack: "Back to the board",
    // QA3 — the noun is elided but the VERB moves in Arabic; see that block.
    availabilitySoldOutCount: "{count} out",
    availabilitySoldOutCountFew: "{count} out",
    availabilitySoldOutCountOne: "1 out",
    availabilitySoldOutCountTwo: "2 out",
    availabilityOn: "Available",
    availabilityOff: "Out",
    // Not the same thing: a dish someone turned off, versus a combo blocked
    // by something inside it. The second cannot be fixed from this screen, so
    // it gets words instead of a button that would do nothing visible.
    availabilityOffComponent: "Missing an item",
    availabilityBlockedHint: "Turn the missing item on first",
    // T14 — the act, never the state's word: "Turn off" / "Turn on" beside the
    // "Available" / "Out" chip.
    availabilityMarkOut: "Turn off",
    availabilityPutBack: "Turn on",
    availabilityUnassigned: "No station",
    availabilityUntilManual: "Until turned on by hand",
    availabilityIngredientUntilManual: "Until reported available",
    availabilityUntilManualHint: "It won't come back by itself when the next business day starts.",
    availabilitySaving: "Saving...",
    availabilityConfirmOutTitle: "Turn this dish off?",
    /* UX-round D46 — two clauses, now that the intro paragraph has left the canvas.
       This is the only statement of the till/QR reach left on the cook's
       screen, so it may not be emptied (E12). The third clause ("the menu
       itself does not change") is carried verbatim by `pos.menuMarkSoldOutBody`
       on the waiter's own tile. */
    availabilityConfirmOutBody:
      '"{item}" stops being orderable on every till and disappears from the QR menu until you turn it on again. On the till it stays visible, marked “Out”.',
    availabilityConfirmBackTitle: "Turn this dish on?",
    availabilityConfirmBackBody:
      '"{item}" becomes orderable again on every till and on the QR menu.',
    availabilityMarkedOut: '"{item}" is off',
    availabilityMarkedBack: '"{item}" is on',
    availabilityEmptyTitle: "There are no menu items yet",
    availabilityEmptyBody:
      "As soon as the owner adds dishes to the menu they show up here and you can turn off what has run out.",
    availabilityDeniedTitle: "This screen is not yours",
    availabilityDeniedBody:
      'This screen needs the "mark an item sold out" permission. If you are meant to work on it, ask the owner to add it to your role from the team screen.',
    // ── Ingredients (B18) ─────────────────────────────────────────────────
    // Two panes because the two claims differ: an 86'd dish cannot be sold,
    // while a dish whose ingredient is finished still sells and the waiter is
    // simply warned.
    // T14 — one title for both panes, «النواقص» / "Out today"
    // (`availabilityTitle`); the pane tabs say which list is open.
    // The ingredient's verb, not the dish's. In Arabic they are the same word;
    // in English a "Sold out" button beside a "Finished" state chip is two
    // words for one state on one screen — the same collision the stock page
    // had.
    availabilityIngredientMarkOut: "Report out",
    availabilityIngredientPutBack: "Report available",
    availabilityTabDishes: "Dishes",
    availabilityTabIngredients: "Ingredients",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    availabilityIngredientsCountOne: "1 finished",
    availabilityIngredientsCountTwo: "2 finished",
    availabilityIngredientsCountFew: "{count} finished",
    availabilityIngredientsCount: "{count} finished",
    availabilityIngredientOn: "Available",
    availabilityIngredientOff: "Finished",
    // This screen carries only the decision, so the bare word is unambiguous.
    // The stock screen carries BOTH claims and already spends "Out" on the
    // cached reading, so there the decision has to name its source — otherwise
    // a tile saying "Out: 0" sits above a row saying the opposite.
    availabilityIngredientOffQualified: "Kitchen says finished",
    availabilityIngredientEmptyTitle: "No ingredients recorded yet",
    availabilityIngredientEmptyBody:
      "As soon as the office records ingredients in the store they show up here and you can report what has run out.",
    availabilityIngredientConfirmOutTitle: "Report this ingredient out?",
    // "until you put it back" — the reversal its two siblings already name and
    // the `caution` tone already promises. See the Arabic.
    availabilityIngredientConfirmOutBody:
      '"{item}" is reported finished until you report it available. Dishes containing it stay orderable — the waiter sees a warning before adding them, and nobody swaps a guest onto it unknowingly.',
    availabilityIngredientConfirmBackTitle: "Report this ingredient available?",
    availabilityIngredientConfirmBackBody:
      '"{item}" becomes available again and the warnings stop.',
    availabilityIngredientMarkedOut: '"{item}" reported finished',
    availabilityIngredientMarkedBack: '"{item}" is available',
    // ── P5 (T12, T14, T16): the wall screen's start, its honesty, its exit ──
    startTitle: "Kitchen screen",
    startBody: "Tap “Start screen” to hear a sound with every new order and keep the screen on.",
    startAction: "Start screen",
    startSilent: "Continue without sound",
    sizeLabel: "Display size",
    sizeAuto: "Auto",
    sizeTouch: "Touch",
    sizeWall: "Wall",
    offlineTitle: "Not connected — last updated {time}",
    offlineTitleNever: "Not connected",
    offlineBody: "Some orders may be missing.",
    offlineRefresh: "Refresh screen",
    offlineNoNetwork: "This device is offline — check the connection and try again.",
    screenOpen: "Screen",
    screenTitle: "Screen settings",
    soundLabel: "New-order sound",
    soundOn: "On",
    soundOff: "Off",
    soundLocked: "Starts on the next tap on the screen",
    soundUnsupported: "Not available on this device",
    soundEnable: "Turn sound on",
    soundMute: "Turn sound off",
    signOutTitle: "Sign out of this screen?",
    signOutBody: "Press and hold the button until it fills. A quick tap does nothing, so a passing touch cannot close the screen.",
    signOutHold: "Hold to sign out",
    actionHandOver: "Hand over",
    actionStartScoped: "Start preparing ({count})",
    actionReadyScoped: "Ready ({count})",
    actionServedScoped: "Serve ({count})",
    actionHandOverScoped: "Hand over ({count})",
    addOnRound: "Add-on {round}",
    totalAge: "{count}m since order",
    totalAgeHours: "{count}h {minutes}m since order",
    unprinted: "Not printed yet",
    voidMark: "Void",
    paidMark: "Paid",
    billClosedMark: "Bill closed",
    hide: "Hide",
    show: "Show",
    viewSwitchLabel: "View",
    viewByStation: "By station",
    viewByOrder: "By order",
    orderComplete: "Complete",
    orderWaitingOn: "Waiting on: {stations}",
    orderServe: "Serve order ({count})",
    orderHandOver: "Hand over order ({count})",
    orderEmptyTitle: "No orders in the kitchen",
    orderEmptyBody: "An order shows up here the moment it is sent to the kitchen, one card per order.",
    batchTitle: "To make now",
    channelFilter: "Takeaway & delivery",
    channelFilterHidden: "{count} hidden",
    recallOpen: "Recently done",
    recallServedTitle: "Served in the last 15 minutes",
    recallAgedOffTitle: "Ready, hidden from the lanes",
    recallEmpty: "Nothing was done in the last 15 minutes.",
    undo: "Undo",
    undoToast: "{state} · #{number}",
    stateServed: "Served",
    stateHandedOver: "Handed over",
  },
  manage: {
    tableIngredient: "Ingredient",
    menuTitle: "Menu",
    menuItemName: "Item name",
    menuItemNameEn: "English name",
    /* D127 — the dish's description, Arabic and English. Optional. */
    menuItemDescriptionAr: "Arabic description",
    menuItemDescriptionEn: "English description",
    menuItemDescriptionHint:
      "Optional, up to {max} characters. Guests see it on the QR menu; waiters see it on the till.",
    menuPriceChargesHint:
      "This is the menu price. Whether VAT and the service charge are added on top of it or already included is set in the general settings.",
    addonGroups: "Addon groups",
    /* UX-round D62 — `floorTitle` ("Areas and tables") is gone: the one screen is named
       `tablesTitle` ("The floor") in both views. */
    areaName: "Area name",
    tableName: "Table name",
    tableCode: "QR code",
    ingredientsTitle: "Ingredients",
    unit: "Unit",
    unitPiece: "piece",
    unitGram: "g",
    unitKilogram: "kg",
    unitMilliliter: "ml",
    avgCost: "Average cost / {unit}",
    onHand: "On hand",
    lowStock: "Running low",
    recordPurchase: "Record a purchase",
    purchaseQuantity: "Quantity",
    purchaseTotal: "Total paid",
    purchaseVatAmount: "VAT included",
    purchaseVatAmountHint: "The part of the total paid that is VAT. Optional.",
    purchaseTaxInvoiceRef: "Tax invoice number",
    purchaseSupplier: "Supplier",
    purchaseSupplierHint:
      "Who delivered this. Optional — without one the purchase is still recorded, but its VAT has no tax invoice behind it.",
    purchaseSupplierNone: "No supplier",
    purchaseVatExceedsTotal: "VAT cannot be more than the total paid.",
    recordAdjustment: "Adjustment",
    adjustmentReason: "Reason",
    /* D138 — reason first, then a positive quantity whose sign the form
       applies: waste and spoilage always take off, a correction says which way. */
    adjustmentQuantity: "Quantity",
    adjustmentQuantityHint:
      "A positive number, in {unit}. The reason decides whether it comes off stock or is added to it.",
    adjustmentQuantityRequired: "Enter a quantity above zero.",
    adjustmentDirection: "Which way",
    adjustmentDirectionUp: "Add to stock",
    adjustmentDirectionDown: "Take off stock",
    adjustmentTakesOffNote: "Waste and spoilage always take stock off.",
    adjustmentPreview: "On hand afterwards: {quantity} {unit}",
    adjustmentNote: "Note",
    adjustmentNoteHint: "Optional — for example: dropped, or past its date.",
    tiersTitle: "Price tiers",
    discountPercent: "Discount %",
    settingsTitle: "Restaurant settings",
    dayStartHour: "Business day starts at (hour)",
    dayStartHint:
      "Invoices before this hour count towards the previous business day.",
    timezone: "Timezone",
    receiptHeader: "Receipt header",
    receiptFooter: "Receipt footer (Arabic)",
    // D82: two stored lines, one per language. There used to be ONE, printed
    // under both, so «شكرًا لزيارتكم» appeared at the bottom of an English slip.
    receiptFooterHint:
      "Printed under the Arabic receipt — the bill and the refund slip alike.",
    receiptFooterEn: "Receipt footer (English)",
    receiptFooterEnHint:
      "Printed under the English receipt. Leave it empty and the Arabic line prints there instead, which is what happens today.",
    receiptLanguage: "Receipt language",
    receiptLanguageHint:
      "The language the check and the receipt print in, whatever language the screen is in. Kitchen tickets stay in the screen's language.",
    stationPausedSuffix: "(paused)",
    ordersViaCashierEnabled: "Waiters send through the cashier",
    ordersViaCashierLabel: "Waiters send orders to the cashier first",
    ordersViaCashierHint:
      "When this is on, a waiter cannot send to the kitchen: they tap “Send to cashier”, and the cashier hears a chime and sends the order. Diners' QR requests are accepted or rejected by the cashier only. When it is off, anyone allowed to send to the kitchen sends directly.",
    shiftTrackingEnabled: "Cash drawer tracking",
    shiftTrackingLabel: "Track the cash drawer",
    shiftTrackingHint:
      'When this is on, a drawer must be open to take a payment, and the "Drawer" and "Drawer history" screens appear in the nav.',
    shiftTrackingWarning:
      'While this is on, nobody in the store can settle a bill until someone holding the "Cash drawers" permission opens a drawer. The ready-made Cashier role carries it — but if your till staff are on a custom role, grant it to them on the roles screen first.',
    // UX-round D53 — a section heading, and now its page title too, after general
    // settings split into six screens.
    taxSectionTitle: "Taxes & charges",
    // UX-round D53 — the block that opened the old page with no heading at all.
    receiptSectionTitle: "The printed receipt",
    shiftSectionTitle: "Cash drawers",
    // Owner concern 2: the number alone never says which day it is making.
    dayStartNow: "Right now a sale counts as {date}.",
    kitchenWorkflowTitle: "Kitchen workflow",
    kitchenWorkflowApplies: "Applies to plates sent after you save.",
    kitchenWorkflowPaperOnly: "Paper only",
    kitchenWorkflowPaperOnlyHint:
      "The ticket prints when the order is sent, which starts the food and deducts stock; cooks never touch a screen, and each station needs a printer. A bill can't close until its ticket prints, and prep and pass times show as “not timed” in reports.",
    kitchenWorkflowPaperAndPass: "Paper and a pass screen",
    kitchenWorkflowPaperAndPassHint:
      "The printed ticket starts the food and deducts stock, and the expediter taps Serve or Hand over once per order; it needs a printer and a screen, and a bill can't close until its ticket prints. Ready plates leave after the hide time, prep time shows as “not timed”, and a disconnected screen needs a manual “Refresh screen”.",
    kitchenWorkflowOneTap: "One-tap screen",
    kitchenWorkflowOneTapHint:
      "The food starts and stock is deducted when the order is sent, and the cook taps Ready once; no printer needed, no bill is held, and queue time shows as “not timed”. Ready plates leave after the hide time, and a disconnected screen needs a manual “Refresh screen”.",
    kitchenWorkflowTwoTap: "Two-tap screen",
    kitchenWorkflowTwoTapHint:
      "The cook taps Start, which deducts stock, then Ready — the only workflow that times the cook; no printer needed, and a bill can't close until its plates are started. Ready plates leave after the hide time, and a disconnected screen needs a manual “Refresh screen”.",
    kitchenWorkflowNone: "No screen or printer",
    kitchenWorkflowNoneHint:
      "The food starts and stock is deducted when the order is sent, and nobody in the kitchen taps anything; no device needed, and no bill is held. Every kitchen time shows as “not timed” in reports.",
    kitchenWorkflowAdvanced: "Advanced",
    kitchenWorkflowSub: "How an order travels from the till to the plate",
    kitchenWorkflowPaperOnlyShort: "The printed ticket starts the food and deducts stock; cooks never touch a screen.",
    kitchenWorkflowPaperAndPassShort: "The printed ticket starts the food; the expediter hands orders over from a screen.",
    kitchenWorkflowOneTapShort: "The food starts when the order is sent; the cook marks it ready on the screen.",
    kitchenWorkflowTwoTapShort: "The cook starts each plate, then marks it ready — the only way prep time is timed.",
    kitchenWorkflowNoneShort: "The food starts and stock is deducted when the order is sent; nobody in the kitchen taps.",
    kitchenFactPrinter: "A printer at each station",
    kitchenFactPrinterAndScreen: "A printer and a pass screen",
    kitchenFactScreen: "A kitchen screen, no printer",
    kitchenFactNoDevice: "No device at all",
    kitchenFactPassTap: "One tap per order at the pass",
    kitchenFactOneTap: "The cook taps Ready once",
    kitchenFactTwoTap: "Start, then Ready",
    kitchenFactNoTaps: "Nobody taps in the kitchen",
    kitchenFactHoldsPrint: "A bill can't close before its ticket prints",
    kitchenFactHoldsStart: "A bill can't close before its plates are started",
    kitchenFactNoHold: "No bill is held",
    kitchenWorkflowPresets: "Start from",
    kitchenWorkflowPresetsHint: "Fills the fields only; nothing is saved until you press Save.",
    kitchenPresetGrillHall: "Grill & dining room",
    kitchenPresetCounter: "Counter",
    kitchenPresetDelivery: "Delivery",
    kitchenPresetCustom: "Custom",
    kitchenPresetTargets:
      "Also sets the service targets: queue {queue} min, prep {prep} min, pass {pass} min.",
    kitchenAgeOff: "Hide ready plates after",
    kitchenAgeOffHint:
      "0 = never hide. Must be longer than the pass target ({minutes} min). Takeaway and delivery never hide until handed over.",
    kitchenAgeOffInvalid:
      "Enter 0, or a time longer than the pass target ({minutes} min), up to 240 minutes.",
    kitchenChitGrouped: "Group identical plates on one ticket line",
    kitchenChitGroupedHint:
      "Like “3× Kebab”; void, change and remake papers stay one plate per line.",
    kitchenWorkflowLastChange: "Last changed: {name} · {date}",
    kitchenWorkflowRemovedMember: "a removed member",
    kitchenWorkflowUntimed: "What nobody records can't be measured: reports show it as “not timed”, not zero.",
    kitchenElsewhereNote: "Who cooks what is set per station.",
    kitchenStationsRow: "Kitchen stations",
    serviceChargeOffNote:
      "The service charge is currently off, at the top of this page, so these choices reach no bill until you turn it on.",
    serviceChargeEnabled: "Service charge",
    serviceChargeLabel: "We add a service charge to bills",
    serviceChargeHint:
      "A percentage added to every newly opened table's bill — like the usual 12% for table service.",
    serviceChargePercent: "Service charge %",
    vatEnabled: "VAT",
    vatLabel: "We charge VAT",
    vatHint:
      "For tax-registered restaurants — the current official rate is 14%.",
    vatPercent: "VAT %",
    vatOnServiceCharge: "What VAT is computed on",
    vatOnServiceChargeLabel: "VAT applies to the bill + service charge",
    vatOnServiceChargeHint:
      "The usual Egyptian treatment: the service charge is itself taxed. Untick it and VAT is computed on the items alone.",
    pricesIncludeTax: "Menu prices",
    pricesIncludeTaxLabel: "Menu prices already include tax and service",
    pricesIncludeTaxHint:
      "Ticked: the menu price is exactly what the customer pays, and the receipt shows what is inside it. Unticked: the charges are added on top — the more common way.",
    taxRegistrationNumber: "Tax registration number",
    taxRegistrationHint: "Printed at the top of every receipt when set.",
    // Unit 3 — the store FACTS reports derive their bases from. Not switches
    // that change a calculation: both figures are computed either way, and
    // these decide which one is read first. Hence "reports", never "bills".
    factsSectionTitle: "This store's tax position",
    vatRegistered: "VAT registration",
    vatRegisteredLabel: "This store is registered for VAT",
    vatRegisteredHint:
      'Not the same as "We charge VAT" above: this is the store\'s own status. A registered store reclaims the VAT inside what it buys, so the report states an estimated cost net of it beside the measured cost.',
    serviceChargeKeptByHouse: "Who keeps the service charge",
    serviceChargeKeptByHouseLabel: "The house keeps it (not the team)",
    serviceChargeKeptByHouseHint:
      "In Egypt the 12% is customarily the staff's money, which is the default here. If the house keeps it, it is house revenue and the margin is taken against a base that includes it.",
    serviceChargeChannelsTitle: "Service charge by order type",
    serviceChargeChannelsHint:
      "The service charge itself is switched on at the top of this page, and dine-in bills always carry it while it is on. Takeaway and delivery each get their own answer here, taken when the bill opens.",
    serviceChargeOnTakeaway: "Takeaway",
    serviceChargeOnTakeawayLabel: "Charge service on takeaway",
    serviceChargeOnDelivery: "Delivery",
    serviceChargeOnDeliveryLabel: "Charge service on delivery",
    deliveryFeeVatEnabled: "VAT on the delivery fee",
    deliveryFeeVatEnabledLabel: "The delivery fee is taxed",
    deliveryFeeVatEnabledHint:
      "Taxed by default. This VAT is kept apart from the food's: it is charged on the fee alone, and the fee stays outside food margin.",
    deliveryFeeDefault: "Default delivery fee",
    deliveryFeeDefaultHint:
      "Snapshotted onto each delivery bill when it opens, and overridable there — a far address costs more. Changing it here never reprices a bill already taken.",
    // G13 — an invalid number is refused beside the field, never saved as 0.
    deliveryFeeDefaultInvalid: "Enter zero or a positive number.",
    // F24 (§5.12) — ordering from the storefront: the owner's switch, the
    // answer window, and a read-only line about the till's pause.
    onlineOrderingTitle: "Ordering from the storefront",
    onlineOrderingEnabled: "Online ordering",
    onlineOrderingEnabledLabel:
      "Customers order takeaway or delivery from the storefront",
    onlineOrderingEnabledHint:
      "An order lands on the till as a card the cashier accepts or rejects. No bill opens and no daily number is used before it is accepted; the bill is priced at accept with the default delivery fee.",
    onlineOrderExpiryMinutes: "Time to answer an order",
    onlineOrderExpiryMinutesHint:
      "How many minutes an order waits for the cashier's answer before it expires on its own and the customer is told.",
    onlineOrderExpiryMinutesInvalid:
      "Enter a whole number from 3 to 60 minutes.",
    onlineOrderExpiryMinutesUnit: "min",
    // 2026-10-23 — the owner's switch for the delivery WhatsApp confirmation.
    onlineCustomerConfirmation: "Delivery confirmation",
    onlineCustomerConfirmationLabel:
      "Confirm delivery orders with the customer on WhatsApp",
    onlineCustomerConfirmationHint:
      "We send the customer a WhatsApp message to confirm the delivery order. The order reaches you either way.",
    // 2026-10-23 — what OFF does: an order-received message, no confirm ask.
    onlineCustomerConfirmationOffHint:
      "When off, the customer gets an order-received message with the tracking link, without being asked to confirm. The order reaches you either way.",
    onlineOrderingPausedNote:
      "The till has paused online orders from the floor screen. It resumes them there, not here.",
    onlineOrderingNotPausedNote:
      "Taking orders now. Pausing — on a busy night, say — is done from the floor screen on the till, not here.",
    writeOffVatCountedAsOwed: "VAT on written-off bills",
    writeOffVatCountedAsOwedLabel: "Count a written-off bill's VAT as owed",
    writeOffVatCountedAsOwedHint:
      "Default: not counted — tax on money that never arrived. Both figures appear on the report either way; this only decides which one leads.",
    factsUnusedNote:
      "These are in force now: a bill carries an order type (dine-in / takeaway / delivery), the service charge on takeaway and delivery is decided here, and the delivery fee has a column of its own outside the food price. They are read only when a bill opens (and when its charges are corrected); an open bill keeps what it started with.",
    taxSnapshotNote:
      "A change here reaches tables opened AFTER it only — a table already seated (and its unpaid cheque) keeps the rates it was opened under.",
    // Round-table finding 3 — the field Unit D shipped the column and the
    // sentence without, leaving every store on zero with no way to move it.
    varianceSectionTitle: "Drawer variance",
    cashVarianceTolerance:
      "Difference to let pass without calling it out (EGP)",
    cashVarianceToleranceHint:
      "A 0.25 drift on a night of 400 bills is not theft. Leave it at 0 to have every difference called out.",
    // D146 — the most a discount at the bill may take.
    billDiscountMaxPctLabel: "Largest bill discount",
    billDiscountMaxPctHint:
      "A percent of the net food, before service and VAT. It binds everyone, the owner included.",
    billDiscountMaxPctRange: "A whole number from {min} to {max}",
    cashVarianceToleranceNote:
      "Display only — never a limit or an allowance. Every variance is stored exactly as measured; this decides when the screen says something about it.",
    cashVarianceToleranceRange: "Enter a number between 0 and {max}.",
    refundSectionTitle: "Refunds",
    refundLateAfterMinutes: "Mark a refund late after",
    refundLateAfterMinutesRange:
      "This must be between {min} minutes and {max} minutes (7 days).",
    refundLateAfterMinutesHint:
      "Minutes from the moment the bill was paid. Default 240 (4 hours); the smallest is 5 and the largest 10080 (7 days). Leave the box empty and the stored value is kept.",
    refundLateNote:
      "This number FLAGS, it never refuses. The refund goes through either way — past this age the cashier is warned before confirming, and the movement is recorded as late so it stands out in the drawer review and the reports. A change here applies to future refunds only and never restates one already recorded.",
    addMenuItem: "Add a menu item",
    addonsListEmpty: "No groups match the search",
    addonsPickTitle: "Pick a group",
    addonsPickBody: "Choose a group from the list to edit it here.",
    addonGroupKind: "What does this group do?",
    addonGroupKindVariant: "Changes the dish itself",
    addonGroupKindExtra: "Adds to the dish",
    addonGroupKindVariantHint:
      "Like Size. The profit report breaks each dish down by this choice.",
    addonGroupKindExtraHint:
      "Like Sauces. The choice is priced into the plate, but never splits the dish in the report.",
    addonGroupType: "How many may be picked?",
    addonGroupPickOne: "Pick one",
    addonGroupPickMany: "Pick many",
    addonGroupTypeHint:
      "A rule for this group only — it has nothing to do with whether the group changes the dish or adds to it.",
    addonGroupRequired: "Required",
    addonGroupRequiredSub: "Cashier must pick before adding the item",
    addonGroupCustomBounds:
      "This group is set to {min}–{max}. Switching will change that.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    addonGroupUsageOne: "Attached to 1 menu item",
    addonGroupUsageTwo: "Attached to 2 menu items",
    addonGroupUsageFew: "Attached to {count} menu items",
    addonGroupUsage: "Attached to {count} menu items",
    addonGroupUsageNone: "Not attached to any item yet",
    addonOptions: "Options",
    addonColGroup: "Group",
    addonColType: "Type",
    addonColUsedOn: "Used on",
    addonColActive: "Active",
    addonTillPreview: "The cashier sees: {name} — {label}: {items}.",
    addonTillPreviewKept:
      "Invoices already sold keep the options as they were.",
    addonOptionsSub: "Each option can add to the item's price.",
    addonOptionAdd: "+ Add option",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    addonOptionCountOne: "1 option",
    addonOptionCountTwo: "2 options",
    addonOptionCountFew: "{count} options",
    addonOptionCount: "{count} options",
    addonRequiredWord: "required",
    addonOptionalWord: "optional",
    addonOptionsEmptyBody: "Add the first choice the cashier will see.",
    addonOptionName: "Name",
    addonOptionNameEn: "English",
    addonOptionPrice: "Extra price",
    addonOptionPlaceholder: "e.g. Large",
    addonOptionsEmpty: "No options yet",
    addonOptionRemove: "Remove option",
    addonLinked: "Linked menu items",
    addonLinkedSub: "The group shows on these items in the POS.",
    // PAPER (2026-10-03): the dish form's quiet "+ Add …" under the rows —
    // the plus is drawn as an icon, so the words carry none.
    addonOptionAddQuiet: "Add option",
    addonLinkSearch: "Link a dish…",
    addonLinkNoMatch: "No dish by that name",
    addonLinkedNone: "Not on any dish yet",
    addonUnlink: "Unlink dish",
    addonUnsaved: "Unsaved changes",
    addonNewTitle: "New add-on group",
    addonNewSub: "The cashier sees it while taking the order",
    addonCreate: "Create group",
    addonNeedName: "Write the group name",
    addonNeedOption: "Add at least one option",
    addonNeedOptionNames: "Every option needs a name",
    addonCreated: "Group created — link it to menu items",
    addonRestoreOption: "Restore option",
    addonOptionRestored: "Option restored",
    addonReactivateTitle: "Bring this group back?",
    addonReactivateBody:
      "It shows to the cashier again on every item it was linked to.",
    addonReactivated: "Group is back",
    addonDeactivate: "Pause group",
    addonDeactivateTitle: "Pause this group?",
    addonDeactivateBody:
      "It disappears from the till and from every item using it. Past invoices are untouched, and you can bring it back any time.",
    addonDeactivated: "Group paused",
    addonReactivate: "Bring the group back",
    // B117/E1 — see the Arabic note: this was the only back-office screen with
    // no runtime gate, so a cook was landed on it and read its heading while
    // the API 403'd underneath.
    menuNoAccessTitle: "You cannot view the menu list",
    menuNoAccessBody:
      "This screen shows item prices and their cost, which needs the menu-view permission. To 86 a dish, use the kitchen screen.",
    menuSearchPlaceholder: "Search the menu…",
    menuNewItem: "New item",
    menuColSection: "Section",
    menuFilterButton: "Filter",
    menuFilterClear: "Clear filter",
    paperMenuSubtitle:
      "Everything on the menu. Toggle items off when they sell out.",
    paperAddonsSubtitle:
      "Extras, sizes and choices guests pick when they order a dish.",
    paperCategoriesSubtitle:
      "Group dishes for the menu and the till. The order here is the order staff see at the POS.",
    paperCatalogNew: "New",
    paperCatalogViews: "View",
    paperCatalogViewTable: "Table",
    paperCatalogViewGallery: "Gallery",
    paperCatalogColCategory: "Category",
    paperCatalogColOnPos: "On POS",
    paperCategoriesSearch: "Search categories",
    paperCategoriesCountOne: "1 category",
    paperCategoriesCountTwo: "2 categories",
    paperCategoriesCountFew: "{count} categories",
    paperCategoriesCount: "{count} categories",
    paperDishesCountOne: "1 dish",
    paperDishesCountTwo: "2 dishes",
    paperDishesCountFew: "{count} dishes",
    paperDishesCount: "{count} dishes",
    paperAddonsActiveOne: "1 active",
    paperAddonsActiveTwo: "2 active",
    paperAddonsActiveFew: "{count} active",
    paperAddonsActive: "{count} active",
    paperGroupsCountOne: "1 group",
    paperGroupsCountTwo: "2 groups",
    paperGroupsCountFew: "{count} groups",
    paperGroupsCount: "{count} groups",
    paperAddonKindVariant: "Variant",
    paperAddonKindExtra: "Extra",
    paperAddonUnused: "Not used yet",
    paperAddonsTitle: "Add-on groups",
    paperDishEdit: "Edit",
    paperDishAllSettings: "All settings",
    paperDishReplacePhoto: "Replace photo",
    paperDishUsePhoto: "Use photo",
    paperDishUseIcon: "Use icon",
    paperDishRowCategory: "Category",
    paperDishRowBasePrice: "Base price",
    paperDishRowFoodCost: "Food cost",
    paperDishRowMargin: "Margin",
    paperDishRowStatus: "Status",
    paperDishRowSold: "Sold",
    paperDishRowRevenue: "Revenue today",
    paperDishRowCanMake: "Can still make",
    paperDishNoRecipe: "No recipe yet",
    paperDishMarginLine: "{pct} · {amount} profit per serving",
    paperDishSoldToday: "{quantity} today",
    paperDishServingsNone: "None left",
    paperDishServingsOne: "1 more serving",
    paperDishServingsTwo: "2 more servings",
    paperDishServingsFew: "{count} more servings",
    paperDishServings: "{count} more servings",
    paperDishPlatesNone: "0 servings",
    paperDishPlatesOne: "1 serving",
    paperDishPlatesTwo: "2 servings",
    paperDishPlatesFew: "{count} servings",
    paperDishPlates: "{count} servings",
    paperDishInStock: "{quantity} in stock",
    paperDishNotTracked: "Not tracked",
    paperDishAvailable: "Available",
    paperDishSoldOut: "Sold out",
    paperDishOffMenu: "Off the menu",
    paperDishSizes: "Sizes",
    paperDishAddons: "Add-ons",
    paperDishIngredientsPerServing: "Ingredients per serving",
    paperDishBase: "Base",
    paperDishNoIngredients: "No ingredients linked. Use Edit to add them.",
    paperDishCrumbNew: "Menu / New item",
    paperDishCrumbEdit: "Menu / Edit item",
    paperDishAddToMenu: "Add to menu",
    paperDishSaveChanges: "Save changes",
    paperDishMediaHint: "Click to pick a badge or upload a photo",
    paperDishChangeIcon: "Change icon",
    paperDishTabBadges: "Badges",
    paperDishTabUpload: "Upload photo",
    paperDishUploadTitle: "Upload a dish photo",
    paperDishUploadHint:
      "PNG or JPG, square works best. Shown on the POS tile and the menu.",
    paperDishRemovePhoto: "Remove photo",
    paperDishAllIcons: "All icons",
    paperDishNamePlaceholder: "Dish name",
    paperDishNeedName: "Give it a name first.",
    paperDishEnglishName: "English name",
    paperDishPrice: "Price",
    paperDishPrepTime: "Preparing time",
    paperDishPrepTimeHint: "Leave empty to use the store's usual time.",
    // D131 — what a guest sees on the dish in the table menu.
    dishChefPick: "Chef's pick",
    dishSpicy: "Spicy",
    dishVegetarian: "Vegetarian",
    dishFlagsHint: "Shown to the guest on the dish in the table menu.",
    dishCalories: "Calories",
    dishCaloriesUnit: "kcal",
    dishCaloriesHint: "Optional. Leave empty if you don't know them.",
    dishCaloriesRange: "Calories must be a whole number from {min} to {max}.",
    paperDishOnMenu: "On the menu",
    paperDishPickCategories: "Pick categories",
    paperDishSizesHint: "Price change on top of the base price",
    paperDishSizeName: "Size name",
    paperDishRegular: "Regular",
    paperDishAddSize: "Add size",
    paperDishAddonsHint: "Optional extras staff can pick at the till",
    paperDishAddonName: "Add-on name",
    paperDishNoAddons: "No add-ons yet.",
    paperDishAddAddon: "Add add-on",
    paperDishAddonSource: "Where this add-on comes from",
    paperDishAddGroup: "Add a ready group",
    paperDishSearchGroups: "Search groups or options",
    paperDishNoGroupMatch: "No group by that name",
    paperDishLibraryGroup: "{name} · from the add-ons library",
    paperDishDetachGroup: "Remove group",
    paperDishAddonFromStock: "From stock",
    paperDishAddonFreeText: "Free text",
    paperDishAddonPerPick: "Amount per pick",
    paperDishAddonNotStock:
      "Not from stock — goes on the bill and to the kitchen only",
    paperDishAddonUseFreeText: "Type a free name instead",
    paperDishFreeAddonPlaceholder: "e.g. well done, no onions",
    paperDishAddonSharedStock:
      "Takes {name} — shared with other dishes, change it on Add-ons",
    paperDishAddonCostedHere:
      "Costed for this dish on its own — change it under “What each option costs this item” on the dish page",
    paperDishIngredients: "Ingredients",
    paperDishIngredientsHint:
      "Used per serving. Stock goes down with each sale.",
    paperDishChooseIngredient: "Choose ingredient",
    paperDishNoIngs: "No ingredients linked yet.",
    paperDishAddIngredient: "Add ingredient",
    paperDishFoodCostPerServing: "Food cost per serving",
    paperDishMarginPct: "{pct}% margin",
    paperDishSharedHint: "Shared with other dishes — rename it on Add-ons",
    paperDishRemove: "Remove",
    paperDishPartSaved:
      "The dish was saved, but not everything with it: {reason}",
    menuFilterActive: "Active",
    menuFilterPaused: "Paused",
    menuFilterSoldOut: "Out today",
    menuItemPaused: "Paused",
    menuItemSoldOut: "Out",
    /* Same defect as B67: no pluralisation in this runtime, so "1 addon
       groups" printed under every single-group dish. Found by B71's browser
       round on a screen that was not that unit's. */
    menuAddonGroupsCountOne: "1 addon group",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    menuAddonGroupsCountTwo: "2 addon groups",
    menuAddonGroupsCountFew: "{count} addon groups",
    menuAddonGroupsCount: "{count} addon groups",
    menuNoMatches: "No item by that name",
    menuNoMatchesBody: "Try another name or clear the search.",
    clearSearch: "Clear search",
    menuListTitle: "Items",
    menuUncategorized: "No section",
    menuEmptyTitle: "Your menu is empty",
    menuEmptyBody:
      "Add your first item above and it'll be ready to order straight away.",
    menuDuplicate: "Duplicate item",
    menuDuplicated: "Copy created",
    menuCopySuffix: " (copy)",
    menuUnsaved: "Unsaved changes",
    menuRecipeCost: "Estimated cost",
    menuRecipePartial: "An ingredient is missing — cost is incomplete",
    /* D139 — a recipe ingredient never purchased: its cost is unknown, not 0. */
    menuRecipeUnknownOne:
      "1 ingredient has never been purchased — its cost is unknown and left out",
    menuRecipeUnknownTwo:
      "2 ingredients have never been purchased — their cost is unknown and left out",
    menuRecipeUnknownFew:
      "{count} ingredients have never been purchased — their cost is unknown and left out",
    menuRecipeUnknown:
      "{count} ingredients have never been purchased — their cost is unknown and left out",
    menuRecipeProfit: "Profit",
    // D37 on this surface too: a price tier discounts a dish at sale time, so
    // there is no single "charged price" to margin against — only a correct
    // label for the one number this card can show.
    menuRecipeMarginListPriceNote:
      "Margin is against the list price, before any price-tier discount.",
    menuRecipeReportLink: "See the real profit on the report",
    // Audit round 2, Phase 3 — the basis of the recipe card's figures, as prose.
    menuRecipeCostBasis:
      "An estimate from the current recipe at the current average cost, before service charge, VAT, platform commission and packaging. Each plate's actual cost is recorded when it is prepared and read on the Item profitability report.",
    saveAndAddAnother: "Save and add another",
    menuItemAdded: "{name} added",
    menuFilterUnfiledClear: "Show all items",
    addArea: "Add an area",
    addTable: "Add a table",
    tablesListTitle: "Tables",
    floorEmptyTitle: "No tables yet",
    floorEmptyBody:
      "Add an area first — the hall or the terrace, say — then add its tables.",
    addIngredient: "Add an ingredient",
    ingredientActions: "Purchase or adjustment",
    ingredientsEmptyTitle: "No ingredients yet",
    ingredientsEmptyBody:
      "Add your ingredients so you can record purchases and know what each dish costs.",
    ingredientsNoAccessTitle: "You can't view stock",
    ingredientsNoAccessBody:
      "This screen needs the costing permission. Ask the restaurant owner to add it to your role.",
    backToMenu: "Back to the menu",
    menuItemDetailTitle: "Item",
    menuItemDetailsSection: "Item details",
    menuItemNotFoundTitle: "That item isn't here",
    menuItemNotFoundBody:
      "It may have been removed. Go back to the menu and pick another item.",
    menuItemStatus: "Status",
    menuItemActive: "Live on the menu",
    menuItemInactive: "Paused",
    deactivateHint:
      "A paused item is hidden from the till and from customers. Past invoices are untouched.",
    menuItemAvailability: "Available today",
    menuItemAvailable: "Available",
    menuItemSoldOutState: "Out today",
    menuItemAvailabilityHint:
      "When an item runs out, mark it Out: it stops being orderable at the till and on the QR menu, and stays visible to staff with a badge. This does not pause the item and does not change the menu itself.",
    recipeTitle: "Recipe",
    recipeSubtitle: "What comes out of stock every time this item is sold.",
    recipeAddIngredient: "+ Ingredient",
    recipeEmptyTitle: "This item has no recipe yet",
    recipeEmptyBody:
      "The recipe is what connects a sale to your stock: once the item sells, these quantities come off your ingredients on their own, and only then can you see what the item costs you and what it earns. Without one, it sells without anything leaving stock.",
    recipeEmptyAction: "Add the first ingredient",
    recipeNoIngredientsTitle: "Record your ingredients first",
    recipeNoIngredientsBody:
      "A recipe is built from stock ingredients. Record yours first — meat, cheese, bread — then say how much of each an item uses.",
    recipeNoIngredientsAction: "Go to ingredients",
    addRecipeLine: "Add an ingredient",
    editRecipeLine: "Edit the quantity",
    deleteRecipeLine: "Remove ingredient",
    deleteRecipeLineTitle: "Remove this ingredient from the recipe?",
    deleteRecipeLineBody:
      "It will no longer come off stock when this item is sold.",
    recipeIngredient: "Ingredient",
    recipePickIngredient: "Pick an ingredient",
    recipeNoIngredientMatch: "No ingredient by that name",
    recipeIngredientRequired: "Pick the ingredient first.",
    recipeIngredientLocked:
      "To use a different ingredient, remove this line and add another one.",
    recipeQuantity: "Quantity per serving",
    recipeQuantityHint:
      "The unit comes from the ingredient itself — it is never typed here.",
    recipeQuantityRequired: "Enter a quantity greater than zero.",
    recipePerServing: "per serving",
    // ---- Add-ons and sizes (groups + options + attaching to an item) ----
    addonsTitle: "Add-ons and sizes",
    addonsDescription:
      "A menu item has one price. Every price difference — small, medium, large, extra cheese, a different sauce — is built here as an add-on group and attached to the item.",
    addonGroupsEmptyTitle: "No add-on groups yet",
    addonGroupsEmptyBody:
      'This is where sizes and extras are built. Create a group like "Size", put the options inside it with the price difference for each, then attach it to the items that come in sizes.',
    addonGroupsEmptyAction: "Create the first group",
    addAddonGroup: "New group",
    editAddonGroup: "Edit group",
    addonGroupName: "Group name",
    addonGroupNameEn: "English name",
    addonGroupNameHint:
      'What the cashier sees above the options, like "Size" or "Extras".',
    addonGroupNameRequired: "Enter the group name.",
    addonGroupMin: "Fewest choices",
    addonGroupMax: "Most choices",
    addonGroupMinHint:
      "Zero makes the group optional. One means a choice is required before the item can be added.",
    addonGroupMaxHint: "Leave it empty for no upper limit.",
    addonGroupCountInvalid: "Enter a whole number of zero or more.",
    addonGroupBoundsInvalid:
      "The most choices must be equal to or greater than the fewest.",
    addonGroupOptional: "Optional",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    addonGroupUpToOne: "1 only",
    addonGroupUpToTwo: "Up to 2",
    addonGroupUpToFew: "Up to {max}",
    addonGroupUpTo: "Up to {max}",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    addonGroupAtLeastOne: "At least 1",
    addonGroupAtLeastTwo: "At least 2",
    addonGroupAtLeastFew: "At least {min}",
    addonGroupAtLeast: "At least {min}",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    addonGroupRangeOne: "Exactly 1",
    addonGroupRangeTwo: "{min} to 2",
    addonGroupRangeFew: "{min} to {max}",
    addonGroupRange: "{min} to {max}",
    addonGroupPaused: "Paused",
    addonGroupActiveLabel: "Group is live",
    addonGroupActiveHint:
      "Paused groups are not offered with any item. Past invoices stay exactly as they were.",
    addonGroupNoAddons:
      "This group has no options yet — add one so the cashier can pick it.",
    addAddon: "Add option",
    editAddon: "Edit option",
    addonName: "Option name",
    addonNameEn: "English name",
    addonNameRequired: "Enter the option name.",
    addonPrice: "Price difference",
    addonPriceHint: "Added on top of the item price. Zero means no difference.",
    addonPriceRequired: "Enter a price of zero or more.",
    addonActiveLabel: "Option is live",
    addonIngredient: "Ingredient it uses",
    addonIngredientHint:
      "If this option takes stock — extra cheese, say — pick the ingredient and the quantity together. Leave both empty if it is only a price difference.",
    addonPickIngredient: "Pick an ingredient",
    addonClearIngredient: "No ingredient",
    addonNoIngredientMatch: "No ingredient by that name",
    addonQuantity: "Quantity each time",
    addonQuantityRequired: "Enter a quantity greater than zero.",
    addonPairingInvalid:
      "Both or neither: an option either takes an ingredient with a quantity out of stock, or it is only a price difference.",
    addonIngredientLocked:
      "This option is tied to a stock ingredient. Changing that needs the stock permission.",
    addonDepletes: "Takes {quantity} {unit} out of stock",
    addonNoDepletion: "Price only — nothing leaves stock",
    addonPaused: "Paused",
    menuItemAddonsTitle: "Add-ons and sizes",
    addonOverrideEdit: "Set this item's prices",
    addonRecipeTitle: "What each option costs this item",
    addonRecipeSubtitle:
      "A large portion eats more. Leave it blank and the whole upcharge is counted as profit.",
    addonRecipeEdit: "Set ingredients",
    addonRecipeAddLine: "Add ingredient",
    addonReplacesLabel: "Replaces",
    addonReplacesNone: "Adds only",
    addonReplacesHint:
      "If this option stands in for something in the dish's recipe — penne instead of fettuccine — pick what it takes off. Without that, the plate depletes both.",
    addonRecipeUsesOwn: "Uses the option's own ingredient.",
    addonRecipeUncosted:
      "Takes nothing extra — the whole upcharge counts as profit.",
    addonRecipeDuplicate: "That ingredient is listed twice.",
    addonRecipeDiscard: "You have unsaved ingredients. Discard them?",
    addonRecipeQuantityInvalid: "Enter a quantity greater than zero.",
    addonRecipePickIngredient: "Pick an ingredient first.",
    addonRecipeNoIngredientsTitle: "Record your ingredients first",
    addonRecipeNoIngredientsBody:
      "Costing an option needs ingredients to cost it against.",
    addonRecipeNoIngredientsAction: "Go to ingredients",
    addonRecipeNoOptionsTitle: "No options attached",
    addonRecipeNoOptionsBody:
      "Attach an add-on group to this item first, then you can cost each option.",
    addonOverrideWas: "· was {price}",
    addonOverrideDiscard: "You have unsaved prices. Discard them?",
    addonOverrideTooManyDecimals: "A price takes at most two decimal places.",
    addonOverrideOffersNothing:
      "Every option in this group is switched off for this item, so customers see none of it.",
    addonOverrideHint:
      "Leave a box empty to charge what the group charges. Switch an option off if this item is not sold with it.",
    addonOverrideOfferedLabel: "{name} is offered on this item",
    addonOverrideFree: "Free",
    addonOverrideInherit: "Group price {price}",
    addonOverriddenByItems: "Items pricing this themselves: {count}",
    addonOverrideNegative: "A price cannot be negative.",
    addonOverrideNotAPrice: "Enter the price in numbers.",
    addonOverrideUnsatisfiable:
      "This group is required, and at least {count} of its options have to stay available.",
    menuItemAddonsSubtitle:
      "The groups attached to this item — what the cashier sees while taking the order.",
    menuItemAddonsEmptyTitle: "This item has no add-ons",
    menuItemAddonsEmptyBody:
      "The item has a single price. If it comes in sizes — small, medium, large — or with extras that change the price, attach an add-on group to it.",
    menuItemAddonsEmptyAction: "Attach a group",
    menuItemAddonsNoGroupsTitle: "Create an add-on group first",
    menuItemAddonsNoGroupsBody:
      'There are no groups yet. Build one once on the add-ons screen — "Size", for instance — then attach it to any item.',
    menuItemAddonsNoGroupsAction: "Go to add-ons",
    // Combos / meal deals (S16, D52).
    comboTitle: "Combo",
    comboSubtitle:
      "The dishes inside this item — sold as one plate at the combo's own price.",
    comboScopeLine:
      "The price charged is this item's own price, not the sum of its parts. Stock depletes for every dish inside it x its quantity, at preparing.",
    // The a-la-carte sum against the combo's own price. The second case — the
    // bundle costing MORE than its parts — is what happens when a component's
    // price is edited months later and nobody revisits the combos it sits in.
    comboSaving:
      "Parts on their own {parts} - combo {combo} - the guest saves {saving}.",
    comboPricedAbove:
      "Parts on their own {parts} but the combo is {combo} - {over} MORE.",
    comboEmptyBody:
      "This item is not a combo yet. Add the first dish below to make it one; clear the list to make it a normal dish again.",
    comboAddLabel: "Add a dish to the combo",
    comboAddPlaceholder: "Pick a dish",
    comboAddNoMatch: "No dish by that name",
    comboNestingHint: "A combo cannot be put inside another combo.",
    comboQuantity: "Quantity",
    comboRemove: "Remove",
    // UX-round D58 — says what it saves. "Save the combo" beside the item's own "Save"
    // was two buttons using the same word for two different things.
    comboSave: "Save what's in the combo",
    comboComponentOff: "86'd",
    comboUnknownComponent: "Removed item",
    comboBlockedByComponent:
      "A dish inside this combo is 86'd, so the combo cannot be sold right now. Put that dish back on to bring the combo back.",
    comboNoCandidatesTitle: "You need other dishes first",
    comboNoCandidatesBody:
      "There are no other active dishes to put inside this combo. Create them on the menu screen, then come back.",
    comboNoCandidatesAction: "Go to the menu",
    attachAddonGroup: "Attach a group",
    attachAddonGroupPick: "Pick a group",
    attachAddonGroupRequired: "Pick a group first.",
    attachAddonGroupNoneLeft: "Every group is already attached to this item.",
    attachAddonGroupNoMatch: "No group by that name",
    detachAddonGroup: "Remove",
    detachAddonGroupTitle: "Remove this group from the item?",
    detachAddonGroupBody:
      "The cashier will no longer be able to pick these add-ons with this item. The group itself stays and can be attached to other items.",

    categoriesTitle: "Menu sections",
    categoriesNoAccessTitle: "This screen is not available to you",
    categoriesNoAccessBody:
      "Menu sections are set up by the owner or a manager. Ask them if you need to get in.",
    floorNoAccessTitle: "This screen is not available to you",
    floorNoAccessBody:
      "Areas and tables are set up by the owner. If you need to change them, ask for the manage-floor permission.",
    addonsNoAccessTitle: "Add-ons are not available to you",
    addonsNoAccessBody:
      "The add-ons screen needs the menu view permission. If you are meant to work on it, ask the owner to add it to your role from the team screen.",
    indexNoAccessTitle: "Nothing in the back office is open to you",
    indexNoAccessBody:
      "Your account holds no permission for any back-office screen. If you are meant to reach something here, ask the owner to adjust your role from the team screen.",
    indexNoAccessToKitchen: "Go to the kitchen board",
    indexNoAccessToPos: "Go to the tables screen",
    categoriesEmptyTitle: "No sections yet",
    categoriesEmptyBody:
      'A section is what keeps the menu readable for the cashier and the customer instead of one long list of items. Create one such as "Grills", then file items into it from the item screen.',
    categoriesEmptyAction: "Create the first section",
    newCategoryTitle: "New section",
    categoryNameAr: "Section name",
    categoryNameArPlaceholder: "e.g. Grills",
    categoryNameEn: "English name (optional)",
    categoryNameEnPlaceholder: "Grills",
    categoryNameTooShort: "The section name needs at least two characters.",
    categoryNameEnTooShort:
      "The English name needs at least two characters, or leave it empty.",
    categoryEmptyShort: "Empty",
    categoryColOrder: "Order",
    categoryColDishes: "Dishes",
    categoryColItems: "Items",
    categoryColShown: "Shown",
    categoryAvailableNone: "None available",
    categoryAvailableOne: "1 available",
    categoryAvailableTwo: "2 available",
    categoryAvailableFew: "{count} available",
    categoryAvailable: "{count} available",
    categoryItemsLabel: "Items in this section",
    categoryItemsHint: "An item can sit in more than one section.",
    categoryItemsEmpty:
      "There are no menu items yet. Create one from the menu screen first.",
    categoryItemsReadOnly:
      "You cannot change which items are filed here, so this list is read-only.",
    categoryCreateCta: "Create section",
    categoryHiddenBadge: "Hidden",
    categoryShowOnMenu: "Show this section on the menu",
    categoryHideFromMenu: "Hide this section from the menu",
    categoryVisibilityFailed: "We could not change whether this section shows.",
    categoryIconLabel: "Icon",
    // The section's badge from the Doodles library (2026-10-03). The emoji is
    // now named for what it does: it shows beside the name on the online menu.
    categoryBadgeChange: "Change icon",
    categoryBadgeAll: "All icons",
    // The sections table's two sales columns, today's (PAPER).
    categoryColSold: "Sold",
    categoryColRevenue: "Revenue",
    categorySalesTodayHint:
      "Today so far: plates billed and food money, per section",
    stockSubtitle: "Ingredients come off with the recipes as items sell.",
    statStockValue: "Stock value",
    /* D139 — a never-purchased ingredient has no cost, so its value is not in
       the total — and the line says how many. */
    stockValueUnknownOne: "1 ingredient with no known cost is not in it",
    stockValueUnknownTwo: "2 ingredients with no known cost are not in it",
    stockValueUnknownFew: "{count} ingredients with no known cost are not in it",
    stockValueUnknown: "{count} ingredients with no known cost are not in it",
    stockSearch: "Search ingredients…",
    stockListEmpty: "No ingredients match the filters",
    tableLevel: "Level",
    tableValue: "Value",
    tableStockStatus: "Status",
    stockStatusOk: "OK",
    stockStatusLow: "Low",
    stockStatusOut: "Out",
    stockNoThreshold: "No minimum recorded",
    // UX-round D57 — the column drew an identical full bar on every row: no scale, no
    // minimum marker, no number. The marker is the halfway point exactly,
    // because the bar is measured against (minimum × 2).
    stockLevelScale: "Minimum {min}",
    stockLevelAria: "Level {pct}% — minimum {min}",
    restockTitle: "Restock",
    restockSubtitle: "Adds to the quantity on hand.",
    /* PAPER (2026-10) — the count line above the stock table, and the filter
       behind one small menu. Chosen on the TOTAL, like `invoices.resultCount`. */
    stockCountOne: "{shown} of 1 ingredient",
    stockCountTwo: "{shown} of 2 ingredients",
    stockCountFew: "{shown} of {total} ingredients",
    stockCount: "{shown} of {total} ingredients",
    stockFilter: "Filter",
    stockFilterClear: "Clear filter",
    // PAPER (founder, 2026-10-02) — the redesign's own sentence under "Stock"
    // in the top bar, and its short "New" on the add button.
    // The design says "against par"; the app has no par, only a minimum.
    paperStockSubtitle:
      "Ingredient levels against their minimum. Adjust counts after deliveries.",
    paperStockNew: "New",
    // PAPER's ingredient sheet: the "Stock / New ingredient" crumb and the
    // redesign's words on its save.
    paperNewIngredient: "New ingredient",
    paperAddToStock: "Add to stock",
    // PAPER's "+" asks which: a delivery (a purchase, with its price) or a
    // count correction (no purchase) — so a recount that found more is never
    // recorded as a free delivery.
    paperAddStock: "Add stock",
    paperAddDelivery: "Delivery received",
    paperAddDeliveryHint: "Records a purchase, with its price and supplier",
    paperAddCorrection: "Count correction",
    paperAddCorrectionHint: "The count found more — no purchase",
    tableAdjust: "Adjust",
    restockConfirm: "Confirm restock",
    currentQuantity: "Quantity on hand",
    quantityReceived: "Quantity received",
    openingStock: "Opening stock",
    openingStockHint:
      "How much of this you have right now. Leave empty to record the delivery later.",
    openingStockPaid: "What you paid for it",
    openingStockPaidHint:
      "Cost is derived from this. Without it, dishes using this ingredient cost zero.",
    openingStockPaidRequired: "Enter what you paid, so costing is right.",
    /* D137 — a free delivery lowers the average (the server is right). The
       old hint said an empty total left the average alone; it never did. */
    amountPaidHint:
      "What you paid for this delivery, VAT included. Leave it empty if the delivery was free.",
    newAvgCost: "New average unit cost: {cost}",
    projectedFreeLot:
      "A free delivery: the new units cost nothing, so the average cost goes down.",
    projectedFreeSetsZero:
      "A free delivery with no known-cost stock to average against, so the average cost becomes 0.",
    projectedSetsUnknown:
      "This ingredient has no known cost yet, so this delivery sets the average cost.",
    projectedSetsEmpty:
      "There is no stock on hand to average with, so this delivery sets the average cost.",
    /* D136 — the delivery's business day: today by default, up to 31 days back. */
    purchaseDate: "Delivery date",
    purchaseDateHint:
      "The business day the delivery arrived. You can go back as far as {from}.",
    purchaseDateRequired: "Pick the delivery date.",
    purchaseDateOutOfRange: "Pick a day between {from} and {to}.",
    purchaseDateBackdated:
      "The delivery will count on {date} and show in that day's reports. Nothing used before you record it is re-costed.",
    quantityRequired: "Enter the quantity received.",
    categoryMoveUp: "Move section up",
    categoryMoveDown: "Move section down",
    categoryReorderFailed:
      "We could not save the new order, so we put it back the way it was.",
    categoryModalSubtitle:
      "A section gathers menu items under one name. You set the order of the sections from the list itself.",
    categoryEntityLabel: "Section",
    deleteCategoryTitle: "Delete this section?",
    deleteCategoryBody:
      "The section goes away. No items are filed under it right now.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    deleteCategoryBodyWithItemsOne:
      "The 1 item in this section is NOT deleted — it stays on the menu, just without a section.",
    deleteCategoryBodyWithItemsTwo:
      "The 2 items in this section are NOT deleted — they stay on the menu, just without a section.",
    deleteCategoryBodyWithItemsFew:
      "The {count} items in this section are NOT deleted — they stay on the menu, just without a section.",
    deleteCategoryBodyWithItems:
      "The {count} items in this section are NOT deleted — they stay on the menu, just without a section.",
    menuItemCategories: "Sections",
    menuItemCategoriesHint: "An item can sit in more than one section.",
    menuItemCategoriesNone:
      "There are no sections yet. Create one on the sections screen first.",
    menuItemCategoriesNoMatch: "No section by that name",
    menuItemImagesTitle: "Photos",
    menuItemImagesSubtitle: "What the customer sees while browsing the menu.",
    menuItemImagesEmptyTitle: "This item has no photos",
    menuItemImagesEmptyBody:
      "One good photo tells the customer what the item is without asking. Upload one from your phone or computer.",
    menuItemImagesEmptyAction: "Upload a photo",
    menuItemPhotoDrop: "Photo — drop or tap (optional)",
    addImage: "Upload a photo",
    imageAlt: "Item photo number {index}",
    imagePrimary: "Main photo",
    imageOrder: "Photo number {index}",
    imageWrongType:
      "That file is not a supported image. Allowed: JPG, PNG or WEBP.",
    imageTooLarge: "That image is too large. The limit is 5 MB.",
    imageUploadFailed: "The photo did not upload. Try again.",
    imageEntityLabel: "Photo",
    removeImageTitle: "Remove this photo?",
    removeImageBody:
      "The photo is removed from the item. The other photos stay exactly as they are, in the same order.",
    // ---- Settings hub (landing + its sections) ----
    settingsHubDescription:
      "Everything you configure about the restaurant, in one place.",
    // PAPER (2026-10 redesign) — the section list beside the open section: a
    // short line under each name, and the save bar shown while there are edits.
    // The short lines are the redesign's own (pixel parity, 2026-10-02).
    settingsNavGeneral: "Business day, timezone, logo",
    settingsNavOperations: "Kitchen, printing, shifts",
    settingsNavTax: "Service charge, VAT, your tax position",
    settingsNavTaxStatus: "Registration, service charge, delivery fee",
    settingsNavRefunds: "When a refund is late",
    settingsNavDrawer: "Drawer, discounts, approvals, refunds",
    settingsNavTiers: "Staff and partner prices",
    settingsNavStorefront: "Theme for your online menu",
    settingsNavTableOrdering: "Order from the table by QR",
    settingsNavPayment: "How guests can pay you by transfer",
    settingsNavStations: "Where each dish is prepared",
    settingsNavAppearance: "Only changes this screen",
    settingsUnsaved: "Unsaved changes in {name}",
    settingsSectionSaved: "{name} saved",
    // PAPER — the page's top-bar title and line, and each section's name in
    // the redesign's word (in the list, the card's head and the save bar).
    settingsPaperTitle: "Settings",
    settingsPaperSubtitle:
      "How the restaurant runs. Each section saves on its own.",
    settingsPaperGeneral: "Restaurant",
    settingsPaperOperations: "Operations",
    settingsPaperTax: "Taxes & charges",
    settingsPaperTaxStatus: "Tax position",
    settingsPaperRefunds: "Refunds",
    settingsPaperDrawer: "Cash & approvals",
    settingsPaperTiers: "Price tiers",
    settingsPaperStorefront: "Storefront",
    settingsPaperTableOrdering: "Table ordering",
    settingsPaperPayment: "Payment options",
    settingsPaperStations: "Kitchen stations",
    settingsPaperAppearance: "This device",
    // The 2026-10-29 regrouping — one topic per section, under four headings.
    settingsPaperKitchen: "Kitchen",
    settingsPaperFloor: "Floor & table ordering",
    settingsPaperPrinting: "Printing",
    settingsPaperDelivery: "Delivery & takeaway",
    settingsPaperOnline: "Online ordering",
    settingsNavKitchen: "Workflow, targets, stations",
    settingsNavFloor: "Sending orders, QR, floor targets",
    settingsNavPrinting: "Receipt, kitchen ticket, printers",
    settingsNavDelivery: "Fee, drivers, platforms",
    settingsNavOnline: "Ordering from your page, its look",
    settingsKitchenRowBody:
      "How the kitchen works, the target time for each step in it, and the kitchen stations.",
    settingsFloorRowBody:
      "Whether waiters send through the cashier, QR table ordering, floor and payment targets, and the table app's colour.",
    settingsPrintingRowBody:
      "The receipt's text and language, the kitchen ticket, and the printers and print computer.",
    settingsDeliveryRowBody:
      "The default delivery fee, how long a driver has to accept, and the drivers and delivery platforms.",
    settingsOnlineRowBody:
      "Whether customers order from your page, how long you have to answer, the WhatsApp confirmation, and the page's look.",
    settingsGroupRestaurant: "The restaurant",
    settingsGroupMoney: "Money",
    settingsGroupService: "Service",
    settingsGroupOffPremise: "Orders that leave",
    settingsGroupElsewhere: "Set up elsewhere",
    settingsElsewhereTeam: "Team & roles",
    settingsElsewhereTeamHint: "Members, invites, permissions",
    settingsElsewhereDrivers: "Drivers",
    settingsElsewhereDriversHint: "Roster, documents, invites",
    settingsElsewherePlatforms: "Delivery platforms",
    settingsElsewherePlatformsHint: "Each platform and its commission",
    settingsElsewhereFloor: "Floor layout & QR codes",
    settingsElsewhereFloorHint: "Areas, tables, QR stickers",
    settingsOpen: "Open",
    taxGroupServiceCharge: "Service charge",
    taxGroupVat: "VAT",
    cashGroupApprovals: "Approvals & limits",
    floorGroupOrders: "Sending orders",
    floorGroupTableOrdering: "Table ordering",
    printingTicketGroup: "Kitchen ticket",
    printingTicketOff:
      "Your kitchen workflow prints no ticket, so there is nothing to group. The workflow is chosen in",
    printingPrintersCard: "Printers & print computer",
    printingPrintersCardHint:
      "The printers, each station's printer, and the receipt printer. Each saves on its own.",
    deliveryChargesSummary: "What takeaway and delivery bills carry",
    deliveryChargesValues:
      "Service charge on takeaway: {takeaway} · on delivery: {delivery} · VAT on the fee: {feeVat}",
    deliveryChargeOn: "on",
    deliveryChargeOff: "off",
    // PAPER — the fields the redesign writes with their unit inside the box
    // ("08 :00", "8 %"): the field's name without the unit, and the unit.
    settingsPaperDayStart: "Business day starts at",
    settingsPaperServiceRate: "Service charge rate",
    settingsPaperVatRate: "VAT rate",
    settingsPaperRefundLate: "A refund is late after",
    settingsPaperBand: "Counts as balanced within",
    settingsPaperRadius: "Radius",
    settingsPaperSessionLength: "Session length",
    settingsPaperUnitMinutes: "min",
    settingsPaperUnitMetres: "m",
    settingsPaperDiscard: "Discard",
    settingsPaperServiceTakeaway: "Service charge on takeaway",
    settingsPaperServiceDelivery: "Service charge on delivery",
    // PAPER — the dashboard-look picker as the redesign's one row: "Look on
    // this device", its line, and which look twins the storefront.
    appearancePaperLabel: "Look on this device",
    appearancePaperHint:
      "Pick a look for this screen only. Nobody else sees the change.",
    appearancePaperTwin: "{name} matches your storefront.",
    // D162 (F38) — the till's text size on this device: three steps, normal
    // being the till as it is. The back office and the kitchen board keep theirs.
    appearanceTillTextLabel: "Text size on the till",
    appearanceTillTextHint:
      "Applies to the till on this device only; the back office keeps its size.",
    appearanceTillText_compact: "Compact",
    appearanceTillText_normal: "Normal",
    appearanceTillText_large: "Large",
    appearanceTillTextChanged: "The till's text size has changed.",
    // UX-round D53 — the old general page was six unrelated policies behind one Save.
    // It is six rows here now, each saving itself. The old body also lied: it
    // promised "business day and receipt" while holding every tax decision.
    settingsGeneralRow: "Restaurant",
    settingsGeneralRowBody:
      "When the business day starts, the timezone, and the restaurant's logo.",
    settingsOperationsRow: "Kitchen & cash drawers",
    settingsOperationsRowBody:
      "The kitchen screen, the kitchen chit, and whether this store counts a cash drawer.",
    settingsTaxRowBody:
      "The service charge and VAT, what takeaway and delivery bills carry, and the restaurant's tax position.",
    settingsTaxStatusRowBody:
      "Whether the store is VAT-registered, who the service charge belongs to, and which order types it reaches.",
    settingsRefundsRowBody:
      'How long before a refund counts as "late" and shows up in the exceptions report.',
    settingsDrawerRowBody:
      "Drawer tracking and the count tolerance, the largest discount, manager approval, when a refund is late, and the blind stocktake.",
    settingsTiersRowBody:
      "Ready-made discount rates you pick at the till instead of working one out each time.",
    settingsStorefrontRow: "Storefront look",
    settingsStorefrontRowBody:
      "Pick a ready-made design for the page customers see, and control which sections show.",
    settingsTableOrderingRow: "Table ordering & QR",
    settingsTableOrderingRowBody:
      "Set the restaurant location and allowed radius, then print a permanent QR for each table.",
    tableOrderingTitle: "Table ordering & location",
    tableOrderingHowItWorksTitle: "The QR itself does not expire",
    tableOrderingHowItWorksBody:
      "The sticker stays on the table. After scanning, the diner shares location and gets a one-hour session; location is checked again when the order is sent.",
    tableOrderingUseCurrentLocation: "Use this device's location",
    tableOrderingAccuracy:
      "Current reading accuracy: about {meters} metres. Accuracy is shown only and is not saved.",
    tableOrderingLocationDenied:
      "The browser denied location access. Allow it in browser settings or enter the coordinates manually.",
    tableOrderingLocationTimeout:
      "The location reading timed out. Move near a window or try again.",
    tableOrderingLocationUnavailable:
      "Location is unavailable on this device. Enter the coordinates manually.",
    tableOrderingLatitude: "Latitude",
    tableOrderingLongitude: "Longitude",
    tableOrderingCoordinatesInvalid:
      "Enter latitude from -90 to 90 and longitude from -180 to 180, or leave both empty.",
    tableOrderingRadiusHint: "From 25 to 1,000 metres around the saved point.",
    tableOrderingRadiusInvalid:
      "Radius must be a whole number from {min} to {max} metres.",
    tableOrderingSessionHint:
      "How long a guest can keep ordering from the table after scanning the code, from 5 to 120 minutes.",
    tableOrderingSessionInvalid:
      "Session length must be a whole number from {min} to {max} minutes.",
    // B125/D115 — service timing: when a plate or a table counts as late.
    settingsPaperServiceTiming: "Service timing",
    settingsNavServiceTiming: "When a plate counts as late",
    settingsServiceTimingRow: "Service timing",
    settingsServiceTimingRowBody:
      "The target for each stage, on the kitchen screen and in the service speed report.",
    serviceTimingTitle: "Service timing",
    serviceTimingKitchenGroup: "Kitchen",
    serviceTimingFloorGroup: "Floor and payment",
    serviceTimingQueue: "Waiting to start",
    serviceTimingQueueHint:
      "From sending the plate to the kitchen until it is started.",
    serviceTimingPrep: "Preparing",
    serviceTimingPrepHint:
      "The usual time for every dish. Any dish can have its own time on its page in the menu.",
    serviceTimingPass: "Ready to table",
    serviceTimingPassHint: "From the plate being ready until it is served.",
    serviceTimingFirstOrder: "Seated to first order",
    serviceTimingFirstOrderHint:
      "Dine-in bills: from opening the bill to the first send to the kitchen.",
    serviceTimingQrAccept: "Guest request acceptance",
    serviceTimingQrAcceptHint:
      "From a guest sending a request until a member of staff accepts it.",
    serviceTimingPayment: "Check to payment",
    serviceTimingPaymentHint:
      "From the last check printed until it is settled.",
    serviceTimingProofReview: "Payment proof review",
    serviceTimingProofReviewHint:
      "From a guest sending a payment proof until it is reviewed.",
    serviceTimingPassOverAgeOff:
      "The pass target must be shorter than the time ready plates stay on screen ({minutes} min). Change that under Kitchen workflow first.",
    serviceTimingPassOverAgeOffUnknown:
      "The pass target must be shorter than the time ready plates stay on screen. Change that under Kitchen workflow first.",
    serviceTimingRange:
      "The time must be a whole number from {min} to {max} minutes.",
    serviceTimingFrozenNote:
      "Each bill keeps the targets it opened with, so a change here never re-judges earlier bills.",
    tableOrderingEnableTitle: "Enable location verification",
    tableOrderingGuestTitle: "What the guest sees at the table",
    tableOrderingAccountsTitle: "Trioty Account at the table",
    tableOrderingAccountsLabel: "Show «My account» to guests and keep the visit in it",
    tableOrderingAccountsHint: "Optional for the guest and never needed to order. Everyone who logs in at the table finds the visit in their past orders; they are not added to your customers.",
    tableOrderingBillHiddenTitle: "Hide the bill total while ordering",
    tableOrderingBillHiddenLabel: "Show no bill total until the table asks for the bill",
    tableOrderingBillHiddenHint: "Guests still see what they ordered and what each dish costs; the total and the ways to pay appear only once the bill is asked for or presented.",
    tableOrderingEnableLabel: "Allow table orders after location verification",
    tableOrderingEnableNeedsLocation:
      "Set latitude and longitude before enabling.",
    tableOrderingPermissionNote:
      "If a diner denies location permission or is too far away, they can still browse the menu but cannot open the table bill or send an order.",
    // D130 — which menu screen a guest opens after scanning the table code.
    tableMenuStyleTitle: "Menu style at the table",
    tableMenuStyleHint: "What a guest sees first after scanning the code. Prices, ordering and the bill are the same in all three.",
    tableMenuStyleCalmList: "Calm list",
    tableMenuStyleCalmListHint: "Sections with sticky tabs, and rows with a small photo for each dish.",
    tableMenuStylePhotoGallery: "Photo gallery",
    tableMenuStylePhotoGalleryHint: "The chef's pick on top, filters, and a two-column photo grid.",
    tableMenuStyleCategoryTiles: "Category tiles",
    tableMenuStyleCategoryTilesHint: "Big tiles for the sections, then one section at a time as large cards.",
    tableMenuStyleGalleryHint: "What a guest sees after scanning a table's QR code. Applies right away — no Publish.",
    tableMenuStyleApplied_toast: "The table menu now uses {name}.",
    tableAppAccentTitle: "Table app colour",
    tableAppAccentHint: "Buttons and selections in the table app. Applies right away.",
    tableAppAccentDefault: "Default",
    tableAppAccentGreen: "Green",
    tableAppAccentRed: "Red",
    tableAppAccentOrange: "Orange",
    tableAppAccentPurple: "Purple",
    tableAppAccentPink: "Pink",
    tableAppAccentBrown: "Brown",
    tableAppAccentInk: "Ink",
    tableAppAccentCustom: "Custom colour",
    tableAppAccentCode: "Colour code",
    tableAppAccentInvalid: "Enter a colour like #2383E2.",
    tableAppAccentApplied_toast: "The table app's colour is updated.",
    tablesTitle: "The floor",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    tablesSeatsOne: "1 seat",
    tablesSeatsTwo: "2 seats",
    tablesSeatsFew: "{count} seats",
    tablesSeats: "{count} seats",
    settingsAppearanceRow: "Dashboard look",
    settingsAppearanceRowBody:
      "Pick the look you and your team work in here. The page customers see is not affected.",
    appearanceChanged: "The dashboard look has changed.",
    appearanceTheme_trioty: "Trioty",
    appearanceTheme_homegrill: "Homegrill",
    appearanceTheme_stack: "Stack",
    appearanceTheme_verde: "Verde",
    appearanceTheme_paper: "Paper",
    storefrontTitle: "Storefront look",
    storefrontThemesTitle: "Designs",
    storefrontThemesBody:
      "Pick one — it applies to a draft first, and you publish it.",
    storefrontApplied: "Applied",
    storefrontApply: "Use this",
    storefrontApplying: "Applying...",
    storefrontApplied_toast: "Applied to your draft. Review it, then publish.",
    storefrontTabTheme: "Theme",
    storefrontTabContent: "Content",
    storefrontTabSections: "Sections",
    storefrontEditorSubtitle: "Restaurant editor",
    storefrontSaved: "Saved",
    storefrontSaving: "Saving…",
    storefrontGroupTheme: "Theme",
    storefrontGroupColors: "Colours",
    storefrontGroupSections: "Page sections",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    storefrontSectionsVisibleOne: "1 of {total} visible",
    storefrontSectionsVisibleTwo: "2 of {total} visible",
    storefrontSectionsVisibleFew: "{visible} of {total} visible",
    storefrontSectionsVisible: "{visible} of {total} visible",
    storefrontMenuNote:
      "Items and prices come from your Menu page — this controls what the customer sees.",
    storefrontPublishedToast: "Published to {url}",
    storefrontReset: "Reset to the theme's design",
    storefrontResetTitle: "Put the theme back as it came?",
    storefrontResetBody:
      "Colours, words and sections go back to how the theme was delivered. Anything you changed on the draft is lost. Your published page doesn't change until you publish.",
    storefrontResetConfirm: "Reset it",
    storefrontResetDone: "The theme is back as it came.",
    storefrontShowSection: "Show this section",
    storefrontUndo: "Undo",
    storefrontNoCopy: "This section has no text to change.",
    storefrontEditor: "Edit the page",
    storefrontEditorTitle: "Page editor",
    storefrontPreviewTitle: "Storefront preview",
    storefrontPreviewLoading: "Getting the preview ready…",
    storefrontPreviewUnavailable: "The preview can't load right now.",
    storefrontPreviewDesktop: "Desktop",
    storefrontPreviewPhone: "Phone",
    storefrontEditorBack: "Back to settings",
    storefrontEditorDraft: "You have unpublished changes",
    storefrontEditorLive: "Your page is published as it is",
    storefrontColorsTitle: "Colours",
    storefrontColorsBody:
      "Change the theme's colours without changing its shape. Saved to your draft like any other edit.",
    // UX-round D60 — "I do not know my hex codes. I know my sign is dark green." So the
    // choice is by name, and the code moved behind "Type the exact code".
    storefrontColorExact: "Type the exact code",
    swatchNearBlack: "Black",
    swatchGrey: "Grey",
    swatchWhite: "White",
    swatchCream: "Cream",
    swatchDarkGreen: "Dark green",
    swatchGreen: "Green",
    swatchRed: "Red",
    swatchOrange: "Orange",
    swatchYellow: "Yellow",
    swatchBlue: "Blue",
    swatchPurple: "Purple",
    swatchBrown: "Brown",
    storefrontColorPrimary: "Primary",
    storefrontColorPrimaryHint: "Buttons, prices, and anything you tap.",
    storefrontColorAccent: "Accent",
    storefrontColorAccentHint: "Badges and small details.",
    storefrontColorSurface: "Background",
    storefrontColorSurfaceHint: "The page and its cards.",
    storefrontColorInk: "Text",
    storefrontColorInkHint: "Headings and body copy.",
    storefrontColorsSave: "Save colours",
    storefrontColorsSaving: "Saving…",
    storefrontColorsSaved: "Colours saved to your draft.",
    storefrontColorsReset: "Undo my changes",
    storefrontColorsInvalid:
      "A colour needs to be a six-digit code like #1B1512.",
    storefrontColorsContrast:
      "Text on this colour is hard to read. Try something darker or lighter.",
    storefrontSectionsTitle: "Page sections",
    storefrontSectionsBody:
      "Hide a section, change its order, or change its words. The design arrives finished.",
    storefrontEditCopy: "Edit text",
    storefrontCloseCopy: "Close",
    storefrontCopyHint:
      "The text a theme arrives with was written for another restaurant — make it yours.",
    storefrontCopySave: "Save text",
    storefrontCopySaving: "Saving…",
    storefrontCopySaved: "Text saved",
    storefrontCopyArabic: "Arabic",
    storefrontCopyEnglish: "English",
    storefrontCopyTooLong: "Too long",
    storefrontShow: "Shown",
    storefrontHide: "Hidden",
    storefrontMoveUp: "Move up",
    storefrontMoveDown: "Move down",
    storefrontLocked: "Fixed",
    storefrontPreviewOnly: "Not live yet",
    storefrontPreviewOnlyBody:
      "Shows in the preview only — the feature isn't built.",
    storefrontDraftPending: "You have unpublished changes.",
    storefrontPublish: "Publish",
    storefrontPublishing: "Publishing...",
    storefrontPublished: "Published. Your page is updated.",
    storefrontDiscard: "Back to published",
    storefrontDiscarded: "Restored the last published version.",
    storefrontOpen: "Open the page",
    storefrontNoThemes: "No designs available right now.",
    storefrontLiveBadge: "Live",
    storefrontDraftBadge: "Unpublished changes",
    storefrontCurrentLive: "This is what your customers see right now.",
    storefrontCurrentDraft: "Applied to your draft — publish to make it live.",
    storefrontCustomize: "Customize",
    storefrontSwitchHint:
      "Switching a design keeps your menu, prices and photos — only the look changes. Customize it in the editor.",
    // Round 5 — "Show prices on the online menu". A restaurant setting, not part
    // of the design draft, so it reaches the live page at once — and says so.
    storefrontPricesLabel: "Show prices on the online menu",
    storefrontPricesHint:
      "When off, people browsing your menu see the dishes without prices. A guest ordering from a table's QR code always sees what each dish costs and what their bill comes to. This changes your live page straight away — it doesn't wait for Publish.",
    storefrontPricesShown_toast: "Prices now show on your online menu",
    storefrontPricesHidden_toast: "Prices are now hidden on your online menu",
    storefrontNothingToPublish: "Nothing to publish yet.",
    // UX-round D60 — the third state this card never had: a store that has not picked a
    // design. It used to be badged "Live" beside "No designs available".
    storefrontUnsetTitle: "No design chosen yet",
    storefrontUnsetBadge: "Not set up",
    storefrontUnsetBody:
      "Pick a design below, then publish it so that is what customers see.",
    storefrontPickFirst: "Pick a design from the list below first.",
    storefrontOpenLive: "Open the page as a customer sees it",
    settingsNoAccessTitle: "No settings are available to your account",
    settingsNoAccessBody:
      "The sections here need the settings permission or the price-tier permission, and you hold neither yet. Ask the owner to widen your access from the team screen.",
    backToSettings: "Back to settings",
    restaurantLogoTitle: "Restaurant logo",
    restaurantLogoHint:
      "Drawn in the centre of the QR code on the table stickers. A square image, JPG, PNG or WEBP.",
    restaurantLogoEmpty: "No logo yet",
    restaurantLogoChoose: "Choose logo",
    restaurantLogoReplace: "Replace logo",
    restaurantLogoRemove: "Remove logo",
    restaurantLogoUpload: "Upload logo",
    restaurantLogoUpdate: "Update logo",
    restaurantLogoSaved: "Logo saved",
    restaurantLogoRemoved: "Logo removed",
    restaurantLogoUnreadable: "That image could not be read. Try another file.",
    generalNoAccessTitle: "You cannot edit these settings",
    generalNoAccessBody:
      "Editing restaurant settings needs the settings permission. Ask the owner for it from the team screen.",
    tiersNoAccessTitle: "You cannot manage price tiers",
    tiersNoAccessBody:
      "Price tiers need the tier permission. Ask the owner for it from the team screen.",
    tiersEmptyTitle: "No price tiers yet",
    tiersEmptyBody:
      "A tier is a discount rate you save once and reuse — staff discount, corporate rate. Add your first tier and set its percentage.",
    tiersEmptyAction: "Add the first tier",
    addTier: "Add a tier",
    newTierTitle: "New price tier",
    editTierTitle: "Edit tier",
    tierNameRequired: "Give the tier a name.",
    tierPercentRange: "The percentage must be a number between 0 and 100.",
    tierInactive: "Paused",
    // PAPER (pixel parity) — the redesign's tier row: name and discount on one line.
    tierPaperName: "{name} · {percent}% off",
    // D47 (B4): the tier's happy-hour window — weekdays plus a from/to time.
    weekdayMon: "Mon",
    weekdayTue: "Tue",
    weekdayWed: "Wed",
    weekdayThu: "Thu",
    weekdayFri: "Fri",
    weekdaySat: "Sat",
    weekdaySun: "Sun",
    tierWindowEveryDay: "Every day",
    tierWindowNoDay: "No day selected",
    tierWindowOvernight: "runs past midnight",
    // UX-round D54 — the row now states the schedule it actually produces, including
    // the widest one. That is the row that hid a 20% discount running all day.
    tierWindowAllDay: "all day",
    tierScheduleAutomatic: "applies on its own",
    tierScheduleManual: "the cashier picks it",
    tierWindowSectionTitle: "When it applies",
    tierWindowDaysLabel: "Days",
    tierWindowDaysHint: "Leave every day ticked if the tier runs all week.",
    tierWindowFromLabel: "From",
    tierWindowToLabel: "Until",
    // UX-round D54 — 267 characters of midnight-crossing rules, the longest string in
    // Settings. The RESULT is now stated above, on its own line, so this says
    // the one thing that line cannot: which night a tier starts on.
    tierWindowHint:
      "Store local time. Leave both blank for a tier that runs all day. The days you tick are the NIGHTS it starts on, so a Friday 22:00-02:00 tier runs Friday night into Saturday morning.",
    // B11 — the window is checked once, at open, not per item added later.
    tierWindowLockInHint:
      "The window is checked when a bill is opened — a bill opened inside it keeps that price until it closes, even if it runs past the window's end.",
    tierWindowPairRequired:
      "Fill the start and end times together, or leave both blank.",
    tierWindowTimeInvalid: "Use a 24-hour time — 17:00, for example.",
    tierWindowNoDaysSelected: "Pick at least one day.",
    tierAutoApplyLabel: "Apply it automatically during its hours",
    // UX-round D54 — was 226 characters. "Applies on its own" is now on the row itself,
    // so this says the part that is not visible: it catches QR tables too.
    tierAutoApplyHint:
      "Without the cashier choosing it — including tables a customer seats themselves from the QR page, so one advertised price covers the whole floor.",
    tierWindowColumn: "When",
    tierActiveLabel: "Active",
    tierActiveHint:
      "A paused tier is not offered when a cashier opens an invoice, and invoices already priced with it are untouched. There is no delete for a tier — pausing is how you retire one.",
    // --- kitchen stations (D21): where a plate gets cooked ---
    stationsTitle: "Stations & printers",
    stationsNoAccessTitle: "You cannot view stations",
    stationsNoAccessBody:
      "Stations and printers require the settings-management permission. Ask the owner for it from the team screen.",
    stationsReadOnlyNote:
      "You can see the stations but not change them. Editing needs the settings permission.",
    stationsEmptyTitle: "No stations yet",
    stationsEmptyBody:
      "A station is where an item gets cooked — grill, cold, bar. Firing an order prints one chit per station, so the grill never sees the bar's drinks.",
    stationsEmptyAction: "Add the first station",
    addStation: "Add station",
    newStationTitle: "New station",
    editStationTitle: "Edit station",
    stationNameArLabel: "Arabic name",
    stationNameEnLabel: "English name",
    stationNameEnHint: "Optional. Shown to staff working in English.",
    stationNameRequired: "Give the station a name.",
    stationSortOrder: "Order",
    stationSortOrderHint: "The lower number comes first on the kitchen screen.",
    stationSortOrderValue: "Order {order}",
    stationActiveLabel: "Station is running",
    stationActiveHint:
      "A paused station is not offered when you set an item's station, and items routed to it fall back to the default station.",
    stationActiveHintDefault:
      "This is the fallback station and cannot be paused. Make another active station the fallback first.",
    stationDefaultBadge: "Default station",
    stationBatchStrip: "“To make now” on the station's screen",
    stationBatchStripHint: "How many of each dish are new or cooking, across the top of the screen.",
    stationDefaultExplainer:
      "Any item with no station of its own goes here. That is why it cannot be deleted — pause it from the edit form if you do not need it.",
    stationPaused: "Paused",
    stationEntityLabel: "Station",
    deleteStationTitle: "Delete station",
    deleteStationBody:
      "Items routed to this station fall back to the default station automatically, so no item is lost. The deletion itself cannot be undone.",
    stationDeleteRefused:
      "The station was not deleted. Someone may have removed it already, or it is the default station, which cannot be deleted.",
    stationBuiltInBadge: "Built in",
    activateStation: "Activate station",
    deactivateStation: "Deactivate station",
    makeFallbackStation: "Make fallback station",
    settingsStationsRowBody:
      "Split the kitchen into stations, then configure printers and item routing.",
    printersTitle: "Printers",
    printersDescription:
      "Detect this device's printers and test one before enabling automatic printing.",
    configureDetectedPrinterTitle: "Configure detected printer",
    editPrinterTitle: "Edit printer",
    printerLabel: "Restaurant printer name",
    printerQueue: "Detected printer",
    printerQueueHint: "Choose a printer from the detected list.",
    printerPaperWidth: "Paper width",
    printerProfile: "Tested device profile",
    printerProfileHint:
      "Optional. Enable cutter or drawer only when this exact hardware and driver profile was tested.",
    printerCutter: "Cutter tested and enabled",
    printerCashDrawer: "Cash drawer tested and enabled",
    printerCapabilityWarning:
      "Off by default; enable only after real-device testing.",
    printerActive: "Printer is active",
    printerRequiredFields:
      "Choose a detected printer and enter its restaurant label.",
    printerDiscoveryTransportNote:
      "Detect USB, Bluetooth, Wi-Fi, and LAN printers connected to this computer, or add a network printer by its IP address with no driver. Ordering devices such as waiter tablets need no printer setup.",
    discoverPrinters: "Discover printers",
    addNetworkPrinter: "Add network printer",
    searchNetwork: "Search this network",
    networkScanHint: "Checks port 9100 on this computer's network. Takes about half a minute.",
    networkScanProgress: "Searching… {done} of {total}",
    networkScanFound: "Printers that answered:",
    networkScanNone:
      "No printer answered on this network. Make sure this computer is on the printer's network, by cable or on Wi-Fi without device isolation.",
    networkScanUse: "Use",
    networkScanAdded: "Already added",
    qzSetupTitle: "Prepare this computer for printing",
    qzSetupSubtitle: "Once per print computer. Waiter tablets don't need it.",
    qzSetupStepInstall: "Install QZ Tray from qz.io/download and open it.",
    qzSetupStepCommand:
      "Copy the command for this computer's system, paste it, and press Enter. It asks for the computer's admin password once.",
    qzSetupMacWhere: "Mac: paste it into the Terminal app.",
    qzSetupWindowsWhere: "Windows: paste it into PowerShell opened as administrator.",
    qzSetupCopyMac: "Copy Mac command",
    qzSetupCopyWindows: "Copy Windows command",
    qzSetupStepMacNetwork:
      "Mac only: allow “java” in System Settings → Privacy & Security → Local Network.",
    qzSetupRerun: "If a trust prompt appears after a QZ Tray update, run the command again.",
    qzSetupCopied: "Command copied. Paste it and press Enter.",
    networkPrinterTitle: "Network printer",
    printerAddress: "Printer IP address",
    printerAddressHint:
      "Printed on the printer's self-test page (hold FEED while switching it on), for example 192.168.1.236.",
    networkPrinterSetupNote:
      "This computer prints to it directly, with no driver. On a Mac, allow “java” in System Settings → Privacy & Security → Local Network.",
    networkPrinterRequiredFields: "Enter the printer's IP address and its restaurant label.",
    discoveredPrinterQueues: "Discovered printer queues",
    printerDiscoveryComplete: "Printers detected. Choose one to configure.",
    printerDiscoveryUnavailable:
      "The printers on this computer are unavailable. Make sure the printing service is running, then try again.",
    printerDiscoveryEmpty:
      "No printers are available on this computer. Add the printer in the device settings and try again.",
    printerDiscoveryNoNew: "Every detected printer is already configured.",
    printOutcomeUnknown:
      "The print send outcome is unknown. Check the printer before retrying.",
    printJobFailed:
      "The print job could not be sent and remains visible for retry.",
    printKitchenAdvancePending:
      "The paper was sent, but the kitchen ticket update is still pending.",
    printBrowserFallbackUsed:
      "The browser print dialog was used as a fallback.",
    printerOnlineConfigured: "Configured",
    printerInactive: "Inactive",
    noPrintersConfigured: "No detected printers configured yet.",
    testPrinter: "Print test",
    testPrintQueued: "The test print was queued.",
    testPrintNoInvoice: "A test print never opens or changes an invoice.",
    deactivatePrinter: "Deactivate printer",
    stationPrinterBindings: "Station printers",
    stationPrinterBindingsHint:
      "Each station has one primary printer and a copy count. Stations may share a printer.",
    stationPrinter: "Printer",
    printCopies: "Copies",
    noPrinter: "No printer",
    receiptPrinterTitle: "Customer receipt printer",
    receiptPrinterHint:
      "Separate from kitchen printers. Automatic printing happens after the invoice is confirmed.",
    receiptPrinter: "Default receipt printer",
    autoReceiptPrint: "Print the customer receipt automatically",
    autoReceiptPrintHint:
      "Requires an active printer. A print failure never blocks invoice confirmation.",
    automaticPrintDeviceLabel: "Current print computer",
    printDeviceSessionMissing:
      "The server did not establish the print-device session.",
    // --- how a diner pays by transfer (D44, B35) ---
    settingsPaymentRow: "Transfers & InstaPay",
    settingsPaymentRowBody:
      "The address a diner transfers to from their phone. Without one there is no transfer button at all.",
    paymentTitle: "Transfers & InstaPay",
    paymentSubtitle:
      "This is what a diner sees on their phone when they choose to transfer. It is published exactly as typed, so check the characters.",
    paymentEmptyTitle: "No transfer address yet",
    paymentEmptyBody:
      "Diners currently see only \u201Cwe\u2019ll pay at the counter\u201D. Add an InstaPay address so they can transfer from the table.",
    paymentAdd: "Add an address",
    paymentEdit: "Edit",
    paymentDelete: "Delete",
    paymentDeleteTitle: "Delete this transfer address?",
    paymentDeleteBody:
      "Diners will stop seeing the transfer button. Bills already paid are unaffected.",
    paymentFieldLabelAr: "Label shown in Arabic",
    paymentFieldLabelEn: "Label shown in English",
    paymentFieldValue: "The address itself",
    paymentFieldAdd: "Add a line",
    paymentFieldRemove: "Remove line",
    paymentActive: "Live",
    paymentInactive: "Off",
    paymentSaved: "Saved",
    paymentDeleted: "Deleted",
    paymentNeedsOneField: "At least one line is needed, carrying the address.",
    // --- menu item: which station cooks it ---
    menuItemStationTitle: "Cooking station",
    menuItemStationSubtitle:
      "The station this item is cooked at, and the one its chit prints from.",
    menuItemStationPick: "Pick a station",
    stationPickerDefaultOption: "The default station (no station of its own)",
    menuItemStationNoneTitle: "No stations set up",
    menuItemStationNoneBody:
      "Create a station first, then you can route this item to it.",
    menuItemStationNoneAction: "Open stations",
    // --- floor: areas & tables (edit / move / pause / delete) ---
    floorDescription:
      "How the place is divided, and which tables sit in each area.",
    floorViewLayout: "Layout",
    floorViewQr: "QR codes",
    tableQrNeedsLocationTitle: "Set the restaurant location first",
    tableQrNeedsLocationAction: "Open table-ordering settings",
    tableQrNeedsLocationBody:
      "Printing becomes available after you save the location and enable verification in table-ordering settings.",
    tableQrNoActiveTitle: "No active tables to print",
    tableQrNoActiveBody:
      "Paused tables are excluded from the print sheet. Reactivate a table in Layout or add a new one.",
    tableQrPermanentNote:
      "These are permanent sticker links. The diner session created after scanning is what expires after one hour.",
    tableQrPreview: "Preview",
    tableQrDownload: "Download PNG",
    tableQrPrintOne: "Print this one",
    tableQrPrintAll: "Print all active tables",
    tableQrStickerScan: "Scan to open the menu and order from this table",
    tableQrStickerTable: "Table",
    tableQrStickerTitle: "Scan the code with your phone camera",
    tableQrStickerBody: "Scan to see the menu and order from your table",
    tableQrImageAlt: "QR code for table {name}",
    tableQrInvalidCode:
      "This reserved code cannot produce a QR. Change the table code in Layout.",
    /* Subtitle on the side panel's table form. */
    floorTableFormHint:
      "The table shows in this area, and gets its own QR code.",
    areasEmptyAction: "Add the first area",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    areaTableCountOne: "1 table",
    areaTableCountTwo: "2 tables",
    areaTableCountFew: "{count} tables",
    areaTableCount: "{count} tables",
    areaTableCountNone: "No tables",
    areaNoTablesBody:
      "This area has no tables yet. Add one so it shows up for the floor staff.",
    /* Layout redesign (2026-10-29 board). */
    addTables: "Add tables",
    addTableShort: "Table",
    addAreaLong: "New area (hall, terrace, rooms…)",
    floorReorder: "Reorder",
    floorReorderDone: "Done",
    floorReorderTitle: "Reorder mode.",
    floorReorderBody:
      "Drag a table or an area into place, or use the arrows. This order is what the cashier and waiters see on the floor.",
    floorDragHandle: "Drag to reorder",
    floorSumAreasOne: "area",
    floorSumAreasTwo: "areas",
    floorSumAreasFew: "areas",
    floorSumAreas: "areas",
    floorSumTablesOne: "table",
    floorSumTablesTwo: "tables",
    floorSumTablesFew: "tables",
    floorSumTables: "tables",
    floorSumSeatsOne: "seat",
    floorSumSeatsTwo: "seats",
    floorSumSeatsFew: "seats",
    floorSumSeats: "seats",
    floorSumPaused: "paused",
    floorSumHint: "Click any table to edit it. Delete and pause are inside.",
    areaPausedCountOne: "1 paused",
    areaPausedCountTwo: "2 paused",
    areaPausedCountFew: "{count} paused",
    areaPausedCount: "{count} paused",
    areaMenuLabel: "Area options",
    areaMenuDetails: "Area type and details",
    areaMenuReorder: "Reorder areas",
    editTableNamed: "Table {name}",
    tableInService: "In service",
    tablePausedSub: "Paused · not on the floor",
    newTablesIn: "New tables in {area}",
    newTablesSub: "They show on the floor as soon as you save",
    newAreaSub: "A part of the place where tables sit",
    tableSeatsLabel: "Seats",
    tablesHowMany: "How many tables?",
    tablesNamesPreview: "They will be added as",
    tablesNamesHint:
      "After the last table in the area. You can rename any of them later, and each gets its own QR code.",
    tableQrBlock: "QR code",
    tableQrCodeLine: "Table code: {code} · changing it stops the printed sticker",
    tableQrChangeCode: "Change",
    tableQrPrintShort: "Print",
    tableDeleteSection: "Delete table",
    tableDeleteNote:
      "It leaves the floor and its QR sticker stops working. If it has past invoices we will suggest pausing it instead.",
    tableDeleteNamed: "Delete {name}",
    addTablesCtaOne: "Add 1 table",
    addTablesCtaTwo: "Add 2 tables",
    addTablesCtaFew: "Add {count} tables",
    addTablesCta: "Add {count} tables",
    addTableToArea: "Add a table here",
    openTableSettings: "Open table settings",
    deleteAreaAction: "Delete area",
    deleteTableAction: "Delete table",
    newAreaTitle: "New area",
    editAreaTitle: "Edit area",
    areaNameRequired: "Give the area a name.",
    areaEntityLabel: "Area",
    deleteAreaTitle: "Delete area",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    deleteAreaBodyOne:
      "This area has 1 table. Deleting it deletes that table too, and the QR code on it stops working. This cannot be undone \u2014 if you want to keep it, move it to another area first.",
    deleteAreaBodyTwo:
      "This area has 2 tables. Deleting it deletes both of them, and the QR codes on those tables stop working. This cannot be undone \u2014 if you want to keep the tables, move them to another area first.",
    deleteAreaBodyFew:
      "This area has {count} tables. Deleting it deletes every one of them, and the QR codes on those tables stop working. This cannot be undone \u2014 if you want to keep the tables, move them to another area first.",
    deleteAreaBody:
      "This area has {count} tables. Deleting it deletes every one of them, and the QR codes on those tables stop working. This cannot be undone \u2014 if you want to keep the tables, move them to another area first.",
    deleteAreaBodyNoTables:
      "This area has no tables, so deleting it affects nothing else. It still cannot be undone.",
    areaBlockedTitle: "This area cannot be deleted",
    areaBlockedBody:
      "Tables in this area have already been billed, and those invoices have to stay attached to their tables for your reports to stay correct. You can still rename the area, and any table you no longer use can be paused from its edit form instead of deleted.",
    areaBlockedUnderstood: "Got it",
    newTableTitle: "New table",
    editTableTitle: "Edit table",
    tableAreaLabel: "Area",
    areaPickerPlaceholder: "Search areas",
    areaPickerNoResults: "No area by that name",
    tableAreaRequired: "Pick the area this table sits in.",
    tableNameRequired: "Give the table a name.",
    tableCodeRequired: "The code cannot be empty.",
    tableCodeInvalid:
      "Use English letters, digits, dashes (-) and underscores (_) only, up to 40 characters, starting with a letter or digit. No dots, spaces or Arabic letters.",
    tableCodeTaken:
      "Another table is already using this code. Every table needs its own.",
    tableCodeChangeWarning:
      "This code is the table\u2019s QR link. Change it and every QR sticker already printed for this table stops working \u2014 a diner who scans the old one lands nowhere. Only change it if you are going to reprint the sticker.",
    tableActiveLabel: "Table in service",
    tableActiveHint:
      "Paused, it disappears from the floor screen and no new invoice can be opened on it. Its past invoices stay untouched.",
    tableInactive: "Paused",
    tableEntityLabel: "Table",
    deleteTableTitle: "Delete table",
    deleteTableBody:
      "The table comes off the floor screen and its QR sticker stops working. This cannot be undone.",
    tableBlockedTitle: "This table cannot be deleted",
    tableBlockedBody:
      "This table has already been billed, and deleting it would cut those invoices loose from their table. Pause it instead: it disappears from the floor screen and no new invoice can be opened on it, while its history stays exactly where it is.",
    tableBlockedBodyInactive:
      "This table has already been billed, and it is already paused, so it is not on the floor screen. Its invoices have to stay attached to it, which is why there is no delete.",
    tableBlockedDeactivate: "Pause the table",
    tableBlockedKeep: "Leave it as it is",
    /* UX-round D62 — `floorLayoutBanner` is gone. It said "this is not the other
       screen"; there is one screen with two views now, so the sentence has no
       subject left. */
    floorOrderFailed:
      "We could not save the new order, so we put it back the way it was.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    seatCountOne: "1 seat",
    seatCountTwo: "2 seats",
    seatCountFew: "{count} seats",
    seatCount: "{count} seats",
    seatsUnset: "Seats not recorded",
    seatsPerTable: "Seats per table",
    tableBatchCount: "How many tables",
    tableBatchHint: "Adds several tables with the same seats in one go.",
    tableAutoName: "Names are assigned automatically from the place name.",
    newTablesTitle: "Add tables",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    tablesAddedOne: "1 table added",
    tablesAddedTwo: "2 tables added",
    tablesAddedFew: "{count} tables added",
    tablesAdded: "{count} tables added",
    renameArea: "Rename area",
    areaKindLabel: "Type",
    areaKindHint: "Just for the icon — behaves the same everywhere.",
    areaKindIndoor: "Indoor",
    areaKindOutdoor: "Outdoor",
    areaKindRooms: "Rooms",
    tableNameLabel: "Table name",
  },
  staff: {
    title: "Team & permissions",
    description: "Who can get in, and what they can do.",
    noAccessTitle: "This screen belongs to the owner",
    noAccessBody:
      "Only the store owner manages the team and its roles. Ask them if you need to get in.",

    membersTitle: "Team",
    membersEmptyTitle: "It is still just you",
    membersEmptyBody:
      "Add each person by name and phone number, then choose the role they should have.",
    memberRoles: "Roles",
    memberNoRoles: "No role",
    memberRolesRequired: "Pick at least one role.",
    removeMember: "Remove from the team",
    removeMemberTitle: "Remove this person from the team?",
    removeMemberBody:
      "They will not be able to sign in to this store again. Everything they already did stays exactly as it is.",
    memberEntityLabel: "Member",

    invitesTitle: "Pending invites",
    invitesEmptyTitle: "No invites waiting",
    invitesEmptyBody:
      "An invite nobody has accepted yet stays here until they accept it or you revoke it.",
    inviteAction: "Add member",
    inviteTitle: "Add a team member",
    inviteEmail: "Email (optional)",
    inviteEmailRequired: "Enter a valid email address.",
    memberPhone: "Phone",
    memberDetailsRequired: "Enter the person's name and phone number.",
    temporaryPassword: "Temporary password",
    temporaryCredentialsHint:
      "Copy these login details now and share them securely. The user must change the password after signing in.",
    copyCredentials: "Copy login details",
    credentialsCopied: "Login details copied",
    inviteRoles: "Role",
    inviteRolesRequired: "Pick at least one role.",
    inviteSend: "Add member",
    copyInviteLink: "Copy invite link",
    copyInviteLinkDone: "Link copied — send it to them",
    inviteSent: "Member added",
    inviteExpires: "Expires",
    invitedBy: "Invited by",
    revokeInvite: "Revoke",
    revokeInviteTitle: "Revoke this invite?",
    revokeInviteBody:
      "The link that was sent stops working. You can send another one any time.",
    inviteEntityLabel: "Invite",

    rolesTitle: "Roles",
    // B117/E2 — see the Arabic note: this URL used to answer with the MEMBERS
    // list instead of refusing.
    rolesNoAccessTitle: "You cannot view roles",
    rolesNoAccessBody:
      "Editing roles and their permissions is the store owner's. To see the team, open the team page.",
    rolesSubtitle:
      "A role is a bundle of permissions you hand to someone. Make a “Manager” role and hand over the back office — without handing over your own login.",
    rolesEmptyTitle: "No roles yet",
    rolesEmptyBody: "Create the first role and decide exactly what it reaches.",
    addRole: "New role",
    systemRole: "Built-in",
    systemRoleHint:
      "Built-in roles cannot be edited or deleted. Make a new role if you need something different.",
    viewRole: "See its permissions",
    editRole: "Edit role",
    deleteRole: "Delete role",
    duplicateRole: "Duplicate",
    roleCopyName: "{name} (copy)",
    /* D128 — lock a role to some kitchen stations. */
    roleStationsTitle: "Kitchen stations",
    roleStationsHint:
      "Which stations this role sees and works on the kitchen board. Pick one to keep a cook to their own station.",
    roleStationsAll: "All stations",
    roleStationsSome: "Only these stations",
    roleStationsPickOne: "Pick at least one station.",
    roleStationsLoadFailed: "Could not load the kitchen stations.",
    roleStationsNone: "No kitchen stations yet.",
    deleteRoleTitle: "Delete this role?",
    deleteRoleBody:
      "The role goes away for good. If anyone still holds it, take it off them first.",
    roleEntityLabel: "Role",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    rolePermissionsCountOne: "1 permission",
    rolePermissionsCountTwo: "2 permissions",
    rolePermissionsCountFew: "{count} permissions",
    rolePermissionsCount: "{count} permissions",
    rolePermissionsFailed:
      "The permission list did not load, so no role can be opened or created right now.",
    rolePermissionsLoading: "Hold on — the permission list is still loading.",

    presetOwner: "Owner",
    presetManager: "Manager",
    presetFulfillment: "Fulfillment",
    presetSupport: "Support",
    presetWaiter: "Waiter",
    presetCashier: "Cashier",
    presetKitchen: "Kitchen",
    presetExpediter: "Expediter",
    // Audit round 2, Phase 3 — the catalogue's own English lines, said here so
    // both languages read them from one place (the restaurant MANAGER line).
    presetDescription_OWNER: "Full access to the store",
    presetDescription_MANAGER:
      "Runs the floor: the till, the kitchen, the drawer, and the calls a cashier escalates",
    presetDescription_FULFILLMENT: "Views and fulfills orders",
    presetDescription_SUPPORT: "Read-only access for customer support",
    presetDescription_WAITER: "Opens tables, appends items, and requests remakes",
    presetDescription_CASHIER:
      "Everything a waiter can do, plus voiding, comping, closing and settling",
    presetDescription_KITCHEN: "Works the kitchen board: starts and finishes food",
    presetDescription_KITCHEN_EXPEDITOR:
      "Works the pass: hands ready food over, reprints and 86s",
    presetDriver: "Delivery driver",
    presetDescription_DRIVER:
      "Delivers the restaurant’s delivery orders from the Trioty Driver app",
    newRoleTitle: "New role",
    editRoleTitle: "Edit role",
    roleName: "Role name",
    roleNamePlaceholder: "e.g. Branch manager",
    roleNameRequired: "Give the role a name.",
    // UX-round D55 — "Code" and its four sentences left with the field. A restaurant
    // owner should not invent an English identifier for something the copy
    // itself called "for the system only"; it is generated at save time now.
    roleDescription: "Short description",
    rolePermissionsTitle: "Permissions",
    roleQuickLevel: "Quick set",
    roleLevelNone: "None",
    roleLevelBasic: "Basic",
    roleLevelAll: "All",
    roleTagSensitive: "Sensitive",
    roleTagPartial: "Partly",
    roleTagCeiling: "Above your access",
    roleGroupCount: "{done} of {total}",
    roleSummaryTitle: "What can this person do?",
    roleSummaryCan: "Can",
    roleSummaryCannot: "Cannot",
    roleSummaryEmpty: "Nothing yet. Turn on what they need.",
    roleSettleNeedsDrawer: "If your restaurant tracks drawers, they can't take payment until they open one. Turn on \"Open, close and count their drawer\".",
    roleMembersBanner: "Members on this role now: {count} · any change applies to all of them",
    roleStartFrom: "Start from",
    roleStartBlank: "Blank role",
    rolePermissionsHint:
      "One level per area, and each level includes the ones below it — so nobody can delete what they cannot even see.",
    showPermissions: "Show the individual permissions",
    teamMembersTab: "Team",
    teamRolesTab: "Roles",
    // PAPER (pixel parity, 2026-10-02) — the redesign's words: the top bar's
    // title and line, the two tabs, and the search box.
    paperTitle: "Team",
    paperSubtitle: "Who works here and what each role can do.",
    paperMembersTab: "Members",
    paperRolesTab: "Roles and permissions",
    paperSearch: "Search name or phone",
    paperFieldName: "Full name",
    paperFieldPhone: "Mobile number",
    paperNamePlaceholder: "e.g. Nour Hassan",
    paperPhonePlaceholder: "010 0000 0000",
    paperEmailPlaceholder: "name@example.com",
    teamRoleFilterAll: "All",
    teamListEmpty: "Nobody matches the filters",
    teamPending: "Not accepted yet",
    teamHeaderMember: "Member",
    /* B67's plural defect again — "1 members" on a role tile. */
    teamRoleMemberCountOne: "1 member",
    teamRoleMemberCountNone: "Nobody yet",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    teamRoleMemberCountTwo: "2 members",
    teamRoleMemberCountFew: "{count} members",
    teamRoleMemberCount: "{count} members",
    teamHeaderRole: "Role",
    teamRoleMembers: "Who holds this role",
    teamNoRoleMembers: "Nobody holds this role yet",
    teamRolePermsTitle: "Permissions",
    teamRoleNote:
      "Permissions are set from the role editor — the built-in roles cannot be changed.",
    teamMemberCount: "{count} in the team",
    // PAPER (2026-10 redesign) — the team table and the member window.
    teamHeaderStatus: "Status",
    statusActive: "Active",
    // Round 5 — the "Last active" column: the member's newest recorded act, and
    // the last 7 business days (the restaurant's own day boundary) as squares.
    // Never colour alone: every square carries the day and the state in words.
    teamHeaderLastActive: "Last active",
    lastActiveNone: "No activity yet",
    activityWeek: "Last 7 business days",
    activityWorked: "Worked",
    activityException: "Void, comp, refund, write-off or correction",
    activityOff: "Off",
    activityExceptionDetail:
      "Worked, with a void, comp, refund, write-off or payment correction",
    activityDay: "{day}: {state}",
    activityLoadFailed: "Couldn't load the team's recent activity.",
    customRole: "Custom",
    editMemberTitle: "Edit {name}",
    memberEmail: "Email",
    // Name, phone and email are the person's own account, not the store's.
    memberIdentityNote:
      "Name, phone and email belong to their own account — only they can change them.",
    inviteNote:
      "If this number already has an account, it is linked as is. Otherwise you get a one-time password to hand them, and they choose their own when they first sign in.",
    hidePermissions: "Hide the details",
    // UX-round D55 — in the read-only role view a permission is stated in a word, not a
    // switch: a switch nobody may touch is a picture of a control.
    permHeld: "Has",
    permNotHeld: "No",
    storefrontAreasTitle: "Online store permissions",
    storefrontAreasHint:
      "These apply to the store's online storefront — products, online orders, coupons, reviews, shipping, payment and store settings — and have no restaurant screen.",
    storefrontAreasGranted: "{count} granted",
    posNeedsMenuView:
      "For the till to work, also grant Menu: view only and Categories: view only — without them the screen opens with no items and no filters.",
    ceilingTitle: "You cannot hand out what you do not hold",
    ceilingBody:
      "The greyed-out permissions are not on your own account, so you cannot grant them to anyone. The store owner can.",
    ceilingRejected:
      "The server refused: this role includes permissions you do not hold, so they are not yours to give.",
    noPermissionsSelected:
      "This role grants nothing yet. Pick something first.",
  },
  serverErrors: {
    purchaseDateOutOfRange:
      "A delivery can't be dated that day. Pick a day between {from} and {to}.",
    purchaseDateOutOfRangeUnbounded:
      "A delivery can't be dated that day. Pick today or a recent day before it.",
    adjustmentSignInvalid:
      "The quantity's direction doesn't match the reason: waste and spoilage take stock off, and a correction can't be zero. Check the quantity and try again.",
    supplierReturnExceedsPurchase:
      "Only {quantity} of this delivery can still go back — the part not returned yet. Change the quantity and try again.",
    supplierReturnExceedsPurchaseUnnamed:
      "That is more than is left of this delivery to return. Change the quantity and try again.",
    // D163 — a reference rides a card or InstaPay tender only.
    tenderReferenceNotAllowed:
      "A transaction reference can only be recorded on a card or InstaPay payment. Remove it or change the payment method.",
    returnToStockNotAllowed:
      "A comped plate can't be returned to stock — a comp always counts as wasted.",
    itemNotReturnableNotCooked:
      "This plate was never cooked, so there is nothing to return to stock. Go ahead without returning it.",
    itemNotReturnableAlreadyReturned:
      "This plate was already returned to stock. Go ahead without returning it again.",
    itemNotReturnable:
      "This plate can't be returned to stock. Go ahead without returning it.",
    stocktakeAlreadyOpen:
      "A stocktake is already open. Finish or cancel it before starting a new one.",
    stocktakeNotOpenPosted:
      "This stocktake has already been posted and can't be changed. Refresh to see what was recorded.",
    stocktakeNotOpenCancelled:
      "This stocktake was cancelled and can't be changed. Start a new one if you need to.",
    stocktakeNotOpen: "This stocktake is no longer open. Refresh the page.",
    stocktakeNotFound:
      "We couldn't find this stocktake. It may have changed on another device — refresh the page.",
    itemCardTooLarge:
      "This period has more movements than the card shows at once. Pick a shorter period.",
    ledgerIdConflict:
      "This entry's id is already used by another record. Close the form and try again.",
    ingredientNotFound:
      "We couldn't find this ingredient. It may have been deleted — refresh the page.",
    addonSelectionTooFew:
      'This item needs at least {count} of its add-ons chosen — tap "+ Addons" first.',
    addonSelectionNotValid:
      "The chosen add-ons are not valid for this item. Close it, reopen it and pick again.",
    invoiceHasUnreachedItems:
      "Some items never reached the kitchen — fire, accept or remove them first.",
    kitchenStationMissing:
      "No kitchen station can take this item. Check the stations settings.",
    stationHasHistory:
      "This station has old chits on record, so it can't be deleted — pause it instead.",
    kitchenStationNotAllowed:
      "This station is not on your role. Ask the owner to add it to your role.",
    stationLockedByRole:
      "A team role is kept to this station. Take it off that role's stations first, or pause the station instead.",
    fallbackStationInactive:
      "The fallback station must be active. Activate it before selecting it.",
    fallbackStationProtected:
      "The fallback station cannot be deactivated or deleted. Select another active fallback first.",
    builtInStationProtected:
      "The built-in Kitchen, Food, and Drinks stations cannot be deleted; deactivate them instead.",
    printingNotFound:
      "The requested printer or print device is missing or inactive. Refresh and check the configuration.",
    printingRequestInvalid:
      "This print configuration is invalid. Check the printer, width, and tested capabilities.",
    printingStateConflict:
      "The print job changed on another device. Refresh before trying again.",
    printDeviceUnauthorized:
      "The print-device session expired or was revoked. Pair the device again.",
    invoiceNotCancellable:
      "The kitchen has been told about items on this invoice — it can't be cancelled. Close it instead.",
    invoiceNotReopenable:
      "This invoice was already settled or cancelled — it can't be reopened.",
    settlementShort: "The amount received is less than the bill total.",
    partyNotFound:
      "This bill has no such party — someone may have cleared the mark. Refresh and try again.",
    partyLabelRequired: "Name the party, or pick one that already exists.",
    // Square already — usually another till took this family's money first.
    partyAlreadySettled:
      "This family has already paid its share. Refresh the bill to see what is still owed.",
    customerNotEditable:
      "This bill is seated at a table — there are no customer details to correct. Refresh the screen.",
    // T6 — `editCharges` on a dine-in bill, or towards dine-in.
    invoiceEmpty: "This bill is empty — cancel it rather than closing it.",
    chargesNotEditable:
      "This bill sits at a table — it has no delivery fee or channel to change. Refresh the screen.",
    // D153 — a captain on a takeaway or delivery bill: a stale screen.
    captainNotDineIn:
      "A captain is only for a dine-in bill. Refresh the screen.",
    // D153 — the person picked is not one of this store's waiters.
    captainNotEligible:
      "That person isn't one of this store's waiters — a captain must hold the Waiter role. Refresh the screen.",
    // C2 — a delivery platform on a dine-in bill.
    aggregatorChannelInvalid:
      "A dine-in bill can't come through a delivery platform.",
    partialWriteOff:
      "A write-off applies to the whole bill, not to one payment. Write the whole bill off from the settle sheet.",
    invoiceAlreadyTendered:
      "Money has already been taken against this bill. Take the rest as a partial payment — a full settle would charge it again.",
    // D133 fix — this diner's share was already settled from their claim (or
    // the claim was refused); a second settle would take it twice.
    paymentShareSettled:
      "This customer's share has already been settled. Refresh the bill to see what is still owed.",
    // R1/F3 — the refund's refusals. Every one is reachable by a cashier
    // mid-service, so every one earns a sentence rather than the backend's
    // English message.
    refundNotSettled: "A bill that has not been paid yet cannot be refunded.",
    // ONE code, TWO different facts, so two sentences. The first is the
    // walk-off (WRITE_OFF): no money ever arrived. The second is a settlement
    // method nobody has yet decided how to pay back through — and saying the
    // first sentence about it would be a lie on screen, because that bill WAS
    // paid. That lie shipped once, when INSTAPAY became a settlement method
    // and this copy told a cashier a transfer-paid bill was closed unpaid.
    refundMethodNotRefundable:
      "This bill was closed unpaid, so there is no money to give back.",
    refundMethodNotRefundableOther:
      "This bill was paid by a method the system cannot yet send money back through. Check with management before giving anything back another way.",
    refundNothingToRefund:
      "This bill is worth zero, so there is nothing to give back.",
    refundItemNotChargeable:
      "One of the plates you picked is not charged on this bill (it was voided or comped), so it has nothing to give back.",
    refundItemAlreadyRefunded:
      "One of the plates you picked has already come back. Refresh and pick the plates that have not.",
    refundDuplicateItem: "The same plate cannot be named twice in one refund.",
    // The reason vocabularies are closed server-side and the sheet already
    // gates both of these, so reaching either means a stale tab or an old
    // bundle sent the request — which still lands on a till, in whatever
    // language that till reads.
    reasonCodeInvalid:
      "That reason is not one of the reasons allowed for this action. Refresh and pick one of the reasons shown.",
    voidReasonNoteRequired:
      'When you pick "Other", you have to write a note saying what happened.',
    // A refund names a bill and its plates, so either can have moved under
    // the screen between its last refresh and the cashier's tap.
    invoiceNotFound:
      "This bill no longer exists. Refresh and open it again from the invoice list.",
    invoiceItemNotFound:
      "One of the plates you picked is no longer on this bill. Refresh and pick again.",
    // B117/D5 — see the Arabic note: this used to arrive as the sentence above,
    // which told the waiter to refresh a screen that was already right.
    itemNotStarted:
      "The kitchen has not started this plate, so there is nothing to remake. Wait for it to start, or take it off the bill.",
    // B117/RT-D — see the Arabic note: the Z is recomputed on every read, so a
    // late amendment rewrites a filed figure.
    recountWindowClosed:
      "This drawer's business day is over, so its count can no longer be amended. If a difference still needs explaining, speak to the owner.",
    // B117/C7+C8 — see the Arabic note: this rendered in raw English on the
    // Arabic screen, and named the spelling just typed rather than the row that
    // exists.
    supplierNameTaken:
      "A supplier is already recorded under this name: \u201c{name}\u201d. Open that one and edit it instead of creating a second.",
    supplierNameTakenUnnamed:
      "A supplier is already recorded under this name. Find it in the list and edit it instead of creating a second.",
    // The command id is minted on the device, so this is effectively
    // unreachable — but if it is reached, nothing happened, and that is the
    // part the person at the till needs.
    commandOutcomeUncertain:
      "The previous attempt could not be confirmed. Check the invoice before adding items or taking payment again.",
    commandIdConflict:
      "This action did not go through — its id is already recorded against something else. Refresh and try again, and tell support if it happens again.",
    // P9: a command id older than 7 days — nothing ran now; reload.
    commandExpired:
      "This action is more than 7 days old and was not run again. Refresh to see where things stand.",
    // P9.3: the device clock is over 7 days behind — refreshing will not fix it.
    deviceClockWrong:
      "This device's date and time are wrong. Set them correctly in the device settings, then try again.",
    moneyLedgerRejected:
      "The refund did not go through and no money moved. Try again, and tell support if it happens again.",
    refundAllocationBasisInvalid:
      "The refund did not go through and no money moved — this bill's own figures do not add up. Report this bill to support.",
    refundAllocationBasisMismatch:
      "The refund did not go through and no money moved — the split across the plates did not come out right. Report this bill to support.",
    settlementNotCorrectable:
      "A payment method can only be corrected on a settled bill. This one hasn't been settled.",
    // B3 — a WRITE_OFF correction target only within the bill's own
    // business day, so a closed shift's numbers stay frozen.
    writeOffOutsideBusinessDay:
      'Correcting to "walked out unpaid" only works on the bill\'s own business day. That day is already closed.',
    settlementAlreadySettledDifferently:
      "This bill is already settled with a different method. Use “correct payment method” if you need to change how it was paid.",
    // Same method, different tendered amount: the record stands and no
    // command changes it, so this sentence deliberately names none.
    settlementAlreadySettledSame:
      "This bill is already settled with the same method; the recorded amount stays as it is.",
    tableOccupied: "That table already has an open invoice.",
    invoiceNotOpen: "This invoice isn't open — refresh the screen.",
    // D172 — «Print ticket» on a ticket whose every plate is voided: nothing was sent.
    ticketNothingToPrint: "Nothing left on this ticket to reprint.",
    // D167 — «Undo» after the plate moved on, the window passed, or another device took it back.
    undoUnavailable: "This can no longer be undone: the plate has moved on or the undo window has passed.",
    // D173 — the hide time against the pass target, off the settings page that says it inline.
    readyAgeOffInvalid:
      "The ready-plate hide time must be 0, or longer than the pass target ({minutes} min), up to 240 minutes.",
    readyAgeOffInvalidUnnamed:
      "The ready-plate hide time must be 0, or longer than the pass target, up to 240 minutes.",
    // P7 R2 — the kitchen paper changed after it was read; nothing printed.
    ticketPaperChanged: "The kitchen ticket changed since it was read. Refresh, then try again.",
    // D146 — the discount at the bill's refusals, each in words.
    billDiscountAggregator: "A delivery platform's bill cannot take a discount.",
    billDiscountPriceTier: "This bill has a price tier, so it cannot take a discount too.",
    billDiscountOverCap:
      "The discount is above the limit: {percent}% of the net food ({amount}). Enter less and try again.",
    billDiscountOverCapUnnamed:
      "The discount is above the store's limit. Enter less and try again.",
    billDiscountBelowPaid:
      "After the discount the total would be below what is already paid ({paid}). Money already taken goes back as a refund, not a discount.",
    billDiscountBelowPaidUnnamed:
      "After the discount the total would be below what is already paid. Money already taken goes back as a refund, not a discount.",
    billDiscountWholeBill:
      "A discount cannot take the whole bill — comp the plates instead.",
    billDiscountNothingOff: "This discount takes nothing off the bill as it stands.",
    billDiscountReasonInvalid: "That reason is not on the list. Pick one of the reasons shown.",
    billDiscountNoteRequired: '"Other" needs a written reason.',
    approvalRequired: "This action needs a manager's password.",
    approvalInvalid: "The approval was not accepted. Check the name and the password.",
    approvalLocked: "Approval by this name is paused after several wrong attempts.",
    approvalPasswordChangeRequired: "The manager must change their temporary password first.",
    shiftRequired: "No drawer is open — open one first before you can settle.",
    shiftRequiredAskManager:
      "No drawer is open — ask someone with the drawer permission to open one first.",
    shiftAlreadyOpen: "A drawer is already open. Refresh the screen.",
    shiftNotOpen: "There's no open drawer to close or record a movement on.",
    shiftTrackingDisabled:
      "Drawer tracking is turned off in settings — enable it before opening a drawer.",
    // B118 — see the Arabic note: none of the new drawer refusals had a line
    // here, so every one of them reached an Arabic till in raw English.
    shiftAlreadyRecounted:
      "This drawer has already had its one recount; there is no third count. If a difference still needs explaining, write a note on the drawer or speak to your manager.",
    // See the Arabic note: one code, two server-side conditions, and the
    // server's own message names only the first — so this sentence names both
    // rather than asserting the wrong one.
    recountUnavailable:
      "This drawer can't be recounted: either it was closed before blind counting existed, or you are not the person who closed it — the recount belongs to whoever closed the box. A manager can count it separately beside the cashier's count.",
    // The server now publishes a `reason` beside the code, so these two name
    // the condition that actually fired. The disjunction above stays as the
    // fallback for a body from a build older than the reason.
    recountNoFirstCount:
      "This drawer was closed before blind counting existed, so there is no first count to recount against. If a difference still needs explaining, write a note on the drawer.",
    recountNotYourDrawer:
      "You aren't the person who closed this drawer, and the recount belongs to whoever closed the box. A manager can count it separately beside the cashier's count.",
    shiftAlreadyManagerCounted:
      "A manager has already counted this drawer, and that count happens once only.",
    managerCountUnavailable:
      "You can't add a manager's count to this drawer: either it isn't closed yet, or you are the one who closed it — and the closer's amendment is the recount, not this.",
    shiftNoteEmpty: "Write the note first — an empty note isn't recorded.",
    // See the Arabic note: the old refusal named a grant the caller neither
    // needs nor should be given.
    shiftNoteNotYourDrawer:
      "This drawer was closed by somebody else, so its notes aren't yours to write. Write on a drawer you closed, or ask someone who reviews drawers to add it.",
    // D95/Unit B — all four carry the box's label, and the server sends it
    // precisely so the screen can name it; a till with three drawers on it
    // cannot act on "no drawer is open". Unnamed twins for a body that arrives
    // without one, which says "this drawer" rather than inventing a name.
    drawerAlreadyOpen:
      "“{label}” is already open and held by someone else. Take it over, or open a box under a different name.",
    drawerAlreadyOpenUnnamed:
      "That drawer is already open and held by someone else. Take it over, or open a box under a different name.",
    drawerIsYours:
      "“{label}” is your own drawer — close it with a count; taking over is for someone else's box.",
    drawerIsYoursUnnamed:
      "That is your own drawer — close it with a count; taking over is for someone else's box.",
    drawerNotOpen:
      "No drawer is open on “{label}” any more — it may have just been closed. Refresh and read the list again.",
    drawerNotOpenUnnamed:
      "No drawer is open on that box any more — it may have just been closed. Refresh and read the list again.",
    drawerAlreadyTakenOver:
      "Someone else has already taken over “{label}”. Refresh and read the drawer list again.",
    drawerAlreadyTakenOverUnnamed:
      "Someone else has already taken over that drawer. Refresh and read the drawer list again.",
    lastDrawerHasOpenInvoices:
      "The last open drawer can't be closed while invoices are still open. Settle or cancel them first, then close the drawer.",
    // B117/Q6 — after the close, covers are a supervisor's correction, which is
    // exactly the boundary the server draws. The sentence names who to ask.
    guestCountLocked:
      "Once the bill is closed, covers can only be corrected by someone who may void items (a cashier or a manager). Ask them to change it.",
    // The guard's own message is "Missing required permissions", which reached
    // the screen verbatim — and this is an ordinary refusal for a cashier
    // without the drawer rung.
    permissionDenied:
      "You don't have the permission this action needs. Ask the owner to grant it, or have someone who holds it do it.",
    // Unit E — see the Arabic note: the form pre-empts all five, so each of
    // these means a stale tab or a person who left the team between the list
    // and the tap. They still land on an Arabic till.
    movementCategoryInvalid:
      "That category doesn't go with this kind of movement. Refresh and pick one of the categories shown.",
    movementCounterpartyRequired:
      "This movement has to name the person the cash changed hands with — pick a name first.",
    movementCounterpartyNotAllowed:
      "This movement has no second member of staff to name — clear the name and try again.",
    movementCounterpartyIsYou:
      "Pick someone else — cash can't change hands with yourself.",
    movementCounterpartyUnknown:
      "That person isn't on this store's team. Refresh and pick from the list again.",
    exportTooLarge:
      "That file would be too large. Narrow the date range in the filters and try again.",
    priceTierNotFound: "That price tier doesn't exist — refresh and try again.",
    priceTierInactive: "That price tier has been retired.",
    priceTierOutsideWindow:
      "That price tier is outside its active hours right now.",
    addonOverrideGroupNotAttached:
      "That group isn't attached to this item. Refresh and try again.",
    addonOverrideAddonNotInGroup:
      "That option doesn't belong to this group. Refresh and try again.",
    addonOverrideDuplicateRow:
      "One row is listed twice — remove the extra one and try again.",
    swapSameIngredient: "An ingredient can't be swapped for itself.",
    swapDuplicateRemoval:
      "That ingredient is already being taken off — by another swap, or by an option on this item. Remove it in one place only.",
    swapChainedSwap:
      "One swap adds what another takes off. Make it a single swap, from the first ingredient to the last.",
    swapIngredientNotFound:
      "That ingredient isn't one of this store's. Refresh and try again.",
    swapNotOnRecipe:
      "That ingredient isn't in this item's recipe, so there is nothing to take off.",
    swapUnitMismatch:
      "The two ingredients are measured in different units and there is no conversion between them. Pick one in the same unit.",
    swapNotSupportedOnCombo: "A combo's ingredients can't be swapped.",
    swapPlateAlreadyStarted:
      "A cook has already started this plate, so it can't be changed. If it must change: void it and order it again.",
    swapPlateNotAmendable:
      "This plate can't be changed from here — it is either a diner's request nobody has accepted yet, or it has been voided.",
    swapInvalid: "That swap isn't valid. Refresh and try again.",
    addonOverrideRequiredUnsatisfiable:
      "This group is required, and this change would leave the item with fewer options than it asks for.",
    addonOverrideRequiredUnsatisfiableNamed:
      "This group is required, and this change would leave “{name}” with fewer options than it asks for.",
    addonOverrideIngredientNotFound:
      "That ingredient isn't one of this store's. Refresh and try again.",
    addonOverrideInvalid:
      "That change isn't allowed. Check the prices and options and try again.",
    // F24 (§5.12) — the customer's request for a bag.
    onlineOrderNotRequested:
      "This order is no longer waiting for an answer — another till accepted it, or it was rejected, expired, or cancelled by the customer. Refresh the screen.",
    onlineOrderingOff:
      "Ordering from the storefront is off for this restaurant. Turn it on under Settings → Online ordering.",
    menuItemUnavailable:
      "A dish in this order is no longer on the menu (sold out or removed). Reject the order or call the customer — no bill was opened.",
  },
  perm: {
    area_POS: "Till",
    area_INVOICE_HISTORY: "Earlier bills",
    area_BILL_ADJUST: "Adjusting a bill",
    area_MENU: "Menu",
    area_FLOOR: "Tables",
    area_COSTING: "Stock & costing",
    area_DELIVERY: "Receiving deliveries",
    area_PRICE_TIER: "Price tiers",
    area_RESTAURANT_SETTINGS: "Restaurant settings",
    area_KITCHEN: "Kitchen",
    area_SHIFT: "Cash drawers",
    area_PRICE_OVERRIDE: "Manual price override",
    area_REFUND: "Refunds",
    area_EXCEPTION_APPROVE: "Approving exceptions",
    area_SUBSTITUTE: "Ingredient swaps",
    area_DRIVER: "Driver work",
    area_DISPATCH: "Dispatch",
    area_DRIVER_DOCUMENTS: "Driver documents",
    area_DRIVER_INVITE: "Inviting drivers",
    area_CUSTOMERS: "Customers",
    area_REPORTS: "Reports",
    // The online storefront's look. Edited in the seller portal, not here,
    // which is why it collapses into the "online store" group.
    area_DESIGN: "Storefront design",
    area_RESTAURANT: "Restaurant — other permissions",
    area_PRODUCT: "Products",
    area_CATEGORY: "Categories",
    area_ORDER: "Online orders",
    area_MEMBER: "Team",
    area_ROLE: "Roles",
    area_STORE: "Store details",
    area_COUPON: "Coupons",
    area_REVIEW: "Reviews",
    area_SHIPPING: "Shipping",
    area_PAYMENT: "Payments",

    tierNone: "None",
    tierView: "View only",
    tierManage: "Manage",
    tierFull: "Full",
    tierCustom: "Custom",
    tierNoneHelp: "They will not see this area at all.",
    tierViewHelp: "They can look, but change nothing.",
    tierManageHelp: "They can look, add and edit — but not delete.",
    tierFullHelp: "Everything in this area, deleting included.",
    tierCustomHelp:
      "A hand-picked combination. Pick a level above to simplify it.",

    p_RESTAURANT_POS_USE: "Open the till",
    p_RESTAURANT_KITCHEN_VIEW: "Kitchen board",
    p_RESTAURANT_ITEM_FIRE: "Send items to the kitchen",
    p_RESTAURANT_ITEM_PREPARE: "Start and finish cooking",
    p_RESTAURANT_ITEM_SERVE: "Serve items",
    p_RESTAURANT_KITCHEN_PRINT: "Print kitchen chits",
    p_RESTAURANT_REPORT_VIEW: "Spend-vs-gain report",
    p_RESTAURANT_REPORT_OPERATIONS_VIEW: "Shift & staff reports",
    p_RESTAURANT_INVOICE_OPEN: "Open a bill for a table",
    p_RESTAURANT_INVOICE_APPEND: "Add items to a bill",
    p_RESTAURANT_INVOICE_REMAKE: "Ask for an item to be remade",
    p_RESTAURANT_INVOICE_VOID: "Void an item off a bill",
    p_RESTAURANT_INVOICE_CLOSE: "Close a bill",
    p_RESTAURANT_INVOICE_SETTLE: "Settle a bill",
    p_RESTAURANT_MENU_VIEW: "See the menu",
    p_RESTAURANT_MENU_MANAGE: "Edit the menu",
    p_RESTAURANT_FLOOR_MANAGE: "Manage areas and tables",
    p_RESTAURANT_COSTING_MANAGE: "Manage stock and costing",
    p_RESTAURANT_DELIVERY_RECORD: "Receive a supplier delivery",
    p_RESTAURANT_TIER_MANAGE: "Manage price tiers",
    p_RESTAURANT_SETTINGS_MANAGE: "Edit restaurant settings",
    p_RESTAURANT_SHIFT_MANAGE: "Open and close cash drawers",
    p_RESTAURANT_INVOICE_PRICE_OVERRIDE: "Type a different price at the till",
    p_RESTAURANT_INVOICE_SUBSTITUTE: "Swap an item's ingredients at the till",
    p_RESTAURANT_MENU_AVAILABILITY: "Mark an item sold out (86)",
    p_RESTAURANT_INVOICE_REFUND: "Refund a settled bill",
    p_RESTAURANT_EXCEPTION_APPROVE: "Approve a cashier's void, comp or discount by password",
    p_RESTAURANT_DRIVER_WORK: "Do their own deliveries from the driver app",
    p_RESTAURANT_DISPATCH_MANAGE: "Assign deliveries to drivers and follow them",
    p_RESTAURANT_DISPATCH_CASH_CONFIRM: "Confirm the cash a driver hands over",
    p_RESTAURANT_DRIVER_DOCUMENTS_VIEW: "View drivers' identity documents",
    p_RESTAURANT_DRIVER_INVITE: "Invite a new delivery driver",
    p_RESTAURANT_CUSTOMER_VIEW: "View the customer list and order history",
    p_RESTAURANT_CUSTOMER_BLOCK: "Block a customer from ordering on the website",
    p_STORE_VIEW: "See store details",
    p_STORE_EDIT: "Edit store details",
    p_DESIGN_VIEW: "See the storefront design",
    p_DESIGN_EDIT: "Edit the design draft",
    p_DESIGN_PUBLISH: "Publish the design live",
    p_PRODUCT_VIEW: "See products",
    p_PRODUCT_CREATE: "Add a product",
    p_PRODUCT_EDIT: "Edit a product",
    p_PRODUCT_DELETE: "Delete a product",
    p_CATEGORY_VIEW: "See categories",
    p_CATEGORY_CREATE: "Add a category",
    p_CATEGORY_EDIT: "Edit a category",
    p_CATEGORY_DELETE: "Delete a category",
    p_ORDER_VIEW: "See orders",
    p_ORDER_EDIT: "Edit orders",
    p_ORDER_FULFILL: "Fulfil and hand over orders",
    p_COUPON_VIEW: "See coupons",
    p_COUPON_CREATE: "Add a coupon",
    p_COUPON_EDIT: "Edit a coupon",
    p_COUPON_DELETE: "Delete a coupon",
    p_REVIEW_VIEW: "See reviews",
    p_REVIEW_MODERATE: "Moderate reviews",
    p_SHIPPING_VIEW: "See shipping settings",
    p_SHIPPING_EDIT: "Edit shipping settings",
    p_PAYMENT_VIEW: "See payment settings",
    p_PAYMENT_EDIT: "Edit payment settings",
    p_MEMBER_VIEW: "See the team",
    p_MEMBER_INVITE: "Invite someone to the team",
    p_MEMBER_REMOVE: "Remove someone from the team",
    p_MEMBER_EDIT_ROLES: "Change team roles",
    p_ROLE_VIEW: "See roles",
    p_ROLE_CREATE: "Create a role",
    p_ROLE_EDIT: "Edit a role",
    p_ROLE_DELETE: "Delete a role",
    p_RESTAURANT_INVOICE_HISTORY_VIEW: "See earlier days' bills",
    p_RESTAURANT_INVOICE_PAST_EDIT: "Work on a bill from an earlier day",
    p_RESTAURANT_INVOICE_CANCEL: "Cancel a whole bill",
    p_RESTAURANT_INVOICE_REOPEN: "Reopen an unpaid bill",
    p_RESTAURANT_INVOICE_CHARGES_EDIT: "Edit service and delivery charges",
    p_RESTAURANT_INVOICE_COMP: "Comp an item",
    p_RESTAURANT_INVOICE_DISCOUNT: "Discount a bill",
    p_RESTAURANT_INVOICE_PAYMENT_CORRECT: "Correct how a bill was paid",
    p_RESTAURANT_INVOICE_WRITE_OFF: "Write off an unpaid bill",
    // The role editor's rows, groups and sections (2026-11-02, `role-acts.ts`).
    act_pos: "Open the till and see the menu",
    actHint_pos: "And today's bills, open and closed",
    act_open: "Open a bill for a table or takeaway",
    actHint_open: "For now also accepts online orders and pauses them",
    act_append: "Add items to an open bill",
    actHint_append: "And guest count, customer, and moving it to another table",
    act_fire: "Send items to the kitchen",
    act_serve: "Mark a dish served",
    act_remake: "Ask for a dish to be remade",
    act_subst: "Swap an ingredient in a dish",
    actHint_subst: "The system prices it, and stock follows",
    act_avail: "Mark an item sold out today",
    actHint_avail: "Hidden from the till until the day ends",
    act_void: "Void an item from the bill",
    actHint_void: "Without \"Approve a colleague\" it asks for a manager's password",
    act_comp: "Comp an item on the house",
    actHint_comp: "Without \"Approve a colleague\" it asks for a manager's password",
    act_discount: "Discount the whole bill",
    actHint_discount: "Without \"Approve a colleague\" it asks for a manager's password",
    act_chargesEdit: "Edit service and delivery charges",
    actHint_chargesEdit: "And switch an order between takeaway and delivery",
    act_cancel: "Cancel a whole bill",
    actHint_cancel: "A bill nothing was charged on, opened by mistake",
    act_reopen: "Reopen a printed bill that isn't paid",
    actHint_reopen: "The guest wants something more · a paid bill never reopens",
    act_payFix: "Correct how a paid bill was paid",
    actHint_payFix: "For example cash to card",
    act_writeOff: "Write off an unpaid bill",
    actHint_writeOff: "The guest left without paying",
    act_override: "Type an item's price by hand",
    act_approve: "Approve a colleague's void, comp or discount",
    actHint_approve: "Types their password on the colleague's device, and needs no approval themselves",
    act_close: "Print the check and close the bill",
    actHint_close: "And set the table's captain",
    act_settle: "Take payment (cash, card, InstaPay)",
    actHint_settle: "Full or split · cash goes into their open drawer",
    act_refund: "Refund a paid bill",
    actHint_refund: "The most dangerous permission in the restaurant: give it to one named person",
    act_historyView: "See earlier days' bills",
    actHint_historyView: "Paid and cancelled bills before today's business day · today's bills and unpaid ones always show",
    act_pastEdit: "Work on a bill from an earlier day",
    actHint_pastEdit: "Close it, take its payment, void from it or refund it",
    act_shift: "Open, close and count their drawer",
    actHint_shift: "For now also cash in and out, taking over a colleague's drawer, and their own past drawers · others' drawers come with \"Operations reports\"",
    act_dispatch: "Assign orders to drivers",
    act_driverCash: "Take a driver's cash",
    actHint_driverCash: "Counted in their drawer",
    act_driverInvite: "Invite a new driver",
    act_driverDocs: "See drivers' documents",
    actHint_driverDocs: "ID and licence",
    act_driverWork: "Work as a driver in the driver app",
    act_kitchenView: "Open the kitchen screen",
    actHint_kitchenView: "Can be limited to some stations",
    act_prepare: "Move dishes: preparing → ready",
    act_kitchenPrint: "Print a kitchen ticket",
    act_menuView: "See the menu",
    actHint_menuView: "The till needs it",
    act_menuManage: "Edit the menu, items and add-ons",
    act_categories: "Add and edit menu sections",
    act_tiers: "Price lists and occasions",
    act_floor: "Edit the floor plan and tables",
    act_receive: "Receive goods from a supplier",
    actHint_receive: "Without seeing costs or recipes",
    act_costing: "Ingredients, recipes, stocktake and purchases",
    actHint_costing: "Sees average cost · also records paying suppliers",
    act_operations: "Operations reports",
    actHint_operations: "Voids by staff, peak hours, and every drawer and the cash log",
    act_profit: "Profit, costs and the accountant",
    actHint_profit: "Item margins and supplier payments",
    act_customers: "Customer list",
    act_blockCustomer: "Block a customer",
    act_settings: "Restaurant settings",
    actHint_settings: "Taxes, service, platforms, printers",
    act_teamView: "See the team",
    act_teamManage: "Invite and remove members and change their roles",
    act_rolesView: "See roles",
    act_rolesManage: "Create, edit and delete roles",
    actHint_rolesManage: "Can't grant more than they hold",
    grp_order: "Orders",
    grpSub_order: "The till, the open bill, kitchen and serving",
    grp_edit: "Changing a bill's amount",
    grpSub_edit: "Before payment: anything that lowers it or changes a price",
    grp_pay: "Check and payment",
    grpSub_pay: "Printing the check, taking payment, refunds",
    grp_history: "Earlier bills",
    grpSub_history: "Before today's business day",
    grp_drawer: "Cash drawer",
    grpSub_drawer: "Their own drawer only",
    grp_delivery: "Delivery and drivers",
    grpSub_delivery: "Assigning orders, drivers and their cash",
    grp_kitchen: "Kitchen screen",
    grpSub_kitchen: "Preparing dishes and printing tickets",
    grp_menu: "Menu and floor",
    grpSub_menu: "Items, prices and the table plan",
    grp_stock: "Stock and suppliers",
    grpSub_stock: "Receiving, ingredients and stocktake",
    grp_reports: "Reports and customers",
    grpSub_reports: "Operations, profit, the customer list",
    grp_admin: "Settings and team",
    grpSub_admin: "Settings, team and roles",
    sec_bill: "The bill: from first order to last payment",
    sec_shift: "The shift",
    sec_admin: "Running the restaurant",
  },
  invoices: {
    pickTitle: "Pick an invoice",
    pickBody: "Choose one from the list to see its full details here.",
    statInvoicesSub: "{count} settled",
    statSales: "Sales",
    statSalesSub:
      "Settled only, write-offs excluded, includes any service charge and VAT collected",
    statAverage: "Average invoice",
    statCancelledNone: "Nothing cancelled",
    /*
     * PAPER (round 3) — the history page's answer tiles, the filter button,
     * the time column and the receipt drawer's pay line. Every figure here is
     * the loaded page's, like the other tiles.
     */
    paperStatOpen: "Not settled yet",
    paperStatCancelledOne: "1 cancelled · {amount}",
    paperStatCancelledTwo: "2 cancelled · {amount}",
    paperStatCancelledFew: "{count} cancelled · {amount}",
    paperStatCancelled: "{count} cancelled · {amount}",
    paperFilter: "Filter",
    tableTime: "Time",
    paperPaidBy: "Paid: {method}",
    paperDue: "{amount} due",
    basisTermOpen: "Not settled yet",
    basisOpenNote:
      "The invoices on this page that are still open or waiting for payment, at their running totals. Money not taken yet, not sales.",

    /*
     * Slice 9 / UX-round D41 / ruling F20 — this screen had the export and no door,
     * which is exactly why its scope caveat floated on the canvas: there was
     * nowhere else for it to go. Now there is a door and a sheet.
     *
     * And every tile footer holds ONE fact: "settled only, write-offs
     * excluded, includes any service charge and VAT collected" is a basis
     * sentence, and a basis sentence belongs behind the door, not under a
     * figure.
     */
    cardTitle: "Invoices",
    basisTrigger: "How this is counted",
    basisTitle: "How these figures are counted",
    basisChipPage: "This page",
    basisTermScope: "What these figures cover",
    basisTermSales: "Sales",
    basisTermAverage: "Average invoice",
    basisAverageNote:
      "The average is sales ÷ the number of settled invoices on this page, rounded to the nearest pound.",
    // The refund total became a tile rather than a second line under sales: a
    // footer holds one fact, and money handed back to a guest is a figure in
    // its own right, not a footnote on another one.
    statRefunded: "Given back",
    statRefundedFooter: "Already taken off the sales figure above",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    statWriteOffsOne: "1 written off · {amount}",
    statWriteOffsTwo: "2 written off · {amount}",
    statWriteOffsFew: "{count} written off · {amount}",
    statWriteOffs: "{count} written off · {amount}",
    // R12 — a refund is money that reached the drawer and went back. The bill
    // stays SETTLED with a cash/card method (there is no new status), so
    // without this line the sales figure would count a fully refunded bill as
    // takings and nothing on screen would say otherwise.
    statRefunds: "after {amount} given back",
    // R12 — the marker on the ROW. The strip above says how much came back
    // across the day; no row said which bill it came back off, so "which
    // bills were refunded today" had no answer in the list. The bill's own
    // total stays above it, unchanged, exactly as it closed (R5).
    rowRefunded: "{amount} given back",
    refundTotal: "Given back",
    refundNote:
      "The figures above are the bill exactly as it was closed — a refund sits beside them and never rewrites them.",
    refundMethodDiverged:
      "Given back by a method this bill no longer names — the settlement was corrected after the refund. A flag, not an error: the drawer really is short that cash.",
    chargeSnapshotExcess:
      "This bill's own figures don't add up: the service charge and VAT recorded on it exceed the total of its items by {amount}. Any refund off this bill returns the customer's money in full, but the VAT returned will be short by that amount.",
    statScopeNote: "Money figures cover the settled rows on this page.",
    tableStatus: "Status",
    // F1 (unit 4): this column now answers "where did this bill happen", not
    // "which table" — a delivery or takeaway bill sits at no table at all, and
    // printing "Delivery" under a heading that says "Table" makes the heading
    // itself a lie to someone who was not in the restaurant.
    tableTable: "Where",
    itemPreviewMore: "+{count}",
    searchPlaceholder: "Invoice number…",
    cancelInvoice: "Cancel invoice",
    cancelInvoiceTitle: "Cancel this invoice?",
    cancelInvoiceBody:
      "It comes out of today's sales. Its items and history stay exactly as they are.",
    // Audit round 2, Phase 3 — the second sentence says what the server
    // guarantees (cancel is refused while any plate is with the kitchen).
    cancelInvoicePosBody:
      "It comes out of today's sales, and anything not yet sent to the kitchen is discarded with it. Nothing on this bill is with the kitchen, so nothing leaves stock and nothing is recorded as waste.",
    cancelInvoiceDone: "Invoice cancelled",
    tableInvoice: "Invoice",
    tableDate: "Date",
    tableItems: "Items",
    tableTotal: "Total",
    title: "Invoices",
    description: "Today's service, and any day before it.",
    paperDescription:
      "Every invoice from today, paid or still open. Open one to see the receipt.",
    filtersLabel: "Filter invoices",
    statusFilterLabel: "Status",
    statusAll: "All",
    tableFilterLabel: "Where",
    // F1: "All places" and not "All tables" — this filter now spans delivery
    // and takeaway bills, which sit at no table at all.
    tableAll: "All places",
    methodFilterLabel: "Payment method",
    // "All payment methods" did not fit the filter bar's shared 12rem control
    // and rendered clipped as "All payment methoc" — caught by a browser pass,
    // not by a test. Shortened rather than widening the control: the four
    // Comboboxes share one width on purpose, and its siblings read "All",
    // "All places", "All bills".
    methodAll: "All methods",
    refundedFilterLabel: "Refunds",
    refundedAll: "All bills",
    refundedWith: "Refunded",
    refundedWithout: "Not refunded",
    // Audit round 2, P5 — the bills something happened to: an item voided
    // after it reached the kitchen, a comped item, a bill discount or a refund.
    // A QR request rejected or withdrawn is not one — the exceptions reports
    // do not count it.
    exceptionFilterLabel: "Exceptions",
    exceptionWith: "With an exception",
    rowException: "Exception",
    basisTermException: "Exception",
    basisExceptionNote:
      "A bill with an item voided after it reached the kitchen, a comped item, a bill discount or a refund. A refund counts whatever day it was given.",
    // T10 — channel, platform and customer: "the Mona order" and "the Talabat
    // bills" without opening each one. Search is by name or phone, 2+ chars.
    channelFilterLabel: "Channel",
    channelAll: "All channels",
    platformFilterLabel: "Platform",
    platformAll: "All platforms",
    customerSearchLabel: "Customer",
    customerSearchPlaceholder: "Customer name, phone or platform order no.",
    customerSearchHint: "At least 2 characters",
    // T9 — the fee's VAT line on the printed receipt, so the paper adds up.
    deliveryFeeVat: "VAT on delivery fee",
    // T20 — the VAT line states its rate and base: "VAT 14% on 130.00".
    vatWithRateBase: "VAT {percent}% on {base}",
    vatIncludedWithRateBase: "Incl. VAT {percent}% on {base}",
    deliveryFeeVatIncluded: "incl. VAT on delivery fee",
    tableFallback: "Table {id}",
    dateRangeToday: "Today's service",
    dateRangeValue: "{from} to {to}",
    dateRangeApply: "Apply",
    dateRangeClear: "Clear",
    dateRangeInvalid:
      "The start date is after the end date. Fix the range and try again.",
    // P5-Q3 (2026-10-27) — the history and its export read at most 92 days,
    // the reports' own limit: the picker's footer hint, and its refusal.
    dateRangeHint: "Pick one day, or a range of up to {span}.",
    dateRangeTooLong: "History covers {span} at most — pick a shorter range.",
    clearFilters: "Clear filters",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    resultCountOne: "Showing {shown} of 1 invoice",
    resultCountTwo: "Showing {shown} of 2 invoices",
    resultCountFew: "Showing {shown} of {total} invoices",
    resultCount: "Showing {shown} of {total} invoices",
    resultCountLoading: "Loading invoices...",
    itemCount: "{count} items charged",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    itemCountFew: "{count} items charged",
    // B117/B4 — and «1 items charged» in English.
    itemCountNone: "No items charged",
    itemCountOne: "1 item charged",
    itemCountTwo: "2 items charged",
    emptyTodayTitle: "No invoices in today's service yet",
    emptyTodayBody:
      "The moment an invoice is closed on any table it shows up here. To look at an earlier day, pick a date above.",
    emptyFilteredTitle: "No invoice matched",
    emptyFilteredBody:
      "Nothing matches the filters you picked. Widen the range, or clear the filters to go back to today's service.",
    paginationLabel: "Invoice pages",
    prevPage: "Previous",
    nextPage: "Next",
    pageOf: "Page {page} of {pages}",
    noAccessTitle: "You cannot see invoices",
    noAccessBody:
      "This screen needs the till permission. Ask the restaurant owner to add it to your role.",
    detailFallbackTitle: "Invoice",
    backToList: "Back to invoices",
    openInTill: "Open in the till",
    openInTillToRefund: "Open in the till to give money back",
    refundNeedsPermission:
      "Giving money back needs its own permission, and you do not hold it. Ask the restaurant owner to open this bill in the till and refund it there.",
    printRefundReceipt: "Print a copy of the refund receipt",
    printRefundReceiptOne: "Print a copy of refund {reference} · {amount}",
    refundMarkerRowLabel: "Refund {reference} · {amount}",
    businessDate: "Service day",
    openedAt: "Opened",
    closedAt: "Closed",
    settledAt: "Settled",
    settlementMethod: "Payment method",
    settlementAmount: "Amount received",
    // B117/B1 — see the Arabic note. On a split-tender bill one figure under a
    // cash-sounding label is two different payments added together.
    settlementTenderBreakdown: "{count} tenders: {parts}",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    settlementTenderBreakdownFew: "{count} tenders: {parts}",
    settlementTenderBreakdownTwo: "2 tenders: {parts}",
    settlementChange: "Change given back",
    notYet: "Not yet",
    // D45: covers as captured at open — shown only when someone counted.
    guestCount: "Guests",
    // D46: a price typed by hand on this line instead of the menu price.
    priceOverrideChip: "Manual price {price}",
    // R12/§10: the money for this plate has gone back. Derived from the money
    // ledger, never stored on the line — the bill itself did not change.
    itemRefundedChip: "Refunded",
    correctSettlement: "Correct payment method",
    correctSettlementTitle: "The right payment method",
    correctSettlementBody:
      "Pick what the customer actually paid with. The amount is adjusted for you. This works on a bill from an earlier day too — a closed drawer keeps its own figures, and the correction shows up in today's drawer review.",
    correctSettlementDone: "Payment method corrected",
    // DECISION 3 — before confirming. Correcting a settlement after a refund
    // is allowed and is never refused, but the refund keeps the method the
    // money physically left by, so the manager is told they are creating that
    // divergence rather than discovering it afterwards.
    correctSettlementRefundWarning:
      "{amount} has already gone back off this bill by {method}. Changing the payment method is allowed and will not be refused, but the money that went back keeps the method it physically left by — so the bill will say one thing and the drawer another. A flag, not an error.",
    correctSettlementRefundDiverged:
      "{amount} has already gone back off this bill by a method it no longer names, because the payment method was corrected once already. Correcting it again is still allowed, and the refund keeps the method the money physically left by. A flag, not an error.",
    // B3 — turning a settled bill into a walk-out erases its revenue from
    // every figure, so it needs a reason on record like any other void.
    writeOffReasonIntro:
      "This bill will become a walk-out — it removes all of its revenue from every figure. Record why.",
    itemsEmptyHistoryBody:
      "This invoice was closed without a single item on it.",
    addonSeparator: ", ",
    remadeTimes: "Remade {count} times",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    remadeTimesOne: "Remade once",
    remadeTimesTwo: "Remade twice",
    remadeTimesFew: "Remade {count} times",
    itemActive: "Charged",
    itemPending: "Waiting for approval",
    itemDraft: "Not sent yet",
    itemQueued: "In the queue",
    itemPreparing: "Preparing",
    itemReady: "Ready",
    itemServed: "Served",
    itemVoided: "Voided",
    itemComped: "On the house",
    itemNotCharged: "Not charged",
    totalsTitle: "Totals",
    subtotal: "Subtotal",
    discountTotal: "Discount",
    serviceCharge: "Service charge",
    serviceChargeIncluded: "Incl. service charge",
    vat: "VAT",
    /** F17: the fee line, named where a refund row has to say which number. */
    deliveryFee: "Delivery fee",
    vatIncluded: "Incl. VAT",
    serviceChargeWithRate: "Service charge {percent}%",
    serviceChargeIncludedWithRate: "Incl. service charge {percent}%",
    vatWithRate: "VAT {percent}%",
    vatIncludedWithRate: "Incl. VAT {percent}%",
    netBeforeCharges: "Net before charges",
    taxRegistrationNumber: "Tax reg. no.",
    totalsStillRunning:
      "This bill is still open — these figures keep moving until it closes, when they are written for good.",
    exportCsv: "Download CSV",
    exportCsvHint:
      "Downloads the invoices shown with the current filters, as a CSV file.",
    exporting: "Downloading the file...",
    exportDone: "File downloaded",
  },
  reconcile: {
    title: "Stock check",
    description:
      "Compares the on-hand number stored on the ingredient with the one recomputed from purchases, adjustments and sales.",
    run: "Check stock",
    running: "Recomputing...",
    cached: "Stored on-hand",
    derived: "Recomputed on-hand",
    drift: "Difference",
    matchChip: "Matches",
    driftChip: "Mismatch",
    matchBody:
      "The two numbers agree. What the screen shows is exactly what the movements add up to, and there is nothing to do.",
    driftBody:
      "The stored on-hand is {drift} {unit} higher than the movements add up to — a negative number means it is lower. This is not food missing from the store room: it means a movement was recorded wrong, or the on-hand number changed with no movement behind it. Go back through this ingredient's purchases and adjustments, and if the real quantity really is different, record an adjustment for the gap.",
    readOnly:
      "This check only reads — it does not change the on-hand number and it does not record an adjustment.",
  },
  ingredient: {
    editTitle: "Edit ingredient",
    nameRequired: "Give the ingredient a name.",
    unitChangeableHint:
      "Nothing has been bought or sold against this ingredient yet, so its unit can still be changed. The first purchase or sale freezes it.",
    unitLockedTitle: "The unit is frozen",
    unitLockedBody:
      "This ingredient already has movements recorded against it, and a recorded quantity carries no unit of its own — 500 is stored as just 500. So changing the unit away from {unit} would leave every past purchase reading as a different unit with the same number, and the average cost would move with it. There is nothing to convert, which is why the change is refused.",
    unitLockedRemedy:
      "If the unit really was wrong from the start, the fix is a new ingredient with the right unit that you use from now on — not a rewrite of what already happened.",
    unitLockedAfterAttempt:
      "The server refused the change: this ingredient has been sold before, even though no purchase is recorded against it. Everything else you typed is still here — press save.",
    packHint: "Pack",
    packHintHelp:
      "If the item comes in a fixed pack (a 5 kg bag of rice), enter its name and its size in {unit}. The delivery screen fills the quantity from it. Leave it empty for weighed goods.",
    packLabel: "Pack name",
    packSize: "Pack size",
    packSuggestions: "Common names",
    packPairRequired: "Enter the pack name and its size together, or leave both empty.",
    packSizeInvalid: "Enter a size above zero.",
    packLabelTooLong: "The pack name takes up to 20 characters.",
    packNotConversion:
      "A hint for receiving only — no unit conversion in recipes, counts or reports.",
    lowStockThreshold: "Warn me at",
    lowStockThresholdHint:
      "When what's on hand falls to this number or below, the ingredient is flagged as running low. In {unit}. Leave it empty for no warning.",
    thresholdInvalid: "Enter a positive number, or leave it empty.",
    sellPrice: "Sell price",
    sellPriceHint:
      "Used only to price a swap: if a waiter replaces this with a dearer ingredient, the difference is added to the plate. It has nothing to do with the dish's own menu price. Leave it empty to let swaps go through free — those are listed in the drawer review so you can price them later.",
    sellPriceInvalid: "Enter zero or a positive number, or leave it empty.",
    // D108: what KIND of number the price above is. FLAT is what every
    // ingredient priced before this carried, and stays the default.
    sellPriceBasis: "Price type",
    sellPriceBasisFlat: "Flat amount per swap",
    sellPriceBasisPerUnit: "Price per {unit}",
    sellPriceBasisFlatHint:
      "The same difference on every dish, however much of it the dish uses. This is what the product has always done.",
    sellPriceBasisPerUnitHint:
      "The difference is multiplied by the recipe quantity, so a beef swap on a large platter costs more than the same swap in a sandwich — which is what it already costs your kitchen. The quantity comes from the dish's own recipe at the moment of the swap.",
    sellPriceBasisChanged:
      "You changed the price type, so the number in the field now means something else. Enter the price again on the new type before saving.",
    // The ingredient's icon (2026-10-03): a badge from the Doodles library.
    icon: "Icon",
    iconAll: "All icons",
    iconChange: "Change icon",
    iconHint: "Click the icon to change it",
    historyAction: "History",
    // UX-round D56 — three record screens opening off the same row became one drawer
    // with a filter. One title; the SUBTITLE is what changes, so each of the
    // three questions stays a question of its own rather than a row in a
    // single mixed feed.
    recordTitle: "{name} — the record",
    recordViewLabel: "Which record",
    recordViewLedger: "Came in & corrected",
    recordViewDepletions: "Cooked with it",
    recordViewAvailability: "Called finished",
    historyTitle: "{name} history",
    historySubtitle:
      "Every purchase and adjustment recorded against this ingredient",
    appendOnlyNote:
      "This history is only ever added to — no line can be edited or deleted. A movement recorded wrongly is corrected by recording a further adjustment for the difference, and both stay visible.",
    purchasesTitle: "Purchases",
    /* P5.3 — the ledger shows at most 200 rows, newest first, like the depletions. */
    purchasesTruncatedNote:
      "There are more purchases on this ingredient than we show — the list below is the latest 200, not the whole history.",
    purchasesEmptyTitle: "No purchases recorded yet",
    purchasesEmptyBody:
      'That\'s normal for a new ingredient. Record the first one from "Record a purchase" on the ingredients screen, and the quantity, what you paid and the unit cost will show up here.',
    unitCostAtPurchase: "Unit cost on this purchase",
    /* D136 — the business day the delivery counts on (can be before it was entered). */
    purchaseBusinessDate: "Counts on",
    avgCostAfter: "Average cost afterwards",
    adjustmentsTitle: "Adjustments",
    adjustmentsTruncatedNote:
      "There are more adjustments on this ingredient than we show — the list below is the latest 200, not the whole history.",
    adjustmentsEmptyTitle: "No adjustments yet",
    adjustmentsEmptyBody:
      "An adjustment is recorded when something is wasted or spoils, or to correct a number entered wrongly. There are none on this ingredient, and that is fine.",
    deltaUp: "Added",
    deltaDown: "Taken off",
    reasonUnknown: "Unrecognised reason ({code})",
  },
  depletionEvents: {
    action: "Which plates used this",
    title: "{name} — what used it",
    subtitle:
      "The plates that drew on this ingredient, each with its quantity and cost.",
    note: "The report totals stay exactly as they are — this is what sits behind them, for when you need to know which plate.",
    emptyTitle: "Nothing has drawn on this ingredient yet",
    emptyBody:
      "No plate in the ledger has been cooked with it. That is a complete answer, not a problem.",
    truncatedNote:
      "There are more events on this ingredient than we show — the list below is the most recent part of the log, so treat any count as a floor.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    /* D140/D141 — the list carries movements back IN too (cook reversals,
       returns to stock), so the total is net: out minus back. */
    pageTotalOne: "This page, net: {cost}, across 1 event",
    pageTotalTwo: "This page, net: {cost}, across 2 events",
    pageTotalFew: "This page, net: {cost}, across {count} events",
    pageTotal: "This page, net: {cost}, across {count} events",
    netNote: "Returns from the kitchen and cook reversals are taken off the net.",
    /* D139 — an event at unknown cost is counted, never summed. */
    unknownCostOne: "1 event at unknown cost is not in the net",
    unknownCostTwo: "2 events at unknown cost are not in the net",
    unknownCostFew: "{count} events at unknown cost are not in the net",
    unknownCost: "{count} events at unknown cost are not in the net",
    billRef: "bill #{sequence}",
  },
  stockMovement: {
    purchase: "Purchase",
    supplierReturn: "Purchase return",
    kitchenIssue: "Kitchen issue",
    remakeWaste: "Remake waste",
    cookReversal: "Cook reversal (amend)",
    returnedToStock: "Returned from kitchen",
    waste: "Waste",
    spoilage: "Spoilage",
    correction: "Correction",
    stocktake: "Stocktake variance",
    directionIn: "In",
    directionOut: "Out",
  },
  stocktake: {
    title: "Stocktake",
    subtitle:
      "Count what is on the shelf and compare it with the books. Stock only changes when you post the count.",
    startTitle: "No count is open",
    startBody:
      "Start a count to record what you find on the shelf for each ingredient. You can carry on from another device; the count stays open until you post or cancel it.",
    start: "Start a count",
    openedBy: "Count no. {number} — started by {name}, {date}",
    openedAt: "Count no. {number} — started {date}",
    startedBy: "Started by {name}, {date}",
    startedAt: "Started {date}",
    resumed: "A count was already open, so here it is. Carry on counting.",
    refresh: "Refresh",
    refreshAria: "Refresh the count to see what another device counted",
    refreshNote: "The count is read again when you open this screen, when you come back to it, and before you post or cancel. Tap Refresh to see what another device has counted just now.",
    reference: "Count no. {number}",
    unitCostLine: "Unit cost: {cost}",
    historyOpen: "View count",
    historyOpenAria: "View count no. {number}",
    viewSubtitle: "What was counted in this count — read only.",
    viewNoLines: "No ingredient was counted in this count.",
    viewCounted: "Counted",
    searchLabel: "Search ingredients",
    searchPlaceholder: "e.g. Tomatoes",
    filterLabel: "Show",
    filterAll: "All",
    filterUncounted: "Not counted",
    filterCounted: "Counted",
    filterVariance: "With a difference",
    sheetLabel: "Count sheet",
    expectedNow: "On the books now",
    expectedAtCount: "On the books when counted",
    countedLabel: "Counted ({unit})",
    countedAria: "Quantity of {name} counted",
    variance: "Difference",
    varianceValue: "Value of difference",
    colIngredient: "Ingredient",
    colBooks: "On the books",
    colCounted: "Counted",
    colValue: "Value",
    booksWhenCounted: "when counted",
    booksNow: "now",
    tileOver: "More on the shelf",
    tileShort: "Missing from the shelf",
    tileLinesNone: "None",
    tileLinesOne: "1 ingredient",
    tileLinesTwo: "2 ingredients",
    tileLinesFew: "{count} ingredients",
    tileLines: "{count} ingredients",
    matches: "Matches",
    notCounted: "Not counted yet",
    saving: "Saving…",
    saved: "Saved",
    unsaved: "Not saved yet — tap outside the field or press Enter",
    invalidCount: "Enter zero or more, with at most four decimal places.",
    clear: "Clear count",
    clearAria: "Clear the count of {name}",
    inactive: "Inactive",
    emptyIngredients: "There are no ingredients yet. Add them on the Stock screen first.",
    noMatch: "No ingredient matches the search or the chosen view.",
    summaryTitle: "Count summary",
    summaryCounted: "Ingredients counted",
    summaryVarianceLines: "Ingredients with a difference",
    summaryValue: "Net value of differences",
    summaryUnknownOne: "1 ingredient's difference is at unknown cost, so it is left out of the value.",
    summaryUnknownTwo: "2 ingredients' differences are at unknown cost, so they are left out of the value.",
    summaryUnknownFew: "{count} ingredients' differences are at unknown cost, so they are left out of the value.",
    summaryUnknown: "{count} ingredients' differences are at unknown cost, so they are left out of the value.",
    summaryPreviewNote:
      "Values use today's average cost and are fixed when you post.",
    post: "Post count",
    postNeedsCount: "Count at least one ingredient before posting.",
    postTitle: "Post this count?",
    postBodyNone:
      "No counted ingredient has a difference, so stock won't change; the count just closes. Posting can't be undone.",
    postBodyOne:
      "The difference on 1 ingredient will be written to stock as a stocktake variance dated today, moving its on-hand by exactly the difference. Posting can't be undone — a mistake is corrected with a new count or an adjustment.",
    postBodyTwo:
      "The differences on 2 ingredients will be written to stock as stocktake variances dated today, moving each on-hand by exactly its difference. Posting can't be undone — a mistake is corrected with a new count or an adjustment.",
    postBodyFew:
      "The differences on {count} ingredients will be written to stock as stocktake variances dated today, moving each on-hand by exactly its difference. Posting can't be undone — a mistake is corrected with a new count or an adjustment.",
    postBody:
      "The differences on {count} ingredients will be written to stock as stocktake variances dated today, moving each on-hand by exactly its difference. Posting can't be undone — a mistake is corrected with a new count or an adjustment.",
    cancel: "Cancel count",
    cancelTitle: "Cancel this count?",
    cancelBody:
      "Nothing is written to stock. What you counted stays in the history, but this count can't be reopened.",
    keepCounting: "Keep counting",
    postedTitle: "Count posted",
    postedBody:
      "The differences were recorded on {date} as stocktake variances. Open an item card to see the movement and the balance after it.",
    postedNoVariance: "There was no difference, so stock did not change.",
    cancelledTitle: "Count cancelled",
    cancelledBody: "Nothing changed in stock.",
    newCount: "Start a new count",
    historyTitle: "Counts in the last 30 days",
    historyEmpty: "No counts in this period.",
    statusOpen: "Open",
    statusPosted: "Posted",
    statusCancelled: "Cancelled",
    historyLinesOne: "1 ingredient counted",
    historyLinesTwo: "2 ingredients counted",
    historyLinesFew: "{count} ingredients counted",
    historyLines: "{count} ingredients counted",
    blindNote:
      "Blind count: the book quantity and the difference are not shown while counting. The difference appears once the count is posted.",
    blindFilterReason: "“With a difference” is available once the count is posted.",
    blindSummary: "The difference appears once the count is posted.",
    postBodyBlind:
      "Each counted ingredient's difference will be written to stock as a stocktake variance dated today, moving its on-hand by exactly the difference; then the differences are shown. Posting can't be undone — a mistake is corrected with a new count or an adjustment.",
    viewBlind: "This count was not posted, so the book quantity and the difference are not shown.",
    settingLabel: "Blind stocktake",
    settingCheckbox: "Hide the book quantity and the difference while counting",
    settingHint:
      "The count sheet shows no book quantity and no difference until the count is posted, so it records what is on the shelf, not what the books say. Applies to counts started after saving.",
  },
  supplierReturn: {
    title: "Purchase return",
    action: "Return to supplier",
    actionAria: "Return part of the {name} delivery to the supplier",
    subtitle: "{name} — delivery of {date}",
    factDelivered: "Delivered",
    factPrice: "Price paid",
    factReturned: "Already returned",
    factLeft: "Can still go back",
    quantityLabel: "Quantity returned ({unit})",
    quantityHint: "Up to {quantity} {unit}",
    quantityRequired:
      "Enter a quantity above zero, with at most four decimal places.",
    quantityTooMuch: "Only {quantity} {unit} of this delivery can still go back.",
    refLabel: "Supplier's credit note number (optional)",
    refHint: "As written on the return note, up to 64 characters.",
    noteLabel: "Note (optional)",
    noteHint: "Why it went back, e.g. damaged on arrival.",
    previewTitle: "What this return does",
    previewValue: "Value returned",
    previewVat: "Its VAT",
    previewStock: "On hand after the return",
    owedLowers: "What you owe {name} goes down by {amount}.",
    owedPaid:
      "This delivery is already paid, so what you owe doesn't change. Get the refund from the supplier directly.",
    owedUntracked:
      "This delivery isn't on a supplier's account, so nothing you owe changes.",
    submit: "Record return",
    done: "Return recorded: {quantity} {unit} worth {amount}.",
    notFound: "We couldn't find this delivery in the ingredient's record. Refresh the page.",
    nothingLeft: "This whole delivery has gone back.",
    returned: "{quantity} {unit} returned",
  },
  itemCard: {
    title: "Item card",
    titleNamed: "Item card — {name}",
    openAction: "Item card",
    subtitle:
      "Every movement of this ingredient in the period, with its quantity and value, and the balance after each one.",
    opening: "Opening balance",
    closing: "Closing balance",
    totals: "Period totals",
    colDate: "Date",
    colMovement: "Movement",
    colDocument: "Document",
    colQtyIn: "Qty in",
    colQtyOut: "Qty out",
    colUnitCost: "Unit cost",
    colValueIn: "Value in",
    colValueOut: "Value out",
    colBalanceQty: "Balance (qty)",
    colBalanceValue: "Balance (value)",
    colNote: "Note",
    valueUnknown: "Cost unknown",
    unknownRowsNote: "Movements at unknown cost (the ingredient had not been purchased yet) count in the quantities, not in the values.",
    liveTitle: "Recorded on hand now",
    liveQtyDifference: "Quantity difference from the closing balance",
    liveValueDifference: "Value difference from the closing balance (an estimate at the current average cost)",
    emptyRange:
      "No movements in this period — the closing balance equals the opening balance.",
    howTitle: "How these figures are worked out",
    howBody: "Values are at cost as recorded, so they include any input VAT recorded on the delivery (the same VAT the input-VAT report takes off). The average cost is weighted and recomputed in the order movements were recorded; unit costs are rounded to four decimal places and each movement's value to the piastre. A delivery dated in the past counts on its own day and changes no earlier value: nothing used before it was recorded is re-costed. Within one day movements follow the order they were entered, so a running balance can dip below zero and recover. A kitchen-issue line gathers the day's plates, and when another movement (a delivery, a count or an adjustment) was recorded between them the line splits around it, so the balance after a count is what was counted.",
    drillTitle: "The plates behind this line",
    drillTruncated: "Only the first 1,000 movements behind this line are listed (a plate cooked again has two); the line's total includes every one.",
    billOfDay: "Bill #{sequence} — {date}",
    stocktakeRef: "Count no. {number}",
    rangeInvalid:
      "Pick a valid period: the start before the end, and no longer than 366 days.",
    printRange: "From {from} to {to}",
    backToStock: "Back to stock",
    unitLine: "Unit: {unit}",
    avgNow: "Average cost now / {unit}: {cost}",
    notFoundTitle: "We couldn't find this ingredient",
    notFoundBody:
      "It may have been deleted, or the link points at another store's ingredient. Go back to stock and pick it from the list.",
    colQty: "Quantity",
    colValue: "Value",
    colBalance: "Balance",
    colDirection: "Direction",
    colBillDate: "Bill date",
    colSupplier: "Supplier",
    colPlates: "Plates",
    colUnknownRows: "Movements at unknown cost",
    unknownRowsOne: "1 movement at unknown cost",
    unknownRowsTwo: "2 movements at unknown cost",
    unknownRowsFew: "{count} movements at unknown cost",
    unknownRows: "{count} movements at unknown cost",
    platesOne: "1 plate",
    platesTwo: "2 plates",
    platesFew: "{count} plates",
    plates: "{count} plates",
    liveOnHand: "Quantity recorded now",
    liveValue: "Its value at the current average cost",
    liveMatches: "Matches the closing balance",
    liveMatchesQty: "Quantity matches the closing balance; the value is unknown",
    liveNone:
      "The period ends before today, so there is no comparison with the stock recorded now.",
    liveNote: "Shown when the period reaches today: the quantity the system records now beside the closing balance, and the difference in quantity and value. What makes a value difference: each movement rounded to the piastre; movements at unknown cost (left out of the values); and stock below zero, which carries no weight in the average, so the next delivery alone sets it — that can make a visible difference, not just a piastre. A delivery dated in the past does not by itself make one here.",
    totalsNote: "Period totals are gross, not net: in includes purchases and returns from the kitchen, out includes purchase returns — so net purchases are purchases less purchase returns.",
    supplierReturnOf: "Against invoice {ref} — {date}",
    supplierReturnOfDelivery: "Against the delivery of {date}",
    returnSourceVoid: "void",
    returnSourceRefund: "refund",
    freeLot: "Free",
    colOriginalInvoice: "Original invoice",
    colOriginalDate: "Original invoice date",
    colDish: "Dish",
    colReturnSource: "Returned by",
    colQtyInUnit: "Qty in ({unit})",
    colQtyOutUnit: "Qty out ({unit})",
    colBalanceQtyUnit: "Balance ({unit})",
    csvStore: "Restaurant",
    csvIngredient: "Ingredient",
    csvRange: "Period",
    csvExportedAt: "Exported at",
    csvExportedBy: "Exported by",
    csvZone: "{zone} time",
    csvZoneDevice: "this device's clock",
    printPreparedBy: "Prepared by",
    printReviewedBy: "Reviewed by",
    printedBy: "Printed by {name}",
    // Short on purpose (phase 12 rework, QA12-2): the restaurant themes set a
    // table header nowrap + uppercase, and "WHAT HAPPENED TO IT" overran its
    // 132px column, so the 1440 drawer scrolled sideways with the header cut.
    drillColOutcome: "Outcome",
    outcomeSold: "Sold",
    outcomeOpenBill: "Bill not settled yet",
    outcomeCancelledBill: "Bill cancelled",
    outcomeStaffMeal: "Staff meal",
    outcomeComped: "Comped",
    outcomeWrittenOff: "Written off",
    outcomeVoided: "Voided",
    outcomeRefunded: "Refunded",
    drillColBill: "Bill",
    drillColProduct: "Dish",
    drillColQty: "Quantity",
    drillTotal: "Total",
    drillEmpty: "No plates are recorded on this line.",
    printButton: "Print card",
    csvItem: "Item",
    csvFigure: "Figure",
  },
  addonOverrideHistory: {
    action: "Addon price history",
    title: "Addon price history — {name}",
    subtitle:
      "Who changed what this dish charges for an option, from what to what, and when.",
    note: "Every save records all of the group's options, including the ones nobody touched — so a row showing the same price again is not a duplicate.",
    emptyTitle: "This dish has never priced an option for itself",
    emptyBody:
      "It charges the group's prices, like any other dish. That is a complete answer, not a problem.",
    truncatedNote:
      "There are more changes on this dish than we show — the list below is the most recent part of the log, so treat any count as a floor.",
    unknownActor: "A member who has left the team",
    deletedOption: "A deleted option",
    stateInherits: "the group's price",
    stateFree: "free",
    stateWithdrawn: "not offered on this dish",
    changed: "from {from} to {to}",
    noRecordedPrevious: "no earlier value recorded — saved as: {to}",
    loadingOption: "Loading options…",
    // The groups call failed. Still saying "loading" would promise a name
    // that is never coming — the same false claim as calling it deleted.
    unresolvedOption: "Option name unavailable",
    restated: "saved unchanged: {to}",
  },
  availabilityHistory: {
    action: "Availability history",
    title: "{name} availability history",
    subtitle: "Who marked this item out, who put it back, and when.",
    note: "Every press of the availability switch is recorded here, including one that re-confirms the state already in force. That is not a duplicate — the confirmation is itself a decision somebody took.",
    emptyTitle: "No decisions on this item yet",
    emptyBody:
      "Nobody has marked it out or put it back since it was created. That is a complete answer, not a fault.",
    truncatedNote:
      "This item has more decisions than are shown — what is below is the most recent part of the record, a floor rather than the whole of it.",
    unknownActor: "A member since removed from the team",
    wentOff: "Marked out",
    cameBack: "Put back on",
    stillOff: "Confirmed still out",
    stillOn: "Confirmed still available",
    setOff: "Recorded as out",
    setOn: "Recorded as available",
    // ── The same log, for an ingredient (B18) ─────────────────────────────
    // Its own title and subtitle because the claim differs: an 86'd dish
    // cannot be sold, while dishes containing a finished ingredient keep
    // selling and the waiter is only warned.
    ingredientAction: "Finished history",
    ingredientTitle: "{name} — finished history",
    ingredientSubtitle:
      "Who called this ingredient finished, who put it back, and when. Dishes containing it stayed orderable throughout — this is a record of warnings, not of refusals.",
    ingredientEmptyTitle: "No decisions on this ingredient yet",
    ingredientEmptyBody:
      "Nobody has called it finished or put it back since it was recorded. That is a complete answer, not a fault.",
    // The same six decisions in the INGREDIENT's words. Borrowing the dish
    // ones printed "Marked sold out" under a title reading "finished history"
    // — two words for one state on one screen.
    ingredientWentOff: "Called it finished",
    ingredientCameBack: "Put it back",
    ingredientStillOff: "Confirmed still finished",
    ingredientStillOn: "Confirmed available",
    ingredientSetOff: "Recorded as finished",
    ingredientSetOn: "Recorded as available",
  },
  reports: {
    // Shared by the item, staff and daypart reports — the same word the
    // invoice screen uses, so saving a file is one thing in this product.
    exportCsv: "Download CSV",
    title: "Spent vs gained",
    description:
      "Revenue, what the food that sold cost, and the margin between them — over any date range.",
    noAccessTitle: "You can't view this report",
    noAccessBody:
      "This screen needs the reports permission. Ask the restaurant owner to add it to your role — it isn't granted automatically, even to a manager.",
    rangeLabel: "{from} to {to}",
    dateRangeApply: "Apply",
    dateRangeClear: "Last 7 days",
    dateRangePrevious: "Previous month",
    dateRangeNext: "Next month",
    tableScrollCue: "Swipe the table sideways to see the other columns",
    paperPeriod: "Period",
    paperPresetToday: "Today",
    paperPresetYesterday: "Yesterday",
    paperPreset7d: "7 days",
    paperPreset30d: "30 days",
    // P6-Q4 (2026-10-27): the 1st of the current business month to today.
    paperPresetMonth: "This month",
    paperPresetCustom: "Custom range",
    paperCustomRangeLabel: "{from} – {to}",
    paperCustomDialog: "Choose a date range",
    paperCustomHint: "Pick the first day, then the last — or just one day.",
    paperCustomTooLong: "A report covers up to {span}. Pick a shorter range.",
    paperCustomFuture: "A range can't end after today's business day.",
    dateRangeInvalid:
      "That range isn't allowed — 92 days max. Shorten it and try again.",
    loading: "Building the report...",
    emptyTitle: "No data in this range yet",
    emptyBody:
      "The report fills in as invoices are closed or purchases are recorded in the range you picked. Try a different date range.",
    statCostOfSalesSub: "What the food that sold cost",
    statMargin: "Gross margin",
    basesTitle: "Margin basis",
    basesEstimateNote:
      "That estimate is not a measurement: cost of sales is priced at a weighted average that includes purchase VAT, and the share taken back out comes from THIS period's purchases — while the stock that was cooked may have been bought earlier at a different share. Read it as an indication, not a figure.",
    statCostOfSalesNet: "≈ {amount} net of purchase VAT (estimate)",
    statAvgTicket: "Average ticket",
    scopeNote:
      'Business-day scope. The method split covers settled bills only — the rest is shown as "not settled yet".',
    methodCash: "Cash",
    methodCard: "Card",
    methodInstapay: "InstaPay",
    methodUnsettled: "Not settled yet",
    // The base is IN the sentence, never left to be read off the bucket above:
    // that bucket counts bills a platform SETTLED, this counts bills a platform
    // BROUGHT, and reading one against the other says the platforms take 65%.
    commissionNote:
      "Platforms brought {orders} this period and kept {amount}. NOT taken off the revenue above — that is what the diners paid. Per the rate and base on the till; the platform's statement is the fact.",
    commissionEmpty: "No platform orders in this period.",
    // T4 — the input VAT the commission probably carries: a derived figure to
    // confirm against the platform's tax invoice, never a reconciliation.
    commissionVatEstimate: "Estimated input VAT on commission",
    // D93 — the courier's fee comes off the ladder like the service charge: one
    // pays a rider, one pays the floor, and the FOOD money at the bottom is
    // neither. A ladder's rows have to reach the figure under them.
    grossToNetLessDeliveryFee: "less delivery fees",
    grossToNetNet: "Net sales",
    grossToNetNote:
      "Net sales is the store's actual revenue — VAT is collected for the tax authority and the service charge is not the price of the food. This is the figure the gross margin is taken against.",
    refundsNote: "Money refunded to a customer leaves revenue, the service charge and the VAT, and is counted on the day it went back rather than the day of the bill — so the VAT position falls with it. The food's cost stays as it was, because it was recorded when the plate was cooked, so the margin drops — unless the cashier chose \"Returned from kitchen\": then the ingredients go back into stock and their cost comes off the cost of sales on the day of the return.",
    refundsMethodNote:
      "A refund leaves the bucket it physically went back by: cash from cash, card from card, InstaPay from InstaPay — and InstaPay touches neither drawer.",
    consumptionSold: "Cost of sales",
    // B117/C1 — see the Arabic note: this row was missing from the ladder
    // entirely while the printed total included it. Same wording as the Z's own
    // line, so the two money screens name one thing one way.
    consumptionAdjustment: "Stock adjustments",
    // B117/C1 — the costs card's "how is this worked out?" sheet never
    // mentioned adjustments at all, so even when the figure was inside the
    // total nothing on the screen said what it was.
    basisDefAdjustmentCost: "Stock that left — or was found — outside a sale: a waste, spoilage, correction or stocktake-variance movement. Each movement is priced at its cost when it was recorded. Not in the cost of food sold and not in gross margin, but in total cost, because the pantry really lost it. A stocktake variance that finds MORE stock than the books said shows as a negative.",
    // C-r2-5 — the same figure, named for the CSV. "Total" is enough at the
    // foot of the cost ladder on screen; in the period-totals block, between
    // VAT and margin, it does not say a total of what. It is also the exact
    // phrase the adjustment clause in the same file uses ("but in total
    // cost"), so the sentence and the row name one thing.
    consumptionTotalCost: "Total cost",
    tiersTitle: "Revenue by tier",
    tierBase: "Base pricing",
    tiersEmpty: "No invoices in this range yet.",
    // B94 — which platform costs what. One number says the platforms kept X;
    // it does not say which of them is worth keeping.
    platformsTitle: "Platforms: brought vs kept",
    daysTitle: "Daily performance",
    dayDate: "Day",
    dayRevenue: "Revenue",
    dayInvoices: "Invoices",
    daySoldCost: "Cost of sales",
    dayUnpaidCost: "Waste & comped",
    dayPurchases: "Purchases",
    dayCostNoRevenueHint:
      "Cost with no revenue is normal on a day with open tables or a prep day.",
    ingredientsTitle: "Ingredient consumption",
    ingredientName: "Ingredient",
    comparePrevious: "vs previous",
    comparisonRangeNote: "Compared with {from} to {to}",
    deltaUp: "▲ {pct}%",
    deltaDown: "▼ {pct}%",
    deltaFlat: "No change",
    // Unit 2 — the money that is not food (F5, B2, and B46's courier half).
    // Each pair is collected against paid out. Traceable movements, NOT an
    // accrued liability: no running balance and no per-staff figure, and the
    // card says so out loud — a limit nobody states is one a reader discovers
    // by trusting a number that never made the promise.
    nonFoodTitle: "Non-food revenue",
    // T12 — does delivery pay for itself: fees collected against courier pay.
    deliveryTitle: "Delivery: fee vs courier cost",
    // The pair is visible from what was recorded: collected off the bills,
    // paid out off the drawer's movements. Where the house keeps the charge
    // there is nothing to hand over, so a zero is a complete answer rather
    // than an obligation nobody met.
    nonFoodServiceChargeKeptNote:
      "This store is set to keep the service charge, so there is nothing to hand to staff — a zero here is correct.",
    nonFoodServiceChargeNote:
      "This difference is this window's only, not a balance owed to staff: last month's charge paid out this month lands in this month, and a window with no payout at all does not mean the whole charge is still outstanding.",
    // The half of tips the product cannot see, stated so the figure is not
    // read as the period's tips.
    nonFoodTipsNote:
      "Cash tips into the drawer only. A tip added to a card payment reaches neither the drawer nor the bill, so the product cannot see it — which is also why this difference goes negative quite normally when tips taken on cards are handed over in cash.",
    // The cost side only. The revenue side (a delivery fee on the bill) does
    // not exist yet.
    nonFoodDeliveryNote:
      'What couriers were paid in this range — the cost side only. The delivery fees collected on bills are the "less delivery fees" line above; compare the two to see whether delivery pays for itself.',
    vatTitle: "VAT",
    vatOutput: "Output VAT (on sales)",
    vatInput: "Input VAT (on purchases)",
    // F8 (unit 5) — the second input basis, beside the first and not instead
    // of it. The word is "backed by a tax invoice", never "claimable": this
    // describes facts in the ledger, and the tax position is not the screen's
    // to take.
    vatInputSupported: "Of which backed by a tax invoice",
    vatPositionOnSupported: "Position on the backed input only",
    vatPosition: "VAT position (output less input)",
    vatNote:
      "A restatement from this store's own data only — not a filing, and other inputs (rent, utilities) are not in it.",
    vatRefunded: "VAT refunded",
    // F6 — the excluded figure carries "not a filing figure" in the LABEL, not
    // in a tooltip. The risk this decision carries is real only when the other
    // number is hidden; it is not hidden, so it must not be hard to find.
    vatWriteOffExcluded: "VAT on written-off bills (not a filing figure)",
    vatPositionIfCounted:
      "Position if those were counted (not a filing figure)",
    vatWriteOffNote:
      "A written-off bill's VAT is not counted as owed — that is what the position above does. The figures beside it show what it would be if it were. Egyptian VAT is invoice-basis, and cancelling tax on a bill that was never paid normally needs a credit note, which this product does not have. Ask your accountant before filing anything from here.",
    // Unit 3 — the same card when the store has flipped F6's setting. Which
    // pair leads changes, and the "not a filing figure" mark travels with the
    // pair that does NOT lead: it marks the OTHER one, not one fixed figure.
    vatPositionCounted: "VAT position (written-off bills counted)",
    // Not "above" or "below": this row MOVES with the leading basis — counted,
    // it is an addend inside the ladder above the position; excluded, it sits
    // under the rule. The label has to be true in both places.
    vatWriteOffIncluded: "VAT on written-off bills (counted in the position)",
    vatPositionExcludedNotFiling: "Position without them (not a filing figure)",
    vatWriteOffNoteCounted:
      "This store is set to count a written-off bill's VAT as owed — that is what the position above does, and the figure beside it shows what it would be without them. Egyptian VAT is invoice-basis, so this reading is the closer one to the letter of the law; but reclaiming tax on a bill that was never paid normally needs a credit note, which this product does not have. Ask your accountant before filing anything from here.",
    // B30 — money that really went back but is inside none of the figures
    // above, because its bill has already left revenue as a write-off.
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    refundsNotNettedNoteOne:
      "{amount} went back against written-off bills — plates: 1.",
    refundsNotNettedNoteTwo:
      "{amount} went back against written-off bills — plates: 2.",
    refundsNotNettedNoteFew:
      "{amount} went back against written-off bills — plates: {count}.",
    refundsNotNettedNote:
      "{amount} went back against written-off bills — plates: {count}.",
    /* The tail that used to be repeated verbatim inside all four arms. One
       sentence beside the figure, on the same card: it is not extra
       explanation, it is the reason the same money is not removed twice, and
       it has to be read with the number. */
    refundsNotNettedBasis:
      "It is NOT deducted from the figures above: that bill's whole value is already out of revenue, so deducting the refund as well would remove the same money twice. The money did leave the till.",
    vatCopied: "Line copied",
    vatCopyFailed: "Couldn't copy — copy it by hand.",
    purchasesInputVatNote:
      "Of which {amount} is recorded input VAT (where recorded).",
    statCovers: "Covers",
    statAvgSpendPerCover: "Avg spend per cover",
    statAvgSpendPerCoverUnknown:
      "Not available — the cover count is missing on most dine-in bills",
    statCoversFootnote:
      "A cover is the guest count the waiter recorded. Counted on dine-in bills only, before tax, service and delivery, and only once at least 70% of them carry one (10 bills).",
    byChannelTitle: "By channel",
    byChannelColChannel: "Channel",
    byChannelColInvoiceCount: "Invoices",
    byChannelItemsPerBillLabel: "Avg items per invoice",
    csvCoveredOfDineIn: "Covers recorded of dine-in bills",
    // B117/Q5 — what the kitchen cooked twice, and what it cost. The MANAGER's
    // report: remake cost otherwise lives only on the owner's rung.
    remakesTitle: "Remakes and what they cost",
    remakesCountLabel: "Remakes",
    remakesCostLabel: "Waste cost",
    remakesByDish: "Remakes by dish",
    remakesDish: "Dish",
    remakesEmptyTitle: "No remakes in this period",
    remakesEmptyBody:
      "Nothing went back to the kitchen — which is the good outcome.",
    remakesNoAccessTitle: "You can't view this report",
    remakesNoAccessBody:
      "This screen needs the operations reporting permission. Managers and the owner have it — ask the owner to add it to your role.",
    /*
     * C-r2-4 — the basis. This report shipped with no "How this is counted"
     * door and no file, and these four sentences had nowhere else to be. The
     * manager is measured on the figures. The screen, the sheet and the CSV all
     * read the same array (`basisNotes`).
     */
    remakesBasisCount:
      "A plate sent back once is ONE remake, however many ingredients it has or whether a recipe was recorded. Counted per plate and attempt, not per stock row that left the pantry.",
    remakesBasisCost:
      "Recorded ingredients at their cost when consumed. History is never repriced with today's average. A remake without a recipe increases the count with zero recorded cost; that does not mean the food was free.",
    remakesBasisWindowTerm: "Period",
    remakesBasisWindow:
      "Business days, like every other figure in the store. A remake after midnight counts on the business day the order opened in.",
    remakesBasisOpenTerm: "Open bills",
    remakesBasisOpen:
      "Included in these figures. The ingredients left the store when the cook picked them up, so the cost is real even if the bill is still open or was cancelled afterwards.",
    // See the Arabic note: the reason block reads the command log, not the
    // item's own reason column, which holds the last code and overwrites the
    // ones before it.
    remakesBasisReasonTerm: "Reasons",
    remakesBasisReason:
      "From the command log itself — every send-back with the reason given at the time, not the last reason left on the plate. “Legacy reason” is a remake made before a reason was asked for, counted so the reasons add back to the remake total above.",
    remakesByReason: "Remakes by reason",
    remakesReason: "Reason",
    // PAPER (2026-10) — the sales reports' toolbar note: how many business
    // days the range covers.
    salesPaperDaysOne: "1 business day",
    salesPaperDaysTwo: "2 business days",
    salesPaperDaysFew: "{count} business days",
    salesPaperDays: "{count} business days",
    // PAPER parity (2026-10-02) — the toolbar as the artboard draws it.
    paperNoteToday: "Today, from {hour} (business day start)",
    paperNoteTodayPlain: "Today, from the start of the business day",
    paperNoteYesterday: "Yesterday, {hour} → {hour}",
    paperNoteYesterdayPlain: "Yesterday's business day",
    paperNoteLastOne: "Last business day",
    paperNoteLastTwo: "Last 2 business days",
    paperNoteLastFew: "Last {count} business days",
    paperNoteLast: "Last {count} business days",
    paperNoteMonth: "{month} so far",
    paperBasisDoor: "How it's calculated",
    paperExport: "Export",
    // PAPER — the remakes report's tiles.
    remakesPaperDishesOne: "Across 1 dish",
    remakesPaperDishesTwo: "Across 2 dishes",
    remakesPaperDishesFew: "Across {count} dishes",
    remakesPaperDishes: "Across {count} dishes",
    remakesPaperPerRemake: "{amount} a remake on average",
    remakesPaperTopDish: "Costliest dish",
    remakesPaperTopReason: "Costliest reason",
    remakesPaperTopSubOne: "{amount} · 1 remake",
    remakesPaperTopSubTwo: "{amount} · 2 remakes",
    remakesPaperTopSubFew: "{amount} · {count} remakes",
    remakesPaperTopSub: "{amount} · {count} remakes",
    remakesPaperNone: "None",
    reasonLegacy: "Legacy reason (before the taxonomy)",
    dayService: "Service charge",
    dayVat: "VAT",
    dayNet: "Net",
    // ── The reports design round (2026-09-11), second pass ──────────────
    // Nine stacked cards became three, and ~2,400 characters of 12px prose
    // came off the canvas into the one disclosure and the CSV's notes. These
    // are the keys that shape carries.
    /** The three cards. Each title is also a heading in the disclosure sheet
     *  and a line in the CSV notes, so the three cannot drift. */
    cardSales: "Sales and settlement",
    cardCosts: "Costs",
    // The audience seam, named for its CONTENT — not, as it first shipped,
    // for its reader ("For your accountant"). A heading that addresses the
    // person reading it makes the report sound like it is talking to them
    // rather than reporting; the founder rejected it on sight, and no
    // ledger, till or accounting package names a section that way. The seam
    // still works: an owner can tell from "VAT and tax bases" that this
    // card is not the one they came for.
    cardTaxBases: "VAT and tax bases",
    // The same words as every other report's door, so an owner learns one
    // affordance across the family rather than four.
    basisTrigger: "How this is counted",
    basisTitle: "How this report is counted",
    basisTermRefunds: "Refunds",
    basisTermScope: "What's counted",
    basisDefSoldCost:
      "What the food that sold cost, priced at what it cost when it was cooked — never at today's average.",
    basisTermWriteOffVat: "VAT on bills that walked out",
    purchasesTerm: "Stock bought",
    purchasesNote:
      "Counted when you buy, not when you cook — so a delivery day is not a loss.",
    csvPeriodTotals: "Period totals",
    csvUnknownCostRows: "Movements at unknown cost (left out of the costs)",
    daySupplierReturns: "Purchase returns",
    csvSupplierReturnsTotal: "Value of purchase returns",
    csvSupplierReturnsCount: "Purchase returns",
    csvSupplierReturnsVat: "Input VAT taken off by returns",
    basisTermSupplierReturns: "Purchase returns",
    basisDefSupplierReturns:
      "What went back to suppliers in the period, on the day it went back. It is not taken off purchases, which stay every delivery you received as recorded. Its VAT is taken off input VAT, so input VAT here is net of returns.",
    purchasesWithoutVatCount: "Purchases with no VAT figure",
    purchasesWithoutTaxInvoiceCount:
      "Purchases whose VAT has no tax invoice behind it",
    /** The closed status vocabulary. Four words, inert, on the figure — never
     *  a sentence and never a colour. A figure with none of them is measured,
     *  and that absence is the encoding. */
    markEstimate: "Estimate",
    markFloor: "Minimum",
    /** The in-page nav. Heading navigation already serves a screen reader;
     *  this exists for the sighted keyboard reader and at 400% zoom. */
    jumpTo: "On this page",
    /** The floor mark, ON the input-VAT row. Not "incomplete" and not a
     *  sentence: the count is what makes it a floor, so the count is in the
     *  mark. */
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    markFloorWithCountOne: "Minimum — 1 purchase with no VAT",
    markFloorWithCountTwo: "Minimum — 2 purchases with no VAT",
    markFloorWithCountFew: "Minimum — {count} purchases with no VAT",
    markFloorWithCount: "Minimum — {count} purchases with no VAT",
    /** F8's mark, on the backed-input row. The count is what explains the gap
     *  between the two figures, so the count is inside the mark. */
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    markUndocumentedWithCountOne: "1 purchase with no tax invoice",
    markUndocumentedWithCountTwo: "2 purchases with no tax invoice",
    markUndocumentedWithCountFew: "{count} purchases with no tax invoice",
    markUndocumentedWithCount: "{count} purchases with no tax invoice",
  },
  /*
   * PAPER's money colour key (founder, 2026-10-03), shared by every PAPER
   * money page through `components/paperTones.tsx`.
   */
  paperTones: {
    legend: "Colour key",
    neg: "Taken off",
    info: "Added on top (VAT, service, tips)",
    pos: "Earned / reduces what you owe",
    warn: "Owed or spent",
  },
  /*
   * PAPER "Sales & costs" (2026-10-03) — the redesign of the spend-gain report
   * (`ReportsPage` under PAPER, `components/PaperSpendGain.tsx`). The other
   * looks keep `reports.*`; figures and their rules are unchanged.
   */
  reportSales: {
    title: "Sales & costs",
    subtitle: "Takings, food cost, margin and VAT for the days you pick.",
    compare: "Compare",
    compareThis: "This period",
    comparePrevious: "vs previous period",
    tileTakings: "Takings",
    tileTakingsSubOne: "1 bill · {avg} average",
    tileTakingsSubTwo: "2 bills · {avg} average",
    tileTakingsSubFew: "{count} bills · {avg} average",
    tileTakingsSub: "{count} bills · {avg} average",
    tileCost: "Cost of sales",
    tileCostSub: "{pct} of net sales",
    tileCostSubOpen: "{pct} of net sales · {pctOpen} without bills still open",
    tileCostSubOnlyOpen: "{amount} of it on bills still open",
    // Audit round 2 (2.3): the line under the cost tile to Item profitability —
    // its figures are that report's `bridge` for the same range, never summed here.
    // Rework (R-1): each figure is an EFFECT on the difference, signed as the
    // ladder signs it ("+255.66", "−8.04", a zero bare) — never "−8.04 returned".
    costBridgeLine:
      "Cost in Item profitability: {cost}. Difference: {open} from food cooked for bills still open, {earlier} from plates sold before the range and returned from kitchen.",
    costBridgeLink: "Item profitability for the same range",
    costBridgeTerm: "Dish cost in Item profitability",
    // Codex review of PR #240: the file only — the two reports were read at
    // different moments even after a fresh read, so no line with figures
    // that do not reach the cost of sales is written.
    costBridgeUnreached:
      "Not tied to the cost of sales in this file: the two reports were read at different moments.",
    // Review of PR #240 (2026-10-16): the items report itself could not be
    // read at export — no line, and not silence either: the file says so.
    costBridgeUnread:
      "Not tied to the cost of sales in this file: the Item profitability report could not be read.",
    tileMargin: "Margin",
    tileMarginSubHouse:
      "Net sales and service kept, minus the food cost of what sold",
    tileMarginSub: "Net sales minus the food cost of what sold",
    tileAvg: "Average ticket",
    tileAvgSub: "Net, per bill · {items} items per bill",
    tileAvgSubPlain: "Net, per bill",
    deltaUp: "↑ {pct}% vs previous",
    deltaDown: "↓ {pct}% vs previous",
    deltaFlat: "No change vs previous",
    deltaNone: "— no comparison",
    chartTitle: "Takings and food cost, by day",
    chartSub: "The gap between the two bars is what the food earned.",
    chartOneDay: "Pick 7 or 30 days to see the shape over time.",
    chartTakings: "Takings",
    chartCost: "Food cost of what sold",
    chartTip: "{date} · takings {revenue} · food cost {cost}",
    chartTipEmpty: "{date} · no bills · food cost {cost}",
    salesTitle: "Sales",
    salesSub: "From net sales to what guests paid",
    lineNet: "Net sales",
    lineService: "Service charge",
    lineVat: "VAT",
    lineDelivery: "Delivery fees",
    lineDeliveryNote: "Charged to guests on delivery bills; not food",
    // D146 — disclosed above the ladder; already out of net sales below.
    lineBillDiscount: "Bill discounts ({count})",
    lineBillDiscountNote: "Already out of net sales below — not taken off again",
    lineTakings: "Takings",
    lineRefunds: "Refunds",
    lineRefundsNoteOne: "1 plate · already out of takings",
    lineRefundsNoteTwo: "2 plates · already out of takings",
    lineRefundsNoteFew: "{count} plates · already out of takings",
    lineRefundsNote: "{count} plates · already out of takings",
    paidTitle: "How guests paid",
    methodCash: "Cash",
    methodCard: "Card",
    methodInstapay: "InstaPay",
    methodPlatforms: "Delivery platforms",
    methodUnsettled: "Not settled yet",
    methodUnsettledNote: "Closed bills not paid yet",
    commission: "Platform commission",
    commissionNote:
      "On {amount} of platform orders — per the rate and base on the till; the platform's statement is the fact",
    lineCommissionVat: "Est. input VAT on commission",
    lineCommissionVatNote:
      "Estimate — not a filing figure; confirm against the platform's tax invoice",
    walkedOutOne: "1 bill walked out · {amount} written off",
    walkedOutTwo: "2 bills walked out · {amount} written off",
    walkedOutFew: "{count} bills walked out · {amount} written off",
    walkedOut: "{count} bills walked out · {amount} written off",
    seeThem: "→ see them",
    costsTitle: "Costs",
    costsSub: "Everything the kitchen used, by why it left",
    consSold: "Sold",
    consSoldNote: "Food cooked for bills being charged",
    consSoldOpenNote: "{amount} of it on bills still open",
    consComped: "Comped",
    consStaff: "Staff meals",
    consVoided: "Voided after cooking",
    consVoidedNote: "Waste",
    consRemake: "Remakes",
    consRemakeNote: "Cooked twice",
    consWalked: "Walked out",
    consWalkedNote: "Written-off bills",
    consAdjust: "Stock adjustments",
    consAdjustNote: "A count that finds stock is negative",
    totalCost: "Total cost",
    compReasons: "Comped food by reason:",
    purchasesSection: "Purchases",
    linePurchases: "Purchases",
    linePurchasesNoteOne: "1 supplier delivery · {vat} input VAT",
    linePurchasesNoteTwo: "2 supplier deliveries · {vat} input VAT",
    linePurchasesNoteFew: "{count} supplier deliveries · {vat} input VAT",
    linePurchasesNote: "{count} supplier deliveries · {vat} input VAT",
    lineNoVat: "Without VAT recorded",
    lineNoTaxInvoice: "No tax invoice from the supplier",
    marginTitle: "Margin",
    marginSubThree: "The same margin on three bases",
    marginSubTwo: "The same margin on two bases",
    lineMargin: "Margin",
    lineMarginHouse: "service kept by the house",
    lineMarginNetOnly: "On net sales only",
    lineMarginNetted: "Cost without input VAT",
    lineMarginAfterCommission: "After platform commission",
    marginSubFour: "The same margin on four bases",
    markEstimate: "Estimate",
    vatTitle: "VAT position",
    copy: "Copy",
    copied: "Copied",
    lineOutputVat: "Output VAT",
    lineOutputVatNote: "Charged on sales, net of {amount} refunded",
    lineOutputVatNotePlain: "Charged on sales",
    lineInputVat: "Input VAT",
    lineInputVatNote: "On supplier invoices you recorded",
    lineSupplierReturns: "Purchase returns",
    lineSupplierReturnsNoteOne: "1 return · {vat} input VAT taken off",
    lineSupplierReturnsNoteTwo: "2 returns · {vat} input VAT taken off",
    lineSupplierReturnsNoteFew: "{count} returns · {vat} input VAT taken off",
    lineSupplierReturnsNote: "{count} returns · {vat} input VAT taken off",
    lineInputVatNetNote: "On supplier invoices you recorded, net of {amount} on returns",
    lineInputVatNegative: "Input VAT (net negative: {amount})",
    lineInputVatNegativeNote: "Returns ({amount}) exceed the VAT on this period's purchases ({vat}), so the net is negative and is added to what you owe instead of taken off it.",
    colSupplierReturns: "Returns",
    linePosition: "VAT position",
    linePositionCounted: "VAT position, walk-outs counted",
    linePositionNote: "What you owe the tax office for this period",
    lineSupported: "Input VAT with a full tax invoice",
    lineSupportedPosition: "Position on that basis",
    markNotFiling: "Not a filing figure",
    vatFoot:
      "Whether input VAT can be deducted is for your accountant. This card shows what your records say.",
    nonFoodTitle: "Money that isn’t food",
    nonFoodSub: "Collected for staff or for delivery, and what went back out",
    lineSvcCollected: "Service charge collected",
    lineSvcPaid: "Service charge paid to staff",
    lineSvcKept: "This store keeps the service charge",
    lineNotPaidYet: "{amount} collected and not paid out yet",
    lineOverPaid: "{amount} more paid out than collected",
    lineTipsCollected: "Tips collected",
    lineTipsPaid: "Tips paid out",
    lineTipsNote: "Cash tips into the drawer only",
    lineDeliveryCost: "Delivery cost",
    lineDeliveryCostNote: "Paid to drivers from the drawer",
    // T12 — the delivery card: fees collected against courier pay.
    deliveryTitle: "Delivery",
    deliverySub: "Fees collected against what couriers were paid",
    lineDeliveryFees: "Delivery fees collected",
    lineDeliveryFeesNote: "Net of refunds, before VAT",
    lineDeliveryCouriers: "Paid to couriers",
    lineDeliveryDifference: "Difference",
    guestsTitle: "Guests",
    guestsSub:
      "Dine-in only. Bills where nobody asked are left out, never guessed.",
    guestsLine: "Guests",
    guestsBills: "Bills with a guest count",
    guestsBillsValueOne: "{covered} of 1 dine-in bill",
    guestsBillsValueTwo: "{covered} of 2 dine-in bills",
    guestsBillsValueFew: "{covered} of {bills} dine-in bills",
    guestsBillsValue: "{covered} of {bills} dine-in bills",
    guestsSpend: "Spend per guest",
    guestsSpendNote: "Net dine-in sales on counted bills ÷ guests",
    guestsNone: "No dine-in bills in this period",
    tiersTitle: "Price tiers",
    tiersSub: "Net sales at each price list",
    tierBillsOne: "1 bill",
    tierBillsTwo: "2 bills",
    tierBillsFew: "{count} bills",
    tierBills: "{count} bills",
    colTier: "Price tier",
    tierTableTitle: "Takings and net sales by price tier",
    platformsTitle: "Delivery platforms",
    platformsSub: "What each platform billed and kept",
    platformSubOne: "1 order · {pct}% commission",
    platformSubTwo: "2 orders · {pct}% commission",
    platformSubFew: "{count} orders · {pct}% commission",
    platformSub: "{count} orders · {pct}% commission",
    leftTitle: "Left out of sales",
    leftSub: "Bills that closed but earned nothing",
    leftWalkedOne: "1 bill walked out",
    leftWalkedTwo: "2 bills walked out",
    leftWalkedFew: "{count} bills walked out",
    leftWalked: "{count} bills walked out",
    leftWalkedNote:
      "Settled as write-offs. Their food cost is counted; their sales are not.",
    leftCompedOne: "1 bill comped in full",
    leftCompedTwo: "2 bills comped in full",
    leftCompedFew: "{count} bills comped in full",
    leftComped: "{count} bills comped in full",
    leftCompedNote:
      "Closed at zero. Left out of the bill count and the average ticket.",
    channelTitle: "By channel",
    channelSub: "Average ticket is net, per bill",
    colChannel: "Channel",
    colBills: "Bills",
    colNet: "Net sales",
    colAvg: "Avg ticket",
    colItems: "Items / bill",
    colCommission: "Commission",
    colNetAfterCommission: "After commission",
    colGuests: "Guests",
    guestsCellOne: "1 guest on {covered} of {bills} bills",
    guestsCellTwo: "2 guests on {covered} of {bills} bills",
    guestsCellFew: "{count} guests on {covered} of {bills} bills",
    guestsCell: "{count} guests on {covered} of {bills} bills",
    total: "Total",
    daysTitle: "Day by day",
    daysSub: "Each row adds up: net + service + VAT = takings.",
    daysSubDelivery:
      "Each row adds up: net + service + VAT + delivery fees = takings.",
    colDay: "Day",
    colTakings: "Takings",
    colService: "Service",
    colVat: "VAT",
    colNetShort: "Net",
    colFoodCost: "Food cost",
    colGivenAway: "Comps & waste",
    colPurchases: "Purchases",
    colDelivery: "Delivery fees",
    ingTitle: "Ingredients used",
    ingSub: "Most expensive first, at the cost on the day used",
    colIngredient: "Ingredient",
    colUsed: "Used",
    colBought: "Bought",
    colCost: "Cost",
    colShare: "Share of food cost",
    moreTitle: "More from this report",
    moreSub:
      "Takings by price tier, stock on hand and what each ingredient was bought for",
    stockTitle: "Stock and unit cost",
    colBoughtCost: "Bought for",
    colOnHand: "On hand",
    colAvgCost: "Average unit cost",
    /* D139 — movements at unknown cost: out of the totals, and counted. */
    unknownCostRowsOne:
      "1 movement at unknown cost (an ingredient not yet purchased at the time) is left out of these costs, so they read low.",
    unknownCostRowsTwo:
      "2 movements at unknown cost (an ingredient not yet purchased at the time) are left out of these costs, so they read low.",
    unknownCostRowsFew:
      "{count} movements at unknown cost (an ingredient not yet purchased at the time) are left out of these costs, so they read low.",
    unknownCostRows:
      "{count} movements at unknown cost (an ingredient not yet purchased at the time) are left out of these costs, so they read low.",
    /* D140/D141 — a return to stock comes off the cost on its own day. */
    negativeCostNote: "A cost can show below zero: what came back from the kitchen into stock or had its cook reversed comes off the cost on its own day, so a period where more came back than was cooked nets negative.",
    pagerLabel: "{name} pages",
    pagerRange: "{first}–{last} of {total}",
    pagerRows: "Rows",
    pagerPrevious: "Previous page",
    pagerNext: "Next page",
  },
  reportItems: {
    title: "Item profitability",
    description:
      "Units sold, net revenue, and realised cost off the depletion ledger — per dish, over any date range.",
    noAccessTitle: "You can't view this report",
    noAccessBody:
      "This screen needs the reports permission. Ask the restaurant owner to add it to your role.",
    loading: "Building the report...",
    emptyTitle: "No data in this range yet",
    emptyBody:
      "The report fills in as invoices are settled in the range you picked. Try a different date range.",
    basisLine:
      'Revenue is net of service charge and VAT (same basis as the main report). Cost is the realised depletion-ledger cost at cook time — not the current recipe — and is GROSS of purchase VAT, so the margin here reads a little lower than the true one. In the option breakdown each row takes the WHOLE plate: a "Large" row also carries the extras sold on those plates, not the price of the size alone.',
    thresholdsLine:
      'Quadrants are drawn from: more than {units} units sold = "popular", and a unit contribution above {contribution} = "high contribution". A dish sold fewer than {minUnits} times in this range is left out of the quadrant.',
    quadrantBelowThreshold:
      "Sold fewer than {minUnits} times — too small a sample to judge",
    refundNote: "When a plate is refunded and its food was wasted, its revenue leaves the dish but its cost and the unit stay — the cost is what the plate consumed when it was cooked, so the dish's margin drops. When the plate went back into stock (\"Returned from kitchen\") it is a sales return: the money and the ingredients both came back, so it is not a sale and leaves the units sold; the count shows beside them (\"returned to stock\"). A refund counts in the period the money went back.",
    colItem: "Item",
    colUnits: "Units sold",
    returnedToStockLabel: "Returned to stock",
    colRevenue: "Net revenue",
    colCost: "Realised cost",
    colContributionUnit: "Contribution / unit",
    colContributionTotal: "Total contribution",
    colMarginPct: "Margin %",
    colQuadrant: "Quadrant",
    costUnknown: "Unknown",
    /* D139 — plates cooked with an ingredient not yet purchased: not in the cost. */
    costUnknownPlatesOne: "1 plate at unknown cost",
    costUnknownPlatesTwo: "2 plates at unknown cost",
    costUnknownPlatesFew: "{count} plates at unknown cost",
    costUnknownPlates: "{count} plates at unknown cost",
    costUnknownPlatesNote:
      "The costs shown leave out plates at unknown cost (an ingredient not yet purchased when they were cooked), so they read low for those dishes.",
    quadrantRelativeNote:
      "The classification compares dishes with each other over this range; it is not a verdict on whether a dish makes money. At least one dish here is above the average AND losing money on its own margin — when a range carries a lot of refunds, the average itself can be negative.",
    quadrantStar: "Star",
    quadrantPlowhorse: "Plowhorse",
    quadrantPuzzle: "Puzzle",
    quadrantDog: "Dog",
    quadrantStarHint:
      "Sells a lot and earns a high margin — your best dish. Protect its quality and price.",
    quadrantPlowhorseHint:
      "Sells a lot but earns a low margin. Try a small price rise or a cheaper recipe.",
    quadrantPuzzleHint:
      "High margin, but few people order it. Promote it: have waiters suggest it, or give it a better spot on the menu.",
    quadrantDogHint:
      "Neither sells much nor earns much. Consider taking it off the menu or reworking it.",
    highlighted: "Selected item",
    breakdownTitle: "Broken down by option",
    colLevel: "Row type",
    levelItem: "Item",
    levelOption: "Option",
    colOption: "Option",
    colOptionGroup: "Option group",
    colOptionUnits: "Option units",
    colOptionRevenue: "Option net revenue",
    colOptionCost: "Option realised cost",
    colOptionContributionUnit: "Option contribution / unit",
    colOptionContributionTotal: "Option total contribution",
    colOptionMarginPct: "Option margin %",
    colCostUnknownPlates: "Plates at unknown cost (not in the cost)",
    variantUnattributed: "No option",
    breakdownNote:
      'These sum back to the item\'s row above. The "No option" row holds the plates sold without a choice from this group — the group was attached partway through the range, or its type was changed, or it is a version the guest could decline. Those plates carry units, revenue and cost like any other.',
    // ── The reports pattern, applied (2026-09-11) ───────────────────────
    tableTitle: "Items ranked by contribution",
    basisTrigger: "How this is counted",
    basisTitle: "How this report is counted",
    statTopContributor: "Top contributor",
    statTopContributorSub: "{amount} from {units} sold",
    statLosing: "Loss-making",
    statCostedAll: "Every dish has a recipe cost",
    statNoCosted: "No dish has a recipe cost yet",
    statNoSample: "No dish has enough sales yet",
    /** The floor mark: an uncosted dish is in none of the money figures on
     *  this page, so they are all floors until the menu is costed. */
    markFloor: "Minimum — {count} not costed",
    // ── PAPER (2026-10): the answer tiles, menu engineering, the table ──────
    paperFoodCost: "Food cost of sales",
    paperFoodCostSub: "{cost} on {revenue} of net revenue",
    paperThinnest: "Thinnest margin",
    paperThinnestSub: "{pct} margin · costs {cost} a plate",
    paperUncosted: "No recipe cost",
    paperQuadTitle: "Menu engineering",
    paperQuadSub:
      "Each dish against the rest of the menu: how often it sells, and what a plate earns.",
    paperRuleStar: "Popular, high margin",
    paperRulePlowhorse: "Popular, low margin",
    paperRulePuzzle: "High margin, rarely ordered",
    paperRuleDog: "Low sales, low margin",
    paperAdviceStar: "Keep it visible and consistent. Don't discount it.",
    paperAdvicePlowhorse:
      "Trim the recipe cost, or nudge the price up a little.",
    paperAdvicePuzzle: "Move it up the menu, or have staff recommend it.",
    paperAdviceDog: "Rework it, reprice it, or take it off the menu.",
    paperQuadEmpty: "No dish in this group",
    paperMoreOne: "1 more",
    paperMoreTwo: "2 more",
    paperMoreFew: "{count} more",
    paperMore: "{count} more",
    paperFilterAll: "All",
    paperFilterAria: "Show dishes by group",
    paperCostPerPlate: "costs {cost} a plate",
    paperCostPlateTerm: "Cost a plate",
    paperBasisFoodCost:
      "Realised cost divided by net revenue, over the dishes that have a cost. A dish with no recipe cost is left out of both figures, so the share is not pulled down by food nobody costed.",
    paperBasisThinnest:
      "The lowest margin among dishes with enough sales to be classified; a dish sold once or twice is not counted here.",
    paperBasisPerPlate:
      "Realised cost divided by units sold — the same basis the contribution per unit is taken on.",
    paperBasisUncosted:
      "A dish with no recipe cost has no margin, so it is in none of the four groups and none of the profit figures.",
    // PAPER (2026-10-02) — the top bar's line, as the artboard words it.
    paperSubtitle:
      "Which dishes earn you money, and what to do about the rest.",
    // The dish losing money, when there is one — the other looks' answer.
    paperLosingSub: "{amount} from {units} sold",
    // ── Audit round 2 (2.2): the reconciliation with Sales & costs ────────
    // Every rung is a SERVER figure (`bridge`); the page adds nothing up.
    bridgeTitle: "Reconciliation with Sales & costs",
    bridgeSub: "From this report's figures to Sales & costs for the same range.",
    bridgeRevenueTitle: "Net sales",
    bridgeCostTitle: "Cost of sales",
    bridgeItemsRevenue: "Items' net revenue in this report",
    bridgePriorRefunds: "Refunds of plates sold before this range",
    bridgeFeeRefunds: "Delivery-fee refunds",
    bridgeDisclosedMark: "Disclosed",
    bridgeFeeRefundsNoteNone: "No delivery-fee refunds in this range.",
    bridgeFeeRefundsNoteOne:
      "{gross} refunded in 1 refund (fee and its VAT); outside net sales, so disclosed, not subtracted.",
    bridgeFeeRefundsNoteTwo:
      "{gross} refunded in 2 refunds (fees and their VAT); outside net sales, so disclosed, not subtracted.",
    bridgeFeeRefundsNoteFew:
      "{gross} refunded in {count} refunds (fees and their VAT); outside net sales, so disclosed, not subtracted.",
    bridgeFeeRefundsNote:
      "{gross} refunded in {count} refunds (fees and their VAT); outside net sales, so disclosed, not subtracted.",
    bridgeSpendGainNet: "Net sales in Sales & costs",
    bridgeItemsCost: "Items' realised cost in this report",
    bridgeOpenBills: "Food cooked for bills still open",
    bridgeGiveBacks: "Returned from kitchen, plates sold before this range",
    bridgeSpendGainSoldCost: "Cost of sales in Sales & costs",
    bridgeDisclosures: "Disclosures",
    bridgeUncosted: "Dishes with no recipe cost",
    bridgeUncostedNote: "Their cost is unknown, so it is in neither figure above.",
    bridgeReturnedNoteNone: "No plate went back to stock in this range.",
    bridgeReturnedNoteOne:
      "1 plate; its value is inside both figures above, so it moves no difference.",
    bridgeReturnedNoteTwo:
      "2 plates; their value is inside both figures above, so it moves no difference.",
    bridgeReturnedNoteFew:
      "{count} plates; their value is inside both figures above, so it moves no difference.",
    bridgeReturnedNote:
      "{count} plates; their value is inside both figures above, so it moves no difference.",
    // Behind the "How it's calculated" door: each rung's definition, its figure first.
    bridgeDefItemsRevenue:
      "The sum of the Net revenue column: what the plates of the range's billed invoices earned after service, VAT and any discount at the bill, less what was refunded of them.",
    bridgeDefPriorRefunds:
      "Money refunded in this range for plates sold before it. Sales & costs takes it off net sales on the day the money went back; no dish here carries it, because its sale is outside the range.",
    bridgeDefFeeRefunds:
      "The effect of delivery-fee refunds on net sales. Net sales never include the delivery fee or its VAT, so refunding them does not lower it; the amount refunded ({gross}) is disclosed, not subtracted.",
    bridgeDefSpendGainNet:
      "Net sales in Sales & costs for the same range: the items' net revenue, less refunds of plates sold before the range, less the effect of delivery-fee refunds.",
    bridgeDefItemsCost:
      "The sum of the Realised cost column, over the costed dishes. Each plate's cost is its own share of the same cost of sales, rounded the same way, so the two meet to the piastre.",
    bridgeDefOpenBills:
      "Food cooked for bills still open: in cost of sales from the moment it is cooked, and on no dish here until its bill is settled. The same figure Sales & costs gives as \"on bills still open\".",
    bridgeDefGiveBacks:
      "Ingredients back in stock in this range from plates sold before it: they lower cost of sales on their own day and touch no dish here.",
    bridgeDefSpendGainSoldCost:
      "Cost of sales in Sales & costs for the same range: the items' realised cost, plus food on bills still open, plus kitchen returns of plates sold before the range.",
    bridgeDefUncosted:
      "How many dishes have no recipe cost. Their cost is unknown (no recipe, or an ingredient never bought), so it is in neither figure, and there is no amount to state for it.",
    bridgeDefReturned:
      "Plates refunded with their food returned to stock in the range, and the value of what came back. The value is taken off both the items' realised cost and cost of sales, so it moves no difference between them.",
  },
  reportRefunds: {
    title: "Refunds",
    description:
      "Every refund in the period: when, what came back and why, how the money went back, and what needs a look.",
    noAccessTitle: "You can't view this report",
    noAccessBody:
      "This screen needs the operations reports or the reports permission. Ask the restaurant owner to add it to your role.",
    emptyTitle: "No refunds in this period",
    emptyBody: "No money went back to a guest on the selected days.",
    tileRefunded: "Refunded",
    tileRefundedSubOne: "1 refund",
    tileRefundedSubTwo: "2 refunds",
    tileRefundedSubFew: "{count} refunds",
    tileRefundedSub: "{count} refunds",
    tileShare: "Share of sales",
    sharePct: "{pct}%",
    tileShareSub: "Of {amount} net sales",
    tileShareNone: "No sales in the period",
    tileLate: "Late refunds",
    tileLateSub: "Limit now: {time} after paying",
    tileOtherWay: "Paid back another way",
    tileOtherWaySub: "The bill's method was corrected after the refund",
    moneyTitle: "Refund money",
    methodsTitle: "Refunds by method",
    methodsEmpty: "No money went back in this period.",
    insideTitle: "Refunded total, by component",
    insideFood: "Food and drinks",
    insideVat: "VAT given back",
    insideService: "Service charge given back",
    insideFee: "Delivery fees given back",
    tableTitle: "Refund list",
    filterAria: "Filter refunds by flag",
    filterAll: "All",
    flagLate: "Late",
    flagMethodDiverged: "Different method",
    flagClosedShift: "Closed drawer",
    flagOtherActor: "Another person",
    colInvoice: "Invoice",
    colAmount: "Amount",
    invoiceRef: "#{sequence}",
    afterPaying: "{time} after paying",
    paidTimeUnknown: "Payment time not recorded",
    plateQuantity: "{quantity}× {name}",
    deliveryFee: "Delivery fee",
    methodsLine: "{paid} → {method}",
    refundedBy: "by {name}",
    unknownActor: "Unknown",
    noFlags: "No flags",
    filterEmpty: "No refunds carry this flag.",
    afterPayingTerm: "After paying",
    basisRefunded:
      "Money handed back to guests, VAT, service charge and delivery fees included, counted on the business day the money went back — not the day of the bill. It is the same figure the spent-vs-gained report takes off.",
    basisShare:
      "The food-and-drinks part of the refunds, over the period's net sales before refunds — no VAT, service charge or delivery fee on either side. A refund of an earlier bill counts here, against this period's sales.",
    basisMethods:
      "The way the money physically went back: cash leaves the drawer open at the time, a card refund moves no cash, and a transfer goes back from the bank.",
    basisInside:
      "Food, VAT, service charge and delivery fees given back add up exactly to the refunded total; each refund carries its own share, worked out when it was made.",
    basisLate:
      "Recorded at the moment of the refund, against the limit set then (Settings → Cash & approvals). Changing the limit later does not re-mark earlier refunds.",
    basisMethodDiverged:
      "The bill's payment method was corrected after the refund, so the refund names a method the bill no longer does. The refund keeps the method the money really left by. A flag, not an error.",
    basisClosedShift:
      "The drawer the bill was paid into had already been closed when the money went back. The refund itself comes out of the drawer open at the time; the closed drawer's count is never changed.",
    basisOtherActor:
      "The person who refunded is not the person who took the payment. Only the person who refunded is named.",
    basisAfterPaying:
      "Minutes from the bill's payment to the refund, rounded down. The strip runs to three times the current limit; the shaded part is past it.",
    csvBusinessDate: "Business day",
    csvRefundedAt: "Refunded at",
    csvInvoice: "Invoice",
    csvInvoiceDate: "Bill's business day",
    csvPlace: "Place",
    csvItems: "Items",
    csvFeeRefunded: "Delivery fee refunded",
    csvReason: "Reason",
    csvNote: "Note",
    csvPaidBy: "Paid by",
    csvRefundMethod: "Refund method",
    csvRefundedBy: "Refunded by",
    csvMinutesAfter: "Minutes after paying",
    csvPeriodTotal: "Period total",
    // PAPER (founder, 2026-10-02): the redesign's own words. Only under PAPER.
    paperSubtitle:
      "Money given back after payment, and the ones worth a second look.",
    paperMethodsTitle: "Paid back by",
    // The figure follows in its own run, 6px after the dot.
    paperMethodLegend: "{method} ·",
    paperInsideTitle: "Inside the refunded total",
    paperListTitle: "Refunds",
    paperColWhen: "When",
    paperColWhat: "What and why",
    paperColMethods: "Paid → back",
    paperColFlags: "Needs a look",
  },
  reportStaff: {
    title: "Voids, comps & refunds by staff",
    description:
      "Each void, comp and refund's count and money, per actor, split by reason, against the actor's own sales — over any date range.",
    noAccessTitle: "You can't view this report",
    noAccessBody:
      "This screen needs the reports permission. Ask the restaurant owner to add it to your role.",
    loading: "Building the report...",
    emptyTitle: "No voids, comps or refunds in this range yet",
    emptyBody:
      "The report fills in as staff void or comp an item, or refund a bill, in the range you picked.",
    // B8 — the ratio compares voids/comps (already net) against the actor's
    // NET sales, not gross — comparing net to a gross-of-VAT-and-service
    // figure understated every ratio by roughly a quarter.
    basisLine:
      "Each actor's sales are the NET total of the plates they themselves added (service charge and VAT excluded). A plate with no actor of its own — one added before we recorded that, or a diner's own QR request — counts towards whoever opened the bill.",
    // Every money column here is on ONE basis — voids, comps and walk-outs
    // alike — because they are summed into a single numerator over a net
    // denominator. And a written-off bill is not a sale, so it leaves the
    // denominator while staying in the numerator: the ratio can pass 100 %,
    // and a reader who is not told that reads a real figure as a bug.
    basisLineMoney:
      "Every amount here is net of service charge and VAT, and a written-off bill is not counted in that actor's sales — so the ratio can exceed 100%.",
    truncatedNote:
      "This range holds more void/comp actions than shown — the figures below are a floor, not a full count.",
    colActor: "Staff member",
    colVoids: "Voids",
    colComps: "Comps",
    compsOfWhichStaffMeals: "{amount} of it staff meals",
    // B7 — the two exception types that move real money and were missing
    // from this report: bills that walked out unpaid, and settlement
    // corrections filed on this actor's bills.
    colWriteOffs: "Walk-outs",
    // A walk-out found after its own day is written off on the day it was
    // found, so the loss lands there. Saying which is what stops a reader
    // taking an old bill for a table that walked out this week.
    lateWriteOffs: "{count} from earlier days",
    colSwaps: "Swaps",
    flaggedSwapCostHeader: "Free swap cost",
    swapGiveAwayHeader: "Give-away value",
    swapGiveAwayLabel: "given away",
    flaggedSwapCostNetHeader: "Free swap cost (net)",
    swapsFreeSuffix: "{count} free",
    // D109: where the give-away came from — the pair, not the person.
    /* Named for SWAPS explicitly, and the column is "Not recovered" rather
       than "Given away": the StatCard at the top of this same page is already
       called "Given away" and counts something else entirely (voids + comps +
       walk-outs). Two different quantities under one label on one screen is
       the defect this report has been fixed for twice. */
    giveAwaySourceTitle: "Where the swap give-away came from",
    giveAwayColPair: "Swap",
    giveAwayColSpread: "Times",
    giveAwayColCharged: "Charged",
    giveAwayColGiveAway: "Not recovered",
    giveAwayActors: "{count} staff",
    giveAwayActorsOne: "1 person",
    giveAwayActorsTwo: "2 staff",
    giveAwayActorsFew: "{count} staff",
    giveAwayOfCost: "of {cost} cost",
    /* B23: handed back. Beside "Charged", never netted into it. */
    giveAwayRefunded: "{amount} refunded on {count} plates",
    giveAwayRefundedOne: "{amount} refunded on 1 plate",
    giveAwayRefundedTwo: "{amount} refunded on 2 plates",
    giveAwayRefundedFew: "{amount} refunded on {count} plates",
    giveAwaySourceBasis:
      "The same give-away money as the table above, grouped by the swap itself instead of by the person who made it. That is what separates the two causes, which look identical in a single figure: your swap price does not cover its cost — in which case the same swap shows up under everyone, every night, and the person named above did nothing — or somebody is working that gap, in which case it shows up under one person. The staff count is what tells you which.",
    /* The same block in the EXPORT (D109). The four headers above are reused
       so the file and the screen say the same words; these five are the
       figures the screen states as a sub-line under a number, and each one
       needs its own column, because a cell that renders two figures must
       never export as one. */
    csvGiveAwayStaff: "Staff on this swap",
    csvGiveAwayCost: "Cost of these swaps",
    csvGiveAwayUnpriced: "Free swaps (no sell price)",
    csvGiveAwayRefundedCount: "Plates since refunded",
    csvGiveAwayRefundedValue: "Charge handed back",

    // A HEADER, so it carries no count — the cell's own suffix does.
    colSwapsFree: "Free swaps",
    swapsBasisLine:
      "The Swaps column is attributed to whoever ADDED the plate. A plate with no actor of its own — one added before we recorded that, or a diner's own QR request — counts towards whoever opened the bill.",
    // B23 — a refunded plate stays CHARGED on purpose (it is what keeps the
    // plate sum equal to the bill), so a swap surcharge that was handed back
    // still read as money the house took. The figure now sits beside it.
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    swapRefundedLabelOne: "1 refunded:",
    swapRefundedLabelTwo: "2 refunded:",
    swapRefundedLabelFew: "{count} refunded:",
    swapRefundedLabel: "{count} refunded:",
    swapsRefundBasisLine:
      "“Refunded” in the swaps column means the plate has come back BY NOW — including where the money went back in a later period than the one shown.",
    csvRatioWithRefunds: "Ratio + refunds (%)",
    csvSwapsRefundedCount: "Swaps on refunded plates",
    csvSwapsRefundedValue: "Swap charge refunded",
    colCorrections: "Payment corrections",
    // F19 (founder, 2026-09-13) — WHO applied a price tier, and what it gave
    // away.
    //
    // The last concession in the product with no name against it. Applying a
    // tier is `openInvoice`'s payload under `RESTAURANT_INVOICE_OPEN`, the
    // lowest rung there is, so a waiter can charge a walk-in the full price in
    // cash, ring the bill on the mall rate and keep the difference. Every other
    // give-away on this report names its actor; this one named nobody.
    //
    // Attributed to the bill's OPENER, which is exact rather than approximate:
    // the tier is chosen at open and can never be changed afterwards.
    colDiscounts: "Tier discounts",
    // D146 — per the person who APPLIED it, beside the comps.
    colBillDiscounts: "Bill discounts",
    billDiscountAfterCheck: "After a pre-bill check ({count})",
    // Rather than a UUID, when the tier has been deleted along with its name.
    unnamedTier: "deleted tier",
    csvDiscountValue: "TierDiscountValue",
    csvDiscountCount: "BillsOnATier",
    csvDiscountTiers: "Tiers",
    // R12 — refunds are their own kind beside the voids and the comps, never
    // folded into them and never into the ratio: that ratio weighs what an
    // actor gives away, and a refund reverses a sale the customer paid for.
    // Whether it belongs in the same quotient is B22, the founder's.
    colRefunds: "Refunds",
    // Refund money comes off the money ledger exactly as it left the till —
    // service charge and VAT included — while every other money column here
    // is net. Said, not hidden.
    refundsBasis:
      "Refund amounts are gross (service charge and VAT included, exactly what the customer got back), unlike the net columns beside them, and they are not in the ratio. They are counted in full even when the rest of the report is truncated.",
    // Taps and plates are different numbers — one refund can hand back six
    // plates — so both are stated and neither stands in for the other.
    // QA3 — one line carrying two independent counts, and `countedKey` picks
    // one key. Split into two, each counting its own noun.
    refundTimesNone: "No refunds",
    refundTimesOne: "Refunds: 1",
    refundTimesTwo: "Refunds: 2",
    refundTimesFew: "Refunds: {count}",
    refundTimes: "Refunds: {count}",
    refundPlatesOne: "Plates: 1",
    refundPlatesTwo: "Plates: 2",
    refundPlatesFew: "Plates: {plates}",
    refundPlates: "Plates: {plates}",
    lateRefunds: "{count} past the allowed window",
    // D75 — the two derived markers, counted per person. Each answers its own
    // question: who hands money back against drawers that were already counted
    // and signed, and who hands back money somebody else took in. Counts, not
    // a score, and never a refusal.
    closedShiftRefunds: "{count} against a closed drawer",
    otherActorRefunds: "{count} on another person's takings",
    csvRefundClosedShift: "Refunds against a closed drawer",
    csvRefundOtherActor: "Refunds on another person's takings",
    // A CSV has no basis line to read, so the header itself carries the
    // basis for the one column whose money is not net.
    csvRefundValueGross: "Refunds (gross)",
    csvRefundCount: "Refund count",
    csvRefundPlates: "Refunded plates",
    csvRefundLate: "Late refunds",
    csvRefundReasons: "Refund reasons",
    countSuffix: "Invoices: {count}",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    countSuffixNone: "No invoices",
    countSuffixOne: "Invoices: 1",
    countSuffixTwo: "Invoices: 2",
    countSuffixFew: "Invoices: {count}",
    colOwnSales: "Net sales",
    colRatio: "Ratio",
    // The sub-line under the ratio, in the same cell. Shown only where the two
    // quotients actually differ — printing the same number twice is noise.
    ratioWithRefundsLabel: "+ refunds {pct}%",
    // The second numerator mixes bases — net give-away money, gross refund
    // money — so it reads slightly high in a charging store. Said, not hidden.
    ratioBasisLine:
      "“Ratio” is voids + comps + write-offs over net own sales. “Ratio + refunds” is the same quotient with money handed back added to the numerator — that term is gross (service charge and VAT included) while the rest is net, so it reads slightly high in a store that charges either.",
    ratioUnknown: "—",
    unknownActor: "Unknown staff member",
    drillLink: "View invoices",
    // ── The reports pattern, applied (2026-09-11) ───────────────────────
    tableTitle: "Exceptions by staff member",
    basisTrigger: "How this is counted",
    basisTitle: "How this report is counted",
    basisTermAttribution: "Who a plate counts against",
    basisTermMoney: "The money figures",
    basisTermSwapRefunds: "Refunded swaps",
    statGivenAway: "Given away",
    statGivenAwaySub: "Across {count} staff",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    statGivenAwaySubOne: "Across 1 staff member",
    statGivenAwaySubTwo: "Across 2 staff",
    statGivenAwaySubFew: "Across {count} staff",
    statPlatesSub: "{plates} plates",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    statPlatesSubOne: "1 plate",
    statPlatesSubTwo: "2 plates",
    statPlatesSubFew: "{plates} plates",
    /** No name in this tile, on purpose — see the comment at the call site. */
    statHighestRatio: "Highest give-away",
    statHighestRatioValue: "{pct}% of own sales",
    statHighestRatioSub: "See the table below",
    markFloor: "Minimum — list truncated",
    // ── PAPER (2026-10): the tiles, the staff table, the reasons ────────────
    paperTopReason: "Most common reason",
    paperTopReasonNone: "No reason recorded",
    paperRemakesSub: "{amount} in waste cost",
    paperShareNote:
      "Share is measured against each person's own net sales. Above {pct}% is flagged.",
    paperShareHigh: "high",
    paperReasonsTitle: "Voids and comps by reason",
    paperReasonsAll: "All",
    paperReasonsAria: "Show reasons by kind",
    paperReasonsEmpty: "No void or comp in this selection.",
    paperReasonsTotal: "Total",
    paperClearStaff: "Show every staff member",
    paperBasisShareFlag:
      "A person's share is flagged when it is above {pct}% of their own sales. It is a reading line, not a rule the product enforces.",
    paperBasisReasons:
      "Void and comp reasons are summed from the same table: plates and net value per reason, ordered by value. The most common reason is the one on the most plates, so it can differ from the top of the list. Choosing a person in the table shows only their reasons.",
    // PAPER (2026-10-02) — the top bar's line, as the artboard words it.
    paperSubtitle: "Money given away at the till, by person and by reason.",
    paperReasonsSub: "Plates and money per reason, this period.",
    // Fix round (2026-10-03) — the void and comp counts, on the Given-away tile.
    paperVoidCount: "{count} voids",
    paperVoidCountOne: "1 void",
    paperVoidCountTwo: "2 voids",
    paperVoidCountFew: "{count} voids",
    paperCompCount: "{count} comps",
    paperCompCountOne: "1 comp",
    paperCompCountTwo: "2 comps",
    paperCompCountFew: "{count} comps",
    csvVoidCount: "Void count",
    csvVoidPlates: "Voided plates",
    csvVoidReasons: "Void reasons",
    csvCompCount: "Comp count",
    csvCompPlates: "Comped plates",
    csvCompReasons: "Comp reasons",
    csvBillDiscountValue: "Bill discounts (net)",
    csvBillDiscountCount: "Bill discount count",
    csvBillDiscountAfterCheck: "Bill discounts after a pre-bill check",
    csvBillDiscountReasons: "Bill discount reasons",
  },
  // B124/D114 — service speed: the time between each stage and the next. No money, no names.
  reportSpeed: {
    title: "Service speed",
    description: "Where the time goes, from ordering a dish to the table.",
    noAccessTitle: "You can't view this report",
    noAccessBody:
      "This screen needs the operations reports permission. Ask the restaurant owner to add it to your role.",
    emptyTitle: "No plates in this period",
    emptyBody:
      "The report fills in once a bill whose plates reached the kitchen is closed.",
    tileWait: "Guest's wait for food",
    tileWaitSub: "1 in 10 waited over {duration}",
    tileLate: "Plates over target",
    tileLateSub: "{late} of {judged}",
    tileLateSubStation: "{late} of {judged} · most at {station}",
    tileHour: "Slowest hour",
    tileMedianSub: "Median {duration}",
    tileTimed: "Plates timed",
    tileTimedSub: "{measured} of {eligible}",
    notEnough: "Too few plates timed",
    notTimed: "Not timed",
    // A dish cell under the timed-plate minimum: no figure to judge by.
    belowMinimum: "Under the minimum",
    // A stage that never happened in the period (no guest requests, say):
    // nothing to time, and never "0 of 0".
    noneInPeriod: "None in this period",
    noStation: "No station",
    stagesTitle: "Service stages",
    plateStagesBlock: "Kitchen to table",
    floorStagesBlock: "Floor and payment",
    stageCol: "Stage",
    medianCol: "Median",
    p90Col: "90th percentile",
    timedCol: "Timed",
    timedOf: "{measured} of {eligible}",
    lateCol: "Over target",
    lateOf: "{late} of {judged}",
    // A stage timed, but only on bills from before the targets: nothing to judge by.
    notJudged: "No target",
    stationLateCol: "Over target to table",
    dishCookLateCol: "Over cooking time",
    stageToTable: "Order to table",
    stageKitchen: "Order to ready",
    stageQueue: "Waiting to start",
    stageCook: "Cooking",
    stagePass: "Ready to table",
    stageFirstOrder: "Seated to first order",
    stageQrAccept: "Guest request acceptance",
    stagePayment: "Check to payment",
    stageProofReview: "Payment proof review",
    excludedRemade: "Remade plates, not timed",
    excludedClosed: "Plates closed with the bill",
    hoursTitle: "By hour",
    hourCol: "Hour",
    platesCol: "Plates",
    hourMedianCol: "Median to table",
    hoursEmpty: "No plate was timed to the table in this period.",
    stationsDishesTitle: "Stations and dishes",
    stationsBlock: "Stations",
    dishesBlock: "Dishes",
    stationCol: "Station",
    dishCol: "Dish",
    toTableCol: "To table",
    kitchenCol: "To ready",
    dishesBelow: "Dishes under the timed-plate minimum",
    dishesEmpty: "No dish reached the timed-plate minimum in this period.",
    durSeconds: "{seconds}s",
    durMinutesSeconds: "{minutes}m {seconds}s",
    durMinutes: "{minutes} min",
    durHoursMinutes: "{hours}h {minutes}m",
    csvMedianSeconds: "Median (seconds)",
    csvP90Seconds: "90th percentile (seconds)",
    csvTimed: "Timed",
    csvEligible: "Eligible",
    csvKitchenSeconds: "To ready, median (seconds)",
    csvToTableSeconds: "To table, median (seconds)",
    csvQueueSeconds: "Waiting to start, median (seconds)",
    csvCookSeconds: "Cooking, median (seconds)",
    csvJudged: "Judged against a target",
    csvLate: "Over target",
    csvToTableJudged: "To table, judged against a target",
    csvToTableLate: "To table, over target",
    csvCookJudged: "Cooking, judged against a target",
    csvCookLate: "Cooking, over target",
    basisMedianTerm: "Median",
    basisMedian:
      "Half the plates took this long or less. Not an average, because one plate forgotten for hours drags an average and does not move the median.",
    basisP90Term: "90th percentile",
    basisP90:
      "Nine plates in ten took this long or less, and the tenth waited longer. These are the long waits a guest remembers.",
    basisTimedTerm: "Timed",
    basisTimed:
      "A stage is timed only where both moments were recorded, and the second came after the first. Nothing missing is assumed, which is why each stage shows how many were timed out of how many it applies to.",
    basisStartTerm: "Starting a plate",
    basisStart:
      "Waiting to start and cooking are timed only when a member of staff pressed “Start preparing” on the kitchen screen or “Start” at the till. In a kitchen that prints its tickets, or with no kitchen set up, the system starts the plate itself, so there is no wait to time.",
    basisCloseTerm: "Closing a bill",
    basisClose:
      "A plate the close finished without anyone serving it is not timed to the table, because the moment of the close is not the moment it was served.",
    basisRemakeTerm: "Remakes",
    basisRemake:
      "A remade plate is out of every stage: a remake sends it back to the kitchen and clears its times, so only the second cook would be left. The count is of plates, not remakes, so a plate remade twice counts once here; the remakes report counts every remake.",
    basisTargetTerm: "Targets",
    basisTarget:
      "A plate is over target when its stage took longer than the time set in the service timing settings, or than the dish's own preparing time where it has one. Each bill keeps the targets it opened with, and each plate its preparing time from when it was sent to the kitchen, so changing a target never re-judges earlier bills; bills from before the targets existed are timed but not judged. Order to ready is judged against waiting plus preparing, and order to table against all three. The kitchen screen marks a late plate by the same rule.",
    basisWindowTerm: "Period",
    basisWindow:
      "Closed or settled bills on the selected business days, written-off ones included, because this report describes operations, not takings.",
    basisFloorTerm: "Floor stages",
    basisFloor:
      "Seated to first order covers dine-in bills only, from opening the bill to the first send to the kitchen. Check to payment runs from the last check printed to the settle.",
    basisHourTerm: "Hour",
    basisHour:
      "The hour the plate was sent to the kitchen, on the restaurant's clock, not the device's.",
    basisMinimumTerm: "Minimum",
    basisMinimum:
      "No dish is listed, and no station or hour is named slowest, before its timed plates reach the minimum, which is {min}: two slow plates are not enough to judge by.",
  },
  feedback: {
    title: "Guest feedback",
    description: "The ratings and notes guests left after paying from the table app.",
    noAccessTitle: "You can't view this page",
    noAccessBody:
      "This screen needs the operations reports permission. Ask the restaurant owner to add it to your role.",
    emptyTitle: "No feedback in this period",
    emptyBody:
      "Guests' feedback appears here once they rate their visit from the table app when they pay.",
    tileAverage: "Average rating",
    tileAverageValue: "{value} out of 5",
    tileCount: "Ratings",
    tileFromOne: "From 1 rating",
    tileFromTwo: "From 2 ratings",
    tileFromFew: "From {count} ratings",
    tileFrom: "From {count} ratings",
    tileLow: "Low ratings",
    tileLowSub: "Rated 1 or 2 out of 5",
    tileTopChip: "What guests liked most",
    tileNone: "Nothing yet",
    tileChipTimesOne: "Once",
    tileChipTimesTwo: "Twice",
    tileChipTimesFew: "{count} times",
    tileChipTimes: "{count} times",
    chipsTitle: "What guests liked",
    ratingsTitle: "How guests rated",
    ratingStars: "{rating} out of 5",
    listTitle: "All feedback",
    listSub: "Newest first",
    colDate: "Date",
    colTable: "Table",
    colBill: "Bill",
    colRating: "Rating",
    colChips: "Liked",
    colNote: "Note",
    noTable: "No table",
    noNote: "No note",
    billNumber: "#{sequence}",
    chipTastyFood: "Tasty food",
    chipFastService: "Fast service",
    chipFriendlyStaff: "Friendly staff",
    chipGreatValue: "Great value",
    chipCleanTable: "Clean table",
  },
  reportDayparts: {
    title: "Dayparts",
    // Five words. The old line ("Revenue by hour and weekday, and each table's
    // turn time — over any date range") named the first section verbatim and
    // ended by describing the date picker sitting beside it.
    description: "Peak hours and table turn time.",
    // PAPER (2026-10-02) — the top bar's line, as the artboard words it.
    paperSubtitle: "When you are busy, so you can staff and prep for it.",
    noAccessTitle: "You can't view this report",
    noAccessBody:
      "This screen needs the reports permission. Ask the restaurant owner to add it to your role.",
    loading: "Building the report...",
    emptyTitle: "No settled invoices in this range yet",
    emptyBody: "Pick another date range, or check back after service.",
    /*
     * The basis, behind one door.
     *
     * This used to be `basisLine` — four clauses, ~45 words, set at 10.5px
     * directly under the page title, which is the most valuable strip on the
     * screen and the one place nobody reads. The facts are not optional
     * (revenue here is GROSS while every sibling report is net, and the day is
     * the BUSINESS day), so they are not cut: they are one tap away, and the
     * same strings are appended to the CSV so the file states its own basis.
     * One disclosure per report, the same label on all four, so an owner
     * learns one affordance rather than four.
     */
    basisTrigger: "How this is counted",
    basisTitle: "How this report is counted",
    basisTermRevenue: "Revenue",
    basisDefRevenue:
      "Gross — service charge and VAT included. The other reports show net, so the same week reads higher here.",
    basisTermHour: "Hour and day",
    basisDefHour:
      "When the bill opened, in the store's own time. A bill that ran past midnight counts on the business day it opened in.",
    basisTermTurn: "Turn time",
    basisDefTurn:
      "Close minus open. Only bills carrying both times are counted, and the count is shown beside each average.",
    // The one word that stays on the canvas. It is the fact that makes this
    // screen disagree with the spend-gain report, and a number that disagrees
    // with another screen unexplained is worse than any amount of clutter.
    basisGross: "Gross",
    statPeakHour: "Busiest hour",
    statTotalSub: "{count} bills",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    statTotalSubOne: "1 bill",
    statTotalSubTwo: "2 bills",
    statTotalSubFew: "{count} bills",
    // Not "…and day": the seven labelled rows already say weekday, and the
    // longer title was ellipsised on a phone by the card that holds it.
    gridTitle: "Revenue by hour",
    // How the whole chart announces itself — one sentence, before the seven
    // rows, so a screen reader is not left to infer a shape from 168 figures.
    chartSummary:
      "One row per day of the week, hours {from} to {to} in the store's own time. Each bar is that hour's gross revenue. Busiest: {peak}.",
    // Per ROW, because a single label on a seven-row chart gives a reader no
    // way to reach one day. `{peak}` is already a weekday-and-hour phrase.
    chartRowLabel: "{weekday}: busiest {hour}, {revenue}. Day total {total}.",
    chartRowEmpty: "{weekday}: no bills.",
    peaksTitle: "Busiest hours",
    peakRow: "{revenue} · {covers}",
    dayTotal: "Day total",
    tableCaption:
      "Gross revenue and bill count per hour of each business day, in the store's own time.",
    colHour: "Hour",
    gridHourLabel: "Hour {hour}",
    // The bill count left this string because the covers phrase now states it
    // — "8 covers on 2 of 9 bills" — so no figure was lost, and the one that
    // needed a denominator finally has it beside it.
    gridCellLabel: "{revenue}, {covers}",
    gridCellEmpty: "No bills",
    /*
     * B117/Q6 — covers, and how many bills they are over.
     *
     * A bill nobody counted is excluded rather than added as zero, so the
     * figure is over a subset. Without this phrase "1,917.16 · 8 covers" over
     * nine bills divides to 240 a head where the truth is 78.36.
     */
    // QA3 — three counts in one sentence; the noun agrees with the last one,
    // which is the shape English already had and Arabic now mirrors.
    // QA4 — "covers" is a counted noun too and read "1 covers". It is built
    // beside the sentence for the reason the Arabic note gives: one sentence,
    // three counts, and `countedKey` picks one key.
    coversOverOne: "{covers} on {covered} of 1 dine-in bill",
    coversOverTwo: "{covers} on {covered} of 2 dine-in bills",
    coversOverFew: "{covers} on {covered} of {bills} dine-in bills",
    coversOver: "{covers} on {covered} of {bills} dine-in bills",
    coversWordOne: "1 cover",
    coversWordTwo: "2 covers",
    coversWordFew: "{covers} covers",
    coversWord: "{covers} covers",
    // "0 covers" reads as "nobody came"; this reads as "nobody counted".
    coversNoneOne: "No covers recorded on 1 bill",
    coversNoneTwo: "No covers recorded on 2 bills",
    coversNoneFew: "No covers recorded on {bills} bills",
    coversNone: "No covers recorded on {bills} bills",
    daypartsTitle: "By daypart",
    daypartBreakfast: "Breakfast",
    daypartLunch: "Lunch",
    daypartDinner: "Dinner",
    daypartLate: "Late night",
    colShare: "Share",
    // Its own revenue label, not `reportItems.colRevenue` — that one reads
    // "Net revenue" and every figure on this page is GROSS. Borrowing it put
    // the one word this page exists to qualify at the head of a column and in
    // the CSV header, saying the opposite of the truth.
    colRevenue: "Revenue",
    turnTitle: "Table turn time",
    colTable: "Table",
    colAvgTurn: "Avg turn",
    // Was "Invoices counted" — the caveat behind the word "counted" is in the
    // disclosure now, where it can be a sentence instead of a participle.
    colTurnCount: "Bills measured",
    minutesShort: "{minutes}m",
    noTurnData: "—",
    // "—" announces as nothing at all, so the dash carries a word beside it
    // that only a screen reader hears.
    noTurnDataLabel: "No closed bills",
    colHours: "Hours",
    // PAPER (2026-10) — the redesign's composition. No "spend per guest":
    // revenue is over every bill and covers over the counted ones only.
    paperPeriodNote: "Business days start at {time}",
    paperPeakHourSub: "{revenue} gross · quietest {time}",
    paperGrossAmount: "{revenue} gross",
    paperStatBusiestDay: "Busiest day",
    paperTurnLongest: "Longest at {name}: {minutes}",
    paperStatCovers: "Covers",
    paperCoverageSub: "Counted on {covered} of {bills} dine-in bills ({pct}%)",
    paperCoverageSubOne: "Counted on {covered} of 1 dine-in bill ({pct}%)",
    paperCoverageSubTwo: "Counted on {covered} of 2 dine-in bills ({pct}%)",
    paperCoverageSubFew:
      "Counted on {covered} of {bills} dine-in bills ({pct}%)",
    paperNoDineIn: "No dine-in bills in this period",
    paperShareGross: "{pct}% of the period, gross",
    paperViewChart: "Chart",
    paperViewTable: "Table",
    paperTurnAverage: "Average across all tables: {minutes}",
    paperColVsAverage: "vs average",
    paperTurnSlower: "{minutes} slower",
    paperTurnFaster: "{minutes} faster",
    paperTurnAbout: "About average",
    // Fix round (2026-10-03) — what the other looks state and PAPER had lost.
    paperPeriodTotal: "{revenue} collected in range",
    paperTurnMedian: "median {minutes}",
    paperTurnSlowest: "slowest: table {name}, {minutes}",
  },
  /**
   * Suppliers (B45, unit 5) — who a delivery was bought from.
   *
   * Not purchase ordering: nothing here orders anything or is owed anything.
   * A row is a name and, where there is one, a tax registration — the third
   * fact an input-VAT claim rests on, which the ledger had nowhere to put.
   */
  aggregators: {
    title: "Delivery platforms",
    description:
      "Talabat, elmenus and the rest — and what each takes off an order. The commission comes out before the money reaches you.",
    noAccessTitle: "You can't see delivery platforms",
    noAccessBody:
      "This screen needs the settings permission to change rates, or a reports permission to see their totals. Ask the restaurant owner to add one to your role.",
    // P6-Q5, option A: a floor manager sees the screen and the totals, read-only.
    readOnlyNote:
      "You can see the platforms and their totals. Adding a platform, changing its rate or retiring it needs the settings permission.",
    add: "New platform",
    editTitle: "Edit platform",
    emptyTitle: "No platforms yet",
    emptyBody:
      "Add Talabat, elmenus or whichever platform you work with and set its rate — then you can pick it when you open a delivery bill.",
    fieldName: "Platform name",
    fieldCommission: "Commission %",
    invoiceCountNone: "No bills on it yet",
    invoiceCountOne: "1 bill on it",
    // English has no dual, so this reads the same as the general case. The key
    // exists because ARABIC has one and the call site is shared.
    invoiceCountTwo: "2 bills on it",
    invoiceCount: "{count} bills on it",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    invoiceCountFew: "{count} bills on it",
    retiredChip: "Retired",
    retire: "Retire platform",
    restore: "Restore platform",
    retired: "Retired — it won't appear in the picker",
    restored: "Restored",
    created: "Platform added",
    saved: "Saved",
    // B118/QA5 — Arabic declines at 1, 2 and 3–10. See `plural.ts`.
    countSummaryOne: "1 active of {total}",
    countSummaryTwo: "2 active of {total}",
    countSummaryFew: "{active} active of {total}",
    countSummary: "{active} active of {total}",
    // PAPER (2026-10 redesign) — the platforms screen: three facts over the
    // cards, and one card per platform.
    factCommissionTitle: "Commission comes out first",
    factCommissionBody:
      "A {amount} order at {percent}% reaches you as {kept}. Reports show what you keep.",
    factPayoutTitle: "Payouts skip the drawer",
    factPayoutBody:
      "Platforms pay days later by transfer, so their orders never change a cash count.",
    factRateTitle: "Rates only move forward",
    factRateBody:
      "Changing a rate applies to new bills. Past bills keep the rate they were opened at.",
    listTitle: "Platforms",
    rateShort: "{percent}% commission",
    // T15 — what the rate is taken on. One sentence per base.
    fieldBase: "Commission is taken on",
    baseFood: "Food only",
    baseFoodHint:
      "The rate applies to the dishes and their VAT only — the platform keeps the delivery fee, so it stays outside the commission.",
    baseTotal: "Whole order value",
    baseTotalHint:
      "The rate applies to everything the customer paid, delivery fee and its VAT included.",
    baseLine: "on {base}",
    statusActive: "Active",
    // P6b (P6-Q2): the figures are for a period the manager picks, read only
    // when asked — on Sales & costs' basis, so they match it for the same days.
    statBilled: "Billed",
    statCommission: "Commission",
    statEffective: "{rate}% effective",
    keepShare: "You keep {pct}%",
    keptToYou: "{amount} to you",
    totalsShow: "Show totals",
    totalsHide: "Hide totals",
    totalsRegion: "Platform totals",
    allTimeChip: "All time",
    // The method behind the reports' «How this is counted» door (report-pages.md).
    basisBilled:
      "The total of every closed or settled bill a platform brought on the business days picked, as stored on the bill — the same figures as Sales & costs for the same days.",
    basisCommission:
      "The commission stored on each bill at the rate it was opened at; changing the rate later does not move it.",
    basisWriteOffTerm: "Unpaid write-offs",
    basisWriteOff:
      "A bill recorded unpaid within these days leaves both figures; one recorded unpaid after them stays in — the reports' rule.",
    basisTitle: "How these figures are counted",
    basisCountTerm: "Bills on a platform",
    basisCount:
      "Every bill that names the platform, any status, all time — not just the days picked. It is why a platform is retired rather than deleted.",
    totalsLoading: "Adding up the bills…",
    totalsAll: "All platforms: {billed} billed · {commission} commission",
    totalsNone: "No platform bills on these days.",
    totalsCardNone: "No bills on it these days",
    totalsPeriod: "In this period",
    totalsTooLong: "Totals cover up to {span}. Pick a shorter range.",
    // Review of #310: without the store's business day the totals are not asked for.
    totalsDayUnknown:
      "Couldn't read the restaurant's business day, so the totals have not been added up.",
    editRate: "Edit rate",
    previewKeep: "On a {amount} order you keep {kept}.",
    previewForward:
      "The new rate applies from the next bill; past bills keep their old rate.",
    // PAPER (pixel parity, 2026-10-02) — the redesign's own words: the top
    // bar's line, the add button, the count, and the card and window buttons.
    paperSubtitle: "Talabat, elmenus and others: commission and what you keep.",
    paperAdd: "Add platform",
    // Two counted halves joined by " · ", each with the four Arabic forms —
    // English reads the same at every count. See `plural.ts`.
    paperCountActiveOne: "1 active",
    paperCountActiveTwo: "2 active",
    paperCountActiveFew: "{count} active",
    paperCountActive: "{count} active",
    paperCountRetiredOne: "1 retired",
    paperCountRetiredTwo: "2 retired",
    paperCountRetiredFew: "{count} retired",
    paperCountRetired: "{count} retired",
    paperRetire: "Retire",
    paperBringBack: "Bring back",
    paperAddTitle: "Add a delivery platform",
    paperEditTitle: "Edit {name}",
    paperNamePlaceholder: "e.g. Talabat",
    paperFieldCommission: "Commission",
  },
  suppliers: {
    title: "Suppliers",
    description:
      "Who you buy from, and their tax registration where they have one.",
    noAccessTitle: "You can't see suppliers",
    noAccessBody:
      "This screen needs the costing-management permission. Ask the restaurant owner to add it to your role.",
    add: "New supplier",
    editTitle: "Edit supplier",
    searchPlaceholder: "Search by name or tax registration",
    filterLabel: "Filter suppliers",
    filterAll: "All",
    filterActive: "Active",
    filterRetired: "Retired",
    emptyTitle: "No suppliers recorded yet",
    emptyBody:
      "Add one and you can name it when you record a delivery — and the input-VAT report can tell which purchases have a tax invoice behind them.",
    noMatchTitle: "No supplier by that name",
    noMatchBody: "Try another name, or clear the filter.",
    // Retired, not deleted: this row is the only record of who a tax invoice
    // came from, so it leaves the picker rather than the database.
    retire: "Retire supplier",
    restore: "Bring back",
    retiredChip: "Retired",
    retired: "Retired — this supplier is no longer offered on a delivery.",
    restored: "Supplier is back.",
    created: "Supplier added.",
    saved: "Saved.",
    taxRegistrationLabel: "Tax registration",
    // The absence is an answer, not a blank: most of an Egyptian restaurant's
    // purchases come from suppliers who have none, and those purchases carry
    // no tax invoice to support them.
    noTaxRegistration: "No tax registration",
    /* Plurals. There is no pluralisation in this i18n runtime, and two keys
       are cheaper than adding one: "1 deliveries recorded" reads as a bug on
       the row an owner is deciding whether to retire. */
    purchaseCountOne: "1 delivery recorded",
    purchaseCountNone: "No deliveries recorded yet",
    purchaseCountTwo: "2 deliveries recorded",
    purchaseCountFew: "{count} deliveries recorded",
    purchaseCount: "{count} deliveries recorded",
    // B118/QA5 — Arabic declines at 1, 2 and 3–10. See `plural.ts`.
    countSummaryOne: "1 active of {total}",
    countSummaryTwo: "2 active of {total}",
    countSummaryFew: "{active} active of {total}",
    countSummary: "{active} active of {total}",
    fieldName: "Supplier name",
    fieldTaxRegistration: "Tax registration number",
    fieldTaxRegistrationHint:
      "The supplier's own number, not the store's. Optional — without it their purchases have no tax invoice behind them.",
    fieldPhone: "Phone",
    fieldNotes: "Notes",
    // PAPER (2026-10) — the sub-lines under the two supplier tiles.
    paperRetireNote: "Retired, never deleted",
    paperAllRegistered: "Every supplier has a tax registration",
    // PAPER (founder, 2026-10-02) — the redesign's sentence under the title,
    // and its words on the add button.
    paperSubtitle: "Who you buy from, what they bring and what you owe them.",
    paperAdd: "Add supplier",
    paperAddTitle: "Add a supplier",
    paperAllPaidUp: "All paid up",
  },
  /**
   * Minimal purchasing (founder decision, 2026-10-02): orders, what you owe
   * suppliers, and the photo of a supplier's invoice.
   *
   * An "order" is what was asked of a supplier and has not arrived; a
   * "delivery" is what arrived and was recorded. An order moves no stock and no
   * cost — only the delivery does — and the copy says so wherever both appear.
   */
  deliveries: {
    title: "Receive a delivery",
    description: "The supplier's paper with all its lines at once — paid from the drawer or on account.",
    noAccessTitle: "Receiving deliveries isn't available to you",
    noAccessBody:
      "This screen needs the “Receive a supplier delivery” permission. Ask the store owner for it.",
    loadFailed: "We couldn't load the items and suppliers. Refresh the page.",
    noIngredientsTitle: "No stock items yet",
    noIngredientsBody:
      "The owner adds the stock items first; then deliveries can be received against them.",
    headerTitle: "The paper",
    paperCardSub: "What the supplier handed you, as written",
    supplierHint: "Optional.",
    colNumber: "#",
    colVat: "VAT",
    photoAdd: "Add a photo",
    photoRemove: "Remove photo",
    noPaperSwitch: "The supplier gave no paper",
    savedDismiss: "Dismiss",
    date: "Received on",
    dateHint: "Today, or back to {from}.",
    dateOutOfRange: "The date must be between {from} and today.",
    supplier: "Supplier",
    supplierNone: "No supplier",
    supplierPlaceholder: "Choose the supplier",
    supplierNoResults: "No supplier by that name",
    supplierTaxRegistered: "This supplier is tax-registered.",
    paperRef: "Paper number",
    paperRefHint: "The number printed on the supplier's paper, if any.",
    paperTotal: "Paper total",
    paperTotalHint: "The amount at the bottom of the paper, as written.",
    paperTotalInvalid: "Enter the paper total (zero or more).",
    taxInvoice: "Kind of paper",
    taxInvoiceYes: "Tax invoice",
    taxInvoiceNo: "No tax invoice from the supplier",
    taxInvoiceYesHint:
      "Enter the VAT printed on each line. Their sum is the paper's VAT.",
    taxInvoiceNoHint: "VAT is recorded as zero on every line.",
    linesTitle: "Lines",
    linesHint: "One line per item on the paper. The same item may appear on two lines.",
    lineNumber: "Line {number}",
    lineIngredient: "Item",
    lineIngredientPlaceholder: "Search for the item",
    lineNoResults: "No item by that name",
    lineQuantity: "Quantity",
    lineQuantityMode: "Quantity in",
    linePackFill: "{packs} {noun} × {size} = {quantity} {unit}",
    lineTotal: "Line total",
    lineVat: "Line VAT",
    lineVatHint: "Included in the line total.",
    lineRemove: "Remove",
    lineRemoveAria: "Remove line {number}",
    addLine: "Add line",
    tooManyLines: "One paper takes up to {max} lines.",
    lineIngredientRequired: "Choose the item.",
    lineQuantityInvalid: "Enter a quantity above zero.",
    lineTotalInvalid: "Enter the line total (zero or more).",
    lineVatInvalid: "VAT can't be more than the line total.",
    noLines: "Add at least one line.",
    summaryTitle: "Totals",
    summaryLines: "Lines total",
    summaryPaper: "Paper",
    summaryNonStock: "Non-stock",
    summaryNonStockHint:
      "The difference between the paper and the lines — gas, soap, ice. Recorded beside the paper; it never blocks the save.",
    summaryOverPaper:
      "The lines add up to {amount} more than the paper. Check the figures — the save is not blocked.",
    summaryVat: "Paper VAT",
    paymentTitle: "Payment",
    paymentDrawer: "From the drawer",
    paymentAccount: "On account",
    paymentDrawerHint:
      "An expense of the paper total ({amount}) is recorded now from the open drawer, and the lines are paid.",
    paymentAccountHint: "The lines stay owed to the supplier until the payment is recorded, like any delivery.",
    paperTitle: "The paper itself",
    paperPhoto: "Photo of the paper",
    paperNone: "No paper",
    paperPick: "Choose a photo",
    paperChange: "Change photo",
    paperPicked: "Photo: {name}",
    paperPhotoHint: "One photo for the whole paper, uploaded right after the save.",
    paperNoneHint: "There is no paper at all — like a market run.",
    paperAnswerRequired: "Photograph the paper, or switch on “The supplier gave no paper”.",
    paperOwnerOptional:
      "The photo is optional for you, but it clears the “No photo” flag on every line of the purchase-tax report.",
    save: "Record the delivery",
    saving: "Recording…",
    savedTitle: "Delivery recorded",
    savedBody: "The paper was recorded with lines totalling {amount}.",
    savedDrawer: "An expense of {amount} was recorded from your drawer.",
    savedAccount: "The lines are on the supplier's account.",
    savedNonStock: "Non-stock on the paper: {amount}.",
    photoUploading: "Uploading the photo…",
    photoUploaded: "The paper's photo is uploaded.",
    photoFailed:
      "The paper was recorded, but the photo didn't upload. Try again from here.",
    photoRetry: "Upload the photo again",
    photoMissing: "Photo missing",
    photoMissingHint:
      "This paper needs a photo. It stays marked “Photo missing” until one is uploaded.",
    receiveAnother: "Receive another",
    openStock: "Stock",
    openSupplier: "Supplier page",
    openDrawer: "Drawer",
    stockEntry: "Receive a delivery",
    errorDrawerNotOpen:
      "You have no open drawer. Open your drawer, or choose “On account”.",
    errorDuplicatePaper:
      "This supplier's paper with this number was already received on {date}.",
    errorDuplicatePaperUndated:
      "This supplier's paper with this number was already received.",
    errorVatWithoutTaxInvoice:
      "A line carries VAT but the paper is not a tax invoice.",
    errorPackInvalid:
      "The quantity doesn't match the number of packs × their size. Enter it again.",
    errorDrawerTotalZero:
      "Paying from the drawer needs a paper total above zero.",
    errorPhotoAlreadyAttached:
      "The paper's photo is already attached. Only the owner can replace it.",
    errorNotFound: "This paper doesn't exist.",
    paperGroupRef: "Paper no. {ref}",
    paperGroupNoRef: "Paper without a number",
    paperGroupBy: "Received by {name}",
    paperGroupTotal: "Paper",
    paperGroupNonStock: "Non-stock",
    paperGroupNoPaper: "No paper",
    paperGroupHasPhoto: "Photo attached",
    paperGroupFromDrawer: "Paid from the drawer",
    paperGroupOnAccount: "On account",
    drawerMovementPaper: "{supplier}'s paper no. {ref}",
    drawerMovementPaperNoRef: "{supplier}'s paper",
  },
  purchasing: {
    // ── Ordering (Stock) ──
    reorder: "Reorder",
    reorderAria: "Reorder {item} from a supplier",
    reorderAllLow: "Reorder all low",
    reorderTitle: "Order from a supplier",
    reorderTitleMany: "Reorder what is running low",
    reorderSubtitle:
      "An order moves no stock and no cost. Stock goes up when the delivery is received.",
    fieldSupplier: "Supplier",
    fieldQuantity: "Quantity",
    fieldExpectedOn: "Expected delivery",
    fieldExpectedOnHint:
      "Optional — leave it blank if the supplier gave no day.",
    suggestionHint:
      "Suggested {quantity} {unit}: back up to twice the minimum, after what is on hand and already on order.",
    suggestionNoMinimum:
      "This ingredient has no minimum, so there is nothing to suggest — type the quantity.",
    supplierRequired: "Pick the supplier you are ordering from.",
    supplierPick: "Pick a supplier",
    noActiveSuppliers:
      "Add a supplier first on the Suppliers screen — an order is placed with someone.",
    removeLine: "Remove {item} from the order",
    placeOrder: "Place order",
    ordered: "Ordered. Stock moves when you receive it.",
    reorderNothing: "Nothing is running low that is not already on order.",
    alreadyCovered: "Already on order in full: {items}",
    // ── Open orders ──
    onOrderLine: "On order {quantity} {unit}",
    onOrderDue: "due {date}",
    openOrdersTitle: "On order",
    openOrdersSubtitle:
      "When an order arrives, press Receive — that records the delivery, which is the only thing that moves stock and cost.",
    openOrdersFromSupplier: "On order from this supplier",
    receive: "Receive",
    receiveAria: "Receive {item} from {name}",
    cancelOrder: "Cancel order",
    cancelOrderAria: "Cancel the order for {item}",
    cancelConfirmTitle: "Cancel this order?",
    cancelConfirmBody:
      "{item} from {name}. Nothing moved when it was ordered, so only the order itself is called off. Order it again if you still need it.",
    cancelConfirmKeep: "Keep the order",
    cancelled: "Order cancelled.",
    expectedOn: "Expected {date}",
    noExpectedDate: "No date given",
    orderedOn: "Ordered {date}",
    receivingNote:
      "Receiving the order placed on {date} with {name}. The supplier is the order's; if someone else delivered, record it without the order.",
    // ── What you owe ──
    youOwe: "You owe",
    paidUp: "Paid up",
    owedSuppliersOne: "to one supplier",
    owedSuppliersTwo: "to two suppliers",
    owedSuppliersFew: "to {count} suppliers",
    owedSuppliers: "to {count} suppliers",
    nextDelivery: "Next delivery",
    nextDeliveryNone: "Nothing on order",
    nextDeliveryUndated: "On order, no date given",
    lastDelivery: "Last delivery",
    noneYet: "None yet",
    colNext: "Next",
    markPaid: "Mark {amount} as paid",
    markPaidTitle: "Mark {amount} to {name} as paid?",
    markPaidBody:
      "This records that you settled every unpaid delivery from this supplier. It does NOT take money out of a cash drawer — if you paid from a drawer, record that pay-out on the drawer as well.",
    markPaidConfirm: "Mark as paid",
    markPaidDone: "Marked as paid.",
    markPaidNeedsReports:
      "Marking a supplier paid also needs the view-reports permission, because it changes what the figures say is owed.",
    owedChanged:
      "What you owe this supplier changed to {amount}. Check it and confirm again.",
    // ── The supplier's record ──
    details: "Details",
    latestDelivery: "Latest delivery · {date}",
    stepOrdered: "Ordered",
    stepNotOrdered: "Not from an order",
    stepDelivered: "Delivered",
    stepTaxIn: "Tax invoice in",
    stepTaxMissing: "Tax invoice missing",
    stepTaxMissingHint: "Ask the supplier for it",
    stepReceiptOnly: "Receipt only",
    stepReceiptOnlyHint: "Supplier not tax-registered",
    stepPaid: "Paid",
    stepUnpaid: "Not paid yet",
    stepUntracked: "Recorded before payments were tracked",
    deliveriesTitle: "Deliveries",
    deliveriesTruncated: "Showing the most recent deliveries only.",
    noDeliveries: "No deliveries recorded from this supplier yet.",
    colDate: "Date",
    colInvoice: "Invoice",
    colItem: "Item",
    colAmount: "Amount",
    colStatus: "Status",
    receipt: "Receipt",
    statusPaid: "Paid",
    statusUnpaid: "Unpaid",
    statusUntracked: "Not tracked",
    // D142 (phase 9): all of it went back before it was paid — nothing owed.
    statusReturned: "Fully returned",
    stepReturned: "Fully returned",
    stepReturnedHint: "Nothing owed on it",
    // Unpaid with part of it returned: what it still owes after the return.
    stepUnpaidNet: "Owes {amount}",
    netOwed: "Net {amount}",
    // ── The supplier's invoice photo ──
    photo: "Invoice photo",
    // The export's photo column.
    photoOnFile: "Attached",
    photoAttach: "Attach photo",
    photoAttachAria: "Attach a photo of the invoice for {item}",
    photoReplace: "Replace photo",
    photoRemove: "Remove photo",
    photoView: "View photo",
    photoAlt: "Supplier invoice for {item}",
    photoPrivateNote:
      "Kept private, and opened only through a short-lived link.",
    photoRemoveTitle: "Remove the invoice photo?",
    photoRemoveBody:
      "The delivery stays exactly as recorded; only the photo goes. You can attach another.",
    photoAttached: "Photo attached.",
    photoRemoved: "Photo removed.",
    photoWrongType: "Use a JPEG, PNG or WebP photo.",
    photoTooLarge: "That photo is too large.",
    photoUploadFailed:
      "The photo did not upload. Check the connection and try again.",
    photoNotYours:
      "That photo does not belong to this delivery. Attach it again.",
    photoLoadFailed: "The photo could not be opened. Try again.",
    // ── Purchase tax ──
    issueNoPhoto: "No photo",
    noPhotoCountOne: "1 purchase with VAT has no invoice photo",
    noPhotoCountTwo: "2 purchases with VAT have no invoice photo",
    noPhotoCountFew: "{count} purchases with VAT have no invoice photo",
    noPhotoCount: "{count} purchases with VAT have no invoice photo",
    noPhotoNote:
      "A missing photo does not make a purchase unsupported — the photo is the copy of the invoice kept here for whoever checks it.",
    // ── Server refusals ──
    errorOrderNotOpen:
      "This order was already received or cancelled. Refresh the list.",
    errorOrderNotFound: "That order is no longer here. Refresh the list.",
    errorOrderMismatch:
      "This delivery does not match the order (another ingredient or supplier). Record it without the order.",
    errorSupplierInactive:
      "This supplier is retired. Bring him back before ordering from him.",
    errorPurchaseNotFound: "That delivery is no longer here. Refresh the page.",
    errorOwedChanged:
      "What you owe this supplier changed. Check the amount and confirm again.",
  },
  /**
   * Input VAT — the other side of the return (F8, unit 5).
   *
   * This screen does not say what may be CLAIMED. That is a tax position, and
   * it belongs to the owner and their accountant. What it measures is
   * COMPLETENESS: whether the three facts a supplier tax invoice is made of
   * are present on a purchase.
   */
  reportInputVat: {
    returnsTitle: "Purchase returns in this period",
    returnsBodyOne: "1 return worth {amount} took {vat} off input VAT. Every VAT figure on this page is net of it; purchases and their count stay as received.",
    returnsBodyTwo: "2 returns worth {amount} took {vat} off input VAT. Every VAT figure on this page is net of them; purchases and their count stay as received.",
    returnsBodyFew: "{count} returns worth {amount} took {vat} off input VAT. Every VAT figure on this page is net of them; purchases and their count stay as received.",
    returnsBody: "{count} returns worth {amount} took {vat} off input VAT. Every VAT figure on this page is net of them; purchases and their count stay as received.",
    returnsNet: "Net input VAT recorded",
    returnsNegativeNote: "Returns this period outweigh its purchases, so net input VAT is negative.",
    basisTermReturns: "Purchase returns",
    basisDefReturns: "A return's VAT is taken off in the period it went back, from the basis its delivery sits on (full tax invoice or not) and from its supplier's row. Returns are listed as lines under the credit notes, not in the deliveries table.",
    csvReturnsTitle: "Purchase returns",
    csvReturnsCount: "Count",
    csvReturnsValue: "Value",
    csvReturnsVat: "VAT taken off",
    csvReturnsSupportedVat: "Of which on fully invoiced deliveries",
    csvNetVat: "Net input VAT recorded",
    creditNotesTitle: "Credit notes",
    colCreditNote: "Credit note",
    colOriginalInvoice: "Against invoice",
    colOriginalDate: "Original invoice date",
    colQuantity: "Quantity",
    creditNotesTruncated: "There are more credit notes than we show; these are the newest only, while the totals above cover the whole period.",
    title: "Purchases & input VAT",
    description: "The other side of the return: VAT paid to suppliers.",
    noAccessTitle: "You can't see this report",
    noAccessBody:
      "This screen needs the report-viewing permission. Ask the restaurant owner to add it to your role.",
    loading: "Building the report...",
    emptyTitle: "No purchases recorded in this period",
    emptyBody:
      "Try another date range, or record deliveries from the stock screen.",
    basisTrigger: "How is this worked out?",
    basisTitle: "How this report is worked out",
    basisTermSupported: "\u201cBacked by a tax invoice\u201d",
    basisDefSupported:
      "A purchase that records VAT, names a supplier, whose supplier carries a tax registration number, and which carries the supplier's invoice number. The report says whether those facts are present — it does not say whether the VAT may be claimed; that is between you and your accountant.",
    basisTermGross: "The amounts",
    basisDefGross:
      "Every figure here is the gross amount paid. The weighted average cost still runs on that same gross figure — nothing here changed it.",
    basisTermDay: "The day",
    basisDefDay:
      "A purchase falls in the business day it was recorded in, exactly as on the spend-vs-gained report — so the purchase total on both reports is the same figure.",
    basisTermScope: "What is not here",
    basisDefScope:
      "Ingredient purchases only. Rent, utilities and every other supplier invoice are outside this system, so this is not a return — it is what the store holds of one.",
    basisGross: "Gross",
    statPurchases: "Purchases",
    statPurchasesSub: "{count} deliveries in the period",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    statPurchasesSubOne: "1 delivery in the period",
    statPurchasesSubTwo: "2 deliveries in the period",
    statPurchasesSubFew: "{count} deliveries in the period",
    statRecorded: "VAT recorded",
    statSupportedSub: "{pct}% of the VAT recorded",
    statSupportedNone: "No VAT recorded in this period",
    splitNoVat: "No VAT recorded",
    splitTotal: "Total",
    purchaseCountOne: "(1 delivery)",
    purchaseCountTwo: "(2 deliveries)",
    purchaseCountFew: "({count} deliveries)",
    purchaseCount: "({count} deliveries)",
    gapsTitle: "What is missing",
    gapNoVat: "No VAT recorded",
    gapNoSupplier: "No supplier",
    gapNoRegistration: "Supplier has no tax registration",
    gapNoRef: "No tax invoice number",
    // The three counts do NOT sum to the bucket: one purchase can be missing
    // two things, and a reader who added them and got a bigger number would
    // rightly distrust the whole card.
    gapsOverlapNote:
      "A purchase can be missing more than one of these, so the counts do not add up to the number of purchases.",
    lineSupported: "Complete",
    suppliersTitle: "By supplier",
    linesTitle: "Deliveries, one by one",
    unattributed: "No supplier",
    noRegistration: "No tax registration",
    colSupplier: "Supplier",
    colRegistration: "Tax registration",
    colPurchases: "Deliveries",
    colTotal: "Total",
    colVat: "VAT",
    colSupportedVat: "Of which backed",
    colDate: "Day",
    colRef: "Invoice no.",
    colItem: "Item",
    colStatus: "Status",
    truncatedNote:
      "The list reached the maximum number of rows shown — the totals above are computed over the whole period.",
    /* PAPER (2026-10) — the redesigned purchase-tax screen. Its word is
       COMPLETENESS, never "claimable": the deduction is the accountant's call. */
    paperMonthAria: "Month",
    paperExport: "Export for accountant",
    paperVatBacked: "{amount} backed by a tax invoice",
    paperCompleteOf: "{count} of {total}",
    paperNeedsFixing: "Needs fixing",
    paperFixSub: "{amount} of VAT without a complete tax invoice",
    paperFixNone: "Nothing to fix",
    paperRingTitle: "How complete your paperwork is",
    // Under the ring's figure: the share of recorded VAT backed by a tax
    // invoice (the ring is weighted by VAT, not by deliveries).
    paperRingCaption: "of VAT backed",
    paperChipNotRegistered: "Not registered",
    paperDeductNote:
      "This page shows what your supplier invoices say. Whether the VAT can be deducted is for your accountant to confirm.",
    paperRefMissing: "Missing",
    paperAddTaxNumber: "Add tax number",
    // PAPER (founder, 2026-10-02) — the redesign's own sentence under
    // "Purchase tax" in the top bar.
    paperSubtitle:
      "VAT on your supplier invoices, and which invoices still need fixing.",
  },
  shift: {
    title: "Drawer",
    description:
      "Open a drawer, record cash movements, and close it against what is actually in the drawer.",
    noAccessTitle: "You can't manage the drawer",
    noAccessBody:
      "This screen needs the drawer permission. Ask the restaurant owner to add it to your role.",
    loading: "Loading the drawer...",
    labelPlaceholder: "Till 1",
    labelTakenChip: "taken",
    labelTakenWarning:
      '"{label}" is open with {name}. You cannot open the same box twice — take it over from the list below, or open another one.',
    someoneElse: "someone else",
    otherDrawersEmpty: "No other drawer is open in this store right now.",
    takeoverButton: "Take over",
    takeoverTitle: 'Take over "{label}"',
    takeoverBody:
      "Count the cash in the box with {name} now, and enter the figure you both agreed. This closes their drawer and opens a new one for you on the same box, in the same moment.",
    takeoverCountHint:
      "What the two of you actually counted, not what it should have been.",
    takeoverDoneToast: "You have taken over the drawer",
    currentErrorBody:
      "We couldn't check whether a drawer is already open, so we're not showing the open-drawer form — opening one over a running drawer would split the drawer. Try again.",
    summaryErrorBody:
      "We couldn't load this drawer's summary, so the figures below aren't available. Try again before you close the drawer.",
    openTitle: "No drawer is open",
    openBody:
      "Open a drawer so you can take payments and the cash is tracked. Count what is in it now and enter that as the opening float.",
    openingFloatLabel: "Opening float",
    openingFloatHint: "The cash in the drawer before service starts.",
    openedToast: "The drawer is open",
    // Unit D — the blind count: you count first, and see the variance after.
    hiddenUntilCounted: "—",
    blindCountPrintNote:
      "The expected figure is not printed while the drawer is open — that is what keeps the count a count. It appears once the drawer is closed.",
    blindCountScreenNote:
      "The rule the expected figure is worked out by is hidden while the drawer is open — so the count stays a count. It appears with the figure once you have counted and closed.",
    blindCountHint:
      "Count what is in the drawer and enter it without looking at any figure. The expected amount and the variance appear once you close — which is what makes this a count rather than a transcription.",
    blindConfirmHint:
      "The expected amount and the variance are deliberately not here. You will see them the moment this closes, and you can count once more then if you think you got it wrong.",
    firstCountNote:
      "The first count was {amount} — that figure is recorded and does not change.",
    // Unit E — what the floor was carrying at the moment of the count, frozen
    // onto the drawer. A variance of zero is not always a clean night: the drawer
    // can close exactly right while 900 is walking the floor, and the shortage
    // then turns up in the morning looking like the morning's problem.
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    closedAgedChequesOne:
      "1 cheque worth {amount} was out on the floor when this was counted — that cash never reached this drawer.",
    closedAgedChequesTwo:
      "2 cheques worth {amount} were out on the floor when this was counted — that cash never reached this drawer.",
    closedAgedChequesFew:
      "{count} cheques worth {amount} were out on the floor when this was counted — that cash never reached this drawer.",
    closedAgedCheques:
      "{count} cheques worth {amount} were out on the floor when this was counted — that cash never reached this drawer.",
    // Round-table finding 6 — the float chain, said to the cashier while they are
    // still standing there rather than to the owner the next morning. The figure
    // is NOT shown before they count: a cashier who reads "closed with 640" types
    // 640, which is the transcription the blind count exists to stop.
    // Round-table finding 4 — the handover on the OWNER's screens, not only on
    // the incoming cashier's. A variance filed against a drawer its holder never
    // counted has to say so: the owner reads it a week later and has no other way
    // to know.
    filedCountedByOther:
      "Counted and closed by {closer}, not by {opener} who opened it — so the variance below is measured against somebody else's count.",
    filedTookOver:
      "Taken over from {name} — the {amount} opening float is the count made at the handover, not a float counted in on its own.",
    filedTookOverChip: "Handover",
    floatChainTitle: "This box last closed with {amount}",
    floatChainHigher:
      "and you opened it with {opened} — {gap} appeared in the box with nothing recording it. Look now, before you start.",
    floatChainLower:
      "and you opened it with {opened} — {gap} left it. Normal if somebody moved the takings to the safe, but it should be recorded.",

    varianceWithinTolerance:
      "This variance is inside the band the store allows ({amount}).",
    // B117/B3 — see the Arabic note: the drawer this cashier closed and may
    // still count once more, offered from the screen they are actually on.
    recountPendingTitle: "You can still count {label} again",
    recountPendingBody:
      "You closed it at {counted}. One second count is allowed, and the first one stays on record.",
    recountBody:
      "Once only. The first count stays on record exactly as it was, and the variance is recomputed from the new count against the same expected figure.",
    recountHint: "What you have just counted.",
    recountedToast: "The new count is recorded",
    trackingOffTitle: "Drawer tracking is turned off in settings",
    trackingOffBody:
      "This restaurant isn't running drawer tracking right now. If it should be, ask the owner.",
    trackingOffBodyOwner:
      'Drawer tracking is currently off — turn on "drawer tracking" in settings, then come back to open a drawer.',
    trackingOffAction: "Open settings",
    payoutOverDrawerWarning:
      "That's more than the drawer should hold — double-check before recording it.",

    statExpectedCash: "Expected cash",
    statExpectedCard: "Expected card",
    statSettledCount: "Settled invoices",
    statFloat: "Opening float",
    statCashTotal: "Bills settled in cash",
    statCardTotal: "Bills settled by card",
    statWriteOff: "Written off",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    statWriteOffSubOne: "1 bill",
    statWriteOffSubTwo: "2 bills",
    statWriteOffSubFew: "{count} bills",
    statWriteOffSub: "{count} bills",
    // R6/R12 (BLOCKER 1) — refunds, named on the drawer screen itself, in the
    // Z's own words ("Drawer refunds" / "Drawer card") so the paper and the
    // screen do not speak two vocabularies for one figure.
    statRefundCash: "Drawer refunds",
    statRefundCard: "Drawer card refunds",
    statRefundSub: "Plates given back: {count}",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    statRefundSubOne: "Plates given back: 1",
    statRefundSubTwo: "Plates given back: 2",
    statRefundSubFew: "Plates given back: {count}",
    // D44 — the count is every plate that came back, whatever the method; the
    // money beside it is only what left a DRAWER. A transfer refund moves
    // neither figure, so without this sentence a true screen reads as broken.
    refundTransferNote:
      "The plate count above covers every refund, whatever the method. The money shown is only what left the drawer or the card terminal: a refund sent back as an InstaPay transfer goes back from the bank, so it changes neither expected cash nor expected card.",
    statInstapay: "InstaPay transfers",
    statInstapaySub: "Went to the bank, not the drawer",
    // Trap 1 — the composition rule ON SCREEN, not in a code comment: without
    // it the sheet prints a float, a cash total and an expected cash that do
    // not add up, with nothing explaining the hole. Transfers appear in
    // neither side of it, which is why they get a line of their own.
    expectedCashFormula:
      "Expected cash = float + bills settled in cash + paid in − paid out − drawer refunds. Expected card = bills settled by card − drawer card refunds.",
    scopeNote: "Settled on this drawer only — a drawer is not a business day.",
    basisLive:
      "An open drawer's figures are live — calculated now and moving with every bill, and frozen the moment you close and count.",
    chargesInCashNote:
      "Expected amounts include any VAT or service charge collected with the bills — that money is in the drawer but not all of it is the restaurant's to keep.",

    /*
     * Slice 9 / UX-round D41 / ruling F15 — this screen became a report page IN PLACE.
     *
     * Founder decision: no second route. It stays a live drawer console AND a
     * drawer report on one route, and that mixing is a recorded, accepted
     * compromise rather than an oversight.
     *
     * What changed: thirteen tiles came down to one row of two — the expected
     * cash and expected card, dashes and all, because those two are the blind
     * count working — and the rest became lines inside three cards, while four
     * basis paragraphs that rendered unconditionally under the tile grid went
     * behind one door and into the CSV. The door's label is verbatim the
     * reports family's, so the whole app has one door.
     */
    cardDrawerTitle: "The drawer now",
    // The third card's name. NOT "Voids & comps" — that became one block
    // inside it, beside the refunds and the free swaps, and a card has to be
    // named wider than its first block.
    cardExceptionsTitle: "Drawer exceptions",
    jumpTo: "On this page",
    basisTrigger: "How this is counted",
    basisTitle: "How this screen is counted",
    exportCsv: "Download CSV",
    // The single basis token left on the canvas in place of the scope
    // paragraph: one word for why these figures are not the day's.
    basisChipDrawer: "This drawer",
    /*
     * PAPER (round 3) — the drawer's header, its answer tiles, its live
     * history and two sheets: pay in / pay out, and count and close. The count
     * stays blind: no expected figure and no running balance before the close.
     */
    paperOpenedSub: "Started {time} with a {amount} float",
    paperDescription:
      "The drawer that is open right now. Record money in or out, then count it to close.",
    paperClosedSub: "Started {opened} · closed {closed}",
    paperStatusOpen: "Open",
    paperStatusBalanced: "Closed · balanced",
    paperStatusOff: "Closed · off by {amount}",
    paperPayIn: "Pay in",
    paperPayOut: "Pay out",
    paperCountAndClose: "Count and close",
    paperHiddenSub:
      "Shown after you count and close — so the count stays a count.",
    paperSettledSubNone: "No invoice settled on this drawer yet",
    paperSettledSubOne: "1 invoice settled on this drawer, any method",
    paperSettledSubTwo: "2 invoices settled on this drawer, any method",
    paperSettledSubFew: "{count} invoices settled on this drawer, any method",
    paperSettledSub: "{count} invoices settled on this drawer, any method",
    paperTilePaidInOut: "Paid in / out",
    paperCardSub: "Not in the drawer — it goes to the bank",
    paperHistoryTitle: "Live history",
    paperHistorySub:
      "Newest first: the float and the cash put in or taken out by hand. Sales are counted in the tiles above.",
    paperPayInTitle: "Put cash in",
    paperPayOutTitle: "Take cash out",
    paperPayInSub: "Cash added to the drawer that is not from a sale.",
    paperPayOutSub: "Cash taken from the drawer that is not a refund.",
    paperCountTitle: "Count the drawer",
    paperCountSub:
      "Count each note and coin, or type the total straight in. The expected amount shows after you close.",
    paperCountNote: "Note / coin",
    paperCountHowMany: "How many",
    paperCountValue: "Value",
    /*
     * PAPER update 2 (2026-10-03) — the drawer's card grid, the open / take
     * over dialog, and the count sheet that doubles as the recount.
     */
    pdPeriodOpen: "This drawer, since it opened {time}",
    pdPeriodClosed: "This drawer, {opened} to {closed}",
    pdPeriodNone: "You have no drawer open right now",
    pdBasisDoor: "How it's calculated",
    pdBasisTitle: "How this drawer is calculated",
    pdExport: "Export",
    pdPrintX: "Print X",
    pdPrintXTitle: "X report: a mid-shift printout, the drawer stays open",
    pdStatusNone: "No drawer",
    pdOpenDrawer: "Open a drawer",
    pdRecountOnce: "Recount once",
    pdCountTitle: "The count",
    pdCountSub: "Expected is only shown now that the drawer is counted",
    pdSeeInLog: "See it in Drawer log",
    pdExpectedCash: "Expected cash",
    pdExpectedCard: "Expected card",
    pdHidden: "Hidden until counted",
    pdBlindTag: "Blind count",
    pdCounted: "What you counted",
    pdRecountTag: "Recount",
    pdManagerTag: "Manager's count",
    pdDifference: "Difference",
    pdOver: "Over",
    pdShort: "Short",
    pdBalanced: "Balanced",
    pdTakingsTitle: "Takings this shift",
    pdTakingsSub: "Only cash lands in this drawer",
    pdCash: "Cash",
    pdCard: "Card",
    pdInstapay: "InstaPay",
    // D163 — the card and InstaPay payments taken with a reference; no sum.
    pdRefsTitle: "Transaction references",
    pdRefsSub: "Card and InstaPay payments taken with a reference",
    pdBillsSettled: "Bills settled",
    pdBillsSettledNote: "Any method",
    pdInNoDrawer: "In no drawer",
    pdFloorTitle: "Still on the floor",
    pdFloorSub: "Money this shift has not collected yet",
    pdOpenTables: "Open tables",
    pdOpenTablesNote: "Still eating — nothing to do",
    pdChecksOut: "Checks out",
    pdPrintedTag: "Printed",
    pdChecksOutNote: "With a waiter — collect it before you count",
    pdCarriedIn: "Carried in",
    pdCarriedInNote: "From before this service started — you inherited them",
    pdSeeBills: "See these bills →",
    pdFloorEmpty: "Nothing on the floor — every bill is settled.",
    pdRefundsTitle: "Refunds",
    pdRefundsSub: "Money given back after payment",
    pdRefundsNone: "No refunds on this drawer.",
    pdRefundsTransfer:
      "A refund paid back by bank transfer leaves neither drawer; it is listed under exceptions.",
    pdMovesTitle: "Paid in and out",
    pdMovesSub: "Where the cash that left went",
    pdLeftBusiness: "Left the business",
    pdLeftBusinessNote: "Expenses, delivery and owner draws",
    pdMovedElsewhere: "Moved elsewhere",
    pdMovedElsewhereNote:
      "Safe drops, bank deposits, and anything marked Other",
    pdPassedThrough: "Passed through",
    pdPassedThroughNote: "Tips and service charge paid to the team",
    pdExceptionsTitle: "Exceptions this shift",
    pdExceptionsSub:
      "Voids, comps, refunds and write-offs, with who approved them",
    pdExceptionsEmpty: "Nothing to review on this drawer.",
    pdSwaps: "Free swaps",
    pdSwapsNote:
      "Cost is food cost, not price. Free swaps change no money in the drawer.",
    pdOthersTitle: "Other open drawers",
    pdOthersSub: "Take one over when a cashier leaves without closing",
    pdOthersSubHolding:
      "Who else is on a box right now. Close yours before you take another over.",
    pdOthersSince: "{name} · since {time}",
    pdNotesTitle: "Notes on your drawers",
    pdNotesSub:
      "Your closed drawers that were off or that someone wrote about. Add your side of the story; notes can’t be edited or deleted.",
    pdNotesFoot:
      "Notes only appear once a drawer is counted and closed, so they can’t reveal the expected amount of an open drawer.",
    pdNoNotes: "No notes yet.",
    pdNotePlaceholder: "Write a note about this drawer",
    pdAddNote: "Add note",
    pdManagersBasis: "(manager's count)",
    pdHistorySub:
      "Newest first. Only cash; card payments never touch the drawer.",
    pdOdTitle: "Open a drawer",
    pdOdDrawer: "Drawer",
    pdOdDrawerHint:
      "The name printed on the X and Z reports. A drawer already open by someone else can’t be opened twice.",
    pdOdSave: "Open drawer",
    pdRuleVariance:
      "Any difference is filed against {name}’s drawer, not yours.",
    pdRuleFloat: "Your count becomes your opening float.",
    pdSaveRecount: "Save recount",
    pdCcCounted: "Counted",
    pdCcExpected: "Expected",
    pdFloatPlain: "Opened on a float of {amount}.",
    pdFloatMatches: "Opened on {amount}, the same as this box's last count.",
    pdFloatTookOver:
      "Took over at {amount} from {name} — your count became this float.",
    pdClosedMore:
      "The rest of this drawer — refunds, paid in and out, exceptions — is in the Drawer log.",
    basisTermScope: "What these figures cover",
    basisTermTiming: "When these figures are taken",
    basisTermCharges: "VAT and service charge",
    basisMovementsNote:
      "Cash paid out splits three ways: money that left the business, money that only moved elsewhere inside it (a drop to the safe), and money that passed through the drawer without ever being the business's (tips). All three come off the expected cash the same way — the difference is in the reading, not the arithmetic.",

    openedAtLabel: "Started {time}",
    /** Unit B: this drawer was inherited, not opened from scratch. */
    tookOverLabel: "Took over {amount} from {name} at {time}",
    openedByLabel: "Opened by {name}",
    unknownStaff: "Unknown staff",
    staffColumn: "Staff",
    movementsTitle: "Cash movements",
    movementsEmpty: "No cash movements on this drawer yet.",
    movementKindOut: "Paid out of the drawer",
    // FIX 1: required on every new movement — that's the whole point of the
    // feature. Choices narrow to what the selected direction (in/out) allows,
    // and the server refuses a mismatch on its own either way.
    movementCategoryLabel: "Movement category",
    movementCategoryRequired: "Choose a category for the movement.",
    // Unit E — cash that changes hands names both people. "Paid out 5,400 —
    // drop to safe" names a drawer, an amount and a category and no second
    // person at all: the cash left a named cashier's till and entered nobody.
    // That is the shape of every disputed handover in a restaurant, and the
    // second name is what turns the row into a transfer.
    movementCounterpartyLabel: "Handed to",
    movementCounterpartyPlaceholder: "Choose the person",
    movementCounterpartyHint:
      "This category is cash changing hands — name who received or brought it.",
    movementCounterpartyRow: "Handed to {name}",
    movementCounterpartyOwedBy: "Owed by {name}",
    movementCounterpartyHeldFor: "Held for {name}",
    categoryExpense: "Expense (spent by the business)",
    categoryDropToSafe: "Drop to safe",
    categoryBankDeposit: "Bank deposit",
    categoryOwnerDraw: "Owner's draw",
    // Unit 2 — the money that is not food. The charge and the tips are the
    // team's money being handed over; delivery is the restaurant's own cost.
    categoryServiceChargePayout: "Service charge paid to staff",
    categoryTipPayout: "Tips paid to staff",
    categoryDeliveryCost: "Delivery cost (courier)",
    categoryDriverShortage: "Driver shortage",
    categoryDriverOverage: "Driver overage",
    categoryTipIn: "Cash tip into the drawer",
    categoryChangeFund: "Change fund",
    categoryOwnerCapital: "Owner capital",
    categoryCorrection: "Correction",
    categoryStaffAdvance: "Staff advance",
    categoryStaffAdvanceRepaid: "Staff advance repaid",
    categoryOther: "Other",
    // Not "Other" — this is a movement recorded before categories existed;
    // nobody was ever asked for one.
    categoryUncategorised: "Uncategorised",
    movementAmountLabel: "Amount",
    movementReasonLabel: "Reason",
    // The CSV's time column. A column header is the name of the quantity in
    // the column — a noun, never a phrase.
    movementAtColumn: "Time",
    movementReasonPlaceholder: "e.g. bought ice",
    movementAdd: "Record movement",
    movementReasonRequired: "Write a reason for the movement.",
    movementAddedToast: "Movement recorded",
    // D157 — the receipt photo is OPTIONAL. Nothing warns when there is none
    // and nothing waits for one: the movement is saved first, the photo after.
    movementPhotoLabel: "Receipt photo",
    movementPhotoOptionalHint:
      "Optional — if you have the paper, photograph it.",
    movementPhotoPick: "Choose photo",
    movementPhotoChange: "Change photo",
    movementPhotoUnpick: "Remove photo",
    movementPhotoAttach: "Attach photo",
    movementPhotoAttachAria: "Attach a receipt photo: {reason}",
    movementPhotoReplace: "Replace photo",
    movementPhotoView: "View receipt photo",
    movementPhotoViewAria: "View the receipt photo: {reason}",
    movementPhotoTitle: "Receipt photo",
    movementPhotoAlt: "Receipt for the movement: {reason}",
    movementPhotoUploading: "Uploading the photo…",
    movementPhotoUploaded: "Photo uploaded.",
    movementPhotoFailed:
      "The movement is recorded, but its photo did not upload. Try again, or attach it later from the history.",
    movementPhotoRetry: "Retry",
    movementPhotoDismiss: "Dismiss",
    movementPhotoErrorAlreadyAttached:
      "This movement already has a photo. Only the owner can replace it.",
    movementPhotoErrorNotAllowed:
      "You did not record this movement and do not manage drawers.",
    movementPhotoErrorViewNotAllowed:
      "This receipt photo is not available to you.",
    movementPhotoErrorNotFound: "This movement was not found.",
    paidInTotal: "Total paid in",
    paidOutTotal: "Total paid out",
    // FIX 1's split — the reason the category feature exists: cash that
    // truly left the business (expense, owner's draw) versus cash that only
    // relocated (safe, bank, correction, or an uncategorised movement).
    paidOutLeftBusinessTotal: "of which left the business",
    paidOutMovedElsewhereTotal: "of which is still the business's (moved only)",
    // Unit 2's third arm. The cash really left, but it was never the
    // business's — the charge and the tips handed to the team. Neither a cost
    // of the business nor an internal relocation.
    paidOutPassedThroughTotal: "of which went to the team (charge/tips)",
    exceptionsTitle: "Voids & comps",
    // B117/RT-A — see the Arabic note: this block is store-wide and the copy
    // said "this drawer", so the same void reads as two cashiers' own and the
    // store's total doubles when a reader adds them.
    exceptionsIntro:
      "Every void, comp and cancellation in the store during this drawer's window — not this drawer's alone. They move no money and change no expected figure, and each row names who did it.",
    exceptionsEmpty: "No voids or comps in this window yet.",
    // R12 (MAJOR 7) — refunds get a heading of their own. They used to be
    // filed under "Voids & comps", which is the exact folding R12 forbids: a
    // comp is food given away, a write-off is money that never arrived, a
    // refund is money that arrived and went back.
    refundsTitle: "Refunds",
    refundsIntro:
      "A refund is money that arrived and went back to the customer — its own kind, never a void and never a comp. Only cash refunds reduce expected cash; card refunds reduce the expected card figure.",
    refundsEmpty: "Nothing was refunded on this drawer.",
    exceptionsTruncated:
      "Showing the newest {count} only — there are older entries in this window that are not listed. The figures above are counted over the whole drawer, not over this list.",
    flaggedSwapsTitle: "Swaps that went out free",
    flaggedSwapsEmpty: "No free swaps on this drawer.",
    flaggedSwapsBasisNote:
      "Attributed to whoever added the plate, falling back to whoever opened the bill for a plate that records no one. The figure beside each one is the food cost that left, not something the guest paid.",
    exceptionVoid: "Item voided",
    exceptionComp: "Item comped",
    exceptionCancel: "Invoice cancelled",
    exceptionCorrectSettlement: "Payment method corrected",
    exceptionWriteOff: "Bill written off",
    // R12: its own kind. Not "cancelled", not "written off" — a comp is food
    // given away, a write-off is money that never arrived, a refund is money
    // that arrived and went back.
    exceptionRefund: "Refunded to the customer",
    exceptionRefundAmount: "Given back {amount}",
    // DECISION 3, on screen: the money went back one way and the bill was
    // corrected to another afterwards. A flag, not an error — the correction
    // is allowed, and the refund keeps the method the money physically left
    // by, or the drawer would stop agreeing with the cash in it.
    exceptionRefundDiverged:
      "Given back by a method this bill no longer names — the settlement was corrected after the refund. A flag, not an error: the drawer really is short that cash.",
    exceptionPlateCount: "Plates: {count}",
    // B118 round table — 3–10 take the plural in Arabic and 11+ the singular.
    // English has no third form, so this is the general form's own words; the
    // shape stays identical in both catalogues, as with `…Two`.
    exceptionPlateCountFew: "Plates: {count}",
    exceptionPlateCountOne: "Plates: 1",
    exceptionPlateCountTwo: "Plates: 2",
    // R12 (MAJOR 7) — the method the money physically left by, so expected
    // cash can be reconstructed by hand from the page: three refunds of the
    // same amount with no method add up to nothing a reader can check.
    exceptionRefundMethod: "Given back by {method}",
    // R8 (MAJOR 3) — one word on the chip; the sentence beneath explains it.
    exceptionRefundLate: "Late",
    exceptionRefundClosedShift: "Closed drawer",
    exceptionRefundClosedShiftNote:
      "This money went back after the drawer it was taken in had been closed and signed off — so the takings that cashier signed for are no longer what the till holds. Allowed and applied, but recorded.",
    exceptionRefundOtherActor: "Another person",
    exceptionRefundOtherActorNote:
      "The person who refunded is not the person who took the payment. Ordinary across a drawer change, and recorded every time.",
    exceptionRefundLateNote:
      "This refund happened after the store's configured limit — allowed and applied, but recorded as late.",
    // This bill is from another day: the decision was taken now and the loss
    // lands in this drawer, but the bill itself belongs to {date}.
    exceptionBillDate: "bill from {date}",
    exceptionOutsideShift: "Between drawers",
    outstandingTitle: "Money not in the drawer yet",
    // Round-table finding 5 — one number first, then the three it is made of.
    // Those three cannot overlap by construction: a table still eating, a cheque
    // printed during this service, and a bill from before it. A cashier was
    // reading three counts of one pile with nothing saying how they related.
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    outstandingTotalOne: "1 bill whose money is not in the drawer yet",
    outstandingTotalTwo: "2 bills whose money is not in the drawer yet",
    outstandingTotalFew: "{count} bills whose money is not in the drawer yet",
    outstandingTotal: "{count} bills whose money is not in the drawer yet",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    outstandingOpenTablesOne:
      "1 table is still eating — normal, nothing to do.",
    outstandingOpenTablesTwo:
      "2 tables are still eating — normal, nothing to do.",
    outstandingOpenTablesFew:
      "{count} tables are still eating — normal, nothing to do.",
    outstandingOpenTables:
      "{count} tables are still eating — normal, nothing to do.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    outstandingChequesOutOne:
      "1 cheque is printed and out with a waiter — that cash hasn't reached the drawer. Collect it before you count.",
    outstandingChequesOutTwo:
      "2 cheques are printed and out with a waiter — that cash hasn't reached the drawer. Collect it before you count.",
    outstandingChequesOutFew:
      "{count} cheques are printed and out with a waiter — that cash hasn't reached the drawer. Collect it before you count.",
    outstandingChequesOut:
      "{count} cheques are printed and out with a waiter — that cash hasn't reached the drawer. Collect it before you count.",
    // Unit E — the cheques that have stopped being tables. The count above is a
    // normal fact at 22:00; one out for over an hour and a half is money nobody
    // has explained. The amount is what makes it a question: a count cannot tell
    // an owner whether the floor is carrying 90 or 9,000.
    // "Of those" — this is a subset of the total above, not a fourth figure.
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    outstandingAgedOne:
      "Of those, 1 has been out a long time, worth {amount} — money nobody has explained. Go and find it.",
    outstandingAgedTwo:
      "Of those, 2 have been out a long time, worth {amount} — money nobody has explained. Go and find them.",
    outstandingAgedFew:
      "Of those, {count} have been out a long time, worth {amount} — money nobody has explained. Go and find them.",
    outstandingAged:
      "Of those, {count} have been out a long time, worth {amount} — money nobody has explained. Go and find them.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    outstandingCarriedInOne:
      "1 bill from an earlier drawer — still open, or the cheque is out — you inherited it.",
    outstandingCarriedInTwo:
      "2 bills from an earlier drawer — still open, or the cheque is out — you inherited them.",
    outstandingCarriedInFew:
      "{count} bills from an earlier drawer — still open, or the cheque is out — you inherited them.",
    outstandingCarriedIn:
      "{count} bills from an earlier drawer — still open, or the cheque is out — you inherited them.",
    outstandingTrace: "View the bills",
    outstandingTraceNoAccess:
      "Those bills are in invoice history — opening it needs floor (POS) access.",
    endShiftButton: "Close the drawer",
    endShiftTitle: "Close the drawer",
    endShiftBody:
      "Enter what you actually counted in the drawer. The expected amount and the variance appear after you close, not before.",
    // Founder decision, 2026-10-03: the last open drawer cannot close while
    // invoices are still open — there would be no drawer left to settle into.
    lastDrawerOpenInvoicesOne:
      "This is the last open drawer and 1 invoice is still open. Settle or cancel it first, then close the drawer — once it is closed there is no drawer left to settle it into.",
    lastDrawerOpenInvoicesTwo:
      "This is the last open drawer and 2 invoices are still open. Settle or cancel them first, then close the drawer — once it is closed there is no drawer left to settle them into.",
    lastDrawerOpenInvoicesFew:
      "This is the last open drawer and {count} invoices are still open. Settle or cancel them first, then close the drawer — once it is closed there is no drawer left to settle them into.",
    lastDrawerOpenInvoices:
      "This is the last open drawer and {count} invoices are still open. Settle or cancel them first, then close the drawer — once it is closed there is no drawer left to settle them into.",
    countedCashLabel: "Cash in the drawer",
    varianceLabel: "Variance",
    varianceShortHint: "The drawer is short of what was expected.",
    varianceOverHint: "The drawer is over what was expected.",
    varianceMatchHint: "The drawer matches.",
    confirmEndShift: "Confirm end of drawer",
    closedBody:
      "The variance is recorded — this drawer is now in the drawer history.",
    ownNotesCounted: "You counted {counted}",
    startNewShift: "Open a new drawer",
    closedToast: "Drawer closed",
    printDocTitle: "Drawer summary (X)",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    printExceptionCountOne: "Exceptions (voids/comps): 1",
    printExceptionCountTwo: "Exceptions (voids/comps): 2",
    printExceptionCountFew: "Exceptions (voids/comps): {count}",
    printExceptionCount: "Exceptions (voids/comps): {count}",
    // The same line when the list was cut: this count is taken FROM that
    // capped list, so it is a floor, and a signed chit may not state a number
    // lower than the truth without saying it is lower.
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    printExceptionCountFloorOne: "Exceptions (voids/comps): at least 1",
    printExceptionCountFloorTwo: "Exceptions (voids/comps): at least 2",
    printExceptionCountFloorFew: "Exceptions (voids/comps): at least {count}",
    printExceptionCountFloor: "Exceptions (voids/comps): at least {count}",
    // BLOCKER 2 — the signed chit may not understate: the counts above come
    // from uncapped reads and the list itself is cut at the server's cap, so
    // the paper says so exactly as the screen does.
    printExceptionsTruncated:
      "The list is cut at the last {count} — older movements are not printed here.",
    // R12 — refunds keep their own heading on paper too.
    printRefundsTitle: "Refunds",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    printRefundCountOne: "Plates given back: 1",
    printRefundCountTwo: "Plates given back: 2",
    printRefundCountFew: "Plates given back: {count}",
    printRefundCount: "Plates given back: {count}",
  },
  shiftHistory: {
    title: "Drawer history",
    description: "Closed drawers, the variance on each, and their full detail.",
    noAccessTitle: "You can't view the drawer history",
    noAccessBody:
      "This screen needs the reports permission. Ask the restaurant owner to add it to your role.",
    loading: "Loading drawer history...",
    emptyTitle: "No closed drawers yet",
    emptyBody:
      "Once a drawer is closed, it will show up here with its full detail.",
    tableOpened: "Opened",
    tableClosed: "Closed",
    tableOpenedBy: "Opened by",
    tableClosedBy: "Closed by",
    tableExpected: "Expected",
    tableCounted: "Counted",
    tableVariance: "Variance",
    unknownStaff: "Unknown",
    paginationLabel: "Drawer history pages",
    prevPage: "Previous",
    nextPage: "Next",
    pageOf: "Page {page} of {pages}",
    // Unit D — the first count, on the screen the OWNER reads.
    // B117 round table 2 — a manager's own count, the choice he makes when it
    // differs, and the words beside a variance.
    managerCountTitle: "Count this drawer yourself",
    managerCountLabel: "Cash you found in the drawer",
    managerCountHint:
      "Enter the figure you counted. The cashier's count and the expected figure are shown next.",
    managerCountRequired: "Enter the figure you counted.",
    managerCountInvalid: "Not a valid amount — two decimal places at most.",
    managerCountContinue: "Continue",
    managerCountCashierLine: "{name} counted",
    managerCountCashierUnnamed: "The cashier",
    managerCountYours: "You counted",
    managerCountExpected: "Expected",
    managerCountDiffers:
      "The two counts differ by {amount} — you need to say which one this drawer stands on.",
    // See the Arabic note: with no prior count there is no difference.
    managerCountOnlyCount:
      "This drawer has no recorded count, so yours is the only figure it can stand on.",
    managerCountChoiceHint:
      "Both counts stay on the record with their names either way. What you choose here is the figure the drawer is reconciled against.",
    managerCountUseMine: "Use my count — {amount}",
    managerCountUseTheirs: "Leave the cashier's count — {amount}",
    managerCountAction: "Count this drawer yourself",
    managerCountedNote:
      "{name} counted this drawer: {amount} (filed at {cashier}).",
    managerCountSupersededNote:
      "The difference was settled on the manager's count.",
    managerCountDeferredNote:
      "The manager saw the difference and left the cashier's count standing.",
    recountedByNote: "That second count was taken by {name}.",
    notesTitle: "Drawer notes",
    notesEmpty: "No notes on this drawer.",
    noteLabel: "Write a note",
    notePlaceholder: "e.g. 50 paid to the ice man, never recorded.",
    noteAdd: "Add note",
    noteAppendOnly:
      "Notes are added, never edited or deleted — a correction is a new note.",
    noteEmpty: "Write the note first.",
    managerCountedToast: "Your count is on the drawer.",
    noteAddedToast: "Note added.",
    detailErrorBody: "We couldn't load this drawer's detail.",
    basisSnapshot:
      "These figures were frozen when the drawer was counted. A payment method corrected after that shows in today's drawer, not here.",
    basisLive:
      "This drawer closed before figures were frozen, so what you see is calculated now from the bills.",

    // Slice 9 / UX-round D41 — this screen became a report page: one card, three tiles,
    // one door, one file. The door's label and the export button are verbatim
    // the reports family's, so an owner learns one affordance, not five.
    cardTitle: "Closed drawers",
    basisTrigger: "How this is counted",
    basisTitle: "How this report is counted",
    exportCsv: "Download CSV",
    // The single basis token left on the canvas: why these figures disagree
    // with the day close-out. The sentence itself is behind the door.
    basisChipPage: "This page",

    // The figure above sums only the drawers somebody counted, and the second
    // arm says so: a sentence about a subset says that it is a subset.
    statVarianceFooterAllCounted: "Every drawer on this page was counted",
    statVarianceFooterSomeUncounted:
      "Some drawers were never counted — they are not in this figure",
    statUncountedFooter: "They read as a dash in the table, never as a zero",

    basisTermTiming: "When these figures were taken",
    basisTermScope: "What these figures cover",
    basisScopeNote:
      "The tiles above are summed from the drawers listed on this page only, not from the whole history — which is why they need not match any one day's close-out.",
    basisCountedNote:
      "“Counted” is the figure the drawer actually stands on. Where a manager counted it himself and decided his figure is the one it stands on, that is this number — and both counts stay on the record with their names.",
    basisVarianceNote:
      "Variance = counted less expected. A drawer nobody counted reads as a dash and is in no total.",

    // The recount / manager-count marks in the table (UX-round D44 / ruling F23).
    // Written from the stored `managerCountSupersedes` flag, never from a
    // comparison of the two numbers — a manager who counted the same figure
    // and one who counted a different figure and deferred have both decided
    // something, and only the flag tells them apart. Both figures ride the
    // line: a bare marker says a number moved but not what it moved from.
    markManagerStands: "Manager's count · filed at {amount}",
    // A drawer closed before blind counting shipped has no cashier count at
    // all, so the mark has no second figure to name — and a zero in its place
    // would assert a count nobody took.
    markManagerOnly: "Manager's count · no count recorded before it",
    markManagerDeferred:
      "Cashier's count · manager counted {amount}, left hers",
    markRecounted: "Counted twice · first count {amount}",

    // PAPER (2026-10) — the drawer log's redesign. Every figure is the
    // server's: the expected cash, the counts and the variance a drawer
    // stands on.
    paperCountedSub: "{count} counted",
    paperCountedSubOne: "1 counted",
    paperCountedSubTwo: "2 counted",
    paperCountedSubFew: "{count} counted",
    paperOutsideBand: "{count} drawers off by more than {amount}",
    paperOutsideBandOne: "1 drawer off by more than {amount}",
    paperOutsideBandTwo: "2 drawers off by more than {amount}",
    paperOutsideBandFew: "{count} drawers off by more than {amount}",
    paperAllWithinBand: "Every counted drawer within {amount}",
    paperBandNote: "Within ±{amount} counts as balanced.",
    paperBandExact: "Only an exact count counts as balanced.",
    paperChecksLabel: "Recounted or checked",
    paperChecksSub: "The first blind count is always kept",
    paperAllBoxes: "All boxes",
    paperColSpan: "Open → close",
    paperColChecks: "Checks",
    paperDrawerName: "{label} · {name}",
    paperDrawerMeta: "{date} · float {amount}",
    paperSpan: "{opened} → {closed}",
    paperDrawerSub: "{date} · {opened} → {closed}",
    paperHandoverFrom: "Handover from {name}",
    paperFloatCountedIn: "Counted in",
    paperNotCounted: "Not counted",
    paperNotCountedNote: "Closed without a count",
    paperTagRecounted: "Recounted",
    paperTagManager: "Manager checked",
    paperTagNotes: "Notes",
    paperTagCustody: "Custody",
    paperTabsAria: "Drawer detail",
    paperTabSummary: "Summary",
    paperTabHistory: "History",
    paperTabExceptions: "Exceptions",
    paperOtherTakings: "Card and other takings",
    paperCountsTitle: "Counts",
    paperCountFirst: "Blind count by {name}",
    paperCountFirstNote: "Frozen, before seeing the expected amount",
    paperCountFirstInline: "frozen, before seeing the expected amount",
    paperCountPlain: "Count by {name}",
    paperCountRecount: "Recount by {name}",
    paperCountRecountUnnamed: "Recount",
    paperCountManager: "Manager's count by {name}",
    paperCountAt: "At {time}",
    paperManagerCountHint:
      "Your count is added beside the cashier's; their counts stay on record as they were.",
    paperHistOpened: "Opened by {name}",
    paperHistClosed: "Closed by {name}",
    paperHistNote: "Note from {name}",
    paperHistExpected: "Expected {amount}",
    // PAPER (founder, 2026-10-02): the redesign's own words. Only under PAPER.
    paperSubtitle:
      "Every cash drawer: what should be in it and what was counted.",
    paperStatDrawers: "Drawers",
    paperStatVariance: "Total difference",
    paperStatUncounted: "Not counted yet",
    paperCardTitle: "Drawers",
    paperColVariance: "Difference",
    paperBalanced: "Balanced",
    paperTagHandover: "Handed over",
    // Who closed the drawer, and the day when it is not the opening day.
    paperClosedBy: "Closed by {name}",
    paperClosedOnBy: "Closed {date} by {name}",
  },
  dayClose: {
    title: "Day close-out (Z)",
    description:
      "One business day, read: its sales, its drawers, and the bridge between them.",
    noAccessTitle: "You can't view the day close-out",
    noAccessBody:
      "This screen needs the reports permission. Ask the restaurant owner to add it to your role.",
    loading: "Preparing the day close-out...",
    errorBody: "We couldn't load this day's close-out.",
    futureDateInvalid: "That day hasn't started yet. Pick a past day or today.",
    dateApply: "Apply",
    dateClear: "Today",

    // ── The report-page pattern (UX-round D41 / Slice 5). See the Arabic note: three
    //    super-sections, a metric row whose first tile is the variance, one
    //    door per card onto one sheet, and a per-business-day CSV.
    cardDrawersTitle: "Drawers and reconciliation",
    cardAuditTitle: "The day's exceptions and amendments",
    jumpTo: "On this page",
    basisTrigger: "How this is counted",
    basisTitle: "How this report is counted",
    exportCsv: "Download CSV",

    statVarianceFooterAllCounted: "Every drawer counted",
    statVarianceFooterSomeUncounted: "A drawer was never counted",
    statVarianceFooterNoDrawers: "No drawers on this day",
    statTakingsLabel: "Net takings",

    basisTermTiming: "When each figure is taken",
    basisTermExpected: "Expected in the drawer",

    csvDayTotals: "Day totals",
    csvDrawers: "Drawers",
    csvBillDiscountTotal: "Bill discount (net)",
    csvBillDiscountCount: "Bills with a discount",

    salesTitle: "The day's sales",
    grossLabel: "Gross sales",
    ofWhichServiceCharge: "of which service charge",
    ofWhichVat: "of which VAT",
    // D93 — the share of the VAT line above that came from DELIVERY FEES, and
    // the fees themselves.
    //
    // `vatTotal` used to be the FOOD's VAT alone, because a fee's VAT lives in
    // its own column on the invoice (the fee has to stay outside `subtotal` or
    // the refund allocation's basis guard throws). So the one figure an
    // accountant carries off this screen to a VAT return was short by the tax on
    // every delivery. It is complete now, and this row says where the extra came
    // from: a filing number that changed without saying why is worse than one
    // that was wrong.
    //
    // The fee itself comes OFF "net food sales" below, for the same reason the
    // service charge does: one pays a rider, one pays the floor, and neither is
    // the kitchen's money.
    ofWhichDeliveryFeeVat: "of which VAT on delivery fees",
    // D44 — the part of the refunds that left no drawer and no card terminal:
    // the transfers. On the paper because `netTakings` below subtracts only the
    // drawer halves, and a manager checking the Z by hand must be able to see
    // why the two do not differ by the whole refund figure.
    // The sentence states what the SUBTRACTION knows — "neither cash nor card"
    // — with the transfer named as today's instance of it rather than as its
    // definition. `offDrawerRefundTotal` is `refundTotal − cash − card`, so any
    // future refundable method lands here too, and the arithmetic will outlive
    // the allowlist that currently makes the two sets identical.
    ofWhichOffDrawer:
      "of which sent back neither in cash nor by card (transfer)",
    netLabel: "Net food sales",
    // D93 — the sentence now names the three things actually taken out. It said
    // service charge and VAT while the figure also subtracted the delivery fee,
    // so it described a subtraction the rows above it visibly contradicted.
    netHint:
      "This is the restaurant's food money, after service charge, VAT and delivery fees are taken out.",
    bucketsTitle: "How it was collected",
    bucketCash: "Cash",
    bucketCard: "Card",
    bucketInstapay: "InstaPay",
    // F2 — sales that came through a delivery platform: money the platform owes
    // the restaurant, in no drawer and on no terminal.
    bucketAggregator: "On platforms",
    // T3 — the set is in the label: bills a platform brought, write-offs excluded.
    commissionLabel: "Platform commission (excluding written-off bills)",
    // T15 — per what each bill recorded; the platform's statement is the fact.
    commissionHint:
      "Platforms brought {orders} tonight and kept {amount}. NOT taken off the total above — that total is what the diners paid. Per the rate and base on the till; the platform's statement is the fact.",
    bucketWriteOff: "Written off",
    bucketUnsettled: "Not settled yet",
    bucketsTotalLabel: "= Total",
    invoiceCountLabel: "Billed invoices",
    sequenceLabel: "Invoice numbers",
    // The `#` rides inside the parameter so an Arabic screen can isolate it
    // with its digits; invisible here. See the Arabic note.
    // QA4 — «Invoice numbers: Numbers #1–#20». The label already says
    // "numbers"; the value repeating it stutters. Arabic does not («أرقام
    // الفواتير: من رقم #1 لـ #20») and is left alone.
    sequenceRangeLabel: "{first}–{last}",
    sequenceRangeNone: "No invoices this day",
    sequenceGapHint:
      "The billed count can be lower than the number range if a ticket was cancelled or is still open — a number is taken the moment an invoice opens, even if it's later cancelled. That gap is meant to stay visible, not a counting mistake.",
    givenAwayTitle: "Given away",
    discountTotalLabel: "Discounts",
    // D146 — its own line beside the tier's, never inside it.
    billDiscountLabel: "Bill discount ({count})",
    compedTotalLabel: "Comped",
    voidedTotalLabel: "Voided",
    // Named for what it is — stock adjusted, not food comped: a waste/
    // spoilage/correction adjustment is a cost that never touched an invoice.
    stockAdjustmentCostLabel: "Stock adjustments",
    givenAwayNetTag: "food money",
    givenAwayBasisNote:
      "The headline figure includes service charge and VAT — it is what the drawer never saw. The smaller figure under it is the food money, net of both. Two different questions; each number is right for its own.",

    // R12 — refunds: their own kind, beside the partition and never inside it.
    refundsTitle: "Refunds",
    // UX-round D37/D38 · ruling F3 — split; see the Arabic for which clause went where.
    refundsIntro:
      "Refunds sit beside the settlement buckets, never inside them — the buckets still add up to gross. They count on the day the money left, even when the bill belongs to an older day.",
    refundsBasisNote:
      "A refund is money that arrived and went back to the customer — its own kind, never a comp and never a write-off.",
    refundTotalLabel: "Given back",
    refundCountLabel: "Plates given back: {count}",
    // B118 round table — Arabic takes the plural at 3–10 and the singular at
    // 11+; English has no third form, so these are the general form's words.
    refundCountLabelOne: "Plates given back: 1",
    refundCountLabelTwo: "Plates given back: 2",
    refundCountLabelFew: "Plates given back: {count}",
    refundCountFeeOnly: "No plates — a delivery fee only",
    refundsNone: "Nothing was refunded on this day.",
    ofWhichDeliveryFeeRefunded: "of which delivery fee given back",
    ofWhichDeliveryFeeVatRefunded: "of which VAT on the fee given back",
    netTakingsLabel: "= Net takings (cash + card − refunds)",
    // UX-round D37/D38 · ruling F3 — split, and gated on `netTakings !== gross`.
    netTakingsHint:
      "What is left of the money that actually reached a drawer — not gross minus refunds.",
    netTakingsBasisNote:
      "A written-off bill and a cheque still out were never money anything could be handed back out of, and a refund sent back by transfer left the bank rather than a drawer.",
    // B118/QA4 — Arabic: plural at 3–10, singular at 11+. See `plural.ts`.
    // UX-round D38 · ruling A3 — the count, and nothing else; the tail is the sibling
    // note below, rendered beside it.
    refundDivergedLabelOne:
      "Refunds that went back by a method the bill no longer names: 1",
    refundDivergedLabelTwo:
      "Refunds that went back by a method the bill no longer names: 2",
    refundDivergedLabelFew:
      "Refunds that went back by a method the bill no longer names: {count}",
    refundDivergedLabel:
      "Refunds that went back by a method the bill no longer names: {count}",
    refundDivergedNote:
      "The refund keeps the method the money physically left by, so the drawer stays honest about what is in it. This is a flag, not an error.",
    // B47 — founder, 2026-09-10: the Z records what actually happened, and both
    // the in and the out are reflected in the net.
    writtenOffInflowLabel: "+ taken on bills later written off",
    writtenOffInflowHint:
      "A bill that was paid and then recorded as a write-off: the money really did reach the drawer, so it is counted here. If it went back to the customer, the refund line above takes it out again — that bill then nets to zero in the takings, which is exactly what happened. If it did not go back, the cash is still in the till and this line says so.",
    netTakingsLabelWithWriteOff:
      "= Net takings (cash + card + taken on written-off bills − refunds)",

    drawersTitle: "The day's drawers",
    ridersTitle: "Riders handing in",
    ridersIntro:
      "Today's delivery bills that left with the restaurant's own riders, grouped by the name typed at the hand-over (two spellings of one name are one rider). Report only: no balance carries from day to day, and the rider's pay is in the drawer movements.",
    ridersUnnamed: "No name",
    ridersNameColumn: "Name",
    ridersBillsColumn: "Bills",
    ridersCashColumn: "Cash",
    ridersInstapayColumn: "InstaPay",
    ridersOtherColumn: "Other methods",
    ridersWriteOffColumn: "Unpaid",
    ridersStillOutColumn: "Still out",
    ridersDifferenceColumn: "Difference",
    ridersCountAmount: "{count} · {amount}",
    basisNote:
      "Sales are calculated now from today's bills; each drawer's figures are the ones frozen when it was counted. A correction made after a close shows under \"Amended after close\", not inside the drawer.",
    varianceTotalLabel: "Total variance",

    bridgeTitle: "The day's sales against the day's drawers",
    bridgeIntro:
      "The day's sales and the day's drawers don't have to match when a drawer starts before midnight — this bridge shows why.",
    daySettledLabel: "Settled from this day's invoices",
    carriedInLabel: "Plus: earlier days' money settled in today's drawers",
    carriedOutLabel:
      "Minus: this day's invoices settled in a drawer not closed out here",
    stillUnsettledLabel:
      "Minus: this day's closed invoices, still unsettled (cheques still out)",
    // B91 — money really in the drawer against a bill nobody has finished
    // paying. NOT the line above it: that is the whole total of the cheques
    // still out, this is the part already paid toward them.
    takenOnUnsettledLabel: "Plus: part-payments on bills not settled yet",
    shiftsTakingsLabel: "= What actually reached today's drawers",
    // The bridge's OTHER side: summed independently from the shifts' own
    // settled invoices, not derived from the same sales figures above.
    shiftsActualTakingsLabel:
      "What the drawers themselves say (summed independently)",
    bridgeMatchesMessage: "Today's invoices match what reached the drawers.",
    bridgeMismatchWarning:
      "Today's invoices do not match what reached the drawers — a difference of {amount}.",
    varianceUnexplainedNote:
      "The bridge compares bills against bills and never looks in the drawer — it can balance while the drawer is short. The drawers are {amount} out, and nothing here explains it.",
    varianceUnexplainedPartialNote:
      "The bridge compares bills against bills and never looks in the drawer. The drawers that were counted are {amount} out with nothing explaining it, and one drawer has not been counted — so this is not the whole day's difference.",
    bridgeRefundedInDayLabel: "Given back to customers today",
    bridgeRefundedCashLabel: "of which cash out of a drawer",
    // Refunds are deliberately NOT part of the chain above: the bridge
    // compares bills against bills, and a refund is not a bill.
    bridgeRefundNote:
      "Refunds are not part of the chain above — the bridge compares bills against bills, and a refund is not a bill. These two lines explain how a drawer can be down while the reconciliation still balances.",

    // GAP 1 / UX-round D39 · ruling F8 — row labels now, one per kind, with every
    // number still named inside the row. See the Arabic.
    sequenceExceptionCancelled: "cancelled",
    sequenceExceptionOpen: "still open",
    // MISSING: no invoice row anywhere claims this number — the one kind that
    // means investigate, and the one F8 forbids summarising.
    sequenceExceptionMissing: "missing entirely",

    // S14/D49: the drawer's own frozen cash/card takings, block B (rework
    // finding 10) — a legacy null renders as "—", never 0.
    drawerCashColumn: "Drawer cash",
    drawerCardColumn: "Drawer card",
    // R6/R12 — the drawer's own refunds, so the row's whole arithmetic is
    // visible on one line.
    drawerRefundColumn: "Drawer refunds",
    drawerRefundFormula:
      "Expected = float + drawer cash + paid in − paid out − cash refunds.",

    // GAP 2: paid-in/paid-out per drawer, and the day's combined movements.
    // QA3 — the column both tables lacked while the story sentences under
    // them name drawers by label.
    drawerLabelColumn: "Drawer",
    // QA4 — a label repeats within a business day (the unique index covers
    // OPEN drawers only), so a handover night has two of them. Added only
    // when it actually repeats.
    drawerLabelAt: "{label} (opened {at})",
    drawerMovementsColumn: "Paid in / out",
    // UX-round D40 · ruling B5 — where the four labels' meaning went when an all-nil
    // cell collapsed to a single `0`. See the Arabic.
    drawerMovementsBasisNote:
      "\u201cIn\u201d is money that entered the drawer outside a bill; \u201cout\u201d is money that left it. Out splits two ways: \u201cleft the business\u201d is a cost that really went, \u201cstill the business\u2019s\u201d only moved to a safe or another drawer. When all four are nil the cell prints a single zero.",
    dayMovementsTitle: "The day's cash movements",
    // FIX 1: the same `paidOutLeftBusinessTotal` split, beside each drawer
    // row here so the reader doesn't have to open every shift to see it.
    drawerLeftBusinessLabel: "left the business",
    drawerMovedElsewhereLabel: "still the business's",
    // Unit 2's third arm — the charge or the tips handed to the team.
    drawerPassedThroughLabel: "went to the team",

    // GAP 3: the day's voids/comps/write-offs/cancellations, accountability.
    dayExceptionsTitle: "The day's voids & comps",
    // The list stopped at the server's cap while the counts above it did not.
    dayExceptionsTruncated:
      "Showing the newest {count} only — there are older entries on this day that are not listed. The figures above, including the refund count, are counted over the whole day, not over this list.",
    platesColumn: "Plates",

    // GAP 4: float continuity — a drawer opened with less/more than what the
    // previous one closed with (FIX 2, corrected 2026-09).
    //
    // The old single flag fired whenever `openingFloat !== previousCountedCash`
    // — which is every ordinary night: close counting 5,000, remove the
    // takings, open the next shift with a 500 float. Money LEAVING between
    // drawers is normal (banking, a drop) and is now informational only.
    // Money APPEARING is not normal — nothing legitimate puts cash IN a
    // drawer between shifts — so that direction keeps the warning tone.
    floatContinuityLower:
      "Previous drawer closed with {amount} — the difference is normal (takings removed or carried between drawers).",
    floatContinuityHigher:
      "Previous drawer closed with only {amount} — extra cash appeared in this drawer with nothing recording it.",

    openShiftPendingWarning:
      "A drawer is still open — this day's close-out isn't final yet.",

    // D42: what happened to this day's invoices after the day itself
    // rolled. Present on every Z, even at zero — "amended after close: none"
    // is the audit answer itself, not nothing to say.
    afterCloseTitle: "Amended after close",
    // UX-round D37 — gated on `afterClose.rows.length > 0`; the full text is the basis
    // note below. `afterCloseNone` is the assertion and stays unconditional.
    afterCloseIntro:
      "Reopening a closed bill has no time limit — and a table that ran past the rollover is not an amendment, just a bill that arrived late.",
    afterCloseBasisNote:
      "Reopening a closed bill has no business-day bound on purpose — someone can still fix a same-drawer mistake after the day rolls. So this Z states exactly what happened to its own invoices after the day itself was already over. A table that simply ran past the rollover and closed late is not an edit of a filed report — it's a bill that arrived late, and its total still counts toward the day.",
    afterCloseNone: "Amended after close — none",
    afterCloseAmendmentCountLabel: "Amendments (can change the total)",
    afterCloseLateTicketCountLabel:
      "Late tickets (closed after the day rolled)",
    afterCloseLateSettlementCountLabel: "Late settlements (a cheque left out)",
    // Finding 2 (follow-up review): the counts above become UNDER-counts
    // once the backend's list hit its own cap — say so plainly rather than
    // let "100" read as exact when it might not be.
    afterCloseTruncatedWarning:
      "This count hit the maximum shown — the real number may be higher.",
    // The three kinds a row can be — always shown as a chip so no two look
    // alike (the float-continuity mistake, not repeated here). A
    // LATE_TICKET (finding 1) is a table that simply ran past the rollover
    // before closing — not an edit of a filed report, the bill itself
    // arrived late.
    afterCloseKindAmendment: "Amendment",
    afterCloseKindLateSettlement: "Late settlement",
    afterCloseKindLateTicket: "Late ticket",
    afterCloseTicketLabel: "Ticket #{sequence}",
    afterCloseTicketUnknown: "Ticket no longer on file",
    // Command labels — only two are the settlement allow-list; everything
    // else, including a future command type this frontend has no word for
    // yet, falls to `afterCloseCommandOther`.
    afterCloseCommandReopenInvoice: "Bill reopened",
    afterCloseCommandSettleInvoice: "Bill settled",
    afterCloseCommandCorrectSettlement: "Payment method corrected",
    afterCloseCommandAddItems: "Items added",
    afterCloseCommandVoidItem: "Item voided",
    afterCloseCommandCompItem: "Item comped",
    afterCloseCommandCloseInvoice: "Bill closed",
    afterCloseCommandCancelInvoice: "Bill cancelled",
    afterCloseCommandTransferInvoice: "Bill moved to another table",
    // The money first: these are what an accountant is looking for on the
    // printed sheet, and all three were reaching it as a raw camelCase token
    // inside "Other change".
    afterCloseCommandSettlePartial: "Part payment taken",
    afterCloseCommandRefundInvoice: "Item refunded",
    afterCloseCommandRefundOverpayment: "Over-payment handed back",
    // Then what changes the shape of a bill without touching money.
    afterCloseCommandOpenInvoice: "Bill opened",
    afterCloseCommandUpdateDraftItems: "Basket changed before sending",
    afterCloseCommandSetGuestCount: "Cover count recorded",
    afterCloseCommandAssignParty: "Plates marked to a family",
    afterCloseCommandEditCustomer: "Customer details corrected",
    afterCloseCommandEditCharges: "Charges or channel corrected",
    afterCloseCommandApplyBillDiscount: "Bill discount applied",
    afterCloseCommandRemoveBillDiscount: "Bill discount removed",
    afterCloseCommandPrintCheck: "Check printed",
    afterCloseCommandRequestCashier: "Sent to the cashier",
    afterCloseCommandSetCaptain: "Captain changed",
    afterCloseCommandSubstituteItem: "Item's swaps amended",
    afterCloseCommandRemakeItem: "Item remade",
    // The kitchen.
    afterCloseCommandFireItems: "Items sent to the kitchen",
    afterCloseCommandPrintTicket: "Kitchen ticket reprinted",
    afterCloseCommandStartPreparing: "Kitchen started an item",
    afterCloseCommandMarkReady: "Item marked ready",
    afterCloseCommandMarkServed: "Item marked served",
    afterCloseCommandUndoReady: "“Ready” undone",
    afterCloseCommandUndoServed: "Serve undone",
    // The QR ones — this app never sends them, but they land on bills and so
    // they reach this report.
    afterCloseCommandRequestItems: "Ordered from the QR menu",
    afterCloseCommandAcceptPendingItems: "Diner's request accepted",
    afterCloseCommandRejectPendingItems: "Diner's request rejected",
    afterCloseCommandCancelRequestedItems: "Diner cancelled their request",
    afterCloseCommandOther: "Other change ({type})",

    printDocTitle: "Day close-out (Z)",
    printBusinessDate: "Business date",
    // B117/RT-E — see the Arabic note: without this the Z, the CSV and the
    // screen are three documents nothing proves are the same day.
    printTimezoneNote: "All times {zone} · printed {at}",
    printTimezoneDevice: "this device's clock",
    // B117 round table 2 (Q1/Q3) — what the filed sheet could not say.
    drawerRecountedLine:
      "{label}: first counted {first}, recounted to {final} by {by} at {at}.",
    drawerManagerCountStands:
      "{label}: {manager} counted it separately at {amount} (filed at {cashier}) at {at}, and ruled that their count is the one this drawer stands on.",
    drawerManagerCountDeferred:
      "{label}: {manager} counted it separately at {amount} (filed at {cashier}) at {at}, and ruled that the cashier's count stands.",
    drawerCountedByUnknown: "an unrecorded person",
    drawerNoteLine: "{label} — {author} wrote at {at}: “{body}”",

    // ── The foot of the sheet: the signature, the "computed at print time"
    //    line and the method block (UX-round D35, D36). See the Arabic note for why the
    //    recomputed line is unconditional, and why the method block reads the
    //    same `basisNotes()` array as the screen's door and the CSV.
    printSignatureName: "Name and signature",
    printSignatureDate: "Date",
    printRecomputedNote:
      "This sheet is computed from the ledger at print time; it is not frozen at close.",
    printMethodTitle: "How these figures are counted",
    // `{page}`/`{pages}`, never `{n}`/`{total}` — the latter pair fails the
    // placeholder guard, reproduced. Western digits, per AGENTS.md:143.
    printPageLabel: "Page {page} of {pages}",

    // ── The PAPER look (2026-10 redesign, round 4). See the Arabic note: there
    //    is no close-the-day command (D38), so the list is what keeps the
    //    day's figures from being final, not a gate in front of an act.
    paperStatusDrawerOpen: "A drawer is still open",
    paperStatusAllClosed: "Every drawer closed",
    paperChecksTitle: "Before you close the day",
    paperChecksLeftOne: "1 thing to finish first",
    paperChecksLeftTwo: "2 things to finish first",
    paperChecksLeftFew: "{count} things to finish first",
    paperChecksLeft: "{count} things to finish first",
    paperChecksNone: "Nothing is left open on this day.",
    paperCheckOpenTicketsOne: "1 invoice still open",
    paperCheckOpenTicketsTwo: "2 invoices still open",
    paperCheckOpenTicketsFew: "{count} invoices still open",
    paperCheckOpenTickets: "{count} invoices still open",
    paperCheckUnsettled: "{amount} on bills not settled yet",
    paperCheckUnsettledSub: "Closed bills still waiting for payment.",
    paperCheckDrawerOpenSub:
      "Count it and close the drawer — until then this day's figures are not final.",
    paperCheckUncountedOne: "1 drawer closed without a count",
    paperCheckUncountedTwo: "2 drawers closed without a count",
    paperCheckUncountedFew: "{count} drawers closed without a count",
    paperCheckUncounted: "{count} drawers closed without a count",
    paperCheckGoFloor: "Go to Floor",
    paperCheckGoInvoices: "See these bills",
    paperCheckGoDrawer: "Go to the drawer",
    paperCheckGoDrawerHistory: "Open Drawer log",
    paperFlowTitle: "From gross to collected",
    paperFlowSubtitle: "Each step moves the total; dark bars are totals.",
    paperFlowInstapay: "InstaPay (in no drawer)",
    paperFlowDrawerTakings: "Cash and card",
    paperFlowWrittenOffInflow: "Taken on bills later written off",
    paperBlockSales: "Sales and charges",
    paperServiceCharge: "Service charge",
    paperVat: "VAT",
    paperDeliveryFee: "Delivery fees (the courier's)",
    paperBlockInvoices: "Invoices",
    paperInvoicesCancelled: "Cancelled",
    paperInvoicesOpen: "Still open",
    paperInvoicesMissing: "Missing entirely",
    paperInvoicesWrittenOff: "Written off — not takings",
    paperAfterTime: "Time",
    paperAfterKind: "What",
    paperAfterTicket: "Invoice",
    paperAfterDetail: "Detail",
    paperAfterBy: "By",
    // PAPER (founder, 2026-10-02): the redesign's own words. Only under PAPER.
    paperSubtitle: "Check, close and print the day.",
    paperPrintButton: "Print Z report",
    // D151 — see the Arabic block.
    shareWhatsapp: "Share on WhatsApp",
    shareTitle: "Day close {date}",
    shareSales: "Gross (incl. service and VAT) {gross} · Net {net}",
    shareDrawer: "{label}: counted {counted}",
    shareDrawerWithVariance: "{label}: counted {counted} · variance {variance}",
    shareDrawersTotal: "Drawers {count} · total variance {variance}",
    shareOpenBills: "Bills still open: {count}",
    shareUnsettled: "Closed, awaiting payment {amount}",
    shareStillOut: "Still out with riders: {count} · {amount}",
    shareAsOf: "As of {time} — a late reopen can move it",
    leadTitle: "The day in brief",
    leadOpenBills: "Invoices still open",
    leadRidersOut: "Still out with riders",
    // P6.1 — the saved Z (see the Arabic catalogue).
    paperStatusSaved: "Closed {time} · figures saved",
    paperStatusSavedOutdated: "Closed {time} · figures as they stand now",
    closedDayOutdated:
      "The copy saved at the close is in an older format of this report and cannot be shown, so the figures above are as they stand now.",
    closedDayTitle: "Changes since the close ({time})",
    closedDayIntro:
      "The figures above are the ones saved when the last drawer closed, and they do not change. Each line here is a figure that moved since: saved, now and the difference.",
    closedDayNone: "Changes since the close — none",
    closedDayFigureColumn: "Figure",
    closedDaySavedColumn: "Saved",
    closedDayNowColumn: "Now",
    closedDayDifferenceColumn: "Difference",
    closedDayFigureOther: "Another figure on the Z",
    shareSaved: "Saved at the close, {time} — later changes show separately on the Z",
    shareChangedSince: "Changed since the close: {count}",
    closedDayFigureGross: "Gross sales",
    closedDayFigureServiceChargeTotal: "Service charge",
    closedDayFigureVatTotal: "VAT",
    closedDayFigureDeliveryFeeTotal: "Delivery fees",
    closedDayFigureDeliveryFeeVatTotal: "VAT on delivery fees",
    closedDayFigureNet: "Net food sales",
    closedDayFigureCashTotal: "Cash",
    closedDayFigureCardTotal: "Card",
    closedDayFigureInstapayTotal: "InstaPay",
    closedDayFigureWriteOffTotal: "Written off",
    closedDayFigureAggregatorTotal: "On platforms",
    closedDayFigureCommissionTotal: "Platform commission",
    closedDayFigureAggregatorOrderTotal: "Platform order value",
    closedDayFigureUnsettledTotal: "Not settled yet",
    closedDayFigureInvoiceCount: "Billed invoices",
    closedDayFigureDiscountTotal: "Discounts",
    closedDayFigureBillDiscountTotal: "Bill discount",
    closedDayFigureBillDiscountCount: "Bills with a discount",
    closedDayFigureCompedTotal: "Comped",
    closedDayFigureVoidedTotal: "Voided",
    closedDayFigureCompedNetTotal: "Comped (food money)",
    closedDayFigureVoidedNetTotal: "Voided (food money)",
    closedDayFigureStockAdjustmentCost: "Stock adjustments",
    closedDayFigureRefundTotal: "Given back",
    closedDayFigureRefundCount: "Plates given back",
    closedDayFigureRefundServiceChargeTotal: "Service charge given back",
    closedDayFigureRefundVatTotal: "VAT given back",
    closedDayFigureRefundDeliveryFeeTotal: "Delivery fee given back",
    closedDayFigureRefundDeliveryFeeVatTotal: "VAT on delivery fee given back",
    closedDayFigureRefundCashTotal: "Refunds in cash",
    closedDayFigureRefundCardTotal: "Refunds to card",
    closedDayFigureRefundMethodDivergedCount: "Refunds by another method",
    closedDayFigureRefundLateCount: "Late refunds",
    closedDayFigureWrittenOffInflowCashTotal: "Cash taken on bills later written off",
    closedDayFigureWrittenOffInflowCardTotal: "Card taken on bills later written off",
    closedDayFigureNetTakings: "Net takings",
    closedDayFigureDaySettled: "Settled from this day's invoices",
    closedDayFigureCarriedIn: "Earlier days' money settled in today's drawers",
    closedDayFigureCarriedOut: "Settled in a drawer not closed out here",
    closedDayFigureStillUnsettled: "Bills not settled yet",
    closedDayFigureTakenOnUnsettled: "Part-payments on bills not settled yet",
    closedDayFigureShiftsTakings: "What reached today's drawers",
    closedDayFigureShiftsActualTakings: "Takings by the drawers' own count",
    closedDayFigureRefundedInDay: "Given back to customers this day",
    closedDayFigureRefundedCashInDay: "of which cash out of a drawer",
    closedDayFigureVarianceTotal: "Total variance",
    closedDayFigureOpenBillCount: "Invoices still open",
  },
  // Driver backend B1 (2026-10-17) — see the Arabic block.
  driverAccess: {
    landingTitle: "This is a delivery driver account",
    landingBody:
      "You take and deliver orders in the Trioty Driver app on your phone, not on this screen.",
    landingStore: "Restaurant: {store}",
    landingHint:
      "Sign in to the app with the same phone number and password you use here.",
    landingSwitchTitle: "Working at another restaurant?",
    landingSwitchBody: "Choose the restaurant to switch to.",
    inviteTitle: "An invitation to deliver",
    inviteBody:
      "Open the invitation in the Trioty Driver app to set up your account and join the restaurant.",
    inviteOpenApp: "Open the app",
    inviteInstallHint:
      "If the app does not open, install the Trioty Driver app first, then open this link again.",
    inviteKeepPrivate: "This link is yours alone — do not share it.",
    inviteNoAppTitle: "The Trioty Driver app is not installed",
    inviteNoAppBody:
      "The app did not open because it is not installed on this phone. To join the restaurant:",
    inviteNoAppStepAsk:
      "Ask the restaurant that invited you for the Trioty Driver app or its download link.",
    inviteNoAppStepInstall: "Install the app on your phone.",
    inviteNoAppStepReopen:
      "Go back to the invite message on WhatsApp and open the same link again.",
    inviteNoAppRetry: "Open the app once it is installed",
    composerAction: "Invite a driver",
    composerTitle: "Invite a delivery driver",
    composerIntro:
      "Enter the driver's name and phone number. You will get a link to send them from WhatsApp on your phone.",
    composerName: "Driver's name",
    composerNamePlaceholder: "e.g. Mahmoud Ali",
    composerPhone: "Phone number",
    composerCreate: "Create the link",
    composerDetailsRequired:
      "Enter the driver's name (at least 2 letters) and phone number.",
    composerReady:
      "The link is ready. Send it to {name} from WhatsApp on your phone, or copy it. It works for seven days.",
    composerShareWhatsapp: "Share on WhatsApp",
    composerCopy: "Copy link",
    composerCopied: "Link copied",
    composerShareMessage:
      "Hello {name}, {store} has invited you to deliver for them on Trioty. Open this link on your phone to set up your account in the Trioty Driver app:\n{url}",
    errorAlreadyOpen:
      "An invitation for this number was created at the same moment. Try again to get its link.",
    errorAlreadyMember:
      "This number already belongs to a member of this restaurant's team.",
    errorInvalid:
      "Check the name and the phone number. Write the number like 01012345678, or in international form.",
    errorPermission:
      "You do not have permission to invite drivers. Ask the restaurant owner.",
  },
  // Driver backend B3 (2026-10-18) — see the Arabic block.
  dispatch: {
    chipOffered: "Offered to {name} · {time}",
    chipAccepted: "{name} accepted",
    chipPickedUp: "On the way with {name}",
    chipArrived: "{name} has arrived",
    chipDelivered: "Delivered by {name}",
    chipFailed: "Not delivered · coming back with {name}",
    chipProblem: "{name} reported a problem",
    chipNotAnswered: "{name} did not answer",
    chipDeclined: "{name} declined",
    actionAssign: "Assign a driver",
    actionChange: "Change driver",
    sheetTitle: "Driver for order #{sequence}",
    sheetCurrent: "Now with {name}",
    sheetPick: "Choose a driver",
    sheetOnShift: "Available",
    sheetOffShift: "Not available",
    sheetActiveOne: "1 order with them",
    sheetActiveTwo: "2 orders with them",
    sheetActiveFew: "{count} orders with them",
    sheetActive: "{count} orders with them",
    sheetNoDrivers: "No drivers yet. Invite one from the team screen.",
    sheetNoneOnShift:
      "No driver is available right now. A driver turns on “Available” in the app to receive orders.",
    sheetTimeout: "A driver has {minutes} to accept. The restaurant owner sets this.",
    sheetWithdraw: "Take the order back",
    sheetLoadFailed: "Could not load the drivers.",
    minutesOne: "1 minute",
    minutesTwo: "2 minutes",
    minutesFew: "{count} minutes",
    minutes: "{count} minutes",
    toastAssigned: "Offered to {name}",
    toastWithdrawn: "Taken back from {name}",
    errorNotAvailable: "This driver is not available right now. Choose an available driver.",
    errorAlreadyAssigned:
      "This order was given to another driver at the same moment. The list is refreshed.",
    errorNotDeliverable:
      "This order cannot go to a driver: it is not the restaurant's own delivery, or it was cancelled or already delivered.",
    errorInvalidTransition:
      "The order moved on — the driver may have picked it up or the offer ended. The list is refreshed.",
    errorNotFound: "This assignment was not found. The list is refreshed.",
    errorConflict: "That did not go through. Try again.",
    settingsTitle: "Delivery drivers",
    settingsTimeoutLabel: "Time a driver has to accept an order",
    settingsTimeoutHint:
      "From 1 to 15 minutes. An order not accepted in time comes back to the till to assign again.",
    settingsMinutesUnit: "min",
    settingsSave: "Save the time",
    settingsSaved: "Accept time saved",
    settingsInvalid: "Enter a whole number from 1 to 15.",
    settingsReadOnly: "Only the restaurant owner can change this.",
    settingsLoadFailed: "Could not load the accept time.",
    // B3 · P14 — see the Arabic block.
    actionProblem: "See the driver's report",
    actionFollowUp: "Follow up the delivery",
    problemSheetTitle: "Delivery of order #{sequence}",
    problemFrom: "Report from {name} · {time}",
    reasonNoAnswer: "The customer does not answer the phone",
    reasonWrongAddress: "Wrong address, or the driver cannot find it",
    reasonRefused: "The customer refused the order",
    reasonMissingOrDamaged: "An item is missing or damaged",
    reasonOther: "Something else",
    problemDriverNote: "Driver's note: {body}",
    problemReplyLabel: "Message to the driver (optional)",
    problemReplyHint: "The driver sees it with your decision.",
    resolveRetry: "Try again",
    resolveRetryHint: "The driver keeps the order and carries on.",
    resolveReturn: "Bring it back",
    resolveReturnHint: "The driver collects nothing and brings the whole order back to the counter.",
    resolveCancel: "Cancel the delivery",
    resolveCancelHint:
      "The delivery ends here and the bill does not change: cancel or refund it from the bill screen if needed.",
    resolveConfirmCancel: "Confirm: cancel the delivery",
    resolveDoneRetry: "{name} was told to try again",
    resolveDoneReturn: "{name} was told to bring the order back",
    resolveDoneCancel: "Delivery of order #{sequence} cancelled",
    endTitle: "End the delivery from the till",
    endHint:
      "For a bag that left and whose driver cannot finish it — removed from the team, phone off. Ending cannot be undone, and the bill does not change.",
    endReturned: "The order came back",
    endCancelled: "The delivery is called off",
    endConfirmReturned: "Confirm: the order came back",
    endConfirmCancelled: "Confirm: the delivery is called off",
    endDelivered: "We checked: the customer got it",
    endConfirmDelivered: "Confirm: the customer received it",
    endNoteLabel: "Why (kept with the driver's report)",
    endDone: "Delivery of order #{sequence} ended",
    errorProblemNotOpen: "This report was already settled. The list is refreshed.",
    groupDriverBadge: "Driver",
    groupPending: "Hand-in waiting",
    handoverPanelTitle: "Driver {name}'s cash",
    handoverPending: "{name} handed in {amount} · {time}",
    handoverNone: "No cash hand-in from {name} is waiting.",
    handoverLoading: "Loading the hand-in…",
    handoverLoadFailed: "Could not load the hand-in. Try again.",
    handoverOrder: "Order #{number}",
    handoverOrderUnknown: "An order",
    handoverBagSettle: "Settled on confirm",
    handoverBagSkip: "Already settled at the sitting — skipped",
    handoverBagChanged: "The bill changed after the hand-in",
    handoverSettleTotal: "Goes into your drawer on confirm",
    handoverSkipTotal: "Already settled at the sitting",
    handoverDrawerNote: "The cash goes into your open drawer, and each bill is settled in your name.",
    handoverChangedNote:
      "At least one bill changed after the hand-in, so the confirm will be refused. Check the bill, or press “Not right” so the driver sends it again.",
    handoverConfirm: "Confirm receipt",
    handoverReject: "Not right",
    handoverRejectNoteLabel: "What's wrong? (optional, the driver does not see it)",
    handoverRejectSend: "Refuse the hand-in",
    handoverRejectHint: "The hand-in goes back to the driver to send again. No bill is settled.",
    handoverConfirmed: "Receipt confirmed · {amount} settled",
    handoverResultSettled: "Settled: {items}",
    handoverResultSkipped: "Left alone, settled at the sitting: {items}",
    handoverRejected: "Hand-in refused. The driver will send it again.",
    handoverReplay: "This was already recorded. Nothing was settled twice.",
    errorHandoverBillChanged:
      "Bill #{number} changed after the hand-in, so nothing was settled. Check the bill, or press “Not right” so the driver sends it again.",
    errorHandoverBillChangedUnknown:
      "A bill changed after the hand-in, so nothing was settled. Check the bills, or press “Not right” so the driver sends it again.",
    errorHandoverNotPending: "This hand-in was already decided. The list is refreshed.",
    errorHandoverNotFound: "This hand-in was not found. The list is refreshed.",
    errorBusy: "The restaurant is busy with other changes right now. Nothing was settled; try again.",
    stillOutTitle: "Still out with the driver",
    perBagTitle: "Delivered, not in a hand-in",
    perBagOpen: "Settle these bag by bag",
    perBagHint:
      "A bag paid toward after it left cannot go in the driver's hand-in; it is settled here at the sitting only.",
    handOverDriver: "Leaves with driver {name}",
    handOverDriverOffered: "Offered to {name}, not accepted yet. You can close it now; they pick it up once they accept.",
    handOverDriverHint: "The driver's name goes on the bill automatically.",
    handOverDriversTitle: "Or give it to an available driver",
    handOverAssignClose: "Assign and close",
    handOverAssignFailed: "Could not give the order to the driver, so it was not closed: {error}",
    // B3 · P14 rework — see the Arabic block.
    chipReturned: "Back at the restaurant · {name}",
    chipDeliveryCancelled: "Delivery called off · {name}",
    sittingEndedReturned: "Came back: not collected here — handle its bill from the bill screen.",
    sittingEndedCancelled: "Delivery called off: not collected here — handle its bill from the bill screen.",
    groupEnded: "Came back or called off: {items}",
    resolveConfirmReturn: "Confirm: bring it back",
    sheetEndShift: "End shift",
    sheetEndShiftConfirm: "Confirm: end shift",
    toastShiftEnded: "{name}'s shift has ended; no new orders will reach them.",
    errorDriverNotFound: "This driver is not on the restaurant's team. The list is refreshed.",
    // B4 · P16 — see the Arabic block.
    trackPageTitle: "Track your order",
    trackOrderNumber: "Order #{number}",
    trackStagePreparing: "The restaurant is preparing your order",
    trackStageOnTheWay: "Your order is on its way",
    trackStageArrived: "The driver has arrived",
    trackStageDelivered: "Your order has been delivered",
    trackStageNotDelivered: "Your order was not delivered",
    trackDeliveredBody: "Enjoy your meal.",
    trackNotDeliveredBody: "Please contact the restaurant about your order.",
    trackEta: "Arrives in about {min}–{max} min",
    trackEtaOne: "Arrives in about 1 min",
    trackEtaTwo: "Arrives in about 2 min",
    trackEtaFew: "Arrives in about {min}–{max} min",
    trackStepPreparing: "Being prepared",
    trackStepOnTheWay: "On the way",
    trackStepArrived: "Driver arrived",
    trackStepDelivered: "Delivered",
    trackDriverTitle: "Your driver",
    trackDriverCall: "Call {name}",
    trackNoPosition: "The driver's live location is not available right now; it appears on the map once it is.",
    trackLastUpdated: "Updated {time}",
    trackMapLabel: "Delivery map",
    trackMapRestaurant: "Restaurant",
    trackMapDestination: "Delivery location",
    trackMapDriver: "Driver",
    trackLoading: "Loading your order…",
    trackRetrying: "Could not update the order status. Trying again shortly.",
    trackNotFoundTitle: "This tracking link is not available",
    trackNotFoundBody:
      "The link is wrong or has expired. If your order is still on its way, contact the restaurant.",
    shareAction: "Share tracking link",
    shareActionShared: "Tracking link shared",
    shareSheetTitle: "Tracking link · order #{sequence}",
    shareIntro: "Send the link to the customer so they can follow the order on a map to their door.",
    shareLoading: "Preparing the link…",
    shareWhatsapp: "Send to the customer on WhatsApp",
    shareCopy: "Copy link",
    shareCopied: "Link copied",
    shareNoPhone: "The bill has no usable customer phone number; copy the link and send it yourself.",
    shareStatusSent: "Shared with the customer at {time}",
    shareStatusNotSent: "Not shared with the customer yet",
    shareExpires: "The link works until {time}",
    shareExpiresDay: "The link works until {day} {time}",
    shareMessage:
      "Hello, you can follow your order #{sequence} from {store} until it reaches you here:\n{url}",
    shareErrorNotDeliverable: "A tracking link cannot be made for this order now.",
    shareCopyFailed: "Could not copy automatically. Select the link and copy it yourself.",
    shareMarkFailed: "Could not record that the link was shared: {error}",
    // B5 · P18: the drivers roster, a driver's details and the review of
    // their documents (manager / owner), with the access log.
    driversTab: "Drivers",
    driversTitle: "Drivers",
    driversSubtitle: "This restaurant's delivery drivers: who is on shift now, their vehicles and the state of their documents.",
    driversSubtitleNoDocs: "This restaurant's delivery drivers: who is on shift now and their vehicles.",
    driversNoAccessTitle: "You cannot view the drivers",
    driversNoAccessBody: "You need the dispatch or driver-documents permission. Ask the owner for it.",
    driversEmptyTitle: "No drivers yet",
    driversEmptyBody: "Invite a driver; they appear here once they activate their account.",
    driversLoadFailed: "Could not load the drivers: {error}",
    driversHeaderDriver: "Driver",
    driversHeaderShift: "Shift",
    driversHeaderVehicle: "Vehicle",
    driversHeaderDocuments: "Documents",
    driverOnShift: "On shift",
    driverOffShift: "Off shift",
    driverDetailsAction: "Details",
    driverDetailsFor: "Details for driver {name}",
    docsSummary: "Accepted {accepted} · Waiting {waiting} · To redo {resubmit} · Missing {missing}",
    vehicleTypeMOTORBIKE: "Motorbike",
    vehicleTypeCAR: "Car",
    vehicleTypeBICYCLE: "Bicycle",
    vehicleTypeFOOT: "On foot",
    vehicleNone: "Not set",
    vehiclePlate: "Plate: {letters} {numbers}",
    driverBack: "All drivers",
    driverLoadFailed: "Could not load the driver: {error}",
    driverNotFound: "This driver is no longer on the restaurant's team.",
    driverNotFoundBody: "Go back to the drivers list to see who is on the team now.",
    driverProfileTitle: "Driver details",
    driverPhone: "Phone",
    driverNationalId: "National ID",
    driverNationalIdLast4: "Ends in {last4}",
    driverNationalIdNone: "Not added",
    driverVehicle: "Vehicle",
    driverConsent: "Consent to document review",
    driverConsentAt: "Given on {date}",
    driverConsentNone: "Not given yet",
    docsTitle: "Documents",
    docsIntro: "Every opening of a document is logged with your name and the time, and a document link works for one minute only.",
    docKindPROFILE_PHOTO: "Profile photo",
    docKindID_FRONT: "ID card front",
    docKindID_BACK: "ID card back",
    docKindSELFIE_WITH_ID: "Selfie holding the ID card",
    docKindCRIMINAL_RECORD: "Criminal record certificate",
    docKindDRIVING_LICENCE: "Driving licence",
    docKindVEHICLE_LICENCE: "Vehicle licence",
    docKindVEHICLE_PHOTO: "Vehicle photo with plate",
    docStatusMISSING: "Not uploaded",
    docStatusSUBMITTED: "Waiting for review",
    docStatusACCEPTED: "Accepted",
    docStatusRESUBMIT: "Asked to redo",
    docUploadedAt: "Uploaded {date}",
    docNote: "Your note: {note}",
    docOpen: "Open",
    docDownload: "Download",
    docOpenFailed: "Could not open the document: {error}",
    docPopupBlocked: "The browser blocked the new window. Allow pop-ups for this site, then try again.",
    docAccept: "Accept",
    docAccepted: "Document accepted",
    docResubmit: "Ask to redo",
    docResubmitTitle: "Ask to redo: {kind}",
    docResubmitIntro: "The driver sees your note in the app and uploads a new file.",
    docResubmitNote: "Note to the driver",
    docResubmitPlaceholder: "For example: the photo is blurry, photograph the card in better light.",
    docResubmitNoteRequired: "Write a note to the driver, up to 500 characters.",
    docResubmitSend: "Send request",
    docResubmitSent: "The request to redo was sent to the driver",
    docCancel: "Cancel",
    docGone: "This document changed or was removed; the list was refreshed.",
    docReviewFailed: "Could not save the review: {error}",
    accessLogTitle: "Document access log",
    accessLogIntro: "Who opened each document, and when.",
    accessLogEmpty: "No one has opened a document of this driver yet.",
    accessLogRow: "{viewer} opened: {kind}",
    accessLogUnknownViewer: "A former team member",
    accessLogFailed: "Could not load the log: {error}",
    /** P19 (G18 RV18-L5): a review button's accessible name, «Accept: ID card front». */
    docActionAria: "{label}: {kind}",
    /** B6 D-39: 409 RESTAURANT_DELIVERY_CONTACT_REQUIRED, by what is missing. */
    contactRequiredBoth: "Add the customer's phone and address before sending this delivery order to the kitchen or closing it.",
    contactRequiredPhone: "Add the customer's phone before sending this delivery order to the kitchen or closing it.",
    contactRequiredAddress: "Add the customer's address before sending this delivery order to the kitchen or closing it.",
    // B6 · P24: the Delivery & takeaway tab, its Waiting-for-a-driver table
    // and the panel that offers one or many bags to a driver.
    tabDelivery: "Delivery & takeaway",
    newDeliveryOrder: "New delivery order",
    newPickupOrder: "New pickup order",
    waitingTitle: "Waiting for a driver",
    waitingAssignSelectedOne: "Assign 1 selected",
    waitingAssignSelectedTwo: "Assign 2 selected",
    waitingAssignSelectedFew: "Assign {count} selected",
    waitingAssignSelected: "Assign {count} selected",
    waitingSelect: "Select order #{sequence}",
    waitingEmpty: "Every delivery order has a driver",
    waitingNoName: "No name",
    waitingNoAddress: "No address",
    kitchenReadyNow: "Ready now",
    kitchenReadyFor: "Ready {count}m",
    kitchenReadyForHours: "Ready {count}h",
    kitchenPreparing: "Preparing",
    kitchenNotSent: "Not sent",
    payCashOnDelivery: "Cash on delivery",
    payPaid: "Paid",
    assignTitle: "Assign to a driver",
    assignMetaOne: "Order #{sequence} · {name}",
    assignMetaOneNoName: "Order #{sequence}",
    assignMetaTwo: "2 orders · {amount}",
    assignMetaFew: "{count} orders · {amount}",
    assignMeta: "{count} orders · {amount}",
    assignStaffDrivers: "Staff drivers",
    assignOnRunOne: "On a run · 1 order",
    assignOnRunTwo: "On a run · 2 orders",
    assignOnRunFew: "On a run · {count} orders",
    assignOnRun: "On a run · {count} orders",
    assignHasOffer: "Has the offer now",
    assignPlatformNote:
      "Platform orders are assigned by the delivery platform. Their drivers appear in settlements once they pick up.",
    assignConfirm: "Assign to {name}",
    assignResultOffered: "Offered to {name}",
    assignResultSkipped: "It can no longer be assigned; its status changed.",
    assignPartial: "Some orders were not offered. The reason is beside each one, and those orders are still selected.",
    toastAssignedBagsOne: "Offered to {name}",
    toastAssignedBagsTwo: "2 orders offered to {name}",
    toastAssignedBagsFew: "{count} orders offered to {name}",
    toastAssignedBags: "{count} orders offered to {name}",
    // P24 rework (G24): the map-pin mark for a pasted location, the short row button.
    waitingMapPin: "Map pin",
    // P24b + P26: the Pickup orders table, the new-order flow straight into the
    // register, and the order panel's Delivery / Pickup head.
    pickupTitle: "Pickup orders",
    pickupEmpty: "No pickup orders right now",
    pickupOpenBill: "Open bill",
    pickupOpenBillAria: "Open the bill for order #{sequence}",
    deliveryOrdersTitle: "Delivery orders",
    backToDelivery: "to Delivery & takeaway",
    crumbNewDelivery: "New delivery",
    crumbNewPickup: "New pickup",
    panelDelivery: "Delivery",
    panelPickup: "Pickup",
    panelDeliveryInfo: "Goes to the driver queue after the kitchen",
    panelPickupInfo: "Customer collects at the counter",
    panelPlatformInfo: "{platform} order",
    makePickup: "Make pickup",
    makeDelivery: "Make delivery",
    contactName: "Customer name",
    contactPhone: "Phone *",
    contactAddress: "Delivery address *",
    contactMissing: "Phone and address are needed before sending",
    contactMissingToast: "Add phone and address first",
    billAssignNotSent: "Send the order to the kitchen first, then assign a driver.",
    closeReasonLead: "Can't close:",
    handOverReasonLead: "Can't hand over:",
    handOverToDriver: "Hand over to the driver",
    handOverToDriverAria: "Hand order #{sequence} over to the driver",
    openBillAria: "Open invoice #{sequence}",
    billChipOffered: "Waiting for the driver to accept · {time}",
    billChipAccepted: "Accepted by the driver",
    billChipPickedUp: "With the driver",
    billChipArrived: "Driver at the door",
    billChipDelivered: "Delivered",
    billChipFailed: "Delivery failed",
    billChipProblem: "Driver reported a problem",
    billChipNotAnswered: "Driver did not answer",
    billChipDeclined: "Driver declined",
    billChipReturned: "Returned",
    billChipDeliveryCancelled: "Delivery cancelled",
    deliveredToastCash: "{name} delivered order #{sequence} · collected {amount}",
    deliveredToastInstapay: "{name} delivered order #{sequence} · InstaPay transfer {amount}",
    deliveredToast: "{name} delivered order #{sequence}",
    switchedToPickup: "Now a pickup order",
    switchedToDelivery: "Now a delivery order",
    // P24b + P26 rework (G24b): the With-a-driver and Not-sent-yet tables, the sent crumb.
    withDriverTitle: "With a driver",
    // 2026-10-26 delivery redesign: three lanes for what happens to a bag —
    // waiting for a driver, with the driver, waiting to collect — then pickups.
    laneWaitingHint: "Select several orders to assign them together",
    waitingItemsOne: "1 item",
    waitingItemsTwo: "2 items",
    waitingItemsFew: "{count} items",
    waitingItems: "{count} items",
    waitingShowItems: "Show the items of order #{sequence}",
    waitingItemsLoading: "Loading the items…",
    waitingItemsFailed: "Could not load the items: {error}",
    waitingItemsNone: "Nothing has been sent to the kitchen yet.",
    laneWithHint: "Accepted · on the way · arrived",
    laneWithEmpty: "No orders with drivers right now",
    laneCollectTitle: "Waiting to collect",
    laneCollectHint: "Cash stays with each driver until settled",
    laneCollectEmpty: "No cash with drivers",
    laneCollectLabel: "To collect",
    laneAge: "{count} min ago",
    laneAgeHours: "{count} h ago",
    laneUnsentChip: "Not sent to the kitchen yet · send it, then assign",
    laneUnsentTip: "Send the order to the kitchen first, then assign a driver.",
    laneBagDeliveredCash: "Delivered · cash",
    laneBagDeliveredPaid: "Delivered · paid",
    laneBagReturned: "Returned",
    laneBagVoided: "Voided",
    laneOutOne: "1 order still with them · settle after the run",
    laneOutTwo: "2 orders still with them · settle after the run",
    laneOutFew: "{count} orders still with them · settle after the run",
    laneOut: "{count} orders still with them · settle after the run",
    laneSettleNamed: "Settle {name}",
    pickupSectionTitle: "Collected at the restaurant",
    pickupSectionHint: "The customer or the platform's courier collects at the counter, no restaurant driver",
    pickupSourceDirect: "Pickup",
    pickupSourceDelivery: "Delivery",
    pickupStateClosedUnpaid: "Collected · unpaid",
    pickupStateEarlierDay: "From an earlier day · hand over or cancel",
    pickupStateCourier: "Waiting for {platform}'s courier",
    pickupPayOnPlatform: "Paid on {platform}",
    pickupPayNotRecorded: "Payment not recorded",
    pickupPayOnCollect: "Paid on collection",
    pickupActCollect: "Collect {amount}",
    pickupActCourier: "Hand to {platform}'s courier",
    pickupActHandCollect: "Hand over and collect",
    pickupRefOn: "Order number on {platform}",
    unsentTitle: "Not sent to the kitchen yet",
    crumbDeliveryNumber: "Delivery #{sequence}",
    crumbPickupNumber: "Pickup #{sequence}",
    // B6 · P25 — the drivers' settlement cards and the settle panel (plan D-41 … D-44).
    settleTitle: "Driver settlements",
    settleFiltersAria: "Filter settlements",
    settleFilterToSettle: "To settle",
    settleFilterReturns: "Has returns",
    settleFilterAll: "All",
    settleSearch: "Search drivers",
    settleChipStaff: "Staff",
    settleChipRider: "Rider",
    settleChipPlatform: "Platform",
    settleCashToCollect: "Cash to collect",
    settleFlagReturned: "#{number} Returned",
    settleFlagVoided: "#{number} Voided",
    settleFlagLeftOwed: "#{number} Left owed",
    settleNoFlags: "No returns or voids",
    settleButton: "Settle",
    settleButtonAria: "Settle {name}",
    settleViewRun: "View run",
    settleSettledAt: "Settled {time}",
    settleEmpty: "No driver has anything to settle",
    settleEmptyFiltered: "No drivers match",
    settleLoadFailed: "Couldn't load the settlements.",
    settleKindStaff: "Staff driver",
    settleKindRider: "Own rider",
    settleKindPlatform: "{platform} courier",
    settleOrders: "{count} orders",
    settleOrdersNone: "No orders",
    settleOrdersOne: "1 order",
    settleOrdersTwo: "2 orders",
    settleOrdersFew: "{count} orders",
    settleRunTitle: "Orders on this run",
    settleStateOnTheWay: "On the way",
    settleStateDelivered: "Delivered",
    settleStateReturned: "Returned",
    settleStateVoided: "Voided",
    settleStateWaiting: "Waiting pickup",
    settleStateWithRider: "With the rider",
    settleMethodCash: "Cash",
    settleMethodInstapay: "InstaPay",
    settleMethodCard: "Card",
    settleMethodPlatform: "Paid to platform",
    settleMethodPaid: "Paid",
    settleMethodSettledEarlier: "Settled earlier",
    settleMethodLeaveOwed: "Leave owed",
    settleMethodNotReceived: "Transfer not received",
    settleMethodAria: "How order #{number} is settled",
    settleViewProof: "View proof",
    settleProofFailed: "Couldn't open the transfer proof: {error}",
    settleProofGone: "The transfer proof is no longer available.",
    settleChangedAfterPickup: "Bill changed after pickup: told to collect {amount}, owes {expected} now",
    settleLeftOwedBefore: "Left owed at an earlier settlement",
    settleDeliversOnSettle: "Not marked delivered by the driver yet: collecting its cash marks it delivered",
    settleSumAlreadyPaid: "Delivered, paid by card or before it left",
    settleSumLeftOwed: "Left owed",
    settleSumReturned: "Returned or voided (not collected)",
    settleSumPayout: "Pay-out to the rider",
    settleSumCashExpected: "Cash expected",
    settleCashHanded: "Cash handed over",
    settleDiffMatch: "Matches expected cash",
    settleDiffShort: "Short by {amount}",
    settleDiffOver: "Over by {amount}",
    settleReturnsTick: "Returned food received back",
    settlePayoutAdd: "Add a pay-out to the rider",
    settlePayoutAmount: "Pay-out amount",
    settlePayoutReason: "Reason",
    settlePayoutHint: "Recorded as a Delivery cost movement on the drawer and taken off the cash expected.",
    settleConfirm: "Confirm settlement",
    settleWithDifference: "Settle with difference",
    settleWithDifferenceArmed: "Tap again to settle with the difference",
    settleStillOut: "{count} still on the way",
    settleStillOutOne: "1 still on the way",
    settleStillOutTwo: "2 still on the way",
    settleStillOutFew: "{count} still on the way",
    settleGateTick: "Tick returned food",
    settleGateType: "Type the cash handed over",
    settleGatePayout: "Finish the pay-out amount and reason",
    settleGatePayoutTooLarge: "The pay-out is larger than the cash owed",
    settleGateNothing: "Nothing on this run to settle",
    settleGone: "Nothing is left to settle for this driver; the cards were refreshed.",
    settleToast: "{name} settled",
    settleStaleLead: "This settlement changed since you opened it. Review the updated figures, then confirm:",
    settleChangeCash: "Cash expected is now {expected} (was {amount})",
    settleChangeAdded: "Order #{number} was added to the run",
    settleChangeRemoved: "Order #{number} left the run",
    settleChangeDue: "Order #{number}: now owes {expected} (was {amount})",
    settleChangeState: "Order #{number} changed",
    settleErrorBagInvalid: "An order on this run is no longer on it or was already settled. The card was refreshed; review it, then confirm.",
    settleErrorOutcome: "That choice is no longer allowed for order #{number}. The card was refreshed; review it, then confirm.",
    settleErrorOutcomeUnknown: "One of the choices is no longer allowed. The card was refreshed; review it, then confirm.",
    settleErrorReturns: "Confirm the returned food came back before settling.",
    settleErrorPayout: "The pay-out ({amount}) is larger than the cash the rider owes ({expected}). Record it as a manual «Delivery cost» movement on the drawer, then settle without a pay-out.",
  },
  customers: {
    title: "Customers",
    description:
      "Every customer with a phone number — whether they ordered from your website or their number was entered on a takeaway or delivery bill.",
    noAccessTitle: "You can't open this page",
    noAccessBody:
      "This screen needs the customer-list permission. Ask the restaurant owner to add it to your role.",
    searchLabel: "Search by name or phone",
    searchPlaceholder: "Name or phone number",
    listLabel: "Customer list",
    loadFailed: "Couldn't load customers: {error}",
    emptyTitle: "No customers yet",
    emptyBody:
      "Customers appear here when they order from your website, or when their number is entered on a takeaway or delivery bill.",
    noMatchTitle: "No customer matches this search",
    noMatchBody: "Try part of the phone number or the start of the name.",
    maskedNote:
      "Customer details are hidden because the store's subscription is restricted. Renew it to see names, phone numbers and addresses.",
    colCustomer: "Customer",
    colPhone: "Phone",
    colBills: "Bills",
    colSpent: "Total spent",
    colLast: "Last activity",
    colStatus: "Status",
    noName: "No name",
    hidden: "Hidden",
    sharesProfile: "Name from their own profile",
    blocked: "Blocked",
    noActivity: "None",
    channelTakeaway: "Takeaway",
    channelDelivery: "Delivery",
    sourceTill: "Till",
    sourceWebsite: "Website",
    openDetails: "Details for {name}",
    detailsLoadFailed: "Couldn't load this customer: {error}",
    notFoundTitle: "Customer not found",
    notFoundBody: "They may no longer be one of this store's customers. Go back to the list and refresh it.",
    profileTitle: "Profile",
    factName: "Name",
    factPhone: "Phone",
    call: "Call",
    factFirstSeen: "First seen",
    factTags: "Tags",
    factStatus: "Status",
    statusActive: "Active",
    statusBlockedSince: "Blocked since {date}",
    factBlockReason: "Block reason",
    addressesTitle: "Addresses",
    addressesEmpty: "No addresses on record for this customer.",
    addressNoText: "No written address",
    addressRecipient: "Recipient: {name}",
    addressLastUsed: "Last used: {date}",
    openMap: "Open in map",
    historyTitle: "Order history",
    historyEmpty: "Nothing on record for this customer yet.",
    historyBill: "Bill #{number}",
    historyBillPlain: "Bill",
    historyRequest: "Request",
    historyOpenBill: "Open bill #{number}",
    historyTruncated: "Showing the latest entries only",
    billOpen: "Open",
    billClosed: "Closed",
    billSettled: "Settled",
    billCancelled: "Cancelled",
    requestRequested: "Awaiting reply",
    requestRejected: "Rejected",
    requestExpired: "Expired",
    requestCancelled: "Cancelled",
    blockAction: "Block customer",
    unblockAction: "Unblock",
    blockHint:
      "A block refuses this number's website orders only — the till can still enter it.",
    blockTitle: "Block {name}",
    blockIntro:
      "This number won't be able to send new orders from your website. Sales at the till are not affected, and you can unblock at any time.",
    blockReasonLabel: "Reason",
    blockReasonPlaceholder: "e.g. repeated orders never collected",
    blockReasonRequired: "Write a reason for the block.",
    blockConfirm: "Block",
    blockedToast: "Customer blocked",
    unblockedToast: "Customer unblocked",
    blockFailed: "Couldn't change the block: {error}",
  },
};

export const catalogs: Record<Locale, typeof ar> = { ar, en };
